// MUST be installed before any AudioContext is constructed. Patches
// window.AudioContext + window.webkitAudioContext to track instances
// and resume them on user gesture. See audio-unlock.js for details.
import {
  installAudioUnlock,
  resumeAllAudioContexts,
  closeAllAudioContexts,
  getReusableAudioContext,
} from './audio-unlock.js';
import { installArtworkCoverRecoveryLayer } from '../shared/components/artwork-image.js';
// Static on purpose: a dependency-free 40-line helper — the "home modules
// stay dynamic while the flag is dark" rule guards panel code, not this.
import { createSingleFlightChain } from './home/mount-chain.js';
import { trackPageView } from './page-views.js';
import { isSafeHomepageReload, monitorServiceWorkerFreshness, registerServiceWorker } from './service-worker-registration.js';
installAudioUnlock();
installArtworkCoverRecoveryLayer(globalThis.document);

import { LibretroCore } from './cores/libretro-core.js';
import { LockstepLibretroCore } from './cores/lockstep-libretro-core.js';
import { EmulatorCore } from './cores/core.js';
import { SYSTEMS, NES_SYSTEM, detectSystem, SERVER_RENDER_SYSTEMS, LOCKSTEP_SOLO_RECORD_SYSTEMS, SELF_DRIVING_RECORD_SYSTEMS, SELF_DRIVING_PLAYBACK_SYSTEMS, usesStatePump } from './cores/systems.js';
import { ATARI_NON_JOYPAD_ROM_HASHES } from './cores/atari-paddle-denylist.js';
import { GENESIS_NON_JOYPAD_ROM_HASHES } from './cores/genesis-lightgun-denylist.js';
import { configureFunnel, trackFunnelStep, resetFunnelSession } from './funnel.js';
import { RollbackEngine } from './rollback.js';
import { LockstepEngine, computeLockstepInputDelay } from './lockstep.js';
import { LockstepConnection, getLockstepDebugSearch } from './lockstep-connection.js';
import { Network, isConnectionCancellation } from './network.js';
import { IdleWatcher } from './idle-watcher.js';
import { openGameReport } from '../shared/components/report-game-panel.js';
import { InputHandler, isGamepadPresent } from './input.js';
import { AudioProcessor } from './audio.js';
import { extractRomFromZip } from './cores/rom-zip.js';
import { currentSaveDBNamespace, initSaveDB, findMostRecentSave, getSave } from './saves.js';
import {
  encodeEmulatorState,
  decodeEmulatorState,
  base64ToU8,
  u8ToBase64,
} from '../shared/save-state-codec.js';
import { romChecksums } from '../shared/rom-checksum.js';
import { primaryHash } from '../shared/rom-hash.js';
import { resolveArtifactCoreName } from '../shared/emulator-core.js';
import { isGenesis32xRom, isIncompatibleGenesis32xSave } from '../shared/genesis-32x.js';
import { escapeHtml } from '../shared/escape-html.js';
import { titleFromFilename } from '../shared/release-type.js';
import { systemTag, systemName } from '../shared/system-label.js';
import { badgeIconEl } from '../shared/components/badge-icons.js';
import { scoreSpecSupported } from '../shared/score-decode.js';
import { achievementsSupported } from '../shared/achievement-support.js';
import { formatShortDate } from '../shared/format-time.js';
import { startRoomThumbnailAnimator } from './room-thumbs.js';
import { HostedNetplay } from './hosted-netplay.js';
import { HostedMultiPeerHost } from './hosted-multipeer.js';
import { VirtualGamepadManager } from './virtual-gamepad.js';

import { InputParityCoordinator, computeTargetDelay } from './netplay/input-parity.js';
import { GameplayInputLeases } from './modules/gameplay-input-leases.js';
import { installLibretroGameplayInputGate } from './modules/libretro-gameplay-input-gate.js';
import { AchievementStore } from './achievements/store.js';
import { AchievementEngine, DEFAULT_WARMUP_TICKS } from './achievements/engine.js';
import { fetchSystemDefinitions } from './achievements/definitions.js';
import { authState } from './auth.js';
import { retireSuperunoResults } from './superuno/results-manager.js';
import { publishGameScore } from './modules/game-score-events.js';
import { SaveSync } from './save-sync.js';
import { SoloStreamHost } from './solo-stream.js';
import { resolvePresentationCapture, beginOrdinaryPresentation } from './superuno/presentation-source.js';
import { SuperunoError } from '../shared/superuno-errors.js';
import { createAudioSourceSelector } from './sbs-audio.js';
import { loadLayout, saveLayout, loadPipCorner, savePipCorner, nearestCorner } from './sbs-layout.js';
import { AsyncGameManager } from './async-game.js';
import { reportCrash, startBreadcrumb, clearBreadcrumb, checkForUncleanShutdown } from './crash-reporter.js';
import { openShareMenu, buildShareButton, socialSharingEnabled } from './modules/social-share.js';
// `./memory-monitor.js` is dynamically imported on first solo-start so the
// MemoryMonitor + thresholds + telemetry helper stay out of the entry
// bundle. The first sample lands a few ms late vs. an eager import, but
// we don't expect a tab to OOM in the first 100 ms.
import { createLazyReportPanel } from './report-panel-lazy.js';
import { initSavesPanel, openSavesPanel, closeSavesPanel, isSavesPanelOpen } from './modules/saves-panel.js';
import {
  bindGameChangeOverlay, showGameChangeOverlay, hideGameChangeOverlay,
  loadGameChangeRomList, loadGameChangeTop, gcMakeTopEntry,
} from './modules/game-change.js';
import { initControlsDialog } from './modules/controls-dialog.js';
import { moduleRegistry } from './module-registry.js';
// The game room gate: the per-launch layout decision, the chunk request and the guarded room
// hooks. The room chunk itself loads only through the gate's import() (A3).
import {
  callRoom, consumeClassicRoomFragment, consumeClassicRoomMarker, guardRoomNavigation, isRoomMode,
   prepareGameScreenLayout, prepareRoomScreenHide, finishRoomSessionCleanup, requestGameRoomChunk, loadGameRoomChunk, ensureRoomRecovery, verifyRoomRecoveryOwnership,
   roomSessionKey as roomLaunchSessionKey, sameSessionKey as sameLaunchSessionKey, roomRecoveryBlocked,
} from './modules/game-room-gate.js';
import {
  takeScreenshot, toggleMute, renderMute, toggleFullscreen, requestReset, setTouchControlMode,
  showExitConfirm, hideExitConfirm, showRewindConfirm, hideRewindConfirm,
} from './modules/game-actions.js';
import * as gameActions from './modules/game-actions.js';
import { touchFaceFor } from './modules/touch-pad-faces.js';
import { roomSessionKey as gameActionSessionKey, isCurrentSessionKey as gameActionSessionCurrent,
  captureRoomOperationOwnership } from './modules/game-room-gate.js';
import { REWIND_DEFAULT_SECONDS } from './replay-snapshot-ring.js';
import { captureBaseStateBytes, captureBaseStateResult, JSNES_STATE_CHARGE_BYTES, jsonUtf8SizeBounded, normalizeJsnesState, newBaseGeneration, isValidCoreBuildId } from './replay-base-state.js';
import { baseStateByteLimitForBuild, sha256Hex } from '../shared/base-state.js';
import { qualifiedMultitapCartridge } from '../shared/snes-multitap-cartridges.js';
import { clearSuperunoLobbyStartup } from './superuno/startup-art.js';
import { clearSuperunoReady } from './superuno/audio-ready.js';
import { retireSuperunoMatchEnd } from './superuno/match-end.js';
import { normalizedAxisToInt16 } from '../shared/replay-format.js';
import { ensureRomTitleCache } from './modules/stats.js';
import * as menuCards from './modules/menu-cards.js';
import { TAB_LABELS } from './modules/menu-cards.js';
import * as screenRouter from './modules/screen-router.js';
import { activateScreenAccessibility, routeTitleForScreen } from './modules/route-accessibility.js';
import { bindSiteNavMenu } from './modules/site-nav-menu.js';
import { setPageTitle } from '../shared/page-title.js';
import * as replayViewer from './modules/replay-viewer.js';
import * as replayBoundary from './modules/replay-boundary.js';
import * as lockstepStart from './modules/lockstep-start.js';
import * as diskPicker from './modules/disk-picker.js';
import { mountC64SpecialKeys, updateC64SpecialKeys, resetC64SpecialKeys } from './modules/c64-special-keys.js';
import {
  c64RequiresLeaderboardGate, createC64EvalClock, tickC64, c64TrackingAllowed, applySessionTrackingLatch,
  noteC64PauseChange,
} from './modules/c64-tracking.js';
import { c64SessionHasGame, trackingOffNotice } from './modules/c64-session.js';
import { requestC64Speed, confirmC64Speed, cancelC64Speed } from './modules/c64-speed-guard.js';
import {
  resolveC64LaunchIdentity, commitC64Identity, finishC64LaunchIdentity, c64CatalogLookup, c64CatalogWait,
  withTimeout,
} from './modules/c64-launch-identity.js';
import { CATALOG_HASH_HEADER } from '../shared/c64-bundle-identity.js';
import * as notificationsUi from './modules/notifications-ui.js';
import * as saveManager from './modules/save-manager.js';
import * as roomChat from './modules/room-chat.js';
import * as cheevoPanel from './modules/cheevo-panel.js';
import * as roomFlow from './modules/room-flow.js';
import { isLockstepEnabled as selectLockstep, qualifiedMultitapChoice, validatedMultitapProfile } from './modules/netplay-profile.js';
import * as gameAudioMute from './modules/game-audio-mute.js';
import { bindAuth, openAuthModal, requireAuthToPlay, whenTosClear, beginPlayIntent, cancelPlayIntent, showPlayIntentRetry, closeAccountPopover } from './modules/auth-ui.js';
import { newsScreenChanged, newsReplayChanged } from './news-modal.js';
import { digestC64Bundle, encodeC64BundleForLaunch, revalidatePlayRom } from './modules/play-intent-rom.js';
import { initGlobalSearch } from './modules/global-search.js';
import { syncUserSettings } from './modules/settings-sync.js';
import { initModalFocusManager } from './modules/focus-trap.js';
import {
  beginCaptureDecisions, settleReplayDecision, settleClipFromPaths,
  replayRecordingState, dispatchReplayRecording, roomSessionKey, isCurrentSessionKey,
  captureReplay, captureClip, bindClipPreview, showClipPreview, dismissClipPreview, saveClipPreview,
} from './modules/capture-state.js';
import { SESSION_END_DELIVERED_EVENT, endServerSession, sessionEndOutbox } from './modules/session-end-outbox.js';
import { syncLocalAchievementsToServer, syncLocalScoresToServer, syncSingleUnlock } from './achievements/server-sync.js';
import { currentScoreContext } from './achievements/score-context.js';
import { initTheme, setThemeFlagSource } from './theme.js';
import { bindThemePicker } from './modules/theme-ui.js';
import { bindGameMoreMenu, directRowWriter } from './modules/game-more-menu.js';
import * as authoringHost from './modules/authoring-host.js';
import { claimRoomKey, handleGameplayEscape, netplayHudApplies, noteNetplayHudOwner, recordHardwareKeyUse, roomLoadShortcutAvailable, singleKeyShortcutAllowed } from './modules/game-keys.js';
import { noteSurfaceOpened } from './modules/surface-order.js';
import { bindThemeShowcase } from './modules/theme-showcase.js';
import { applyC64LaunchPort, bindC64JoystickPortControl, resetC64JoystickPortControl } from './modules/c64-joystick-port.js';
import {
  renderFriendsScreen,
  mountProfileActionButton,
  sendEmailInvite,
  sendFriendRequest,
} from './friends.js';
// activity.js is dynamically imported in _showActivityScreen

// Start fetching the manual-save chunk with the initial app bundle. A game can
// remain open across deployments, so waiting until the user presses Save can
// leave its old hashed chunk unavailable. Keeping the module lazy preserves the
// eager-bundle budget while this promise pins the loaded module for the tab.
const manualSaveModulePromise = import('./modules/manual-save.js').catch((err) => {
  console.warn('[save] manual save module unavailable:', err);
  return null;
});
// The unlock and score sync senders (spec 6.4, 7.7, 7.9) are imported eagerly
// above, never as a lazy chunk: in server mode an unlock lives only in memory
// until the live sender posts it, and a browser can keep a failed chunk import
// for the page's lifetime, so a lost chunk would lose every unlock of the page.
// The authored overlay loader (spec 7.7, 9.3), with the engine overlay API it
// installs, stays lazy like the manual-save chunk. Kept once loaded, so the session-end and
// sign-out delegates run synchronously (no overlay exists before it loads). A
// chunk that fails to load leaves static definitions only (spec 15), and the
// next game load tries it again.
let authoredLoaderModule = null;
let authoredLoaderModulePromise = null;
function loadAuthoredLoaderModule() {
  authoredLoaderModulePromise ??= import('./achievements/authored-loader.js').then((module) => {
    authoredLoaderModule = module;
    return module;
  }, (err) => {
    console.warn('[authoring] authored overlay module unavailable:', err);
    authoredLoaderModulePromise = null;
    return null;
  });
  return authoredLoaderModulePromise;
}
loadAuthoredLoaderModule();
// Amended spec 9.3: loadGame waits for the authored delivery's published part
// at most this long after starting it; whatever arrives later is installed onto
// the running game.
const AUTHORED_PUBLISHED_WAIT_MS = 1_500;
// Spec 9.1's one load deadline (TIMINGS.loadDeadlineMs), started with the delivery, before its chunk loads.
const AUTHORED_LOAD_DEADLINE_MS = 15_000;

// Only C64 sessions use the C64 core classes. Prefetch their chunk with the
// initial bundle, like the manual-save chunk above, so a deployment during the
// tab's life cannot strand an old hashed chunk; keeping it lazy holds the
// eager-bundle budget in build.js, which is byte-pinned.
// A failed prefetch is retried by the next C64 launch, which reports any error.
let C64DrivesCore = null;
const loadC64DrivesCore = () => import('./cores/c64-drives-core.js').then((module) => { C64DrivesCore = module.C64DrivesCore; });
loadC64DrivesCore().catch(() => {});

// Register relic custom elements (<retro-avatar>, <retro-username>,
// <retro-profile-effect>, <retro-banner>) at boot. Side-effect imports
// — no exports consumed here, just the customElements.define() calls.
import './shared/relics/retro-avatar.js';
import './shared/relics/retro-username.js';
import './shared/relics/retro-profile-effect.js';
import './shared/relics/retro-banner.js';

// The 32X is a Genesis add-on. Register its native file extension only in the
// gameplay shell; the qualified replay/checkpoint system registry stays intact.
if (!SYSTEMS.genesis.extensions.includes('.32x')) SYSTEMS.genesis.extensions.push('.32x');

// Ordinary room decisions share policy with room-flow.js.
function isLockstepEnabled(systemId, coreName = null, romData = null) {
  // Lockstep is strictly a ≤2-player path. >2-player systems (N64) are
  // hosted-only and must NEVER resolve to lockstep — regardless of the per-system
  // flag. moduleRegistry.isEnabled() is opt-out (an absent flag reads as enabled),
  // so without this guard a system that was never added to the lockstep flag list
  // (like N64) would default to lockstep and break — and only a server restart +
  // client reload would fix it. Gating on maxPlayers makes it robust by design.
  return selectLockstep(systemId, coreName, romData, { lockstep_netplay: moduleRegistry.isEnabled('lockstep_netplay'),
    [`lockstep:${systemId}`]: moduleRegistry.isEnabled(`lockstep:${systemId}`) });
}

function retireMultitapSession(app, { leave = true } = {}) {
  const completedNative = !!(app._superunoCompletedLease || app._superunoCompletedRetirement);
  if (leave) {
    void app._retireSuperunoStoppedRoom?.();
    retireSuperunoResults(app); retireSuperunoMatchEnd(app);
    if (app._superunoCompletedLease) void app._superunoCompletedLease.destroy();
  }
  const session = app._multitapSession;
  if (!session) return completedNative ? { completedNative: true } : null;
  try { session.presentation?.invalidate(new SuperunoError('owner_lost', 'display', false)); }
  catch (error) { console.error('[multitap] presentation retirement failed', error); }
  // A fallible leave cannot prevent native/mesh retirement or shell cleanup.
  if (leave) try { session.leave(); } catch (error) { console.error('[multitap] leave failed', error); }
  try { void session.destroy({ retireOwned: true }); } catch (error) { console.error('[multitap] retirement failed', error); }
  return session;
}

const canvasStreamOwners = new WeakMap();
const canvasStreamReturns = new WeakMap();
const canvasStreamPrivateOwners = new WeakMap();

function connectionAssignmentCurrent(assignment, fields) {
  if (fields === null) return assignment == null;
  const current = Object.getOwnPropertyDescriptors(assignment), keys = Reflect.ownKeys(fields);
  return Reflect.ownKeys(current).length === keys.length && keys.every(key => {
    const now = current[key], previous = fields[key];
    return now && now.value === previous.value && now.get === previous.get && now.set === previous.set
      && now.enumerable === previous.enumerable && now.configurable === previous.configurable && now.writable === previous.writable;
  });
}

function publicCaptureCurrent(app, capture, purpose, connection, key) {
  const net = connection.net;
  if (app.net !== net || net?.ws !== connection.socket || net?._socketGeneration !== connection.generation
    || net?.roomCode !== connection.roomCode || !sameLaunchSessionKey(key, roomLaunchSessionKey(app))) return false;
  const resolved = resolvePresentationCapture(app, purpose);
  if (capture.kind === 'ordinary') return resolved.kind === 'ordinary' && app.canvas === capture.canvas;
  const lease = capture.lease;
  return resolved.kind === 'public' && resolved.lease.canvas === lease.canvas
    && resolved.lease.ownerToken === lease.ownerToken && resolved.lease.revision === lease.revision
    && lease.isCurrent() === true && app.net === net && net?.ws === connection.socket
    && net?._socketGeneration === connection.generation && net?.roomCode === connection.roomCode
    && sameLaunchSessionKey(key, roomLaunchSessionKey(app));
}

function refuseUnsupportedTitleEntry(app) {
  if (resolvePresentationCapture(app, 'screenshot').kind !== 'ordinary')
    throw new SuperunoError('unsupported_contract', 'display', false);
}

// JSON's own leading whitespace (space, tab, LF, CR), then an object.
const JSON_OBJECT_START = /^[ \t\n\r]*\{/;

function isC64BundleText(value) {
  if (typeof value !== 'string' || !JSON_OBJECT_START.test(value)) return false;
  try {
    const parsed = JSON.parse(value);
    // Any bundle shape counts, valid or not: it must never reach the single-ROM path.
    return parsed !== null && typeof parsed === 'object' && Object.hasOwn(parsed, 'disks');
  } catch { return false; }
}

const titleRomProbes = new WeakMap();

function sameOrdinaryRomBytes(rom, bytes) {
  if (!bytes) return true;
  if (!(rom instanceof Uint8Array) || rom.length !== bytes.length) return false;
  for (let index = 0; index < bytes.length; index++) if (rom[index] !== bytes[index]) return false;
  return true;
}

// Launch policy also recognizes a roomless qualified ROM. Flags and filenames
// cannot turn that title into an ordinary solo/hosted cartridge.
function unsupportedTitleRomGuard(app) {
  return roomFlow.ordinaryRoomTitleGuard(app);
}

function ordinaryRomIdentityGuard(app) {
  const system = app.system, rom = app.romData;
  if (system?.id !== 'snes' || !(rom instanceof Uint8Array || typeof rom === 'string')) return null;
  const net = app.net, socket = net?.ws, generation = net?._socketGeneration, roomCode = net?.roomCode;
  const key = roomLaunchSessionKey(app), canvas = app.canvas;
  const current = () => app.system === system && app.romData === rom && app.canvas === canvas
    && app.net === net && net?.ws === socket && net?._socketGeneration === generation && net?.roomCode === roomCode
    && sameLaunchSessionKey(key, roomLaunchSessionKey(app));
  const earlier = titleRomProbes.get(app);
  // Serialize hashes per app; replacing a launch cannot allocate a pile of ROM copies.
  const pending = (async () => {
    if (earlier) await earlier;
    if (!current()) return false;
    const bytes = rom instanceof Uint8Array ? rom.slice() : Uint8Array.from(rom, character => character.charCodeAt(0));
    const [hash, { requiresBanyunoTitleContext }] = await Promise.all([
      sha256Hex(bytes), import('../shared/superuno/release-policy.js'),
    ]);
    if (!current() || rom instanceof Uint8Array && (rom.length !== bytes.length || rom.some((byte, index) => byte !== bytes[index]))) return false;
    if (requiresBanyunoTitleContext(hash)) throw new SuperunoError('unsupported_contract', 'display', false);
    return rom instanceof Uint8Array ? bytes : current();
  })();
  const settled = pending.then(() => {}, () => {});
  titleRomProbes.set(app, settled);
  void settled.then(() => { if (titleRomProbes.get(app) === settled) titleRomProbes.delete(app); });
  return pending;
}

// Leaderboard active-state gating, controlled per system. Fail-CLOSED (an absent
// flag reads as OFF, unlike isEnabled): score capture only narrows to a live
// attempt when BOTH the master flag and the per-system flag are explicitly on.
function leaderboardGatingEnabled(systemId) {
  const f = moduleRegistry.getFeatures();
  return f.leaderboard_active_state === true && f[`leaderboard_active_state:${systemId}`] === true;
}

const LOCKSTEP_WARMUP_SAMPLES = 3;
const LOCKSTEP_WARMUP_TIMEOUT_MS = 1400;
const LOCKSTEP_BOOTSTRAP_DELAY = 2;

// Live rewind: capture a save-state ~once/second into a bounded ring so a tap
// can jump back ~REWIND_DEFAULT_SECONDS. Solo + non-self-driving cores only.
const REWIND_SNAPSHOT_INTERVAL = 60;   // frames between captures (~1s @ 60fps)
const REWIND_RING_MAX = 15;            // ~15s of history

// _recreateSoloRoom cross-invocation guard: net.onDisconnect re-enters the
// method every time the recreated room drops, and a room that registers but
// dies immediately (the WS upgrade completes before the server's room lookup,
// so net.connect() resolves even for a room the server then closes with 4001)
// would otherwise cycle POST→open→close as fast as the network allows. A
// recreate that dies within STABLE_MS counts as a rapid cycle; rapid cycles
// back off exponentially and give up after MAX_RAPID_CYCLES.
const SOLO_ROOM_STABLE_MS = 30000;
const SOLO_ROOM_MAX_RAPID_CYCLES = 5;
const SOLO_ROOM_CYCLE_BASE_MS = 4000;
const SOLO_ROOM_CYCLE_MAX_MS = 30000;
const HOSTED_SWAP_TIMEOUT_MS = 30000;
const GBA_LINK_RTT_WARN_MS = 10;
const GBA_LINK_RTT_WARN_SAMPLES = 3;
const GBA_LINK_RTT_RECOVER_SAMPLES = 3;

// ── Core Registry ───────────────────────────────────────
// Maps system id → factory function that returns an EmulatorCore.
// NES uses bundled jsnes; all others use libretro WASM cores.
// The core name comes from each system's `core` field in systems.js.
//
// Systems with `coreMultiplayer` ship two WASM builds: a solo core built
// with ASYNCIFY_IGNORE_INDIRECT=1 (lean — iOS Safari's memory budget) and a
// full-ASYNCIFY `_mp` core (link cable needs it: P2 serial dispatches
// emscripten_sleep through indirect calls).
//
// ⚠️ Stage-2 ROLLED BACK (2026-07-03): the rebuilt lean+correct core (PR #126,
// unified everywhere by #127) crashes serial-polling titles in-browser (GB
// Tetris title screen: "null function" in gambatte::Memory::nontrivial_write)
// when a boot-time Asyncify suspend never completes — a suspended
// AudioContext means al_write's backpressure sleep can block indefinitely,
// and any wasm re-entry during that window corrupts the Asyncify state. The
// same binary steps the same title clean headless (no sleeps), so the
// emulation is sound; the browser suspend-lifecycle is not. Binaries are the
// prod-proven May lean build again; the Stage-2 core re-lands behind proper
// audio-suspend hardening + real-device verification. gb_replay_recording
// does NOT wait on that: PR #137 moved GB recording off the stepped
// (stepReady-gated) path onto the self-driving input-log ring
// (_selfDrivingRecordLoop), safe on any binary — the _startSolo arm branch
// fail-closes where the ring exports are missing (enableRecordingRing()
// false on this ring-less May lean build; desktop _mp carries the ring).
//
// Selection (the proven post-#125 rule): desktop keeps _mp; iOS solo play
// gets the lean build (b117bbc: full ASYNCIFY gets the tab killed at startup
// on iPads/iPhones — the 2026-06 _mp-everywhere trial regressed exactly
// that). Link-cable multiplayer always needs _mp. The sticky ?gbcore
// override (gbCoreOverride) beats the device rule for solo A/B only; it must
// never substitute a lean build in multiplayer. The May
// lean build's formerly-accepted flaw — rare Dr.-Mario-class doRewind
// traps on iOS solo — is root-cause-fixed by the sleepless lean gambatte
// config (PR #154; see _writeConfig in libretro-core.js — engages on
// ~60/~120Hz displays only, rollback lever retroplay:gb-sleepless=off),
// and _handleCrash's wasm-trap auto-relaunch (PR #151) cushions any trap
// that still fires into a resume-from-last-save hiccup.
function isIOSDevice() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPadOS desktop-UA mode
}
export function pickCore(sys, multiplayer = false) {
  if (!sys.coreMultiplayer) return sys.core;
  if (multiplayer) return sys.coreMultiplayer;
  return isIOSDevice() ? sys.core : sys.coreMultiplayer;
}

// Debug / rollback escape hatch for the GB solo-core trial: force a specific
// gambatte build via ?gbcore=solo|mp|v2 (sticky in localStorage), or reset
// with ?gbcore=auto. Lets us A/B solo builds on real iOS hardware without
// redeploying. Multiplayer never consults this override because link-cable
// serial dispatch requires the full-ASYNCIFY gambatte_mp build. `v2` selects
// the STAGED pinned-source lean rebuild
// (gambatte_v2: May source + ring + playback exports) being gated on real
// iPad hardware before it becomes the default lean core — the iPad-parity
// path for GB replay recording/live playback. GB only; returns a core name
// or null when no override is active.
const GB_BOOT_MARKER = 'retroplay:gbcore-boot';
function gbCoreOverride() {
  try {
    const q = new URLSearchParams(window.location.search).get('gbcore');
    if (q !== null) {
      if (q === '' || /^(auto|default|clear)$/i.test(q)) localStorage.removeItem('retroplay:gbcore');
      else localStorage.setItem('retroplay:gbcore', q);
    }
    const v = (localStorage.getItem('retroplay:gbcore') || '').toLowerCase();
    const name = (v === 'mp' || v === 'gambatte_mp') ? 'gambatte_mp'
      : (v === 'solo' || v === 'gambatte') ? 'gambatte'
        : (v === 'v2' || v === 'gambatte_v2') ? 'gambatte_v2'
          : null;
    if (!name) return null;
    // Crash safety valve. The override is sticky and invisible, and on the
    // wrong device it is lethal — ?gbcore=mp on an iPad loads the 3.8 MB
    // full-ASYNCIFY build and iOS kills the tab AT STARTUP (the 2026-07-04
    // incident: a desktop test link opened on the iPad crash-looped GB until
    // ?gbcore=auto; no server-side fix could help). Bracket each overridden
    // boot with a marker: if a PREVIOUS page load's marker is still pending,
    // that boot never survived 10s — drop the override so the device heals
    // itself instead of crash-looping. (Closing the tab within 10s of a GB
    // launch also trips this; for a debug-only override that false positive
    // is the right trade — just re-add ?gbcore=X.) Checked once per page
    // load so this page's own marker is never misread as a crash.
    if (!window.__gbcoreBootChecked) {
      window.__gbcoreBootChecked = true;
      if (localStorage.getItem(GB_BOOT_MARKER) === 'pending') {
        localStorage.removeItem(GB_BOOT_MARKER);
        localStorage.removeItem('retroplay:gbcore');
        console.warn(`[gbcore] override "${v}" cleared: the previous overridden GB boot did not survive (crash safety valve)`);
        // Surface the auto-clear in the UI at the next GB boot — the silent
        // clear made the 2026-07-04 iPad core gate undiagnosable in the field
        // (the tester believed v2 was under test; crash stacks proved the
        // default core). _createEmulator reads + flashes this.
        window.__gbcoreValveCleared = v;
        return null;
      }
      localStorage.setItem(GB_BOOT_MARKER, 'pending');
      setTimeout(() => { try { localStorage.removeItem(GB_BOOT_MARKER); } catch { /* ignore */ } }, 10_000);
    }
    return name;
  } catch {}
  return null;
}

// Resolve the WASM core name for a system. GB debug overrides are deliberately
// solo-only: every multiplayer path must retain the system's coreMultiplayer.
export function resolveCoreName(sys, multiplayer = false, romData = null) {
  if (sys.id === 'genesis' && isGenesis32xRom(romData)) return 'picodrive';
  if (sys.id === 'genesis'
      && moduleRegistry.getFeatures().genesis_core_clownmdemu === true) {
    return 'clownmdemu';
  }
  if (sys.id === 'gb' && !multiplayer) {
    const override = gbCoreOverride();
    if (override) return override;
  }
  return pickCore(sys, multiplayer);
}
const CORE_REGISTRY = {
  snes: (canvas, sys, mp) => new LibretroCore(canvas, sys.id, resolveCoreName(sys, mp)),
  gb:   (canvas, sys, mp) => new LibretroCore(canvas, sys.id, resolveCoreName(sys, mp)),
  gba:  (canvas, sys, mp) => new LibretroCore(canvas, sys.id, resolveCoreName(sys, mp)),
  genesis: (canvas, sys, mp) => new LibretroCore(canvas, sys.id, resolveCoreName(sys, mp)),
  atari2600: (canvas, sys, mp) => new LibretroCore(canvas, sys.id, resolveCoreName(sys, mp)),
  c64:  (canvas, sys, mp) => new C64DrivesCore(canvas, sys.id, resolveCoreName(sys, mp)),
  n64:  (canvas, sys, mp) => new LibretroCore(canvas, sys.id, resolveCoreName(sys, mp)),
};

// ── State Machine ───────────────────────────────────────
export const State = {
  MENU: 'menu',
  LOBBY: 'lobby',
  LOADING: 'loading',
  PLAYING: 'playing',
  SOLO: 'solo',
  SPECTATING: 'spectating',
};

function stripDiacritics(s) {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

export class NESPlay {
  constructor() {
    // Theme bootstrap script in index.html has already set <html data-theme="...">;
    // initTheme() re-validates it against the current catalog.
    initTheme();

    // Crash resilience: catch uncaught WASM/JS errors and do a clean exit
    // instead of letting Safari kill + reload the tab (which dumps the user
    // back to the homepage with no save).
    window.onerror = (_msg, _src, _line, _col, error) => {
      if (this._handleCrash(error || new Error(_msg))) return true;
    };
    window.addEventListener('unhandledrejection', (e) => {
      this._handleCrash(e.reason instanceof Error ? e.reason : new Error(String(e.reason)));
    });

    // Pause self-driving WASM cores when the tab is hidden (iOS Safari
    // aggressively freezes backgrounded tabs — the WASM module stalls and
    // crashes on resume without this).
    document.addEventListener('visibilitychange', () => this._onVisibilityChange());

    checkForUncleanShutdown();

    // Prevent iOS from auto-focusing the room code input and opening the keyboard
    requestAnimationFrame(() => {
      setTimeout(() => {
        const el = document.getElementById('room-code-input');
        if (el && document.activeElement === el) el.blur();
      }, 0);
    });

    this.state = State.MENU;
    // A5/S4: a recovery reload carries #classic-room or its session backup. Consume it before anything
    // reads the URL or launches, so every launch in this page runs classic.
    this._gameRoomFailureLatch = null;
    consumeClassicRoomFragment(this);
    consumeClassicRoomMarker(this);
    this._homeFallbackActive = false;
    // The inline six-second boot failsafe can reveal the legacy shell even when
    // a boot-critical request never settles. Give that shell real data loaders
    // instead of leaving its static empty-state pseudo-element on screen.
    window.__onHomeFallback = () => this._activateLegacyHomeFallback();
    // A cold bundle can arrive after the inline timer fired. Replay its marker
    // once this constructor has finished and this instance has been published.
    queueMicrotask(() => {
      if (window.app === this && document.documentElement.hasAttribute('data-home-fallback')) {
        this._activateLegacyHomeFallback();
      }
    });
    this.canvas = document.getElementById('nes-canvas');
    const lastSystem = localStorage.getItem('retroplay-system');
    this.system = (lastSystem && SYSTEMS[lastSystem]) || NES_SYSTEM;
    this.emu = null;
    // Invalidates asynchronous WASM-core creation when a session is stopped or
    // superseded. Without this, an init() that finishes after returning home can
    // put the departed emulator back into this.emu and receive the next save.
    this._emulatorGeneration = 0;
    // Covers the work that happens before _createEmulator() publishes a core.
    // Exiting or starting another game invalidates every await in that launch.
    this._gameLaunchGeneration = 0;
    this.net = new Network();
    this.input = new InputHandler();
    this._gameplayInputLeases = new GameplayInputLeases({
      suspend: () => this._suspendEmulatorInput(),
      resume: () => this._resumeEmulatorInput(),
    });
    // Controller Profiles (task 3.11): the real InputManager backing the
    // InputHandler shim. Stays null unless `controller_profiles` is ON — wired in
    // _setupControllerProfiles() once flags load. null ⇒ the shim runs the
    // byte-identical legacy input path (flag-OFF must be untouched).
    this._inputManager = null;
    this._controllerAuthorityTransitionGeneration = 0;
    window.addEventListener('retroplay:flags-loaded', () => {
      this._notifyControllerFlagRefresh();
      this._syncHomeCreatorLink();
      // A3: fetch the room chunk as soon as the flag is known to be on.
      requestGameRoomChunk(this);
    });
    this.audio = new AudioProcessor();

    // Solo idle ("are you still there?") guard. Armed only during solo sessions
    // (started in _startSolo, stopped in _exitToMenu). See idle-watcher.js.
    this._idleLastMove = 0;
    this._idleCountdownTimer = null;
    this._idleGamepadPoll = null;
    this.idleWatcher = new IdleWatcher({
      onPrompt: () => this._showIdlePrompt(),
      onExpire: () => this._idleExpire(),
    });
    this._bindIdleActivity();
    this._roomDebugSearch = getLockstepDebugSearch();
    this.rollback = null;
    this.romData = null;
    this._romDataB64 = null;
    this.romHash = null;
    this.romName = null;
    this._pendingSave = null;
    // One ownership token spans save loaders and ordinary ROM/game launches.
    // Newer intent invalidates older async work before it can replace a session.
    this._gameLaunchIntentGeneration = 0;
    // Validation that deliberately leaves the current session alive (joining a
    // room, resolving a save, loading an async turn) uses a candidate token.
    // The active generation advances only once that candidate reaches its
    // destructive boundary, so a failed lookup cannot orphan the retained room.
    this._gameLaunchCandidateGeneration = 0;
    this._pendingGameLaunchCandidate = null;
    // One-shot events from the retained room are paused while a prospective
    // launch validates. If validation fails they replay in arrival order; if it
    // commits, the old-room events are discarded with the old session.
    this._deferredCandidateRoomEvents = [];
    // Carries the join intent across Network.connect() into onAssigned, whose
    // SBS racer path performs its own asynchronous ROM download.
    this._roomJoinLaunchOwner = null;
    this._pendingSoloHandoffSave = null;
    this._soloHandoffCaptureToken = null;
    this._handoffChargeBytes = 0;
    this._soloHandoffWasmPaused = false;
    noteC64PauseChange(this);
    this._hostedSwapPrepared = false;
    this._hostedSwapFrozen = false;
    noteC64PauseChange(this);
    this._hostedSwapWasmPaused = false;
    this._hostedSwapWasRunning = false;
    this._hostedSwapTimeout = null;
    this._hostedSwapPreparationToken = null;
    this._hostedSwapPreparationPromise = null;
    this._hostedSwapAcceptToken = null;
    this._hostedSwapAcceptPromise = null;
    this._hostedRoleTransitionOwner = null;
    this._initialSoloRoomSetupOwner = null;
    // In-memory mirror of the last recorded cloud-save conflict; the
    // authoritative copy lives in a per-user localStorage map read by
    // save-manager's maybeResolveSaveConflictOnLoad.
    this._pendingSaveConflict = null;
    // One-shot solo save-mode override consumed by _startSolo: set by a save
    // load (to the save's own mode) so loading respects the save's mode without
    // changing the user's per-system default. null → use the saved default.
    this._soloModeOverride = null;
    this._pendingGameChange = null;
    this._activeRomFilename = null;
    this.soloFrame = 0;
    this.running = false;
    // Direct save-state capture from stepped libretro cores must happen at a
    // completed-frame boundary. While true, _soloLoop lets any in-flight
    // Asyncify step finish without starting the next frame.
    this._stateCapturePending = false;
    this._replayBoundaryOwner = null;
    this.animFrameId = null;
    this.lastFrameTime = 0;
    this.frameAccumulator = 0;
    this.targetFrameTime = 1000 / this.system.fps;

    this.clipRecorder = null;
    // Shared pre-core audio tap used by local WebM clips and room spectators.
    this._clipAudioCapture = null;
    this._clipRecorderLoading = false;
    this._clipToastTimeout = null;
    this._clipPendingData = null;
    // Touch devices never run the local WebM recorder: their clips render on the
    // server from the replay (_milestoneClipFromReplay / _manualClipFromReplay).
    // Continuous encoding costs phones battery and heat, and iOS Safari before
    // 18.4 cannot record WebM at all.
    this._clipsEnabled = !navigator.maxTouchPoints && moduleRegistry.isEnabled('video_clips');

    // Replay recorder — observes the per-frame input stream during solo and
    // lockstep sessions. Buffers in memory (sub-megabyte even at marathon
    // length) and only uploads on explicit save trigger. See replay-recorder.js
    // and the RPL1 format at src/shared/replay-format.js.
    this.replayRecorder = null;
    this._replayRecorderLoading = false;
    this._replaysEnabled = moduleRegistry.isEnabled('replays');
    this._recordDrainCursor = 0;
    this._recordFrameOffset = 0;
    this._recordResumePrefix = null;
    this._atariReplayCheckpointPromise = null;
    // Replay base states (spec §1). `_replayBaseStateLoaded` is a HINT: a state
    // was applied to this core, so the recorder must capture a base snapshot in
    // the same turn it starts. `_replayRecordingOff` is the permanent "this
    // session will never record" decision (every arm-failure path sets it via
    // _replayRecordingUnavailable). `_pendingBaseGeneration` is the GB-only
    // pre-recorder handoff: the base is captured at ring-arm time, before the
    // recorder exists.
    this._replayBaseStateLoaded = false;
    this._replayRecordingOff = false;
    this._pendingBaseGeneration = null;
    this._recordLoopEmu = null;
    // Latest score observed in this recording session, plus the score already
    // claimed by its saved replay. At teardown, a longer session replay can be
    // extended once so the durable high-score write has matching verifier
    // evidence instead of stopping at the first podium PB milestone.
    this._sessionScorePeak = null;
    this._sessionReplayClaimedScore = null;
    this._finalScoreReplayFlushPromise = null;
    this._pendingReplayPostRoll = null;
    // Ghost races launch dark behind their own flag — replays can ship
    // (record/verify/share/leaderboards) before the two-emulator race path
    // is runtime-verified.
    this._ghostRacesEnabled = moduleRegistry.isEnabled('ghost_races');
    this._sideBySideEnabled = moduleRegistry.isEnabled('side_by_side');

    // Replay PLAYBACK (the inverse of recording). When _pendingReplayId is set
    // at boot via /?replay=:id, _startSolo diverts to _startReplayPlayback,
    // which loads the RPL1 log and drives this.emu through ReplayPlayer.
    // _replayPlaybackActive disables the recorder while playing back so we
    // don't record a replay-of-a-replay.
    this._pendingReplayId = null;
    this._pendingReplayCoreName = null;
    this._replayPlayer = null;
    this._replaySnapshotRing = null;   // rewind history for the fullscreen replay HUD
    this._replayPlaybackActive = false;

    // GHOST RACE. /?ghost=:id boots the player into a normal (recordable)
    // solo session running side-by-side with a second emulator that replays
    // a ranked ghost run, frame-synced. Unlike playback, the player's own
    // run IS recorded — you can set a new record while racing.
    this._pendingGhostId = null;
    this._ghostRaceActive = false;
    this._ghostEmu = null;
    this._ghostPlayer = null;
    this._ghostCanvas = null;
    this._ghostEndFrame = 0;

    // Passive memory monitor — sampling starts when a solo session begins
    // (see `_startSolo` / wherever `startBreadcrumb` is called). Phase 1:
    // instrumentation only, no detection or mitigation.
    this.memoryMonitor = null;

    this._spectators = new Map();
    this._spectatorAudioCtx = null;
    this._spectatorAudioSources = [];
    this._spectatorAudioAnchors = [];
    this._spectatorAudioTracks = [];
    this._spectatorAudioAttempt = null;
    this._pendingUpgrade = null;
    this._micLevelFrameId = null;
    this._micLevelLastTick = 0;
    this._gamepadConnected = false;

    // Input parity (Tier 2 hosted-netplay fairness). Coordinator is
    // created lazily per hosted session; the toggle persists globally.
    this.inputParity = null;
    this._inputParityEnabled = this._loadInputParityEnabled();
    this._hostFrameEstimate = 0;       // guest-side estimate of host's frame
    this._hostDelayFrames = 0;         // guest-side: delay reported by host
    this._hostFrameKnownAt = 0;        // performance.now() when last pong arrived
    this._parityHudTimer = null;

    if ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0) {
      document.body.classList.add('is-touch');

      // Short-landscape touch viewports (phones held sideways) auto-enter
      // the same fullscreen layout that the ⛶ button provides in portrait.
      // Only active while the game screen is visible — otherwise menu/lobby
      // would inherit overflow:hidden and become unscrollable.
      this._landscapeFsMq = window.matchMedia('(orientation: landscape) and (max-height: 500px)');
      this._applyLandscapeFs = () => {
        const screenGame = document.getElementById('screen-game');
        const inGame = screenGame && !screenGame.classList.contains('hidden');
        const active = this._landscapeFsMq.matches && inGame;
        document.body.classList.toggle('landscape-fs', active);
        this._reconcileChatViewport?.();
        // Clear any lingering tap-hidden toolbar state when leaving fs.
        if (!active) document.getElementById('game-controls')?.classList.remove('tap-hidden');
      };
      this._applyLandscapeFs();
      this._landscapeFsMq.addEventListener('change', this._applyLandscapeFs);

      // Tap the canvas area in landscape-fs to toggle the icon toolbar.
      // Clicks on #game-controls and #touch-gamepad don't bubble here
      // (different DOM subtrees), so they won't accidentally toggle.
      // Use capture phase + pointerup so libretro/Emscripten canvas handlers
      // can't stopPropagation the event away from us (NES works either way
      // because jsnes attaches no canvas listeners; SNES/GBA via libretro do).
      const toggleDock = (e) => {
        if (isRoomMode(this)) return; // The room releases field leases before toggling the dock.
        const b = document.body.classList;
        if (!b.contains('landscape-fs') && !b.contains('mobile-fullscreen')) return;
        // Only react to primary button / single touch
        if (e.pointerType && e.button !== 0) return;
        document.getElementById('game-controls')?.classList.toggle('tap-hidden');
      };
      document.getElementById('canvas-container')?.addEventListener('pointerup', toggleDock, true);
    }

    // ── Achievements ─────────────────────────────────────
    // Per-system definitions are loaded lazily from /cheevos/<sys>.json
    // so triggers don't bloat the main bundle. See achievements/*.
    this.cheevosStore = new AchievementStore();
    this.cheevosEngine = new AchievementEngine({
      store: this.cheevosStore,
      loader: fetchSystemDefinitions,
      onUnlock: (ach) => this._onAchievementUnlocked(ach),
      onScore: (score) => this._onScoreUpdate(score),
      // The draft partition (spec 9.2, 11.1): firings, score reports and their clock go
      // through the authoring host to the studio or draft testing (PR 2 README decision 5).
      onDraftFire: (event) => authoringHost.onEngineDraftFire(this, event),
      onDraftScore: (report) => authoringHost.onEngineDraftScore(this, report),
      draftClock: () => authoringHost.draftClockFor(this),
      warmupTicks: DEFAULT_WARMUP_TICKS,
      // Fail-closed, per system: gate score capture on the RA leaderboard's
      // Start/Submit only when the master flag AND the per-system flag are on.
      // Re-applied post-flag-load below (constructor runs before flags load).
      enableLeaderboardGating: leaderboardGatingEnabled,
      // C64 scores are captured only inside a leaderboard gate, whatever the
      // flags say (spec 4.9): disk loaders overwrite RAM after a zero.
      requireLeaderboardGate: c64RequiresLeaderboardGate,
    });
    // Unlocks and scores record the core and authored score version (spec 6.4, 7.9).
    this.cheevosStore.setContextProvider((systemId, romHash) => currentScoreContext(this, systemId, romHash));
    this._cheevosSessionStart = 0;
    this._cheevosSessionSys = null;
    this._cheevosSessionHash = null;
    this._cheevosPlayTimeInterval = null;

    this.asyncGame = new AsyncGameManager(this);

    // Live rewind state (per play-session, solo + non-self-driving cores only)
    this._rewindRing = [];
    this._rewindConfirmed = false;
    this._achievementsDisabledThisSession = false;
    // C64 tracking policy (modules/c64-tracking.js): why tracking is off this
    // session ('rewind' | 'speed' | null), the per-session speed latch, the
    // fixed-cadence evaluation clock, the verified identity (set by the C64
    // identity resolver) and the WebGL context-loss pause flag.
    this._achievementsDisabledReason = null;
    this._c64SpeedLatched = false;
    this._c64EvalClock = createC64EvalClock();
    this._c64Identity = null;
    this._c64GlContextLost = false;
    noteC64PauseChange(this);

    // Server-side play session bookkeeping (Phase 4 endpoints).
    // No-op when the user isn't signed in.
    this._serverSessionId = null;
    this._serverHeartbeatInterval = null;
    this._serverSessionStartedAt = 0;
    this._serverSessionMeta = null; // {system, romHash, gameSlug, gameTitle, durationStart}
    this._libraryExitInFlight = null;
    this._canonicalTitle = null;
    this._canonicalTitleKey = null;
    this._lobbyTitleCache = new Map();
    this._lobbyTitleLookupKey = null;

    // Cross-device save sync (Phase 6). SaveSync fetches the server's
    // save index on sign-in and handles upload/download/conflict.
    this.saveSync = new SaveSync();
    this._scratchDiskName = 'Scratch';

    // Load feature flags from server (non-blocking).
    // Flags are used by the admin panel and will gate module init
    // as features are extracted into standalone modules.
    moduleRegistry.load().then(async () => {
      // Wire the theme system's flag checker so disabled `theme:<id>` flags
      // filter the picker + settings grid. Done post-load so the first fetch
      // result is available before anything renders the catalog, and before
      // syncUserSettings hydrates the account's theme: until the checker is
      // wired every theme counts as enabled, so a disabled server theme would
      // be applied and then bounced back to the default on every load. A
      // failed fetch leaves it unwired, so a network blip never erases a
      // saved theme.
      setThemeFlagSource(moduleRegistry);
      window.dispatchEvent(new CustomEvent('retroplay:theme-flags-loaded'));

      await syncUserSettings(this, authState.getCurrentUser());
      // `_clipsEnabled` was eagerly set in the constructor when isEnabled()
      // returns the optimistic `true` fallback. Re-evaluate now that real
      // flag values (including the localStorage override) are loaded.
      this._clipsEnabled = !navigator.maxTouchPoints && moduleRegistry.isEnabled('video_clips');
      this._asyncEnabled = moduleRegistry.isEnabled('async_multiplayer');
      // Re-apply leaderboard gating now real flag values are loaded (fail-closed).
      // Also refresh the already-loaded game: a fast launch's loadGame can beat
      // this fetch, which left the whole session ungated (and exposed to the
      // reset boot-garbage latch the gate would otherwise refuse).
      this.cheevosEngine.setLeaderboardGating(leaderboardGatingEnabled);
      this.cheevosEngine.refreshLeaderboardGating();

      // Settle funnel instrumentation (RP-E15). Fail-closed on purpose — a
      // data-collection path must stay silent when the flag is off or unknown,
      // so it reads `=== true` rather than isEnabled()'s fail-open check. Steps
      // fired before this point (notably `landing`) are buffered by the module
      // and flush here.
      configureFunnel({ enabled: moduleRegistry.getFeatures().funnel_analytics === true });

      // Controller Profiles (task 3.11): construct + wire the InputManager now
      // that real flags are known (fail-closed `=== true`). Self-guarding + never
      // throws, so it can't break boot; flag-OFF leaves the legacy input path
      // untouched. Must run after flags load — the seed is OFF during dev.
      await this._setupControllerProfiles();
      void this._runControllerUi('startControllerUi', () => this.state === State.MENU);

      const isLocal = location.hostname === 'localhost' || location.hostname === '127.0.0.1' || location.hostname.endsWith('.trycloudflare.com');
      if (!isLocal) {
        document.querySelectorAll('.system-btn').forEach(el => {
          if (!moduleRegistry.isEnabled(`system:${el.dataset.system}`)) {
            el.style.display = 'none';
          }
        });
      }

      // Gate the Replays nav link on the replay_browse feature flag.
      if (moduleRegistry.isEnabled('replay_browse')) {
        document.getElementById('nav-replays-desktop')?.classList.remove('hidden');
        document.getElementById('nav-replays-mobile')?.classList.remove('hidden');
      }

      if (this.state === State.MENU) {
        this._loadTrendingCard();
        this._loadTopGamesCard();
      }

      initControlsDialog(this);

      const discordUrl = moduleRegistry.getFeatures().discordInviteUrl;
      if (discordUrl) {
        document.querySelectorAll('[data-discord-invite-link]').forEach((link) => {
          link.href = discordUrl;
        });
        document.getElementById('discord-header-link')?.classList.remove('hidden');
        document.getElementById('nav-discord-mobile')?.classList.remove('hidden');
        const footerWrap = document.getElementById('discord-footer-link');
        if (footerWrap) {
          footerWrap.classList.remove('hidden');
        }
        const card = document.getElementById('discord-card');
        if (card) {
          card.classList.remove('hidden');
        }
      }

      // ── Persistent chat ──────────────────────────────
      this._applyChatPreferences();

      // ── Homepage v2 ──────────────────────────────────
      // Boot treats v2 as the default before flags arrive. Re-run once the
      // server value is authoritative so an explicit emergency OFF can still
      // restore legacy, while the normal ON path remains v2 throughout.
      if (this.state === State.MENU) this._loadMenuData();
    });

    if (window.location.pathname !== '/') {
      document.querySelectorAll('.screen').forEach(el => el.classList.add('hidden'));
    }

    this._bindUI();
    initModalFocusManager();
    // Before bindAuth: that is where the outbox's boot flush starts.
    this._wireSessionEndOutbox();
    bindAuth(this);
    // Homepage v2: authOnly panels resolve at mount, so remount when the
    // signed-in identity changes. v1 is untouched — the gate is fail-closed.
    authState.subscribe(() => {
      // Draft overlays are session state: an identity change clears them (spec 9.2).
      authoredLoaderModule?.clearAuthoredDrafts(this);
      if (this._homeHost && this._homeHostUser !== (authState.getCurrentUser()?.id || null)) {
        this._homeHost.closeActions?.();
      }
      // Controller Profiles (task 3.11): an identity change is a §2.14 lifecycle
      // change — fence in-flight init/switch + dispose live gameplay. No-op flag-OFF.
      // authState only fires this on a real identity/auth change, not routine
      // same-user refreshes, so it won't disturb a live session gratuitously.
      this._notifyControllerAccountChange();
      this._syncHomeCreatorLink();
      void this._runControllerUi('handleControllerAuthChange', () => this.state === State.MENU);
      if (this._homeV2Enabled() && this.state === State.MENU) this._loadMenuData();
    });
    bindThemePicker(this);
    bindGameMoreMenu(this);
    // Achievement authoring: the flag and sign-in listeners behind the cached admin gate, and
    // the Admin rows' and author confirm's handlers (PR 2 README decisions 10 and 12).
    authoringHost.bindAuthoringHost(this);
    bindThemeShowcase(this);
    initGlobalSearch(this);
    this._bindNetwork();
    this._bindGamepadDetection();
    this._initDockAutoHide();
    this._selectSystem(this.system.id);
    initSaveDB(authState.getCurrentUser()?.id).then(() => this._loadSavesList());
    this._checkJoinLink();

    // Periodically refresh the LIVE NOW + TRENDING cards while on the menu.
    // v1 only — the v2 panel host owns its own refresh cadence (Phase 5).
    setInterval(() => {
      if (this.state === State.MENU && (!this._homeV2Enabled() || this._homeFallbackActive)) {
        this._loadActiveGamesCard();
        this._loadTrendingCard();
      }
    }, 10000);

    // Advance live room thumbnails on a steady tick so they read as a
    // stop-motion of the host's game rather than a frozen first frame. The
    // animator only touches img[data-screenshot-room] (home + rooms surfaces).
    this._stopRoomThumbnailAnimator = startRoomThumbnailAnimator();
  }

  get _voiceConnection() {
    return this.hostedNetplay || this._voiceChat || null;
  }

  /** Spectators are listen-only; expose room mic controls only to voice-capable players. */
  _setRoomMicControlsVisible(visible) {
    const show = !!visible && !validatedMultitapProfile(this._roomProfile);
    document.getElementById('btn-voice')?.classList.toggle('hidden', !show);
    const sidebarMic = document.getElementById('btn-sidebar-mic');
    sidebarMic?.classList.toggle('hidden', !show);
    if (!show) {
      document.getElementById('btn-voice')?.classList.add('voice-muted');
      sidebarMic?.classList.remove('mic-active');
      roomChat.syncRoomMicAccessibility(false);
    }
  }

  // ── Desktop Dock Auto-Hide ──────────────────────────────
  // Hides the bottom icon toolbar unless the cursor is near the
  // bottom edge of the window (macOS Dock auto-hide style).
  _initDockAutoHide() {
    if ('ontouchstart' in window) return; // touch: keep mobile top bar
    const dock = document.getElementById('game-controls');
    const screenGame = document.getElementById('screen-game');
    if (!dock || !screenGame) return;

    const REVEAL_PX = 120;
    // The ⋯ "More actions" menu lives inside the dock and opens upward, so
    // retracting the dock while it's open would slide the open menu off-screen
    // (e.g. when the cursor rolls onto a menu row or group divider above the
    // bar). Keep the dock put whenever the menu is open.
    const moreMenuOpen = () => {
      const m = document.getElementById('game-more-menu');
      return !!m && !m.classList.contains('hidden');
    };

    window.addEventListener('mousemove', (e) => {
      // H5: the game room docks the toolbar; the classic auto-hide is off.
      if (isRoomMode(this)) return;
      if (screenGame.classList.contains('hidden')) return;
      if (window.innerWidth <= 800) return;
      if (document.body.classList.contains('tour-active')) return;
      if (moreMenuOpen()) return;
      const inRevealZone = e.clientY >= window.innerHeight - REVEAL_PX;
      // A user can reach for the dock during its initial peek. Keep it open in
      // that case instead of letting the peek timer retract it from under the
      // pointer just as they try to click an action.
      if (this._dockPeekTimer) {
        if (!inRevealZone) return;
        clearTimeout(this._dockPeekTimer);
        this._dockPeekTimer = null;
      }
      if (inRevealZone) {
        dock.classList.remove('dock-hidden');
      } else {
        dock.classList.add('dock-hidden');
      }
    });

    document.addEventListener('mouseleave', () => {
      if (isRoomMode(this)) return;
      if (screenGame.classList.contains('hidden')) return;
      if (window.innerWidth <= 800) return;
      if (document.body.classList.contains('tour-active')) return;
      if (moreMenuOpen()) return;
      if (this._dockPeekTimer) return;
      dock.classList.add('dock-hidden');
    });
  }

  // ── Persistent Chat ────────────────────────────────────

  _initPersistentChat() {
    if (this._chatClient || this._chatInitPromise || this._chatEnabled === false) return this._chatInitPromise;
    const user = authState.getCurrentUser();
    if (!user) return;
    const expectedUserId = user.id;
    const generation = this._chatGeneration || 0;
    let initializingClient = null;
    let initializingPanel = null;

    const initialization = (async () => {
      try {
        const { ChatClient } = await import('./chat-client.js');
        const { ChatPanel } = await import('./chat-ui.js');
        const currentUser = authState.getCurrentUser();
        if ((this._chatGeneration || 0) !== generation
            || currentUser?.id !== expectedUserId
            || this._chatEnabled === false
            || !moduleRegistry.isEnabled('persistent_chat')) return false;

        // Keep construction local until every synchronous setup step succeeds.
        // A thrown panel constructor/setup must not leave a client reference
        // that suppresses every later retry.
        initializingClient = new ChatClient();
        initializingPanel = new ChatPanel(initializingClient, {
          voiceEnabled: this._voiceChatEnabled !== false,
        });
        initializingPanel.onInputFocus = () => this.acquireGameplayInput('persistent-chat');
        initializingPanel.onInputBlur = () => this.releaseGameplayInput('persistent-chat');
        initializingPanel.onChatDisabled = () => this._teardownPersistentChat();
        initializingPanel.onReportUser = ({
          userId, username, chatLog, voiceLog, dmChannelId, dmMessageId,
        }) => {
          this._reportPanel?.open({
            players: [{ userId, username }],
            chatLog,
            voiceLog,
            roomCode: '',
            dmChannelId,
            dmMessageId,
          });
        };
        initializingPanel.onBeforeVoiceCall = () => {
          if (this._voiceChat) {
            this._voiceChat.destroy();
            this._voiceChat = null;
            this._setRoomMicControlsVisible(false);
            this._flash('Room voice ended, started DM voice call');
          }
          if (this.hostedNetplay) {
            this.hostedNetplay.stopVoice?.();
          }
        };
        initializingClient.connect();
        initializingPanel.show();
        this._chatClient = initializingClient;
        this._chatPanel = initializingPanel;
        this._chatInitializationError = null;
        return true;
      } catch (error) {
        // Resolve the fire-and-forget operation as a retryable failure. Cleanup
        // is intentionally local so an unrelated/newer chat generation is not
        // invalidated, and no rejected outer async wrapper can escape.
        try { initializingPanel?.destroy(); } catch { /* partial setup */ }
        try { initializingClient?.destroy(); } catch { /* partial setup */ }
        if (this._chatPanel === initializingPanel) this._chatPanel = null;
        if (this._chatClient === initializingClient) this._chatClient = null;
        this._chatInitializationError = error;
        return false;
      }
    })();
    this._chatInitPromise = initialization;
    const clearInitialization = () => {
      if (this._chatInitPromise === initialization) this._chatInitPromise = null;
    };
    // This method is deliberately non-async: callers receive this exact
    // handled promise instead of a distinct rejected outer wrapper.
    void initialization.then(clearInitialization, clearInitialization);
    return initialization;
  }

  _applyChatPreferences({ chatEnabled = this._chatEnabled, voiceEnabled = this._voiceChatEnabled } = {}) {
    this._chatEnabled = chatEnabled !== false;
    this._voiceChatEnabled = voiceEnabled !== false;
    gameActions.refreshGameToolbarAvailability(this, 'chat-prefs');
    roomChat.syncRoomChatAvailability(this);
    if (!authState.getCurrentUser() || !moduleRegistry.isEnabled('persistent_chat') || !this._chatEnabled) {
      this._teardownPersistentChat();
      return;
    }
    if (!this._chatClient) void this._initPersistentChat();
    this._chatPanel?.setVoiceEnabled(this._voiceChatEnabled);
  }

  _teardownPersistentChat() {
    this.releaseGameplayInput('persistent-chat');
    // Invalidate any dynamic-import continuation before releasing references.
    // A direct A→B switch can start B while A's initialization is still
    // loading; the stale continuation must never mount A's client afterward.
    this._chatGeneration = (this._chatGeneration || 0) + 1;
    this._chatInitPromise = null;
    this._chatInitializationError = null;

    const panel = this._chatPanel;
    const client = this._chatClient;
    this._chatPanel = null;
    this._chatClient = null;

    // ChatPanel destroys ChatVoice, which sends hangup/presence and stops local
    // media. Keep the authenticated socket usable until that teardown finishes.
    try {
      panel?.destroy();
    } finally {
      client?.destroy();
    }
  }

  _endDmVoiceIfActive() {
    if (this._chatPanel?._chatVoice?.active) {
      this._chatPanel._chatVoice.endCall();
      this._flash('DM voice call ended, joined room voice');
    }
  }

  // ── System Selection ────────────────────────────────────

  _selectSystem(systemId) {
    if (!this._applySystemState(systemId)) return;
    // Only load homepage data when we're actually on the menu screen.
    // Deep-linked pages (/activity, /friends, /u/..., etc.) don't need
    // ROM lists, community cards, or achievement definitions.
    if (window.location.pathname === '/' || !document.getElementById('screen-menu')?.classList.contains('hidden')) {
      this._loadMenuData();
    }
  }

  /**
   * Homepage v2 system-filter delegate: apply the system affordances, then
   * surgically refresh only the panels that follow the filter — never the
   * full-menu reload _selectSystem triggers (spec §4.4).
   */
  async _setHomeSystem(systemId) {
    if (!this._applySystemState(systemId)) return;
    const sys = SYSTEMS[systemId];
    await this._homeHost?.setSystem(systemId, sys.name || systemId);
    document.dispatchEvent(new CustomEvent('home:system-changed', { detail: { systemId } }));
  }

  /** Everything system selection changes EXCEPT menu-data loading — shared
   *  by _selectSystem (v1, reloads the menu) and _setHomeSystem (v2). */
  _applySystemState(systemId, { preservePlayIntent = false } = {}) {
    const sys = SYSTEMS[systemId];
    if (!sys) return false;
    if (!preservePlayIntent && this.system?.id && this.system.id !== systemId) cancelPlayIntent('canceled');
    this.system = sys;
    localStorage.setItem('retroplay-system', systemId);
    this.targetFrameTime = 1000 / sys.fps;
    this.input.configure(sys);

    // Update canvas dimensions (PAR-corrected to match display aspect ratio)
    const [arW, arH] = sys.aspectRatio.split('/').map(s => parseFloat(s.trim()));
    this.canvas.width = Math.round(sys.height * arW / arH);
    this.canvas.height = sys.height;

    // Update CSS aspect ratio
    const container = document.getElementById('canvas-container');
    if (container) {
      container.style.aspectRatio = sys.aspectRatio;
      container.style.setProperty('--game-ar-w', String(arW));
      container.style.setProperty('--game-ar-h', String(arH));
    }

    // Update ROM file input accept attribute
    const romInput = document.getElementById('rom-input');
    if (romInput) romInput.accept = sys.extensions.join(',');

    // Update controls reference (menu + in-game hint + cheatsheet)
    this._updateControlsRef();
    this._updateGameControlsHint();
    this._updateCheatsheet();

    // Update system selector active state
    document.querySelectorAll('.system-btn').forEach(el => {
      el.classList.toggle('active', el.dataset.system === systemId);
    });

    // Update touch gamepad for this system
    this._buildTouchGamepad();

    // Show BASIC button for C64
    document.getElementById('btn-basic')?.classList.toggle('hidden', systemId !== 'c64');

    // Clear loaded ROM since system changed
    if (this.romData) {
      this.romData = null;
      this._romDataB64 = null;
      this.romHash = null;
      this.romName = null;
      this._activeRomFilename = null;
      const el = document.getElementById('rom-name');
      if (el) { el.textContent = ''; el.classList.add('hidden'); }
    }
    return true;
  }

  _updateControlsRef() {
    const details = document.querySelector('.controls-ref');
    if (!details) return;
    const summary = details.querySelector('summary');
    if (summary) summary.textContent = `${this.system.name} Controls`;
    const grid = details.querySelector('.controls-grid');
    if (!grid) return;
    grid.innerHTML = this.system.controls.map(c =>
      `<div class="ctrl-group">` +
      `<span class="ctrl-label">${c.label}</span>` +
      `<span class="ctrl-keys">${c.keys}</span>` +
      `</div>`
    ).join('');
  }

  /** Populate the in-game keyboard controls hint (below the canvas). */
  _updateGameControlsHint() {
    const el = document.getElementById('game-controls-hint');
    if (!el) return;
    el.innerHTML = this.system.controls
      .filter(c => c.label !== 'Gamepad' && c.label !== 'Keyboard')
      .map(c => `<span><span class="hint-label">${c.label}</span> ${c.keys}</span>`)
      .join('');
    if (!('ontouchstart' in window)) {
      el.innerHTML += '<span><span class="hint-label">EXIT</span> ESC ESC</span>';
    }
  }

  _buildTouchGamepad() {
    const container = document.getElementById('touch-gamepad');
    if (!container) return;

    // N64 gets its own dedicated layout (D-pad + analog stick, L/Z/R triggers,
    // Start, A/B + C-button cluster) approximating the real controller. The
    // joystick/D-pad mode toggle doesn't apply there (both are always shown).
    const isN64 = this.system.id === 'n64';
    container.classList.toggle('n64', isN64);
    if ('ontouchstart' in window) {
      document.getElementById('btn-toggle-pad')?.classList.toggle('hidden', isN64);
    }
    if (isN64) { this._buildN64TouchGamepad(container); return; }

    const buttons = this.system.buttons;
    // A face override (Genesis) brings its own letters and a six-button arrangement.
    const face = touchFaceFor(this.system);
    const labels = { ...(this.system.touchLabels || {}), ...(face?.labels || {}) };
    const six = face?.six || null;
    const hasLR = !six && 'L' in buttons && 'R' in buttons;
    const hasXY = 'X' in buttons && 'Y' in buttons;
    const hasA = 'A' in buttons;
    const hasB = 'B' in buttons;
    const actionCount = (hasA ? 1 : 0) + (hasB ? 1 : 0) + (hasXY ? 2 : 0);
    const singleAction = actionCount === 1;

    const selLabel = labels.SELECT || 'SEL';
    const startLabel = labels.START || 'START';

    const useJoystick = this.input.touchControlModeFor(this.system) === 'joystick';
    const leftSide = useJoystick
      ? `<div class="touch-joystick" aria-label="Joystick">
          <div class="joystick-ring"></div>
          <div class="joystick-knob"></div>
        </div>`
      : `<div class="touch-dpad">
          <button class="touch-btn dpad-up" data-btn="UP" aria-label="Up"></button>
          <button class="touch-btn dpad-left" data-btn="LEFT" aria-label="Left"></button>
          <button class="touch-btn dpad-center"></button>
          <button class="touch-btn dpad-right" data-btn="RIGHT" aria-label="Right"></button>
          <button class="touch-btn dpad-down" data-btn="DOWN" aria-label="Down"></button>
        </div>`;

    container.innerHTML = `
      ${leftSide}
      ${hasLR ? `<div class="touch-shoulders">
        <button class="touch-btn shoulder-btn" data-btn="L">L</button>
        <button class="touch-btn shoulder-btn" data-btn="R">R</button>
      </div>` : ''}
      <div class="touch-right">
        <div class="touch-meta">
          ${'SELECT' in buttons ? `<button class="touch-btn meta-btn" data-btn="SELECT">${selLabel}</button>` : ''}
          ${'START' in buttons ? `<button class="touch-btn meta-btn" data-btn="START">${startLabel}</button>` : ''}
        </div>
        <div class="touch-ab ${six ? 'touch-ab-6' : hasXY ? 'touch-ab-4' : ''} ${singleAction ? 'touch-ab-single' : ''}">
          ${six ? [
            ...six.top.map((btn) => `<button class="touch-btn ab-btn btn-six-top" data-btn="${btn}">${labels[btn] || btn}</button>`),
            ...six.bottom.map((btn) => `<button class="touch-btn ab-btn btn-a" data-btn="${btn}">${labels[btn] || btn}</button>`),
          ].join('') : `
          ${hasXY ? `<button class="touch-btn ab-btn btn-y" data-btn="Y">${labels.Y || 'Y'}</button>` : ''}
          ${hasXY ? `<button class="touch-btn ab-btn btn-x" data-btn="X">${labels.X || 'X'}</button>` : ''}
          ${hasB ? `<button class="touch-btn ab-btn btn-b" data-btn="B">${labels.B || 'B'}</button>` : ''}
          ${hasA ? `<button class="touch-btn ab-btn btn-a" data-btn="A">${labels.A || 'A'}</button>` : ''}`}
        </div>
      </div>
    `;
    this._augmentTouchGamepadForManager(container);
  }

  /**
   * Dedicated N64 touch layout — approximates the official controller and
   * exposes every N64 input. Left: small D-pad (DPAD_* bits) above the analog
   * stick. Top corners: L / R shoulders. Center: Start above the large Z
   * trigger (Z is the most-used N64 trigger, kept low for the thumb). Right:
   * yellow C-button cluster above
   * the equal-size blue A / green B face buttons. Every data-btn is a key in
   * N64_SYSTEM.buttons; all reach the core via the touchButtons→injectP1Input
   * path, and the stick drives proportional analog (see input.js joystickAxes).
   */
  _buildN64TouchGamepad(container) {
    container.innerHTML = `
      <div class="touch-n64-triggers">
        <button class="touch-btn n64-trigger trig-l" data-btn="SELECT" aria-label="L shoulder">L</button>
        <button class="touch-btn n64-trigger trig-r" data-btn="R2" aria-label="R shoulder">R</button>
      </div>
      <div class="touch-n64-left">
        <div class="touch-dpad n64-dpad">
          <button class="touch-btn dpad-up" data-btn="DPAD_UP" aria-label="D-pad Up"></button>
          <button class="touch-btn dpad-left" data-btn="DPAD_LEFT" aria-label="D-pad Left"></button>
          <button class="touch-btn dpad-center"></button>
          <button class="touch-btn dpad-right" data-btn="DPAD_RIGHT" aria-label="D-pad Right"></button>
          <button class="touch-btn dpad-down" data-btn="DPAD_DOWN" aria-label="D-pad Down"></button>
        </div>
        <div class="touch-joystick" aria-label="Analog Stick">
          <div class="joystick-ring"></div>
          <div class="joystick-knob"></div>
        </div>
      </div>
      <div class="touch-n64-center">
        <button class="touch-btn meta-btn n64-start" data-btn="START" aria-label="Start">START</button>
        <button class="touch-btn n64-ztrigger" data-btn="L2" aria-label="Z trigger">Z</button>
      </div>
      <div class="touch-n64-right">
        <div class="touch-c-cluster">
          <button class="touch-btn c-btn c-up" data-btn="X" aria-label="C Up"></button>
          <button class="touch-btn c-btn c-left" data-btn="L" aria-label="C Left"></button>
          <button class="touch-btn c-btn c-right" data-btn="R" aria-label="C Right"></button>
          <button class="touch-btn c-btn c-down" data-btn="A" aria-label="C Down"></button>
        </div>
        <div class="touch-n64-ab">
          <button class="touch-btn n64-face btn-n64-b" data-btn="Y" aria-label="B button">B</button>
          <button class="touch-btn n64-face btn-n64-a" data-btn="B" aria-label="A button">A</button>
        </div>
      </div>
    `;
    this._augmentTouchGamepadForManager(container);
  }

  /**
   * flag-ON (task 4.1) touch-DOM augmentation, appended AFTER the byte-identical
   * innerHTML so a flag-OFF build is unchanged. Tags the analog joystick with its
   * catalog action id (`data-analog`, satisfying touch-layout.js's contract — the
   * touch UI routing reads it to register the analog contact), and adds the
   * tainted-contact recovery overlay (§2.8/§3.4): a hidden "tap to resume"
   * affordance shown by _syncTouchRecoveryUI while the touch seat is suspended.
   * The tap itself resumes via InputManager's own consumed-recovery-tap handling.
   */
  _augmentTouchGamepadForManager(container) {
    this._controllerRuntime?.augmentTouchGamepad(this, container);
  }

  _scheduleKeyHandlerCaptureRestore(sessionEmu, beforeRestore = null) {
    setTimeout(() => {
      if (this.emu !== sessionEmu) return;
      beforeRestore?.();
      sessionEmu.restoreAddEventListener?.();
    }, 2000);
  }

  /**
   * Create an emulator instance for the current system.
   * For WASM cores, this is async (downloads the core on first use).
   */
  async _createEmulator({ lockstep = false, multiplayer = false, coreName = null, n64ClipRecording = false, startupOwner = null, replayInput = null } = {}) {
    if (this._unsafeReplayBoundary) {
      this._flash('Reload the page to play again.');
      return null;
    }
    if (this.system?.id === 'genesis' && isGenesis32xRom(this.romData)
        && coreName && coreName !== 'picodrive') {
      this._flash('This 32X game requires PicoDrive');
      return null;
    }
    const generation = (this._emulatorGeneration || 0) + 1;
    this._emulatorGeneration = generation;
    const system = this.system;
    const factorySession = this._lockstepSession;
    const factoryRom = this.romData, factoryCanvas = this.canvas, factoryNet = this.net;
    const factorySocket = factoryNet?.ws, factorySocketGeneration = factoryNet?._socketGeneration;
    const factoryRoomCode = factoryNet?.roomCode, factoryAssignment = factoryNet?.roomAssignment;
    const factoryAssignmentFields = factoryAssignment == null ? null : Object.freeze(Object.getOwnPropertyDescriptors(factoryAssignment));
    const factoryKey = roomLaunchSessionKey(this), factoryIntent = this._gameLaunchIntentGeneration;
    const factoryCandidate = this._pendingGameLaunchCandidate, factoryTitleSession = this._multitapSession;
    const factoryRetirement = this._multitapRetirement;
    let factoryRomBytes = null;
    const factoryCurrent = (canvas = factoryCanvas) => this._emulatorGeneration === generation && this.system === system
      && this.romData === factoryRom && sameOrdinaryRomBytes(factoryRom, factoryRomBytes)
      && this.canvas === canvas && this.net === factoryNet
      && factoryNet?.ws === factorySocket && factoryNet?._socketGeneration === factorySocketGeneration
      && factoryNet?.roomCode === factoryRoomCode && factoryNet?.roomAssignment === factoryAssignment
      && connectionAssignmentCurrent(factoryAssignment, factoryAssignmentFields)
      && this._gameLaunchIntentGeneration === factoryIntent && this._pendingGameLaunchCandidate === factoryCandidate
      && this._multitapSession === factoryTitleSession && !this._unsafeReplayBoundary
      && (!this._multitapRetirement || this._multitapRetirement === factoryRetirement)
      && sameLaunchSessionKey(factoryKey, roomLaunchSessionKey(this))
      && (!startupOwner || this._lockstepSession === factorySession && !factorySession?.settled);
    if (factoryRetirement) {
      const retirement = await factoryRetirement;
      if (!factoryCurrent()) return null;
      if (retirement?.ok === false) { this._flash('Reload the page to play again.'); return null; }
    }
    const titleGuard = ordinaryRomIdentityGuard(this);
    const identity = titleGuard && await titleGuard;
    if (titleGuard && !identity) return null;
    if (identity instanceof Uint8Array) factoryRomBytes = identity;
    if (!factoryCurrent()) return null;
    let JsnesCore;
    if (system?.id === 'nes') {
      const earlier = this.emu, canvas = this.canvas, input = this.input, session = this._lockstepSession;
      const current = () => factoryCurrent()
        && this.emu === earlier && this.canvas === canvas && this.input === input && !this._unsafeReplayBoundary
        && (!startupOwner || (this._lockstepSession === session && !session?.settled));
      try { ({ JsnesCore } = await this._loadJsnesCoreModule()); }
      catch (error) { if (!current()) return null; throw error; }
      if (!current()) return null;
    }
    if (system?.id === 'c64' && !C64DrivesCore) {
      try { await loadC64DrivesCore(); }
      catch (error) { if (!factoryCurrent()) return null; throw error; }
      if (!factoryCurrent()) return null;
    }

    // Clean up previous emulator
    if (this.virtualPads) {
      this.virtualPads.destroy();
      this.virtualPads = null;
      resetC64JoystickPortControl(this);
      this.input.useRealGamepads(null);
      if (!factoryCurrent()) return null;
    }
    if (this.emu) {
      const oldCore = this.emu;
      startupOwner?.onRetiring?.(oldCore);
      if (!factoryCurrent()) return null;
      try { oldCore.destroy(); } catch (error) {
        startupOwner?.onRetirementFailed?.(oldCore, error);
        throw error;
      }
      const retirement = startupOwner?.onRetired(oldCore);
      if (this.emu === oldCore) this.emu = null;
      if (retirement?.confirmed === false) return null;
      if (!factoryCurrent()) return null;
    }

    // The canvas swap below orphans every consumer of the old element. A
    // ClipRecorder surviving it keeps capturing the detached canvas + dead
    // audio node — black, silent clips for the rest of the session (seen when
    // a new game starts without full teardown, e.g. menu-nav mid-game). Tear
    // both down here; they're lazily rebuilt against the fresh canvas
    // (_initClipRecorder on ROM load, _getCanvasStream on next use).
    this._destroyClipRecorder();
    if (!factoryCurrent()) return null;
    this._releaseCanvasStream();
    if (!factoryCurrent()) return null;

    // Replace canvas to clear any prior WebGL/2D context — a canvas
    // can only have one context type, so switching cores requires a fresh one.
    const old = this.canvas;
    const fresh = document.createElement('canvas');
    fresh.id = old.id;
    fresh.width = old.width;
    fresh.height = old.height;
    old.parentNode.replaceChild(fresh, old);
    this.canvas = fresh;
    // A fresh canvas has a live context, and the old core (destroyed above) has
    // no main loop left to resume: clear any context loss or visibility pause
    // it left, or C64 tracking would treat the new core as paused.
    this._c64GlContextLost = false;
    this._wasmPausedByVisibility = false;
    noteC64PauseChange(this);
    if (!factoryCurrent(fresh)) return null;
    // The title tombstone stays closed until the actual successor core has
    // initialized successfully and still owns these validated ROM bytes.
    // X8: the room names every new game canvas (no room, no call).
    callRoom(this, 'onCanvasReplaced');
    if (!factoryCurrent(fresh)) return null;

    clearTimeout(this._glRecoveryTimeout);
    fresh.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      console.warn('[webgl] Context lost');
      this._c64GlContextLost = true;
      noteC64PauseChange(this);
      if (this.emu?.selfDriving && typeof this.emu.module?.pauseMainLoop === 'function') {
        this.emu.module.pauseMainLoop();
      }
      this._glRecoveryTimeout = setTimeout(() => {
        console.error('[webgl] Context not restored after 5s');
        this._handleCrash(new Error('WebGL context lost and not restored'));
      }, 5000);
    });
    fresh.addEventListener('webglcontextrestored', () => {
      console.log('[webgl] Context restored');
      clearTimeout(this._glRecoveryTimeout);
      this._c64GlContextLost = false;
      noteC64PauseChange(this);
      if (this.emu?.selfDriving && this._unsafeReplayBoundary !== this.emu
          && typeof this.emu.module?.resumeMainLoop === 'function') {
        this.emu.module.resumeMainLoop();
      }
    });
    fresh.addEventListener('c64-disk-status', (event) => {
      if (this._emulatorGeneration !== generation || this.canvas !== fresh) return;
      diskPicker.setC64DiskStatus(
        this,
        event.detail?.state,
        event.detail?.message,
        event.detail?.operation,
        false,
        event.detail?.drive || 8,
      );
    });
    fresh.addEventListener('c64-runtime-ready', () => {
      if (this._emulatorGeneration !== generation || this.canvas !== fresh) return;
      diskPicker.beginC64Runtime(this);
    });

    if (replayInput) {
      if (system.id !== 'snes' || coreName !== 'snes9x2010_multitap') throw new Error('invalid_multitap_replay');
      let initializingCore = null;
      const current = () => this._emulatorGeneration === generation && this.system === system
        && this.canvas === fresh && !this._unsafeReplayBoundary && replayInput.current()
        && !replayInput.signal.aborted;
      const pendingBoot = { core: null, settled: null, retire: () => this._multitapReplayStartup?.abort() };
      let retirementFailed = false;
      const initialize = async () => {
        try {
          const core = await this._buildReplayCore(system.id, fresh, coreName, replayInput.romBytes, {
            current, signal: replayInput.signal,
            superunoRun: replayInput.superunoRun, superunoTitle: replayInput.superunoTitle,
            superunoCapability: replayInput.superunoCapability,
            onCreated: ownedCore => {
              initializingCore = ownedCore;
              if (!current()) throw new Error('replay startup superseded');
              this.emu = ownedCore;
              pendingBoot.core = ownedCore;
              this._multitapReplayPendingBoot = pendingBoot;
              startupOwner?.onCreated?.(ownedCore);
            },
          });
          if (!current()) {
            try { core.destroy(); }
            catch (error) { retirementFailed = true; this._unsafeReplayBoundary ||= initializingCore || true; throw error; }
            return null;
          }
          this.emu = core;
          return core;
        } catch (error) {
          if (this.emu === initializingCore) this.emu = null;
          if (error.cleanupErrors?.length || retirementFailed) throw error;
          if (!current()) return null;
          throw error;
        } finally {
          if (this._multitapReplayPendingBoot === pendingBoot) this._multitapReplayPendingBoot = null;
          startupOwner?.onInitSettled?.(initializingCore);
        }
      };
      const initialization = initialize();
      pendingBoot.settled = initialization.then(() => {}, () => {});
      return await initialization;
    }

    let factory = system.id === 'nes' ? canvas => new JsnesCore(canvas) : CORE_REGISTRY[system.id];
    if (!factory) throw new Error(`No core registered for ${this.system.id}`);

    if (!coreName && this.system.id === 'genesis') {
      coreName = resolveCoreName(this.system, multiplayer, this.romData);
    }

    if (coreName && this.system.id !== 'nes') {
      const Core = system.id === 'c64' ? C64DrivesCore : LibretroCore;
      factory = (canvas, sys) => new Core(canvas, sys.id, coreName);
    }

    if (lockstep && this.system.id !== 'nes') {
      factory = (canvas, sys, mp) => new LockstepLibretroCore(canvas, sys.id, coreName || resolveCoreName(sys, mp));
    }

    if (!factoryCurrent(fresh)) return null;
    // `let`: an identified launch whose identification failed replaces it (_initLaunchCore).
    let emu = factory(this.canvas, this.system, multiplayer);
    const manualGb = lockstep && system.id === 'gb' && emu instanceof LockstepLibretroCore
      && emu.coreName === 'gambatte_mp' && emu.selfDriving === false;
    const manualGbIsCurrent = () => this._emulatorGeneration === generation && this.emu === emu
      && this.system === system && this.canvas === fresh && this._lockstepSession === factorySession
      && !factorySession?.settled && !this._unsafeReplayBoundary;
    const retireManualGb = () => {
      if (!startupOwner && this._unsafeReplayBoundary !== emu) {
        if (this.emu === emu) this.emu = null;
        emu.destroy();
      }
    };
    // Publish the initializing core so a synchronous exit can tear it down.
    // The generation check below performs a second destroy after init settles,
    // because an Emscripten factory may finish constructing its module after
    // that first teardown attempt.
    this.emu = emu;
    startupOwner?.onCreated(emu);
    try {
      if (emu instanceof LibretroCore) installLibretroGameplayInputGate(emu);
      if (this._gameplayInputLeases?.active) emu.setGameplayInputSuspended?.(true);

      // WASM cores need async initialization
      if (emu instanceof LibretroCore) {
        this._flash('Loading emulator core...');
        try {
          if (n64ClipRecording && this.system.id === 'n64' && !lockstep && !multiplayer) {
            const { n64ClipFactory } = await import('./cores/n64-clip-recording.js');
            await emu.init(await n64ClipFactory());
          } else if (!await this._initLaunchCore(emu, {
            multiplayer, startupOwner, isCurrent: () => factoryCurrent(fresh) && this.emu === emu,
          }, () => {
            // PR 2 README decision 12: identification failed and released the core; a fresh
            // instance boots without an identity. `emu` follows it, so the catch below and
            // every check after init see the core that is initializing.
            if (!factoryCurrent(fresh)) return null;
            emu = factory(this.canvas, this.system, multiplayer);
            this.emu = emu;
            installLibretroGameplayInputGate(emu);
            if (this._gameplayInputLeases?.active) emu.setGameplayInputSuspended?.(true);
            return emu;
          })) return null;
        } catch (err) {
          if (startupOwner) { startupOwner.onInitFailed?.(emu, err); throw err; }
          if (manualGb) {
            const superseded = !manualGbIsCurrent();
            try { retireManualGb(); }
            catch (cleanupError) { throw new AggregateError([err, cleanupError], 'Emulator initialization and cleanup failed', { cause: err }); }
            if (superseded) return null;
            throw err;
          }
          const superseded = this._emulatorGeneration !== generation || this.emu !== emu;
          if (this.emu === emu) this.emu = null;
          emu.destroy();
          if (superseded) return null;
          throw err;
        }
      }

      // A stop or a newer launch won while this core initialized. Never let this
      // stale continuation reclaim the active-emulator slot.
      if (manualGb && !manualGbIsCurrent()) { retireManualGb(); return null; }
      if (!factoryCurrent(fresh) || this.emu !== emu) {
        if (this.emu === emu) this.emu = null;
        if (!startupOwner) emu.destroy();
        return null;
      }
      // This pinned artifact already guards its frontend runner. Enable that
      // per-instance guard before ROM boot when our engine owns its frame clock.
      if (manualGb) emu.module.retroplayManualFrames = true;

      // GB core visibility: the dual/staged-core machinery (?gbcore override +
      // crash valve) can change WHICH build runs with no UI signal — during the
      // 2026-07-04 iPad gate the tester couldn't tell that v2 had silently
      // dropped back to the default core (crash stacks were the only evidence).
      // Flash the active build whenever an override is in play, and call out a
      // valve auto-clear explicitly.
      if (this.system?.id === 'gb') {
        if (window.__gbcoreValveCleared) {
          this._flash(`⚠️ GB core override "${window.__gbcoreValveCleared}" cleared after a crash, running ${emu.coreName}`);
          window.__gbcoreValveCleared = null;
        } else if (localStorage.getItem('retroplay:gbcore')) {
          this._flash(`GB core: ${emu.coreName} (override, ?gbcore=auto to clear)`);
        }
      }

      if (emu instanceof LibretroCore) {
        this.canvas.addEventListener('emulator-abort', (e) => {
          if (this.emu !== emu || this.canvas !== fresh) return;
          if (this.system?.id === 'c64') diskPicker.setC64DiskStatus(this, 'stopped', String(e.detail?.reason || 'Emulator exited'));
          this._handleCrash(new Error(`Emulator abort: ${e.detail?.reason}`));
        }, { once: true });
      }

      if (!factoryCurrent(fresh) || this.emu !== emu) {
        if (this.emu === emu) this.emu = null;
        if (!startupOwner) emu.destroy();
        return null;
      }
      if (factoryRomBytes) {
        // Keep the guard's bytes private for this core's lifetime. Later
        // caller awaits (including voice setup and reset) cannot feed the
        // real native loader a changed, caller-owned Uint8Array.
        const loadROM = emu.loadROM;
        emu.loadROM = (romData, ...options) => {
          const ownsCore = () => this.emu === emu && this._emulatorGeneration === generation && this.canvas === fresh;
          if (!ownsCore() || this.system !== system || this.romData !== factoryRom || romData !== factoryRom
            || !sameOrdinaryRomBytes(factoryRom, factoryRomBytes) || this._unsafeReplayBoundary) {
            const error = new SuperunoError('stale_assignment', 'display', false);
            if (ownsCore()) {
              this.running = false; this.emu = null;
              // Closing A's native identity makes its retained ordinary source
              // sticky-blocked. A replacement source/core receives no writes.
              resolvePresentationCapture(this, 'screenshot');
              if (!startupOwner) try { emu.destroy(); }
              catch (cleanupError) {
                if (this._emulatorGeneration === generation && this.canvas === fresh && this.emu == null)
                  this._unsafeReplayBoundary ||= emu;
                throw new AggregateError([error, cleanupError], 'ROM identity refusal cleanup failed', { cause: error });
              }
              // Lockstep startup keeps its existing retirement ticket owner.
              // Its load catch retires this captured core and records cleanup.
            }
            throw error;
          }
          return loadROM.call(emu, factoryRomBytes.slice(), ...options);
        };
      }
      beginOrdinaryPresentation(this, Object.freeze({ generation, canvas: fresh, system }), Object.freeze({
        rom: factoryRom, core: emu, system, canvas: fresh, previousCanvas: old, emulatorGeneration: generation,
        net: factoryNet, socket: factorySocket, socketGeneration: factorySocketGeneration, roomCode: factoryRoomCode,
        assignment: factoryAssignment, assignmentFields: factoryAssignmentFields,
        key: factoryKey, launchIntent: factoryIntent, candidate: factoryCandidate,
      }));
      roomFlow.noteOrdinaryFactoryChoice(this);
      if (!factoryCurrent(fresh) || this.emu !== emu) {
        if (this.emu === emu) this.emu = null;
        if (!startupOwner) emu.destroy();
        return null;
      }
      return emu;
    } finally { startupOwner?.onInitSettled?.(emu); }
  }

  /**
   * PR 2 README decision 12 (spec 11.4): initialize a launch's libretro core, identified when an
   * author launch or an admin's draft testing wants it (authoring-host.js coreIdentityWanted), after
   * waiting (bounded) for an admin gate decision still in flight (B3.11). An identification that did
   * not start initializes the same core plainly; a failed one releases the core (destroyed again when
   * its abandoned init settles) and `recreate()` builds a fresh instance (null when the launch moved
   * on), which initializes without an identity (Review Focus 1). Resolves the initialized core, or
   * null when the launch moved on.
   */
  async _initLaunchCore(emu, { multiplayer = false, startupOwner = null, isCurrent = () => true } = {}, recreate = () => null) {
    this._coreIdentityFailure = null;
    if (multiplayer || startupOwner) {
      await emu.init();
      return emu;
    }
    if (!await authoringHost.awaitLaunchGate(this, { isCurrent })) {
      if (this.emu === emu) this.emu = null;
      authoringHost.releaseUnidentifiedCore(emu);
      return null;
    }
    if (!authoringHost.coreIdentityWanted(this)) {
      await emu.init();
      return emu;
    }
    const outcome = await authoringHost.identifyLaunchCore(this, emu);
    if (outcome.status === 'identified') return emu;
    if (outcome.status === 'not_started') {
      await emu.init();
      return emu;
    }
    if (this.emu === emu) this.emu = null;
    authoringHost.releaseUnidentifiedCore(emu, outcome.settled);
    const next = recreate();
    if (!next) return null;
    await next.init();
    return next;
  }

  _loadJsnesCoreModule() {
    return import('./cores/jsnes-core.js');
  }

  // ── UI Binding ──────────────────────────────────────────

  /** Toggle local game audio for every system and play mode. */
  _toggleMute() {
    return toggleMute(this);
  }

  _bindUI() {
    mountC64SpecialKeys(this);
    // Site header SPA navigation
    document.querySelectorAll('.site-nav-link[data-page]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const page = link.dataset.page;
        if (page === 'menu') this._navigateToMenu();
        else if (page === 'friends') this._navigateToFriends();
        else if (page === 'rooms') this._navigateToRooms();
        else if (page === 'top100') this._navigateToTop100();
        else if (page === 'leaderboards') this._navigateToLeaderboards();
        else if (page === 'replays') this._navigateToReplays();
        else if (page === 'controllers') this._navigateToControllers();
      });
    });

    // Also wire the dropdown links (they have the same data-page attrs)
    document.getElementById('site-header-dropdown')?.querySelectorAll('.site-nav-link[data-page]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const page = link.dataset.page;
        if (page === 'menu') this._navigateToMenu();
        else if (page === 'friends') this._navigateToFriends();
        else if (page === 'rooms') this._navigateToRooms();
        else if (page === 'top100') this._navigateToTop100();
        else if (page === 'leaderboards') this._navigateToLeaderboards();
        else if (page === 'replays') this._navigateToReplays();
        else if (page === 'controllers') this._navigateToControllers();
      });
    });

    document.getElementById('footer-contact-link')?.addEventListener('click', (e) => {
      e.preventDefault();
      this._navigateToContact();
    });

    document.getElementById('footer-privacy-link')?.addEventListener('click', (e) => {
      e.preventDefault();
      this._navigateToPrivacy();
    });

    document.getElementById('footer-tos-link')?.addEventListener('click', (e) => {
      e.preventDefault();
      this._navigateToTos();
    });

    document.getElementById('footer-standards-link')?.addEventListener('click', (e) => {
      e.preventDefault();
      this._navigateToCommunityStandards();
    });

    document.getElementById('footer-acknowledgements-link')?.addEventListener('click', (e) => {
      e.preventDefault();
      this._navigateToAcknowledgements();
    });

    const feedbackBtn = document.getElementById('btn-feedback');
    if (feedbackBtn) {
      const handler = async () => {
        feedbackBtn.removeEventListener('click', handler);
        const { initFeedbackPanel } = await import('./modules/feedback-panel.js');
        initFeedbackPanel();
      };
      feedbackBtn.addEventListener('click', handler);
    }

    // Mobile ☰ menu. The module owns every open and close path (the button,
    // an outside click, any row, Escape) so aria-expanded never goes stale,
    // docks the menu below the visible chrome, and wires "Send feedback" to
    // _showContactScreen(). The page-link handlers above only navigate.
    bindSiteNavMenu(this);

    const isLocal = location.hostname === 'localhost' || location.hostname === '127.0.0.1' || location.hostname.endsWith('.trycloudflare.com');
    document.querySelectorAll('.system-btn').forEach(el => {
      if (!isLocal && !moduleRegistry.isEnabled(`system:${el.dataset.system}`)) {
        el.style.display = 'none';
      }
      el.addEventListener('click', () => {
        this._selectSystem(el.dataset.system);
      });
    });

    // ROM filter
    document.getElementById('rom-filter')?.addEventListener('input', (e) => {
      this._filterRomLibrary(e.target.value);
    });
    document.getElementById('rom-filter-ach')?.addEventListener('click', (e) => {
      this._romFilterAch = !this._romFilterAch;
      e.currentTarget.classList.toggle('active', this._romFilterAch);
      e.currentTarget.setAttribute('aria-pressed', String(this._romFilterAch));
      this._applyRomFilters();
    });
    document.getElementById('rom-filter-score')?.addEventListener('click', (e) => {
      this._romFilterScore = !this._romFilterScore;
      e.currentTarget.classList.toggle('active', this._romFilterScore);
      e.currentTarget.setAttribute('aria-pressed', String(this._romFilterScore));
      this._applyRomFilters();
    });

    // ROM file input
    const romInput = document.getElementById('rom-input');
    const romBtn = document.getElementById('rom-btn');
    romBtn.addEventListener('click', () => romInput.click());
    romInput.addEventListener('change', (e) => this._onROMSelected(e));

    // Also support drag & drop on the whole page
    document.addEventListener('dragover', (e) => e.preventDefault());
    document.addEventListener('drop', (e) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (!file) return;
      if (file.name.toLowerCase().endsWith('.nesplay') || file.name.toLowerCase().endsWith('.retroplay')) {
        this._loadSaveFile(file);
      } else {
        this._loadROMFile(file);
      }
    });

    // C64 BASIC mode — boot to BASIC prompt with a blank disk
    document.getElementById('btn-basic')?.addEventListener('click', () => {
      this._launchC64Basic();
    });

    // Join room
    document.getElementById('btn-join').addEventListener('click', () => {
      const code = document.getElementById('room-code-input').value.trim().toUpperCase();
      if (code.length < 4) return this._flash('Enter a room code');
      this._joinRoom(code);
    });

    // Post-ROM-load play-mode prompt
    document.getElementById('btn-play-mode-solo')?.addEventListener('click', (event) => {
      this._closePlayModePrompt();
      if (!this.romData) return;
      void this._choosePlayIntentMode('solo', this._playModeOpener || event.currentTarget);
    });
    document.getElementById('solo-save-mode')?.addEventListener('click', (e) => {
      const seg = e.target.closest('.solo-save-mode-seg');
      if (!seg) return;
      const sys = this.system?.id;
      if (!sys) return;
      this._setSavedSaveMode(sys, seg.dataset.mode === 'fast' ? 'fast' : 'replay');
      this._renderSoloSaveMode(); // re-highlight from the persisted default
    });
    document.getElementById('btn-play-mode-match')?.addEventListener('click', (event) => {
      this._closePlayModePrompt();
      if (!this.romData) return;
      void this._choosePlayIntentMode('match', this._playModeOpener || event.currentTarget);
    });
    document.getElementById('btn-play-mode-create')?.addEventListener('click', (event) => {
      this._closePlayModePrompt();
      if (!this.romData) return;
      void this._choosePlayIntentMode('room', this._playModeOpener || event.currentTarget);
    });
    document.getElementById('btn-play-mode-multitap')?.addEventListener('click', (event) => {
      this._closePlayModePrompt();
      if (!this.romData) return;
      void this._choosePlayIntentMode('multitap', this._playModeOpener || event.currentTarget);
    });
    document.getElementById('btn-play-mode-schedule')?.addEventListener('click', (event) => {
      this._closePlayModePrompt();
      if (!this.romData) return;
      void this._choosePlayIntentMode('schedule', this._playModeOpener || event.currentTarget);
    });
    document.getElementById('btn-play-mode-sbs')?.addEventListener('click', (event) => {
      this._closePlayModePrompt();
      if (!this.romData) return;
      void this._choosePlayIntentMode('sbs', this._playModeOpener || event.currentTarget);
    });
    document.getElementById('btn-play-mode-async')?.addEventListener('click', (event) => {
      this._closePlayModePrompt();
      if (!this.romData) return;
      void this._choosePlayIntentMode('async', this._playModeOpener || event.currentTarget);
    });
    document.getElementById('play-mode-modal-close')?.addEventListener('click', () => {
      this._closePlayModePrompt();
    });
    document.getElementById('btn-play-mode-suggest')?.addEventListener('click', () => {
      this._closePlayModePrompt();
      this._openSuggestGamePanel();
    });
    document.getElementById('btn-invite-suggest')?.addEventListener('click', () => {
      this._closeInviteModal();
      this._openSuggestGamePanel();
    });

    // Room code input — auto uppercase
    const codeInput = document.getElementById('room-code-input');
    codeInput.addEventListener('input', () => {
      codeInput.value = codeInput.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    });

    // Leave room
    document.getElementById('btn-leave')?.addEventListener('click', () => {
      this._leaveRoom();
    });

    // Save / Load state
    document.getElementById('btn-save')?.addEventListener('click', () => this._saveGame());
    initSavesPanel(this);
    document.getElementById('btn-load-save')?.addEventListener('click', () => this._loadSaveFile());

    // Screenshot (T6 source rules and the PNG download live in game-actions.js)
    document.getElementById('btn-screenshot')?.addEventListener('click', () => { void takeScreenshot(this); });
    // T3: Screenshot shows only with a capture source; the guest video's frame
    // comes and goes with its media events (#guest-video is static markup).
    try { gameActions.bindScreenshotSourceRefresh(this); }
    catch (cause) { gameActions.reportScreenshotSourceBindingFailure(cause); }

    // Clip capture
    document.getElementById('btn-clip')?.addEventListener('click', () => this._onClipButton());
    this._bindClipToastCaption();
    // Manual replay save
    document.getElementById('btn-replay')?.addEventListener('click', () => this._onReplayButton());

    // Chat
    if (typeof this._desktopChatSidebarPreferredOpen !== 'boolean') {
      this._desktopChatSidebarPreferredOpen = true;
    }
    this._chatSidebarOpen = !this._isMobileChat() && this._desktopChatSidebarPreferredOpen;
    roomChat.bindChatViewportReconciliation(this);
    document.getElementById('btn-chat')?.addEventListener('click', () => {
      if (this._chatEnabled === false) return;
      if (this._isMobileChat()) {
        if (this._chatSidebarOpen) this._closeChatDrawer();
        else this._openChatDrawer();
      } else {
        this._toggleChatSidebar();
      }
    });
    const chatInput = document.getElementById('chat-input');
    chatInput?.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') this._sendChat();
      if (e.key === 'Escape') this._closeChat();
    });
    chatInput?.addEventListener('blur', () => this._closeChat());

    // Sidebar input, send, close and backdrop bindings (room-mode aware, CH3).
    roomChat.bindSidebarChatControls(this);
    document.getElementById('presence-toggle')?.addEventListener(
      'click', () => roomChat.togglePresenceDisclosure(),
    );

    roomChat.bindRoomChatShortcut(this);

    // Mute toggle. The gate's creation hook must be installed before any
    // AudioContext exists (_bindUI runs from the constructor), so it sits
    // nearest the speakers and every game audio path flows through it.
    gameAudioMute.installGameAudioMute(this);
    document.getElementById('btn-mute')?.addEventListener('click', () => { toggleMute(this); });

    // Voice chat toggle (shared handler for toolbar + sidebar mic buttons)
    this._toggleMic = () => !validatedMultitapProfile(this._roomProfile) && roomChat.toggleRoomMic(this);
    document.getElementById('btn-voice')?.addEventListener('click', this._toggleMic);
    document.getElementById('btn-sidebar-mic')?.addEventListener('click', this._toggleMic);

    // Fullscreen: touch CSS full screen or the desktop Fullscreen API (game-actions.js)
    document.getElementById('btn-fullscreen')?.addEventListener('click', () => { toggleFullscreen(this); });

    // Clean up mobile-fullscreen state if user exits via system gesture
    // (Android edge-swipe, ESC on desktop, etc.)
    const onFullscreenChange = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      if (!fsEl && document.body.classList.contains('mobile-fullscreen')) {
        document.body.classList.remove('mobile-fullscreen');
        this._reconcileChatViewport();
        document.getElementById('game-controls')?.classList.remove('tap-hidden');
        try { screen.orientation?.unlock?.(); } catch {}
      }
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);

    // Swap touch control mode (joystick ↔ d-pad)
    document.getElementById('btn-toggle-pad')?.addEventListener('click', () => {
      const next = this.input.touchControlModeFor(this.system) === 'joystick' ? 'dpad' : 'joystick';
      if (setTouchControlMode(this, next).ok !== true) return;
      this._flash(next === 'joystick' ? 'Analog joystick' : 'D-pad');
    });
    if (!('ontouchstart' in window)) {
      document.getElementById('btn-toggle-pad')?.classList.add('hidden');
    }

    // Swap players
    document.getElementById('btn-swap')?.addEventListener('click', () => {
      this._requestPlayerSwap();
    });
    this._bindSwapPromptActions();

    // Spectator upgrade accept/decline
    document.getElementById('btn-upgrade-accept')?.addEventListener('click', () => {
      document.getElementById('upgrade-prompt')?.classList.add('hidden');
      this.net.sendUpgradeAccept();
    });
    document.getElementById('btn-upgrade-decline')?.addEventListener('click', () => {
      document.getElementById('upgrade-prompt')?.classList.add('hidden');
      this.net.sendUpgradeDecline();
    });

    // Game change
    document.getElementById('btn-change-game')?.addEventListener('click', () => this._showGameChangeOverlay());
    document.getElementById('btn-gc-accept')?.addEventListener('click', () => {
      this.net.sendGameChangeAccept();
      document.getElementById('game-change-prompt')?.classList.add('hidden');
    });
    document.getElementById('btn-gc-decline')?.addEventListener('click', () => {
      this.net.sendGameChangeDecline();
      document.getElementById('game-change-prompt')?.classList.add('hidden');
      this._pendingGameChange = null;
    });
    bindGameChangeOverlay(this);

    // Disk picker
    document.getElementById('btn-disk-picker')?.addEventListener('click', () => this._toggleDiskPicker());
    document.getElementById('btn-c64-drive')?.addEventListener('click', () => this._toggleDiskPicker());
    document.getElementById('btn-c64-drive9')?.addEventListener('click', () => this._toggleDiskPicker(9));
    document.getElementById('disk-eject-btn')?.addEventListener('click', () => diskPicker.ejectDiskPicker(this));
    document.getElementById('disk-copy-btn')?.addEventListener('click', () => this._copyMountedDisk());
    document.getElementById('disk-create-btn')?.addEventListener('click', () => this._createSaveDisk());
    document.getElementById('disk-save-btn')?.addEventListener('click', () => this._saveDisksToAccount());

    const c64Speed = document.getElementById('c64-speed-select');
    c64Speed?.addEventListener('change', (event) => requestC64Speed(this, event.target.value));
    c64Speed?.addEventListener('focus', () => this.acquireGameplayInput('c64-speed'));
    c64Speed?.addEventListener('blur', () => this.releaseGameplayInput('c64-speed'));
    bindC64JoystickPortControl(this);

    // Cheat sheet
    document.getElementById('btn-cheatsheet')?.addEventListener('click', () => this._toggleCheatsheet());
    document.getElementById('btn-c64-guide')?.addEventListener('click', () => this._toggleCheatsheet());
    document.getElementById('cheatsheet-close')?.addEventListener('click', () => this._toggleCheatsheet());

    document.getElementById('btn-gameplay-tour')?.addEventListener('click', async () => {
      const { resetGameplayTourState } = await import('./modules/gameplay-tour.js');
      resetGameplayTourState();
      this._startGameplayTour();
    });

    // Achievements modal
    document.getElementById('achievements-modal-close')?.addEventListener('click', () => this._closeAchievementsModal());
    document.getElementById('achievements-modal')?.addEventListener('click', (e) => {
      if (e.target.id === 'achievements-modal') this._closeAchievementsModal();
    });
    // In-game achievement panel — role=button div, so Enter/Space must work too
    document.getElementById('stat-cheevos')?.addEventListener('click', () => this._toggleCheevoPanel());
    document.getElementById('stat-cheevos')?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this._toggleCheevoPanel();
      }
    });
    document.getElementById('cheevo-panel-close')?.addEventListener('click', () => this._closeCheevoPanel());

    document.getElementById('btn-stats')?.addEventListener('click', () => {
      this._navigateToStats();
    });
    document.getElementById('btn-lb-detail-back')?.addEventListener('click', () => {
      this._navigateToLeaderboards();
    });
    document.getElementById('btn-lb-ach-detail-back')?.addEventListener('click', () => {
      this._navigateToLeaderboards();
    });
    document.getElementById('btn-lb-view-all')?.addEventListener('click', () => {
      this._navigateToLeaderboards();
    });

    // Handle browser back/forward between / and /u.
    window.addEventListener('popstate', () => this._routeFromLocation());

    window.addEventListener('retroplay:start-tour', () => this._startTour());
    window.addEventListener('retroplay:start-gameplay-tour', () => this._startGameplayTour());

    // Use window capture phase — fires before RetroArch's document-level handlers.
    window.addEventListener('keydown', (e) => this._handleGlobalGameplayKeydown(e), true);

    // Rewind (tap to jump back ~10s — solo, non-self-driving cores)
    document.getElementById('btn-rewind')?.addEventListener('click', () => this._rewindLive());
    document.getElementById('rewind-confirm-ok')?.addEventListener('click', () => {
      this._confirmRewind();
    });
    document.getElementById('rewind-confirm-cancel')?.addEventListener('click', () => {
      hideRewindConfirm(this);
    });
    // C64 speed (c64-speed-guard.js): a tracked game confirms before leaving 1x.
    document.getElementById('c64-speed-confirm-ok')?.addEventListener('click', () => confirmC64Speed(this));
    document.getElementById('c64-speed-confirm-cancel')?.addEventListener('click', () => cancelC64Speed(this));

    // Reset: the classic screen resets at once; room mode asks first (T5).
    document.getElementById('btn-reset')?.addEventListener('click', () => { void requestReset(this); });

    // Exit to menu — players confirm before abandoning progress, while
    // spectators can leave immediately because they have no local game state
    // to save.
    document.getElementById('btn-exit')?.addEventListener('click', () => {
      this._requestExitToMenu();
    });

    // "Back to RetroPlay home" follows the same player/spectator rule.
    document.getElementById('back-to-menu')?.addEventListener('click', () => {
      this._requestExitToMenu();
    });
    // "Change game" link → same overlay as the 📂 toolbar button.
    document.getElementById('change-game-link')?.addEventListener('click', () => {
      this._showGameChangeOverlay();
    });
    document.getElementById('exit-confirm-ok')?.addEventListener('click', () => {
      this._exitToMenu();
    });
    document.getElementById('exit-confirm-cancel')?.addEventListener('click', () => {
      hideExitConfirm(this);
    });

    this._initReportPanels();

    // End Turn button (async turn-based multiplayer)
    document.getElementById('btn-end-turn')?.addEventListener('click', () => {
      if (this.asyncGame.isActive) {
        this.asyncGame.endTurn('manual');
      } else if (this.state === State.SOLO) {
        this.asyncGame.showChallengePicker('manual');
      }
    });
    document.getElementById('async-challenge-close')?.addEventListener('click', () => {
      this.asyncGame.hideChallengePicker();
    });
    document.getElementById('async-challenge-search')?.addEventListener('input', (e) => {
      const q = e.target.value.trim().toLowerCase();
      const rows = document.querySelectorAll('#async-challenge-friends .friend-row');
      for (const row of rows) {
        const name = row.querySelector('.friend-row-name')?.textContent?.toLowerCase() || '';
        const username = row.querySelector('.friend-row-presence')?.textContent?.toLowerCase() || '';
        row.classList.toggle('hidden', q && !name.includes(q) && !username.includes(q));
      }
    });

    // Invite a friend (Phase 5.5b) — solo only, opens the modal
    document.getElementById('btn-invite')?.addEventListener('click', () => {
      this._inviteFriend();
    });
    document.getElementById('btn-lobby-invite-friend')?.addEventListener('click', () => {
      if (this._pendingInvite) {
        this._cancelPendingLobbyInvite();
        return;
      }
      if (!this._lobbyRoomCode) return;
      this._openInviteModalForLobby(this._lobbyRoomCode);
    });
    document.getElementById('invite-modal-close')?.addEventListener('click', () => {
      // The X button hides the modal without canceling the invite —
      // users who've already copied the link shouldn't have the room
      // revoked out from under them.
      this._closeInviteModal();
    });
    document.getElementById('btn-invite-copy')?.addEventListener('click', () => {
      this._copyInviteLink();
    });
    document.getElementById('btn-invite-cancel')?.addEventListener('click', () => {
      this._cancelInvite();
    });

    // Input delay tuning
    document.getElementById('btn-delay-down')?.addEventListener('click', () => {
      if (this.rollback) {
        this.rollback.setInputDelay(this.rollback.inputDelay - 1);
        this._flash(`Input delay: ${this.rollback.inputDelay} frames`);
      }
    });
    document.getElementById('btn-delay-up')?.addEventListener('click', () => {
      if (this.rollback) {
        this.rollback.setInputDelay(this.rollback.inputDelay + 1);
        this._flash(`Input delay: ${this.rollback.inputDelay} frames`);
      }
    });

    // Input parity toggle (hosted netplay fairness)
    const parityToggle = document.getElementById('parity-toggle');
    if (parityToggle) {
      parityToggle.checked = this._inputParityEnabled;
      parityToggle.addEventListener('change', () => {
        this.setInputParityEnabled(parityToggle.checked);
        this._flash(parityToggle.checked
          ? 'Input parity ON: both players feel the same latency'
          : 'Input parity OFF: P1 has no added delay');
      });
    }
  }

  /**
   * Report Participant and Report a Problem (in-game More menu). Both have
   * text fields, so each holds its own gameplay-input lease while open
   * (AGENTS.md "Text overlays over a running emulator"). The panels release
   * it on every close path; one left open across a game exit keeps a newly
   * started game neutral until it closes.
   */
  _initReportPanels() {
    this._reportPanel = createLazyReportPanel({
      getPlayers: () => this._getReportablePlayers(),
      getChatLog: () => this._chatLog || [],
      getVoiceLog: () => this._voiceModeration?.getTranscriptLog() || [],
      getRoomCode: () => this.net?.roomCode || '',
      onFlash: (msg) => this._flash(msg),
      onOpen: () => this.acquireGameplayInput('report-participant'),
      onClose: () => this.releaseGameplayInput('report-participant'),
    });
    document.getElementById('btn-report')?.addEventListener('click', () => {
      this._reportPanel.open();
    });
    document.getElementById('btn-report-game')?.addEventListener('click', () => {
      const meta = this._serverSessionMeta || {};
      openGameReport({
        system_id: this.system?.id || meta.system || null,
        rom_hash: this.romHash || meta.romHash || null,
        rom_filename: this.romName || null,
        game_title: meta.gameTitle || this._activeGameTitle?.() || this.romName || null,
      }, {
        onOpen: () => this.acquireGameplayInput('report-game'),
        onClose: () => this.releaseGameplayInput('report-game'),
      });
    });
  }

  _handleGlobalGameplayKeydown(e) {
    if (e.key === 'Escape' && e.repeat && handleGameplayEscape(this, e) === 'handled') {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (typeof this._roomPageRecoveryKey === 'function' && this._roomPageRecoveryKey(e) === true) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (document.querySelector('.game-share-overlay, .suggest-panel:not(.hidden), .report-panel:not(.hidden)')) return;
    // The lazy overwrite picker owns its keys while visible. Its document
    // listener handles Escape/Backquote after this capture-phase guard returns.
    if (document.querySelector('#save-overwrite-modal:not(.hidden)')) return;
    // The What's New dialog owns its keys while open too: its own listener
    // handles Escape, and the global focus trap handles Tab. Stepping aside
    // here (no preventDefault/stopImmediatePropagation) keeps every gameplay
    // shortcut below, including the Escape exit-to-menu arm/fire, from
    // reaching the game underneath while the dialog covers it.
    if (document.getElementById('news-modal')) return;
    if (this._diskPickerOpen) {
      this._diskPickerKeyHandler(e);
      return;
    }
    // K4, K6, K7: a key on a room control, a key a room dialog swallows, or T
    // in room mode belongs to the room. This window capture listener is bound
    // in _bindUI before any game input listener, so stopping here keeps the key
    // from InputHandler and the cores. The room never claims Escape (K3 below).
    recordHardwareKeyUse(this, e);
    const roomClaim = claimRoomKey(this, e);
    if (roomClaim.status === 'claimed' || (roomClaim.status === 'failed' && roomClaim.consume)) {
      e.stopImmediatePropagation();
      return;
    }
    // Every Esc (K3 and X-ESC, both layouts): game-keys.js closes the top
    // surface (a room layer, the Saves panel, a confirm, the controls overlay).
    if (e.key === 'Escape') {
      const esc = handleGameplayEscape(this, e);
      if (esc === 'ignored' || esc === 'yielded') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (esc === 'primed') {
        this._escExitPrimed = Date.now();
        this._flash('Press ESC again to exit');
      } else if (esc === 'exit') {
        // A deliberate second Esc exits at once, without the confirm (the
        // Exit button and the "back to RetroPlay home" link still confirm).
        this._escExitPrimed = 0;
        this._exitToMenu();
      }
      return;
    }
    if (!this.running) return;
    // While the saves panel is open, gameplay shortcuts (disk picker,
    // cheatsheet, HUD) must not fire beneath it — F9 would open the
    // disk picker UNDER the panel (z 150 vs 950) and _diskPickerKeyHandler
    // would hijack every key. Esc is handled above; only Backquote may act.
    if (isSavesPanelOpen() && e.code !== 'Backquote') return;

    // C64 owns its complete keyboard matrix during gameplay. Keep only the
    // RetroPlay shortcuts whose PC keys are outside that matrix (F9/F10/F12).
    // In particular, Ctrl+D must reach VICE and Escape is C64 RUN/STOP.
    // K7: disk-picker.js holds the disk keys' rule; the game room's Controls
    // tab lists the keys from the same function.
    if (diskPicker.isDiskPickerShortcut(this, e)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this._toggleDiskPicker();
    } else if (e.key === 'F10') {
      e.preventDefault();
      e.stopImmediatePropagation();
      // K7: the Controls tab in room mode, today's overlay otherwise.
      if (isRoomMode(this)) callRoom(this, 'toggleControlsTab');
      else this._toggleCheatsheet();
    } else if (e.key === 'F12' && netplayHudApplies(this)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this._toggleHUDVisibility();
    } else if (e.code === 'Backquote' && this._savesShortcutAllowed()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (isSavesPanelOpen()) closeSavesPanel();
      else openSavesPanel(this).catch(() => {});
    }
  }

  _bindGamepadDetection() {
    const refreshOpenControllerCheatsheet = () => {
      if (
        this._cheatsheetOpen
        && this.system?.id !== 'c64'
        && moduleRegistry.getFeatures().controller_hints === true
      ) {
        this._updateCheatsheet();
      }
    };

    window.addEventListener('gamepadconnected', (event) => {
      this._gamepadConnected = true;
      const pad = document.getElementById('touch-gamepad');
      const hint = document.getElementById('game-controls-hint');
      const inGame = this.state === State.PLAYING || this.state === State.SOLO || this.state === State.SPECTATING;
      if (pad && inGame) {
        pad.classList.add('hidden');
      }
      if (hint && inGame) hint.classList.add('hidden');
      // Reflect the new state live if the controls overlay is open.
      refreshOpenControllerCheatsheet();
      if (this.state === State.MENU) {
        void this._runControllerUi('maybeOfferControllerAlias', event.gamepad, () => this.state === State.MENU);
      }
    });

    window.addEventListener('gamepaddisconnected', () => {
      this._gamepadConnected = isGamepadPresent();
      if (!this._gamepadConnected) {
        const pad = document.getElementById('touch-gamepad');
        const hint = document.getElementById('game-controls-hint');
        const inGame = this.state === State.PLAYING || this.state === State.SOLO || this.state === State.SPECTATING;
        if (pad && 'ontouchstart' in window && inGame) {
          pad.classList.remove('hidden');
        }
        if (hint && inGame) hint.classList.remove('hidden');
      }
      // Reflect the change live if the controls overlay is open.
      refreshOpenControllerCheatsheet();
    });
  }

  _bindNetwork() {
    this.net.onMultitapMessage = message => roomFlow.routeMultitapOwner(this, message);
    this.net.onBeforeRoomTransition = (message) => {
      if (this._pendingGameLaunchCandidate == null) return true;
      const predecessorOwner = this._captureRoomSessionOwner();
      return !this._deferCandidateRoomEvent(predecessorOwner, () => {
        predecessorOwner.expectedNet?._handleMessage({ ...message });
      });
    };

    this.net.onAssigned = (playerNum, roomCode, spectator, gameInProgress, hostUsername, hostDisplayUsername, participants, assignment) => {
      const assignedNet = this.net, socket = assignedNet.ws, generation = assignedNet._socketGeneration;
      const connectionCurrent = () => this.net === assignedNet && assignedNet.ws === socket
        && assignedNet._socketGeneration === generation && assignedNet.roomCode === roomCode;
      if (assignment && this.net.roomCode === roomCode) roomFlow.prepareMultitapAssignmentIngress(this, assignment);
      if (!connectionCurrent()) return;
      // Fresh room assignment: any previously known peer identity is stale.
      // The server re-announces a present peer via peer_joined right after.
      this._roomPeerUserId = null;
      this._updateReportButtonVisibility();
      if (!connectionCurrent()) return;
      // Solo rooms skip the lobby — the solo host stays in the game screen
      if (this.state === State.SOLO) {
        // The assignment can arrive before streaming handlers are installed
        // on reload. Keep its viewers for the replacement stream to offer.
        if (playerNum === 1 && !spectator && this.net.roomCode === roomCode && participants) {
          this._spectators = new Map((participants.spectators || []).map(s => [
            s.spectatorId, { username: s.username || null, image: s.image || null },
          ]));
        }
        return;
      }
      if (this._sbsRole === 'racer') return; // already racing (host started before this fired); don't re-route

      const assignmentOwner = {
        launchOwner: this._roomJoinLaunchOwner,
        expectedNet: assignedNet,
        expectedRoomCode: roomCode,
      };
      if (!this._roomAssignmentIsCurrent(assignmentOwner)) return;
      if (!connectionCurrent()) return;
      if (assignment?.roomProfile === 'snes-multitap-v1') return this._multitapAssignmentWork = roomFlow.handleMultitapAssignment(this, assignment, assignmentOwner);
      this._roomProfile = null;

      // Seed presence tracking from server snapshot
      this._spectators = new Map();
      if (participants) {
        for (const s of participants.spectators || []) {
          this._spectators.set(s.spectatorId, { username: s.username || null, image: s.image || null });
        }
      }

      // Side-by-side room. Spectators watch both streams. A joining racer (P2)
      // auto-loads the room ROM and starts its own solo session immediately — the
      // host is already waiting in the lobby, so the race begins as soon as P2 is in.
      if (this._sbsJoining) {
        this._sbsRoom = true;
        if (spectator) {
          this._startSideBySideSpectator(roomCode, participants, assignmentOwner);
        } else {
          // Claim the racer successor before the ROM await. If a prospective
          // launch starts while that download is in flight, the retained room
          // can finish booting without clearing the candidate itself.
          const racerLaunchOwner = assignmentOwner.launchOwner == null
            ? this._beginRoomSessionLaunchIntent()
            : this._beginRoomSessionLaunchIntent({}, assignmentOwner);
          if (racerLaunchOwner == null) return;
          const racerSessionOwner = {
            launchOwner: racerLaunchOwner,
            expectedNet: assignmentOwner.expectedNet,
            expectedRoomCode: roomCode,
          };
          const joinIsCurrent = () => (
            this._roomAssignmentIsCurrent(racerSessionOwner)
            && this._sbsJoining
            && this.state === State.LOBBY
          );
          return (async () => {
            try {
              const rom = await this._downloadROM(roomCode);
              if (!joinIsCurrent()) return;
              if (rom) {
                await this._installRoomRom(rom, { isCurrent: joinIsCurrent });
              }
              if (!joinIsCurrent()) return;
              const racerStart = this._startSideBySideRacer(
                playerNum,
                roomCode,
                participants,
                racerSessionOwner,
              );
              await racerStart;
            } catch (err) {
              if (!this._roomAssignmentIsCurrent(racerSessionOwner)) return;
              console.warn('[sbs] join-as-racer failed', err);
              this._flash('Could not join side-by-side race');
            }
          })();
        }
        return;
      }

      // Self-reconnect: skip lobby, just signal ready so server sends game_start
      if (this._selfReconnecting && gameInProgress) {
        const isHosted = !isLockstepEnabled(this.system?.id, this._roomCoreName, this.romData) &&
                         (this.system?.netplayMode === 'hosted' ||
                         (this.system?.rollbackCapable && this.system?.preferHosted));
        if (isHosted && playerNum !== 1) {
          this.net.sendRomReady('hosted-guest');
        } else {
          this.net.sendRomReady(this.romHash || 'hosted-guest');
        }
        return;
      }

      const lobbyMaxPlayers = Math.max(2, this.system?.maxPlayers || 2);

      // Spectators get player:0 — populate slot usernames from the server snapshot
      if (spectator && participants?.players) {
        this._lobbySlotUsernames = Array(lobbyMaxPlayers).fill(null);
        this._lobbySlotImages = Array(lobbyMaxPlayers).fill(null);
        this._lobbySlotOccupied = Array(lobbyMaxPlayers).fill(false);
        this._lobbyCardCache = {};
        for (const p of participants.players) {
          if (p.slot >= 1 && p.slot <= lobbyMaxPlayers && (p.occupied || p.username)) {
            this._lobbySlotUsernames[p.slot - 1] = p.username ?? '';
            this._lobbySlotImages[p.slot - 1] = p.image || null;
            this._lobbySlotOccupied[p.slot - 1] = !!p.occupied;
          }
        }
      }

      // Solo room spectators skip the lobby — download ROM and start immediately
      if (spectator && gameInProgress) {
        return this._prepareAndStartSpectator(roomCode, assignmentOwner);
      }

      this._showScreen('lobby');
      this._configureLobbySlots(lobbyMaxPlayers);
      document.getElementById('lobby-room-code').textContent = roomCode;
      document.getElementById('lobby-player').textContent = spectator ? 'Spectator' : `You are Player ${playerNum}`;

      const user = authState.getCurrentUser();
      const myUsername = user?.displayUsername || user?.username || null;
      const myImage = user?.image || null;
      const hostDisplay = hostDisplayUsername || hostUsername || '';
      this._lobbySlotUsernames = Array(lobbyMaxPlayers).fill(null);
      this._lobbySlotImages = Array(lobbyMaxPlayers).fill(null);
      this._lobbySlotOccupied = Array(lobbyMaxPlayers).fill(false);
      this._lobbyCardCache = {};
      if (participants?.players) {
        for (const p of participants.players) {
          if (p.slot >= 1 && p.slot <= lobbyMaxPlayers && (p.occupied || p.username)) {
            this._lobbySlotUsernames[p.slot - 1] = p.username ?? '';
            this._lobbySlotImages[p.slot - 1] = p.image || null;
            this._lobbySlotOccupied[p.slot - 1] = !!p.occupied;
          }
        }
      }
      if (lobbyMaxPlayers >= 1 && !this._lobbySlotUsernames[0]) {
        this._lobbySlotUsernames[0] = hostDisplay;
      }
      if (!spectator && playerNum >= 1 && playerNum <= lobbyMaxPlayers) {
        this._lobbySlotUsernames[playerNum - 1] = myUsername || this._lobbySlotUsernames[playerNum - 1] || '';
        this._lobbySlotImages[playerNum - 1] = myImage || this._lobbySlotImages[playerNum - 1] || null;
        this._lobbySlotOccupied[playerNum - 1] = true;
      }
      this._pendingInvite = null;

      // Reset the active slots and render the assigned roster.
      for (let slot = 1; slot <= lobbyMaxPlayers; slot++) {
        this._setPlayerStatus(slot, this._lobbySlotOccupied[slot - 1] ? 'connected' : 'waiting');
      }
      this._pendingLobbySwap = false;
      document.querySelectorAll('.player-slot').forEach(s => {
        s.classList.remove('selectable', 'selected');
        delete s.dataset.swapHint;
        s.onclick = null;
      });
      if (!spectator && playerNum === 1 && lobbyMaxPlayers === 2 && !this._linkCableRoom) {
        this._setupLobbySlotSelection();
      }
      // Track lobby room code so the Invite a Friend button can open the
      // modal pre-populated with this room.
      this._lobbyRoomCode = roomCode;
      // Reset peer-joined flag — _handleAssigned fires fresh per room.
      this._lobbyPeerJoined = this._lobbySlotOccupied.some((occupied, i) => occupied && i !== playerNum - 1);
      this._updateLobbyGameTitle();
      // Link cable badge
      let lcBadge = document.getElementById('lobby-link-cable-badge');
      if (this._linkCableRoom) {
        if (!lcBadge) {
          lcBadge = document.createElement('div');
          lcBadge.id = 'lobby-link-cable-badge';
          lcBadge.style.cssText = 'text-align:center;margin:4px 0;font-size:10px;color:var(--accent);letter-spacing:2px';
          lcBadge.textContent = '⚡ LINK CABLE';
          const title = document.getElementById('lobby-game-title');
          title?.parentNode.insertBefore(lcBadge, title.nextSibling);
        }
        lcBadge.classList.remove('hidden');
      } else {
        lcBadge?.classList.add('hidden');
      }
      // Clean up previous link cable ROM picker, then show for P2 only
      document.getElementById('lobby-link-cable')?.remove();
      if (this._linkCableRoom && !spectator && playerNum === 2) {
        this._showLinkCableRomPicker(this._linkCableRoomCode || roomCode);
      }
      // Wire up copy link
      const codeEl = document.getElementById('lobby-room-code');
      codeEl.style.cursor = 'pointer';
      codeEl.title = 'Click to copy invite link';
      codeEl.onclick = () => this._copyRoomLink(roomCode);
      const copyBtn = document.getElementById('btn-copy-link');
      if (copyBtn) copyBtn.onclick = () => this._copyRoomLink(roomCode);
      this._refreshLobbyInviteButton();
      let assignmentContinuation = null;
      // Spectators download ROM and wait for game_start (lockstep spectators
      // watch a WebRTC video stream instead, so they skip the ROM download)
      if (spectator) {
        if (!isLockstepEnabled(this.system?.id, this._roomCoreName, this.romData)) {
          assignmentContinuation = this._prepareSpectator(roomCode, assignmentOwner);
        }
      } else if (gameInProgress) {
        // Rejoin — auto-download ROM and signal ready
        assignmentContinuation = this._prepareRejoin(roomCode, assignmentOwner);
      }

      // Matchmaking lobby enhancements: update status text and start polling
      if (this._isMatchmaking) {
        const lobbyStatus = document.getElementById('lobby-status');
        if (lobbyStatus) lobbyStatus.textContent = 'Searching for opponent...';
        this._startMatchmakingPolling();
      }
      this._updatePresenceList();

      // Register an ephemeral room channel in the persistent chat panel.
      if (this._chatPanel) {
        this._chatPanel.addRoomChannel(roomCode, !!spectator);
      }
      return assignmentContinuation;
    };

    this.net.onPeerJoined = async (peerPlayer, peerUsername, peerImage, peerUserId) => {
      // The opponent's user id (null for anonymous peers) — read by
      // _startLockstep to attribute the replay's opponent_user_id.
      this._roomPeerUserId = peerUserId || null;
      this._playJoinPing();
      // Side-by-side lobby: the second racer just joined. The host (waiting in the
      // lobby) launches the race now; P2 starts from its own onAssigned. Once a
      // racer runtime is live it rebinds onPeerJoined, so this only runs pre-race.
      if (this._sbsRoom && this._sbsRole == null) {
        if (this.net.playerNumber === 1) {
          const roomCode = this._lobbyRoomCode || this._sbsRoomCode || this.net.roomCode;
          const hostStartOwner = {
            launchOwner: this._roomJoinLaunchOwner,
            expectedNet: this.net,
            expectedRoomCode: roomCode,
          };
          if (!this._roomAssignmentIsCurrent(hostStartOwner) || this.state !== State.LOBBY) return;
          const me = authState.getCurrentUser();
          const players = [
            { slot: 1, username: this._lobbySlotUsernames?.[0] || me?.displayUsername || me?.username || '', image: this._lobbySlotImages?.[0] || me?.image || null, occupied: true },
            { slot: 2, username: peerUsername || '', image: peerImage || null, occupied: true },
          ];
          this._flash('Opponent joined. Starting race!');
          const racerStart = this._startSideBySideRacer(1, roomCode, { players });
          const racerLaunchOwner = this._gameLaunchIntentGeneration;
          try {
            await racerStart;
          } catch (err) {
            if (!this._roomAssignmentIsCurrent({
              launchOwner: racerLaunchOwner,
              expectedNet: this.net,
              expectedRoomCode: roomCode,
            })) return;
            console.warn('[sbs] host racer start failed', err);
            this._flash('Could not start side-by-side race');
          }
        }
        return;
      }
      // Persist the peer's username so the slot label survives re-renders.
      // When the host pre-swapped to P2, the peer (server-assigned P2)
      // should appear in P1's slot since the swap hasn't fired yet.
      const slotIdx = this._pendingLobbySwap && (this._lobbyMaxPlayers || 2) === 2
        ? 0
        : (peerPlayer - 1);
      if (this._lobbySlotUsernames) this._lobbySlotUsernames[slotIdx] = peerUsername || '';
      if (this._lobbySlotImages) this._lobbySlotImages[slotIdx] = peerImage || null;
      if (this._lobbySlotOccupied && slotIdx >= 0) this._lobbySlotOccupied[slotIdx] = true;
      // Peer joined → there's no pending invite anymore (they accepted or
      // joined via link). Drop any pending-invite state.
      this._pendingInvite = null;
      this._setPlayerStatus(slotIdx + 1, 'connected');
      this._refreshLobbyInviteButton();
      this._lobbyPeerJoined = true;
      this._updateLobbyInviteButtonVisibility();
      if (this._waitingForRejoin) {
        this._flash('Opponent reconnected!');
        // Don't re-send rom_ready — we're already marked ready on the server.
        // The rejoining player will send rom_ready and trigger game_start.
      } else {
        // Solo → 2-player (Phase 5.5b): close the Invite modal now that
        // a friend has actually joined. The handoff itself happens on
        // game_start below.
        if (this.state === State.SOLO) {
          this._closeInviteModal();
          this._flash('Friend joined! Starting 2-player…');
          // A self-driving Genesis core can serialize only while its pump is
          // still running. Preflight the state transfer before announcing P1
          // ready; if capture fails, P2 stays in the lobby and the server never
          // transitions room.gameStarted to true.
          if (!(await this._prepareSoloMultiplayerHandoff())) return;
        } else {
          this._flash('Player joined!');
        }
        if (this.romData) {
          this.net.sendRomReady(this.romHash);
        }
      }
      this._updatePresenceList();
    };

    this.net.onPeerReady = () => {
      document.getElementById('lobby-status').textContent = 'Opponent is ready!';
    };

    this.net.onPeerLeft = (peerPlayer) => {
      const maxPlayers = this._lobbyMaxPlayers || this.system?.maxPlayers || 2;
      const departedSlot = Number.isInteger(peerPlayer)
        ? peerPlayer
        : (this.net.playerNumber === 1 ? 2 : 1);
      if (maxPlayers <= 2) this._roomPeerUserId = null;
      if (this.state === State.PLAYING) {
        // A departed C64 guest's held input must not stay on its pad, where a
        // later port switch would move it onto the other port. The host's own
        // input loop stops while it waits for a rejoin, so release its player too.
        if (this.system?.id === 'c64' && departedSlot === 2) for (const p of [0, 1]) this.virtualPads?.setC64Player?.(p, { bitmask: 0 });
        const hostLeft = departedSlot === 1;
        if (maxPlayers > 2 && !hostLeft) {
          // One guest leaving an N-player hosted game must not pause the host
          // stream or the other guests.
          this._flash(`Player ${departedSlot} disconnected`);
        } else {
          // Keep game state alive for potential rejoin.
          this._waitingForRejoin = true;
        }
        const remainingMultiGuest = maxPlayers > 2 && !hostLeft;
        if (remainingMultiGuest) {
          // Session remains live.
        } else if (this._netMode === 'link-cable' && this._linkCableP2PHealthy()) {
          // Only the peer's ROOM socket dropped — the P2P link is still
          // exchanging and their game is still running. Keep playing.
          this._showSysStatus('Peer room link lost', 'warn');
        } else {
          this.running = false;
          this._flash('Opponent disconnected - waiting for rejoin...');
        }
      }
      if (this._lobbySlotUsernames) this._lobbySlotUsernames[departedSlot - 1] = null;
      if (this._lobbySlotImages) this._lobbySlotImages[departedSlot - 1] = null;
      if (this._lobbySlotOccupied) this._lobbySlotOccupied[departedSlot - 1] = false;
      this._setPlayerStatus(departedSlot, 'waiting');
      // Seat freed — re-show Invite a Friend if we're back in the lobby.
      this._lobbyPeerJoined = this._lobbySlotOccupied?.some(
        (occupied, i) => occupied && i !== (this.net.playerNumber - 1),
      ) || false;
      if (this.state === State.SOLO && !this._lobbyPeerJoined) {
        this._cancelSoloMultiplayerHandoff();
      }
      // Preserve the normal peer-left `running = false` wait state, but undo
      // the extra WASM pause owned by a pending hosted-swap checkpoint. The
      // standard rejoin path will restart JS-driven loops when the peer returns.
      this._discardHostedSwapHandoff({ resumeWasm: true });
      const swapButton = document.getElementById('btn-swap');
      if (swapButton) swapButton.disabled = false;
      this._refreshLobbyInviteButton();
      this._updatePresenceList();
    };

    this.net.onGameStart = async (playerNum, hashMatch, spectator, mode, rejoin, connectedPlayers) => {
      console.log('[game_start] received', { playerNum, hashMatch, spectator, mode, rejoin, connectedPlayers, state: this.state, system: this.system?.id });
      // Side-by-side racers/spectators start immediately in onAssigned and do
      // not use the netplay ready→game_start handshake. Ignore any stray
      // game_start so it can't tear an SBS racer (State.SOLO) into a hosted/
      // lockstep handoff. No-op for legacy paths (`_sbsRole` is undefined there).
      if (this._sbsRole) return;
      const gameStartOwner = this._captureRoomSessionOwner();
      // Network's synchronous preflight queues game_start while a prospective
      // launch is validating. Once the event enters this handler it owns the
      // retained room until that candidate actually commits; an ownerless or
      // already-retired room event must not start a replacement emulator.
      if (!this._isRoomSessionOwnerCurrent(gameStartOwner)) return;
      this._stopMatchmakingPolling();

      if (spectator) {
        if (isLockstepEnabled(this.system?.id, this._roomCoreName, this.romData)) {
          this._prepareAndStartSpectator(this.net?.roomCode, gameStartOwner);
        } else {
          this._startSpectating(gameStartOwner);
        }
        return;
      }

      // Already playing and not waiting for rejoin — ignore duplicate game_start
      if (this.state === State.PLAYING && !this._waitingForRejoin && !this._selfReconnecting) return;

      if (rejoin && this._unsafeReplayBoundary) {
        this._flash('Reload the page to play again.');
        return;
      }

      // Staying player resumes — send state to the rejoining peer
      if (rejoin && this._waitingForRejoin) {
        this._waitingForRejoin = false;

        if (this.hostedNetplay) {
          // Hosted mode: the staying player's role depends on whether the
          // dropped peer was the host or the guest. `playerNum` from the
          // server is *our* slot, so:
          //   playerNum === 1 → we're the staying host; guest came back → rehost
          //   playerNum === 2 → we're the staying guest; host came back → reconnect as guest
          if (playerNum === 1) {
            this._hostedRejoinAsHost(gameStartOwner);
          } else {
            this._startHostedGuest(gameStartOwner);
          }
        } else if (this._netMode === 'link-cable' && this._linkCableP2PHealthy()) {
          // The peer's room socket bounced; the P2P link never dropped.
          // Nothing to rebuild — just resume any paused page loops.
          this._resumeLinkCableAfterRoomBounce();
        } else if (this.rollback && this.emu) {
          // Rollback mode: sync state and resume
          this.running = true;
          gameActions.refreshGameToolbarAvailability(this, 'core-running');
          this.lastFrameTime = performance.now();
          this.frameAccumulator = 0;
          this.rollback.lastConfirmedRemoteFrame = this.rollback.currentFrame - 1;
          const state = this.emu.saveState();
          this.net.sendForceSync(this.rollback.currentFrame, state);
          this._netplayLoop(performance.now());
        }

        this._flash('Opponent rejoined!');
        return;
      }

      // We are the one rejoining after our own connection dropped
      if (rejoin && this._selfReconnecting) {
        this._selfReconnecting = false;
        if (this.hostedNetplay) {
          if (playerNum === 1) this._hostedRejoinAsHost(gameStartOwner);
          else this._startHostedGuest(gameStartOwner);
        } else if (this._netMode === 'link-cable' && this._linkCableP2PHealthy()) {
          // Our room socket bounced; the P2P link never dropped. Resume in
          // place — rebuilding would reboot both players' games.
          this._resumeLinkCableAfterRoomBounce();
        }
        this._flash('Reconnected!');
        return;
      }

      // Resolve effective netplay mode. Server sends mode='link-cable' for
      // link cable rooms; otherwise check lockstep/rollback/hosted flags.
      const useLinkCable = mode === 'link-cable';
      const useLockstep = !useLinkCable && isLockstepEnabled(this.system?.id, this._roomCoreName, this.romData);
      const useRollback = !useLinkCable && !useLockstep && this.system?.rollbackCapable && !this.system?.preferHosted;
      const effectiveMode = useLinkCable ? 'link-cable'
        : useLockstep ? 'lockstep'
        : useRollback ? 'rollback'
        : (mode || this.system?.netplayMode);

      // Solo → hosted-netplay handoff (Phase 5.5b invite flow). The host
      // captured the live emulator before sending rom_ready, so game_start
      // can consume the prepared state without introducing a failure point
      // after the server and P2 have committed. Stop the solo rAF loop here
      // while the fresh hosted-host emulator resumes at that exact frame.
      let handoffUnshared = false;
      let authorSessionEnded = false;
      if (this.state === State.SOLO && playerNum === 1 && this.emu) {
        if (!this._pendingSoloHandoffSave) {
          console.error('[invite] game_start arrived without a prepared solo handoff');
          this._flash('Could not preserve this session for multiplayer');
          return;
        }
        authorSessionEnded = authoringHost.endAuthorSessionAtHandoff(this);
        if (effectiveMode === 'lockstep') {
          // §6 Invite handoff: end the solo recording as a clean exit does,
          // then refuse in _startLockstep(); the other player lacks this state.
          this._endSoloHandoffRecording();
          const discarded = this._pendingSoloHandoffSave;
          this._pendingSoloHandoffSave = null;
          this._releaseHandoffSave(discarded);
          handoffUnshared = true;
        } else {
          this._replacePendingSave(this._pendingSoloHandoffSave);
          this._pendingSoloHandoffSave = null;
          // The exact reservation follows the same save object into _pendingSave.
        }
        this._soloHandoffCaptureToken = null;
        this._soloHandoffWasmPaused = false;
        noteC64PauseChange(this);
        this.running = false;
        this._closeInviteModal();
        gameActions.refreshGameToolbarAvailability(this, 'handoff');
      }

      if (effectiveMode === 'link-cable') {
        const linkStarted = await this._startLinkCable(playerNum, gameStartOwner);
        if (linkStarted === false || !this._roomAssignmentIsCurrent(gameStartOwner)) return;
      } else if (effectiveMode === 'lockstep') {
        if (!hashMatch) {
          console.error('[game_start] ROM mismatch in lockstep', { playerNum, hashMatch, mode, effectiveMode, system: this.system?.id, romHash: this.romHash });
          this._flash('ROM mismatch! Both players need the same ROM.');
          return;
        }
        const lockstepStarted = await this._startLockstep(playerNum, gameStartOwner, { handoffUnshared });
        if (lockstepStarted === false || !this._roomAssignmentIsCurrent(gameStartOwner)) return;
        if (this._pendingLobbySwap) {
          this._pendingLobbySwap = false;
          this._preSwapVisual = true;
          await this._acceptPlayerSwap();
          if (!this._roomAssignmentIsCurrent(gameStartOwner)) return;
        }
      } else if (effectiveMode === 'hosted') {
        if (playerNum === 1) {
          const hostedStarted = await this._startHostedHost(connectedPlayers, gameStartOwner);
          if (hostedStarted === false || !this._roomAssignmentIsCurrent(gameStartOwner)) return;
          // Auto-swap if creator pre-selected the P2 slot in the lobby
          if (this._pendingLobbySwap) {
            this._pendingLobbySwap = false;
            this._preSwapVisual = true;
            await this._acceptPlayerSwap();
            if (!this._roomAssignmentIsCurrent(gameStartOwner)) return;
          }
        } else {
          this._startHostedGuest(gameStartOwner);
        }
      } else {
        if (!hashMatch) {
          console.error('[game_start] ROM mismatch in rollback', { playerNum, hashMatch, mode, effectiveMode, system: this.system?.id, romHash: this.romHash });
          this._flash('ROM mismatch! Both players need the same ROM.');
          return;
        }
        this._startNetplay(playerNum, rejoin);
      }

      if (!this._roomAssignmentIsCurrent(gameStartOwner)) return;
      // The new core's load message replaced the line said at the handoff.
      if (authorSessionEnded) authoringHost.repeatHandoffNotice(this);

      // Host (P1) broadcasts a live listing thumbnail for this room — every
      // multiplayer mode (link-cable / lockstep / hosted / netplay), matching what
      // solo/async/side-by-side already do via _startSolo. Guests + spectators
      // never send (the tick also re-checks _amRoomHost).
      if (playerNum === 1 && !this.net?.spectator) this._startRoomScreenshots();
    };

    this.net.onRemoteInput = (frame, buttons, adv, player) => {
      if (this.state === State.SPECTATING) {
        this._spectatorInput(player, frame, buttons);
        return;
      }
      if (this.rollback) {
        this.rollback.receiveRemoteInput(frame, buttons);
        this.rollback.receiveRemoteAdvantage(adv);
      }
    };

    this.net.onRemoteSync = (frame, hash) => {
      if (this.rollback) {
        this.rollback.receiveRemoteSyncHash(frame, hash);
      }
    };

    this.net.onForceSync = (frame, state) => {
      if (this.rollback) {
        this.rollback.applyForceSync(frame, state);
        if (this._awaitingSync) {
          this._awaitingSync = false;
          this.running = true;
          gameActions.refreshGameToolbarAvailability(this, 'core-running');
          this.lastFrameTime = performance.now();
          this.frameAccumulator = 0;
          this._netplayLoop(performance.now());
        }
      }
    };

    this.net.onDisconnect = (code, reason) => {
      const predecessorOwner = this._captureRoomSessionOwner();
      // A retained room may close after a newer join/save/async candidate has
      // taken request ownership. Reject that old close before it can cancel the
      // candidate, stop the emulator, or navigate away. Failed validation
      // releases the candidate and makes this room's callbacks current again.
      if (!this._isRoomSessionOwnerCurrent(predecessorOwner)) {
        this._deferCandidateRoomEvent(predecessorOwner, () => {
          predecessorOwner.expectedNet?.onDisconnect?.(code, reason);
        });
        return;
      }
      this._stopMatchmakingPolling();
      if (this._sbsRole) {
        // Side-by-side v1: no seamless rejoin. Tear the session down and return
        // to the menu instead of recreating a solo room (which would mis-handle
        // a dropped racer — it sits in State.SOLO but must not become a solo room).
        // _exitToMenu() runs the full SBS teardown (sinks/stage/solo-stream) +
        // play-session cleanup; the explicit teardown here closes the inbound
        // sink PCs while _sbsSinks still holds them.
        this._teardownSbsSession();
        this._hideReconnectOverlay();
        this._flash('Disconnected from race');
        this._exitToMenu({ flushPendingPostRoll: false });
        return;
      }
      if (this.state === State.SOLO) {
        this._stopRoomScreenshots();
        this._stopSoloStream();
        this._hideReconnectOverlay();
        // D6 and CH1: the active launch retains its room while recreation retries.
        // The ownership predicate, rather than the socket, decides availability.
        roomChat.syncRoomChatAvailability(this);
        this._recreateSoloRoom();
        return;
      }
      // A terminal room close owns the transition back to the menu. Invalidate
      // assignment/rejoin work before exposing that state; Network keeps the
      // departed roomCode on the object, so room identity alone cannot reject a
      // late ROM/save continuation.
      const multitap = retireMultitapSession(this, { leave: false });
      this._beginGameLaunchIntent({ clearPendingSave: true });
      this._roomJoinLaunchOwner = null;
      if (this.state === State.PLAYING) {
        if (multitap) this._stopGame({ multitapAudioOwned: true }); else this._stopGame();
        this._flash('Disconnected from server');
      } else if (this.state === State.LOBBY) {
        if (code === 4001) this._flash(reason || 'Room not found');
        else if (code === 4003) this._flash('Room is private');
        else this._flash('Could not join the room');
      }
      this._hideReconnectOverlay();
      this.state = State.MENU;
      this._resetRoomSessionUi();
      this._showScreen('menu');
      window.history.replaceState({}, '', '/');
    };

    this.net.onReconnecting = (attempt, max) => {
      clearSuperunoLobbyStartup(this);
      const reconnectNet = this.net, generation = reconnectNet._socketGeneration, session = this._multitapSession;
      session?.presentation?.invalidate(new SuperunoError('stale_assignment', 'display', false));
      if (this.net !== reconnectNet || reconnectNet._socketGeneration !== generation || this._multitapSession !== session) return;
      // D6 and CH1: the current launch keeps its Chat tab during reconnection.
      // Refresh ownership so a superseded room cannot supply the tab.
      roomChat.syncRoomChatAvailability(this);
      if (this.state === State.SOLO) return;
      // Link-cable play rides on the P2P DataChannel — a room-WS bounce
      // doesn't touch it. Keep the game running (no blocking overlay, no
      // pause); _selfReconnecting makes onAssigned skip the lobby when the
      // socket comes back.
      if (this.state === State.PLAYING && this._netMode === 'link-cable'
          && this._linkCableP2PHealthy()) {
        this._selfReconnecting = true;
        this._showSysStatus('Room reconnecting...', 'warn');
        return;
      }
      this._showReconnectOverlay(attempt, max);
      if (this._multitapSession?.current()) return;
      if (this.state === State.PLAYING) {
        this.running = false;
        this._selfReconnecting = true;
      }
    };

    this.net.onReconnected = () => {
      this._hideReconnectOverlay();
      roomChat.syncRoomChatAvailability(this);
      if (this._sbsRole === 'racer' && this.state === State.SOLO && !this._multitapSession) {
        this._sbsWireSource();
      }
      if (this._netMode === 'link-cable' && this._linkCableP2PHealthy()) {
        this._showSysStatus('Link Cable', 'ok');
      }
    };

    this.net.onReconnectFailed = () => {
      this._hideReconnectOverlay();
      this._selfReconnecting = false;
    };

    this.net.onPingResponse = (ms, hostState) => {
      const el = document.getElementById('stat-ping');
      if (el) el.textContent = `${ms}ms`;
      // Guest-side: absorb host's current frame + chosen parity delay
      // so we can target future frames when sending input.
      if (hostState) {
        if (Number.isInteger(hostState.frame)) {
          this._hostFrameEstimate = hostState.frame;
          this._hostFrameKnownAt = performance.now();
        }
        if (Number.isInteger(hostState.delayFrames)) {
          this._hostDelayFrames = hostState.delayFrames;
        }
      }
      // Host-side: recompute parity delay from relay RTT only when P2P
      // RTT isn't available yet. Once the DataChannel ping/pong is live,
      // the P2P measurement is more accurate and takes over via onP2PRtt.
      if (this.inputParity && this._netMode === 'host'
          && !(this.hostedNetplay?.p2pLatency > 0)) {
        const target = computeTargetDelay(ms);
        this.inputParity.setDelayTarget(target);
      }
    };

    this.net.onChat = (text, player, username, colorIdx, spectatorId, userId) => {
      this._showChatMessageFrom(text, player || 0, username, colorIdx, spectatorId, userId);
      if (this._chatPanel && this.net.roomCode) {
        this._chatPanel.pushRoomMessage(this.net.roomCode, { text, username, playerNum: player });
      }
    };

    this.net.onChatAck = (clientMessageId) => {
      roomChat.acknowledgePendingChat(this, clientMessageId);
    };

    this.net.onChatBlocked = (reason, clientMessageId, retryable) => {
      if (roomChat.failPendingChat(
        this,
        clientMessageId,
        reason || 'Message not sent.',
        !!retryable,
      )) return;
      this._showChatSystemMessage(reason || 'Message not sent.');
    };

    this.net.onChatTransportClose = () => {
      roomChat.failAllPendingChats(this);
    };

    this.net.onChatWarned = (reason) => {
      this._showChatSystemMessage(reason || 'Please keep chat respectful.');
    };

    this.net.onVoiceMuted = (duration, reason) => {
      if (this._voiceConnection && !this._voiceConnection.voiceMuted) {
        this._voiceConnection.toggleVoiceMute();
      }
      this._stopMicLevelLoop();
      roomChat.setRoomMicSanctioned(true);
      if (this._voiceModeration) {
        this._voiceModeration.mute(duration);
      }
      this._flash(reason || 'Voice chat muted due to violation.');
    };

    // Spectator join/leave broadcast — surfaced as system chat messages
    // for everyone in the room (both players + other spectators). The
    // solo-stream wiring below in _startSoloStream() reassigns these
    // callbacks for the WebRTC fan-out path; the wrapper there calls
    // back into _showChatSystemMessage so the chat broadcast still fires.
    this.net.onSpectatorJoined = (spectatorId, username, image) => {
      this._playJoinPing();
      this._spectators.set(spectatorId, { username: username || null, image: image || null });
      this._updatePresenceList();
      const label = username ? `@${username}` : `Spectator ${spectatorId}`;
      this._showChatSystemMessage(`${label} has entered the chat`);
    };
    this.net.onSpectatorLeft = (spectatorId, username) => {
      this._spectators.delete(spectatorId);
      this._updatePresenceList();
      const label = username ? `@${username}` : `Spectator ${spectatorId}`;
      this._showChatSystemMessage(`${label} has left the chat`);
    };

    // ── Spectator-to-Player Upgrade ─────────────────────
    this.net.onUpgradeInvite = (hostUsername) => {
      this._showUpgradePrompt(hostUsername);
    };

    // Network must authorize a retained-room promotion before mutating its
    // player/spectator/room fields. For P1/P2, claim the successor generation
    // in that synchronous preflight and pass it into the async continuation.
    this.net.onBeforeUpgradePromoted = (player, roomCode) => {
      const predecessorOwner = this._captureRoomSessionOwner();
      if (!this._isRoomSessionOwnerCurrent(predecessorOwner)) return null;
      if ((player === 1 || player === 2) && roomCode) {
        const launchOwner = this._beginRoomSessionLaunchIntent(
          { clearPendingSave: true },
          predecessorOwner,
        );
        if (launchOwner == null) return null;
        return {
          launchOwner,
          expectedNet: predecessorOwner.expectedNet,
          expectedRoomCode: roomCode,
        };
      }
      return predecessorOwner;
    };

    this.net.onUpgradePromoted = async (
      player,
      roomCode,
      spectatorId,
      username,
      promotionOwner,
    ) => {
      if (!promotionOwner || !this._roomAssignmentIsCurrent(promotionOwner)) return false;
      if (player === 2 && roomCode) {
        // We are the promoted spectator — disconnect and rejoin as P2
        // through the normal 2-player join flow.
        const { launchOwner, expectedNet } = promotionOwner;
        const promotionIsCurrent = () => (
          this._roomAssignmentIsCurrent(promotionOwner)
          && this.state === State.LOBBY
        );
        // assigned may arrive while connect() is still pending. Give its
        // rejoin/spectator setup the same owner before opening the new socket.
        this._roomJoinLaunchOwner = launchOwner;
        document.getElementById('upgrade-prompt')?.classList.add('hidden');
        if (this._spectatorPC) {
          this._spectatorPC.close();
          this._spectatorPC = null;
        }
        this._teardownSpectatorAudio();
        this._flash('Joining as Player 2...');
        this.state = State.LOBBY;
        expectedNet.disconnect();
        try {
          await expectedNet.connect(roomCode);
        } catch (err) {
          if (!promotionIsCurrent() || isConnectionCancellation(err)) {
            this._retireRoomConnectionIfOwned(expectedNet, roomCode, launchOwner);
            return;
          }
          console.error('[upgrade] room connection failed:', err);
          this._flash('Failed to join as Player 2');
          return;
        }
        if (!promotionIsCurrent()) {
          this._retireRoomConnectionIfOwned(expectedNet, roomCode, launchOwner);
          return;
        }
        const isHosted = !isLockstepEnabled(this.system?.id, this._roomCoreName, this.romData) &&
                         (this.system?.netplayMode === 'hosted' ||
                         (this.system?.rollbackCapable && this.system?.preferHosted) ||
                         !this.system?.rollbackCapable);
        if (isHosted) {
          if (!promotionIsCurrent()) return;
          expectedNet.sendRomReady('hosted-guest');
        } else {
          try {
            const rom = await this._downloadROM(roomCode);
            if (!promotionIsCurrent()) {
              this._retireRoomConnectionIfOwned(expectedNet, roomCode, launchOwner);
              return;
            }
            if (!rom) throw new Error('ROM not found in room');
            await this._installRoomRom(rom, { isCurrent: promotionIsCurrent });
            if (!promotionIsCurrent()) return;
            expectedNet.sendRomReady(this.romHash);
          } catch (err) {
            if (!promotionIsCurrent()) {
              this._retireRoomConnectionIfOwned(expectedNet, roomCode, launchOwner);
              return;
            }
            console.error('[upgrade] ROM download failed:', err);
            this._flash('Failed to download ROM');
          }
        }
      } else if (player === 1 && this.state === State.SOLO) {
        if (!this._roomAssignmentIsCurrent(promotionOwner)) return false;
        // We are the host — stop solo, move to lobby for the new room.
        // P2 will reconnect and the normal lobby → game_start flow takes over.
        this._pendingUpgrade = null;
        this._stopRoomScreenshots();
        this._stopSoloStream();
        this._stopGame();
        this._spectators = new Map();
        // CH3: in the game room, the room alone writes _chatSidebarOpen.
        if (!isRoomMode(this)) {
          const mobileChat = this._isMobileChat();
          this._chatSidebarOpen = !mobileChat && this._desktopChatSidebarPreferredOpen !== false;
          this._chatViewportMode = mobileChat ? 'drawer' : 'desktop';
        }
        if (roomCode) {
          this._soloRoomCode = roomCode;
          this._replaceRoomHistory(roomCode);
        }
        this.state = State.LOBBY;
        this._showScreen('lobby');
        document.getElementById('lobby-room-code').textContent = roomCode || '';
        document.getElementById('lobby-player').textContent = 'You are Player 1';
        document.getElementById('lobby-status').textContent = 'Waiting for Player 2…';
        const me = authState.getCurrentUser();
        const myUsername = me?.username || null;
        this._lobbySlotUsernames = [myUsername || '', null];
        this._lobbySlotImages = [me?.image || null, null];
        this._updatePresenceList();
        if (this.romData) {
          this.net.sendRomReady(this.romHash);
        }
      } else if (spectatorId != null) {
        if (!this._roomAssignmentIsCurrent(promotionOwner)) return false;
        // We are another spectator — remove the promoted one from our list
        this._spectators.delete(spectatorId);
        this._updatePresenceList();
      }
    };

    this.net.onUpgradeDeclined = (spectatorId, reason) => {
      this._pendingUpgrade = null;
      this._flash(reason === 'timeout' ? 'Invite expired' : 'Invite declined');
      this._updatePresenceList();
    };

    this.net.onUpgradeError = (reason) => {
      this._pendingUpgrade = null;
      this._flash('Could not invite spectator');
      this._updatePresenceList();
    };

    this.net.onRelayError = (messageType) => {
      if (messageType === 'screenshot_frame') {
        this._showSysStatus('Room preview update failed', 'warn');
      } else if (messageType === 'force_sync') {
        this._flash('Game sync could not be sent');
      } else {
        this._flash('Room message could not be sent');
      }
    };

    this.net.onUpgradeCanceled = () => {
      document.getElementById('upgrade-prompt')?.classList.add('hidden');
    };

    // ── Async Turn-Based: Live Baton Pass ────────────────
    this.net.onBatonPass = (payload) => {
      const predecessorOwner = this._captureRoomSessionOwner();
      if (!this._isRoomSessionOwnerCurrent(predecessorOwner)) return;
      console.log('[baton] received pass:', payload, 'currentState:', this.state);
      if (payload?.role === 'become_player') {
        return this._batonBecomePlayer(payload, predecessorOwner);
      } else if (payload?.role === 'become_spectator') {
        return this._batonBecomeSpectator(payload);
      }
    };
    this.net.onBatonCommitted = (payload) => {
      const predecessorOwner = this._captureRoomSessionOwner();
      if (!this._isRoomSessionOwnerCurrent(predecessorOwner)) return;
      console.log('[baton] committed:', payload);
      const me = authState.getCurrentUser?.();
      const myUsername = me?.displayUsername || me?.username || null;
      if (payload?.role === 'become_player') {
        const playerUsername = payload.playerUsername || myUsername || null;
        this._lobbySlotUsernames = [playerUsername ?? '', null];
        this._lobbySlotImages = [payload.playerImage || me?.image || null, null];
        if (payload.oldSpectatorId != null) this._spectators.delete(payload.oldSpectatorId);
        this._updatePresenceList();
      } else if (payload?.role === 'become_spectator') {
        const playerUsername = payload.playerUsername || null;
        this._lobbySlotUsernames = [playerUsername ?? '', null];
        this._lobbySlotImages = [payload.playerImage || null, null];
        this._spectators = new Map();
        if (payload.spectatorId != null) {
          this._spectators.set(payload.spectatorId, { username: payload.spectatorUsername || myUsername || null });
        }
        this._updatePresenceList();
      }
      gameActions.refreshGameToolbarAvailability(this, 'baton');
    };

    this.net.onSwapRequest = () => {
      if (this._netMode === 'link-cable') {
        // GB/GBC link roles are fixed (P1 clocks as master, P2 responds as
        // slave). Hosted state migration cannot safely swap those roles.
        this.net.sendSwapDecline?.();
        return;
      }
      this._setSwapPromptAcceptPending(false);
      document.getElementById('swap-prompt')?.classList.remove('hidden');
    };

    this.net.onSwapDecline = () => {
      this._hostedSwapAcceptToken = null;
      this._resumeHostedSwapHandoff();
      this._setSwapPromptAcceptPending(false);
      this._flash('Swap declined');
      const swapButton = document.getElementById('btn-swap');
      if (swapButton) swapButton.disabled = false;
    };

    this.net.onPlayersSwapped = (newPlayerNum) => {
      const predecessorOwner = this._captureRoomSessionOwner();
      if (!this._isRoomSessionOwnerCurrent(predecessorOwner)) return;
      if (this._multitapSession?.presentation) {
        this._multitapSession.presentation.invalidate(new SuperunoError('owner_lost', 'display', false));
        this.running = false;
        return;
      }
      // Defense for mixed-version servers/clients. Routing a link-cable
      // session into _hostedSwap would destroy the independent cartridges.
      if (this._netMode === 'link-cable') {
        this._flash('Player swap is unavailable during link cable play');
        return;
      }
      if (this._lobbySlotUsernames && !this._preSwapVisual) {
        const tmp = this._lobbySlotUsernames[0];
        this._lobbySlotUsernames[0] = this._lobbySlotUsernames[1];
        this._lobbySlotUsernames[1] = tmp;
      }
      this._preSwapVisual = false;
      if (this.state === State.LOBBY) {
        document.getElementById('lobby-player').textContent = `You are Player ${newPlayerNum}`;
        this._setPlayerStatus(newPlayerNum, 'connected');
        this._setPlayerStatus(newPlayerNum === 1 ? 2 : 1, 'connected');
        this._updatePresenceList();
        return;
      }
      document.getElementById('swap-prompt')?.classList.add('hidden');
      document.getElementById('btn-swap').disabled = false;
      if (this.lockstep) {
        this._discardHostedSwapHandoff();
        this.lockstep.localPlayer = newPlayerNum - 1;
        this.lockstep.remotePlayer = 1 - this.lockstep.localPlayer;
        this._flash(`Swapped! You are now Player ${newPlayerNum}`);
      } else {
        this._hostedSwap(newPlayerNum, predecessorOwner);
      }
      this._updatePresenceList();
      gameActions.refreshGameToolbarAvailability(this, 'swap');
    };

    this.net.onSwapHostReady = () => {
      if (this._netMode === 'link-cable') {
        // Mixed-version or malicious peers must not jump directly to the
        // hosted-migration completion message and replace this cartridge.
        this.net.sendSwapDecline?.();
        this._flash('Player swap is unavailable during link cable play');
        return;
      }
      // New host: old host has uploaded state, safe to start hosting
      const transitionOwner = this._hostedRoleTransitionOwner;
      this._hostedRoleTransitionOwner = null;
      if (!this._isHostedRoleTransitionCurrent(transitionOwner)) return;
      return this._becomeHost(transitionOwner);
    };

    // ── Game Change ──────────────────────────────────────

    this.net.onGameChangeRequest = (system, romName) => {
      if (this._netMode === 'link-cable') {
        // Each peer owns an independent cartridge. The ordinary change-game
        // flow only loads P1's selection and would ready P2 with stale ROM
        // data, so require a fresh room until a two-cartridge picker exists.
        this.net.sendGameChangeDecline?.();
        return;
      }
      const label = this._romDisplayTitle?.(romName, system) || romName.replace(/\.[^.]+$/i, '');
      const sysLabel = system !== this.system.id ? ` (${systemTag(system)})` : '';
      document.getElementById('game-change-prompt-text').textContent =
        `Switch to ${label}${sysLabel}?`;
      document.getElementById('game-change-prompt')?.classList.remove('hidden');
      this._pendingGameChange = { system, romName };
    };

    this.net.onGameChangeAccept = () => {
      // P1 received acceptance — execute the game change
      this._executeGameChange();
    };

    this.net.onGameChangeDecline = () => {
      this._flash('Game change declined');
      this._pendingGameChange = null;
      document.getElementById('btn-change-game').disabled = false;
    };

    this.net.onGameChange = async (newSystem, coreName, linkCable = false) => {
      const predecessorOwner = this._captureRoomSessionOwner();
      if (!this._isRoomSessionOwnerCurrent(predecessorOwner)) return;
      // The server-confirmed core is authoritative for BOTH peers. P1 may have
      // proposed a stale locally cached Genesis flag value; it must not announce
      // ROM readiness until this echo pins the same core P2 will boot.
      if (newSystem && newSystem !== this.system.id) this._selectSystem(newSystem);
      this._roomCoreName = coreName || resolveCoreName(this.system, true);
      // Change Game reuses the room, so the server explicitly echoes the
      // transport mode it recomputed for the replacement system. A GB/GBA
      // transition must not retain the previous room's hosted/lockstep mode.
      this._linkCableRoom = linkCable;
      this._linkCableRoomCode = linkCable ? this.net.roomCode : null;
      if (this.net.playerNumber === 1) {
        this.net.sendRomReady(this.romHash);
        return;
      }
      const expectedNet = this.net;
      const expectedRoomCode = expectedNet.roomCode;
      // A same-room reconnect replaces expectedNet.ws in place. The Network
      // object plus room code owns this continuation so the replacement socket
      // can carry rom_ready without letting a different room claim the result.
      const launchOwner = this._beginRoomSessionLaunchIntent(
        { clearPendingSave: true },
        predecessorOwner,
      );
      if (launchOwner == null) return;
      const changeOwner = { launchOwner, expectedNet, expectedRoomCode };
      const changeIsCurrent = () => (
        this._roomAssignmentIsCurrent(changeOwner)
        && this.state === State.LOBBY
      );
      this._stopGame();
      this.state = State.LOBBY;
      this._showScreen('lobby');
      this._configureLobbySlots(this.system?.maxPlayers || 2);
      this._refreshLobbyInviteButton();
      document.getElementById('lobby-status').textContent = 'Loading new game...';
      const isHosted = !linkCable && !isLockstepEnabled(this.system?.id, this._roomCoreName, this.romData) &&
                       (this.system?.netplayMode === 'hosted' ||
                       (this.system?.rollbackCapable && this.system?.preferHosted));
      if (isHosted) {
        if (changeIsCurrent()) expectedNet.sendRomReady('hosted-guest');
      } else {
        try {
          const rom = await this._downloadROM(expectedRoomCode);
          if (!changeIsCurrent()) return;
          if (!rom) return this._flash('Failed to download ROM');
          await this._installRoomRom(rom, { isCurrent: changeIsCurrent });
          if (!changeIsCurrent()) return;
          expectedNet.sendRomReady(this.romHash);
        } catch (err) {
          if (!changeIsCurrent()) return;
          console.error('[game-change] ROM download failed:', err);
          this._flash('Failed to download ROM');
        }
      }
    };

  }

  // ── ROM Loading ─────────────────────────────────────────

  _onROMSelected(e) {
    const file = e.target.files?.[0];
    if (file) this._loadROMFile(file);
  }

  _loadROMFile(file) {
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    const isZip = ext === '.zip';
    const detected = detectSystem(file.name);

    // If detection is ambiguous (e.g. .bin shared by Atari/Genesis), accept
    // the file if the current system supports that extension; otherwise reject.
    if (!isZip && !detected) {
      if (!this.system.extensions.includes(ext)) {
        this._flash('Unsupported ROM file format');
        return;
      }
    }
    cancelPlayIntent('canceled');
    const launchOwner = this._beginGameLaunchIntent({ clearPendingSave: true });
    const launchIsCurrent = () => this._isGameLaunchOwnerCurrent(launchOwner);
    this._hideSysStatus();
    if (detected && detected.id !== this.system.id) {
      this._selectSystem(detected.id);
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (!launchIsCurrent()) return;
      let data = new Uint8Array(e.target.result);
      let romName = file.name;

      // Extract ROM from zip if needed
      if (isZip) {
        const extracted = this._extractRomFromZip(data);
        if (!extracted) {
          this._flash('No ROM found in zip file');
          return;
        }
        data = extracted.data;
        romName = extracted.name;
      }

      // Convert to binary string (jsnes compat, also used for hashing/upload)
      let romStr = '';
      for (let i = 0; i < data.length; i++) {
        romStr += String.fromCharCode(data[i]);
      }
      if (!launchIsCurrent()) return;
      this._activeRomFilename = null;
      this._setRom(romStr, romName);
      document.querySelectorAll('.rom-entry.selected').forEach(el => el.classList.remove('selected'));
      document.querySelectorAll('.fav-entry.selected').forEach(el => el.classList.remove('selected'));
      document.querySelectorAll('.myrom-entry.selected').forEach(el => el.classList.remove('selected'));

      // Persist to server for signed-in users
      this._uploadMyRom(data, romName);
    };
    reader.readAsArrayBuffer(file);
  }

  /**
   * Extract the first ROM file from a zip archive.
   * Returns { name, data: Uint8Array } or null.
   *
   * Thin delegate to the shared cores/rom-zip.js extractor so the main app and
   * the RetroReel live-source shim share one implementation.
   */
  _extractRomFromZip(zipData, system = this.system) {
    return extractRomFromZip(zipData, system);
  }

  // ── Room Management ─────────────────────────────────────
  // Moved to modules/room-flow.js — see that file's header comment for what
  // stayed here and why (borderline cases: `_leaveRoom`, `_checkJoinLink`,
  // `_inviteFriend`, the ROM upload/download helpers).

  _roomPath(code) {
    return roomFlow.roomPath(this, code);
  }

  _replaceRoomHistory(code) {
    return roomFlow.replaceRoomHistory(this, code);
  }

  _pushRoomHistory(code) {
    return roomFlow.pushRoomHistory(this, code);
  }

  async _createRoom({ multitap = false } = {}) {
    const launchOwner = this._beginGameLaunchIntent();
    await this._refreshFeatureFlags();
    if (!this._isGameLaunchOwnerCurrent(launchOwner)) return false;
    return roomFlow.createRoom(this, launchOwner, { multitap });
  }

  /** Host flow: create a side-by-side race room, upload the ROM, enter as racer (slot 1). */
  async _createSideBySideRoom() {
    const launchOwner = this._beginGameLaunchIntent();
    await this._refreshFeatureFlags();
    if (!this._isGameLaunchOwnerCurrent(launchOwner)) return false;
    return roomFlow.createSideBySideRoom(this, launchOwner);
  }

  // ── Matchmaking ─────────────────────────────────────────

  async _findMatch() {
    const launchOwner = this._beginGameLaunchIntent();
    await this._refreshFeatureFlags();
    if (!this._isGameLaunchOwnerCurrent(launchOwner)) return false;
    return roomFlow.findMatch(this, launchOwner);
  }

  _showMatchmakingBrowse(rooms, system, romHash, romTitle) {
    return roomFlow.showMatchmakingBrowse(this, rooms, system, romHash, romTitle);
  }

  async _createMatchmakingRoom(system, romHash, romTitle) {
    const launchOwner = this._beginGameLaunchIntent();
    await this._refreshFeatureFlags();
    if (!this._isGameLaunchOwnerCurrent(launchOwner)) return;
    return roomFlow.createMatchmakingRoom(this, system, romHash, romTitle, launchOwner);
  }

  _startMatchmakingPolling() {
    return roomFlow.startMatchmakingPolling(this);
  }

  _stopMatchmakingPolling() {
    return roomFlow.stopMatchmakingPolling(this);
  }

  async _refreshOnlineCount() {
    return roomFlow.refreshOnlineCount(this);
  }

  async _showMatchmakingNudge() {
    return roomFlow.showMatchmakingNudge(this);
  }

  async _joinRoom(code, existingLaunchOwner = null, { onLaunchCommitted = null, spectator = false } = {}) {
    // G4: during a room session the leave dialog asks first; its run joins
    // with a fresh launch owner after the dialog's teardown and answers the
    // join's own result (c2).
    if (guardRoomNavigation(this, {
      kind: 'launch',
      label: 'a room',
      run: async () => {
        const attempt = notificationsUi.captureNotificationLaunch(this);
        const joined = await this._joinRoom(code, attempt.owner, { onLaunchCommitted: attempt.commitLaunch, ...(spectator ? { spectator: true } : {}) });
        return attempt.result(joined, 'The room join did not complete');
      },
    })) return;
    cancelPlayIntent('canceled');
    const launchOwner = existingLaunchOwner
      ?? this._beginGameLaunchCandidate({ clearPendingSave: true });
    try {
      await this._refreshFeatureFlags();
      if (!this._isGameLaunchOwnerCurrent(launchOwner)) return;
      return await roomFlow.joinRoom(this, code, launchOwner, { onLaunchCommitted, ...(spectator ? { spectator: true } : {}) });
    } finally {
      this._finishGameLaunchCandidate(launchOwner);
    }
  }

  _leaveRoom() {
    this.net.clearMultitapReconnect?.(this.net.roomCode);
    retireMultitapSession(this);
    // Leaving the lobby supersedes any assignment-side ROM/save continuation
    // that has not finished yet (notably the SBS racer download).
    this._beginGameLaunchIntent({ clearPendingSave: true });
    this._stopMatchmakingPolling();
    this.net.disconnect();
    this._roomPeerUserId = null;
    this._sbsRoom = false;
    this._sbsJoining = false;
    this._roomJoinLaunchOwner = null;
    this.state = State.MENU;
    this._resetRoomSessionUi();
    this._showScreen('menu');
    window.history.replaceState({}, '', '/');
    try { sessionStorage.removeItem('retroplay:hostCode'); } catch {}
  }

  async _checkJoinLink() {
    const pathname = window.location.pathname;
    // Claim cold-start game/room intent before any verification/auth landing
    // work can await. Callback URLs may carry both verification markers and
    // launch params; a user action during that earlier await must still win.
    const queryParams = new URLSearchParams(window.location.search);
    const librarySystem = queryParams.get('system');
    const libraryRom = queryParams.get('rom');
    const myromHash = queryParams.get('myrom');
    const replayQueryId = queryParams.get('replay');
    const ghostQueryId = queryParams.get('ghost');
    const suggestionQueryId = queryParams.get('suggestion');
    const fromLibrary = queryParams.get('from') === 'library';
    const soloParam = queryParams.get('solo') === '1';
    const hasLibraryLaunch = pathname === '/' && librarySystem && (libraryRom || myromHash);
    const hasSoloResumeLaunch = /^\/u\/[^/]+\/play\/?$/.test(pathname);
    const hasRoomLaunch = /^\/r\/[A-Z0-9]+\/?$/i.test(pathname)
      || (pathname === '/' && !!queryParams.get('join'));
    if (hasLibraryLaunch || hasSoloResumeLaunch || hasRoomLaunch) cancelPlayIntent('canceled');
    const deepLinkLaunchOwner = hasLibraryLaunch || hasSoloResumeLaunch || hasRoomLaunch
      ? this._beginGameLaunchIntent({ clearPendingSave: true })
      : null;
    // Achievement authoring (spec 12, Open in game and Edit): `author` and `author_checkpoint`
    // are read here, before any replaceState below strips the query; `author` implies solo.
    const authorLaunch = authoringHost.captureAuthorLaunchParams(this, queryParams,
      { enabled: !!hasLibraryLaunch, owner: deepLinkLaunchOwner });

    // Email verification / change landing: Better Auth redirects verification
    // links back here with a marker we set on the callbackURL —
    // `email_verified=1` after a sign-up (or resent) verification, or
    // `email_change=verified` after an email change — plus `&error=<CODE>`
    // appended when the token was invalid/expired. Surface a toast either way,
    // then strip the markers so they don't survive a refresh. We do NOT return
    // here — normal boot continues so the user still lands on the menu.
    const verifyParams = new URLSearchParams(window.location.search);
    const emailChangeLanding = verifyParams.has('email_change');
    const emailVerifiedLanding = verifyParams.has('email_verified');
    if (emailChangeLanding || emailVerifiedLanding) {
      const verifyError = verifyParams.get('error');
      const cleaned = new URL(window.location.href);
      cleaned.searchParams.delete('email_change');
      cleaned.searchParams.delete('email_verified');
      cleaned.searchParams.delete('error');
      window.history.replaceState({}, '', cleaned.pathname + cleaned.search + cleaned.hash);
      if (verifyError) {
        this._flash('That email verification link is invalid or has expired. Please request a new one.');
      } else {
        try { await authState.refreshSession(); } catch { /* non-fatal */ }
        this._flash(emailChangeLanding
          ? 'Your email address has been updated ✓'
          : 'Your email address has been verified ✓');
      }
    } else if (pathname !== '/reset-password' && verifyParams.has('error')) {
      // Magic-link / OAuth failure landing: Better Auth redirects a failed
      // link click or OAuth error back to the request's callbackURL with only
      // `?error=<CODE>` appended — no marker of ours. That callbackURL is `/`
      // by default, but Task 18's returnTo continuation can point it at a
      // deep-link destination instead (e.g. `/friends?invite=<id>` when the
      // sign-in was launched from an invite), so this can land on any
      // pathname. Left unhandled, the raw code in the address bar is the only
      // feedback the user gets (the July 2026 lockout reports were people
      // staring at `?error=new_user_signup_disabled`). Translate the known
      // codes and strip (only) the error param so a refresh doesn't re-toast,
      // leaving any other query params (like `?invite=<id>`) intact.
      // Excluded: /reset-password?error=INVALID_TOKEN belongs to the reset
      // screen (auth-ui.js showResetPasswordScreen), which renders its own
      // explanation and reads the param itself — toasting here too would
      // double-handle the same failure.
      const code = verifyParams.get('error');
      const cleaned = new URL(window.location.href);
      cleaned.searchParams.delete('error');
      window.history.replaceState({}, '', cleaned.pathname + cleaned.search + cleaned.hash);
      const messages = {
        new_user_signup_disabled: 'No RetroPlay account exists for that email address. Create one first, or sign in with Discord or Twitch if that\'s how you signed up.',
        EXPIRED_TOKEN: 'That login link has expired (links last 5 minutes). Request a new one to sign in.',
        INVALID_TOKEN: 'That login link is invalid or was already used. Request a new one to sign in.',
        ATTEMPTS_EXCEEDED: 'That login link is invalid or was already used. Request a new one to sign in.',
      };
      this._flash(messages[code] || 'Sign-in didn\'t complete. Please try again.');
    }

    if (pathname === '/sign-in') {
      const { handleSignInRoute } = await import('./modules/sign-in-route.js');
      await handleSignInRoute(this, queryParams);
      return;
    }

    // Library deep-link: /?system=<id>&rom=<filename>[&solo=1]
    // The library app at /library launches games by redirecting here with
    // these query params. We strip them from the URL to keep back-button
    // behaviour sane, switch to the requested system, load the ROM, and
    // (when solo=1) auto-start a solo play session so the user lands
    // straight in gameplay instead of back on the menu.
    if (librarySystem && libraryRom && pathname === '/') {
      const launchOwner = deepLinkLaunchOwner;
      const launchIsCurrent = () => this._isGameLaunchOwnerCurrent(launchOwner);
      if (!launchIsCurrent()) return;
      window.history.replaceState({}, '', '/');
      this._arrivedFromLibrary = true;
      const pendingReplayId = replayQueryId && /^[0-9a-f-]{36}$/i.test(replayQueryId)
        ? replayQueryId
        : null;
      const pendingGhostId = ghostQueryId && /^[0-9a-f-]{36}$/i.test(ghostQueryId)
        ? ghostQueryId
        : null;
      const pendingSuggestionId = suggestionQueryId && /^[0-9a-f-]{36}$/i.test(suggestionQueryId)
        ? suggestionQueryId
        : null;
      let pendingReplayCoreName = null;
      if (pendingReplayId) {
        // Resolve deterministic core provenance before _startSolo constructs
        // the emulator. This prevents an admin default switch from booting a
        // historical Genesis log under the wrong core.
        if (!authState.isLoaded()) {
          try { await authState.refreshSession(); } catch { /* public replays still work */ }
          if (!launchIsCurrent()) return;
        }
        try {
          const replayRes = await fetch(`/api/replays/${pendingReplayId}`, { credentials: 'include' });
          if (!launchIsCurrent()) return;
          if (replayRes.ok) {
            const replayMeta = await replayRes.json();
            if (!launchIsCurrent()) return;
            pendingReplayCoreName = replayMeta.core_name
              || (librarySystem === 'genesis' ? 'genesis_plus_gx' : null);
          }
        } catch { /* the playback fetch will surface the actual error */ }
      }
      if (!launchIsCurrent()) return;
      if (this.system?.id !== librarySystem) {
        this._selectSystem(librarySystem);
      }
      try { await this._loadRomLibrary(); } catch { /* non-fatal */ }
      if (!launchIsCurrent()) return;
      // Auto-launch solo when arriving with a replay or ghost payload, or when
      // an explicit ?solo=1 deep-link asks for it (the library "Play" button,
      // suggestion links, and the admin game-report "Play in solo mode" link
      // all emit it). The originating click already expressed intent, so skip
      // the menu. Anonymous visitors are left on the menu with the ROM
      // pre-loaded — the play button there runs them through requireAuthToPlay
      // — so we don't surprise them with a sign-in modal on arrival.
      //
      // Resolve auth BEFORE loading the ROM: when solo will auto-start, the
      // play-mode modal that _setRom→_maybePromptPlayMode opens must be
      // suppressed, or it lingers over the running game. Replay/ghost boots
      // skip the modal inside _maybePromptPlayMode itself.
      let autoStartSolo = false;
      if ((soloParam || authorLaunch) && !pendingReplayId && !pendingGhostId) {
        if (!authState.isLoaded()) {
          try { await authState.refreshSession(); } catch { /* non-fatal */ }
          if (!launchIsCurrent()) return;
        }
        autoStartSolo = !!authState.getCurrentUser();
      }
      let loaded = false;
      try {
        loaded = await this._selectLibraryRom(libraryRom, null, {
          launchOwner,
          suppressPlayModePrompt: !!pendingReplayId || !!pendingGhostId || autoStartSolo,
        });
      } catch (err) {
        if (launchIsCurrent()) console.warn('[main] library deep-link load failed:', err);
      }
      if (!loaded || !launchIsCurrent()) return;
      if (fromLibrary) {
        this._pendingLibraryLaunch = {
          owner: launchOwner, system: this.system?.id, romName: this.romName,
        };
      }
      // Game-suggestion launch (DM card / notification email link) — retain the
      // id for an anonymous arrival that loads the requested ROM now and signs
      // in before pressing Play later.
      this._pendingSuggestionId = pendingSuggestionId;
      if (pendingReplayId || pendingGhostId || autoStartSolo) {
        // Publish replay/ghost/suggestion metadata only after this exact deep
        // link installed its ROM. A competing launch can therefore never
        // consume payload state left behind by a superseded cold-start fetch.
        this._pendingReplayId = pendingReplayId;
        this._pendingReplayCoreName = pendingReplayCoreName;
        this._pendingGhostId = pendingGhostId;
        try { await this._startSolo(launchOwner); }
        catch (err) {
          if (launchIsCurrent()) console.warn('[main] auto-start solo failed:', err);
        }
      }
      return;
    }

    // Library "My ROMs" deep link: /?system=<sys>&myrom=<hash>[&solo=1]
    // launches one of the signed-in user's uploaded (non-bundled) ROMs by
    // content hash. Mirrors the bundled deep-link branch above: params are
    // stripped, the system is selected, and solo=1 auto-starts only for a
    // signed-in user (anonymous visitors land on the menu with a flash).
    if (librarySystem && myromHash && pathname === '/') {
      const launchOwner = deepLinkLaunchOwner;
      const launchIsCurrent = () => this._isGameLaunchOwnerCurrent(launchOwner);
      if (!launchIsCurrent()) return;
      window.history.replaceState({}, '', '/');
      this._arrivedFromLibrary = true;
      if (this.system?.id !== librarySystem) {
        this._selectSystem(librarySystem);
      }
      try { await this._loadRomLibrary(); } catch { /* non-fatal */ }
      if (!launchIsCurrent()) return;
      let autoStartSolo = false;
      if (soloParam || authorLaunch) {
        if (!authState.isLoaded()) {
          try { await authState.refreshSession(); } catch { /* non-fatal */ }
          if (!launchIsCurrent()) return;
        }
        autoStartSolo = !!authState.getCurrentUser();
      }
      let loaded = false;
      try {
        loaded = await this._loadMyRomByHash(librarySystem, myromHash, {
          launchOwner,
          suppressPlayModePrompt: autoStartSolo,
        });
      } catch (err) {
        if (launchIsCurrent()) console.warn('[main] my-rom deep-link load failed:', err);
      }
      if (loaded && launchIsCurrent() && fromLibrary) {
        this._pendingLibraryLaunch = {
          owner: launchOwner, system: this.system?.id, romName: this.romName,
        };
      }
      if (autoStartSolo && loaded && launchIsCurrent()) {
        try { await this._startSolo(launchOwner); }
        catch (err) {
          if (launchIsCurrent()) console.warn('[main] auto-start solo failed:', err);
        }
      }
      return;
    }

    if (/^\/(?:contact|privacy|tos|community-standards|acknowledgements|third-party-notices)$/.test(pathname)) {
      screenRouter.routeFromLocation(this);
      return;
    }

    // /settings — settings page.
    if (pathname === '/settings') {
      this._showSettingsScreen();
      return;
    }

    // /u/:username/play — resume own solo session, or spectate another user's.
    const playMatch = pathname.match(/^\/u\/([^/]+)\/play\/?$/);
    if (playMatch) {
      const playUser = decodeURIComponent(playMatch[1]);
      const launchOwner = deepLinkLaunchOwner
        ?? this._beginGameLaunchIntent({ clearPendingSave: true });
      if (await this._tryResumeSoloSession(playUser, launchOwner)) return;
      if (!this._isGameLaunchOwnerCurrent(launchOwner)) return;
      if (!authState.isLoaded()) {
        try { await authState.refreshSession(); } catch {}
        if (!this._isGameLaunchOwnerCurrent(launchOwner)) return;
      }
      const me = authState.getCurrentUser();
      if (me?.username && me.username.toLowerCase() === playUser.toLowerCase()) {
        this._navigateToMenu();
        return;
      }
      this._joinWatchRoom(playUser, launchOwner);
      return;
    }

    // /leaderboards/achievements/:gameId — per-game achievement leaderboard.
    const lbAchGameMatch = pathname.match(/^\/leaderboards\/achievements\/(\d+)\/?$/);
    if (lbAchGameMatch) {
      this._showLeaderboardsScreen(Number(lbAchGameMatch[1]), 'achievements');
      return;
    }
    // /leaderboards/:gameId — specific game score leaderboard.
    const lbGameMatch = pathname.match(/^\/leaderboards\/(\d+)\/?$/);
    if (lbGameMatch) {
      this._showLeaderboardsScreen(Number(lbGameMatch[1]));
      return;
    }
    // /leaderboards — leaderboards index.
    if (/^\/leaderboards\/?$/.test(pathname)) {
      this._showLeaderboardsScreen();
      return;
    }

    // /replays — public replays browse page.
    if (/^\/replays\/?$/.test(pathname)) {
      this._showReplaysScreen();
      return;
    }

    // /clip/:id — legacy clip share URL.
    const clipMatch = pathname.match(/^\/clip\/([0-9a-f-]+)\/?$/);
    if (clipMatch) {
      this._openClipViewer(clipMatch[1], true);
      return;
    }

    // /replay/:id — public replay share URL. This is the branch that fires on a
    // cold full-page load (boot calls _checkJoinLink; _routeFromLocation only
    // runs on popstate). Open the full viewer on top of the replays screen.
    const replayMatch = pathname.match(/^\/replay\/([0-9a-f-]+)\/?$/);
    if (replayMatch) {
      this._showReplaysScreen();
      import('./modules/replays-browse.js').then(({ openReplayById }) => openReplayById(replayMatch[1], this));
      return;
    }

    // /u/:username/replays — deep link to gallery pre-filtered to replays.
    const replaysPathMatch = pathname.match(/^\/u\/([^/]+)\/replays\/?$/);
    if (replaysPathMatch) {
      this._showGalleryScreen(decodeURIComponent(replaysPathMatch[1]), { defaultFilter: 'replays' });
      return;
    }

    // /u/:username/gallery — user clip gallery.
    const galleryPathMatch = pathname.match(/^\/u\/([^/]+)\/gallery\/?$/);
    if (galleryPathMatch) {
      this._showGalleryScreen(decodeURIComponent(galleryPathMatch[1]));
      return;
    }

    // /u/:username/activity — user activity feed.
    const activityPathMatch = pathname.match(/^\/u\/([^/]+)\/activity\/?$/);
    if (activityPathMatch) {
      this._showActivityScreen(decodeURIComponent(activityPathMatch[1]));
      return;
    }
    if (/^\/friends\/activity\/?$/.test(pathname)) {
      this._showActivityScreen(null, 'friends');
      return;
    }
    if (/^\/activity\/?$/.test(pathname)) {
      this._showActivityScreen();
      return;
    }

    // /u/:username — public profile page.
    const profileMatch = pathname.match(/^\/u\/([^/]+)\/?$/);
    if (profileMatch) {
      this._showStatsScreen(decodeURIComponent(profileMatch[1]));
      return;
    }
    // /u — own stats page.
    if (/^\/u\/?$/.test(pathname)) {
      this._showStatsScreen();
      return;
    }

    // /rooms — rooms browser.
    if (/^\/rooms\/?$/.test(pathname)) {
      this._showRoomsScreen();
      return;
    }

    // /controllers — all-user Controllers screen. The route always resolves;
    // the screen renders "coming soon" when the controller_profiles flag is OFF.
    if (/^\/controllers\/?$/.test(pathname)) {
      this._showControllersScreen();
      return;
    }

    // /top100 — curated top 100 lists.
    const top100Match = pathname.match(/^\/top100(?:\/([a-z0-9]+))?\/?$/);
    if (top100Match) {
      this._showTop100Screen(top100Match[1] || null);
      return;
    }

    // /friends — friends page.
    if (pathname === '/friends') {
      this._showFriendsScreen();
      return;
    }
    // /invite/:token — share-link invite landing.
    const inviteMatch = pathname.match(/^\/invite\/([A-Za-z0-9_-]+)\/?$/);
    if (inviteMatch) {
      this._showInviteAcceptScreen(inviteMatch[1]);
      return;
    }

    // Path-based: /r/ABCDEF
    const pathMatch = pathname.match(/^\/r\/([A-Z0-9]+)\/?$/i);
    let code = pathMatch ? pathMatch[1].toUpperCase() : null;

    if (!code) {
      // Legacy query-based: ?join=ABCDEF — redirect to /r/{code} then join.
      const params = new URLSearchParams(window.location.search);
      code = params.get('join')?.toUpperCase() || null;
      if (!code) return;
      this._replaceRoomHistory(code);
    }

    const launchOwner = deepLinkLaunchOwner
      ?? this._beginGameLaunchIntent({ clearPendingSave: true });

    // Hide the menu immediately. _joinRoom() is async (room info fetch +
    // WebSocket handshake, up to a second on cold connections) and the
    // first screen change comes from onAssigned → _showScreen('lobby').
    // Without this, the user sees the main-menu home page flash for
    // that full second before the lobby appears. Swap straight to the
    // lobby shell so they see "joining…" chrome instead.
    this._showScreen('lobby');
    this.state = State.LOBBY;
    const lobbyStatus = document.getElementById('lobby-status');
    if (lobbyStatus) lobbyStatus.textContent = 'Joining room…';

    // If this tab originally created this room, decide whether to resume
    // as host or bail to menu based on whether the room is still alive.
    let stored = null;
    try {
      const raw = sessionStorage.getItem('retroplay:hostCode');
      if (raw) stored = JSON.parse(raw);
    } catch {}

    if (stored && stored.code === code) {
      const info = await this._fetchRoomInfo(code);
      if (!this._isGameLaunchOwnerCurrent(launchOwner)) return;
      const sameRoom = info && info.createdAt === stored.createdAt;
      if (sameRoom) {
        // Room is alive and it's ours — resume as host. (P2 kept it open.)
        this._resumeAsHost(code, info, launchOwner);
        return;
      }
      // Room is gone, or the code was reissued to a different room. Clean up.
      // We already called _showScreen('lobby') above to avoid a menu flash
      // while joining, so we MUST swap back to the menu here — otherwise the
      // user is stuck staring at a code-less "Joining room…" lobby shell.
      try { sessionStorage.removeItem('retroplay:hostCode'); } catch {}
      window.history.replaceState({}, '', '/');
      this._showScreen('menu');
      this.state = State.MENU;
      this._flash('Room is no longer available');
      return;
    }

    // Normal guest arrival.
    this._joinRoom(code, launchOwner);
  }

  async _fetchRoomInfo(code) {
    try {
      const r = await fetch(`/api/rooms/${code}`);
      if (!r.ok) return null;
      return await r.json();
    } catch {
      return null;
    }
  }

  _copyRoomLink(roomCode) {
    const url = `${window.location.origin}/r/${roomCode}`;
    navigator.clipboard.writeText(url).then(() => {
      this._flash('Room link copied!');
    }).catch(() => {
      this._flash(url);
    });
  }

  // ── Invite a friend (Phase 5.5b) ─────────────────────────────────
  //
  // Opens the current solo room to a 2nd player. Solo play keeps
  // running until the friend actually joins — we don't pause the
  // emulator, and the audio + achievement loops stay intact. The
  // server re-keys the room (solo:<username> → fresh 6-char code)
  // so the user can share a clean /r/XXXXXX URL. Handoff to hosted
  // netplay happens in the onGameStart branch below.

  async _inviteFriend() {
    if (this.state !== State.SOLO) {
      this._flash('Invite is only available in solo play');
      return;
    }
    if (!this._soloRoomCode) {
      this._flash('Sign in to invite a friend');
      return;
    }
    try {
      const res = await fetch(`/api/rooms/${encodeURIComponent(this._soloRoomCode)}/open-to-friends`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        this._flash(body.error === 'room_private'
          ? 'Your room is private. Enable public watching in Settings to invite viewers.'
          : body.error === 'not_host' ? 'Only the host can invite' : 'Could not open the room to friends');
        return;
      }
      const { code, spectatorOnly = false } = await res.json();
      // Re-key client-side state: the WebSocket connection stays
      // alive (the room object kept its reference) but every future
      // HTTP call for this room uses the new code.
      this._soloRoomCode = code;
      if (this.net) this.net.roomCode = code;
      // URL: if the solo URL (/u/<username>/play) was active, swap
      // to /r/<newCode> so the browser back button and copy-URL
      // both give the shareable form.
      if (!spectatorOnly) this._replaceRoomHistory(code);
      // Remember this tab owns the room so _checkJoinLink treats a
      // reload as a host-resume, not a guest arrival.
      try {
        if (!spectatorOnly) {
          sessionStorage.setItem('retroplay:hostCode', JSON.stringify({
            code,
            createdAt: Date.now(),
          }));
        }
      } catch {}
      // Solo invites are cancelable (close-to-friends reverts the room
      // back to spectator mode). Make sure the button is visible — a
      // prior lobby invite may have hidden it.
      document.getElementById('btn-invite-cancel')?.classList.toggle('hidden', spectatorOnly);
      const title = document.getElementById('invite-modal-title');
      if (title) title.textContent = spectatorOnly ? 'Invite to watch' : 'Invite a friend';
      const hint = document.querySelector('#invite-modal .invite-modal-hint');
      if (hint) {
        hint.textContent = spectatorOnly
          ? 'Invite a friend to watch your C64 and chat. You keep control of the keyboard and disks.'
          : "Send this link to a friend. When they open it, you'll start playing together. Your game keeps running until they join.";
      }
      this._openInviteModal(code);
    } catch (err) {
      console.error('[invite] failed:', err);
      this._flash('Could not open the room to friends');
    }
  }

  _openInviteModal(code) {
    const modal = document.getElementById('invite-modal');
    const codeEl = document.getElementById('invite-code-display');
    const statusEl = document.getElementById('invite-modal-status');
    const searchEl = document.getElementById('invite-friends-search');
    if (codeEl) codeEl.textContent = code;
    if (statusEl) statusEl.textContent = '';
    if (searchEl) searchEl.value = '';
    // Reset email invite section — hidden by default (lobby shows it explicitly)
    const emailSection = document.getElementById('invite-email-section');
    const emailInput = document.getElementById('invite-email-input');
    const emailStatus = document.getElementById('invite-email-status');
    if (emailSection) emailSection.classList.add('hidden');
    if (emailInput) emailInput.value = '';
    if (emailStatus) emailStatus.textContent = '';
    // "Suggest instead" escape hatch — only in solo play (where the invite is
    // about the current game), not in the lobby variant.
    document.getElementById('btn-invite-suggest')?.classList.toggle(
      'hidden',
      this.state !== State.SOLO || this._isBasicMode || !this._suggestEnabled(),
    );
    if (modal) {
      // Friend search and email are text fields over a game that keeps
      // running: hold a gameplay-input lease until _closeInviteModal.
      this.acquireGameplayInput('invite-modal');
      modal.classList.remove('hidden');
    }
    this._renderInviteFriendsPicker(code);
  }

  /**
   * Open the invite modal pre-populated with the lobby's room code.
   * The room is already a 2-player room (created via POST /api/rooms),
   * so there's nothing to "cancel" — hide the Cancel button and reword
   * the hint to match the lobby context.
   */
  _openInviteModalForLobby(code) {
    const title = document.getElementById('invite-modal-title');
    if (title) title.textContent = 'Invite a friend';
    document.getElementById('btn-invite-cancel')?.classList.add('hidden');
    const hint = document.querySelector('#invite-modal .invite-modal-hint');
    if (hint) {
      hint.textContent = "Pick a friend below or share the code. They'll get an in-app notification and an email.";
    }
    this._openInviteModal(code);
    this._wireInviteEmailSection(code);
  }

  _wireInviteEmailSection(code) {
    const section = document.getElementById('invite-email-section');
    const form = document.getElementById('invite-email-form');
    const input = document.getElementById('invite-email-input');
    const status = document.getElementById('invite-email-status');
    if (!section || !form || !input) return;
    section.classList.remove('hidden');

    const clone = form.cloneNode(true);
    form.replaceWith(clone);
    const submitBtn = clone.querySelector('button[type="submit"]');
    const emailInput = clone.querySelector('input');

    clone.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = emailInput.value.trim();
      if (!email) { this._showInviteEmailError(status, 'Enter an email address.'); return; }
      submitBtn.disabled = true;
      if (status) status.textContent = '';
      try {
        await sendEmailInvite({ email, room_code: code });
        this._showInviteEmailSuccess(status, `Invite sent to ${email}.`);
        emailInput.value = '';
        clone.classList.add('hidden');
      } catch (err) {
        this._handleInviteEmailError(status, err, email);
      } finally {
        submitBtn.disabled = false;
      }
    });
  }

  _showInviteEmailSuccess(el, msg) {
    if (!el) return;
    el.innerHTML = '';
    const span = document.createElement('span');
    span.className = 'friend-email-invite-ok';
    span.textContent = `✓ ${msg}`;
    el.appendChild(span);
  }

  _showInviteEmailError(el, msg) {
    if (!el) return;
    el.innerHTML = '';
    const span = document.createElement('span');
    span.className = 'friend-email-invite-err';
    span.textContent = msg;
    el.appendChild(span);
  }

  _handleInviteEmailError(el, err, email) {
    const code = err?.body?.error || err?.message || 'request_failed';
    if (code === 'bad_email') return this._showInviteEmailError(el, 'Enter a valid email address.');
    if (code === 'self') return this._showInviteEmailError(el, "That's your own email.");
    if (code === 'email_throttled') return this._showInviteEmailError(el, 'You already invited that email today.');
    if (code === 'daily_limit_reached') {
      const limit = err?.body?.limit || 25;
      return this._showInviteEmailError(el, `Daily invite limit reached (${limit}/day). Try again tomorrow.`);
    }
    if (code === 'email_is_user') {
      const username = err?.body?.username;
      const displayName = err?.body?.displayUsername || username;
      if (username) {
        this._showInviteEmailError(el, `@${displayName} is already on RetroPlay.`);
        const addBtn = document.createElement('button');
        addBtn.className = 'friend-action friend-action-primary';
        addBtn.textContent = 'Send friend request';
        addBtn.style.marginLeft = '8px';
        addBtn.addEventListener('click', async () => {
          addBtn.disabled = true;
          try {
            await sendFriendRequest(username);
            this._showInviteEmailSuccess(el, `Friend request sent to @${displayName}.`);
          } catch {
            this._showInviteEmailError(el, 'Friend request failed.');
          }
        });
        el.appendChild(addBtn);
        return;
      }
      return this._showInviteEmailError(el, 'That email already has an account.');
    }
    this._showInviteEmailError(el, 'Invite failed. Try again.');
  }

  async _renderInviteFriendsPicker(code) {
    const listEl = document.getElementById('invite-friends-list');
    if (!listEl) return;
    listEl.innerHTML = '<p class="invite-friend-empty">Loading friends…</p>';
    let friends = [];
    try {
      const res = await fetch('/api/friends', { credentials: 'same-origin' });
      if (!res.ok) throw new Error(String(res.status));
      const body = await res.json();
      friends = Array.isArray(body.friends) ? body.friends : [];
    } catch {
      listEl.innerHTML = '<p class="invite-friend-empty">Could not load friends list.</p>';
      return;
    }
    if (!friends.length) {
      listEl.innerHTML = '<p class="invite-friend-empty">Add a friend first to send direct invites.</p>';
    } else {
      this._renderInviteFriendRows(listEl, friends, code);
    }

    // Wire up search input
    const searchEl = document.getElementById('invite-friends-search');
    if (!searchEl) return;
    clearTimeout(this._inviteSearchTimer);
    const handler = () => {
      const q = searchEl.value.trim();
      clearTimeout(this._inviteSearchTimer);
      if (q.length < 2) {
        // Restore the original friends list
        if (!friends.length) {
          listEl.innerHTML = '<p class="invite-friend-empty">Add a friend first to send direct invites.</p>';
        } else {
          this._renderInviteFriendRows(listEl, friends, code);
        }
        return;
      }
      listEl.innerHTML = '<p class="invite-friend-empty">Searching…</p>';
      this._inviteSearchTimer = setTimeout(() => this._runInviteSearch(q, code, listEl, searchEl), 220);
    };
    searchEl.removeEventListener('input', searchEl._inviteHandler);
    searchEl._inviteHandler = handler;
    searchEl.addEventListener('input', handler);
  }

  _renderInviteFriendRows(listEl, friends, code) {
    listEl.innerHTML = '';
    for (const f of friends) {
      const row = document.createElement('div');
      row.className = 'friend-row';
      const uname = this._escapeHtml(f.username || '');
      const avatarHtml = `<retro-avatar class="friend-avatar" username="${uname}" size="list" fallback-avatar></retro-avatar>`;
      const display = this._escapeHtml(f.displayUsername || f.username || '?');
      row.innerHTML = `
        <div class="friend-row-link" style="cursor:default">
          ${avatarHtml}
          <div class="friend-row-text">
            <span class="friend-row-name">${display}</span>
            <span class="friend-row-presence">@${uname}</span>
          </div>
        </div>
        <div class="friend-row-actions">
          <button type="button" class="friend-action friend-action-primary">Invite</button>
        </div>
      `;
      const actionBtn = row.querySelector('.friend-action');
      actionBtn.addEventListener('click', () => this._sendDirectInvite(actionBtn, f, code));
      listEl.appendChild(row);
    }
  }

  async _runInviteSearch(q, code, listEl, searchEl) {
    try {
      const res = await fetch(`/api/friends/search?q=${encodeURIComponent(q)}`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(String(res.status));
      const body = await res.json();
      // Bail if user has changed the query while the fetch was in flight
      if (searchEl.value.trim() !== q) return;
      const users = body.users || [];
      if (!users.length) {
        listEl.innerHTML = '<p class="invite-friend-empty">No matches found.</p>';
        return;
      }
      this._renderInviteSearchResults(listEl, users, code);
    } catch {
      listEl.innerHTML = '<p class="invite-friend-empty">Search failed. Try again.</p>';
    }
  }

  _renderInviteSearchResults(listEl, users, code) {
    listEl.innerHTML = '';
    for (const u of users) {
      const row = document.createElement('div');
      row.className = 'friend-row';
      const uname = this._escapeHtml(u.username || '');
      const avatarHtml = `<retro-avatar class="friend-avatar" username="${uname}" size="list" fallback-avatar></retro-avatar>`;
      const display = this._escapeHtml(u.displayUsername || u.username || '?');
      const status = u.friend_status || 'none';

      let actionHtml;
      let actionClass;
      if (status === 'friends') {
        actionClass = 'friend-action-primary';
        actionHtml = 'Invite';
      } else if (status === 'pending_outgoing') {
        actionClass = 'friend-action-pending';
        actionHtml = '⋯ Pending';
      } else {
        actionClass = 'friend-action-primary';
        actionHtml = '+ Add';
      }

      row.innerHTML = `
        <div class="friend-row-link" style="cursor:default">
          ${avatarHtml}
          <div class="friend-row-text">
            <span class="friend-row-name">${display}</span>
            <span class="friend-row-presence">@${uname}</span>
          </div>
        </div>
        <div class="friend-row-actions">
          <button type="button" class="friend-action ${actionClass}"${status === 'pending_outgoing' ? ' disabled' : ''}>${actionHtml}</button>
        </div>
      `;

      const actionBtn = row.querySelector('.friend-action');
      if (status === 'friends') {
        actionBtn.addEventListener('click', () => this._sendDirectInvite(actionBtn, u, code));
      } else if (status !== 'pending_outgoing') {
        actionBtn.addEventListener('click', async () => {
          actionBtn.disabled = true;
          try {
            const r = await fetch('/api/friends/requests', {
              method: 'POST', credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ username: u.username }),
            });
            actionBtn.textContent = r.ok ? '⋯ Pending' : 'Failed';
            if (r.ok) actionBtn.className = 'friend-action friend-action-pending';
          } catch {
            actionBtn.disabled = false;
            actionBtn.textContent = '+ Add';
          }
        });
      }
      listEl.appendChild(row);
    }
  }

  async _sendDirectInvite(btnEl, friend, code) {
    if (!friend?.username || !code) return;
    const lobbyTargetSlot = this.state === State.LOBBY
      ? ((this._lobbyMaxPlayers || 2) > 2
          ? this._firstOpenLobbySlot()
          : (this._pendingLobbySwap ? (this.net?.playerNumber || 1) : ((this.net?.playerNumber || 1) === 1 ? 2 : 1)))
      : null;
    if (this.state === State.LOBBY && lobbyTargetSlot == null) {
      this._flash('This room is full');
      return;
    }
    btnEl.disabled = true;
    btnEl.textContent = 'Sending…';
    try {
      const res = await fetch('/api/play-invites', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_username: friend.username, room_code: code }),
      });
      if (res.status === 409) {
        btnEl.textContent = 'Already invited';
        return;
      }
      if (!res.ok) throw new Error(String(res.status));
      const body = await res.json().catch(() => ({}));
      btnEl.textContent = 'Invited ✓';
      btnEl.className = 'friend-action friend-action-friends';
      this._flash(`Invited @${friend.displayUsername || friend.username}`);
      // From the lobby: stash the invite + invitee, surface in the P2 slot,
      // swap the button to "Cancel Invitation", and auto-close the modal so
      // the lobby UX takes over.
      if (this.state === State.LOBBY && body?.id) {
        this._pendingInvite = { id: body.id, username: friend.displayUsername || friend.username, slot: lobbyTargetSlot };
        this._setPlayerStatus(lobbyTargetSlot, 'waiting');
        this._refreshLobbyInviteButton();
        setTimeout(() => this._closeInviteModal(), 600);
      }
    } catch {
      btnEl.disabled = false;
      btnEl.textContent = 'Failed, retry';
    }
  }

  /**
   * Toggle the lobby's invite button between "Invite a Friend" and
   * "Cancel Invitation" depending on whether a pending invite exists.
   * Also re-evaluates visibility (peer-joined hides it entirely).
   */
  _refreshLobbyInviteButton() {
    const btn = document.getElementById('btn-lobby-invite-friend');
    if (!btn) return;
    if (this._pendingInvite) {
      btn.textContent = 'Cancel Invitation';
      btn.classList.add('btn-danger-soft');
    } else {
      btn.textContent = 'Invite a Friend';
      btn.classList.remove('btn-danger-soft');
    }
    this._updateLobbyInviteButtonVisibility();
  }

  /**
   * Cancel the currently-pending lobby invite. Calls
   * DELETE /api/play-invites/:id (sender-only). On success, clear the
   * pending state and reset the slot + button. 410 (already accepted /
   * declined / expired) is treated as success.
   */
  async _cancelPendingLobbyInvite() {
    const inv = this._pendingInvite;
    if (!inv) return;
    const targetSlot = inv.slot;
    try {
      const res = await fetch(`/api/play-invites/${encodeURIComponent(inv.id)}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      });
      if (!res.ok && res.status !== 410 && res.status !== 404) {
        this._flash('Could not cancel the invite');
        return;
      }
      this._pendingInvite = null;
      this._setPlayerStatus(targetSlot, 'waiting');
      this._refreshLobbyInviteButton();
      this._flash(`Invite to @${inv.username} canceled`);
    } catch {
      this._flash('Could not cancel the invite');
    }
  }

  /** Every invite-modal close path goes through here to release its lease. */
  _closeInviteModal() {
    document.getElementById('invite-modal')?.classList.add('hidden');
    this.releaseGameplayInput('invite-modal');
  }

  _copyInviteLink() {
    const code = document.getElementById('invite-code-display')?.textContent?.trim();
    if (!code || code === '------') return;
    const url = `${window.location.origin}/r/${code}`;
    const statusEl = document.getElementById('invite-modal-status');
    navigator.clipboard.writeText(url).then(() => {
      if (statusEl) statusEl.textContent = 'Link copied!';
    }).catch(() => {
      // Fallback for browsers/insecure contexts where clipboard is
      // unavailable — show the URL so the user can select it manually.
      if (statusEl) statusEl.textContent = url;
    });
  }

  async _cancelInvite() {
    const code = this._soloRoomCode;
    if (!code) {
      this._closeInviteModal();
      return;
    }
    try {
      const res = await fetch(`/api/rooms/${encodeURIComponent(code)}/close-to-friends`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.status === 409) {
        // A guest has already joined — the handoff has started, too
        // late to cancel. Let it proceed.
        const statusEl = document.getElementById('invite-modal-status');
        if (statusEl) statusEl.textContent = 'A friend has already joined. Starting now…';
        return;
      }
      if (!res.ok) {
        this._flash('Could not cancel the invite');
        return;
      }
      const { code: restoredCode } = await res.json();
      this._soloRoomCode = restoredCode;
      if (this.net) this.net.roomCode = restoredCode;
      // Restore the /u/<username>/play URL if we have a username,
      // otherwise drop to /.
      const user = authState.getCurrentUser();
      if (user?.username) {
        window.history.replaceState({}, '', `/u/${encodeURIComponent(user.username)}/play`);
      } else {
        window.history.replaceState({}, '', '/');
      }
      try { sessionStorage.removeItem('retroplay:hostCode'); } catch {}
      this._closeInviteModal();
      this._flash('Invite canceled');
    } catch (err) {
      console.error('[invite] cancel failed:', err);
      this._flash('Could not cancel the invite');
    }
  }

  /**
   * Reclaim the host slot of an existing room after a tab reload.
   * Called only when `_checkJoinLink` has verified that the room is still
   * alive (P2 kept it open) AND that the createdAt matches what this tab
   * recorded at create time. Reconstructs host-side state from what's on
   * the server: the ROM is still cached in the live room, and a save
   * state is loaded if one is present (e.g. from a prior swap).
   */
  async _resumeAsHost(code, info, launchOwner) {
    const expectedNet = this.net;
    const ownsLaunch = () => launchOwner === undefined
      || this._isGameLaunchOwnerCurrent(launchOwner);
    const ownsSession = () => ownsLaunch() && this.net === expectedNet;
    if (!ownsSession()) return;
    try {
      // Align the emulator system with the server's record for the room.
      if (info.system && info.system !== this.system.id) {
        this._selectSystem(info.system);
      }

      // Pull the ROM back from the server.
      const rom = await this._downloadROM(code);
      if (!ownsSession()) return;
      if (!rom) {
        this._flash('Could not resume: ROM missing');
        try { sessionStorage.removeItem('retroplay:hostCode'); } catch {}
        window.history.replaceState({}, '', '/');
        this._showScreen('menu');
        this.state = State.MENU;
        return;
      }
      await this._installRoomRom(rom, { isCurrent: ownsSession });
      if (!ownsSession()) return;

      // Optional save state — resume from it if one exists, otherwise the
      // emulator will start from frame 0 when the game begins.
      const save = await this._downloadSaveState(code, ownsSession);
      if (!ownsSession()) return;
      if (save) this._replacePendingSave(save);

      // Join the existing WS room. Server assigns us to slot 0 (the freed
      // host slot) and we behave as host from `assigned` onward — same
      // tail as `_createRoom` after its own net.connect().
      // onAssigned may run before connect() settles. Publish the resume owner
      // first so assignment-side rejoin work inherits the same intent.
      this._roomJoinLaunchOwner = launchOwner ?? null;
      await expectedNet.connect(code);
      if (!ownsSession() || expectedNet.roomCode !== code) {
        this._retireRoomConnectionIfOwned(expectedNet, code, launchOwner);
        return;
      }
      this.state = State.LOBBY;
      expectedNet.sendRomReady(this.romHash);
      this._flash('Resuming as host…');
    } catch (err) {
      if (!ownsSession() || isConnectionCancellation(err)) {
        this._retireRoomConnectionIfOwned(expectedNet, code, launchOwner);
        return;
      }
      console.error(err);
      this._flash('Failed to resume room');
      try { sessionStorage.removeItem('retroplay:hostCode'); } catch {}
      window.history.replaceState({}, '', '/');
      this._showScreen('menu');
      this.state = State.MENU;
    }
  }

  async _tryResumeSoloSession(username, existingLaunchOwner = null) {
    const launchOwner = existingLaunchOwner
      ?? this._beginGameLaunchIntent({ clearPendingSave: true });
    const launchIsCurrent = () => this._isGameLaunchOwnerCurrent(launchOwner);
    if (!launchIsCurrent()) return false;
    this._hideSysStatus();
    let stored = null;
    try {
      const raw = sessionStorage.getItem('retroplay:soloSession');
      if (raw) stored = JSON.parse(raw);
    } catch {}
    const basicResume = stored?.isBasicMode === true && stored.system === 'c64';
    if (!stored?.romHash && !basicResume) return false;

    if (typeof stored.username !== 'string' || stored.username.toLowerCase() !== username.toLowerCase()) return false;

    if (!authState.isLoaded()) {
      try { await authState.refreshSession(); } catch {}
      if (!launchIsCurrent()) return false;
    }
    const user = authState.getCurrentUser();
    if (!user?.username || user.username.toLowerCase() !== username.toLowerCase()) return false;

    // A cold host reload must settle the optional room UI before a launch
    // permanently chooses its layout, just like a cold C64 spectator join.
    if (stored.system === 'c64') {
      await this._refreshFeatureFlags();
      if (!launchIsCurrent() || authState.getCurrentUser()?.id !== user.id) return false;
      if (moduleRegistry.getFeatures().game_room_v2 === true) await loadGameRoomChunk(this);
      if (!launchIsCurrent() || authState.getCurrentUser()?.id !== user.id) return false;
    }

    if (basicResume) {
      if (moduleRegistry.getFeatures()['system:c64'] !== true) return false;
      this._selectSystem('c64');
      if (!launchIsCurrent()) return false;
      return this._startBasicSafely(launchOwner, { roomCode: stored.roomCode || null });
    }

    const resumeSolo = async () => {
      this._restartSoloRoomCode = stored.roomCode || null;
      try {
        await this._startSolo(launchOwner);
        return launchIsCurrent();
      } finally {
        if (launchIsCurrent()) this._restartSoloRoomCode = null;
      }
    };

    await initSaveDB(user.id);
    if (!launchIsCurrent()) return false;
    const storageNamespace = currentSaveDBNamespace();
    const save = await findMostRecentSave(stored.romHash);
    if (!launchIsCurrent()) return false;
    if (currentSaveDBNamespace() !== storageNamespace) return false;
    if (!save?.emulatorState) {
      // A newly loaded C64 game may not have an autosave yet. Restore the
      // host's launch from the catalog while keeping the room and viewers.
      if (stored.system === 'c64' && /^[A-Z0-9]{6}$/.test(stored.roomCode || '')
          && typeof stored.romFilename === 'string' && stored.romFilename
          && moduleRegistry.getFeatures()['system:c64'] === true) {
        this._prepareFreshSaveLaunch();
        this._selectSystem('c64');
        if (!launchIsCurrent()) return false;
        const loaded = await this._fetchAndSetRom('c64', stored.romFilename, launchOwner, null,
          { suppressPlayModePrompt: true });
        if (!launchIsCurrent() || authState.getCurrentUser()?.id !== user.id
            || currentSaveDBNamespace() !== storageNamespace) return false;
        if (loaded && primaryHash(this.romHash) === primaryHash(stored.romHash)) {
          return await resumeSolo() && this.running;
        }
      }
      try { sessionStorage.removeItem('retroplay:soloSession'); } catch {}
      return false;
    }

    const systemId = save.system || stored.system || 'nes';
    const { createLocalSaveTarget } = await import('./modules/save-overwrite.js');
    if (!launchIsCurrent()) return false;
    if (authState.getCurrentUser()?.id !== user.id) return false;
    let saveTarget = createLocalSaveTarget(
      this, save, save.id ?? null, user.id, storageNamespace,
    );

    const clearSession = () => {
      try { sessionStorage.removeItem('retroplay:soloSession'); } catch {}
      return false;
    };

    // Single-ROM saves no longer embed the ROM — _resolveSaveRom refetches it
    // from the library (the embedded multi-disk bundle is still used directly).
    if (stored.isMultiDisk || save.isMultiDisk) {
      const rom = await this._resolveSaveRom(save, systemId);
      if (!launchIsCurrent()) return false;
      if (!rom?.bundle) return clearSession();
      // A C64 bundle resumes under its verified catalog identity (spec 4.3).
      // The resolution and the local copy can wait (the ROM list, the copy's
      // budget), so the account and the save database are checked after each.
      const ownsResumeStorage = () => authState.getCurrentUser()?.id === user.id
        && currentSaveDBNamespace() === storageNamespace;
      let identity = null;
      if (systemId === 'c64') {
        try { identity = await resolveC64LaunchIdentity(this, rom.bundle, { isCurrent: launchIsCurrent }); } catch { return clearSession(); }
        if (!identity || !launchIsCurrent() || !ownsResumeStorage()) return false;
      }
      this._prepareFreshSaveLaunch();
      if (systemId !== this.system.id) this._selectSystem(systemId);
      this.romData = rom.bundle;
      this._romDataB64 = save.romData;
      this.romName = save.romName || 'Unknown';
      this._isMultiDisk = true;
      if (identity) commitC64Identity(this, identity);
      else {
        this.romHash = EmulatorCore.hashROM(rom.bundle.disks.map(d => d.data).join('').substring(0, 8192));
        this._c64Identity = null;
      }
      // A save stored under the legacy hash is not the overwrite target.
      if (identity && primaryHash(save.romHash) !== identity.hash) saveTarget = null;
      if (identity) {
        // storageNamespace was captured right after this resume opened its database.
        await finishC64LaunchIdentity(this, rom.bundle, identity, { expectedNamespace: storageNamespace });
        if (!launchIsCurrent() || !ownsResumeStorage()) return false;
      }
    } else {
      const rom = await this._resolveSaveRom(save, systemId);
      if (!launchIsCurrent()) return false;
      if (!rom?.romStr) return clearSession();
      this._prepareFreshSaveLaunch();
      if (systemId !== this.system.id) this._selectSystem(systemId);
      this.romData = rom.romStr;
      this.romName = save.romName || rom.name || 'Unknown';
      this.romHash = save.romHash || EmulatorCore.hashROM(rom.romStr);
      this._c64Identity = null;
      this._isMultiDisk = false;
    }

    this._saveRef = saveTarget;
    this._replacePendingSave({
      emulatorState: save.emulatorState,
      frame: save.frame || 0,
      replayLog: save.replayLog || null,
      coreName: saveManager.coreNameForSave(save, save.system || this.system?.id),
    });
    this._soloModeOverride = saveManager.soloModeForSave(save, systemId);
    this._flash('Resuming your game…');
    return resumeSolo();
  }

  // ── ROM Helpers ─────────────────────────────────────────

  _clearPendingLaunchArtifacts({ preserveSaveRef = false } = {}) {
    this._replacePendingSave(null);
    if (!preserveSaveRef) this._saveRef = null;
    this._soloModeOverride = null;
    // These payloads belong to the ROM/session that staged them. A newer
    // ordinary ROM, save, room, or exit intent must not replay/mark/boot them
    // against its replacement game.
    this._pendingReplayId = null;
    this._pendingReplayCoreName = null;
    this._pendingGhostId = null;
    this._pendingSuggestionId = null;
    this._pendingAsyncGame = null;
  }

  _beginGameLaunchIntent({ clearPendingSave = false } = {}) {
    clearSuperunoLobbyStartup(this);
    clearSuperunoReady(this);
    retireSuperunoMatchEnd(this);
    this._multitapPreparation?.abort();
    this._multitapReplayStartup?.abort();
    this._pendingAsyncChallenge = null;
    this._pendingLibraryLaunch = null;
    // A committed launch supersedes any validation-only candidate immediately.
    this._pendingGameLaunchCandidate = null;
    this._nextSaveLaunchRef = null;
    this._committedSaveLaunchRef = null;
    this._deferredCandidateRoomEvents = [];
    const generation = (this._gameLaunchIntentGeneration || 0) + 1;
    this._gameLaunchIntentGeneration = generation;
    if (clearPendingSave) this._clearPendingLaunchArtifacts();
    return generation;
  }

  /**
   * Start validation for a possible launch without invalidating the session
   * that remains visible/running until validation succeeds. Only the newest
   * candidate may commit. Room events are held while a candidate is pending,
   * then become current again automatically if validation fails.
   */
  _beginGameLaunchCandidate({ clearPendingSave = false } = {}) {
    if (!Array.isArray(this._deferredCandidateRoomEvents)) {
      this._deferredCandidateRoomEvents = [];
    }
    const candidate = {
      generation: (this._gameLaunchCandidateGeneration || 0) + 1,
      saveRef: this._nextSaveLaunchRef || null,
    };
    this._nextSaveLaunchRef = null;
    this._gameLaunchCandidateGeneration = candidate.generation;
    this._pendingGameLaunchCandidate = candidate;
    if (clearPendingSave) this._clearPendingLaunchArtifacts({ preserveSaveRef: true });
    return candidate;
  }

  _isGameLaunchCandidateCurrent(candidate) {
    return !!candidate && this._pendingGameLaunchCandidate === candidate;
  }

  _isGameLaunchOwnerCurrent(owner) {
    return typeof owner === 'object' && owner !== null
      ? this._isGameLaunchCandidateCurrent(owner)
      : this._pendingGameLaunchCandidate == null
        && this._isGameLaunchIntentCurrent(owner);
  }

  /** Commit a validated candidate, returning the active generation it owns. */
  _commitGameLaunchCandidate(owner, { clearPendingSave = false } = {}) {
    // Callers that inherited an already-committed deep-link/room owner do not
    // need another generation; they only need its currentness confirmed.
    if (typeof owner !== 'object' || owner === null) {
      return this._isGameLaunchOwnerCurrent(owner) ? owner : null;
    }
    if (!this._isGameLaunchCandidateCurrent(owner)) return null;
    this._pendingGameLaunchCandidate = null;
    this._deferredCandidateRoomEvents = [];
    const generation = (this._gameLaunchIntentGeneration || 0) + 1;
    this._gameLaunchIntentGeneration = generation;
    if (clearPendingSave) this._clearPendingLaunchArtifacts();
    const target = owner.saveRef;
    this._committedSaveLaunchRef = target?.storageOwnerId === (authState.getCurrentUser()?.id || null)
      && target?.storageNamespace === currentSaveDBNamespace()
      ? target
      : null;
    return generation;
  }

  _finishGameLaunchCandidate(candidate) {
    if (this._pendingGameLaunchCandidate === candidate) {
      this._pendingGameLaunchCandidate = null;
      const deferredEvents = this._deferredCandidateRoomEvents || [];
      this._deferredCandidateRoomEvents = [];
      // Establish which events still belong to the retained room before any
      // replay can synchronously advance its generation (game change/swap).
      const replayable = deferredEvents.filter(({ predecessorOwner }) => (
        this._retainedRoomSessionOwnerMatches(predecessorOwner)
      ));
      for (const { replay } of replayable) {
        try { replay(); } catch (err) {
          console.warn('[launch] deferred room event replay failed:', err);
        }
      }
    }
  }

  /**
   * Advance launch ownership for a transition that deliberately keeps the
   * current room socket alive (game change, hosted player-role swap, or async
   * baton handoff).
   * Automatic reconnect assignments arrive on a replacement socket but the
   * same Network instance/room; publishing the successor here lets the normal
   * assignment guard accept that current room without weakening stale initial
   * assignment rejection for unrelated launches.
   */
  _beginRoomSessionLaunchIntent(options = {}, predecessorOwner) {
    // Server-driven events may retain the current socket, but they may not
    // reclaim ownership from a newer save/room/ROM launch that started while
    // the old room was still connected. Deliberate local transitions omit the
    // predecessor and are allowed to advance ownership explicitly.
    if (predecessorOwner !== undefined
        && !this._isRoomSessionOwnerCurrent(predecessorOwner)) return null;
    const generation = this._beginGameLaunchIntent(options);
    this._roomJoinLaunchOwner = generation;
    return generation;
  }

  _captureRoomSessionOwner() {
    return {
      launchOwner: this._roomJoinLaunchOwner,
      expectedNet: this.net,
      expectedRoomCode: this.net?.roomCode,
    };
  }

  _isRoomSessionOwnerCurrent(owner) {
    // This predicate gates a newly-arriving one-shot room event. A candidate
    // pauses such events so they can be replayed on validation failure. Async
    // work that already owns the retained room uses _roomAssignmentIsCurrent
    // instead and remains live until the candidate actually commits.
    return this._pendingGameLaunchCandidate == null
      && owner?.launchOwner != null
      && this._retainedRoomSessionOwnerMatches(owner);
  }

  _retainedRoomSessionOwnerMatches(owner) {
    return owner?.launchOwner != null
      && this._isGameLaunchIntentCurrent(owner.launchOwner)
      && this._roomJoinLaunchOwner === owner.launchOwner
      && this.net === owner.expectedNet
      && owner.expectedNet?.roomCode === owner.expectedRoomCode;
  }

  _deferCandidateRoomEvent(predecessorOwner, replay) {
    if (this._pendingGameLaunchCandidate == null
        || !this._retainedRoomSessionOwnerMatches(predecessorOwner)) return false;
    if (!Array.isArray(this._deferredCandidateRoomEvents)) {
      this._deferredCandidateRoomEvents = [];
    }
    this._deferredCandidateRoomEvents.push({ predecessorOwner, replay });
    return true;
  }

  _isGameLaunchIntentCurrent(generation) {
    return this._gameLaunchIntentGeneration === generation;
  }

  _roomAssignmentIsCurrent(assignmentOwner) {
    if (!assignmentOwner) return true;
    const { launchOwner, expectedNet, expectedRoomCode } = assignmentOwner;
    const roomMatches = this.net === expectedNet
      && expectedNet?.roomCode === expectedRoomCode;
    if (!roomMatches) return false;
    // An ownerless initial assignment is never allowed to race a prospective
    // launch. A published active-room owner, however, remains valid while that
    // candidate only validates. Committing the candidate advances the active
    // generation, which makes the retained continuation stale immediately.
    return launchOwner == null
      ? this._pendingGameLaunchCandidate == null
      : this._retainedRoomSessionOwnerMatches(assignmentOwner);
  }

  _retireRoomConnectionIfOwned(expectedNet, roomCode, launchOwner) {
    if (this.net !== expectedNet
        || expectedNet?.roomCode !== roomCode
        || this._roomJoinLaunchOwner !== (launchOwner ?? null)) return;
    expectedNet.disconnect();
    this._roomJoinLaunchOwner = null;
  }

  /**
   * Scope the canonical title to the installed system and ROM hash. Prefer a
   * previously resolved title for this ROM, then the launch's catalog title;
   * a new ROM without either starts with its filename. The lookup may refine
   * the title later. Returns the current title, or '' when there is none.
   */
  _resetCanonicalTitle(title) {
    const known = typeof title === 'string' ? title.trim() : '';
    const key = this._lobbyTitleCacheKey();
    const resolved = (key && this._lobbyTitleCache?.get(key)) || known;
    this._canonicalTitle = resolved || null;
    this._canonicalTitleKey = resolved ? key : null;
    if (known && key && !this._lobbyTitleCache?.has(key)) {
      (this._lobbyTitleCache ??= new Map()).set(key, known);
    }
    return resolved;
  }

  _setRom(romStr, name, {
    preserveLaunchIntent = false,
    suppressPlayModePrompt = false,
    title = null,
  } = {}) {
    // C64 bundles never take the single-ROM path: every bundle launch sets
    // romData to the bundle object. A bundle's JSON text arriving here (the
    // byte-pinned legacy local-save loader re-reading a row that became a
    // bundle) is refused before anything consumes it.
    if (isC64BundleText(romStr)) throw Object.assign(new Error('C64 bundle on the single-ROM path'), { code: 'c64_bundle_on_rom_path' });
    this._c64Identity = null;
    const previousRom = this.romData;
    this._pendingLibraryLaunch = null;
    // Installing an ordinary ROM is a competing launch and must cancel any
    // older save download/import as well as discard an already staged save.
    if (!preserveLaunchIntent) {
      this._beginGameLaunchIntent({ clearPendingSave: true });
    }
    this.romData = romStr;
    this.romName = name;
    this.romHash = EmulatorCore.hashROM(romStr);
    roomFlow.noteLoadedRomChoice(this, previousRom);
    const choiceOwner = { source: romStr, system: this.system?.id };
    const choiceButton = document.getElementById('btn-play-mode-multitap');
    if (choiceButton) choiceButton.hidden = true;
    void qualifiedMultitapChoice(choiceOwner.system, romStr, moduleRegistry.getFeatures()).then(qualified => {
      if (this.romData !== choiceOwner.source || this.system?.id !== choiceOwner.system) return;
      if (choiceButton) choiceButton.hidden = !qualified;
      if (choiceButton && qualified) choiceButton.querySelector('.play-mode-sub').textContent = `Qualified cartridge · ${qualified.minPlayers ?? 3}–${qualified.controllerCount} configured human controllers`;
    }).catch(() => {});
    const knownTitle = this._resetCanonicalTitle(title);
    this._isMultiDisk = false;
    const el = document.getElementById('rom-name');
    el.textContent = name;
    el.classList.remove('hidden');
    this._updateLobbyGameTitle();
    this._flash(`Loaded: ${knownTitle || name}`);
    // Prompt the user to pick a play mode if they're at the menu and just
    // loaded a fresh ROM — saves a hunt for the Solo / Create Room buttons.
    if (!suppressPlayModePrompt) this._maybePromptPlayMode(name);
    // Log the hash so users can seed it into achievements/definitions.js.
    // Kept as a plain console.info — intentionally not behind a dev flag.
    console.info(`[cheevos] ROM hash for ${name}: ${this.system.id}:${this.romHash}`);
  }

  /**
   * Resolve the playable ROM for a save object.
   *
   * Live saves no longer embed the ROM — carrying a base64 copy bloated every
   * 60s auto-save (a multi-MB structured-clone on the main thread) and was the
   * OOM tab-kill trigger. Only multi-disk bundles and legacy/exported saves
   * still carry `romData`. So prefer an embedded copy when present, reuse the
   * already-loaded ROM when it matches, else refetch from the library by
   * content hash → filename → romName (extracting from a zip if needed).
   *
   * Returns { bundle, name } for multi-disk saves, { romStr, name } for single
   * ROMs (binary string ready for _setRom / loadROM), or null if the ROM can't
   * be obtained (the caller should flash + bail).
   */
  async _resolveSaveRom(save, systemId) {
    // Multi-disk (C64) bundles stay embedded: small, and not cleanly
    // refetchable from the single-file ROM library.
    if (save.isMultiDisk) {
      if (!save.romData) return null;
      try {
        return { bundle: JSON.parse(atob(save.romData)), name: save.romName || 'Unknown' };
      } catch {
        return null;
      }
    }

    // Re-loading a save for the game already in memory — reuse it, no refetch.
    // Either side can be a GB dual-hash ARRAY (the session's app.romHash, or a
    // row saveGame stored unnormalized) — compare primaries.
    const saveHash = primaryHash(save.romHash);
    if (saveHash && saveHash === primaryHash(this.romHash)
        && typeof this.romData === 'string' && this.romData) {
      return { romStr: this.romData, name: save.romName || this.romName || 'Unknown' };
    }

    // Legacy / imported saves that still embed the ROM.
    if (save.romData) {
      try {
        return saveManager.decodeEmbeddedSaveRom(this, save, systemId);
      } catch {
        return null;
      }
    }

    // Saves may name an extracted member (NHL 94.gen) while the catalog
    // serves its archive (NHL 94.zip). Resolve the stable content hash first.
    if (!save.romName && !save.romFilename && !saveHash) return null;
    this._flash('Downloading ROM...');
    const romList = saveHash
      ? await fetch(`/api/roms?system=${systemId}`).then(r => r.ok ? r.json() : null).catch(() => null)
      : null;
    const matches = Array.isArray(romList) ? romList.filter(r => r?.hash === saveHash) : [];
    const match = matches.find(r => r.name === save.romFilename)
      || matches.find(r => r.name === save.romName) || matches[0];
    const filenames = [...new Set([match?.name, save.romFilename, save.romName]
      .filter(name => typeof name === 'string' && name))];
    let res, resolvedFilename;
    for (const filename of filenames) {
      res = await fetch(`/api/roms/${systemId}/${encodeURIComponent(filename)}`);
      if (res.ok) {
        resolvedFilename = filename;
        break;
      }
    }
    if (!res?.ok) return null;

    const buf = await res.arrayBuffer();
    let data = new Uint8Array(buf);
    // The catalog entry can have a different container extension from
    // the save metadata (for example a legacy `.gen` name resolved by hash to
    // the deployed `.zip`). Inspect the file we actually downloaded so archive
    // bytes are extracted before they reach the emulator.
    let name = resolvedFilename;
    if (name.toLowerCase().endsWith('.zip')) {
      const extracted = this._extractRomFromZip(data, SYSTEMS[systemId] || this.system);
      if (!extracted) return null;
      data = extracted.data;
      name = extracted.name;
    }
    let romStr = '';
    for (let i = 0; i < data.length; i++) romStr += String.fromCharCode(data[i]);
    return { romStr, name };
  }

  async _loadRomLibrary(gen) {
    // Clear filter when reloading library (e.g. system switch)
    const filterInput = document.getElementById('rom-filter');
    if (filterInput) filterInput.value = '';
    this._romFilterQuery = '';
    this._romFilterAch = false;
    this._romFilterScore = false;
    document.getElementById('rom-filter-ach')?.classList.remove('active');
    document.getElementById('rom-filter-score')?.classList.remove('active');
    document.getElementById('rom-filter-ach')?.setAttribute('aria-pressed', 'false');
    document.getElementById('rom-filter-score')?.setAttribute('aria-pressed', 'false');

    try {
      const res = await fetch(`/api/roms?system=${this.system.id}`);
      if (gen !== undefined && gen !== this._menuDataGen) return;
      const roms = await res.json();
      if (gen !== undefined && gen !== this._menuDataGen) return;
      const container = document.getElementById('rom-library');
      container.innerHTML = '';
      container.scrollTop = 0;
      const countEl = document.getElementById('rom-count');
      if (countEl) countEl.textContent = roms.length ? `(${roms.length})` : '';
      const romLabel = countEl?.closest('.card-label');
      if (romLabel) { romLabel.firstChild.textContent = `${this.system.name} Games `; }
      if (!roms.length) return;

      // Compute collision-aware display names so regional variants
      // and revision bumps (same cartridge-header title, different
      // filename) can be disambiguated. Three-pass resolution:
      //
      //   1. Base = `rom.title || strippedFilename`. If unique across
      //      the system list, use it as-is.
      //   2. On collision and a `variant` tag is present, try
      //      `${base} (${variant})`. If that's unique, use it.
      //   3. If the variant-augmented name is STILL colliding (two
      //      games that extract the same header AND share a variant,
      //      or a variant-less collision), fall back to the raw
      //      filename-without-extension — guaranteed unique.
      const displayNames = this._resolveDisplayNames(roms);

      // Cache resolved display names per filename so favourites /
      // recently-played / game-change overlay can look them up by
      // filename alone. Keyed by filename within the current system.
      this._romTitleCache = this._romTitleCache || {};
      this._romHashToTitle = this._romHashToTitle || {};
      this._romFilenameToHash = this._romFilenameToHash || {};
      const sysTitleMap = this._romTitleCache[this.system.id] = {};
      const sysHashMap = this._romHashToTitle[this.system.id] = {};
      const sysFileToHash = this._romFilenameToHash[this.system.id] = {};

      this._multiDiskRoms = this._multiDiskRoms || {};
      this._multiDiskRoms[this.system.id] = new Set(
        roms.filter(r => r.type === 'directory').map(r => r.name),
      );

      const favs = this._getFavorites();

      for (const rom of roms) {
        const entry = document.createElement('div');
        entry.className = 'rom-entry';
        const isMultiDisk = rom.type === 'directory';
        const displayName = displayNames.get(rom.name);
        sysTitleMap[rom.name] = displayName;
        if (rom.hash) sysHashMap[rom.hash] = displayName;
        if (rom.hash) sysFileToHash[rom.name] = rom.hash;
        entry.dataset.name = displayName.toLowerCase();
        const isFav = favs.some(f => f.name === rom.name && f.system === this.system.id);
        entry.innerHTML =
          `<button class="btn-fav${isFav ? ' active' : ''}" title="Favorite" aria-label="Favorite ${this._escapeHtml(displayName)}" aria-pressed="${isFav}">★</button>` +
          `<button type="button" class="rom-entry-name" title="${this._escapeHtml(displayName)}">${displayName}</button>`;
        if (isMultiDisk) {
          entry.querySelector('.rom-entry-name').addEventListener('click', () => this._selectMultiDiskRom(rom.name, entry));
        } else {
          entry.querySelector('.rom-entry-name').addEventListener('click', () => this._selectLibraryRom(rom.name, entry));
        }
        entry.querySelector('.btn-fav').addEventListener('click', (e) => {
          e.stopPropagation();
          this._toggleFavorite(rom.name, e.currentTarget);
        });
        container.appendChild(entry);
      }
    } catch {
      // Server may not have a roms/ directory — that's fine
    }

    this._loadFavoritesList();
    this._loadRecentlyPlayedList();
  }

  _filterRomLibrary(query) {
    this._romFilterQuery = query;
    this._applyRomFilters();
  }

  _applyRomFilters() {
    const q = stripDiacritics((this._romFilterQuery || '').toLowerCase().trim());
    const wantAch = this._romFilterAch || false;
    const wantScore = this._romFilterScore || false;
    const entries = document.querySelectorAll('#rom-library .rom-entry');
    let visible = 0;
    for (const entry of entries) {
      let hide = false;
      if (q && !stripDiacritics(entry.dataset.name).includes(q)) hide = true;
      if (wantAch && entry.dataset.ach !== '1') hide = true;
      if (wantScore && entry.dataset.score !== '1') hide = true;
      entry.classList.toggle('hidden', hide);
      if (!hide) visible++;
    }
    const countEl = document.getElementById('rom-count');
    if (countEl && (q || wantAch || wantScore)) {
      countEl.textContent = `(${visible})`;
    } else if (countEl && entries.length) {
      countEl.textContent = `(${entries.length})`;
    }
  }

  _stampRomBadges() {
    const sysId = this.system?.id;
    if (!sysId) return;
    const sys = this.cheevosEngine._systemCache.get(sysId);
    if (!sys) return;
    const fileToHash = this._romFilenameToHash?.[sysId] || {};
    const entries = document.querySelectorAll('#rom-library .rom-entry');
    for (const entry of entries) {
      const nameEl = entry.querySelector('.rom-entry-name');
      if (!nameEl) continue;
      const filename = Object.keys(fileToHash).find(
        f => (this._romTitleCache?.[sysId]?.[f] || '').toLowerCase() === entry.dataset.name
      );
      if (!filename) continue;
      const hash = fileToHash[filename];
      if (!hash) continue;
      const game = sys.byHash.get(hash);
      const hasAch = game && Array.isArray(game.achievements) && game.achievements.length > 0;
      const hasScore = !!(game && scoreSpecSupported(game.scoreSpec));
      entry.dataset.ach = hasAch ? '1' : '';
      entry.dataset.score = hasScore ? '1' : '';
      if (entry.querySelector('.rom-badge')) continue;
      if (!hasAch && !hasScore) continue;
      const badge = document.createElement('span');
      badge.className = 'rom-badge';
      badge.title = [
        hasAch ? `${game.achievements.length} achievements` : '',
        hasScore ? 'High score tracking' : '',
      ].filter(Boolean).join(' · ');
      if (hasAch) badge.appendChild(badgeIconEl('achievements'));
      if (hasScore) badge.appendChild(badgeIconEl('score'));
      nameEl.insertAdjacentElement('beforebegin', badge);
    }
    this._applyRomFilters();
  }

  // ── Favorites ──────────────────────────────────────────

  /**
   * Resolve collision-aware display names for a list of ROM entries
   * from /api/roms. Returns `Map<filename, displayName>`.
   *
   * Three-pass logic so regional variants and revision bumps stay
   * distinct when their cartridge headers extract to the same title:
   *   1. base = `rom.title || strippedFilename`
   *   2. If base is unique, use it.
   *   3. If base collides and the rom has a `variant` tag, try
   *      `base (variant)` — if that's unique, use it.
   *   4. Otherwise fall back to the raw filename (minus extension),
   *      which is guaranteed unique within a single directory.
   */
  _resolveDisplayNames(roms) {
    const baseOf = (rom) => rom.type === 'directory'
      ? rom.name
      : (rom.title || rom.name.replace(/\.[^.]+$/i, ''));
    const strippedFilename = (rom) => rom.name.replace(/\.[^.]+$/i, '');

    // Pass 1: count base names
    const baseCounts = new Map();
    for (const rom of roms) {
      const b = baseOf(rom);
      baseCounts.set(b, (baseCounts.get(b) || 0) + 1);
    }

    // Pass 2: compute tentative name (base, or base+variant on collision)
    const tentative = new Map();
    for (const rom of roms) {
      const b = baseOf(rom);
      let t;
      if (rom.type === 'directory') t = rom.name;
      else if (baseCounts.get(b) > 1 && rom.variant) t = `${b} (${rom.variant})`;
      else if (baseCounts.get(b) > 1) t = strippedFilename(rom);
      else t = b;
      tentative.set(rom.name, t);
    }

    // Pass 3: if a tentative name still collides (two different games
    // sharing both base and variant), fall back to raw filename for
    // every member of that collision group.
    const tentativeCounts = new Map();
    for (const t of tentative.values()) {
      tentativeCounts.set(t, (tentativeCounts.get(t) || 0) + 1);
    }
    const final = new Map();
    for (const rom of roms) {
      const t = tentative.get(rom.name);
      if (rom.type !== 'directory' && tentativeCounts.get(t) > 1) {
        final.set(rom.name, strippedFilename(rom));
      } else {
        final.set(rom.name, t);
      }
    }
    // A subset owns a playable file, but its database title describes an
    // achievement set. Give the file its own name after ordinary collisions
    // have settled, comparing without case so the base game stays canonical.
    const occupied = new Set(roms.filter(rom => !rom.subset_linked)
      .map(rom => final.get(rom.name)?.toLowerCase()));
    for (const rom of roms.filter(rom => rom.subset_linked)) {
      let name = titleFromFilename(rom.name, { isDirectory: rom.type === 'directory' }) || strippedFilename(rom);
      if (occupied.has(name.toLowerCase())) name = `${name} (${rom.variant || 'variant'})`;
      if (occupied.has(name.toLowerCase())) name = strippedFilename(rom);
      final.set(rom.name, name);
      occupied.add(name.toLowerCase());
    }
    return final;
  }

  /** Look up the already-resolved display name for a ROM filename on a
   *  given system. The cache is populated by _loadRomLibrary() every
   *  time the user picks a system — entries there already include
   *  collision-aware disambiguation (e.g. "Zelda (Japan)" when the
   *  US version also exists in the same list). Falls back to the
   *  extension-stripped filename when the cache is cold. WebRTC spectators
   *  can enter the gameplay shell before they have any local ROM filename;
   *  missing metadata therefore resolves to an empty title, never an error. */
  _romDisplayTitle(filename, systemId) {
    if (typeof filename !== 'string' || !filename) return '';
    const cache = this._romTitleCache?.[systemId];
    if (cache && cache[filename]) return cache[filename];
    return filename.replace(/\.[^.]+$/i, '');
  }

  _getFavorites() {
    try {
      return JSON.parse(localStorage.getItem('retroplay-favorites') || '[]');
    } catch { return []; }
  }

  _saveFavorites(favs) {
    localStorage.setItem('retroplay-favorites', JSON.stringify(favs));
  }

  // ── Recently Played ────────────────────────────────────

  _getRecentlyPlayed() {
    try { return JSON.parse(localStorage.getItem('retroplay-recent') || '[]'); }
    catch { return []; }
  }

  _saveRecentlyPlayed(list) {
    localStorage.setItem('retroplay-recent', JSON.stringify(list));
  }

  /** Record a server-library ROM as recently played. */
  _trackRecentlyPlayed(serverFilename) {
    const list = this._getRecentlyPlayed();
    const systemId = this.system.id;
    // Remove existing entry for this ROM+system
    const idx = list.findIndex(r => r.name === serverFilename && r.system === systemId);
    if (idx >= 0) list.splice(idx, 1);
    // Prepend
    list.unshift({ name: serverFilename, system: systemId });
    // Cap total to prevent unbounded growth
    if (list.length > 30) list.length = 30;
    this._saveRecentlyPlayed(list);
    this._loadRecentlyPlayedList();
  }

  async _loadRecentlyPlayedList() {
    const container = document.getElementById('recent-list');
    const card = document.getElementById('recent-card');
    if (!container || !card) return;

    let items = [];
    let crossSystem = false;
    if (authState.getCurrentUser()) {
      try {
        const res = await fetch('/api/me/recent-games?limit=5');
        if (res.ok) {
          const { games } = await res.json();
          items = games
            .slice(0, 5)
            .map(g => ({ name: g.filename, title: g.title, system: g.system_id }));
          crossSystem = true;
        }
      } catch { /* fall through to localStorage */ }
    }
    if (!items.length) {
      items = this._getRecentlyPlayed()
        .filter(r => r.system === this.system.id)
        .slice(0, 3);
    }

    container.innerHTML = '';
    if (!items.length) {
      card.classList.add('hidden');
      return;
    }
    card.classList.remove('hidden');

    for (const item of items) {
      const entry = document.createElement('div');
      entry.className = 'fav-entry';
      const itemName = item.name || item.title;
      if (itemName && this._activeRomFilename === itemName) entry.classList.add('selected');
      const displayName = item.title || this._romDisplayTitle(item.name, item.system || this.system.id);
      const sysLabel = crossSystem && item.system
        ? `<span class="fav-system">${this._escapeHtml(systemTag(item.system))}</span>`
        : '';
      entry.innerHTML = `<button type="button" class="fav-name" title="${this._escapeHtml(displayName)}">${this._escapeHtml(displayName)}</button>${sysLabel}`;
      if (itemName) {
        const itemSystem = item.system || this.system.id;
        entry.querySelector('.fav-name').addEventListener('click', () => {
          if (itemSystem !== this.system.id) this._selectSystem(itemSystem);
          this._selectFavorite(itemName, entry, itemSystem);
        });
      }
      container.appendChild(entry);
    }
  }

  _toggleFavorite(romName, btnEl) {
    const favs = this._getFavorites();
    const idx = favs.findIndex(f => f.name === romName && f.system === this.system.id);
    const hash = this._romFilenameToHash?.[this.system.id]?.[romName] || null;
    if (idx >= 0) {
      favs.splice(idx, 1);
      btnEl.classList.remove('active');
      btnEl.setAttribute('aria-pressed', 'false');
      this._syncFavoriteToServer(this.system.id, hash, false);
    } else {
      favs.push({ name: romName, system: this.system.id, hash });
      btnEl.classList.add('active');
      btnEl.setAttribute('aria-pressed', 'true');
      this._syncFavoriteToServer(this.system.id, hash, true);
    }
    this._saveFavorites(favs);
    this._favLastToggle = Date.now();
    this._loadFavoritesList();
  }

  async _loadFavoritesList() {
    const container = document.getElementById('favorites-list');
    const card = document.getElementById('favorites-card');
    if (!container || !card) return;

    let allFavs = [];
    const signedIn = !!authState.getCurrentUser();
    const skipFetch = this._favLastToggle && (Date.now() - this._favLastToggle < 2000);

    if (signedIn && !skipFetch) {
      try {
        const res = await fetch('/api/favorites', { credentials: 'include' });
        if (res.ok) {
          const { favorites } = await res.json();
          const hashToFilename = {};
          for (const [sys, fileMap] of Object.entries(this._romFilenameToHash || {})) {
            hashToFilename[sys] = hashToFilename[sys] || {};
            for (const [fn, h] of Object.entries(fileMap)) hashToFilename[sys][h] = fn;
          }
          const neededSystems = new Set(favorites.filter(f => !f.removed_at).map(f => f.system_id));
          for (const sys of neededSystems) {
            if (!this._romFilenameToHash?.[sys]) {
              await ensureRomTitleCache(this, sys);
              const fileMap = this._romFilenameToHash?.[sys] || {};
              hashToFilename[sys] = {};
              for (const [fn, h] of Object.entries(fileMap)) hashToFilename[sys][h] = fn;
            }
          }
          allFavs = favorites
            .filter(f => !f.removed_at)
            .map(f => ({
              name: hashToFilename[f.system_id]?.[f.rom_hash] || null,
              system: f.system_id,
              hash: f.rom_hash,
            }))
            .filter(f => f.name);
          this._saveFavorites(allFavs);
        }
      } catch { /* fall through to localStorage */ }
    }

    if (!allFavs.length) {
      allFavs = this._getFavorites();
      if (!signedIn) allFavs = allFavs.filter(f => f.system === this.system.id);
    }

    this._allFavorites = allFavs;
    this._renderFavoritesCard(allFavs);
  }

  _renderFavoritesCard(allFavs) {
    const container = document.getElementById('favorites-list');
    const card = document.getElementById('favorites-card');
    if (!container || !card) return;

    container.innerHTML = '';
    card.querySelector('.fav-filter-row')?.remove();

    if (!allFavs.length) {
      card.classList.add('hidden');
      return;
    }
    card.classList.remove('hidden');

    const signedIn = !!authState.getCurrentUser();
    const systems = [...new Set(allFavs.map(f => f.system))];

    if (signedIn && systems.length > 0) {
      const filterRow = document.createElement('div');
      filterRow.className = 'fav-filter-row top-games-tabs';
      const activeFilter = this._favSystemFilter || 'all';

      const allChip = document.createElement('button');
      allChip.type = 'button';
      allChip.className = `top-games-tab${activeFilter === 'all' ? ' active' : ''}`;
      allChip.textContent = 'ALL';
      allChip.addEventListener('click', () => {
        this._favSystemFilter = 'all';
        this._renderFavoritesCard(this._allFavorites);
      });
      filterRow.appendChild(allChip);

      for (const sys of systems) {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = `top-games-tab${activeFilter === sys ? ' active' : ''}`;
        chip.textContent = TAB_LABELS[sys] || sys.toUpperCase();
        chip.addEventListener('click', () => {
          this._favSystemFilter = sys;
          this._renderFavoritesCard(this._allFavorites);
        });
        filterRow.appendChild(chip);
      }

      card.insertBefore(filterRow, container);
    }

    const activeFilter = this._favSystemFilter || 'all';
    const filtered = (signedIn && activeFilter !== 'all')
      ? allFavs.filter(f => f.system === activeFilter)
      : allFavs;

    for (const fav of filtered) {
      const entry = document.createElement('div');
      entry.className = 'fav-entry';
      if (this._activeRomFilename === fav.name) entry.classList.add('selected');
      const displayName = this._romDisplayTitle(fav.name, fav.system);
      const sysLabel = signedIn
        ? `<span class="fav-system">${this._escapeHtml(systemTag(fav.system))}</span>`
        : '';
      entry.innerHTML =
        `<button type="button" class="fav-name" title="${this._escapeHtml(displayName)}">${this._escapeHtml(displayName)}</button>` +
        sysLabel +
        `<button class="save-action-btn delete" title="Remove" aria-label="Remove ${this._escapeHtml(displayName)} from favorites">✕</button>`;
      entry.querySelector('.fav-name').addEventListener('click', () => {
        if (fav.system !== this.system.id) this._selectSystem(fav.system);
        this._selectFavorite(fav.name, entry, fav.system);
      });
      entry.querySelector('.delete').addEventListener('click', (e) => {
        e.stopPropagation();
        this._removeFavorite(fav.name, fav.system);
      });
      container.appendChild(entry);
    }
  }

  async _selectFavorite(romName, el, systemId = null, {
    launchOwner: existingLaunchOwner = null,
    suppressPlayModePrompt = false,
  } = {}) {
    cancelPlayIntent('canceled');
    const launchOwner = existingLaunchOwner
      ?? this._beginGameLaunchIntent({ clearPendingSave: true });
    const launchIsCurrent = () => this._isGameLaunchOwnerCurrent(launchOwner);
    if (!launchIsCurrent()) return false;
    this._hideSysStatus();
    document.querySelectorAll('.rom-entry.selected').forEach(e => e.classList.remove('selected'));
    document.querySelectorAll('.fav-entry.selected').forEach(e => e.classList.remove('selected'));
    document.querySelectorAll('.myrom-entry.selected').forEach(e => e.classList.remove('selected'));
    el.classList.add('selected');
    this._flash('Loading ROM...');
    const sys = systemId || this.system.id;
    try {
      const res = await fetch(`/api/roms/${sys}/${encodeURIComponent(romName)}`);
      if (!launchIsCurrent()) return false;
      if (!res.ok) {
        if (res.status === 404) {
          return this._selectMultiDiskRom(romName, el, {
            launchOwner,
            suppressPlayModePrompt,
          });
        }
        throw new Error('Download failed');
      }
      const buf = await res.arrayBuffer();
      if (!launchIsCurrent()) return false;
      let data = new Uint8Array(buf);
      let name = romName;
      if (romName.toLowerCase().endsWith('.zip')) {
        const extracted = this._extractRomFromZip(data);
        if (!extracted) { this._flash('No ROM found in zip'); return false; }
        data = extracted.data;
        name = extracted.name;
      }
      let romStr = '';
      for (let i = 0; i < data.length; i++) romStr += String.fromCharCode(data[i]);
      if (!launchIsCurrent()) return false;
      this._activeRomFilename = romName;
      this._setRom(romStr, name, {
        preserveLaunchIntent: true,
        suppressPlayModePrompt,
      });
      this._trackRecentlyPlayed(romName);
      return true;
    } catch {
      if (!launchIsCurrent()) return false;
      this._flash('Failed to load ROM');
      el.classList.remove('selected');
      return false;
    }
  }

  _removeFavorite(romName, systemId = null) {
    const sys = systemId || this.system.id;
    const favs = this._getFavorites();
    const idx = favs.findIndex(f => f.name === romName && f.system === sys);
    const hash = idx >= 0 ? favs[idx].hash : (this._romFilenameToHash?.[sys]?.[romName] || null);
    if (idx >= 0) favs.splice(idx, 1);
    this._saveFavorites(favs);
    this._syncFavoriteToServer(sys, hash, false);
    this._favLastToggle = Date.now();
    this._loadFavoritesList();
    if (sys === this.system.id) {
      document.querySelectorAll('#rom-library .btn-fav.active').forEach(btn => {
        const entry = btn.closest('.rom-entry');
        const entryName = entry?.querySelector('.rom-entry-name')?.textContent;
        const displayName = romName.replace(/\.[^.]+$/i, '');
        if (entryName === displayName) btn.classList.remove('active');
      });
    }
  }

  // ── My ROMs (user-uploaded, server-persisted) ─────────

  async _uploadMyRom(data, romName) {
    if (!authState.getCurrentUser()) return;
    try {
      const res = await fetch(`/api/user-roms/${this.system.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-ROM-Name': romName,
        },
        body: data,
      });
      if (!res.ok) return;
      const result = await res.json();
      if (result.deduplicated) {
        this._flash('ROM already saved');
      } else {
        this._flash(`Saved: ${romName}`);
      }
      this._loadMyRomsList();
    } catch {
      // Upload failure shouldn't block local playback
    }
  }

  async _loadMyRomsList() {
    const card = document.getElementById('my-roms-card');
    const container = document.getElementById('my-roms-list');
    if (!card || !container) return;

    if (!authState.getCurrentUser()) {
      card.classList.add('hidden');
      return;
    }

    try {
      const res = await fetch(`/api/user-roms?system=${this.system.id}`);
      if (!res.ok) { card.classList.add('hidden'); return; }
      const { roms } = await res.json();

      container.innerHTML = '';
      if (!roms || !roms.length) {
        card.classList.add('hidden');
        return;
      }

      card.classList.remove('hidden');
      for (const rom of roms) {
        const entry = document.createElement('div');
        entry.className = 'myrom-entry';
        const sizeKB = Math.round(Number(rom.size_bytes) / 1024);
        // Prefer the matched catalog title (BYO metadata ingest) over the raw
        // upload filename; a ✓ marks a recognized game.
        const label = rom.game_title || rom.filename;
        const matchedMark = rom.matched ? '<span class="myrom-matched" title="Recognized game">✓</span> ' : '';
        entry.innerHTML =
          `<button type="button" class="myrom-name" title="${this._escapeHtml(label)}">${matchedMark}${this._escapeHtml(label)}</button>` +
          `<span class="myrom-size">${sizeKB}KB</span>` +
          `<button class="save-action-btn delete" title="Remove" aria-label="Remove ${this._escapeHtml(label)}">✕</button>`;

        entry.querySelector('.myrom-name').addEventListener('click', () => {
          this._selectMyRom(rom.system_id, rom.hash, entry);
        });
        entry.querySelector('.delete').addEventListener('click', (e) => {
          e.stopPropagation();
          this._removeMyRom(rom.system_id, rom.hash);
        });

        container.appendChild(entry);
      }
    } catch {
      card.classList.add('hidden');
    }
  }

  /**
   * Fetch one of the signed-in user's uploaded ROMs by content hash and boot
   * it. Claim rows 302-redirect to the bundled route server-side; fetch
   * follows transparently, the redirected response carries no X-ROM-Name, so
   * the filename is recovered from the final URL — and bundled twins are
   * frequently zips, so `.zip` payloads are unwrapped before booting.
   * Throws on failure after flashing a user-facing message.
   */
  async _loadMyRomByHash(system, hash, {
    launchOwner: existingLaunchOwner = null,
    suppressPlayModePrompt = false,
  } = {}) {
    // The optional fallback keeps this helper usable by its focused borrowed-
    // method tests; every real NESPlay instance owns the shared launch token.
    const launchOwner = existingLaunchOwner
      ?? this._beginGameLaunchIntent?.({ clearPendingSave: true })
      ?? null;
    const launchIsCurrent = () => launchOwner === null
      || this._isGameLaunchIntentCurrent?.(launchOwner) !== false;
    let flashed = false;
    try {
      const res = await fetch(`/api/user-roms/${system}/${encodeURIComponent(hash)}`);
      if (!launchIsCurrent()) return false;
      if (!res.ok) {
        flashed = true;
        this._flash(res.status === 401
          ? 'Sign in to play your uploaded ROMs'
          : 'Failed to load ROM');
        throw new Error(`user-rom fetch failed (${res.status})`);
      }
      let romName = res.headers.get('X-ROM-Name') || '';
      if (romName && res.headers.get('X-ROM-Name-Encoded') === '1') {
        try { romName = decodeURIComponent(romName); } catch { /* keep raw */ }
      }
      if (!romName) {
        try { romName = decodeURIComponent(new URL(res.url).pathname.split('/').pop() || ''); }
        catch { romName = ''; }
      }
      romName = romName || 'Unknown';
      const buf = await res.arrayBuffer();
      if (!launchIsCurrent()) return false;
      let data = new Uint8Array(buf);
      if (romName.toLowerCase().endsWith('.zip')) {
        const extracted = this._extractRomFromZip(data);
        if (!extracted) {
          flashed = true;
          this._flash('No ROM found in zip');
          throw new Error('no rom in zip');
        }
        data = extracted.data;
        romName = extracted.name;
      }
      let romStr = '';
      for (let i = 0; i < data.length; i++) romStr += String.fromCharCode(data[i]);
      if (!launchIsCurrent()) return false;
      this._activeRomFilename = null;
      this._setRom(romStr, romName, {
        preserveLaunchIntent: true,
        suppressPlayModePrompt,
      });
      return true;
    } catch (err) {
      // A rejected fetch from an obsolete selection is expected cancellation:
      // it must not flash over, reject into, or clear the newer selection.
      if (!launchIsCurrent()) return false;
      // Every throw path above already flashed a specific message; anything
      // else (dropped connection, timeout, a _setRom failure) falls through
      // to this generic notice so the caller never fails silently.
      if (!flashed) this._flash('Failed to load ROM');
      throw err;
    }
  }

  async _selectMyRom(system, hash, el, options = {}) {
    cancelPlayIntent('canceled');
    const launchOwner = options.launchOwner
      ?? this._beginGameLaunchIntent({ clearPendingSave: true });
    const launchIsCurrent = () => this._isGameLaunchOwnerCurrent(launchOwner);
    this._hideSysStatus();
    document.querySelectorAll('.rom-entry.selected').forEach(e => e.classList.remove('selected'));
    document.querySelectorAll('.fav-entry.selected').forEach(e => e.classList.remove('selected'));
    document.querySelectorAll('.myrom-entry.selected').forEach(e => e.classList.remove('selected'));
    el.classList.add('selected');
    this._flash('Loading ROM...');
    try {
      return await this._loadMyRomByHash(system, hash, {
        ...options,
        launchOwner,
      });
    } catch {
      if (launchIsCurrent()) el.classList.remove('selected');
      return false;
    }
  }

  async _removeMyRom(system, hash) {
    try {
      await fetch(`/api/user-roms/${system}/${encodeURIComponent(hash)}`, { method: 'DELETE' });
      this._loadMyRomsList();
    } catch {
      this._flash('Failed to remove ROM');
    }
  }

  async _selectLibraryRom(name, el, {
    launchOwner: existingLaunchOwner = null,
    suppressPlayModePrompt = false,
    selectionIsCurrent = null,
    title = null,
    onSelectionCommitted = null,
  } = {}) {
    cancelPlayIntent('canceled');
    const launchOwner = existingLaunchOwner
      ?? this._beginGameLaunchIntent({ clearPendingSave: true });
    const launchIsCurrent = () => this._isGameLaunchOwnerCurrent(launchOwner)
      && (selectionIsCurrent == null || selectionIsCurrent());
    if (!launchIsCurrent()) return false;
    this._hideSysStatus();
    if (this._multiDiskRoms?.[this.system.id]?.has(name)) {
      return this._selectMultiDiskRom(name, el, {
        launchOwner,
        suppressPlayModePrompt,
        selectionIsCurrent,
        title,
        onSelectionCommitted,
      });
    }
    if (el) {
      document.querySelectorAll('.rom-entry.selected').forEach(e => e.classList.remove('selected'));
      document.querySelectorAll('.fav-entry.selected').forEach(e => e.classList.remove('selected'));
      document.querySelectorAll('.myrom-entry.selected').forEach(e => e.classList.remove('selected'));
      el.classList.add('selected');
    }

    this._flash('Loading ROM...');
    try {
      const res = await fetch(`/api/roms/${this.system.id}/${encodeURIComponent(name)}`);
      if (!launchIsCurrent()) return false;
      // BYO-ROM mode: the bundled copy is withheld (401 anon / 403 byo_required).
      // Prompt the player to supply their own ROM, which plays locally and
      // uploads in the background (matching unlocks it for next time).
      if (res.status === 401 || res.status === 403) {
        if (el) el.classList.remove('selected');
        this._promptByoUpload(name);
        return false;
      }
      if (!res.ok) throw new Error('Download failed');
      const buf = await res.arrayBuffer();
      if (!launchIsCurrent()) return false;
      let data = new Uint8Array(buf);
      let romName = name;

      // Extract from zip if needed
      if (name.toLowerCase().endsWith('.zip')) {
        const extracted = this._extractRomFromZip(data);
        if (!extracted) {
          this._flash('No ROM found in zip');
          if (el) el.classList.remove('selected');
          return false;
        }
        data = extracted.data;
        romName = extracted.name;
      }

      let romStr = '';
      for (let i = 0; i < data.length; i++) romStr += String.fromCharCode(data[i]);
      if (!launchIsCurrent()) return false;
      const beforeSelection = roomLaunchSessionKey(this);
      this._activeRomFilename = name;
      this._setRom(romStr, romName, {
        preserveLaunchIntent: true,
        suppressPlayModePrompt,
        title,
      });
      if (onSelectionCommitted && onSelectionCommitted(beforeSelection, roomLaunchSessionKey(this)) !== true) return false;
      if (!launchIsCurrent()) return false;
      this._trackRecentlyPlayed(name);
      return true;
    } catch {
      if (!launchIsCurrent()) return false;
      this._flash('Failed to load ROM');
      if (el) el.classList.remove('selected');
      return false;
    }
  }

  /**
   * BYO-ROM mode withheld a bundled ROM (the `byo_rom_mode` master switch is on
   * and the player doesn't own this game). Ask them to provide their own copy:
   * a flash + the ROM file picker. The picked file plays locally via
   * `_loadROMFile` and uploads in the background, where the server matches it to
   * this game by content hash — unlocking it for next time. Triggering the
   * picker after an `await` may be blocked by some browsers' gesture policy; the
   * flash + the always-available 📁 ROM button are the fallback.
   */
  _promptByoUpload(romName) {
    const pretty = String(romName || '').replace(/\.[^.]+$/i, '');
    this._flash(pretty ? `Upload your copy of "${pretty}" to play` : 'Upload your ROM to play');
    const romInput = document.getElementById('rom-input');
    if (romInput) { try { romInput.click(); } catch { /* gesture-blocked; flash guides the user */ } }
  }

  async _selectMultiDiskRom(name, el, {
    launchOwner: existingLaunchOwner = null,
    suppressPlayModePrompt = false,
    selectionIsCurrent = null,
    title = null,
    onSelectionCommitted = null,
  } = {}) {
    const launchNamespace = currentSaveDBNamespace();
    cancelPlayIntent('canceled');
    const launchOwner = existingLaunchOwner
      ?? this._beginGameLaunchIntent({ clearPendingSave: true });
    const launchIsCurrent = () => this._isGameLaunchOwnerCurrent(launchOwner)
      && (selectionIsCurrent == null || selectionIsCurrent());
    if (!launchIsCurrent()) return false;
    document.querySelectorAll('.rom-entry.selected').forEach(e => e.classList.remove('selected'));
    document.querySelectorAll('.fav-entry.selected').forEach(e => e.classList.remove('selected'));
    if (el) el.classList.add('selected');

    this._flash('Loading game disks...');
    this._showSysStatus('Downloading disks...', 'warn');
    try {
      const res = await fetch(`/api/roms/${this.system.id}/${encodeURIComponent(name)}/disks`);
      if (!launchIsCurrent()) return false;
      if (!res.ok) throw new Error('Download failed');
      const headerHash = res.headers?.get?.(CATALOG_HASH_HEADER) ?? null;

      // Stream response to track download progress
      const contentLength = +res.headers.get('content-length');
      let receivedBytes = 0;
      const reader = res.body.getReader();
      const chunks = [];
      const startTime = performance.now();
      while (true) {
        const { done, value } = await reader.read();
        if (!launchIsCurrent()) {
          try { await reader.cancel(); } catch { /* ignore */ }
          return false;
        }
        if (done) break;
        chunks.push(value);
        receivedBytes += value.length;
        if (contentLength) {
          const pct = Math.round((receivedBytes / contentLength) * 100);
          const elapsed = (performance.now() - startTime) / 1000;
          const speed = elapsed > 0 ? Math.round(receivedBytes / 1024 / elapsed) : 0;
          this._showSysStatus(`${pct}% · ${speed} KB/s`, 'warn');
        }
      }
      // Reconstruct the full response text
      let offset = 0;
      const fullBuf = new Uint8Array(receivedBytes);
      for (const chunk of chunks) { fullBuf.set(chunk, offset); offset += chunk.length; }
      const bundle = JSON.parse(new TextDecoder().decode(fullBuf));
      if (!launchIsCurrent()) return false;
      // The verified catalog identity (spec 4.3). A C64IdentityError falls
      // into the catch below, which flashes "Failed to load game".
      const identity = await resolveC64LaunchIdentity(this, bundle, { headerHash, isCurrent: launchIsCurrent });
      if (!identity || !launchIsCurrent()) return false;
      this._hideSysStatus();
      const beforeSelection = roomLaunchSessionKey(this);
      this.romData = bundle;
      this._romDataB64 = btoa(JSON.stringify(bundle));
      this.romName = bundle.name;
      this._isMultiDisk = true;
      commitC64Identity(this, identity);
      if (onSelectionCommitted && onSelectionCommitted(beforeSelection, roomLaunchSessionKey(this)) !== true) return false;
      if (!launchIsCurrent()) return false;
      await finishC64LaunchIdentity(this, bundle, identity, { expectedNamespace: launchNamespace });
      if (!launchIsCurrent()) return false;
      // Same reset as _setRom, so a C64 load never shows the previous
      // game's title in the play-mode prompt.
      const knownTitle = this._resetCanonicalTitle(title);

      const el2 = document.getElementById('rom-name');
      el2.textContent = `${name} (${bundle.disks.length} disks)`;
      el2.classList.remove('hidden');
      this._activeRomFilename = name;
      this._flash(`Loaded: ${knownTitle || name} (${bundle.disks.length} disks)`);
      this._trackRecentlyPlayed(name);
      this._updateLobbyGameTitle();
      if (!suppressPlayModePrompt) this._maybePromptPlayMode(name);
      return true;
    } catch {
      if (!launchIsCurrent()) return false;
      this._flash('Failed to load game');
      if (el) el.classList.remove('selected');
      return false;
    }
  }

  // The room ROM codec and installer load on first use, outside the eager
  // bundle. The ROM is read before that await, as the upload began with it.
  async _uploadROM(roomCode, uploadId = null, { signal, hostCapability } = {}) {
    const romData = this.romData, romName = this.romName;
    if (!romData) return;
    const { encodeRoomRomPayload } = await import('./modules/room-rom-payload.js');
    const bytes = encodeRoomRomPayload(romData);
    return fetch(`/api/rooms/${roomCode}/rom?name=${encodeURIComponent(romName || 'Unknown.nes')}${uploadId ? `&uploadId=${encodeURIComponent(uploadId)}` : ''}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/octet-stream', ...(hostCapability ? { 'x-superuno-host': hostCapability } : {}) },
      body: bytes,
      ...(signal ? { signal } : {}),
    });
  }

  async _downloadROM(roomCode) {
    const res = await fetch(`/api/rooms/${roomCode}/rom`);
    if (!res.ok) return null;
    const headerName = res.headers.get('X-ROM-Name');
    const bytes = new Uint8Array(await res.arrayBuffer());
    const { decodeRoomRomPayload } = await import('./modules/room-rom-payload.js');
    return decodeRoomRomPayload(bytes, headerName);
  }

  /** Install a `_downloadROM` result; see modules/room-rom-install.js. */
  async _installRoomRom(rom, options) {
    const { installRoomRom } = await import('./modules/room-rom-install.js');
    return installRoomRom(this, rom, options);
  }

  // ── Save / Load ─────────────────────────────────────────
  //
  // Auto-save timer, saves list (menu screen), in-place save load/apply, and
  // save-state export/delete now live in modules/save-manager.js. Two
  // exceptions stayed here (see the module's header comment for the
  // caller-evidence split): `_fastForwardLoad` (only called from within
  // `_startSolo`) and `_resolveSaveRom` (also called from
  // `_tryResumeSoloSession`, above) — both are boot/session-startup logic
  // that reaches beyond the saves cluster. `_uploadSaveState` /
  // `_downloadSaveState` (below) likewise stayed: their callers are
  // room-join/resume boot flows, not this feature.

  _isReplayDerivedSaveSession() {
    return saveManager.isReplayDerivedSaveSession(this);
  }

  _generateReplayDerivedSave() {
    return saveManager.generateReplayDerivedSave(this);
  }

  /**
   * Resolve a pending save for a JS-stepped core. GX/legacy Atari recording
   * sessions reconstruct from RPL1 because unserializing them while stepped
   * deadlocks. ClownMDEmu is explicitly different: its stepped core supports
   * direct state restore, so a state-only Clown save is complete and portable.
   *
   * @returns {{ replayLog: string, frame: number } | null} deferred RPL1 load
   */
  _restoreSteppedPendingSave(pendingSave, sessionEmu = this.emu) {
    if (pendingSave.replayLog) {
      return { replayLog: pendingSave.replayLog, frame: pendingSave.frame || 0 };
    }

    const replayDerivedCore = this.system?.id === 'atari2600'
      || (this.system?.id === 'genesis' && sessionEmu?.coreName !== 'clownmdemu');
    if (replayDerivedCore) {
      // The RPL1 is the only load artifact for these stepped cores. Never feed
      // an empty/legacy state to loadState: GX/Atari can deadlock here.
      console.warn('replay-derived save missing input log: cannot restore');
      this._flash('Save data is incomplete. Starting fresh');
      this._saveRef = null;
      return null;
    }

    try {
      const restored = sessionEmu.loadState(pendingSave.emulatorState);
      if (restored === false) {
        this._flash('Could not restore save. Starting fresh');
        this._saveRef = null;
      } else {
        this.soloFrame = pendingSave.frame || 0;
        this._onEmulatorStateLoaded();
      }
    } catch (e) {
      console.warn('Failed to restore save state:', e);
      this._flash('Could not restore save. Starting fresh');
      this._saveRef = null;
    }
    return null;
  }

  /**
   * Reconstruct a replay-derived save's state by REPLAYING its stored input log
   * (RPL1) through the live lockstep core — the browser can't unserialize these
   * cores under lockstep without deadlocking. Each frame is also observed into the
   * (already-started) recorder so the rebuilt session holds the full 0..N log and a
   * later save covers the whole run. Chunked with rAF yields + a progress flash so a
   * long log doesn't freeze the tab. Runs after _initReplayRecorder, before _soloLoop.
   * @param {{ replayLog: string, frame: number }} ff
   */
  async _fastForwardLoad(ff) {
    this._fastForwardActive = true;
    try {
      const { decodeRpl1, expandFrames } = await import('../shared/replay-format.js');
      const decoded = decodeRpl1(base64ToU8(ff.replayLog));
      const total = decoded.frameCount || ff.frame || 0;
      let f = 0;
      for (const { p1, p2 } of expandFrames(decoded)) {
        if (this.replayRecorder?.isRecording
            && this.replayRecorder.observe(f, p1, p2) === false) {
          this._replayRecordingUnavailable('replay_capacity_exceeded');
        }
        this.emu.setButtons(0, p1);
        this.emu.setButtons(1, p2);
        this.emu.stepFrame();
        f++;
        if ((f & 0x7FF) === 0) { // every 2048 frames: progress + yield to the UI
          this._flash(`Restoring… ${Math.round((f / Math.max(1, total)) * 100)}%`, 0);
          await new Promise((r) => requestAnimationFrame(r));
          if (!this.running || this.state !== State.SOLO || !this.emu?.loaded) return; // session ended mid-restore
        }
      }
      this.soloFrame = f;
      this._flash('Save restored', 1200);
    } catch (e) {
      console.warn('fast-forward load failed', e);
      this._flash('Could not restore save. Starting fresh');
      this._saveRef = null;
    } finally {
      this._fastForwardActive = false;
    }
  }

  async _saveGame() {
    if (this._inLockstepSession() && this._replayBoundaryOwner) {
      this._flash('Save is busy. Try again.');
      return;
    }
    const manualSave = await manualSaveModulePromise;
    if (!manualSave) return this._flash('Save unavailable. Reload RetroPlay and try again');
    return manualSave.saveGame(this);
  }

  _startAutoSaveTimer() {
    return saveManager.startAutoSaveTimer(this);
  }

  /**
   * Start passive memory sampling for the active solo session. The monitor
   * reads `this.emu` and `this.clipRecorder` lazily on each sample, so it
   * stays accurate even when the clip recorder is lazily loaded later.
   *
   * Phase 1: instrumentation only — snapshot consumed by the breadcrumb
   * and by `_serverSessionHeartbeat`.
   * Phase 2: `onStateChange` fires when the monitor crosses a threshold.
   * We POST a `kind: 'memory_pressure'` event to `/api/client-errors` so
   * the existing rate limiter + admin pipeline absorb it. No mitigation
   * runs here — Phase 3 reads `monitor.getState()` to react.
   */
  async _startMemoryMonitor() {
    if (this.memoryMonitor) this.memoryMonitor.stop();
    if (!this._memoryMonitorModule) {
      this._memoryMonitorModule = await import('./memory-monitor.js');
    }
    // Bail if the session ended before the chunk loaded.
    if (!this.running) return;
    this.memoryMonitor = new this._memoryMonitorModule.MemoryMonitor({
      getEmu: () => this.emu,
      getClipRecorder: () => this.clipRecorder,
      systemId: this.system?.id || null,
      romHash: Array.isArray(this.romHash) ? this.romHash[0] : this.romHash,
      onStateChange: (prev, next, snap) => this._onMemoryStateChange(prev, next, snap),
    });
    this.memoryMonitor.start();
  }

  _stopMemoryMonitor() {
    if (this.memoryMonitor) {
      this.memoryMonitor.stop();
      this.memoryMonitor = null;
    }
  }

  /**
   * Forward a memory-pressure state transition to the server. Phase 3
   * mitigation reads `this.memoryMonitor.getState()` separately; this
   * hook is only the telemetry leg. Filtering keeps healthy↔watch churn
   * out of the event stream — it's GC noise, not signal.
   */
  _onMemoryStateChange(prev, next, snap) {
    if (next !== 'pressure' && next !== 'critical'
        && prev !== 'pressure' && prev !== 'critical') return;
    // Lazy module reuses the dynamic-import chunk loaded by
    // _startMemoryMonitor — no extra network round-trip.
    this._memoryMonitorModule?.reportMemoryPressure?.(
      prev, next, snap, { rom_name: this.romName || null }
    );
    // Phase 3 — flag-gated stepped mitigation. Ratchet the clip
    // recorder down before the tab dies.
    if (moduleRegistry.isEnabled('memory_pressure_mitigation')) {
      this._mitigateMemoryPressure(next, snap);
    }
  }

  /**
   * Apply the next step of the clip-recorder quality ladder when memory
   * pressure crosses a threshold. The ladder is monotonic — we only ever
   * raise the level, never lower it within a session. See
   * `ClipRecorder.setQualityLevel` for the bitrate/fps ladder.
   *
   *   pressure → step up by 1 (caller sustains 60s before this fires)
   *   critical → jump to Level 3 (off)
   *
   * Also stops the solo-screenshot timer at Level 2+, since it's another
   * canvas.toBlob source that competes for the same GPU+encode pipeline.
   */
  _mitigateMemoryPressure(state, snap) {
    if (!this.clipRecorder) return;
    const current = this.clipRecorder.getQualityLevel();
    let target = current;
    if (state === 'critical') target = 3;
    else if (state === 'pressure') target = Math.min(3, current + 1);
    if (target <= current) return;

    const changed = this.clipRecorder.setQualityLevel(target);
    if (!changed) return;

    if (target >= 2) this._stopRoomScreenshots();

    const msg =
      target === 1 ? '📹 Clip quality reduced to keep your tab healthy.' :
      target === 2 ? '📹 Clip resolution reduced. Tab is using more memory than expected.' :
                     '📹 Clip recording paused to prevent a crash.';
    try { this._flash(msg); } catch {}

    // Telemetry — mitigation level is the new column the server reads.
    // clip recorder is now in `mitigated_*` state per the snapshot's
    // next read; recompute the snapshot to capture the post-step state.
    this._memoryMonitorModule?.reportMemoryPressure?.(
      state, state, this.memoryMonitor?.getSnapshot?.() || snap,
      { rom_name: this.romName || null, mitigation_level: target }
    );
  }

  async _autoSave(opts = {}) {
    // An author session keeps the admin's own Auto Save. The profile, not authorSessionActive: Exit sets the menu state first.
    if (this._authorProfile) return false;
    return saveManager.autoSave(this, opts);
  }

  async _autoSaveInner(opts = {}) {
    return saveManager.autoSaveInner(this, opts);
  }

  async _loadSavesList() {
    return saveManager.loadSavesList(this);
  }

  _renderSaveEntries(saves, container) {
    return saveManager.renderSaveEntries(this, saves, container);
  }

  _getServerSavesForDisplay() {
    return saveManager.getServerSavesForDisplay(this);
  }

  async _loadSaveFromDB(id) {
    const storageOwnerId = authState.getCurrentUser()?.id || null;
    const target = { pendingLocal: true, localId: id, storageOwnerId, storageNamespace: currentSaveDBNamespace() };
    // Ownership: any newer launch (another save, a library pick, a room) replaces
    // or clears _nextSaveLaunchRef, so a slower, older request stops below.
    this._nextSaveLaunchRef = target;
    const owns = () => this._nextSaveLaunchRef === target;
    const scopeHolds = () => (authState.getCurrentUser()?.id || null) === storageOwnerId
      && currentSaveDBNamespace() === target.storageNamespace;
    // The saves list that offers this id renders only after this account's
    // database is open, so a request with no open database is stale. It fails
    // here instead of opening one: an open started for an account that is no
    // longer current could finish after a newer account took the shared handle.
    if (!target.storageNamespace) {
      this._nextSaveLaunchRef = null;
      return this._flash('Failed to load save');
    }
    let stored;
    try {
      stored = await getSave(id);
    } catch {
      // A failed probe fails the load; it never falls through to another loader.
      if (owns()) { this._nextSaveLaunchRef = null; this._flash('Failed to load save'); }
      return;
    }
    if (!owns() || !scopeHolds()) return;
    if (!stored) { this._nextSaveLaunchRef = null; return this._flash('Invalid save'); }
    // Route from the confirmed row. The legacy loader (save-manager.js, byte-
    // pinned) reads the id again; if that second read ever returned a C64
    // bundle, _setRom refuses it (below), so the bundle is never consumed by
    // the single-ROM path and the load fails as 'Failed to load save'.
    if (stored.system === 'c64' && stored.isMultiDisk) {
      return this._loadC64SaveFromDB(id, { storageOwnerId, storageNamespace: target.storageNamespace });
    }
    try {
      return await saveManager.loadSaveFromDB(this, id);
    } finally {
      if (this._nextSaveLaunchRef === target) this._nextSaveLaunchRef = null;
    }
  }

  /** Local C64 multi-disk saves: a dedicated loader (save-manager's local
   * loader treats the embedded bundle as a single ROM). Mirrors
   * _loadSaveFromServer's launch-candidate ownership; storageScope was
   * captured when the request started. */
  async _loadC64SaveFromDB(id, storageScope) {
    const launchOwner = this._beginGameLaunchCandidate({ clearPendingSave: true });
    try {
      if ((authState.getCurrentUser()?.id || null) !== storageScope.storageOwnerId
          || currentSaveDBNamespace() !== storageScope.storageNamespace) return;
      const { loadC64LocalSave } = await import('./modules/save-overwrite.js');
      if (!this._isGameLaunchCandidateCurrent(launchOwner)) return;
      return await loadC64LocalSave(this, id, launchOwner, storageScope, {
        coreNameForSave: saveManager.coreNameForSave, soloModeForSave: saveManager.soloModeForSave,
      });
    } finally {
      this._finishGameLaunchCandidate(launchOwner);
    }
  }

  async _loadSaveFromServer({ system, romHash, kind, slot }) {
    const storageOwnerId = authState.getCurrentUser()?.id || null;
    const launchOwner = this._beginGameLaunchCandidate({ clearPendingSave: true });
    try {
      await initSaveDB(storageOwnerId);
      if (!this._isGameLaunchCandidateCurrent(launchOwner)) return;
      if ((authState.getCurrentUser()?.id || null) !== storageOwnerId) return;
      const storageScope = {
        storageOwnerId,
        storageNamespace: currentSaveDBNamespace(),
      };
      const { loadTrackedServerSave } = await import('./modules/save-overwrite.js');
      if (!this._isGameLaunchCandidateCurrent(launchOwner)) return;
      return await loadTrackedServerSave(
        this,
        { system, romHash, kind, slot },
        launchOwner,
        storageScope,
        {
          coreNameForSave: saveManager.coreNameForSave,
          soloModeForSave: saveManager.soloModeForSave,
        },
      );
    } finally {
      this._finishGameLaunchCandidate(launchOwner);
    }
  }

  async _deleteServerSave({ system, romHash, kind, slot }) {
    return saveManager.deleteServerSave(this, { system, romHash, kind, slot });
  }

  async _deleteSaveEverywhere({ system, romHash, kind, slot }) {
    return saveManager.deleteSaveEverywhere(this, { system, romHash, kind, slot });
  }

  /**
   * Apply a save's emulator state to the running solo session. Same-core saves
   * load in place; a Genesis save from the other emulator reboots through its
   * saved core before unserializing. The caller guarantees the save belongs to
   * the current ROM; multi-disk/BASIC/stale-hash saves use the reboot loaders.
   * See save-manager.js for the full behavior doc.
   * @returns {Promise<boolean>} true when the state was applied or a core-safe
   * reboot was started.
   */
  async _applySaveInPlace(save, saveTarget = null) {
    if (this._gameplayHeld()) { this._flash('Save is busy. Try again.'); return false; }
    const currentOwnerId = authState.getCurrentUser()?.id || null;
    const target = saveTarget?.storageOwnerId === currentOwnerId
      && saveTarget?.storageNamespace === currentSaveDBNamespace() ? saveTarget : null;
    const previousTarget = this._saveRef;
    // Once an eligible in-place load is accepted, the old replay log must not
    // be finalized against the state the core is about to restore. Cancel the
    // milestone wait before either a synchronous load or an async state-pump
    // load can mutate emulator memory. The module repeats these eligibility
    // guards before doing the actual work.
    if (this.state === State.SOLO && this.emu?.loaded
        && !this._replayPlaybackActive && !this._ghostRaceActive
        && !this._applySaveInFlight) {
      this._cancelPendingReplayPostRoll();
    }
    // Achievement authoring (spec 8.3): the author session samples nothing while the state
    // changes, and a successful load starts a new segment (authoring-host.js).
    const stateChange = authoringHost.beginAuthorStateChange(this, 'apply_save');
    let applied = false;
    try {
      applied = await saveManager.applySaveInPlace(this, save);
    } finally {
      stateChange.end(applied);
    }
    if (applied && target && this._saveRef === previousTarget) this._saveRef = target;
    if (target && (target.storageOwnerId !== (authState.getCurrentUser()?.id || null)
        || target.storageNamespace !== currentSaveDBNamespace())
        && this._saveRef === target) this._saveRef = null;
    return applied;
  }

  _savesShortcutAllowed() {
    // K7, T3: in room mode backtick also follows the Single-key shortcuts switch and opens Load
    // only while Load is available (round 2, n14: the button exists, is displayed and is enabled).
    // save-manager.js is pinned (S7), so the check lives outside it.
    return saveManager.savesShortcutAllowed(this) && singleKeyShortcutAllowed(this)
      && roomLoadShortcutAvailable(this);
  }

  async _exportSaveFile(id, name) {
    return saveManager.exportSaveFile(this, id, name);
  }

  async _exportServerSave({ server, name }) {
    return saveManager.exportServerSave(this, { server, name });
  }

  _downloadDiskImage(save) {
    return saveManager.downloadDiskImage(this, save);
  }

  async _deleteSaveEntry(id) {
    return saveManager.deleteSaveEntry(this, id);
  }

  _loadSaveFile(file) {
    return saveManager.loadSaveFile(this, file);
  }

  _parseSaveFile(file) {
    return saveManager.parseSaveFile(this, file);
  }

  async _uploadSaveState(roomCode, save) {
    const reservation = this._handoffSaves?.get(save);
    let overlap = 0;
    const controller = reservation ? new AbortController() : null;
    try {
      if (reservation) {
        if (!reservation.retained) throw new Error('handoff cancelled');
        const state = save.emulatorState;
        const frame = lockstepStart.validateSaveFrame(save.frame);
        if (!frame.ok) throw new Error('invalid handoff frame');
        if (state instanceof Uint8Array) {
          const n = state.length, b = 4 * Math.ceil(n / 3), j = b + 1024;
          overlap = 4 * n + 2 * b + 4 * j;
        } else {
          const j = jsonUtf8SizeBounded({ emulatorState: state, frame: frame.frame }, reservation.limit + 1024);
          if (j == null) throw new Error('invalid handoff JSON');
          overlap = 4 * j;
        }
        this._chargeHandoff(reservation, overlap);
        reservation.operations++;
        reservation.controllers.add(controller);
      }
      const res = await fetch(`/api/rooms/${roomCode}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(encodeEmulatorState({ emulatorState: save.emulatorState, frame: save.frame })),
        ...(controller ? { signal: controller.signal } : {}),
      });
      if (!res.ok) throw new Error(`Room save upload failed (${res.status})`);
    } finally {
      if (reservation?.controllers.delete(controller)) {
        reservation.operations--;
        reservation.bytes -= overlap;
        this._settleHandoff(reservation);
      }
    }
  }

  async _downloadSaveState(roomCode, ownsLaunch = null) {
    // A request owns its outcome across awaits, including retries for one room.
    // Resume has not connected yet; its caller supplies that launch authority.
    const net = this.net, launch = this._gameLaunchIntentGeneration;
    const assigned = net?.roomCode === roomCode, joinOwner = this._roomJoinLaunchOwner;
    const current = () => this.net === net && this._gameLaunchIntentGeneration === launch
      && (!ownsLaunch || ownsLaunch())
      && (!assigned || (net.roomCode === roomCode && this._roomJoinLaunchOwner === joinOwner));
    const admitted = current() && (ownsLaunch || !net?.roomCode || assigned)
      && (ownsLaunch || joinOwner == null || launch == null || joinOwner === launch);
    const request = { roomCode, current };
    if (admitted) { this._roomSaveRequest = request; this._roomSaveRefusal = null; }
    const publish = error => {
      if (admitted && current() && this._roomSaveRequest === request)
        this._roomSaveRefusal = { roomCode, error, request };
    };
    let res;
    try { res = await fetch(`/api/rooms/${roomCode}/save`); } catch (error) {
      publish(error); throw error;
    }
    if (res.status === 404) return null;
    if (!res.ok) {
      publish(new Error(`room save download failed (${res.status})`));
      return null;
    }
    try { return decodeEmulatorState(await res.json()); } catch (error) {
      publish(error); throw error;
    }
  }

  // ── Chat ────────────────────────────────────────────────
  // Moved to modules/room-chat.js — see that file's header comment for what
  // stayed here and why (borderline cases: `_toggleMic`, the `net.onChat*`
  // family, `_voiceConnection`).

  _isMobileChat() {
    return roomChat.isMobileChat(this);
  }

  _reconcileChatViewport() {
    return roomChat.reconcileChatViewport(this);
  }

  _openChatDrawer() {
    return roomChat.openChatDrawer(this);
  }

  _closeChatDrawer(opts) {
    return roomChat.closeChatDrawer(this, opts);
  }

  _toggleChatSidebar() {
    return roomChat.toggleChatSidebar(this);
  }

  /**
   * A new solo room forces the classic chat column open on tablet/desktop so
   * the presence list (with spectator invite buttons) is visible at once. The
   * game room keeps chat in its Chat tab and selects tabs by TB1 (CH3).
   */
  _openSoloRoomChatColumn() {
    if (isRoomMode(this) || this._isMobileChat()) return;
    this._desktopChatSidebarPreferredOpen = true;
    this._chatSidebarOpen = true;
    this._reconcileChatViewport();
  }

  /**
   * Suspend BOTH keyboard input paths so typing in an on-screen overlay
   * (chat, feedback, game-change filter) reaches the text field instead of
   * the emulator. Path 1: the JS InputHandler (gated by `input.enabled`).
   * Path 2: RetroArch's captured keydown/keyup handlers, which listen at the
   * document level and consume keymapped keys (WASD etc.) regardless of canvas
   * focus — only `suspendKeyboard()` stops them. `suspendKeyboard` is absent on
   * jsnes/NES (optional-chained to a no-op) and idempotent on libretro cores.
   */
  /**
   * Open the shared suggest-a-game panel for the currently loaded ROM.
   * Prefers _activeRomFilename (the server-side filename the launch deep
   * link needs) over this.romName (the post-unzip inner name). The panel has
   * a note input, so both keyboard paths are suspended while it's open.
   */
  _openSuggestGamePanel() {
    return this._openGameSharePanel(null, { suggestOnly: true });
  }

  _openGameSharePanel(context = null, options = {}) {
    const launchOwner = this._gameLaunchIntentGeneration;
    const sessionOwner = this._gameLaunchGeneration;
    return import('./modules/game-sharing.js').then(({ openGameSharing, closeGameSharing }) => {
      if (launchOwner !== this._gameLaunchIntentGeneration || sessionOwner !== this._gameLaunchGeneration) return;
      this._closeShare = () => closeGameSharing(this);
      openGameSharing(this, context, options);
    }).catch(() => this._flash('Sharing unavailable. Try again.'));
  }

  /** Launch-dark gate for suggest-game UI — strict true, not opt-out. */
  _suggestEnabled() {
    return moduleRegistry.getFeatures().game_suggestions === true;
  }

  /**
   * "Schedule a Match" from the play-mode modal. Lazy-imported: schedule-match
   * pulls in the full schedule sheet (invite-sheet.js + its game-catalog
   * picker), otherwise a home-panel-only lazy chunk. A static import would drag
   * all of it into the eager main.js bundle and blow the size budget, so the
   * whole flow (game resolution + suspend/resume wiring) lives in that module's
   * openScheduleMatchForApp and we only load it on demand.
   */
  _openScheduleMatchPanel(loadSchedule = () => import('./schedule-match.js')) {
    const owner = this._gameLaunchIntentGeneration || 0;
    const isCurrent = () => this._isGameLaunchOwnerCurrent(owner);
    return loadSchedule().then((m) => isCurrent()
      ? m.openScheduleMatchForApp(this, { isCurrent }) : false);
  }

  _inputLeases() {
    return this._gameplayInputLeases ||= new GameplayInputLeases({
      suspend: () => this._suspendEmulatorInput(), resume: () => this._resumeEmulatorInput(),
    });
  }

  acquireGameplayInput(owner) { return this._inputLeases().acquire(owner); }
  releaseGameplayInput(owner) { return this._inputLeases().release(owner); }

  _queueGameplayManagerTransition(manager, action) {
    const previous = this._gameplayManagerTransition;
    const transition = Promise.resolve(previous).then(async () => {
      if (manager !== this._inputManager) return;
      if (action === 'resume' && this._gameplayInputLeases?.active) return;
      if (action === 'suspend' && !this._gameplayInputLeases?.active) return;
      let timer;
      const settled = Promise.resolve().then(() => manager[action]?.())
        .then(() => 'settled', () => 'failed');
      const outcome = await Promise.race([settled, new Promise(resolve => {
        timer = setTimeout(() => resolve('timeout'), 2000);
      })]);
      clearTimeout(timer);
      if (outcome === 'timeout') {
        // A manager operation may settle after the next owner has changed the
        // desired state. Reconcile then, without holding this transition queue.
        void settled.then(result => {
          if (result === 'settled' && manager === this._inputManager) {
            this._queueGameplayManagerTransition(manager,
              this._gameplayInputLeases?.active ? 'suspend' : 'resume');
          }
        });
      }
    });
    this._gameplayManagerTransition = transition.catch(() => {});
  }

  // Low-level pair: only GameplayInputLeases calls these after owner conversion.
  _suspendEmulatorInput() {
    // N64 virtual axes read the physical pad directly. Require a neutral
    // sample after each overlay before publishing held axes again.
    this._n64AnalogAwaitNeutral = true;
    if (this.input) this.input.enabled = false;
    // Release any key already held down: _onKeyUp (input.js) returns early
    // while disabled, so the eventual keyup for an already-held key would
    // never clear it, and poll() reads keyState regardless of `enabled` — a
    // held direction or button would otherwise keep driving the game for as
    // long as this suspend lasts. Reuses the same release InputHandler
    // already runs on a lost window focus (input.js _onBlur) instead of
    // duplicating it here.
    this.input?.setGameplaySuspended?.(true);
    this.emu?.suspendKeyboard?.();
    this.emu?.setGameplayInputSuspended?.(true);
    this.virtualPads?.setSuspended?.(true);
    if (this._inputManager && this._gameplayInputManager !== this._inputManager) {
      const manager = this._inputManager;
      this._gameplayInputManager = manager;
      this._queueGameplayManagerTransition(manager, 'suspend');
    }
  }

  /** Re-enable both keyboard input paths after an overlay closes. */
  _resumeEmulatorInput() {
    const finish = () => {
      if (this._gameplayInputLeases?.active) return;
      this.input?.setGameplaySuspended?.(false);
      if (this.input) this.input.enabled = true;
      this.emu?.setGameplayInputSuspended?.(false);
      this.virtualPads?.setSuspended?.(false);
      if (this.input?._useManager?.() === true && this.system?.id !== 'c64') this.emu?.suspendKeyboard?.();
      else this.emu?.resumeKeyboard?.();
    };
    finish();
    if (this._inputManager) {
      const manager = this._inputManager;
      this._gameplayInputManager = null;
      this._queueGameplayManagerTransition(manager, 'resume');
    }
  }

  // ── Controller Profiles (task 3.11): flag-gated InputManager wiring ────────
  // Inert unless `controller_profiles` is ON: the manager is constructed ONLY
  // when the flag is true, so every wiring point guards on `this._inputManager`
  // (null flag-OFF ⇒ the InputHandler shim runs the byte-identical legacy path).
  // The manager's SESSION lifecycle (init/dispose/setSystem + startPolling +
  // markGameplayActive) is driven by the shim from main.js's existing
  // input.start()/stop()/configure() calls; the blur/visibility/freeze/gamepad
  // wake set is owned by the manager's installWakeListeners(). This block adds
  // only the ambient wiring the shim cannot: constructing + handing over the
  // manager, the neutral-publish + disposal seams, the gap-poll loop starter,
  // and the account-lifecycle adapter. See cp-sdd/task-3.11-report.md.

  /**
   * Construct the real InputManager. Lazily imported so its dependency subtree
   * stays OUT of the eager main.js bundle when the flag is OFF (the same
   * bundle-splitting discipline the rest of main.js follows). Its config-load
   * deps (account / features / controller store / cache / api) resolve to the
   * real modules via the manager's own lazy defaults, and init() wires the real
   * per-device resolveConfig — nothing to inject here. Overridable in tests.
   */
  async _createInputManager() {
    const { InputManager } = await import('./input-manager.js');
    return new InputManager();
  }

  /**
   * One-time, flag-gated setup run after feature flags load. Constructs the
   * InputManager, registers the neutral-publish + gameplay-disposal seams,
   * installs the wake listeners, primes (but does not run) the gap-poll loop,
   * then hands the manager to the InputHandler shim so the flag-ON delegation
   * path activates. Idempotent + fail-safe: any failure leaves `_inputManager`
   * null and the legacy input path fully intact.
   */
  async _loadControllerRuntime() {
    if (!this._controllerRuntime) this._controllerRuntime = await import('./modules/controller-runtime.js');
    return this._controllerRuntime;
  }

  async _setupControllerProfiles(isCurrent) {
    if (!isCurrent) {
      while (this._controllerAuthorityTransition) await this._controllerAuthorityTransition;
      isCurrent = this._controllerAuthorityTransitionGeneration;
    }
    if (moduleRegistry.getFeatures().controller_profiles !== true) return;
    if (this._inputManager) return this._inputManager;
    const runtime = await this._loadControllerRuntime();
    return runtime.setupControllerProfiles(this, () => this._createInputManager(), isCurrent);
  }

  _runControllerTransition(method, ...args) {
    if (this._controllerRuntime) return this._controllerRuntime[method](this, ...args);
    const generation = ++this._controllerAuthorityTransitionGeneration;
    return this._controllerAuthorityTransition = this._loadControllerRuntime()
      .then((runtime) => runtime[method](this, ...args, generation))
      .catch(() => {
        if (this._controllerAuthorityTransitionGeneration === generation) {
          this._controllerAuthorityTransition = null;
        }
        return false;
      });
  }

  /**
   * Account/identity-change adapter (§2.14 lifecycle): fence any in-flight
   * init/switch (superseded) + abort the profile fetch, and — if a session is
   * live — dispose it (the manager owns the teardown). No-op flag-OFF.
   */
  _notifyControllerAccountChange() {
    const enabled = moduleRegistry.getFeatures().controller_profiles === true;
    if (!this._controllerRuntime && !enabled) return;
    return this._runControllerTransition('beginControllerAuthorityTransition', { account: true }, enabled);
  }

  /** Track actual feature-flag edges in both directions. Repeated flag loads
   * only refresh the active route; OFF→ON and ON→OFF change authority. */
  _notifyControllerFlagRefresh() {
    const enabled = moduleRegistry.getFeatures().controller_profiles === true;
    return this._runControllerTransition('refreshControllerFlag', enabled);
  }

  _controllerWorkflowUser() {
    return authState.isAuthenticated() ? authState.getCurrentUser() : null;
  }

  _runControllerUi(method, ...args) {
    if (moduleRegistry.getFeatures().controller_profiles !== true) return;
    return import('./modules/controller-runtime-ui.js').then((runtime) => runtime[method](this, ...args));
  }

  /** Await the controller readiness/generation gate before any game loop starts. */
  async _startInputForGameplay() {
    while (this._controllerAuthorityTransition) await this._controllerAuthorityTransition;
    this._gameplayInputLeases?.attach();
    const managerMode = this.input?._useManager?.() === true;
    if (managerMode && this.system?.id !== 'c64') this.emu?.suspendKeyboard?.();
    const readyPromise = this.input.start();
    let started;
    if (!managerMode) {
      await readyPromise;
      started = true;
    } else {
      const runtime = await this._loadControllerRuntime();
      started = await runtime.startInputForGameplay(this, { initialSuspendDone: true, readyPromise });
    }
    // A dialog that suspends gameplay input at open time (What's New,
    // news-modal.js) may have had nothing to suspend if no game was running
    // yet. Tell it a game just started so it can take the suspend now
    // instead of leaving this freshly started session's input reaching a
    // game the dialog still covers.
    if (started) window.dispatchEvent(new CustomEvent('retroplay:gameplay-input-started'));
    if (started) this._gameplayInputLeases?.attach();
    return started;
  }

  /**
   * Whether solo libretro should let RetroArch poll the physical pad natively
   * (the `soloOpts.nativeGamepad` lever — see `_createEmulator`). GB is the only
   * system that ever requests it; GBA already injects through pollGamepadStable()
   * in `_libretroInputLoop` (it never sets nativeGamepadInput).
   *
   * Controller Profiles (spec §1 "GB/GBA gamepad path", line 186) requires ALL
   * GB/GBA gamepad input to route through InputManager UNCONDITIONALLY so
   * deadzones/release-timing/profiles apply, with the RetroArch native polling
   * path DISABLED. So when a manager is wired (flag ON) this returns false:
   * `LibretroCore.nativeGamepadInput` then stays false, which both makes
   * `_libretroInputLoop` read `this.input.pollGamepadStable()` (→ shim →
   * getPlayer(0)) instead of the bypass's `0`, AND makes RetroArch drop its
   * native pad binds (libretro-core.js:381/447) so nothing double-drives. Flag
   * OFF (`this._inputManager` null) it returns the legacy value byte-for-byte:
   * `this.system?.id === 'gb'`.
   */
  _wantsNativeGamepad() {
    return this.system?.id === 'gb' && this.input?._useManager?.() !== true;
  }

  // ── ⋯ "More actions" menu — game-centric actions ──────────────────────

  /** True if the active game is in the local favorites list. */
  _isCurrentGameFavorited() {
    const name = this._activeRomFilename || this.romName;
    if (!name || !this.system?.id) return false;
    return this._getFavorites().some(f => f.name === name && f.system === this.system.id);
  }

  /**
   * Sync the ⋯-menu favorite row's icon/label to the current favorite state.
   * The game room passes its journaled row writer (M1, A5); classic callers
   * pass none and write directly (directRowWriter), as before.
   */
  _refreshFavoriteRow(writer = null) {
    const btn = document.getElementById('btn-favorite');
    if (!btn) return;
    const write = writer || directRowWriter;
    const fav = this._isCurrentGameFavorited();
    write.cls(btn, 'active', fav);
    const icon = btn.querySelector('.gm-icon');
    const label = btn.querySelector('.gm-label');
    if (icon) write.text(icon, fav ? '★' : '☆');
    if (label) write.text(label, fav ? 'Favorited' : 'Favorite');
    write.attr(btn, 'title', fav ? 'Remove from favorites' : 'Favorite');
  }

  /**
   * Toggle the active game's favorite state. Uses `this.romHash` directly
   * (the in-game hash is always known) + the shared favorites store, so it
   * works anonymously (localStorage) and syncs to the server when signed in.
   */
  _favoriteCurrentGame() {
    const name = this._activeRomFilename || this.romName;
    const sys = this.system?.id;
    if (!name || !sys) { this._flash('No game loaded'); return; }
    const hash = this.romHash || null;
    const favs = this._getFavorites();
    const idx = favs.findIndex(f => f.name === name && f.system === sys);
    let nowFav;
    if (idx >= 0) { favs.splice(idx, 1); nowFav = false; }
    else { favs.push({ name, system: sys, hash }); nowFav = true; }
    this._saveFavorites(favs);
    this._favLastToggle = Date.now();
    this._syncFavoriteToServer(sys, hash, nowFav);
    this._refreshFavoriteRow();
    this._flash(nowFav ? 'Added to favorites' : 'Removed from favorites');
  }

  /**
   * Resolve the current ROM to a games-table id via /api/games/lookup
   * (cached per hash). Needed for queue + list adds, which key on game_id.
   * Returns null when the ROM isn't in the catalog.
   */
  async _resolveCurrentGameId() {
    const sys = this.system?.id;
    const hash = this.romHash;
    // Pass the catalog filename (the .zip name, NOT the extracted romName) as a
    // fallback: the FNV hash can drift from the catalog (re-dump, resume with a
    // stale stored hash, format/header edge cases), but the filename is stable.
    const rom = this._activeRomFilename || null;
    if (!sys || (!hash && !rom)) return null;
    const cacheKey = `${sys}|${hash || ''}|${rom || ''}`;
    if (this._canonicalGameIdKey === cacheKey && (this._canonicalGameId || this._canonicalGameScoreSupported === false)) return this._canonicalGameId;
    // C1 (game room): the stored game data belongs to one ROM key, so a lookup for
    // another key clears it first; nothing keyed to the previous ROM is read for this one.
    const sessionKey = roomSessionKey(this);
    const sessionWasOpen = this._roomSessionOpen === true;
    if (this._canonicalGameIdKey !== cacheKey) {
      this._canonicalGameId = null;
      this._canonicalGameIdKey = null;
      this._canonicalGameSlug = null;
      this._canonicalGameTitle = null;
      this._canonicalGameScoreSupported = null;
    }
    try {
      const params = new URLSearchParams({ system: sys });
      if (hash) params.set('hash', hash);
      if (rom) params.set('rom', rom);
      const res = await fetch(`/api/games/lookup?${params.toString()}`);
      if (!res.ok) return null;
      const body = await res.json();
      if (body?.game_id || (body?.game_id === null && body.score_supported === false)) {
        // b7: store the answer only while its ROM key is still the current one; a
        // lookup that resolves after another ROM loaded still returns its own answer.
        const romNow = this._activeRomFilename || null;
        const keyNow = this.system?.id && (this.romHash || romNow)
          ? `${this.system.id}|${this.romHash || ''}|${romNow || ''}` : null;
        if (keyNow !== cacheKey || (sessionWasOpen && !isCurrentSessionKey(this, sessionKey))) return body.game_id;
        this._canonicalGameId = body.game_id;
        this._canonicalGameIdKey = cacheKey;
        // C1: the game room card reads the slug and title only with this key.
        this._canonicalGameSlug = typeof body.slug === 'string' ? body.slug : null;
        this._canonicalGameTitle = typeof body.title === 'string' ? body.title : null;
        this._canonicalGameScoreSupported = typeof body.score_supported === 'boolean' ? body.score_supported : null;
        return body.game_id;
      }
    } catch { /* offline / catalog miss → null */ }
    return null;
  }

  /** Add the active game to the user's play queue (POST /api/library/queue/items). */
  async _addCurrentGameToQueue() {
    if (!authState.getCurrentUser()) { this._flash('Sign in to use your queue'); openAuthModal(); return; }
    const gameId = await this._resolveCurrentGameId();
    if (!gameId) { this._flash('This game is not in the library'); return; }
    try {
      const res = await fetch('/api/library/queue/items', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId }),
      });
      if (res.status === 401) { this._flash('Sign in to use your queue'); openAuthModal(); return; }
      if (!res.ok) throw new Error(String(res.status));
      this._flash('Added to your queue');
    } catch (err) {
      console.warn('[queue] add failed', err);
      this._flash('Could not add to queue');
    }
  }

  /** Open the add-to-list panel for the active game. */
  async _addCurrentGameToList() {
    if (!authState.getCurrentUser()) { this._flash('Sign in to use lists'); openAuthModal(); return; }
    const gameId = await this._resolveCurrentGameId();
    if (!gameId) { this._flash('This game is not in the library'); return; }
    const title = this._canonicalTitle
      || this._romDisplayTitle?.(this.romName, this.system?.id) || this.romName;
    const { openAddToList } = await import('../shared/components/add-to-list-panel.js');
    const releaseInput = this.acquireGameplayInput(Symbol('add-to-list'));
    try {
      openAddToList({
        gameId,
        title,
        onFlash: (message) => this._flash(message),
        onClose: releaseInput,
      });
    } catch (error) {
      releaseInput();
      throw error;
    }
  }

  // ── Homepage tile actions (by game_id) ────────────────────────────────
  // The homepage shelves reuse the library's favorite/queue/list behavior but
  // key on the catalog game_id rather than the loaded game. These take the id
  // directly so any tile can fire them without booting the game.

  /**
   * Toggle a game's favorite by game_id. Favorites key on a ROM hash the
   * tiles don't carry, so POST /api/favorites resolves the canonical dump
   * server-side. Signed-in only (anonymous is prompted); throws on failure so
   * the tile can revert its optimistic star.
   */
  async _toggleHomeFavorite(gameId, favorited) {
    if (!authState.getCurrentUser()) { this._flash('Sign in to favorite'); openAuthModal(); throw new Error('auth_required'); }
    if (gameId == null) throw new Error('no_game');
    const res = await fetch('/api/favorites', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game_id: Number(gameId), favorited: !!favorited }),
    });
    if (!res.ok) { this._flash("Couldn't update favorites"); throw new Error(String(res.status)); }
    this._flash(favorited ? 'Added to favorites' : 'Removed from favorites');
  }

  /** Add a game to the user's play queue by game_id (homepage tiles). Throws
   *  on failure so the tile-action handler can revert its optimistic state. */
  async _addGameToQueue(gameId) {
    if (!authState.getCurrentUser()) { this._flash('Sign in to use your queue'); openAuthModal(); throw new Error('auth_required'); }
    if (gameId == null) throw new Error('no_game');
    let res;
    try { res = await fetch('/api/library/queue/items', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game_id: Number(gameId) }),
    }); } catch (err) { this._flash("Couldn't add to queue"); throw err; }
    if (res.status === 401) { this._flash('Sign in to use your queue'); openAuthModal(); throw new Error('auth_required'); }
    if (!res.ok) { this._flash("Couldn't add to queue"); throw new Error(String(res.status)); }
    this._flash('Added to your queue');
  }

  /** Remove a game from the user's play queue by game_id (homepage tiles). Throws
   *  on failure so the tile-action handler can revert its optimistic state. */
  async _removeGameFromQueue(gameId) {
    if (!authState.getCurrentUser()) { this._flash('Sign in to use your queue'); openAuthModal(); throw new Error('auth_required'); }
    if (gameId == null) throw new Error('no_game');
    let res;
    try { res = await fetch(`/api/library/queue/items/${encodeURIComponent(gameId)}`, {
      method: 'DELETE',
      credentials: 'include',
    }); } catch (err) { this._flash("Couldn't remove from queue"); throw err; }
    if (res.status === 401) { this._flash('Sign in to use your queue'); openAuthModal(); throw new Error('auth_required'); }
    if (!res.ok) { this._flash("Couldn't remove from queue"); throw new Error(String(res.status)); }
    this._flash('Removed from your queue');
  }

  /** Open the add-to-list panel for a game by game_id (homepage tiles). */
  async _addGameToList(gameId, title) {
    if (!authState.getCurrentUser()) { this._flash('Sign in to use lists'); openAuthModal(); return; }
    if (gameId == null) { this._flash('This game is not in the library'); return; }
    const { openAddToList } = await import('../shared/components/add-to-list-panel.js');
    openAddToList({
      gameId: Number(gameId),
      title: title || 'this game',
      onFlash: (message) => this._flash(message),
    });
  }

  _openChat() {
    return roomChat.openChat(this);
  }

  _closeChat() {
    return roomChat.closeChat(this);
  }

  _sendChat() {
    return roomChat.sendChat(this);
  }

  _addChatMsg(html, container, persistent, extraClass, deferRemoval = false) {
    return roomChat.addChatMsg(this, html, container, persistent, extraClass, deferRemoval);
  }

  _showChatSystemMessage(text) {
    return roomChat.showChatSystemMessage(this, text);
  }

  _showChatMessage(text, source) {
    return roomChat.showChatMessage(this, text, source);
  }

  _escapeHtml(str) {
    return escapeHtml(str);
  }

  _showChatMessageFrom(
    text, playerNum, username, colorIdx, spectatorId, userId = null, source = 'remote',
  ) {
    return roomChat.showChatMessageFrom(
      this, text, playerNum, username, colorIdx, spectatorId, userId, source,
    );
  }

  // ── Game Lifecycle ──────────────────────────────────────

  _installC64VirtualPads() {
    if (this.system?.id !== 'c64') return null;
    if (!this.virtualPads) {
      this.virtualPads = new VirtualGamepadManager();
      this.virtualPads.install();
      // Both slots must exist when C64Core registers them after boot. Hosted
      // P2 can then send input without a late connection callback into WASM.
      this.virtualPads.setC64Player(0);
      this.virtualPads.setC64Player(1);
      this.virtualPads.setSuspended(this._gameplayInputLeases?.active === true);
    }
    this.input.useRealGamepads(() => this.virtualPads?.getRealGamepads?.() || []);
    return this.virtualPads;
  }

  /** Spec section 7: the C64 port, right after a boot's content-load call. */
  _applyC64LaunchPort(sessionEmu) {
    return applyC64LaunchPort(this, sessionEmu);
  }

  /** The BASIC action shares the existing sign-in/legal gate and launch fence. */
  async _launchC64Basic() {
    if (this.system?.id !== 'c64' || moduleRegistry.getFeatures()['system:c64'] !== true
        || this.state !== State.MENU || this.running) return false;
    cancelPlayIntent('canceled');
    const selectedSystem = this.system;
    const owner = this._beginGameLaunchIntent({ clearPendingSave: true });
    const isCurrent = () => this.system === selectedSystem && this.state === State.MENU
      && !this.running && this._isGameLaunchOwnerCurrent(owner)
      && moduleRegistry.getFeatures()['system:c64'] === true;
    const start = () => {
      if (!isCurrent()) return;
      void this._startBasicSafely();
    };
    return requireAuthToPlay(() => {
      const userId = authState.getCurrentUser()?.id;
      whenTosClear(() => {
        if (authState.isAuthenticated() && authState.getCurrentUser()?.id === userId) start();
      });
    }, { isCurrent });
  }

  /** Both menu launches and cold BASIC resumes own the same failure cleanup. */
  async _startBasicSafely(existingLaunchOwner = null, options = {}) {
    const owner = existingLaunchOwner ?? this._beginGameLaunchIntent({ clearPendingSave: true });
    const selectedSystem = this.system, userId = authState.getCurrentUser()?.id;
    const ownsLaunch = () => this.system === selectedSystem
      && this._isGameLaunchOwnerCurrent(owner) && authState.getCurrentUser()?.id === userId;
    try {
      await this._startBasic(owner, options);
      return ownsLaunch() && this.running && this._isBasicMode;
    } catch (error) {
      if (!ownsLaunch() || !this._isBasicMode) return false;
      diskPicker.setC64DiskStatus(this, 'stopped', error?.message || 'Could not start C64');
      // Exit invalidates this owner synchronously before cleanup can await.
      const cleanup = this._exitToMenu({ flushPendingPostRoll: false });
      this._flash('Could not start C64 BASIC');
      try { await cleanup; } catch (cleanupError) {
        console.warn('[c64] startup cleanup failed:', cleanupError);
      }
      return false;
    }
  }

  /**
   * Boot C64 to BASIC prompt with a blank formatted disk.
   */
  async _startBasic(existingLaunchOwner = null, { roomCode = null } = {}) {
    if (this.system?.id !== 'c64') return;
    const launchOwner = existingLaunchOwner ?? this._beginGameLaunchIntent({ clearPendingSave: true });
    const user = authState.getCurrentUser();
    this.romData = null;
    this._romDataB64 = null;
    this.romHash = null;
    this._c64Identity = null;
    this.romName = 'C64 BASIC';
    this._activeRomFilename = null;
    this._isMultiDisk = false;
    this._canonicalTitle = null;
    this._canonicalTitleKey = null;
    this._canonicalGameId = null;
    this._canonicalGameIdKey = null;
    this._canonicalGameTitle = null;
    this.state = State.SOLO;
    this._isBasicMode = true;
    this._soloRoomCode = null;
    this._roomCoreName = null;
    this._showScreen('game');
    this._showSysStatus('Loading C64...', 'warn');

    const captureKey = roomSessionKey(this);
    const launchIsCurrent = () => this._isGameLaunchOwnerCurrent(launchOwner)
      && isCurrentSessionKey(this, captureKey) && this.system?.id === 'c64'
      && this.state === State.SOLO && this._isBasicMode
      && authState.getCurrentUser()?.id === user?.id;
    // RWebAudio creates its context during boot. Install the capture tap first,
    // after the game-audio mute gate, so clips retain audio while speakers mute.
    await this._prepareClipAudioCapture(launchIsCurrent);
    if (!launchIsCurrent()) return;

    const sessionEmu = await this._createEmulator();
    if (!sessionEmu || this.emu !== sessionEmu || !launchIsCurrent()) return;
    this._installC64VirtualPads();

    if (user?.username) {
      await this._createInitialSoloRoom(user, { code: roomCode, isLaunchCurrent: launchIsCurrent, launchOwner });
      if (this.emu !== sessionEmu || !launchIsCurrent()) return;
    }
    let scratchDisk = null;
    this._scratchDiskName = 'Scratch';
    this._c64DiskSyncDisabled = false;
    if (authState.getCurrentUser()) {
      if (!this.saveSync?.isLoaded()) await this.saveSync?.init?.();
      if (!this.saveSync?.isLoaded()) {
        this._c64DiskSyncDisabled = true;
        this._flash('Cloud scratch disk could not load. Sync is off for this session');
      } else {
        const meta = this.saveSync.getServerMeta('c64', 'basic', 'disk', 'scratch');
        if (meta) {
          try {
            scratchDisk = await this.saveSync.download('c64', 'basic', 'disk', 'scratch');
            if (!scratchDisk) throw new Error('scratch disk blob unavailable');
            if (meta.label) this._scratchDiskName = meta.label;
          } catch (err) {
            // Boot a temporary blank disk, but fail closed on upload so the
            // inaccessible authoritative copy cannot be replaced by it.
            this._c64DiskSyncDisabled = true;
            console.warn('[disk-sync] scratch disk download failed:', err);
            this._flash('Cloud scratch disk could not load. Sync is off for this session');
          }
        }
      }
    }
    if (this.emu !== sessionEmu || !launchIsCurrent()) return;
    if (!scratchDisk) {
      scratchDisk = LibretroCore.createBlankD64('SCRATCH');
    }
    // BASIC uses the same captured-handler path as ordinary C64 sessions so
    // the on-screen special-key keyboard can call into VICE directly.
    sessionEmu.captureKeyHandlers();
    // Boot with the disk — VICE will show BASIC prompt. Claim the AudioContext
    // created inside Emscripten so normal teardown can close it.
    sessionEmu.loadMultiDiskROM([{ name: 'scratch.d64', data: scratchDisk }], 'Scratch', { noAutostart: true });
    this._applyC64LaunchPort(sessionEmu);
    if (!this._c64DiskSyncDisabled) await this._loadUserDisksFromServer();
    if (this.emu !== sessionEmu || !launchIsCurrent()) return;
    this._scheduleKeyHandlerCaptureRestore(sessionEmu);

    this.running = true;
    updateC64SpecialKeys(this);
    gameActions.refreshGameToolbarAvailability(this, 'core-running');
    if (!(await this._startInputForGameplay())) return false;
    this._p1PrevBitmask = 0;
    this._libretroSoloFrame = 0;
    this._libretroInputLoop(sessionEmu);
    this._startFpsMonitor();
    this._startAutoSaveTimer();
    this._showDiskPicker();

    if (this._soloRoomCode && this.net?.connected) {
      this._startRoomScreenshots();
      this._startSoloStream();
      this._startIdleGuard();
      this._showScreen('game');
      this._openSoloRoomChatColumn();
    }
    this._updateInviteButtonVisibility();

    this.canvas.tabIndex = 0;
    this.canvas.focus();
    this.canvas.classList.add('show-cursor');
    if (!launchIsCurrent() || this.emu !== sessionEmu) return;
    this._initClipRecorder().catch(error => console.warn('[clips] C64 recorder startup failed:', error));
  }

  // Hardware-rendered libretro cores (N64) present to a GL viewport derived from
  // the *displayed* canvas size, but RetroArch sizes the drawing buffer to the
  // core's large internal geometry (e.g. 1440x1080). The presented frame then
  // fills only a sub-region of the oversized buffer, which object-fit then scales
  // down — the game appears small in a corner. Keep the drawing buffer matched to
  // the displayed container so the present viewport fills the buffer. Software
  // cores are unaffected (they render straight to the buffer).
  _fitLibretroCanvasToContainer() {
    const container = document.getElementById('canvas-container');
    if (!container || !this.canvas) return;
    const sync = () => {
      const w = Math.round(container.clientWidth);
      const h = Math.round(container.clientHeight);
      if (w > 0 && h > 0 && (this.canvas.width !== w || this.canvas.height !== h)) {
        this.canvas.width = w;
        this.canvas.height = h;
      }
    };
    let rafPending = false;
    const scheduleSync = () => {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(() => { rafPending = false; sync(); });
    };
    // Win against RetroArch's initial geometry-based sizing for the first few
    // seconds, then track container resizes for the rest of the session.
    let ticks = 0;
    const iv = setInterval(() => { sync(); if (++ticks >= 40 || !this.running) clearInterval(iv); }, 100);
    try { this._libretroCanvasObs?.disconnect(); } catch {}
    this._libretroCanvasObs = new ResizeObserver(scheduleSync);
    this._libretroCanvasObs.observe(container);
    sync();
  }

  /**
   * Log a diagnostic when a ROM fails to load (e.g. "Unsupported mapper: N").
   * Includes the ROM's CRC-32 / SHA-1 (full file, incl. iNES header — the
   * No-Intro convention) so the dump can be validated against a database
   * before chasing emulator bugs, and a bad copy swapped out. Best-effort:
   * never throws, so the original load error still propagates.
   */
  async _reportRomLoadFailure(err) {
    const parts = [
      `[main] ROM failed to load: ${err?.message || err}`,
      `system=${this.system?.id || '?'}`,
      `rom="${this.romName || '?'}"`,
    ];
    try {
      if (typeof this.romData === 'string') {
        const { crc32, sha1, sizeBytes } = await romChecksums(this.romData);
        parts.push(`crc32=${crc32}`);
        if (sha1) parts.push(`sha1=${sha1}`);
        parts.push(`size=${sizeBytes}B`);
      }
    } catch { /* checksum is best-effort diagnostic */ }
    console.error(parts.join(' '));
  }

  /**
   * Reset state and DOM that belong to a departed room. This is shared by the
   * normal exit and fresh-save launch paths so neither can carry chat,
   * spectators, upgrade prompts, or identity UI into the replacement session.
   */
  _resetRoomSessionUi() {
    this._closeInviteModal();
    this._hideUserCard();
    roomChat.resetRoomChatSession(this);
    this._spectators = new Map();
    this._pendingUpgrade = null;
    this._pendingInvite = null;
    this._pendingLobbySwap = false;
    this._roomJoinLaunchOwner = null;
    this._hostedRoleTransitionOwner = null;
    if (this.net) {
      this.net.playerNumber = null;
      this.net.roomCode = null;
      this.net.spectator = false;
      this.net.spectatorId = null;
    }

    document.getElementById('presence-section')?.classList.add('hidden');
    const spectators = document.getElementById('presence-spectators');
    if (spectators) spectators.innerHTML = '';
    const badge = document.getElementById('chat-presence-badge');
    if (badge) { badge.textContent = '0'; badge.classList.add('hidden'); }
    document.getElementById('upgrade-prompt')?.classList.add('hidden');
    this._updateInviteButtonVisibility?.();
    this._updateReportButtonVisibility?.();
  }

  /**
   * Tear down every resource owned by the previous gameplay session before a
   * save loader installs its system, ROM, and pending state. Save launchers can
   * run from the homepage while an old core (or an async core launch) is still
   * alive, so relying on _createEmulator's core/canvas replacement is not
   * sufficient: input, recording, streaming, room, and self-driving loops also
   * belong to the departed session.
   *
   * This must run BEFORE the loader changes this.system/this.romData. _stopGame
   * needs the old metadata to flush system-specific state such as C64 disks.
   */
  _prepareFreshSaveLaunch() {
    const committedSaveRef = this._committedSaveLaunchRef || null;
    this._committedSaveLaunchRef = null;
    const previousState = this.state;
    const wasSolo = previousState === State.SOLO
      || !!this._soloRoomCode
      || this.net?.roomCode?.startsWith?.('solo:');

    // Invalidate any in-flight launch before exposing menu state. A pending
    // socket/reconnect counts too; disconnect() is idempotent and cancels both.
    this.state = State.MENU;
    this.net?.disconnect(wasSolo ? 4000 : undefined);
    this._stopRoomScreenshots();
    this._stopSoloStream();
    this._stopIdleGuard();
    this._teardownSbsSession();
    this._releaseCanvasStream();
    this._soloRoomCode = null;
    this._restartSoloRoomCode = null;
    this._roomCoreName = null;
    this._roomPeerUserId = null;
    this._replacePendingSave(null);
    this._saveRef = null;
    this._soloModeOverride = null;
    try { sessionStorage.removeItem('retroplay:soloSession'); } catch { /* ignore */ }
    this._stopGame();
    this._resetRoomSessionUi();
    this._showScreen('menu');
    if (committedSaveRef?.storageOwnerId === (authState.getCurrentUser()?.id || null)
        && committedSaveRef?.storageNamespace === currentSaveDBNamespace()) {
      this._saveRef = committedSaveRef;
    }
  }

  /**
   * Reboot a running solo session for a pending cross-core save without leaving
   * any loop, timer, capture stream, or spectator host attached to the old
   * emulator. Keep the current room socket/code so the server can atomically
   * refresh its core provenance before friends join the relaunched session.
   */
  async _restartSoloForSave() {
    this._restartSoloRoomCode = this._soloRoomCode
      || (this.net?.connected ? this.net.roomCode : null);
    this._stopRoomScreenshots();
    this._stopSoloStream();
    this._stopIdleGuard();
    this._stopGame();
    this._releaseCanvasStream();
    try {
      await this._startSolo();
    } finally {
      this._restartSoloRoomCode = null;
    }
  }

  async _startSolo(existingLaunchOwner = null, { retainedRoomOwner = null, onLaunchCommitted = null } = {}) {
    // Achievement authoring (spec 8.1, 12): this launch takes a pending author launch (an author
    // link's or the admin menu's relaunch) first thing, so no later launch can find it.
    const authorLaunch = this._takeAuthorLaunch(existingLaunchOwner);
    const titleGuard = unsupportedTitleRomGuard(this);
    if (titleGuard && !await titleGuard) return false;
    const launchBeforeKey = roomLaunchSessionKey(this);
    const launchRomSource = this.romData;
    const launchFilename = this._activeRomFilename;
    const launchWasOpen = this._roomSessionOpen === true;
    let launchExpectedKey = launchBeforeKey;
    let ownedSave = this._pendingSave;
    const reservedSave = !!this._handoffSaves?.has(ownedSave);
    const releaseSaveUse = this._holdHandoffSave(ownedSave);
    const releaseOwnedSave = () => {
      if (reservedSave) this._discardPendingSave(ownedSave);
      ownedSave = null;
      releaseSaveUse();
    };
    try {
    if (this._unsafeReplayBoundary) {
      this._flash('Reload the page to play again.');
      return;
    }
    const libraryLaunch = this._takeLibraryLaunchAttribution(existingLaunchOwner);
    const launchOwner = existingLaunchOwner ?? this._beginGameLaunchIntent();
    const ownerIsCurrent = () => retainedRoomOwner
      ? this._roomAssignmentIsCurrent(retainedRoomOwner)
      : this._isGameLaunchOwnerCurrent(launchOwner);
    if (!ownerIsCurrent()) return;
    const launchGeneration = (this._gameLaunchGeneration || 0) + 1;
    this._gameLaunchGeneration = launchGeneration;
    const launchIsCurrent = () => (
      this._gameLaunchGeneration === launchGeneration && this.state === State.SOLO
      && ownerIsCurrent() && !roomRecoveryBlocked(this) && sameLaunchSessionKey(launchExpectedKey, roomLaunchSessionKey(this))
      && this.romData === launchRomSource && this._activeRomFilename === launchFilename
    );

    // Read the screen we're leaving BEFORE _showScreen overwrites it — it's how
    // the funnel attributes the launch to a surface.
    const funnelSurface = this._funnelSurfaceForLaunch(libraryLaunch);
    this.state = State.SOLO;
    launchExpectedKey = Object.freeze({
      ...launchBeforeKey, sessionGen: launchBeforeKey.sessionGen + (launchWasOpen ? 0 : 1),
    });
    this._showScreen('game');
    const netModeKey = gameActionSessionKey(this);
    if (!launchIsCurrent() || this._roomSessionOpen !== true) return false;
    this._showSysStatus('Loading core...', 'warn');
    this._updateResetButtonVisibility();
    // Activation step 3, before the core boots — so the gap to first_frame
    // measures load failures and abandonment during loading.
    this._trackFunnelLaunch(funnelSurface);
    // One launch owns one replay base. A prior save-loaded session must not
    // keep the next fresh-boot session blocked.
    this._replayBaseStateLoaded = false;
    this._replayRecordingOff = false;
    this._clearPendingBaseGeneration();
    // Session generation token captured by saveReplay's finalizer (Task 19) so a
    // finalizer that outlives a relaunch can never regain authority.
    this._replaySessionGen = (this._replaySessionGen || 0) + 1;
    this._recordDrainCursor = 0;
    this._recordFrameOffset = 0;
    this._recordResumePrefix = null;

    // The session probe runs fire-and-forget at boot (refreshSession in
    // the constructor). Most entry points — menu clicks, game-change
    // overlay — give auth time to resolve before the user clicks play,
    // so `getCurrentUser()` reliably returns the signed-in user. BUT
    // the library deep-link path (`/?system=X&rom=Y&solo=1`) fires
    // _startSolo() synchronously during boot, before the session
    // probe completes. Without awaiting, getCurrentUser() returns
    // null → no solo room gets created → the 👥 Invite button stays
    // hidden and the user has no way to invite a friend from a
    // library-launched session. Await loaded state once here so every
    // entry point sees a resolved session.
    if (!authState.isLoaded()) {
      try { await authState.refreshSession(); } catch { /* non-fatal */ }
      if (!launchIsCurrent()) return;
    }

    // Surface any recorded cloud-save conflict for this game and let the
    // user reconcile BEFORE the session consumes _pendingSave ('remote'
    // replaces it with the server state). Plain solo launches only —
    // replay playback, ghost races, async-turn resumes, and SBS races
    // must not block on a modal.
    if (!this._pendingReplayId && !this._pendingGhostId
      && !this._pendingAsyncGame && this._sbsRole !== 'racer') {
      try {
        await this._maybeResolveSaveConflictOnLoad(launchIsCurrent);
      } catch (err) {
        console.warn('[sync] save conflict resolution failed:', err);
      }
      if (!launchIsCurrent()) return;
    }

    // Keep the saved bytes available in the save shelf, but launch this
    // cartridge fresh rather than feed a legacy Genesis state to PicoDrive.
    if (isIncompatibleGenesis32xSave(this.system?.id, this.romData, this._pendingSave)) {
      this._replacePendingSave(null);
      this._saveRef = null;
      this._flash('This save uses another Genesis core; starting the 32X game fresh');
    }

    // Resolve artifact provenance before room registration so authenticated
    // solo-room metadata and the emulator we boot can never select different
    // Genesis cores. Legacy Genesis saves necessarily came from GX.
    const artifactCoreName = this._pendingReplayCoreName
      || this._pendingSave?.coreName
      || (this._sbsRole === 'racer' ? this._roomCoreName : null)
      || (this._pendingSave && this.system?.id === 'genesis' ? 'genesis_plus_gx' : null);

    if (this._pendingReplayId && this.system?.id === 'snes' && artifactCoreName === 'snes9x2010_multitap') {
      const replayId = this._pendingReplayId;
      const startup = new AbortController();
      this._multitapReplayStartup = startup;
      const timer = setTimeout(() => startup.abort(), 30_000);
      let ownedCore = null;
      try {
        const { prepareMultitapReplayInput } = await import('./modules/replay-viewer.js');
        if (!launchIsCurrent() || startup.signal.aborted) return false;
        const prepared = await prepareMultitapReplayInput(replayId, this.romData, launchIsCurrent, startup.signal);
        if (!launchIsCurrent() || startup.signal.aborted) return false;
        ownedCore = await this._createEmulator({ coreName: artifactCoreName,
          replayInput: { romBytes: prepared.romBytes, current: launchIsCurrent, signal: startup.signal,
            superunoRun: prepared.replay.superunoRun, superunoTitle: prepared.replay.superunoTitle,
            superunoCapability: prepared.replay.superunoCapability } });
        if (!ownedCore || !launchIsCurrent() || startup.signal.aborted) return false;
        this._soloRecordMode = false;
        this._replayRecordingOff = true;
        this._replayPlaybackActive = true;
        this._netMode = 'solo';
        if (!gameActions.noteNetModeWrite(this, netModeKey)) return false;
        this.running = true;
        gameActions.refreshGameToolbarAvailability(this, 'net-mode');
        this._pendingReplayId = null;
        if (!await this._startReplayPlayback(replayId, prepared)) throw new Error('multitap replay startup failed');
        return true;
      } catch (error) {
        if (launchIsCurrent()) {
          if (this.emu === ownedCore) { this.emu = null; ownedCore?.destroy(); }
          this.running = false; this._replayPlaybackActive = false;
          this._showSysStatus('Replay could not start', 'error');
          this._flash(error.message || 'Replay could not start');
        }
        return false;
      } finally {
        clearTimeout(timer);
        if (this._multitapReplayStartup === startup) this._multitapReplayStartup = null;
      }
    }

    // Re-fetch feature flags before creating a genuinely new room or choosing
    // record mode. The server-confirmed room core remains authoritative for an
    // authenticated session; this refresh covers anonymous/roomless launches.
    await this._refreshFeatureFlags();
    if (!launchIsCurrent()) return;
    this._replaysEnabled = moduleRegistry.isEnabled('replays');
    // Achievement authoring: every solo launch is a draft test session (PR 2 README decision 5);
    // a taken author launch still available gets the author session profile (spec 8.1) before
    // record mode and the core are chosen below.
    this._startDraftTestSession({ fromSave: !!this._pendingSave });
    if (authorLaunch) {
      await this._beginAuthorLaunch(authorLaunch);
      if (!launchIsCurrent()) return;
    }

    // A suggestion launch reached gameplay — count it as played. Lives here
    // (not in the deep-link handler) so anonymous arrivals who sign in and
    // then hit Play still resolve the suggestion.
    this._markSuggestionPlayed();

    // Authenticated users get a spectatable solo room
    const user = authState.getCurrentUser();
    const restartingRoomCode = this._restartSoloRoomCode || null;
    this._soloRoomCode = restartingRoomCode;
    if (!restartingRoomCode && !this._pendingAsyncGame && this._sbsRole !== 'racer') {
      this._roomCoreName = null;
    }
    // Async turn-based resume owns its own room (async:<gameId>) via the
    // boot-path call to asyncGame.startTurn → _ensureLiveRoom. Creating a
    // solo:<username> room here would either overwrite that WebSocket (in
    // the baton-pass case, where we're already in async:<gameId>) or
    // force a churn-y reconnect right afterwards (in the notification
    // resume case). Skip the solo-room block entirely; _ensureLiveRoom
    // handles connection.
    if (this._pendingAsyncGame) {
      // Carry forward an existing async room connection (baton-pass).
      if (this.net?.roomCode?.startsWith('async:')) {
        this._soloRoomCode = this.net.roomCode;
      }
    } else if (this._sbsRole === 'racer') {
      // Side-by-side racer: we're already connected to the shared two-racer
      // room (joined by the entry-point flow before this runs). Creating a
      // solo:<username> room here would call net.connect() and tear down that
      // SBS WebSocket, breaking opponent/spectator signaling. Instead carry
      // the existing room forward so _startSoloStream()/screenshots fan out
      // over the open socket. No-op for legacy solo (`_sbsRole` undefined).
      if (this.net?.connected) {
        this._soloRoomCode = this.net.roomCode;
      }
    } else if (user?.username) {
      await this._createInitialSoloRoom(user, {
        code: restartingRoomCode,
        saveCoreName: artifactCoreName,
        isLaunchCurrent: launchIsCurrent,
        launchOwner,
      });
      if (!launchIsCurrent()) return;
    }

    await this._prepareClipAudioCapture(launchIsCurrent);
    if (!launchIsCurrent()) return;

    // Record-eligible solo (signed-in, replays on, allowlisted system) runs the
    // JS-stepped lockstep core so the recorder gets a faithful per-frame log —
    // except SELF_DRIVING_RECORD_SYSTEMS (GB/Atari), which record from the input-log
    // ring while self-driving (lockstep stepping traps gambatte). Cores that
    // cannot self-drive at all (_coreCannotSelfDrive: SNES) step even when the
    // session records nothing. Everything else keeps today's self-driving path.
    // _createEmulator only swaps to LockstepLibretroCore for non-NES systems
    // (NES is always jsnes).
    const savedCoreName = this._roomCoreName || artifactCoreName;
    this._soloRecordMode = this._shouldRecordSolo(savedCoreName || undefined);
    this._soloModeOverride = null; // one-shot consumed; later launches use the saved default
    const steppedReplayPlayback = !!this._pendingReplayId
      && LOCKSTEP_SOLO_RECORD_SYSTEMS.has(this.system?.id);
    // Retain only the boolean across startup awaits, not the state payload.
    const pendingSaveHasState = !!this._pendingSave?.emulatorState
      && this._pendingSave.emulatorState.byteLength !== 0;
    // Legacy Atari replay-derived saves contain only RPL1 and still need the
    // stepped fast-forward loader. New combined saves contain state + RPL1 and
    // stay self-driving so restore is instant and recording can resume.
    const legacyAtariReplayOnly = this.system?.id === 'atari2600'
      && !!this._pendingSave?.replayLog
      && !pendingSaveHasState;
    const lockstepCore = steppedReplayPlayback
      || legacyAtariReplayOnly
      || (this._soloRecordMode && !SELF_DRIVING_RECORD_SYSTEMS.has(this.system?.id))
      || this._coreCannotSelfDrive(savedCoreName || resolveCoreName(this.system, false, this.romData));
    const n64Clip = this.system?.id === 'n64' && moduleRegistry.getFeatures().replay_render_n64 === true
      ? await import('./cores/n64-clip-recording.js') : null;
    const n64ClipRecording = !!n64Clip && await n64Clip.canRecordN64Clip(this, restartingRoomCode);
    if (!launchIsCurrent()) return;
    const sessionEmu = await this._createEmulator({ lockstep: lockstepCore, coreName: savedCoreName, n64ClipRecording });
    if (!sessionEmu) return;
    if (!launchIsCurrent() || this.emu !== sessionEmu) {
      // _createEmulator normally observes _stopGame through its own generation,
      // but also close the core if a non-standard state transition invalidated
      // this launch after core creation completed.
      if (this.emu === sessionEmu) {
        this.emu = null;
        sessionEmu.destroy?.();
      }
      return;
    }

    if (n64ClipRecording) {
      await n64Clip.prepareN64ClipRecording(sessionEmu, this.romData);
      if (!launchIsCurrent() || this.emu !== sessionEmu) return;
    }
    this._showSysStatus('Loading ROM...', 'warn');

    if (sessionEmu.selfDriving) {
      // Self-driving cores (EmulatorJS/RetroArch WASM) manage their own
      // game loop, rendering, audio, and input via the canvas.

      // Libretro: capture Emscripten's keydown/keyup handlers BEFORE loadROM.
      // Most cores use them for injected controller input; C64 reserves them
      // for its compact on-screen special-key keyboard. Physical keyboard
      // events still reach the core through the original listeners.
      const isLibretro = sessionEmu instanceof LibretroCore;
      if (isLibretro) {
        sessionEmu.captureKeyHandlers();
      }

      // Solo N64: route the real left stick through a virtual pad so RetroArch
      // reads it natively and PROPORTIONALLY (the spike-proven path — see
      // docs/spikes/n64-analog-gamepad-spike.md). We install the getGamepads
      // override BEFORE the core boots (before loadROM) so RetroArch never
      // autoconfigs the real pad; the real stick is fed into virtual slot 0 by
      // _libretroInputLoop. Buttons keep flowing via keyboard injection.
      // (Reading the raw native pad directly does NOT work in solo — that was
      // the regression: the left stick had no working path.)
      if (isLibretro && this.system?.id === 'n64') {
        this.virtualPads = new VirtualGamepadManager();
        this.virtualPads.install();
        this.virtualPads.setSuspended(this._gameplayInputLeases?.active === true);
        this.input.useRealGamepads(() => this.virtualPads.getRealGamepads());
      }
      if (isLibretro) this._installC64VirtualPads();

      // Solo libretro uses the same P1 key injection path as hosted host,
      // so retroarch.cfg must bind P1 to the keys in P1_KEY_MAP.
      // C64 is excluded inside _writeConfig because it has its own handling.
      const soloOpts = isLibretro ? {
        injectInput: true,
        // The lean iOS Gambatte build cannot safely accept a WASM dynCall for
        // every controller edge while Asyncify may be suspended. Let RetroArch
        // poll the physical GB pad natively; touch/keyboard stay injected.
        // Controller Profiles ON disables this native path so GB/GBA gamepad
        // input routes through InputManager instead (spec §1 "GB/GBA gamepad
        // path"); see _wantsNativeGamepad().
        nativeGamepad: this._wantsNativeGamepad(),
      } : {};
      const singleC64Disk = this._singleC64DiskForCurrentRom();
      if (this._isMultiDisk && this.romData.disks) {
        const disks = this.romData.disks.map(d => ({
          name: d.name,
          data: Uint8Array.from(atob(d.data), c => c.charCodeAt(0)),
        }));
        sessionEmu.loadMultiDiskROM(disks, this.romData.name, soloOpts);
        this._applyC64LaunchPort(sessionEmu);
        if (this.system?.id === 'c64') {
          await this._loadUserDisksFromServer();
          if (!launchIsCurrent() || this.emu !== sessionEmu) return;
          this._showDiskPicker();
        } else if (sessionEmu.totalDisks > 1) {
          this._showDiskPicker();
        }
      } else if (singleC64Disk) {
        sessionEmu.loadMultiDiskROM([singleC64Disk], this.romName, soloOpts);
        this._applyC64LaunchPort(sessionEmu);
        await this._loadUserDisksFromServer();
        if (!launchIsCurrent() || this.emu !== sessionEmu) return;
        this._showDiskPicker();
      } else {
        sessionEmu.loadROM(this.romData, soloOpts);
        this._applyC64LaunchPort(sessionEmu);
        if (sessionEmu._n64ClipRecording) {
          await n64Clip.validateN64ClipBoot(sessionEmu);
          if (!launchIsCurrent() || this.emu !== sessionEmu) return;
        }
      }

      // Delay restore to catch handlers registered during the early frames
      // of the main loop (matches the hosted-host path).
      if (sessionEmu instanceof LibretroCore) {
        this._scheduleKeyHandlerCaptureRestore(sessionEmu);
      }

      this.running = true;
      gameActions.refreshGameToolbarAvailability(this, 'core-running');
      this._startFpsMonitor();
      this._startAutoSaveTimer();
      // Self-driving core: JS never sees a frame boundary, so first_frame is
      // inferred after a boot grace period rather than claimed on the first
      // input-loop tick (which fired even when the core never booted).
      this._armFunnelBootInference();

      if (isLibretro && this.system?.id === 'n64') {
        this._fitLibretroCanvasToContainer();
      }

      // Captured for the self-driving record-arm branch below:
      // true only when a save actually restored successfully — a caught
      // restore failure leaves the session at power-on ("starting fresh"),
      // which IS verifiable, so it must not block arming.
      let selfDrivingSaveApplied = false;
      let recordResumePrefix = null;
      if (this._pendingSave && (!reservedSave || this._pendingSave === ownedSave)) {
        let pendingSave = this._pendingSave;
        this._replacePendingSave(null);
        try {
          if (this.system?.id === 'n64' || (usesStatePump(this.system?.id, sessionEmu.coreName) && sessionEmu.selfDriving)) {
            // N64 restores via the async in-loop unserialize. The core
            // self-drives here (running=true above), so the pump fires.
            const ok = await this._withStatePumpLock(
              sessionEmu,
              () => sessionEmu.loadStateN64(pendingSave.emulatorState),
            );
            if (this.emu !== sessionEmu) return;
            if (!ok) {
              this._flash('Could not restore save. Starting fresh');
              this._saveRef = null;
            }
            selfDrivingSaveApplied = ok;
          } else {
            const restored = sessionEmu.loadState(pendingSave.emulatorState);
            if (restored === false) {
              this._flash('Could not restore save. Starting fresh');
              this._saveRef = null;
            } else selfDrivingSaveApplied = true;
          }
        } catch (e) {
          console.warn('Failed to restore save state:', e);
          this._flash('Could not restore save. Starting fresh');
          this._saveRef = null;
        }
        if (selfDrivingSaveApplied
            && this.system?.id === 'atari2600'
            && pendingSave.replayLog) {
          recordResumePrefix = pendingSave.replayLog;
        } else if (selfDrivingSaveApplied) {
          this._onEmulatorStateLoaded();
        }
        pendingSave = null;
        releaseOwnedSave();
      }
      // Captured for the same reason — a pending async-game turn pauses the
      // session (asyncGame._pauseGame() below sets running=false) before the
      // record loop's first tick, so it can never actually run.
      const selfDrivingAsyncGameTurnPending = !!this._pendingAsyncGame;
      if (this._pendingAsyncGame) {
        const ag = this._pendingAsyncGame;
        this._pendingAsyncGame = null;
        this.asyncGame.startTurn({
          id: ag.id,
          system_id: ag.system_id,
          core_name: ag.core_name,
          rom_hash: ag.rom_hash,
          rom_name: ag.rom_name,
        });
        this.asyncGame._pauseGame();
        this._flash(`Turn ${(ag.turn_number || 0) + 1}: unpause to start your turn`, 0);
        const resumeOnInput = () => {
          document.removeEventListener('keydown', resumeOnInput);
          this.canvas.removeEventListener('pointerdown', resumeOnInput);
          this.asyncGame._resumeGame();
          this._flash('Go!', 1500);
        };
        document.addEventListener('keydown', resumeOnInput);
        this.canvas.addEventListener('pointerdown', resumeOnInput);
      }

      // Give the canvas focus so the core receives keyboard events
      this.canvas.tabIndex = 0;
      this.canvas.focus();
      if (this.system?.id === 'c64') this.canvas.classList.add('show-cursor');

      // Libretro reads physical keyboard natively, but the on-screen touch
      // gamepad (and physical Gamepad API) need bitmask→keyboard injection
      // (autodetect is disabled in retroarch.cfg). Start a lightweight
      // rAF loop that forwards touch + gamepad bitmasks via injectP1Input.
      if (!(await this._startInputForGameplay())) return false;
      this._p1PrevBitmask = 0;
      this._libretroSoloFrame = 0;
      this._startMemoryMonitor();
      startBreadcrumb({ system: this.system?.id, romName: this.romName, romHash: this.romHash,
        getLiveData: () => {
          const snap = this.memoryMonitor?.getSnapshot?.();
          return {
            frameCount: this._libretroSoloFrame,
            uptimeMs: this._serverSessionStartedAt ? Date.now() - this._serverSessionStartedAt : null,
            heapSize: this.emu?.module?.HEAPU8?.byteLength ?? null,
            deviceClass: snap?.deviceClass ?? null,
            growth_5m: snap?.growth_5m ?? null,
            clipsState: snap?.clipsState ?? null,
          };
        },
      });
      // A self-driving record session that loads a save without a native frame
      // boundary acknowledgment or starts in a paused async-game turn cannot
      // produce a faithful recording (see
      // _selfDrivingRecordArmBlocked's doc comment) — drop out of record mode
      // entirely rather than just skipping the arm once, so the later
      // unconditional _startAchievementSession() → _initReplayRecorder()
      // tail call (and Critical-1's self-driving guard, which reads
      // _soloRecordMode) can't resurrect a zombie recorder.
      if (this._soloRecordMode && SELF_DRIVING_RECORD_SYSTEMS.has(this.system?.id)
          && this._selfDrivingRecordArmBlocked({
            saveApplied: selfDrivingSaveApplied,
            asyncGameTurnPending: selfDrivingAsyncGameTurnPending,
          })) {
        this._applySelfDrivingArmBlock({ saveApplied: selfDrivingSaveApplied });
      }
      let recordRingArmed = false;
      if (this._soloRecordMode && SELF_DRIVING_RECORD_SYSTEMS.has(this.system?.id)) {
        try {
          recordRingArmed = this.system?.id === 'n64'
            ? !!this.emu.enableN64RecordingRing?.()
            : this._armRecordRingWithBaseState();
        } catch (err) {
          console.warn('[replay] input ring arm failed:', err);
          recordRingArmed = false;
        }
        // EVERY ring-arm failure routes through the ONE fallback,
        // not just the base-state-loaded case — a bare _soloRecordMode clear left
        // a boot-start failure with _replayRecordingOff false and WebM down.
        if (!recordRingArmed) this._onSelfDrivingRingArmFailed();
      } else {
        // CR3: a self-driving core outside record mode never arms a recorder.
        settleReplayDecision(this, 'off');
      }
      this._recordResumePrefix = recordRingArmed ? recordResumePrefix : null;
      this._startAchievementSession();
      if (!this.hostedNetplay && !this.hostedMultiPeer) {
        if (this._pendingReplayId) {
          const replayId = this._pendingReplayId;
          this._pendingReplayId = null;
          this._startReplayPlayback(replayId).catch((err) => {
            console.warn('[main] self-driving replay playback failed:', err);
            this._replayPlaybackActive = false;
            gameActions.refreshGameToolbarAvailability(this, 'replay');
            this._flash('Could not start replay');
            this._libretroInputLoop(this.emu);
          });
        } else if (recordRingArmed) {
          // Self-driving recording: override input + ring drain → recorder.
          // Reset soloFrame so the recorder's baseFrame is 0 (the ring reset to
          // frame 0 in enableRecordingRing); a stale value from a prior session
          // would desync the headless re-render (which assumes baseFrame === 0).
          this.soloFrame = 0;
          this._recordDrainCursor = 0;
          this._recordFrameOffset = 0;
          const armed = await this._armSelfDrivingRecordLoop(this.emu);
          if (!armed) this._libretroInputLoop();
        } else {
          this._libretroInputLoop();
        }
      }
    } else {
      // JS-driven cores (jsnes) — we control the game loop
      try {
        this.emu.loadROM(this.romData);
      } catch (err) {
        await this._reportRomLoadFailure(err);
        throw err;
      }
      this.soloFrame = 0;

      // Replay-derived saves (atari2600/genesis lockstep recording) can't be
      // unserialized in-browser — loadState would deadlock. They reconstruct state
      // by REPLAYING the stored input log (fast-forward) after the recorder starts,
      // so the recorder re-captures 0..N and a later save covers the full session.
      // Deferred to just before _soloLoop (see below). SNES/jsnes saves have no
      // replayLog → the normal loadState path; flag-OFF loads go self-driving
      // (loadStateN64) up in the isLibretro branch instead.
      let pendingFastForward = null;
      if (this._pendingSave && (!reservedSave || this._pendingSave === ownedSave)) {
        pendingFastForward = this._restoreSteppedPendingSave(this._pendingSave, sessionEmu);
        this._replacePendingSave(null);
      }
      if (this._pendingAsyncGame) {
        const ag = this._pendingAsyncGame;
        this._pendingAsyncGame = null;
        this.asyncGame.startTurn({
          id: ag.id,
          system_id: ag.system_id,
          core_name: ag.core_name,
          rom_hash: ag.rom_hash,
          rom_name: ag.rom_name,
        });
        this.asyncGame._pauseGame();
        this._flash(`Turn ${(ag.turn_number || 0) + 1}: unpause to start your turn`, 0);
        const resumeOnInput = () => {
          document.removeEventListener('keydown', resumeOnInput);
          this.canvas.removeEventListener('pointerdown', resumeOnInput);
          this.asyncGame._resumeGame();
          this._flash('Go!', 1500);
        };
        document.addEventListener('keydown', resumeOnInput);
        this.canvas.addEventListener('pointerdown', resumeOnInput);
      }

      if (!(await this._startInputForGameplay())) return false;

      // jsnes feeds audio through this.audio; a lockstep libretro core (solo
      // SNES recording) renders audio via RetroArch's own driver, so skip the
      // jsnes audio path for it. (Video is likewise RetroArch's own here.)
      if (!(this.emu instanceof LibretroCore)) {
        this.emu.onAudioBufferReady = (samples) => {
          this.audio.writeSamples(samples);
        };
        this.audio.init().catch(() => {});
      }
      if (!gameActionSessionCurrent(this, netModeKey)) return false;
      this.running = true;
      this._netMode = 'solo';
      if (!gameActions.noteNetModeWrite(this, netModeKey)) return false;
      gameActions.refreshGameToolbarAvailability(this, 'net-mode');
      this._startFpsMonitor();
      this._startAutoSaveTimer();
      this._startMemoryMonitor();
      startBreadcrumb({ system: this.system?.id, romName: this.romName, romHash: this.romHash,
        getLiveData: () => {
          const snap = this.memoryMonitor?.getSnapshot?.();
          return {
            frameCount: this.soloFrame,
            uptimeMs: this._serverSessionStartedAt ? Date.now() - this._serverSessionStartedAt : null,
            heapSize: null,
            deviceClass: snap?.deviceClass ?? null,
            growth_5m: snap?.growth_5m ?? null,
            clipsState: snap?.clipsState ?? null,
          };
        },
      });
      this._startAchievementSession();
      this.lastFrameTime = performance.now();
      this.frameAccumulator = 0;
      // If the session was launched via /?replay=:id, divert into replay
      // playback instead of starting the user-driven solo loop. The emulator,
      // ROM, and canvas are already set up — ReplayPlayer just drives them.
      if (this._pendingReplayId) {
        const replayId = this._pendingReplayId;
        this._pendingReplayId = null;
        this._startReplayPlayback(replayId).catch(err => {
          console.warn('[main] replay playback failed:', err);
          gameActions.refreshGameToolbarAvailability(this, 'replay');
          this._flash('Could not start replay');
          this._soloLoop(performance.now());
        });
      } else if (this._pendingGhostId) {
        // Ghost race: the player runs a normal (recordable) solo session,
        // but a second emulator replays the ghost beside them. _startGhostRace
        // owns the loop, so we don't start _soloLoop here.
        const ghostId = this._pendingGhostId;
        this._pendingGhostId = null;
        this._startGhostRace(ghostId).catch(err => {
          console.warn('[main] ghost race failed:', err);
          gameActions.refreshGameToolbarAvailability(this, 'race');
          this._flash('Could not start ghost race');
          this._soloLoop(performance.now());
        });
      } else {
        // Start the replay recorder at boot (soloFrame 0) and AWAIT it so it is
        // recording BEFORE the first frame steps. The server highlight render
        // (render-engine.js replayFramesFromCore) re-runs the log from a fresh
        // boot and assumes baseFrame === 0. This init used to run only at the tail
        // of _startAchievementSession(), after its async cheevos-definition and
        // achievement-state fetches — so on mobile (slow network) the solo loop had
        // already advanced soloFrame by tens-to-hundreds of frames. Those early
        // inputs went unrecorded and every later input landed `baseFrame` frames too
        // early in the re-render, desyncing the auto high-score/achievement clip
        // (replay_render is the touch-device clip path) into a frozen, degenerate
        // run. Recording from frame 0 keeps baseFrame === 0 so the render is faithful.
        const recorderEmu = this.emu;
        try { await this._initReplayRecorder(); }
        catch (err) {
          console.warn('[replay] recorder arm failed:', err);
          if (this.emu === recorderEmu) this._replayRecordingUnavailable('recorder_arm');
        }
        // Replay-derived save: replay the stored log to reach the saved frame now
        // that the recorder is live (it re-captures 0..N for a faithful re-save).
        if (pendingFastForward) await this._fastForwardLoad(pendingFastForward);
        pendingFastForward = null;
        releaseOwnedSave();
        this._rewindRing = [];
        this._updateRewindButtonVisibility();
        this._soloLoop(performance.now());
      }
    }

    // Start WebRTC streaming + screenshot capture for spectators (if in a solo room)
    if (this._soloRoomCode && this.net?.connected) {
      this._startRoomScreenshots();
      this._startSoloStream();
      this._startIdleGuard();
      // Re-trigger screen update so the chat UI becomes visible
      // (_showScreen ran before _soloRoomCode was set)
      this._showScreen('game');
      this._openSoloRoomChatColumn();
    }

    // C64 has no qualified replay path or trackable achievement definitions.
    // Manual clips use the live canvas/audio buffer independently of those.
    if (!launchIsCurrent() || this.emu !== sessionEmu) return false;
    if (this.system?.id === 'c64') {
      this._initClipRecorder().catch(error => console.warn('[clips] C64 recorder startup failed:', error));
    }
    // Achievement authoring (spec 8.1): an author session starts its studio on the running core.
    this._startAuthorStudio();

    // Surface the Invite-a-friend button. Requires an active solo room
    // (authenticated users only) — unauthenticated solo sessions don't
    // have a room to open up, so the button stays hidden for them.
    this._updateInviteButtonVisibility();

    if (!this._gameplayTourShown) {
      this._gameplayTourShown = true;
      setTimeout(() => this._startGameplayTour(), 2500);
    }
    if (!launchIsCurrent() || this.emu !== sessionEmu) return false;
    const accepted = typeof onLaunchCommitted !== 'function'
      || onLaunchCommitted(launchBeforeKey, roomLaunchSessionKey(this),
        () => launchIsCurrent() && this.emu === sessionEmu) === true;
    return accepted && launchIsCurrent() && this.emu === sessionEmu;
    } finally {
      releaseOwnedSave();
    }
  }

  /**
   * Resolve the room code from a 2xx POST /api/rooms response. The solo code
   * is deterministic, so a payload without a usable code (proxy-mangled body,
   * API drift) falls back to the requested code — never hand net.connect()
   * `undefined`: the WS upgrade completes before the server's room lookup,
   * so connect() would "succeed" and then die 4001 (see the retry-storm
   * guard in _recreateSoloRoom).
   */
  async _resolveSoloRoomCode(res, requestedCode, isLaunchCurrent = null) {
    const body = await res.json().catch(() => null);
    if (isLaunchCurrent && !isLaunchCurrent()) return null;
    const confirmedCore = typeof body?.coreName === 'string'
      ? resolveArtifactCoreName(this.system?.id, body.coreName)
      : null;
    if (this.system?.id === 'genesis' && confirmedCore) this._roomCoreName = confirmedCore;
    if (typeof body?.code === 'string' && body.code) return body.code;
    console.warn('[solo] Room create returned an unexpected 2xx payload; using requested code');
    return requestedCode;
  }

  _failSoloRoomRegistration(existingCode = null) {
    this._soloRoomCode = null;
    if (this.system?.id === 'genesis') this._roomCoreName = null;
    if (existingCode && this.net?.connected && this.net.roomCode === existingCode) {
      // Network retires this socket before close delivery, so its delayed close
      // cannot be mistaken for a drop of a replacement solo connection.
      this.net.disconnect(4000);
    }
    this._updateInviteButtonVisibility?.();
    gameActions.refreshGameToolbarAvailability(this, 'solo-room');
    return false;
  }

  /**
   * Register an authenticated solo session, or refresh the existing owned room
   * during a safe cross-core reboot. Any failure degrades to a room-less solo
   * session; from then on the disconnect path (_recreateSoloRoom) owns retries
   * and backoff.
   */
  async _createInitialSoloRoom(user, {
    code: existingCode = null,
    saveCoreName = null,
    isLaunchCurrent = null,
    launchOwner = null,
  } = {}) {
    const setupOwner = {};
    this._initialSoloRoomSetupOwner = setupOwner;
    const ownsSetup = () => this._initialSoloRoomSetupOwner === setupOwner;
    const setupIsCurrent = () => ownsSetup()
      && (!isLaunchCurrent || isLaunchCurrent());
    const finishSetup = (result) => {
      if (ownsSetup()) this._initialSoloRoomSetupOwner = null;
      return result;
    };
    const cancelSetup = (sessionNet = null, roomCode = null) => {
      if (!ownsSetup()) return false;
      // The exit path may have disconnected before connect() had a socket to
      // close. If this exact setup subsequently connected, close it again.
      if (sessionNet && this.net === sessionNet && sessionNet.roomCode === roomCode) {
        sessionNet.disconnect(4000);
      }
      if (this.system?.id === 'genesis') this._roomCoreName = null;
      this._initialSoloRoomSetupOwner = null;
      return false;
    };

    if (!setupIsCurrent()) return cancelSetup();
    // BASIC gets a normal shareable code but remains a one-host solo room.
    const code = existingCode || (this._isBasicMode ? null : `solo:${user.username.toLowerCase()}`);
    if (this.system?.id === 'genesis') this._roomCoreName = null;
    try {
      const romTitle = this._bestDisplayTitle();
      const res = await fetch('/api/rooms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          system: this.system?.id || 'nes',
          code,
          basic: this._isBasicMode || undefined,
          romTitle,
          romHash: primaryHash(this.romHash) || undefined,
          coreName: this.system?.id === 'genesis' && isGenesis32xRom(this.romData)
            ? 'picodrive' : undefined,
          saveCoreName: saveCoreName || undefined,
        }),
        credentials: 'include',
      });
      if (!setupIsCurrent()) return cancelSetup();
      if (res.ok || res.status === 409) {
        let roomCode = code;
        if (res.ok) {
          roomCode = await this._resolveSoloRoomCode(res, code, setupIsCurrent);
          if (!setupIsCurrent()) return cancelSetup();
        } else {
          // Older/mixed replicas can still answer 409 for an owned room.
          // Fetch its authoritative metadata so the emulator does not fall
          // back to a stale client flag while reconnecting.
          const info = await fetch(`/api/rooms/${encodeURIComponent(code)}`, {
            credentials: 'include',
          }).catch(() => null);
          if (!setupIsCurrent()) return cancelSetup();
          if (info?.ok) {
            roomCode = await this._resolveSoloRoomCode(info, code, setupIsCurrent);
            if (!setupIsCurrent()) return cancelSetup();
          }
        }
        const requiredCoreName = this.system?.id === 'genesis' && isGenesis32xRom(this.romData)
          ? 'picodrive' : saveCoreName;
        if (this.system?.id === 'genesis' && requiredCoreName
            && this._roomCoreName !== requiredCoreName) {
          console.warn('[solo] Room did not confirm the cartridge/save core; continuing without a room');
          return finishSetup(this._failSoloRoomRegistration(existingCode));
        }
        if (!roomCode) return finishSetup(this._failSoloRoomRegistration(existingCode));
        const sessionNet = this.net;
        if (!(sessionNet?.connected && sessionNet.roomCode === roomCode)) {
          await sessionNet.connect(roomCode);
          if (!setupIsCurrent()) return cancelSetup(sessionNet, roomCode);
        }
        // Retained-room callbacks (notably an initial async baton pass) require
        // the room's predecessor launch, not merely a matching room code. A
        // normal authenticated solo room is created inside _startSolo rather
        // than room-flow, so publish that launch here once the socket is owned.
        this._roomJoinLaunchOwner = launchOwner;
        // Do not publish room metadata until the launch still owns the live
        // connection. Otherwise a late continuation can rewrite home history
        // and session state after teardown.
        this._soloRoomCode = roomCode;
        // Solo hosts reload through their authenticated resume route. The
        // shareable /r/<code> path is the viewer's entry point.
        window.history.replaceState({}, '', `/u/${encodeURIComponent(user.username)}/play`);
        try {
          sessionStorage.setItem('retroplay:soloSession', JSON.stringify({
            romHash: this.romHash,
            system: this.system.id,
            username: user.username,
            isMultiDisk: this._isMultiDisk || false,
            roomCode,
            romFilename: this._activeRomFilename || this.romName,
            ...(this._isBasicMode ? { isBasicMode: true } : {}),
          }));
        } catch {}
        // Upload ROM so spectators can download it
        if (this.romData) {
          const romData = this.romData, romName = this.romName;
          import('./modules/room-rom-payload.js').then(({ encodeRoomRomPayload }) => fetch(
            `/api/rooms/${encodeURIComponent(roomCode)}/rom?name=${encodeURIComponent(romName || 'game')}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/octet-stream' },
              body: encodeRoomRomPayload(romData),
            })).catch(() => {});
        }
        console.log(res.ok
          ? `[solo] Room created: ${roomCode}`
          : `[solo] Connected to existing room: ${roomCode}`);
        gameActions.refreshGameToolbarAvailability(this, 'solo-room');
        return finishSetup(true);
      } else {
        const err = await res.json().catch(() => ({}));
        if (!setupIsCurrent()) return cancelSetup();
        console.log(`[solo] Room creation failed: ${res.status}`, err.error || '');
        return finishSetup(this._failSoloRoomRegistration(existingCode));
      }
    } catch (e) {
      if (isConnectionCancellation(e)) return cancelSetup();
      if (!setupIsCurrent()) return cancelSetup();
      console.warn('[solo] Room setup error:', e);
      return finishSetup(this._failSoloRoomRegistration(existingCode));
    }
  }

  async _recreateSoloRoom() {
    const user = authState.getCurrentUser();
    if (!user?.username) {
      this._soloRoomCode = null;
      this._updateInviteButtonVisibility();
      gameActions.refreshGameToolbarAvailability(this, 'solo-room');
      return;
    }
    const launchOwner = this._gameLaunchIntentGeneration;
    const sessionGeneration = this._gameLaunchGeneration;
    const sessionNet = this.net;
    const expectedSoloRoomCode = this._soloRoomCode;
    const launchIsCurrent = () => (
      (launchOwner === undefined || this._isGameLaunchIntentCurrent(launchOwner))
      && this._gameLaunchGeneration === sessionGeneration
      && this.state === State.SOLO
      && this.net === sessionNet
      && this._soloRoomCode === expectedSoloRoomCode
    );

    // Rapid-cycle guard (see SOLO_ROOM_* constants): back off across
    // invocations when each recreated room dies within the stability
    // window, and stop entirely once the streak hits the cap. The streak
    // resets by itself when a recreate is old news (> STABLE_MS ago).
    if (this._soloRoomRecreatedAt
        && Date.now() - this._soloRoomRecreatedAt < SOLO_ROOM_STABLE_MS) {
      this._soloRoomRapidCycles = (this._soloRoomRapidCycles || 0) + 1;
    } else {
      this._soloRoomRapidCycles = 0;
    }
    if (this._soloRoomRapidCycles >= SOLO_ROOM_MAX_RAPID_CYCLES) {
      console.warn('[solo] Room keeps dropping right after recreation, giving up');
      this._soloRoomCode = null;
      this._updateInviteButtonVisibility();
      gameActions.refreshGameToolbarAvailability(this, 'solo-room');
      return;
    }
    if (this._soloRoomRapidCycles > 0) {
      const delay = Math.min(
        SOLO_ROOM_CYCLE_BASE_MS * 2 ** (this._soloRoomRapidCycles - 1),
        SOLO_ROOM_CYCLE_MAX_MS,
      );
      await new Promise(r => setTimeout(r, delay));
      if (!launchIsCurrent()) return;
    }

    const code = expectedSoloRoomCode || `solo:${user.username.toLowerCase()}`;
    const maxRetries = 3;
    const baseDelay = 2000;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      if (!launchIsCurrent()) return;

      try {
        const romTitle = this._bestDisplayTitle();
        const res = await fetch('/api/rooms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            system: this.system?.id || 'nes',
            code,
            basic: this._isBasicMode || undefined,
            romTitle,
            romHash: primaryHash(this.romHash) || undefined,
            saveCoreName: this.emu?.coreName || undefined,
          }),
          credentials: 'include',
        });
        if (!launchIsCurrent()) return;

        if (res.ok || res.status === 409) {
          // A permanently-bad 2xx payload must never read as a retriable
          // error or reach net.connect() as `undefined` — see
          // _resolveSoloRoomCode.
          const roomCode = res.ok ? await this._resolveSoloRoomCode(res, code) : code;
          if (!launchIsCurrent()) return;
          await sessionNet.connect(roomCode);
          if (!launchIsCurrent()) return;
          this._soloRoomCode = roomCode;
          if (this.romData) {
            let romBytes;
            if (this.romData instanceof ArrayBuffer) romBytes = new Uint8Array(this.romData);
            else if (typeof this.romData === 'string') {
              const u8 = new Uint8Array(this.romData.length);
              for (let i = 0; i < this.romData.length; i++) u8[i] = this.romData.charCodeAt(i);
              romBytes = u8;
            } else romBytes = this.romData;
            fetch(`/api/rooms/${encodeURIComponent(roomCode)}/rom?name=${encodeURIComponent(this.romName || 'game')}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/octet-stream' },
              body: romBytes,
            }).catch(() => {});
          }
          this._startRoomScreenshots();
          this._startSoloStream();
          this._updateInviteButtonVisibility();
          gameActions.refreshGameToolbarAvailability(this, 'solo-room');
          this._soloRoomRecreatedAt = Date.now();
          console.log(`[solo] Room recreated: ${roomCode} (attempt ${attempt})`);
          return;
        }
      } catch (e) {
        if (!launchIsCurrent() || isConnectionCancellation(e)) return;
        console.warn(`[solo] Room recreate attempt ${attempt} failed:`, e);
      }

      if (attempt < maxRetries) {
        await new Promise(r => setTimeout(r, baseDelay * attempt));
        if (!launchIsCurrent()) return;
      }
    }

    if (!launchIsCurrent()) return;
    console.warn('[solo] Room recreation failed after all retries');
    this._soloRoomCode = null;
    this._updateInviteButtonVisibility();
    gameActions.refreshGameToolbarAvailability(this, 'solo-room');
  }

  /**
   * Show the 👥 Invite button only when a solo room is live. Hidden in
   * netplay (a guest is already connected), in rollback mode, during
   * spectating, and for anonymous solo sessions (no room to share).
   * The game room passes its journaled row writer (M1, A5); classic callers
   * pass none and write directly (directRowWriter), as before.
   */
  _updateInviteButtonVisibility(writer = null) {
    // Every solo-room create, failure and recreate lands here (CH1).
    roomChat.syncRoomChatAvailability(this);
    const btn = document.getElementById('btn-invite');
    if (!btn) return;
    const write = writer || directRowWriter;
    const canInvite = this.state === State.SOLO && !!this._soloRoomCode;
    write.setHidden(btn, !canInvite);

    const endTurnBtn = document.getElementById('btn-end-turn');
    if (endTurnBtn) {
      const showEndTurn = this.state === State.SOLO && !!authState.getCurrentUser() && this.asyncGame?.isActive;
      write.setHidden(endTurnBtn, !showEndTurn);
    }
  }

  /** Key title authority to both the ROM's system and content hash. */
  _lobbyTitleCacheKey() {
    const system = this.system?.id;
    const hash = this.romHash;
    return system && hash ? JSON.stringify([system, hash]) : null;
  }

  /**
   * Refresh the game-title label beneath the Lobby heading. A title already
   * known for this system and ROM hash wins immediately; otherwise the ROM
   * display title stays until a games-table lookup succeeds.
   */
  _updateLobbyGameTitle() {
    const el = document.getElementById('lobby-game-title');
    if (!el) return;
    if (!this.romName) {
      el.textContent = '';
      this._lobbyTitleLookupKey = null;
      return;
    }
    const key = this._lobbyTitleCacheKey();
    const fallback = this._romDisplayTitle?.(this.romName, this.system?.id) || this.romName.replace(/\.[^.]+$/i, '');
    const cached = key && this._lobbyTitleCache?.get(key);
    if (cached) {
      this._canonicalTitle = cached;
      this._canonicalTitleKey = key;
    }
    el.textContent = cached || (this._canonicalTitleKey === key && this._canonicalTitle) || fallback;
    const sys = this.system?.id;
    const hash = this.romHash;
    if (!key || !sys || !hash) return;
    // Host assignment and ROM installation can both update the same label.
    if (this._lobbyTitleLookupKey === key) return;
    this._lobbyTitleLookupKey = key;
    fetch(`/api/games/lookup?system=${encodeURIComponent(sys)}&hash=${encodeURIComponent(hash)}`)
      .then(r => r.ok ? r.json() : null)
      .then(body => {
        const title = typeof body?.title === 'string' ? body.title.trim() : '';
        if (!title) {
          if (this._lobbyTitleLookupKey === key) this._lobbyTitleLookupKey = null;
          return;
        }
        (this._lobbyTitleCache ??= new Map()).set(key, title);
        // A late response may fill the cache, but cannot relabel another ROM.
        if (this._lobbyTitleCacheKey() !== key) return;
        this._canonicalTitle = title;
        this._canonicalTitleKey = key;
        if (el.isConnected) el.textContent = title;
      })
      .catch(() => {
        if (this._lobbyTitleLookupKey === key) this._lobbyTitleLookupKey = null;
        // Keep the current display title on a transient lookup failure.
      });
  }

  /**
   * Show the play-mode prompt (Solo / Create Room) when the user has just
   * loaded a ROM from the menu. Skipped if they're already in a room or
   * mid-game — those flows have their own next-step affordances.
   */
  /**
   * The restorable play mode of the current session, baked into saves so a
   * save can reopen in the same mode without re-prompting. Only Solo and a
   * hosted netplay room are restorable; everything else (side-by-side, async,
   * guest/spectator, matchmaking) returns null → the play-mode modal still
   * shows on load.
   */
  _currentPlayMode() {
    // Host of a live netplay room → restorable as a fresh host room.
    if ((this.rollback || this.lockstep)
        && this.net?.playerNumber === 1 && !this.net?.spectator && !this._sbsRoom) {
      return 'room';
    }
    // Plain solo play (not a side-by-side race, not an async challenge).
    if (this.state === State.SOLO && !this._sbsRoom
        && !String(this._soloRoomCode || '').startsWith('async:')) {
      return 'solo';
    }
    return null;
  }

  _runPlayIntentMode(mode) {
    // These existing starters use undefined for both success and early abort.
    // Solo has a live game state to verify; room/sheet starters report true
    // only after their connection or sheet is established.
    const complete = (started, ready) => {
      const owner = this._gameLaunchIntentGeneration;
      return Promise.resolve(started).then(result => result !== false
        && this._gameLaunchIntentGeneration === owner && ready(result));
    };
    const soloReady = () => this.state === State.SOLO
      && this._activeScreenName === 'game' && this.running === true && !!this.emu;
    if (mode === 'solo') return complete(this._startSolo(), soloReady);
    if (mode === 'match') return complete(this._findMatch(), result => result === true);
    if (mode === 'room') return complete(this._createRoom(), result => result === true);
    if (mode === 'multitap') return complete(this._createRoom({ multitap: true }), result => result === true);
    if (mode === 'schedule') return complete(this._openScheduleMatchPanel(), result => result === true);
    if (mode === 'sbs') return complete(this._createSideBySideRoom(), result => result === true);
    if (mode === 'async') {
      const started = this._startSolo();
      const owner = this._gameLaunchIntentGeneration;
      const pending = { owner };
      this._pendingAsyncChallenge = pending;
      const ownsChallenge = () => this._gameLaunchIntentGeneration === owner
        && this._pendingAsyncChallenge === pending;
      return complete(started, soloReady).then(async ok => {
        if (!ok || !ownsChallenge() || !this.asyncGame?.showChallengePicker) return false;
        const opened = await this.asyncGame.showChallengePicker('manual',
          { isLaunchCurrent: ownsChallenge });
        return opened === true && ownsChallenge();
      }).finally(() => {
        if (this._pendingAsyncChallenge === pending) this._pendingAsyncChallenge = null;
      });
    }
    return false;
  }

  _isPlaySelectionCurrent({ owner, source, filename, hash, system }) {
    return owner === (this._gameLaunchIntentGeneration || 0)
      && this.romData === source
      && this._activeRomFilename === filename
      && this.romHash === hash
      && this.system?.id === system;
  }

  async _choosePlayIntentMode(mode, opener) {
    if (!this.romData) return false;
    const selectedAt = performance.now();
    const deadlineAt = selectedAt + 20_000;
    const expiresAt = selectedAt + 600_000;
    const selected = {
      owner: this._gameLaunchIntentGeneration || 0,
      source: this.romData,
      filename: this._activeRomFilename,
      hash: this.romHash,
      system: this.system?.id,
    };
    const { source, filename, hash, system } = selected;
    if (!authState.isLoaded()) {
      const controller = new AbortController();
      let timer;
      const timeout = new Promise(resolve => {
        timer = setTimeout(() => { controller.abort(); resolve(false); },
          Math.max(0, deadlineAt - performance.now()));
      });
      try {
        await Promise.race([authState.refreshSession({ signal: controller.signal })
          .then(() => true, () => false), timeout]);
      } finally { clearTimeout(timer); }
    }
    if (performance.now() >= deadlineAt) { showPlayIntentRetry('timeout'); return false; }
    if (!this._isPlaySelectionCurrent(selected)) return false;
    const title = String(this._canonicalTitle || this._romDisplayTitle?.(this.romName, system)
      || this.romName || filename || 'this game').replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, 512);
    if (mode === 'multitap' && !authState.isAuthenticated()) {
      const qualified = await qualifiedMultitapChoice(system, source, moduleRegistry.getFeatures());
      if (!this._isPlaySelectionCurrent(selected) || performance.now() >= deadlineAt) return false;
      if (qualified?.title) {
        cancelPlayIntent('canceled');
        return this._runPlayIntentMode(mode);
      }
    }
    if (authState.isAuthenticated() || !this._activeRomFilename) {
      cancelPlayIntent('canceled');
      return requireAuthToPlay(() => this._runPlayIntentMode(mode), {
        isCurrent: () => this._isPlaySelectionCurrent(selected), deadlineAt, expiresAt,
        playIntent: { title, mode },
      });
    }
    if (!filename || !hash || !system || !title) return false;
    const selection = { system, filename, hash, title, mode };
    if (system === 'c64' && this._isMultiDisk) {
      try { selection.content_digest = await digestC64Bundle(source, { deadlineAt }); }
      catch { this._flash('Unable to verify game disks. Try Play again.'); return false; }
    }
    if (performance.now() >= deadlineAt) { showPlayIntentRetry('timeout'); return false; }
    if (!this._isPlaySelectionCurrent(selected)) return false;
    return beginPlayIntent(selection, { opener, deadlineAt });
  }

  async _validatePlayIntentSelection(selection, { signal, deadlineAt } = {}) {
    const launchNamespace = currentSaveDBNamespace();
    const owner = this._gameLaunchIntentGeneration || 0;
    const isCurrent = () => owner === (this._gameLaunchIntentGeneration || 0);
    const selectionChanged = () => Object.assign(new Error('Game selection changed'),
      { code: 'selection_changed' });
    if (!SYSTEMS[selection.system]) {
      throw Object.assign(new Error('Selected system unavailable'), { code: 'invalid_selection' });
    }
    if (!isCurrent()) throw selectionChanged();
    if (signal?.aborted) return false;
    // A fresh server snapshot is required here: the ordinary module registry
    // retains old values when its best-effort refresh fails.
    let flags;
    try {
      const response = await fetch('/api/admin/features', {
        credentials: 'include', cache: 'no-store', signal,
      });
      if (!response.ok) throw Object.assign(new Error('Feature status unavailable'), { code: 'intent_unavailable' });
      flags = await response.json();
    } catch (error) {
      if (!isCurrent()) throw selectionChanged();
      if (signal?.aborted) return false;
      throw Object.assign(new Error('Feature status unavailable', { cause: error }), { code: 'intent_unavailable' });
    }
    if (!isCurrent()) throw selectionChanged();
    if (signal?.aborted) return false;
    if (!flags || typeof flags !== 'object') {
      throw Object.assign(new Error('Feature status unavailable'), { code: 'intent_unavailable' });
    }
    if (flags[`system:${selection.system}`] !== true) {
      throw Object.assign(new Error('Selected system unavailable'), { code: 'invalid_selection' });
    }
    if (selection.mode === 'multitap' && (selection.system !== 'snes'
        || !['snes_multitap_netplay', 'lockstep_netplay', 'lockstep:snes', 'system:snes'].every(key => flags[key] === true))) {
      throw Object.assign(new Error('Selected mode unavailable'), { code: 'invalid_selection' });
    }
    if (selection.mode === 'sbs' && flags.side_by_side !== true
        || selection.mode === 'async' && flags.async_multiplayer !== true
        || selection.mode === 'schedule' && flags.play_invites_scheduled !== true) {
      throw Object.assign(new Error('Selected mode unavailable'), { code: 'invalid_selection' });
    }
    let prepared;
    try {
      prepared = await revalidatePlayRom(selection, { signal, isCurrent, deadlineAt,
        catalogLookup: c64CatalogLookup(this), waitForCatalog: c64CatalogWait(this, { deadlineAt }) });
    }
    catch (error) {
      if (!isCurrent()) throw selectionChanged();
      throw error;
    }
    if (!isCurrent()) throw selectionChanged();
    if (!prepared || signal?.aborted) return false;
    if (prepared.identity) {
      await finishC64LaunchIdentity(this, prepared.romData, prepared.identity, { expectedNamespace: launchNamespace });
      if (!isCurrent()) throw selectionChanged();
      if (signal?.aborted) return false;
    }
    if (selection.mode === 'multitap') {
      const qualified = await qualifiedMultitapChoice(prepared.system, prepared.romData, flags);
      if (!isCurrent()) throw selectionChanged();
      if (signal?.aborted) return false;
      if (!qualified) throw Object.assign(new Error('Selected cartridge unavailable'), { code: 'invalid_selection' });
    }
    if (prepared.kind === 'bundle') {
      try {
        prepared.romDataB64 = await encodeC64BundleForLaunch(prepared.romData, { signal, deadlineAt });
      } catch (error) {
        if (!isCurrent()) throw selectionChanged();
        throw error;
      }
      if (!isCurrent()) throw selectionChanged();
      if (signal?.aborted) return false;
    }
    return { ...prepared, owner };
  }

  _launchPlayIntentSelection(prepared, { deadlineAt } = {}) {
    if (!prepared?.romData || prepared.owner !== (this._gameLaunchIntentGeneration || 0)
        || !SYSTEMS[prepared.system] || deadlineAt != null && performance.now() >= deadlineAt) return false;
    const launchOwner = this._beginGameLaunchIntent({ clearPendingSave: true });
    // The redeemed continuation owns this system change. Canceling the play
    // intent here would invalidate the launch owner we just established.
    if (this.system?.id !== prepared.system
        && !this._applySystemState(prepared.system, { preservePlayIntent: true })) return false;
    this._activeRomFilename = prepared.filename;
    if (prepared.kind === 'bundle') {
      this.romData = prepared.romData;
      if (!prepared.romDataB64) return false;
      this._romDataB64 = prepared.romDataB64;
      if (deadlineAt != null && performance.now() >= deadlineAt) return false;
      this.romName = prepared.romName;
      // A launch without a resolved identity must never keep the previous
      // bundle's verified identity, even when its hash happens to match.
      if (prepared.identity) commitC64Identity(this, prepared.identity);
      else { this.romHash = prepared.hash; this._c64Identity = null; }
      this._isMultiDisk = true;
      this._resetCanonicalTitle(prepared.title);
      const el = document.getElementById('rom-name');
      if (el) { el.textContent = `${prepared.filename} (${prepared.romData.disks.length} disks)`; el.classList.remove('hidden'); }
      this._updateLobbyGameTitle();
    } else {
      this._setRom(prepared.romData, prepared.romName, {
        preserveLaunchIntent: true, suppressPlayModePrompt: true, title: prepared.title,
      });
    }
    if (deadlineAt != null && performance.now() >= deadlineAt) return false;
    if (!this._isGameLaunchOwnerCurrent(launchOwner)) return false;
    return this._runPlayIntentMode(prepared.mode);
  }

  _abortPlayIntentLaunch(owner) {
    if (!this._isGameLaunchOwnerCurrent(owner)) return false;
    if (this._saveConflictPickerOwner === owner) this._saveConflictPickerAbort?.abort();
    this._beginGameLaunchIntent({ clearPendingSave: true });
    this._prepareFreshSaveLaunch();
    return true;
  }

  _maybePromptPlayMode(romName) {
    if (this._suppressPlayModePrompt) return;
    if (this.state !== State.MENU) return;
    // When booting straight into replay playback or a ghost race
    // (/?replay= or /?ghost=), the ROM loads but we auto-launch — there's no
    // mode to choose, so don't flash the "How do you want to play?" modal.
    if (this._pendingReplayId || this._pendingGhostId) return;
    const modal = document.getElementById('play-mode-modal');
    if (!modal) return;
    const opener = document.activeElement;
    this._playModeOpener = opener instanceof HTMLElement && opener.isConnected
      && opener.getClientRects().length && !modal.contains(opener) ? opener : null;
    const romEl = document.getElementById('play-mode-modal-rom');
    if (romEl) {
      const display = this._canonicalTitle
        || this._romDisplayTitle?.(romName, this.system?.id)
        || romName.replace(/\.[^.]+$/i, '');
      romEl.textContent = display;
    }
    const asyncBtn = document.getElementById('btn-play-mode-async');
    if (asyncBtn) {
      const signedIn = !!authState.getCurrentUser();
      asyncBtn.classList.toggle('hidden', !signedIn || !this._asyncEnabled);
    }
    const sbsBtn = document.getElementById('btn-play-mode-sbs');
    if (sbsBtn) sbsBtn.classList.toggle('hidden', !this._sideBySideEnabled);
    const suggestBtn = document.getElementById('btn-play-mode-suggest');
    if (suggestBtn) {
      const signedIn = !!authState.getCurrentUser();
      suggestBtn.classList.toggle('hidden', !signedIn || !this._suggestEnabled());
    }
    const scheduleBtn = document.getElementById('btn-play-mode-schedule');
    if (scheduleBtn) {
      const signedIn = !!authState.getCurrentUser();
      // Gated on the same flag as the Friends card's SCHEDULE button.
      const scheduleOn = moduleRegistry.getFeatures().play_invites_scheduled === true;
      scheduleBtn.classList.toggle('hidden', !signedIn || !scheduleOn);
    }
    this._renderSoloSaveMode();
    modal.classList.remove('hidden');
  }

  /** Genesis keeps its save-mode pill; Atari now gets both capabilities. */
  _renderSoloSaveMode() {
    const wrap = document.getElementById('solo-save-mode');
    if (!wrap) return;
    const sys = this.system?.id;
    const clownGenesis = sys === 'genesis' && resolveCoreName(this.system) === 'clownmdemu';
    const show = sys === 'genesis'
      && !clownGenesis
      && this._replayModeAvailable();
    wrap.classList.toggle('hidden', !show);
    if (!show) return;
    this._soloModeOverride = null;
    const mode = this._savedSaveMode(sys);
    for (const seg of wrap.querySelectorAll('.solo-save-mode-seg')) {
      const on = seg.dataset.mode === mode;
      seg.classList.toggle('active', on);
      seg.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  }

  _closePlayModePrompt() {
    document.getElementById('play-mode-modal')?.classList.add('hidden');
  }

  /**
   * Show the lobby's Invite a Friend button only while the host is in the
   * Lobby with an empty player seat. Hidden for spectators, anonymous users
   * (no friends list), and once the room has reached its system capacity.
   */
  _updateLobbyInviteButtonVisibility() {
    const btn = document.getElementById('btn-lobby-invite-friend');
    if (!btn) return;
    const isHost = this.net?.playerNumber === 1 && !this.net?.spectator;
    const signedIn = !!authState.getCurrentUser();
    const hasOpenSlot = this._lobbySlotOccupied?.length
      ? this._lobbySlotOccupied.some(occupied => !occupied)
      : !this._lobbyPeerJoined;
    const canInvite =
      this.state === State.LOBBY &&
      !!this._lobbyRoomCode &&
      isHost &&
      hasOpenSlot &&
      signedIn;
    btn.classList.toggle('hidden', !canInvite);
  }

  _soloLoop(now) {
    if (!this.running) return;
    this.animFrameId = requestAnimationFrame((t) => this._soloLoop(t));

    const delta = Math.min(now - this.lastFrameTime, this.targetFrameTime * 2);
    this.lastFrameTime = now;
    this.frameAccumulator += delta;

    while (this.frameAccumulator >= this.targetFrameTime) {
      // The author pause (spec 8.2, SNES amendment) holds JS-stepped NES and SNES here: its own
      // flag, because _gameplayHeld() also refuses resets and in-place loads.
      if (this._gameplayHeld() || authoringHost.authorHoldActive(this)) {
        this.frameAccumulator = 0;
        break;
      }
      // A frame that suspended mid-step (audio backpressure) completes
      // asynchronously; stepping again before it lands corrupts the Asyncify
      // state (the GB "~frame 18" wall). Shed the accumulated time instead of
      // carrying it — a catch-up burst into a still-suspended core is the
      // exact re-entrancy this guard exists to prevent. Cores without the
      // completed-frame counter always report ready (no behavior change).
      if (typeof this.emu.stepReady === 'function' && !this.emu.stepReady()) {
        this.frameAccumulator = 0;
        break;
      }
      const bitmask = this.input.poll();
      // Observe BEFORE incrementing soloFrame — the current bitmask drives
      // the step from frame N (this.soloFrame) to N+1. Same convention the
      // lockstep recorder uses.
      if (this.replayRecorder?.isRecording) {
        if (this.replayRecorder.observe(this.soloFrame, bitmask, 0) === false) {
          this._replayRecordingUnavailable('replay_capacity_exceeded');
        }
      }
      // Do not send this frame through the room WebSocket. Solo, async-baton,
      // and side-by-side viewers receive WebRTC media from SoloStreamHost.
      // Room-WS `input` remains exclusively in rollback's _startNetplay path;
      // lockstep and hosted modes use their own peer DataChannels.
      this.emu.setButtons(0, bitmask);
      this.emu.stepFrame();
      this.soloFrame++;
      this._trackFunnelFirstFrame(); // idempotent — one boolean check per frame
      this._maybeCaptureRewindSnapshot();
      this.cheevosEngine.tick(this.soloFrame);
      this.asyncGame.tick(this.soloFrame);
      this._tickFps();
      this.frameAccumulator -= this.targetFrameTime;
    }
  }

  _hostedHostLoop(now) {
    if (!this.running) return;
    this.animFrameId = requestAnimationFrame((t) => this._hostedHostLoop(t));

    const delta = Math.min(now - this.lastFrameTime, this.targetFrameTime * 2);
    this.lastFrameTime = now;
    this.frameAccumulator += delta;

    while (this.frameAccumulator >= this.targetFrameTime) {
      const bitmask = this.input.poll();
      if (this.inputParity) {
        this.inputParity.submitP1(bitmask);
        this.inputParity.tick();
      } else {
        this.emu.setButtons(0, bitmask);
        this.emu.setButtons(1, this._hostedP2Bitmask);
      }
      this.emu.stepFrame();
      this.hostedHostFrame++;
      this._trackFunnelFirstFrame();
      this.cheevosEngine.tick(this.hostedHostFrame);
      this._tickFps();
      this.frameAccumulator -= this.targetFrameTime;
    }
  }

  _hostedP1InputLoop() {
    if (!this.running || (!this.hostedNetplay && !this.hostedMultiPeer)) return;
    requestAnimationFrame(() => this._hostedP1InputLoop());
    // Feed physical and touch controllers into the hosted input path. C64
    // deliberately excludes keyboard directions because VICE owns the full
    // keyboard while the parity coordinator updates virtual gamepads.
    const managerMode = this.input?._useManager?.() === true;
    const gameplayBlocked = this._gameplayInputLeases?.active || this.input?._gameplayBlocked?.();
    const nativeKeyboardSystem = this.system?.id === 'n64' || this.system?.id === 'c64';
    const arrows = managerMode || nativeKeyboardSystem ? 0 : this.input.pollArrowKeys();
    const stableGamepad = managerMode ? 0 : this.input.pollGamepadStable();
    let bitmask = gameplayBlocked ? 0 : stableGamepad | this.input.touchButtons | arrows;
    // flag-ON (task 4.1): touch drives gameplay ONLY through InputManager's
    // arm/seat/taint-gated output — swap the raw touchButtons OR for the gated
    // read (no double input). Reuses the single pollGamepadStable() read above.
    if (managerMode) bitmask = gameplayBlocked ? 0 : this.input.poll();
    if (this._isMultiPeerHost && this.system?.hasAnalog && this.virtualPads) {
      const { analogX, analogY } = gameplayBlocked
        ? { analogX: 0, analogY: 0 } : this.input.pollAnalog();
      this.virtualPads.setPlayer(0, { analogX, analogY });
    }
    if (this.inputParity) {
      // Libretro cores self-drive frames — the parity coordinator's
      // tick() is paced by the 60Hz _cheevosTickInterval, not RAF.
      // Here we just feed the current gamepad/touch state in.
      this.inputParity.submitP1(bitmask);
    } else if (this.system?.id === 'c64' && this.virtualPads) {
      this.virtualPads.setC64Player(0, { bitmask });
      this._p1PrevBitmask = bitmask;
    } else if (bitmask !== this._p1PrevBitmask) {
      this.emu.injectP1Input(bitmask, this._p1PrevBitmask);
      this._p1PrevBitmask = bitmask;
    }
  }

  /**
   * Solo-mode input injection for self-driving libretro cores.
   * Mirrors _hostedP1InputLoop but runs outside hosted netplay.
   * Most cores receive controller bitmasks as synthetic key events. C64 is
   * the exception: its keyboard stays native while controller input updates
   * a virtual standard gamepad.
   */
  _libretroInputLoop(sessionEmu = this.emu) {
    if (!this.running || this.hostedNetplay || this.hostedMultiPeer) return;
    // Session-identity guard — same rationale as _selfDrivingRecordLoop's:
    // a mid-session relaunch swaps/nulls this.emu with `running` still true.
    // Without it the stale loop throws on the destroyed emulator (injectP1Input
    // of null) or double-drives input/cheevos ticks against the new session.
    if (!sessionEmu || this.emu !== sessionEmu) return;
    requestAnimationFrame(() => this._libretroInputLoop(sessionEmu));
    // GBA link-cable input reaches the core as raw poll() bitmasks (the
    // lockstep engine's getLocalInput), so the gamepad→keyboard injection
    // below must stay off there: P1_KEY_MAP uses retroarch.cfg's binds
    // (KeyJ/KeyK = RetroPad B/A — crossed relative to InputHandler's keymap)
    // and the fake keydowns land in InputHandler's captured listener, giving
    // every pad press a phantom second button (pad A arrives as A+B). The
    // loop itself keeps running for the cheevos/async/autosave ticks below.
    // GB link keeps the injection — its self-driving cores read native
    // RetroArch binds, not poll().
    const gbaLinkCable = this._netMode === 'link-cable' && this.system?.id === 'gba';
    const managerMode = this.input?._useManager?.() === true;
    const gameplayBlocked = this._gameplayInputLeases?.active || this.input?._gameplayBlocked?.();
    if (this.system?.id === 'c64' && this.virtualPads) {
      const stableGamepad = managerMode ? 0 : this.input.pollGamepadStable();
      let bitmask = gameplayBlocked ? 0 : stableGamepad | this.input.touchButtons;
      // flag-ON (task 4.1): route touch through InputManager's gated output.
      if (managerMode) bitmask = gameplayBlocked ? 0 : this.input.poll();
      this.virtualPads.setC64Player(0, { bitmask });
      this._p1PrevBitmask = bitmask;
    } else if (!gbaLinkCable) {
      // N64 binds the arrow keys natively to the D-pad (WASD drives the analog
      // stick), so skip arrow injection there — injecting them maps Up→KeyW,
      // which is now an analog axis, and would double-drive movement.
      const arrows = managerMode || this.system?.id === 'n64' ? 0 : this.input.pollArrowKeys();
      // Solo GB lets RetroArch poll the physical pad natively. Sending the
      // same pad edge through injectP1Input would both double-drive input and
      // re-enter lean Gambatte through an Emscripten dynCall while Asyncify may
      // be suspended (the iPad signature-mismatch crash). Touch and arrows
      // still use keyboard injection. Hosted/link-cable cores never set this
      // flag and retain the existing stable-pad injection path.
      const gamepad = managerMode || sessionEmu.nativeGamepadInput ? 0 : this.input.pollGamepadStable();
      let bitmask = gameplayBlocked ? 0 : gamepad | this.input.touchButtons | arrows;
      // flag-ON (task 4.1): touch drives gameplay ONLY through InputManager's
      // arm/seat/taint-gated output — swap the raw touchButtons OR (no double
      // input). Reuses the single gamepad read above.
      if (managerMode) bitmask = gameplayBlocked ? 0 : this.input.poll();
      if (bitmask !== this._p1PrevBitmask) {
        this.emu.injectP1Input(bitmask, this._p1PrevBitmask);
        this._p1PrevBitmask = bitmask;
      }
      // Solo N64: feed the analog stick into virtual slot 0 so RetroArch reads
      // it proportionally via the P1 axis binds in retroarch.cfg. The on-screen
      // touch stick takes precedence over a real controller's left stick (it's
      // the active control on mobile). Buttons/C-buttons/D-pad stay on the
      // keyboard-injection path above.
      if (this.system?.id === 'n64' && this.virtualPads) {
        // flag-ON (task 4.1): the touch stick reads through InputManager's
        // arm/taint-gated analog output; flag-OFF it is the raw joystickAxes
        // (byte-identical). The gamepad-stick fallback below is unchanged.
        const j = gameplayBlocked ? { x: 0, y: 0, analogX: 0, analogY: 0 }
          : managerMode ? this.input.pollAnalog() : this.input.joystickAxes;
        const touchActive = j.x !== 0 || j.y !== 0;
        const gp = gameplayBlocked ? null : this.virtualPads.getRealGamepads().find((p) => p);
        if (!gameplayBlocked && this._n64AnalogAwaitNeutral
            && (!gp || (Math.abs(gp.axes?.[0] ?? 0) < 0.15
              && Math.abs(gp.axes?.[1] ?? 0) < 0.15))) {
          this._n64AnalogAwaitNeutral = false;
        }
        const physicalAxesReady = !this._n64AnalogAwaitNeutral;
        const analogX = gameplayBlocked ? 0 : managerMode ? j.analogX
          : (touchActive ? j.x : (physicalAxesReady ? gp?.axes?.[0] ?? 0 : 0));
        const analogY = gameplayBlocked ? 0 : managerMode ? j.analogY
          : (touchActive ? j.y : (physicalAxesReady ? gp?.axes?.[1] ?? 0 : 0));
        this.virtualPads.setPlayer(0, { analogX, analogY });
      }
    }
    this._libretroSoloFrame++;
    // C64 evaluates at a fixed active-time cadence, not per rAF (c64-tracking.js).
    // Under the author pause the C64 clock stops (c64MachineAdvancing); other self-driving cores
    // keep this loop running under _cmd_pause, so the tick is skipped: drafts never count hits
    // on frozen memory (spec 8.2).
    if (this.system?.id === 'c64') tickC64(this, performance.now());
    else if (!authoringHost.authorHoldActive(this)) this.cheevosEngine.tick(this._libretroSoloFrame);
    this.asyncGame.tick(this._libretroSoloFrame);
    if (this._autoSavePending) {
      this._autoSavePending = false;
      this._autoSave();
    }
  }

  /**
   * Await _initReplayRecorder() and, if the session is still the one that
   * requested it, start _selfDrivingRecordLoop(). `emuAtArm` is `this.emu`
   * captured by the caller BEFORE the await — _initReplayRecorder does a
   * lazy dynamic import on first use, so a quick exit+relaunch during that
   * gap can swap this.emu out from under us (_createEmulator always
   * constructs a fresh instance; see its comment). If that happened, the
   * recorder _initReplayRecorder just built belongs to the stale session —
   * starting the loop would record the NEW session with the OLD session's
   * already-resolved locals, and leaving the recorder in place would light
   * the REC button over a loop that never runs (the same zombie-recorder
   * shape as the save-resumed/async-game exclusions above). Tear it down and
   * bail instead. Mirrors the LiveReplaySession superseded-session guards
   * (compare a captured reference to the live property after an await).
   */
  async _armSelfDrivingRecordLoop(emuAtArm) {
    try { await this._initReplayRecorder(); }
    catch (err) {
      console.warn('[replay] self-driving recorder arm failed:', err);
      if (this.emu === emuAtArm) this._replayRecordingUnavailable('selfdriving_recorder_arm');
      return false;
    }
    if (this.emu !== emuAtArm || !this.replayRecorder?.isRecording) {
      this._destroyReplayRecorder();
      return false;
    }
    // A Saves-panel re-arm happens while the session's loop is still running —
    // never start a second one (it would double-drain the ring).
    if (this._recordLoopEmu !== this.emu) this._selfDrivingRecordLoop();
    return true;
  }

  /** Drain the input-log ring into the recorder (one tick). Extracted for tests. */
  _drainRecordRing() {
    const n64 = this.system?.id === 'n64';
    const drain = n64 ? this.emu?.drainN64InputLog : this.emu?.drainInputLog;
    if (!drain) return;
    let cursor, entries;
    try { ({ cursor, entries } = drain.call(this.emu, this._recordDrainCursor | 0)); }
    catch {
      this._replayRecordingUnavailable('replay_ring_overrun');
      return;
    }
    const offset = this._recordFrameOffset | 0;
    for (const e of entries) {
      if (!this.replayRecorder?.isRecording) continue;
      let accepted;
      if (n64) {
        accepted = this.replayRecorder.observe(offset + e.frame, e.buttons, e.analogX, e.analogY);
      } else {
        accepted = this.replayRecorder.observe(offset + e.frame, e.p1, e.p2);
      }
      if (accepted === false) this._replayRecordingUnavailable('replay_capacity_exceeded');
    }
    this._recordDrainCursor = cursor;
    this._libretroSoloFrame = offset + cursor;
    this.soloFrame = this._libretroSoloFrame;
  }

  /** True while Atari is using the combined self-driving record/save path. */
  _isAtariCombinedReplaySession() {
    return this.system?.id === 'atari2600'
      && this.emu?.selfDriving === true
      && this._soloRecordMode === true
      && this.replayRecorder?.isRecording === true
      && moduleRegistry.getFeatures().atari_genesis_replay_saves === true;
  }

  /** True while a self-driving core (GB/Atari/N64) records through the input-log
   *  ring and this session's drain loop owns the live emulator. A replay capture
   *  must drain the ring first: the core may have run frames the loop has not yet
   *  pulled into the recorder, and the live fingerprint already describes them. */
  _isSelfDrivingRingRecordSession() {
    return SELF_DRIVING_RECORD_SYSTEMS.has(this.system?.id)
      && !!this.emu
      && this._recordLoopEmu === this.emu
      && this._replayPlaybackActive !== true
      && this.replayRecorder?.isRecording === true;
  }

  /** Capture one atomic Atari state + replay-prefix checkpoint. */
  async _captureAtariReplayCheckpoint(opts = {}) {
    const { captureAtariReplayCheckpoint } = await import('./replay-recorder.js');
    return captureAtariReplayCheckpoint(this, opts);
  }

  async _withStatePumpLock(sessionEmu, operation, opts = {}) {
    const lease = replayBoundary.retainMonotonicDeadline(this, opts.deadline);
    const watchdog = replayBoundary.deadlineSignal(lease.deadline, opts.signal);
    try {
      const imported = await replayBoundary.raceDeadline(this._loadStatePumpModule(), lease.deadline);
      if (imported.timedOut || watchdog.signal.aborted) {
        watchdog.abort();
        throw new replayBoundary.StatePumpLockTimeoutError('queued');
      }
      if (imported.error) throw imported.error;
      return await imported.value.withStatePumpLock(this, sessionEmu, operation, { ...opts, signal: watchdog.signal });
    } finally { watchdog.dispose(); lease.release(); }
  }
  _loadStatePumpModule() { return import('./replay-recorder.js'); }

  /** Input + record loop for a self-driving GB/Atari/N64 session. */
  _selfDrivingRecordLoop(sessionEmu = this.emu) {
    if (!this.running || this.hostedNetplay || this.hostedMultiPeer) {
      if (this._recordLoopEmu === sessionEmu) this._recordLoopEmu = null;
      return;
    }
    // Replay playback owns the input override (the core-side playback buffer
    // only writes at change-points, so a gamepad push here would poison the
    // held masks between events). The playback player drains the ring itself.
    if (this._replayPlaybackActive) {
      if (this._recordLoopEmu === sessionEmu) this._recordLoopEmu = null;
      return;
    }
    // Session-identity guard on the RUNNING loop (the arm-time guard in
    // _armSelfDrivingRecordLoop only covers the await before the first iteration).
    // A mid-session relaunch — a room join from an invite toast, a replay-
    // derived-save fast-forward — swaps or nulls this.emu while `running` is
    // still true, and the already-scheduled rAF iteration then fires against
    // the dead session. The next frame is scheduled BEFORE the dereference
    // below, so without this check a destroyed emulator is an uncaught
    // "setOverrideInput of null" every frame, forever (the 2026-07-06 GB
    // link-cable join incident), and a swapped one gets this session's input
    // override pushed into the NEW session's core.
    if (!sessionEmu || this.emu !== sessionEmu) {
      // Clear the one-loop-per-session marker ONLY when it is still ours: a
      // stale loop from a replaced core must never erase the live loop's marker
      // (spec §5) — that would let a Saves-panel re-arm start a duplicate.
      if (this._recordLoopEmu === sessionEmu) this._recordLoopEmu = null;
      return;
    }
    // Confirmed live: claim the session's single self-driving record loop so a
    // re-arm (_armSelfDrivingRecordLoop) never starts a second, ring-draining one.
    this._recordLoopEmu = sessionEmu;
    requestAnimationFrame(() => this._selfDrivingRecordLoop(sessionEmu));
    if (this._stateCapturePending || this._replayBoundaryOwner) return;
    if (this.system?.id === 'n64') {
      const { analogX, analogY } = this.input.pollAnalog();
      sessionEmu.setN64OverrideInput(
        this.input.poll(),
        normalizedAxisToInt16(analogX),
        normalizedAxisToInt16(analogY),
      );
    } else {
      sessionEmu.setOverrideInput(this.input.poll());
    }
    this._drainRecordRing();
    this.cheevosEngine.tick(this._libretroSoloFrame);
    this.asyncGame.tick(this._libretroSoloFrame);
    // The opt-in clean-boot clip prefix is at most 90 seconds plus audio lookahead.
    // Defer its first scheduled save until that boundary; explicit saves still work.
    const clipPrefix = sessionEmu.readN64ClipProvenance?.();
    if (this._autoSavePending && (!clipPrefix || clipPrefix.closed)) {
      this._autoSavePending = false; this._autoSave();
    }
  }

  // ── Input Parity (Tier 2 hosted fairness) ───────────────

  _loadInputParityEnabled() {
    const raw = localStorage.getItem('retroplay-input-parity');
    // Default ON — the whole point of Tier 2 is fair latency by default.
    return raw === null ? true : raw === '1';
  }

  _saveInputParityEnabled(enabled) {
    localStorage.setItem('retroplay-input-parity', enabled ? '1' : '0');
  }

  setInputParityEnabled(enabled) {
    this._inputParityEnabled = !!enabled;
    this._saveInputParityEnabled(this._inputParityEnabled);
    if (this.inputParity) this.inputParity.setEnabled(this._inputParityEnabled);
    this._renderParityHud();
  }

  _createInputParityCoordinator() {
    const isLibretro = this.emu?.selfDriving && this.emu instanceof LibretroCore;
    const c64VirtualPads = isLibretro && this.system?.id === 'c64' && this.virtualPads;
    const initialDelay = computeTargetDelay(this.net?.latency || 0);
    const coord = new InputParityCoordinator({
      initialDelay,
      onP1Apply: c64VirtualPads
        ? (mask) => { this.virtualPads?.setC64Player(0, { bitmask: mask }); }
        : isLibretro
        ? (mask, prev) => { if (mask !== prev) this.emu.injectP1Input(mask, prev); }
        : (mask) => { this.emu.setButtons(0, mask); },
      onP2Apply: c64VirtualPads
        ? (mask) => { this.virtualPads?.setC64Player(1, { bitmask: mask }); }
        : isLibretro
        ? (mask, prev) => { if (mask !== prev) this.emu.injectP2Input(mask, prev); }
        : (mask) => { this._hostedP2Bitmask = mask; this.emu.setButtons(1, mask); },
    });
    coord.setEnabled(this._inputParityEnabled);
    return coord;
  }

  /** Return host state to piggyback on pong responses (null when n/a). */
  _parityPongPiggyback() {
    if (!this.inputParity || this._netMode !== 'host') return null;
    return {
      frame: this.inputParity.hostFrame,
      delayFrames: this.inputParity.delayFrames,
    };
  }

  _startParityHudTimer() {
    noteNetplayHudOwner(this, 'hosted', this._netMode === 'host' ? this.inputParity : this.hostedNetplay);
    if (this._parityHudTimer) clearInterval(this._parityHudTimer);
    this._parityHudTimer = setInterval(() => this._renderParityHud(), 500);
    this._renderParityHud();
  }

  _stopParityHudTimer() {
    if (this._parityHudTimer) {
      clearInterval(this._parityHudTimer);
      this._parityHudTimer = null;
    }
  }

  _renderParityHud() {
    const wrap = document.getElementById('hud-parity');
    if (!wrap) return;
    // Visible to both the host (who owns parity) and the guest (who
    // benefits from seeing their RTT). Hide everywhere else — menu,
    // lobby, solo play, spectating.
    const isHost = this._netMode === 'host' && !!this.inputParity;
    const isGuest = this._netMode === 'guest' && !!this.hostedNetplay;
    const show = isHost || isGuest;
    wrap.classList.toggle('hidden', !show);
    wrap.classList.toggle('is-guest', isGuest);
    if (!show) return;

    const rtt = this.hostedNetplay?.p2pLatency ?? this.net?.latency ?? 0;
    const rttEl = document.getElementById('parity-rtt');
    const framesEl = document.getElementById('parity-frames');
    const healthEl = document.getElementById('parity-health');
    const toggleEl = document.getElementById('parity-toggle');
    const toggleWrap = document.querySelector('.parity-toggle');
    const framesMetric = framesEl?.closest('.hud-parity-metric');
    const healthMetric = healthEl?.closest('.hud-parity-metric');

    if (rttEl) rttEl.textContent = `${rtt}ms`;

    if (isHost) {
      toggleWrap?.classList.remove('hidden');
      framesMetric?.classList.remove('hidden');
      healthMetric?.classList.remove('hidden');
      const frames = this._inputParityEnabled ? this.inputParity.delayFrames : 0;
      const healthPct = Math.round(this.inputParity.health() * 100);
      if (framesEl) framesEl.textContent = this._inputParityEnabled ? `${frames}f` : 'off';
      if (healthEl) {
        healthEl.textContent = `${healthPct}%`;
        healthEl.classList.toggle('parity-bad', healthPct < 80);
        healthEl.classList.toggle('parity-warn', healthPct >= 80 && healthPct < 95);
        healthEl.classList.toggle('parity-ok', healthPct >= 95);
      }
      if (toggleEl && toggleEl.checked !== this._inputParityEnabled) {
        toggleEl.checked = this._inputParityEnabled;
      }
    } else {
      // Guest: strip the parity-specific chrome, keep just the NET read-out.
      toggleWrap?.classList.add('hidden');
      framesMetric?.classList.add('hidden');
      healthMetric?.classList.add('hidden');
    }
  }

  // ── Hosted Netplay (WebRTC) ─────────────────────────────

  /**
   * Resume hosted mode after P2 disconnects and rejoins.
   * The emulator is still alive — just re-establish WebRTC.
   */
  async _hostedRejoinAsHost(assignmentOwner = null) {
    if (this._unsafeReplayBoundary) {
      this._flash('Reload the page to play again.');
      return false;
    }
    if (this._rehostInProgress) return;
    const sessionHostedNetplay = this.hostedNetplay;
    const sessionEmu = this.emu;
    const sessionNet = this.net, generation = this._replaySessionGen;
    const rehostIsCurrent = () => (
      this._roomAssignmentIsCurrent(assignmentOwner)
      && this.net === sessionNet && this._replaySessionGen === generation
      && this.hostedNetplay === sessionHostedNetplay
      && this.emu === sessionEmu
      && this.state === State.PLAYING
    );
    const rehostIsOperational = () => rehostIsCurrent() && !this._unsafeReplayBoundary;
    const retireMedia = () => {
      const errors = sessionHostedNetplay._rehostCleanupErrors ||= [];
      const retain = error => { if (!errors.includes(error)) errors.push(error); };
      const attempt = fn => {
        try { const result = fn(); result?.catch?.(retain); return true; }
        catch (error) { retain(error); return false; }
      };
      if (rehostIsCurrent()) {
        this.hostedNetplay = null;
        if (attempt(() => sessionHostedNetplay.destroy?.())) return;
      }
      // A replaced rehost owns its media, but not shared DOM or signal routing.
      for (const method of ['_resetMicrophone', '_stopDCPingLoop', '_cleanupAudioCapture'])
        attempt(() => sessionHostedNetplay[method]?.());
      for (const field of ['_micAnalyserCtx', '_remoteAnalyserCtx'])
        attempt(() => sessionHostedNetplay[field]?.close?.());
      attempt(() => { if (sessionHostedNetplay._voiceAudio) sessionHostedNetplay._voiceAudio.srcObject = null; });
      for (const field of ['dataChannel', 'pc']) attempt(() => sessionHostedNetplay[field]?.close?.());
    };
    if (!rehostIsCurrent()) return false;
    this._rehostInProgress = true;

    try {
      // Reset P2 input state
      this._hostedP2Bitmask = 0;
      this._p2PrevBitmask = 0;
      this._p1PrevBitmask = 0;
      this.virtualPads?.setC64Player?.(1, { bitmask: 0 });
      this.inputParity = this._createInputParityCoordinator();

      // Re-wire P2 input callback for the new DataChannel
      sessionHostedNetplay.onRemoteInput = (bitmask, targetFrame) => {
        if (rehostIsOperational()) this.inputParity.submitP2(bitmask, targetFrame);
      };
      sessionHostedNetplay.onP2PRtt = (ms) => {
        if (rehostIsOperational() && this.inputParity) {
          this.inputParity.setDelayTarget(computeTargetDelay(ms));
        }
      };
      this.net.onPingCheckRequest = () => (
        rehostIsOperational() ? this._parityPongPiggyback() : null
      );
      this._startParityHudTimer();

      sessionHostedNetplay.onConnectionStateChange = (state) => {
        if (!rehostIsOperational()) return;
        if (state === 'connected') this._showSysStatus('Streaming to P2', 'ok');
        else if (state === 'connecting') this._showSysStatus('Reconnecting P2P...', 'warn');
      };
      sessionHostedNetplay.onError = (msg) => {
        if (rehostIsOperational()) this._flash(msg);
      };

      // Create fresh WebRTC connection, reusing existing audioStreamDest
      try { await sessionHostedNetplay.rehost(this.canvas); }
      catch (error) {
        const errors = sessionHostedNetplay._rehostCleanupErrors ||= [];
        if (error && (typeof error === 'object' || typeof error === 'function')) {
          for (const prior of error.cleanupErrors || []) if (!errors.includes(prior)) errors.push(prior);
          try { error.cleanupErrors = errors; } catch { /* captured media still retains evidence */ }
        }
        retireMedia();
        throw error;
      }
      if (!rehostIsOperational()) {
        const owned = rehostIsCurrent();
        if (owned) this._flash('Reload the page to play again.');
        retireMedia();
        return false;
      }

      // Reset voice button (mic was stopped during rehost)
      this._stopMicLevelLoop();
      const voiceBtn = document.getElementById('btn-voice');
      if (voiceBtn) voiceBtn.classList.add('voice-muted');

      // Resume game loop
      this.running = true;
      gameActions.refreshGameToolbarAvailability(this, 'core-running');
      this.lastFrameTime = performance.now();
      this.frameAccumulator = 0;

      if (!this.emu.selfDriving) {
        // JS-driven cores (jsnes): restart the host frame loop
        this._hostedHostLoop(performance.now());
      } else if (this.emu instanceof LibretroCore) {
        // Self-driving cores: restart the P1 gamepad input loop
        this._hostedP1InputLoop();
      }
      return true;
    } finally {
      if (this._roomAssignmentIsCurrent(assignmentOwner) && this.net === sessionNet
        && this._replaySessionGen === generation && this.emu === sessionEmu
        && (this.hostedNetplay === sessionHostedNetplay || this.hostedNetplay === null))
        this._rehostInProgress = false;
    }
  }

  _getReportablePlayers() {
    const selfUserId = authState.getCurrentUser()?.id || null;
    if (!this.net?.roomCode) return [];
    const targets = this.net.getReportableRoomParticipants?.(selfUserId) || [];
    const seen = new Set(targets.map(target => target.userId).filter(Boolean));
    for (const entry of this._chatLog || []) {
      if (!entry?.userId || entry.userId === selfUserId || seen.has(entry.userId)) continue;
      seen.add(entry.userId);
      // Room chat logs are session-owned and cleared by _resetRoomSessionUi.
      // Keeping the stable sender ID here lets a player report someone who
      // chatted and then disconnected without carrying targets into a later room.
      targets.push({
        role: 'chat',
        userId: entry.userId,
        username: entry.username || null,
      });
    }
    return targets;
  }

  /**
   * Show Report participant only in an active room. The game room passes its
   * journaled row writer (M1, A5); classic callers pass none and write
   * directly (directRowWriter), as before.
   */
  _updateReportButtonVisibility(writer = null) {
    const btn = document.getElementById('btn-report');
    if (!btn) return;
    const hasActiveRoom = !!this.net?.roomCode && (
      this.state === State.PLAYING
      || this.state === State.SPECTATING
      || this.state === State.SOLO
    );
    (writer || directRowWriter).setHidden(btn, !hasActiveRoom);
  }

  _pushChatLog(username, text, type, userId = null) {
    return roomChat.pushChatLog(this, username, text, type, userId);
  }

  _initVoiceModeration() {
    return roomChat.initVoiceModeration(this);
  }

  async _startHostedHost(connectedPlayers = null, transitionOwner = null) {
    const titleGuard = unsupportedTitleRomGuard(this);
    if (titleGuard && !await titleGuard) return false;
    if (this._unsafeReplayBoundary) {
      this._flash('Reload the page to play again.');
      return false;
    }
    let ownedSave = this._pendingSave;
    const reservedSave = !!this._handoffSaves?.has(ownedSave);
    const releaseSaveUse = this._holdHandoffSave(ownedSave);
    const releaseOwnedSave = () => {
      if (reservedSave) this._discardPendingSave(ownedSave);
      ownedSave = null;
      releaseSaveUse();
    };
    try {
    const transitionIsCurrent = () => (
      transitionOwner == null
      || (this._roomAssignmentIsCurrent(transitionOwner)
        && (transitionOwner.expectedPlayerNumber == null
          || (this.state === State.PLAYING
            && transitionOwner.expectedNet?.playerNumber === transitionOwner.expectedPlayerNumber)))
    );
    if (!transitionIsCurrent()) return false;
    const funnelSurface = this._funnelSurfaceForLaunch();
    this.state = State.PLAYING;
    this._showScreen('game');
    const netModeKey = gameActionSessionKey(this);
    this._trackFunnelLaunch(funnelSurface);
    this._showSysStatus('Loading core...', 'warn');
    // State is no longer SOLO — hide the Invite button.
    this._updateInviteButtonVisibility();

    // >2-player rooms (N64 4-player) use the multi-peer host (one PC per guest +
    // per-guest virtual gamepads); 2-player keeps the single-peer HostedNetplay.
    this._isMultiPeerHost = (this.system?.maxPlayers || 2) > 2;
    let sessionHostedMultiPeer = null;
    let sessionHostedNetplay = null;

    // Set up audio capture before emulator starts
    if (this._isMultiPeerHost) {
      sessionHostedMultiPeer = new HostedMultiPeerHost(this.net, { fps: this.system.fps });
      this.hostedMultiPeer = sessionHostedMultiPeer;
      this.hostedMultiPeer.prepareAudioCapture();
    } else {
      sessionHostedNetplay = new HostedNetplay(this.net, { fps: this.system.fps });
      this.hostedNetplay = sessionHostedNetplay;
      this.hostedNetplay.prepareAudioCapture();
    }

    // A core that cannot self-drive (_coreCannotSelfDrive: SNES) hosts on the
    // stepped core and takes the JS-driven branch below, like jsnes.
    const hostCore = this._roomCoreName || resolveCoreName(this.system, true, this.romData);
    const sessionEmu = await this._createEmulator({
      multiplayer: true,
      lockstep: this._coreCannotSelfDrive(hostCore),
      coreName: this._roomCoreName,
    });
    if (!sessionEmu || this.emu !== sessionEmu || this.state !== State.PLAYING
        || !transitionIsCurrent()) return false;
    if (this._isMultiPeerHost) {
      // _createEmulator tears down the previous core and its pads. Install this
      // session's pads after that cleanup, but before loadROM boots RetroArch.
      // The host's P1 input loop reads real pads through getRealGamepads().
      this.virtualPads = new VirtualGamepadManager();
      this.virtualPads.install();
      this.virtualPads.setSuspended(this._gameplayInputLeases?.active === true);
      this.input.useRealGamepads(() => this.virtualPads.getRealGamepads());
    }
    this._installC64VirtualPads();
    this._showSysStatus('Loading ROM...', 'warn');

    if (this.emu.selfDriving) {
      // Libretro/WASM cores: capture Emscripten's keydown/keyup handlers
      // BEFORE callMain() for controller injection on most cores and the C64
      // on-screen special-key keyboard.
      if (this.emu instanceof LibretroCore) {
        this.emu.captureKeyHandlers();
      }

      // Load ROM with guest bindings enabled. C64 uses virtual pads for both
      // players; other 2-player cores use P2 keyboard injection, while N64
      // multi-peer binds ports 2..N to virtual pads.
      const hostedOpts = { hostedNetplay: true };
      if (this._isMultiPeerHost) hostedOpts.hostPlayers = this.system.maxPlayers;
      const singleC64Disk = this._singleC64DiskForCurrentRom();
      if (this._isMultiDisk && this.romData.disks) {
        const disks = this.romData.disks.map(d => ({
          name: d.name,
          data: Uint8Array.from(atob(d.data), c => c.charCodeAt(0)),
        }));
        this.emu.loadMultiDiskROM(disks, this.romData.name, hostedOpts);
        this._applyC64LaunchPort(sessionEmu);
        if (this.system?.id === 'c64') {
          await this._loadUserDisksFromServer();
          if (this.emu !== sessionEmu || !transitionIsCurrent()) return false;
          this._showDiskPicker();
        } else if (this.emu.totalDisks > 1) {
          this._showDiskPicker();
        }
      } else if (singleC64Disk) {
        sessionEmu.loadMultiDiskROM([singleC64Disk], this.romName, hostedOpts);
        this._applyC64LaunchPort(sessionEmu);
        await this._loadUserDisksFromServer();
        if (this.emu !== sessionEmu || !transitionIsCurrent()) return false;
        this._showDiskPicker();
      } else {
        sessionEmu.loadROM(this.romData, hostedOpts);
        this._applyC64LaunchPort(sessionEmu);
      }

      // Delay restore to catch handlers registered in early frames of the main loop
      if (sessionEmu instanceof LibretroCore) {
        this._scheduleKeyHandlerCaptureRestore(sessionEmu);
      }

    } else {
      // JS-driven cores (jsnes, or a stepped libretro core that cannot
      // self-drive): we control the frame loop
      this.emu.loadROM(this.romData);
      if (!(await this._startInputForGameplay())) return false;

      // jsnes plays through this.audio. A stepped libretro core plays through
      // RetroArch's own AudioContext, which prepareAudioCapture() has already
      // tapped for the guest stream; init() would create another context and
      // move that tap onto it, streaming silence.
      if (!(this.emu instanceof LibretroCore)) {
        this.emu.onAudioBufferReady = (samples) => {
          this.audio.writeSamples(samples);
        };
        this.audio.init().catch(() => {});
      }
    }

    // Restore save state if loading from a saved game
    if (this._pendingSave && (!reservedSave || this._pendingSave === ownedSave)) {
      let pendingSave = this._pendingSave;
      this._replacePendingSave(null);
      try {
        if (this.system?.id === 'n64' || (usesStatePump(this.system?.id, sessionEmu.coreName) && sessionEmu.selfDriving)) {
          // N64 restores via the async in-loop unserialize; the core's
          // emscripten loop self-drives from ROM load, so the pump fires.
          const ok = await this._withStatePumpLock(
            sessionEmu,
            () => sessionEmu.loadStateN64(pendingSave.emulatorState),
          );
          if (this.emu !== sessionEmu || !transitionIsCurrent()) return false;
          if (!ok) {
            this._flash('Could not restore save. Starting fresh');
            this._saveRef = null;
          }
        } else {
          const restored = sessionEmu.loadState(pendingSave.emulatorState);
          if (restored === false) {
            this._flash('Could not restore save. Starting fresh');
            this._saveRef = null;
          }
        }
      } catch (e) {
        if (!transitionIsCurrent()) return false;
        console.warn('Failed to restore save state:', e);
        this._flash('Could not restore save. Starting fresh');
        this._saveRef = null;
      } finally {
        pendingSave = null;
        releaseOwnedSave();
      }
    }

    if (!transitionIsCurrent()) return false;
    if (!gameActionSessionCurrent(this, netModeKey)) return false;
    this.hostedHostFrame = 0;
    this.running = true;
    this._netMode = 'host';
    if (!gameActions.noteNetModeWrite(this, netModeKey)) return false;
    this._startFpsMonitor(); // shows the C64 runtime controls before the refit measures the dock
    gameActions.refreshGameToolbarAvailability(this, 'net-mode');
    this._startAutoSaveTimer();
    this._startAchievementSession();
    this.canvas.tabIndex = 0;
    this.canvas.focus();
    if (this.system?.id === 'c64') this.canvas.classList.add('show-cursor');

    // For self-driving cores, tick achievements + input parity on a
    // 60Hz timer since _hostedHostLoop doesn't run (the WASM core
    // drives its own frames). The parity tick MUST be paced here, not
    // in the RAF-based _hostedP1InputLoop — high-refresh displays run
    // RAF at 120Hz and would drain the parity queue at double speed.
    if (this.emu.selfDriving) {
      // A self-driving hosted host advances hostedHostFrame from THIS interval,
      // not from _hostedHostLoop, so the first_frame call in that loop never ran
      // here — hosted self-driving sessions were silently missing from the funnel.
      // Same inference contract as solo self-driving (see _armFunnelBootInference).
      this._armFunnelBootInference();
      this._cheevosTickInterval = setInterval(() => {
        if (!this.running) return;
        this.hostedHostFrame++;
        if (this.system?.id === 'c64') tickC64(this, performance.now());
        else this.cheevosEngine.tick(this.hostedHostFrame);
        if (this.inputParity) this.inputParity.tick();
        if (this._autoSavePending) {
          this._autoSavePending = false;
          this._autoSave();
        }
      }, 1000 / 60);
    }

    // Wire P2 input via the parity coordinator — drains at the frame
    // the guest targeted, so both players experience the same input
    // latency. When parity is toggled OFF by the user, the coordinator
    // short-circuits to the original direct-apply behaviour.
    this._p1PrevBitmask = 0;
    if (this._isMultiPeerHost) {
      // Multi-peer: each guest drives a virtual gamepad on its assigned port.
      // The host doesn't offer upfront — it adds a peer when each guest joins.
      this.inputParity = null;
      await new Promise(r => setTimeout(r, 500));
      if (!transitionIsCurrent() || this.hostedMultiPeer !== sessionHostedMultiPeer) return false;
      sessionHostedMultiPeer.setSource(this.canvas, sessionHostedMultiPeer.audioStreamDest);
      this.hostedMultiPeer.onRemoteInput = (playerIndex, bitmask, _targetFrame, analogX, analogY) => {
        this.virtualPads?.setPlayer(playerIndex, { bitmask, analogX, analogY });
      };
      this.hostedMultiPeer.onPeerStateChange = (idx, state) => {
        if (state === 'failed' || state === 'disconnected') this.virtualPads?.removePlayer(idx);
      };
      // Server tags each inbound signal with the sender's player index.
      // These net.* handlers outlive the session (they aren't reset on teardown),
      // so guard against a null host/pads — a stray peer_joined after the game
      // ends must not crash (was: "Cannot read properties of null (addPeer)").
      this.net.onSignal = (payload, fromPlayer) => this.hostedMultiPeer?.handleSignal(fromPlayer, payload);
      this.net.onPeerJoined = (peerPlayer, username, image) => {
        const idx = peerPlayer - 1;
        if (this._lobbySlotUsernames) this._lobbySlotUsernames[idx] = username || '';
        if (this._lobbySlotImages) this._lobbySlotImages[idx] = image || null;
        if (this._lobbySlotOccupied) this._lobbySlotOccupied[idx] = true;
        this.hostedMultiPeer?.addPeer(idx);
        this._updatePresenceList();
      };
      this.net.onPeerLeft = (peerPlayer) => {
        if (typeof peerPlayer === 'number') {
          const idx = peerPlayer - 1;
          this.hostedMultiPeer?.removePeer(idx);
          this.virtualPads?.removePlayer(idx);
          if (this._lobbySlotUsernames) this._lobbySlotUsernames[idx] = null;
          if (this._lobbySlotImages) this._lobbySlotImages[idx] = null;
          if (this._lobbySlotOccupied) this._lobbySlotOccupied[idx] = false;
          this._updatePresenceList();
        }
      };
      // Open a peer to every guest already in the room at game_start. Their
      // peer_joined fired before the handler above was wired (the guest readies,
      // then the server sends game_start), so without this they'd never get an
      // offer. addPeer is idempotent, so a later peer_joined is harmless.
      for (const slot of (connectedPlayers || [])) {
        if (slot !== 1) this.hostedMultiPeer.addPeer(slot - 1);
      }
    } else {
      // 2-player: P2 input via the parity coordinator — drains at the frame the
      // guest targeted, so both players experience the same input latency.
      this._hostedP2Bitmask = 0;
      this._p2PrevBitmask = 0;
      this.inputParity = this._createInputParityCoordinator();
      this.hostedNetplay.onRemoteInput = (bitmask, targetFrame) => {
        this.inputParity.submitP2(bitmask, targetFrame);
      };
      this.hostedNetplay.onP2PRtt = (ms) => {
        if (this.inputParity) this.inputParity.setDelayTarget(computeTargetDelay(ms));
      };
      this.net.onPingCheckRequest = () => this._parityPongPiggyback();
      this._startParityHudTimer();

      this.hostedNetplay.onConnectionStateChange = (state) => {
        if (state === 'connected') this._showSysStatus('Streaming to P2', 'ok');
        else if (state === 'connecting') this._showSysStatus('Connecting P2P...', 'warn');
      };

      this.hostedNetplay.onError = (msg) => this._flash(msg);

      // Small delay for audio context to initialize, then start WebRTC
      await new Promise(r => setTimeout(r, 500));
      if (!transitionIsCurrent() || this.hostedNetplay !== sessionHostedNetplay) return false;
      await sessionHostedNetplay.startHost(this.canvas);
      if (!transitionIsCurrent() || this.hostedNetplay !== sessionHostedNetplay) return false;
    }

    // Show voice chat button (unless disabled in settings). Multi-peer voice is
    // a follow-up — the single-peer voice plumbing isn't wired into the N-peer host.
    const roomVoiceAvailable = this._voiceChatEnabled !== false && !this._isMultiPeerHost;
    this._setRoomMicControlsVisible(roomVoiceAvailable);
    if (roomVoiceAvailable) {
      this._endDmVoiceIfActive();
      if (!this._voicePromptShown) {
        this._voicePromptShown = true;
        this._flash('Voice chat available. Tap the mic to join');
      }
    }

    // JS-driven cores need a frame loop on the host side
    if (!this.emu.selfDriving) {
      this.lastFrameTime = performance.now();
      this.frameAccumulator = 0;
      this._hostedHostLoop(performance.now());
    }

    // Self-driving cores: feed P1 physical/touch controller state into the
    // hosted input path. C64 applies it through a virtual gamepad.
    if (this.emu.selfDriving && this.emu instanceof LibretroCore) {
      if (!(await this._startInputForGameplay())) return false;
      this._p1PrevBitmask = 0;
      this._hostedP1InputLoop();
    }

    // Show game change button for host
    document.getElementById('btn-change-game')?.classList.remove('hidden');

    // ↺ Reset button is host-only — show it for P1
    this._updateResetButtonVisibility();

    this._flash('Streaming to guest, waiting for P2P connection...');
    this._initVoiceModeration();
    this._updateReportButtonVisibility();
    return true;
    } finally {
      releaseOwnedSave();
    }
  }

  async _startHostedGuest(assignmentOwner = null) {
    if (this._unsafeReplayBoundary) {
      this._flash('Reload the page to play again.');
      return false;
    }
    if (!this._roomAssignmentIsCurrent(assignmentOwner)) return false;
    const funnelSurface = this._funnelSurfaceForLaunch();
    this.state = State.PLAYING;
    this._showScreen('game');
    const netModeKey = gameActionSessionKey(this);
    this._trackFunnelLaunch(funnelSurface);

    // Clean up stale HostedNetplay from a previous session (e.g. network blip)
    if (this.hostedNetplay) {
      this.hostedNetplay.destroy();
      this.hostedNetplay = null;
    }

    // Hide canvas, show video element for the remote stream.
    // Explicitly detach any stale stream from a previous hosted session
    // — when the guest drops and rejoins, the <video> element keeps its
    // old srcObject pointing at a now-closed MediaStream, and on some
    // browsers (notably Safari) assigning the new stream to srcObject
    // doesn't always force a re-attach / autoplay.
    this.canvas.style.display = 'none';
    const videoEl = document.getElementById('guest-video');
    videoEl.classList.remove('hidden');
    try { videoEl.pause(); } catch { /* ignore */ }
    videoEl.srcObject = null;
    videoEl.removeAttribute('src');
    try { videoEl.load(); } catch { /* ignore */ }
    document.getElementById('guest-unmute-overlay')?.remove();

    // Start input polling (guest sends input to host)
    if (!(await this._startInputForGameplay())) return false;

    // Set up WebRTC guest session. In a multi-peer room (N64 4-player) the guest
    // must target the host explicitly (toPlayer 0) rather than the untagged
    // broadcast-relay used for 1v1.
    const guestOpts = { fps: this.system.fps };
    if ((this.system?.maxPlayers || 2) > 2) guestOpts.signalToPlayer = 0;
    const sessionHostedNetplay = new HostedNetplay(this.net, guestOpts);
    this.hostedNetplay = sessionHostedNetplay;
    const guestIsCurrent = () => (
      this._roomAssignmentIsCurrent(assignmentOwner)
      && this.hostedNetplay === sessionHostedNetplay
    );

    this.hostedNetplay.onConnectionStateChange = (state) => {
      if (!guestIsCurrent()) return;
      if (state === 'connected') {
        this._showSysStatus('Connected to host', 'ok');
        this._trackFunnelConnected(); // multiplayer step 3
      } else if (state === 'connecting') this._showSysStatus('Connecting...', 'warn');
    };
    this.hostedNetplay.onError = (msg) => {
      if (guestIsCurrent()) this._flash(msg);
    };

    // A hosted guest runs no local core, so its "first frame" is the first frame
    // of the host's video actually playing — that is the moment the player can
    // see and play the game. `playing` fires once per attach; the funnel step is
    // idempotent per launch anyway.
    videoEl.addEventListener('playing', () => {
      if (guestIsCurrent()) this._trackFunnelFirstFrame();
    }, { once: true });

    try {
      await sessionHostedNetplay.startGuest(videoEl);
    } catch (err) {
      if (this._roomAssignmentIsCurrent(assignmentOwner)
          && this.hostedNetplay === sessionHostedNetplay) {
        throw err;
      }
      if (this.hostedNetplay === sessionHostedNetplay) {
        sessionHostedNetplay.destroy();
        this.hostedNetplay = null;
      }
      return false;
    }
    if (!this._roomAssignmentIsCurrent(assignmentOwner)
        || this.hostedNetplay !== sessionHostedNetplay) {
      if (this.hostedNetplay === sessionHostedNetplay) {
        sessionHostedNetplay.destroy();
        this.hostedNetplay = null;
      }
      return false;
    }

    // Show voice chat button (unless disabled in settings)
    const roomVoiceAvailable = this._voiceChatEnabled !== false && (this.system?.maxPlayers || 2) <= 2;
    this._setRoomMicControlsVisible(roomVoiceAvailable);
    if (roomVoiceAvailable) {
      this._endDmVoiceIfActive();
      if (!this._voicePromptShown) {
        this._voicePromptShown = true;
        this._flash('Voice chat available. Tap the mic to join');
      }
    }

    // Start sending input to host
    if (!gameActionSessionCurrent(this, netModeKey)) return false;
    this.running = true;
    this._netMode = 'guest';
    if (!gameActions.noteNetModeWrite(this, netModeKey)) return false;
    gameActions.refreshGameToolbarAvailability(this, 'net-mode');
    this._lastSentBitmask = -1;
    this._lastSentAnalogX = null;
    this._lastSentAnalogY = null;
    this._guestInputLoop();
    this._startParityHudTimer();

    // Track play session for P2 (no achievements — no local emulator).
    this._startPlaySession();

    // ↺ Reset button is host-only — hide it for P2
    this._updateResetButtonVisibility();

    this._flash(`Connected! You are Player ${this.net?.playerNumber || 2}.`);
    this._initVoiceModeration();
    this._updateReportButtonVisibility();
    return true;
  }

  _guestInputLoop() {
    if (!this.running || !this.hostedNetplay) return;
    this.animFrameId = requestAnimationFrame(() => this._guestInputLoop());

    const bitmask = this.input.poll();
    const analog = this.system?.hasAnalog
      ? this.input.pollAnalog()
      : { analogX: null, analogY: null };
    if (bitmask !== this._lastSentBitmask
        || analog.analogX !== this._lastSentAnalogX
        || analog.analogY !== this._lastSentAnalogY) {
      this._lastSentBitmask = bitmask;
      this._lastSentAnalogX = analog.analogX;
      this._lastSentAnalogY = analog.analogY;
      const targetFrame = this._estimatedHostTargetFrame();
      this.hostedNetplay.sendInput(bitmask, targetFrame, analog.analogX, analog.analogY);
    }
  }

  /**
   * Guess what frame the host will be on by the time it receives our
   * input, based on the most recent pong piggyback. Returns null until
   * we've received at least one parity-aware pong — the sender then
   * falls back to the legacy 2-byte format, which the host treats as
   * direct-apply (safe in both pre-parity and parity-disabled cases).
   */
  _estimatedHostTargetFrame() {
    if (!this._hostFrameKnownAt) return null;
    const elapsedMs = performance.now() - this._hostFrameKnownAt;
    const elapsedFrames = Math.max(0, Math.round(elapsedMs / (1000 / 60)));
    return this._hostFrameEstimate + elapsedFrames + this._hostDelayFrames + 1;
  }

  async _startNetplay(playerNum, rejoin = false) {
    let ownedSave = this._pendingSave;
    const reservedSave = !!this._handoffSaves?.has(ownedSave);
    const releaseSaveUse = this._holdHandoffSave(ownedSave);
    const releaseOwnedSave = () => {
      if (reservedSave) this._discardPendingSave(ownedSave);
      ownedSave = null;
      releaseSaveUse();
    };
    try {
    this.state = State.PLAYING;
    this._showScreen('game');
    const startupOwner = captureRoomOperationOwnership(this);
    const netModeKey = gameActionSessionKey(this);
    const expectedNet = this.net;
    const expectedRoomCode = expectedNet?.roomCode;
    const startupIsCurrent = () => startupOwner.current() && gameActionSessionCurrent(this, netModeKey)
      && this.state === State.PLAYING && this.net === expectedNet
      && expectedNet?.roomCode === expectedRoomCode && expectedNet?.playerNumber === playerNum
      && expectedNet?.spectator !== true && (playerNum === 1 || playerNum === 2)
      && startupOwner.current() && gameActionSessionCurrent(this, netModeKey);
    const rollbackOwner = Object.freeze({ current: startupIsCurrent });
    if (!startupIsCurrent()) return false;

    const localPlayer = playerNum - 1;
    const sessionEmu = await this._createEmulator({ multiplayer: true, coreName: this._roomCoreName });
    if (!sessionEmu || !startupIsCurrent() || this.emu !== sessionEmu || !startupIsCurrent()) return false;
    sessionEmu.loadROM(this.romData);
    if (!startupIsCurrent() || this.emu !== sessionEmu || !startupIsCurrent()) return false;

    const rollbackOpts = { inputDelay: 2, maxRollbackFrames: 8 };

    if (this._pendingSave && (!reservedSave || this._pendingSave === ownedSave)) {
      const save = this._pendingSave;
      const loaded = sessionEmu.loadState(save.emulatorState);
      if (!startupIsCurrent() || this.emu !== sessionEmu || !startupIsCurrent()) return false;
      if (loaded === false) this._saveRef = null;
      if (!startupIsCurrent()) return false;
      rollbackOpts.startFrame = save.frame;
      this._discardPendingSave(save);
      releaseOwnedSave();
    }

    if (!(await this._startInputForGameplay())) return false;
    if (!startupIsCurrent() || this.emu !== sessionEmu || !startupIsCurrent()) return false;

    const sessionRollback = new RollbackEngine(sessionEmu, localPlayer, rollbackOpts);
    if (!startupIsCurrent() || this.emu !== sessionEmu || !startupIsCurrent()) return false;
    this.rollback = sessionRollback;
    const rollbackOwnerIsCurrent = () => startupIsCurrent() && this.emu === sessionEmu
      && this.rollback === sessionRollback && startupIsCurrent();
    if (!rollbackOwnerIsCurrent()
        || !gameActions.noteRollbackSession(this, netModeKey, sessionRollback, rollbackOwner)) return false;
    const rollbackIsCurrent = () => rollbackOwnerIsCurrent()
      && gameActions.isCurrentRollbackSession(this, sessionRollback) && rollbackOwnerIsCurrent();
    if (!rollbackIsCurrent()) return false;
    settleReplayDecision(this, 'off');  // CR3: rollback netplay never records
    if (!rollbackIsCurrent()) return false;

    sessionRollback.onSendInput = (frame, buttons, adv) => {
      if (rollbackIsCurrent()) expectedNet.sendInput(frame, buttons, adv);
    };

    sessionRollback.onSendSync = (frame, hash) => {
      if (rollbackIsCurrent()) expectedNet.sendSync(frame, hash);
    };

    sessionRollback.onDesync = (frame, localHash, remoteHash) => {
      if (!rollbackIsCurrent()) return;
      console.warn(`DESYNC at frame ${frame}: local=${localHash} remote=${remoteHash}`);
      this._flash('Desync detected - syncing...');
    };

    sessionRollback.onSendForceSync = (frame, state) => {
      if (rollbackIsCurrent()) expectedNet.sendForceSync(frame, state);
    };

    sessionEmu.onAudioBufferReady = (samples) => {
      if (rollbackIsCurrent()) this.audio.writeSamples(samples);
    };

    if (!rollbackIsCurrent()) return false;
    noteNetplayHudOwner(this, 'rollback', sessionRollback);
    if (!rollbackIsCurrent()) return false;
    document.getElementById('hud').classList.remove('hidden');
    if (!rollbackIsCurrent()) return false;

    // Show game change button for host
    if (playerNum === 1) {
      document.getElementById('btn-change-game')?.classList.remove('hidden');
    }

    if (!rollbackIsCurrent()) return false;
    this.audio.init().catch(() => {});
    if (!rollbackIsCurrent()) return false;

    if (rejoin) {
      // Wait for force_sync from Player 1 before starting the game loop
      this._awaitingSync = true;
      if (!rollbackIsCurrent()) return false;
      this.running = false;
      if (!rollbackIsCurrent()) return false;
      gameActions.refreshGameToolbarAvailability(this, 'rollback-ready');
      if (!rollbackIsCurrent()) return false;
      this._flash('Syncing with host...');
    } else {
      this.running = true;
      if (!rollbackIsCurrent()) return false;
      gameActions.refreshGameToolbarAvailability(this, 'core-running');
      if (!rollbackIsCurrent()) return false;
      this._startAutoSaveTimer();
      if (!rollbackIsCurrent()) return false;
      this.lastFrameTime = performance.now();
      if (!rollbackIsCurrent()) return false;
      this.frameAccumulator = 0;
      if (!rollbackIsCurrent()) return false;
      this._netplayLoop(performance.now());
    }
    } finally {
      releaseOwnedSave();
    }
  }

  _netplayLoop(now) {
    if (!this.running) return;
    this.animFrameId = requestAnimationFrame((t) => this._netplayLoop(t));

    const delta = Math.min(now - this.lastFrameTime, this.targetFrameTime * 2);
    this.lastFrameTime = now;
    this.frameAccumulator += delta;

    while (this.frameAccumulator >= this.targetFrameTime) {
      this.rollback.setLocalInput(this.input.poll());
      this.rollback.advance();
      this._trackFunnelFirstFrame();
      this.frameAccumulator -= this.targetFrameTime;
    }

    if (this.rollback.currentFrame % 30 === 0) {
      this._updateHUD();
    }
  }

  // ── Lockstep Netplay ──────────────────────────────────────

  _createLockstepConnection(network, isInitiator) {
    return new LockstepConnection(network, isInitiator);
  }

  _createLockstepEngine(emulator, localPlayer, options) {
    return new LockstepEngine(emulator, localPlayer, options);
  }

  async _loadRoomVoiceChat() {
    return (await import('./voice-chat.js')).VoiceChat;
  }

  _createRoomVoiceChat(network, isInitiator, VoiceChat) {
    return new VoiceChat(network, isInitiator);
  }

  _createSoloStreamHost(network, videoStream, audioSource, fps, sourceCurrent = null) {
    return new SoloStreamHost(network, videoStream, audioSource, fps, null, sourceCurrent);
  }

  /** Prefer the pre-core audio tap; fall back to the jsnes gain node. */
  _soloStreamAudioSource() {
    if (this._clipAudioCapture) return this._clipAudioCapture;
    try {
      const ctx = this.audio?.ctx;
      if (!ctx) return null;
      const dest = ctx.createMediaStreamDestination();
      if (this.audio?._gainNode) this.audio._gainNode.connect(dest);
      return dest;
    } catch {
      return null;
    }
  }

  async _startLockstep(playerNum, assignmentOwner = null, { handoffUnshared = false } = {}) {
    const titleGuard = unsupportedTitleRomGuard(this);
    if (titleGuard && !await titleGuard) return false;
    let startupResources = null;
    let ownedSave = this._pendingSave;
    const reservedSave = !!this._handoffSaves?.has(ownedSave);
    const releaseSaveUse = this._holdHandoffSave(ownedSave);
    const releaseOwnedSave = () => {
      if (reservedSave) this._discardPendingSave(ownedSave);
      ownedSave = null;
      if (startupResources) startupResources.pendingSave = null;
      releaseSaveUse();
    };
    try {
    const assignmentIsCurrent = () => this._roomAssignmentIsCurrent(assignmentOwner);
    if (!assignmentIsCurrent()) return false;
    // §6: a quarantine is checked first of all, and only a page reload ends it.
    if (this._unsafeReplayBoundary) {
      this._refuseLockstepStartup('netplay_start_quarantined');
      return false;
    }
    const earlier = lockstepStart.captureEarlierSession(this);
    const session = this._beginLockstepStartOutcome();
    earlier.startupSession = session;
    const resources = startupResources = { earlier, pendingSave: ownedSave,
      restoreHandlers: null, soloStream: null, voiceChat: null, engine: null, conn: null,
      sessionEmu: this.emu || null, recorder: null, autoSaveInterval: null, mutations: null, mutationsAtOrigin: 0, retired: false, earlierErrors: [], earlierRetirement: null, romLoad: null };
    session.resources = resources;
    lockstepStart.transferEarlierCore(earlier, resources);
    // Track the earlier core until the factory confirms retiring it, then every
    // new core immediately on publication, before input hooks or async init.
    const startupOwner = {
      onCreated: (emu) => {
        if (resources.transferredCoreTicket?.emu === emu) return;
        resources.sessionEmu = emu; session.emu = emu;
        if (resources.coreTicket?.emu !== emu) resources.coreTicket = lockstepStart.coreRetirementTicket(emu, emu instanceof LibretroCore);
      },
      onRetiring: (emu) => {
        if (earlier.coreTicket?.emu === emu) earlier.coreTicket.cleanupDeadline = replayBoundary.monoNow() + (replayBoundary.CLEANUP_MS - replayBoundary.STAGE_MS);
      },
      onRetirementFailed: (emu, error) => {
        if (earlier.coreTicket?.emu === emu) lockstepStart.recordCoreRetirement(earlier.coreTicket,
          earlier.coreTicket.cleanupDeadline ?? replayBoundary.monoNow(), [error]);
      },
      onRetired: (emu) => {
        if (resources.sessionEmu === emu) resources.sessionEmu = null;
        if (earlier.coreTicket?.emu === emu) return lockstepStart.recordCoreRetirement(earlier.coreTicket,
          earlier.coreTicket.cleanupDeadline ?? replayBoundary.monoNow() + (replayBoundary.CLEANUP_MS - replayBoundary.STAGE_MS));
      },
      onInitFailed: (emu, error) => {
        if (resources.coreTicket?.emu === emu) lockstepStart.recordCoreInitializationFailure(resources.coreTicket, error);
      },
      onInitSettled: (emu) => {
        if (resources.coreTicket?.emu === emu) lockstepStart.finishCoreInitialization(resources.coreTicket, resources);
      },
    };
    let startedPlay = false;                         // set once conn.onConnected has started the loop
    const refuse = (cause, { error = null, owner = null } = {}) => {
      if (session.settled || startedPlay) {
        // A late setup failure never tears down a game that already started.
        console.warn('[netplay] setup failure after the game started:', cause, error);
        return false;
      }
      this._discardPendingSave(ownedSave);               // refuse only this startup's save
      resources.pendingSave = null;
      this._refuseLockstepStartup(cause, { error, owner, resources, session });
      return false;
    };
    const retireSuperseded = (extraErrors = []) => {
      // §2 Staleness: a replaced startup retires only what it still owns and
      // writes no flag and shows no message in the session that replaced it.
      const cleanupDeadline = resources.startHold?.cleanupDeadline
        ?? replayBoundary.monoNow() + (replayBoundary.CLEANUP_MS - replayBoundary.STAGE_MS);
      if (resources.startHold?.recorder) {
        resources.recorder = resources.startHold.recorder; resources.startHold.recorder = null;
      }
      // A capture may begin during an await before the startup token changes.
      // The supported lifecycle retains its exact core until that capture settles.
      if (resources.sessionEmu && this.emu === resources.sessionEmu
          && (this._stateCapturePending || this._statePumpQueue)) resources.captureUnsettled = true;
      if (!resources.earlierRetirement) resources.earlierRetirement = this._retireCapturedEarlier(cleanupDeadline, resources.earlier);
      const retired = this._retireLockstepResources(resources, cleanupDeadline);
      resources.earlierErrors.push(...resources.earlierRetirement.cleanupErrors);
      if (this._lockstepSession === session) this._lockstepSession = null;
      const cleanupErrors = [...new Set([...resources.earlierErrors, ...extraErrors, ...retired.cleanupErrors])];
      session.settle({ start: 'superseded', recording: 'off', stages: [], cleanupErrors });
      return false;
    };
    const expectedNet = assignmentOwner?.expectedNet || this.net;
    try {
      const captureActive = () => this._stateCapturePending === true || !!this._statePumpQueue;
      // A cancellation failure must not let refusal destroy a core with active capture work.
      resources.captureUnsettled = captureActive();
      // §6: stop the captured old loop synchronously before any capture wait.
      // Its capture flag/queue remain owned by the active capture until it settles.
      this.running = false;
      if (resources.earlier.animFrameId != null) cancelAnimationFrame(resources.earlier.animFrameId);
      if (this.animFrameId === resources.earlier.animFrameId) this.animFrameId = null;
      if (resources.earlier.lockstepTimer != null) clearTimeout(resources.earlier.lockstepTimer);
      if (this._lockstepTimer === resources.earlier.lockstepTimer) this._lockstepTimer = null;
      // Never replace the core while a pinned capture still owns its hold/queue.
      const captureDeadline = replayBoundary.monoNow() + replayBoundary.STAGE_MS;
      while (captureActive() && replayBoundary.monoNow() < captureDeadline) {
        await new Promise((resolve) => setTimeout(resolve, 16));
        if (!assignmentIsCurrent() || this._lockstepSession !== session || (this.emu || null) !== resources.earlier.ownerEmu)
          return retireSuperseded();
      }
      if (!assignmentIsCurrent() || this._lockstepSession !== session || this.net !== expectedNet
          || (this.emu || null) !== resources.earlier.ownerEmu) return retireSuperseded();
      if (captureActive()) {
        resources.captureUnsettled = true;
        return refuse('netplay_start_boundary_unavailable');
      }
      resources.captureUnsettled = false;
      // §6: every retirement/reset/setup step from here has the same refusal catch.
      const earlierRecorder = this.replayRecorder;
      try {
        resources.earlierRetirement = this._retireEarlierSession(
          replayBoundary.monoNow() + (replayBoundary.CLEANUP_MS - replayBoundary.STAGE_MS), resources.earlier);
        resources.earlierErrors = resources.earlierRetirement.cleanupErrors;
      }
      finally {
        // A failed helper cannot skip stopping the owned loop or detaching its recorder.
        // No await occurs here. Never change a replacement session.
        if (assignmentIsCurrent() && this._lockstepSession === session) {
          const cleanup = (fn) => { try { fn(); } catch (error) { resources.earlierErrors.push(error); } };
          this.running = false;
          cleanup(() => { if (this.animFrameId) cancelAnimationFrame(this.animFrameId); });
          this.animFrameId = null;
          cleanup(() => { if (this._lockstepTimer) clearTimeout(this._lockstepTimer); });
          this._lockstepTimer = null;
          if (earlierRecorder && this.replayRecorder === earlierRecorder) {
            const bytes = replayBoundary.recorderRetainedBytes(earlierRecorder);
            this.replayRecorder = null;
            resources.earlier.recorderRetired = true;
            earlierRecorder.detachedSource = true;
            let stopFailed = false;
            try { earlierRecorder.stop(); } catch (error) { stopFailed = true; resources.earlierErrors.push(error); }
            cleanup(() => replayBoundary.drainRecorder(this, earlierRecorder, { stopFailed, bytes }));
          }
        }
      }
      if (resources.earlierRetirement?.status !== 'ok' || !resources.earlierRetirement.confirmed) {
        return refuse('netplay_start_cleanup_unconfirmed', { error: resources.earlierRetirement?.error });
      }
      // Only a confirmed helper may release the old engine/connection ownership.
      // New resource slots remain separate; confirmed earlier native resources are retired.
      this._resetLockstepLaunchState();
      if (handoffUnshared) return refuse('netplay_start_handoff_unshared');
      // §6: a recorded room-save download failure is honored before anything loads.
      const saveRefusal = this._roomSaveRefusal;
      if (saveRefusal && saveRefusal.roomCode === expectedNet?.roomCode
          && saveRefusal.request === this._roomSaveRequest && saveRefusal.request?.current()) {
        this._roomSaveRefusal = null;
        return refuse('netplay_start_save_unavailable', { error: saveRefusal.error });
      }

      const funnelSurface = this._funnelSurfaceForLaunch();
      this.state = State.PLAYING;
      this._showScreen('game');
      this._trackFunnelLaunch(funnelSurface);
      this._updateResetButtonVisibility();
      const netModeKey = roomSessionKey(this);
      if (playerNum === 1) {
        document.getElementById('btn-change-game')?.classList.remove('hidden');
      }
      if (this.canvas) this.canvas.style.display = '';
      const guestVideo = document.getElementById('guest-video');
      if (guestVideo) { guestVideo.classList.add('hidden'); guestVideo.srcObject = null; }
      const localPlayer = playerNum - 1;
      let sessionEmu;
      try {
        sessionEmu = await this._createEmulator({ lockstep: true, multiplayer: true, coreName: this._roomCoreName, startupOwner });
      } catch (error) {
        if (!assignmentIsCurrent() || this._lockstepSession !== session || this.net !== expectedNet
            || (this.emu || null) !== resources.sessionEmu) return retireSuperseded([error]);
        return refuse('netplay_start_failed', { error });
      }
      // A null return must not erase a partial core already captured by startupOwner.
      if (sessionEmu && !resources.transferredCoreTicket) startupOwner.onCreated(sessionEmu);
      const emulatorIsCurrent = () => assignmentIsCurrent()
        && this._lockstepSession === session && this.net === expectedNet
        && this.emu === sessionEmu
        && this.state === State.PLAYING;
      let ownedAutoSaveInterval = null;
      if (!sessionEmu) return assignmentIsCurrent() && this._lockstepSession === session && this.net === expectedNet
          && (this.emu || null) === resources.sessionEmu
        ? refuse('netplay_start_core_unavailable') : retireSuperseded();
      if (!emulatorIsCurrent()) return retireSuperseded();
      // Load voice before the lockstep handshake, so a peer's first voice offer
      // cannot race a late module fetch. Keep optional call code out of page boot.
      let VoiceChat;
      const voiceEnabled = this._voiceChatEnabled !== false;
      try { if (voiceEnabled) VoiceChat = await this._loadRoomVoiceChat(); }
      catch (error) {
        if (!emulatorIsCurrent()) return retireSuperseded([error]);
        return refuse('netplay_start_failed', { error });
      }
      if (!emulatorIsCurrent()) return retireSuperseded();
      // §6 Fresh core: count every mutation, require a complete ROM load, then
      // apply the start-from-save rule before any frame steps.
      resources.mutations = lockstepStart.countCoreMutations(sessionEmu);
      try { sessionEmu.loadROM(this.romData); } catch (error) {
        return refuse('netplay_start_core_unavailable', { error });
      }
      const romLoaded = resources.romLoad = lockstepStart.romLoadComplete(sessionEmu);
      if (romLoaded.status !== 'ok') return refuse(romLoaded.cause, { error: romLoaded.error });
      let startFrame = 0;
      {
        let save = ownedSave;
        try {
          this._discardPendingSave(save); // the load holds the only reference
          if (save) {
            const frame = lockstepStart.validateSaveFrame(save.frame);
            if (!frame.ok) return refuse('netplay_start_invalid_frame');
            const state = save.emulatorState;
            const noStateBytes = state === undefined || state === null
              || (state instanceof Uint8Array && state.length === 0);
            if (noStateBytes) {
              this._flash("This save can't be continued in a two-player game. Starting from the beginning.");
            } else {
              const loaded = lockstepStart.confirmLockstepLoad(sessionEmu, state);
              if (!loaded.ok) return refuse('netplay_start_load_unconfirmed', { error: loaded.error });
              startFrame = frame.frame;
              this._onEmulatorStateLoaded();
              this.soloFrame = startFrame;
              this._libretroSoloFrame = startFrame;
              this._recordFrameOffset = startFrame;
              this._recordDrainCursor = 0;
            }
          }
        } finally {
          save = null;
          releaseOwnedSave();
        }
      }
      resources.mutationsAtOrigin = resources.mutations.count();

      let inputStarted;
      try { inputStarted = await this._startInputForGameplay(); }
      catch (error) {
        if (!emulatorIsCurrent() || this._lockstepSession !== session) return retireSuperseded([error]);
        return refuse('netplay_start_failed', { error });
      }
      if (!emulatorIsCurrent() || this._lockstepSession !== session) return retireSuperseded();
      if (!inputStarted) return refuse('netplay_start_failed', { error: new Error('gameplay input unavailable') });

      const frameDurationMs = this.system?.fps ? 1000 / this.system.fps : undefined;

      // Prefetch before signaling; only this rejected promise may clear its slot.
      if (!this._replayRecorderModulePromise) {
        const modulePromise = this._replayRecorderModulePromise = this._loadReplayRecorderModule();
        modulePromise.catch(() => {
          if (this._replayRecorderModulePromise === modulePromise) this._replayRecorderModulePromise = null;
        });
      }
      // Set up P2P DataChannel connection (player 1 is the initiator)
      this.lockstepConn = this._createLockstepConnection(expectedNet, playerNum === 1);
      const conn = this.lockstepConn;
      resources.conn = conn;
      const pendingInputs = [];
      const pendingSyncHashes = [];
      this.lockstep = null;
      let engine = null;
      let voiceChat = null;
      let ownedSoloStream = null;
      let resetHandler = null;
      let spectatorJoinedHandler = null;
      let spectatorLeftHandler = null;
      let spectatorSignalHandler = null;
      let reconnectedHandler = null;
      let bindSpectatorVoice = null;
      const previousResetHandler = expectedNet.onLockstepReset;
      const previousSpectatorJoinedHandler = expectedNet.onSpectatorJoined;
      const previousSpectatorLeftHandler = expectedNet.onSpectatorLeft;
      const previousSpectatorSignalHandler = expectedNet.onSpectatorSignal;
      const previousReconnectedHandler = expectedNet.onReconnected;

      const startupIsCurrent = () => emulatorIsCurrent()
        && this._lockstepSession === session && this.lockstepConn === conn;
      resources.restoreHandlers = () => {
        if (expectedNet.onLockstepReset === resetHandler) expectedNet.onLockstepReset = previousResetHandler;
        if (expectedNet.onSpectatorJoined === spectatorJoinedHandler) expectedNet.onSpectatorJoined = previousSpectatorJoinedHandler;
        if (expectedNet.onSpectatorLeft === spectatorLeftHandler) expectedNet.onSpectatorLeft = previousSpectatorLeftHandler;
        if (expectedNet.onSpectatorSignal === spectatorSignalHandler) expectedNet.onSpectatorSignal = previousSpectatorSignalHandler;
        if (expectedNet.onReconnected === reconnectedHandler) expectedNet.onReconnected = previousReconnectedHandler;
      };

      const attachLockstepEngine = (inputDelay) => {
        if (!startupIsCurrent()) return false;
        engine = this._createLockstepEngine(sessionEmu, localPlayer, {
          inputDelay: LOCKSTEP_BOOTSTRAP_DELAY,
          frameDurationMs,
          startFrame,
        });
        this.lockstep = engine;
        resources.engine = engine;
        engine.inputDelay = Math.max(LOCKSTEP_BOOTSTRAP_DELAY, inputDelay);

        engine.onSendInput = (frame, bitmask) => {
          if (!startupIsCurrent() || this.lockstep !== engine) return;
          conn.sendInput(frame, bitmask);
        };
        engine.onSendSync = (frame, hash) => {
          if (!startupIsCurrent() || this.lockstep !== engine) return;
          conn.sendSyncHash(frame, hash);
        };
        engine.onDesync = (frame, localHash, remoteHash) => {
          if (!startupIsCurrent() || this.lockstep !== engine) return;
          console.warn(`DESYNC at frame ${frame}: local=${localHash} remote=${remoteHash}`);
          this._flash('Desync detected!');
        };
        // Replay recording — observe canonical (p1, p2) order regardless of side.
        engine.onApplyInputs = (frame, p1, p2) => {
          if (!startupIsCurrent() || this.lockstep !== engine) return;
          this._observeLockstepFrame(frame, p1, p2);
        };
        for (const [frame, bitmask] of pendingInputs.splice(0)) {
          engine.receiveRemoteInput(frame, bitmask);
        }
        for (const [frame, hash] of pendingSyncHashes.splice(0)) {
          engine.receiveRemoteSyncHash(frame, hash);
        }
        return true;
      };

      conn.onRemoteInput = (frame, bitmask) => {
        if (!startupIsCurrent()) return;
        if (this.lockstep === engine && engine) engine.receiveRemoteInput(frame, bitmask);
        else pendingInputs.push([frame, bitmask]);
      };
      conn.onRemoteSyncHash = (frame, hash) => {
        if (!startupIsCurrent()) return;
        if (this.lockstep === engine && engine) engine.receiveRemoteSyncHash(frame, hash);
        else pendingSyncHashes.push([frame, hash]);
      };
      conn.onConnected = async () => {
        try {
          if (!startupIsCurrent()) return;
          // Multiplayer step 3 — the P2P DataChannel is open. Recorded before the
          // latency measurement below so a give-up during that wait shows as a
          // connected-but-no-first-frame drop rather than vanishing.
          this._trackFunnelConnected();
          this._flash('Measuring P2P latency...');
          this._showSysStatus('Measuring latency...', 'warn');
          const initialDelay = await this._measureLockstepStartupDelay(frameDurationMs);
          if (!startupIsCurrent()) {
            retireSuperseded();
            return;
          }
          const latencyTransportError = resources.startDisconnectedError
            || (!conn.isOpen ? new Error('the channel closed during the latency probe') : null);
          if (latencyTransportError) {
            refuse('netplay_start_failed', { error: latencyTransportError });
            return;
          }

          if (!attachLockstepEngine(initialDelay)) return;
          conn.onP2PRtt = (rtt) => {
            if (startupIsCurrent() && this.lockstep === engine) engine.recordRttSample(rtt);
          };

          const outcome = await this._runLockstepStartBoundary({ engine, conn, sessionEmu, startFrame,
            startupIsCurrent, resources, session });
          const hold = resources.startHold;
          // The helper's final checks precede this caller's microtask handoff.
          if (outcome.start === 'superseded' || (outcome.start === 'started'
              && (!startupIsCurrent() || this.lockstep !== engine || this._replayBoundaryOwner !== null
                || this._replaySessionGen !== hold.generation
                || (resources.recorder && this.replayRecorder !== resources.recorder)))) {
            retireSuperseded(outcome.cleanupErrors); return;
          }
          if (outcome.start !== 'started') return;
          const transportError = resources.startDisconnectedError
            || (!conn.isOpen ? new Error('the channel closed during lockstep startup') : null);
          if (transportError) { refuse('netplay_start_failed', { error: transportError, owner: hold }); return; }
          this._flash(`P2P connected - lockstep active (${initialDelay}f delay)`);
          this._showSysStatus('Lockstep', 'ok');
          if (!gameActionSessionCurrent(this, netModeKey)) return retireSuperseded();
          this.running = true;
          this._netMode = 'lockstep';
          if (!gameActions.noteNetModeWrite(this, netModeKey)) return retireSuperseded();
          gameActions.refreshGameToolbarAvailability(this, 'net-mode');
          if (!startupIsCurrent()) return retireSuperseded();
          this._startAutoSaveTimer();
          ownedAutoSaveInterval = this._autoSaveInterval;
          resources.autoSaveInterval = ownedAutoSaveInterval;
          this._startAchievementSession();
          this.lastFrameTime = performance.now();
          this.frameAccumulator = 0;
          this._lockstepSchedule();
          startedPlay = true;
          session.settle(outcome);
        } catch (error) {
          if (!startupIsCurrent()) { retireSuperseded([error]); return; }
          refuse('netplay_start_failed', { error, owner: resources.startHold });
        }
      };
      conn.onDisconnected = (error) => {
        if (!startupIsCurrent() || this.lockstep !== engine) return;
        if (!startedPlay) {
          // PC disconnected/failed events can arrive while the input channel is open.
          resources.startDisconnectedError ||= error instanceof Error
            ? error : new Error('P2P disconnected before gameplay started');
          return;
        }
        this.running = false;
        this._flash('P2P connection lost');
      };

      resetHandler = () => {
        if (!startupIsCurrent()) return;
        this._handleLockstepReset('remote').catch((error) => console.warn('[replay] remote reset failed:', error));
      };
      expectedNet.onLockstepReset = resetHandler;

      sessionEmu.onAudioBufferReady = (samples) => {
        if (startupIsCurrent()) this.audio.writeSamples(samples);
      };

      const hud = document.getElementById('hud');
      noteNetplayHudOwner(this, 'lockstep', this.lockstep);
      hud.classList.remove('hidden');
      this._setLockstepHUDLabels();
      this.audio.init().catch(() => {});

      try {
        await conn.start();
      } catch (error) {
        if (!startupIsCurrent()) return retireSuperseded([error]);
        return refuse('netplay_start_failed', { error });   // only logs once the game has started
      }
      if (!startupIsCurrent()) return retireSuperseded();

      if (playerNum === 1) {
        const sourceCanvas = this.canvas, sourceRoom = expectedNet.roomCode;
        const voiceTracks = new Set();
        let videoStream = null, audioSource = null;
        let wiredConnection = null;
        const sourceOwnerIsCurrent = () => {
          if (!startupIsCurrent() || expectedNet.roomCode !== sourceRoom
              || !gameActionSessionCurrent(this, netModeKey) || this.canvas !== sourceCanvas
              || this._sbsRole || this._multitapSession) return false;
          const capture = resolvePresentationCapture(this, 'spectator');
          return capture.kind === 'ordinary' && capture.canvas === sourceCanvas;
        };
        // Reconnect retains the game and borrowed capture, while each host and
        // its callbacks belong to just one signaling socket.
        const wireSpectators = () => {
          if (!sourceOwnerIsCurrent() || (ownedSoloStream && this._soloStream !== ownedSoloStream)) return;
          const socket = expectedNet.ws, generation = expectedNet._socketGeneration;
          if (wiredConnection && wiredConnection.socket === socket && wiredConnection.generation === generation) return;
          const current = () => sourceOwnerIsCurrent() && expectedNet.ws === socket
            && expectedNet._socketGeneration === generation
            && ((!this._soloStream && !ownedSoloStream) || this._soloStream === ownedSoloStream)
            && expectedNet.onSpectatorJoined === spectatorJoinedHandler
            && expectedNet.onSpectatorLeft === spectatorLeftHandler
            && expectedNet.onSpectatorSignal === spectatorSignalHandler;
          const viewerIds = [...(ownedSoloStream?._peers?.keys() || [])];
          if (ownedSoloStream) {
            ownedSoloStream.destroy();
            this._soloStream = ownedSoloStream = resources.soloStream = null;
          }

          const initStream = () => {
            if (!current()) return null;
            if (this._soloStream) return this._soloStream === ownedSoloStream ? ownedSoloStream : null;
            if (!videoStream) {
              videoStream = this.canvas.captureStream();
              audioSource = this._soloStreamAudioSource();
            }
            const host = this._createSoloStreamHost(expectedNet, videoStream,
              audioSource, this.system?.fps || 60, current);
            if (!current() || this._soloStream) { host.destroy(); return null; }
            this._soloStream = ownedSoloStream = resources.soloStream = host;
            for (const track of voiceTracks) if (track.readyState !== 'ended') host.addVoiceTrack(track);
            return host;
          };
          spectatorJoinedHandler = (spectatorId, username, image) => {
            const host = initStream();
            if (!host) return;
            this._spectators.set(spectatorId, { username: username || null, image: image || null });
            host.addSpectator(spectatorId);
            this._flash(`Spectator joined (${host.spectatorCount} watching)`);
            const label = username ? `@${username}` : `Spectator ${spectatorId}`;
            this._showChatSystemMessage(`${label} has entered the chat`);
            this._updatePresenceList();
            this._updateRewindButtonVisibility();  // hide rewind while watched
          };
          spectatorLeftHandler = (spectatorId, username) => {
            if (!current()) return;
            this._spectators.delete(spectatorId);
            ownedSoloStream?.removeSpectator(spectatorId);
            if (ownedSoloStream?.spectatorCount === 0 && this._soloStream === ownedSoloStream) {
              ownedSoloStream.destroy();
              this._soloStream = ownedSoloStream = resources.soloStream = null;
            }
            const label = username ? `@${username}` : `Spectator ${spectatorId}`;
            this._showChatSystemMessage(`${label} has left the chat`);
            this._updatePresenceList();
            this._updateRewindButtonVisibility();  // restore rewind once unwatched
          };
          spectatorSignalHandler = (spectatorId, payload) => {
            if (current() && this._soloStream === ownedSoloStream) ownedSoloStream?.handleSignal(spectatorId, payload);
          };
          expectedNet.onSpectatorJoined = spectatorJoinedHandler;
          expectedNet.onSpectatorLeft = spectatorLeftHandler;
          expectedNet.onSpectatorSignal = spectatorSignalHandler;
          wiredConnection = { socket, generation };
          bindSpectatorVoice = () => {
            const ownedVoice = voiceChat;
            if (!ownedVoice) return;
            const forward = (track) => {
              if (!current() || voiceChat !== ownedVoice || this._voiceChat !== ownedVoice) return;
              if (!track || track.readyState === 'ended' || voiceTracks.has(track)) return;
              voiceTracks.add(track);
              if (this._soloStream === ownedSoloStream) ownedSoloStream?.addVoiceTrack(track);
            };
            ownedVoice.onLocalMicTrack = forward;
            ownedVoice.onRemoteVoiceTrack = forward;
          };
          bindSpectatorVoice();
          if (viewerIds.length) {
            const host = initStream();
            for (const id of viewerIds) host?.addSpectator(id);
          }
        };
        wireSpectators();
        reconnectedHandler = () => {
          if (this.net !== expectedNet || expectedNet.onReconnected !== reconnectedHandler) return;
          previousReconnectedHandler?.();
          if (expectedNet.onSpectatorJoined !== spectatorJoinedHandler
              || expectedNet.onSpectatorLeft !== spectatorLeftHandler
              || expectedNet.onSpectatorSignal !== spectatorSignalHandler) return;
          wireSpectators();
        };
        expectedNet.onReconnected = reconnectedHandler;
      }

      // Voice chat — separate peer connection (does not touch lockstep DataChannels)
      if (voiceEnabled && this._voiceChatEnabled !== false) {
        this._endDmVoiceIfActive();
        voiceChat = this._createRoomVoiceChat(expectedNet, playerNum === 1, VoiceChat);
        this._voiceChat = voiceChat;
        resources.voiceChat = voiceChat;

        // P1 forwards voice tracks to spectators via SoloStreamHost
        if (playerNum === 1) bindSpectatorVoice?.();

        try {
          await voiceChat.start();
        } catch (error) {
          if (!startupIsCurrent() || this._voiceChat !== voiceChat) return retireSuperseded([error]);
          if (error.message !== 'microphone_busy' && !startedPlay) return refuse('netplay_start_failed', { error });
          const failedVoice = voiceChat;
          const cleanup = lockstepStart.retireVoiceResources(failedVoice);
          resources.optionalVoiceFailure = { error, cleanupErrors: cleanup.cleanupErrors };
          if (cleanup.cleanupErrors.length) console.warn('[voice] cleanup failed:', error, cleanup.cleanupErrors);
          if (resources.voiceChat === failedVoice) resources.voiceChat = null;
          if (this._voiceChat === failedVoice) this._voiceChat = null;
          voiceChat = null;
          if (!startupIsCurrent()) return retireSuperseded([error, ...cleanup.cleanupErrors]);
          if (this._voiceChat) return true;
          if (error.message === 'microphone_busy') this._flash('Voice is unavailable while another call owns the microphone.');
          else console.warn('[voice] voice start failed after the game started:', error);
        }
        if (!startupIsCurrent() || (voiceChat && this._voiceChat !== voiceChat)) return retireSuperseded();

        this._setRoomMicControlsVisible(!!voiceChat);

        if (voiceChat && !this._voicePromptShown) {
          this._voicePromptShown = true;
          this._flash('Voice chat available. Tap the mic to join');
        }
      } else {
        this._setRoomMicControlsVisible(false);
      }
      if (!startupIsCurrent()) return retireSuperseded();
      this._initVoiceModeration();
      this._updateReportButtonVisibility();
      return true;
    } catch (error) {
      if (!assignmentIsCurrent() || this._lockstepSession !== session || this.net !== expectedNet
          || (this.emu || null) !== resources.sessionEmu
          || (resources.conn && this.lockstepConn !== resources.conn)) return retireSuperseded([error]);
      return refuse('netplay_start_failed', { error });
    }
    } finally {
      releaseOwnedSave();
    }
  }

  _measureLockstepStartupDelay(frameDurationMs) {
    const conn = this.lockstepConn;
    if (!conn) return Promise.resolve(2);

    return new Promise((resolve) => {
      const samples = [];
      let done = false;
      let timeoutId = null;

      const finish = () => {
        if (done) return;
        done = true;
        if (timeoutId) clearTimeout(timeoutId);
        if (conn.onP2PRtt === onSample) conn.onP2PRtt = null;

        if (samples.length === 0) {
          const relayRtt = this.net?.latency || 0;
          resolve(Math.max(LOCKSTEP_BOOTSTRAP_DELAY, computeLockstepInputDelay(relayRtt, frameDurationMs)));
          return;
        }

        const sorted = [...samples].sort((a, b) => a - b);
        const idx = Math.min(sorted.length - 1, Math.ceil(sorted.length * 0.9) - 1);
        resolve(Math.max(LOCKSTEP_BOOTSTRAP_DELAY, computeLockstepInputDelay(sorted[idx], frameDurationMs)));
      };

      const onSample = (rtt) => {
        if (!Number.isFinite(rtt) || rtt < 0) return;
        samples.push(rtt);
        if (samples.length >= LOCKSTEP_WARMUP_SAMPLES) finish();
      };

      conn.onP2PRtt = onSample;
      timeoutId = setTimeout(finish, LOCKSTEP_WARMUP_TIMEOUT_MS);
      if (Number.isFinite(conn.p2pLatency) && conn.p2pLatency > 0) {
        onSample(conn.p2pLatency);
      }
    });
  }

  _lockstepSchedule() {
    if (document.hidden) {
      this._lockstepTimer = setTimeout(() => this._lockstepLoop(), 4);
    } else {
      this.animFrameId = requestAnimationFrame(() => this._lockstepLoop());
    }
  }

  _lockstepLoop() {
    if (!this.running) return;
    if (this._gameplayHeld()) {
      this.frameAccumulator = 0;
      this.lastFrameTime = performance.now();
      this._lockstepSchedule();
      return;
    }

    const now = performance.now();

    // Cap delta to prevent catch-up avalanche when a tab regains focus
    const delta = Math.min(now - this.lastFrameTime, this.targetFrameTime * 2);
    this.lastFrameTime = now;
    this.frameAccumulator += delta;

    while (this.frameAccumulator >= this.targetFrameTime) {
      if (this.emu?.stepReady && !this.emu.stepReady()) break;
      this.lockstep.setLocalInput(this.input.poll());
      if (!this.lockstep.advance()) break;
      this.frameAccumulator -= this.targetFrameTime;
    }

    this._lockstepHudCounter = (this._lockstepHudCounter || 0) + 1;
    if (this._lockstepHudCounter >= 30) {
      this._lockstepHudCounter = 0;
      this._updateLockstepHUD();
    }
    this._lockstepSchedule();
  }

  /** Count applied frames even when recording has ended; enforce its base and contiguity. */
  _observeLockstepFrame(frame, p1, p2) {
    this._lockstepAppliedFrame = true;
    this._lockstepAppliedSinceReset = (this._lockstepAppliedSinceReset || 0) + 1;
    const delivery = this._netplayResetDelivery;
    if (delivery && delivery.epoch === this._netplayResetEpoch && delivery.appliedAt == null
        && this._lockstepAppliedSinceReset > delivery.firstPeerFrame) {
      delivery.appliedAt = replayBoundary.monoNow();
    }
    const recorder = this.replayRecorder;
    if (recorder?.isRecording) {
      const expected = this._lockstepExpectedFrame ?? recorder.baseFrame;
      if (expected !== null && expected !== undefined && frame !== expected) {
        this._logBoundaryFailure({ operation: 'netplay_record', origin: 'lockstep', stage: 'observe',
          status: 'failed', cause: 'netplay_frame_gap' });
        this._replayRecordingUnavailable('netplay_frame_gap');
      } else if (recorder.observe(frame, p1, p2) === false) {
        this._replayRecordingUnavailable('replay_capacity_exceeded');
      } else {
        this._lockstepExpectedFrame = frame + 1;
      }
    }
    this._trackFunnelFirstFrame();
  }

  _setLockstepHUDLabels() {
    const rbLabel = document.querySelector('#stat-rollbacks')?.previousElementSibling;
    const advLabel = document.querySelector('#stat-advantage')?.previousElementSibling;
    if (rbLabel) rbLabel.textContent = 'STALLS';
    if (advLabel) advLabel.textContent = 'HEALTH';
  }

  _restoreHUDLabels() {
    const rbLabel = document.querySelector('#stat-rollbacks')?.previousElementSibling;
    const advLabel = document.querySelector('#stat-advantage')?.previousElementSibling;
    if (rbLabel) rbLabel.textContent = 'RB';
    if (advLabel) advLabel.textContent = 'ADV';
  }

  _updateLockstepHUD() {
    if (!this.lockstep) return;
    const stats = this.lockstep.getStats();

    const ping = document.getElementById('stat-ping');
    const frame = document.getElementById('stat-frame');
    const rb = document.getElementById('stat-rollbacks');
    const adv = document.getElementById('stat-advantage');
    const delay = document.getElementById('stat-delay');
    const sync = document.getElementById('stat-sync');

    const rtt = this.lockstepConn?.p2pLatency;
    if (ping) ping.textContent = rtt != null ? `${rtt}ms` : '?ms';
    if (frame) frame.textContent = stats.frame;
    if (rb) rb.textContent = stats.stalls;
    if (adv) adv.textContent = `${stats.health}%`;
    if (delay) delay.textContent = stats.delay;
    if (sync) {
      sync.textContent = stats.desync ? 'DESYNC' : 'OK';
      sync.classList.toggle('desync', stats.desync);
      sync.classList.toggle('sync-ok', !stats.desync);
    }
  }

  /** §4: owns the netplay reset boundary for both origins. Resolves, never rejects. */
  async _handleLockstepReset(origin = 'remote') {
    return origin === 'local' ? this._localLockstepReset() : this._remoteLockstepReset();
  }

  _netplayResetResult(reset, recording, owner = null, extraStages = []) {
    return { reset, recording, stages: [...(owner?.stages || []), ...extraStages],
      cleanupErrors: owner?.cleanupErrors || [] };
  }

  async _localLockstepReset() {
    const engine = this.lockstep;
    const connection = this.lockstepConn;
    const recordingNow = () => (this.replayRecorder?.isRecording ? 'continued' : 'off');
    const refuse = (message, stage, status, cause, owner = null) => {
      this._flash(message);
      this._logBoundaryFailure({ operation: 'netplay_reset', origin: 'local', stage, status, cause });
      return this._netplayResetResult('refused', recordingNow(), owner, [replayBoundary.stageResult(stage, status, cause)]);
    };
    // §4 Local origin: only after the engine has applied inputDelay + 60 frames
    // since the session started or since this player's last committed reset.
    const gate = (engine?.inputDelay || 0) + replayBoundary.RESET_GATE_EXTRA_FRAMES;
    if (!this._lockstepStartReleased || !engine || !connection || (this._lockstepAppliedSinceReset || 0) < gate) {
      return refuse('Reset is busy. Try again.', 'hold', 'failed', 'gate');
    }
    const hold = this._beginResetHold('local', { engine, connection });
    if (hold.busy) return refuse('Reset is busy. Try again.', 'hold', 'failed', 'busy');
    const owner = hold.owner;
    let result;
    try {
      const ack = await this._acknowledgeResetFrame(owner);
      owner.stages.push(ack);
      if (owner.superseded) return this._netplayResetResult('superseded', recordingNow(), owner);
      if (replayBoundary.isStale(this, owner)) return this._netplayResetResult('skipped_stale', 'off', owner);
      if (ack.status === 'skipped_stale') return this._netplayResetResult('skipped_stale', 'off', owner);
      if (ack.status !== 'ok') {
        this._flash('Reset could not reach a frame boundary');
        this._logBoundaryFailure({ operation: 'netplay_reset', origin: 'local', stage: 'acknowledge',
          status: ack.status, cause: ack.cause || 'frame_boundary' });
        return this._netplayResetResult('refused', recordingNow(), owner);
      }
      // One synchronous turn: deadline check, send, commit.
      if (replayBoundary.monoNow() >= owner.deadline) {
        return refuse('Reset could not reach a frame boundary', 'send', 'timeout', 'deadline', owner);
      }
      let sent = false;
      let sendError = null;
      try { sent = this.net.sendLockstepReset() === true; } catch (error) { sendError = error; }
      if (!sent) {
        owner.stages.push(replayBoundary.stageResult('send', 'failed', 'send_failed', sendError));
        this._flash('Reset could not reach the other player. Try again.');
        this._logBoundaryFailure({ operation: 'netplay_reset', origin: 'local', stage: 'send', status: 'failed', cause: 'send_failed' });
        return this._netplayResetResult('refused', recordingNow(), owner);
      }
      owner.sent = true;
      owner.stages.push(replayBoundary.stageResult('send', 'ok'));
      try {
        this._commitReset(owner);
      } catch (error) {
        return this._haltNetplayReset(owner, 'local', 'commit', 'failed', 'netplay_reset_commit_failed', error);
      }
      result = await this._finishNetplayReset(owner);
    } finally {
      this._releaseResetHold(owner);
    }
    // §4: a successful send does not prove delivery; progress does. Gameplay
    // is already released, so the frames this wait needs can run.
    if (result.reset !== 'applied') return result;
    const delivery = await this._awaitResetDelivery(owner, owner.epoch);
    if (this._netplayResetEpoch !== owner.epoch || this.emu !== owner.emu
        || this.lockstep !== owner.engine || this.lockstepConn !== owner.connection || this.net !== owner.net) {
      return this._netplayResetResult('skipped_stale', 'off', owner);
    }
    if (delivery === 'undelivered') {
      return this._haltNetplayReset(owner, 'local', 'deliver', 'timeout', 'netplay_reset_undelivered');
    }
    if (delivery !== 'delivered') return this._netplayResetResult('skipped_stale', 'off', owner);
    owner.stages.push(replayBoundary.stageResult('deliver', 'ok'));
    return this._netplayResetResult(result.reset, result.recording, owner);
  }

  async _remoteLockstepReset() {
    const arrived = replayBoundary.monoNow();
    const recordingNow = () => (this.replayRecorder?.isRecording ? 'continued' : 'off');
    const ignore = (cause) => {
      this._logBoundaryFailure({ operation: 'netplay_reset', origin: 'remote', stage: 'hold', status: 'failed', cause });
      return this._netplayResetResult('ignored', recordingNow(), null, [replayBoundary.stageResult('hold', 'failed', cause)]);
    };
    if (!this._lockstepStartReleased) return ignore('netplay_reset_before_start');
    if (this._remoteResetPending) return ignore('netplay_reset_flood');
    const token = {};
    this._remoteResetPending = token;
    const sessionEmu = this.emu;
    const sessionEngine = this.lockstep;
    const sessionConnection = this.lockstepConn;
    const sessionNet = this.net;
    const sessionIsCurrent = () => this.emu === sessionEmu && this.lockstep === sessionEngine
      && this.lockstepConn === sessionConnection && this.net === sessionNet;
    let owner = null;
    try {
      const open = this._replayBoundaryOwner;
      if (open?.kind === 'reset' && open.origin === 'local' && !open.sent) {
        owner = replayBoundary.takeOverResetHold(this, open, { origin: 'remote' });
      } else {
        const deadline = arrived + replayBoundary.STAGE_MS;
        while (this._gameplayHeld() && !this._unsafeReplayBoundary && replayBoundary.monoNow() < deadline) {
          await new Promise((resolve) => setTimeout(resolve, 16));
          if (!sessionIsCurrent()) {
            return this._netplayResetResult('skipped_stale', 'off');
          }
        }
        if (!sessionIsCurrent()) return this._netplayResetResult('skipped_stale', 'off');
        const hold = replayBoundary.monoNow() < deadline
          ? this._beginResetHold('remote', { engine: this.lockstep, connection: this.lockstepConn, t0: arrived })
          : { busy: true };
        if (hold.busy) return this._haltNetplayReset(null, 'remote', 'hold', 'timeout', 'netplay_reset_hold_unavailable');
        owner = hold.owner;
      }
      const ack = await this._acknowledgeResetFrame(owner);
      owner.stages.push(ack);
      if (replayBoundary.isStale(this, owner)) return this._netplayResetResult('skipped_stale', 'off', owner);
      if (ack.status === 'skipped_stale') return this._netplayResetResult('skipped_stale', 'off', owner);
      if (ack.status !== 'ok') return this._haltNetplayReset(owner, 'remote', null, ack.status, 'netplay_reset_frame_boundary');
      if (replayBoundary.monoNow() >= owner.deadline) {
        return this._haltNetplayReset(owner, 'remote', 'commit', 'timeout', 'netplay_reset_commit_deadline');
      }
      try {
        this._commitReset(owner);
      } catch (error) {
        return this._haltNetplayReset(owner, 'remote', 'commit', 'failed', 'netplay_reset_commit_failed', error);
      }
      return await this._finishNetplayReset(owner);
    } finally {
      if (owner) this._releaseResetHold(owner);
      if (this._remoteResetPending === token) this._remoteResetPending = null;
    }
  }

  /**
   * §4 Halt: this player's core may no longer match the other player's. `stage`
   * is the failed stage; pass null when that stage's result is already recorded.
   */
  _haltNetplayReset(owner, origin, stage, status, cause, error = null) {
    const failed = stage ? [replayBoundary.stageResult(stage, status, cause, error)] : [];
    if (owner) owner.stages.push(...failed);
    this._logBoundaryFailure({ operation: 'netplay_reset', origin, stage: stage || 'acknowledge', status: status || 'timeout', cause });
    this._haltUncertainReplayBoundary(owner?.emu || this.emu, cause);
    return this._netplayResetResult('halted', 'ended', owner, owner ? [] : failed);
  }

  /** After a committed netplay reset: counters, budget, late check, arm. */
  async _finishNetplayReset(owner) {
    const now = replayBoundary.monoNow();                  // read just after the commit (A1)
    owner.stages.push(replayBoundary.stageResult('commit', 'ok'));
    owner.committedAt = now;
    owner.firstPeerFrame = owner.engine?.inputDelay || 0;
    owner.epoch = this._netplayResetEpoch = (this._netplayResetEpoch || 0) + 1;
    this._netplayResetDelivery = { epoch: owner.epoch, firstPeerFrame: owner.firstPeerFrame, appliedAt: null };
    this._lockstepAppliedSinceReset = 0;
    this._lockstepExpectedFrame = null;
    this._netplayResetTimes = (this._netplayResetTimes || [])
      .filter((t) => now - t < replayBoundary.RESET_BUDGET_WINDOW_MS);
    this._netplayResetTimes.push(now);
    this._flash('Reset!');
    // §2: with recording off, the recorder steps are skipped and nothing is announced.
    if (!replayBoundary.recordingWanted(this)) return this._netplayResetResult('applied', 'off', owner);
    if (now >= owner.deadline) return this._endResetRecording(owner, 'netplay_reset_deadline', owner.origin);
    if (this._netplayResetTimes.length > replayBoundary.RESET_BUDGET) {
      return this._endResetRecording(owner, 'netplay_reset_budget', owner.origin);
    }
    const arm = await this._armAfterResetCommit(owner, { isNetplay: true, opponentUserId: this._roomPeerUserId || null });
    if (replayBoundary.isStale(this, owner)) return this._netplayResetResult('skipped_stale', 'off', owner);
    owner.stages.push(arm);
    if (arm.status === 'ok') {
      this._lockstepExpectedFrame = 0;
      return this._netplayResetResult('applied', 'continued', owner);
    }
    if (arm.status === 'unsupported') return this._netplayResetResult('applied', 'off', owner);
    if (arm.status === 'skipped_stale') return this._netplayResetResult('skipped_stale', 'off', owner);
    return this._endResetRecording(owner, arm.status === 'timeout' ? 'netplay_reset_deadline' : 'netplay_reset_arm_failed',
      owner.origin, arm.cause);
  }

  /** §4: delivered once the engine applies the first frame past the zero-filled input-delay frames. */
  _awaitResetDelivery(owner, epoch) {
    const engine = owner.engine;
    const until = owner.committedAt + replayBoundary.DELIVERY_WATCH_MS;
    return new Promise((resolve) => {
      const check = () => {
        if (this._netplayResetEpoch !== epoch || this.emu !== owner.emu || this.lockstep !== engine
            || this.lockstepConn !== owner.connection || this.net !== owner.net) return resolve('stale');
        // The observer timestamps application, so a delayed poll keeps timely
        // evidence but cannot accept a frame applied at or after the deadline.
        const delivery = this._netplayResetDelivery;
        if (delivery && delivery.epoch === epoch && delivery.appliedAt != null) {
          return resolve(delivery.appliedAt < until ? 'delivered' : 'undelivered');
        }
        const left = until - replayBoundary.monoNow();
        if (left <= 0) return resolve('undelivered');
        setTimeout(check, Math.min(100, left));
      };
      check();
    });
  }

  _retireLockstepResources(resources, cleanupDeadline) {
    return lockstepStart.retireLockstepResources(this, resources, cleanupDeadline);
  }
  _retireEarlierSession(cleanupDeadline, earlier) { return lockstepStart.retireEarlierSession(this, cleanupDeadline, earlier); }
  _resetLockstepLaunchState() { return lockstepStart.resetLockstepLaunchState(this); }
  _retireCapturedEarlier(cleanupDeadline, earlier) {
    // Fallback bypasses a throwing delegate, and cannot reject a typed refusal.
    try { return lockstepStart.retireEarlierSession(this, cleanupDeadline, earlier); }
    catch (error) {
      const cleanupErrors = [error];
      const step = (fn) => { try { fn(); } catch (e) { cleanupErrors.push(e); } };
      let retired = { confirmed: false, late: false, cleanupErrors: [] };
      step(() => { retired = this._retireLockstepResources(earlier.nativeResources, cleanupDeadline); });
      cleanupErrors.push(...retired.cleanupErrors);
      const recorder = earlier.recorder;
      if (recorder && !earlier.recorderRetired) {
        earlier.recorderRetired = true;
        step(() => { if (this.replayRecorder === recorder) this.replayRecorder = null; });
        step(() => { recorder.detachedSource = true; });
        const bytes = replayBoundary.recorderRetainedBytes(recorder);
        let stopFailed = false;
        try { recorder.stop(); } catch (e) { stopFailed = true; cleanupErrors.push(e); }
        step(() => replayBoundary.drainRecorder(this, recorder, { stopFailed, bytes }));
      }
      return { stage: 'earlier_retirement', status: 'failed', cause: 'netplay_start_cleanup_unconfirmed',
        error, cleanupErrors, confirmed: false, late: retired.late || replayBoundary.monoNow() >= cleanupDeadline };
    }
  }

  /** §6: confirm the origin and arm before releasing the first lockstep frame. */
  async _runLockstepStartBoundary({ engine, conn, sessionEmu, startFrame, startupIsCurrent, resources, session }) {
    const t0 = replayBoundary.monoNow(), deadline = t0 + replayBoundary.STAGE_MS;
    const hold = resources.startHold = { kind: 'start', origin: 'start', emu: sessionEmu, engine,
      connection: conn, net: this.net, recorder: null, generation: this._replaySessionGen,
      t0, deadline, cleanupDeadline: t0 + replayBoundary.CLEANUP_MS,
      watchdog: replayBoundary.deadlineSignal(deadline), stages: [], cleanupErrors: [] };
    let acquired = false;
    const current = () => startupIsCurrent() && this._lockstepSession === session
      && this.emu === sessionEmu && this.lockstep === engine && this.lockstepConn === conn && this.net === hold.net
      && this._replaySessionGen === hold.generation && (!acquired || this._replayBoundaryOwner === hold)
      && (!(hold.recorder || resources.recorder) || this.replayRecorder === (hold.recorder || resources.recorder));
    const transferRecorder = () => {
      if (hold.recorder) { resources.recorder = hold.recorder; hold.recorder = null; }
    };
    const captureOwned = () => {
      // Sample only this core/session, never the replacement's capture flags.
      if (current()) resources.captureUnsettled = this._stateCapturePending === true || !!this._statePumpQueue;
    };
    const expired = () => hold.watchdog.signal.aborted || replayBoundary.monoNow() >= deadline;
    const errors = () => [...new Set([...(resources.earlierErrors || []), ...hold.cleanupErrors])];
    const superseded = () => {
      transferRecorder();
      return { start: 'superseded', recording: 'off', stages: hold.stages, cleanupErrors: errors() };
    };
    const refuse = (cause, error = null) => {
      captureOwned();
      return this._refuseLockstepStartup(cause, { error, owner: hold, resources, session });
    };
    const refuseTransport = () => {
      const error = resources.startDisconnectedError
        || (!conn.isOpen ? new Error('the channel closed during lockstep startup') : null);
      return error ? refuse('netplay_start_failed', error) : null;
    };
    const originConfirmed = () => !this._lockstepAppliedFrame
      && resources.mutations?.count() === resources.mutationsAtOrigin;
    try {
      while ((this._gameplayHeld() || this._statePumpQueue) && !this._unsafeReplayBoundary && !expired()) {
        captureOwned();
        await new Promise(resolve => setTimeout(resolve, 16));
        if (!current()) return superseded();
        const transportFailure = refuseTransport();
        if (transportFailure) return transportFailure;
      }
      if (!current()) return superseded();
      captureOwned();
      const holdTransportFailure = refuseTransport();
      if (holdTransportFailure) return holdTransportFailure;
      if (this._unsafeReplayBoundary) return refuse('netplay_start_quarantined');
      if (expired() || this._gameplayHeld() || this._statePumpQueue) return refuse('netplay_start_boundary_unavailable');
      this._replaySessionGen = (this._replaySessionGen || 0) + 1;
      hold.generation = this._replaySessionGen;
      this._replayBoundaryOwner = hold;
      acquired = true;
      if (!originConfirmed()) return refuse('netplay_start_origin_unconfirmed');
      let recording = 'off';
      if (replayBoundary.recordingWanted(this)) {
        captureOwned();
        const released = await replayBoundary.waitForDrained(this,
          Math.min(t0 + replayBoundary.ARM_RELEASE_WAIT_MS, deadline), null, hold.watchdog.signal);
        if (!current()) return superseded();
        captureOwned();
        const drainTransportFailure = refuseTransport();
        if (drainTransportFailure) return drainTransportFailure;
        if (!originConfirmed()) return refuse('netplay_start_origin_unconfirmed');
        let arm;
        if (!released) arm = replayBoundary.stageResult('arm', 'timeout', 'draining_unreleased');
        else if (expired()) arm = replayBoundary.stageResult('arm', 'timeout', 'arm_deadline');
        else {
          const arming = this._initReplayRecorder({ isNetplay: true,
            opponentUserId: this._roomPeerUserId || null, baseFrame: startFrame, cleanupErrors: hold.cleanupErrors });
          const raced = await replayBoundary.raceDeadline(arming, deadline);
          transferRecorder();
          if (!current()) return superseded();
          captureOwned();
          arm = (raced.timedOut || expired())
            ? replayBoundary.stageResult('arm', 'timeout', 'arm_deadline', raced.error || raced.value?.error || null)
            : raced.error ? replayBoundary.stageResult('arm', 'failed', 'exception', raced.error)
              : (raced.value || replayBoundary.stageResult('arm', 'failed', 'no_result'));
        }
        if (!current()) return superseded();
        hold.stages.push(arm);
        if (arm.status === 'ok' && this.replayRecorder) resources.recorder = this.replayRecorder;
        const armTransportFailure = refuseTransport();
        if (armTransportFailure) return armTransportFailure;
        if (!originConfirmed()) return refuse('netplay_start_origin_unconfirmed');
        if (arm.status === 'ok') recording = 'continued';
        else if (arm.status !== 'unsupported') {
          recording = 'ended';
          const reason = arm.status === 'timeout' ? 'netplay_start_deadline' : (arm.cause || 'netplay_start_arm_failed');
          if (!this._replayRecordingOff) this._replayRecordingUnavailable(reason);
          this._logBoundaryFailure({ operation: 'netplay_start', origin: 'start', stage: 'arm',
            status: arm.status, cause: arm.cause || reason });
        }
      }
      if (!current()) return superseded();
      const releaseTransportFailure = refuseTransport();
      if (releaseTransportFailure) return releaseTransportFailure;
      if (!originConfirmed()) return refuse('netplay_start_origin_unconfirmed');
      this._lockstepExpectedFrame = startFrame;
      this._lockstepStartReleased = true;
      return { start: 'started', recording, stages: hold.stages, cleanupErrors: errors() };
    } finally {
      hold.watchdog.dispose();
      if (this._replayBoundaryOwner === hold) this._replayBoundaryOwner = null;
    }
  }

  /** §6: one start result per session, awaited through _lockstepStartOutcome. */
  _beginLockstepStartOutcome() {
    let resolve;
    const promise = new Promise((done) => { resolve = done; });
    const session = { emu: null, settled: false, settle: (result) => {
      const ticket = session.pendingCoreTicket;
      const voice = session.resources?.optionalVoiceFailure;
      const cleanupErrors = [...new Set([...(session.pendingResult?.cleanupErrors || []), ...(session.result?.cleanupErrors || []),
        ...(result.cleanupErrors || []), ...(ticket?.cleanupErrors || []), ...(voice?.cleanupErrors || [])])];
      if (session.settled) { session.result.cleanupErrors = cleanupErrors; return; }
      if (ticket && (!ticket.outcome || ticket.initializing)) {
        session.pendingResult = { ...result, cleanupErrors };
        return;
      }
      session.settled = true;
      session.result = { ...result, cleanupErrors };
      ticket?.evidenceTargets.add(session.result);
      session.resources?.earlier?.coreTicket?.evidenceTargets.add(session.result);
      resolve(session.result);
    } };
    this._lockstepStartOutcome = promise;
    this._lockstepSession = session;
    return session;
  }

  /** §6 Refused startup: steps no frame and never reloads. Call it only for the current startup. */
  _refuseLockstepStartup(cause, { error = null, owner = null, resources = null, session = null } = {}) {
    this._discardPendingSave(resources ? resources.pendingSave : this._pendingSave);
    const stages = [...(owner?.stages || []), ...(resources?.earlierRetirement ? [resources.earlierRetirement] : []),
      ...(resources?.romLoad ? [resources.romLoad] : []), replayBoundary.stageResult('start', 'failed', cause, error)];
    let cleanupErrors = [...(owner?.cleanupErrors || []), ...(resources?.earlierErrors || [])];
    let finalCause = cause;
    this._lockstepStartReleased = false;
    if (resources?.sessionEmu && this.emu === resources.sessionEmu && (!session || this._lockstepSession === session))
      resources.captureUnsettled = this._stateCapturePending === true || !!this._statePumpQueue;
    // `running` is cleared by the retirement, and only for this startup's own core.
    // Logged before retirement, so the line still names the core.
    this._logBoundaryFailure({ operation: 'netplay_start', origin: 'start', stage: 'start', status: 'failed', cause });
    if (resources) {
      const deadline = owner?.cleanupDeadline
        ?? replayBoundary.monoNow() + (replayBoundary.CLEANUP_MS - replayBoundary.STAGE_MS);
      if (resources.earlier && !resources.earlierRetirement) resources.earlierRetirement = this._retireCapturedEarlier(deadline, resources.earlier);
      cleanupErrors = cleanupErrors.concat((resources.earlierRetirement?.cleanupErrors || []));
      if (resources.earlierRetirement && !stages.includes(resources.earlierRetirement)) stages.splice(stages.length - 1, 0, resources.earlierRetirement);
      const retired = this._retireLockstepResources(resources, deadline);
      cleanupErrors = cleanupErrors.concat(retired.cleanupErrors);
      if (!retired.confirmed || (resources.earlierRetirement && !resources.earlierRetirement.confirmed)) {
        finalCause = 'netplay_start_cleanup_unconfirmed';
        const status = (retired.late || resources.earlierRetirement?.late) ? 'timeout' : 'failed';
        stages.push(replayBoundary.stageResult('cleanup', status, finalCause));
        this._logBoundaryFailure({ operation: 'netplay_start', origin: 'start', stage: 'cleanup', status, cause: finalCause });
        this._unsafeReplayBoundary = resources.sessionEmu || this._unsafeReplayBoundary || {};
      }
    }
    this._flash(lockstepStart.startMessageFor(finalCause));
    const result = { start: 'refused', recording: 'off', stages, cleanupErrors: [...new Set(cleanupErrors)] };
    resources?.earlier?.coreTicket?.evidenceTargets.add(result);
    if (session) {
      if (this._lockstepSession === session) this._lockstepSession = null;
      session.settle(result);
    } else {
      this._lockstepStartOutcome = Promise.resolve(result);
    }
    return result;
  }

  /** A quarantined page leaves for the home page: reloading a room URL would rejoin the room (§6). */
  _leavePageAfterUncertainBoundary() { window.location.assign('/'); }

  _stopLockstep() {
    this._lockstepSession?.resources?.restoreHandlers?.();
    this.net.onLockstepReset = null;
    if (this._lockstepTimer) {
      clearTimeout(this._lockstepTimer);
      this._lockstepTimer = null;
    }
    this._stopMicLevelLoop();
    document.getElementById('btn-sidebar-mic')?.classList.remove('mic-active');
    if (this._voiceChat) {
      this._voiceChat.destroy();
      this._voiceChat = null;
    }
    if (this.lockstepConn) {
      this.lockstepConn.destroy();
      this.lockstepConn = null;
    }
    if (this.lockstep) {
      this.lockstep.destroy();
      this.lockstep = null;
    }
    this._restoreHUDLabels();
  }

  // ── Link Cable Netplay ─────────────────────────────────────

  async _showLinkCableRomPicker(roomCode) {
    const existing = document.getElementById('lobby-link-cable');
    if (existing) { existing.classList.remove('hidden'); return; }

    const lobby = document.querySelector('#screen-lobby .lobby');
    if (!lobby) return;

    const div = document.createElement('div');
    div.id = 'lobby-link-cable';
    div.className = 'card lobby-card';
    div.innerHTML = `
      <div class="card-label">Select Your Game</div>
      <div id="link-cable-host-rom" class="hidden" style="margin:8px 0"></div>
      <p id="link-cable-list-label" style="margin:8px 0;font-size:11px;color:var(--text-dim)">Pick your ROM for link cable play</p>
      <input id="link-cable-filter" type="text" placeholder="Filter..." autocomplete="off"
        style="width:100%;box-sizing:border-box;padding:6px 8px;margin-bottom:6px;font-size:11px;
        background:var(--bg-secondary);color:var(--text);border:1px solid var(--border);border-radius:4px;
        font-family:inherit">
      <div id="link-cable-rom-list" style="max-height:200px;overflow-y:auto;margin:8px 0"></div>
      <div id="link-cable-rom-status" style="margin-top:6px;font-size:10px;color:var(--text-dim)"></div>
    `;
    const leaveBtn = document.getElementById('btn-leave');
    lobby.insertBefore(div, leaveBtn);

    const list = document.getElementById('link-cable-rom-list');
    const status = document.getElementById('link-cable-rom-status');
    const hostRomDiv = document.getElementById('link-cable-host-rom');
    const listLabel = document.getElementById('link-cable-list-label');

    // Most link cable sessions are with the same ROM on both sides
    // (Tetris with Tetris, Pokemon Red with Pokemon Red). Fetch P1's
    // chosen ROM so we can present it as a one-click default and save
    // P2 from scrolling the full library. We match by content hash —
    // P1's romName is the extracted name (e.g. "Tetris.gb" from inside
    // "Tetris.zip"), which won't directly fetch from the library, but
    // the rom-index hash on each library entry uniquely identifies the
    // same content.
    let p1RomName = null;
    let p1RomHash = null;
    try {
      const infoRes = await fetch(`/api/rooms/${roomCode}`);
      if (infoRes.ok) {
        const info = await infoRes.json();
        p1RomName = info.romName || null;
        p1RomHash = info.romHash || null;
      }
    } catch { /* fall through — picker still works without it */ }

    let activeSelection = null;
    const selectRom = async (romName, displayName, indicatorEl) => {
      // Picking P2's cartridge is part of the room join that is already in
      // progress, not a competing launch. Starting a new launch generation
      // here makes the server's immediately-following game_start look stale,
      // leaving P2 in the lobby while P1 waits forever for the link peer.
      const roomOwner = {
        launchOwner: this._roomJoinLaunchOwner,
        expectedNet: this.net,
        expectedRoomCode: roomCode,
      };
      const selection = {};
      activeSelection = selection;
      const selectionIsCurrent = () => activeSelection === selection
        && this.state === State.LOBBY
        && this.net?.playerNumber === 2
        && document.getElementById('lobby-link-cable') === div
        && this._roomAssignmentIsCurrent(roomOwner);
      if (!selectionIsCurrent()) return;
      list.querySelectorAll('.rom-entry').forEach((e) => e.classList.remove('selected'));
      hostRomDiv.querySelector('.lc-host-rom-btn')?.classList.remove('selected');
      indicatorEl?.classList.add('selected');
      status.textContent = 'Loading...';
      const loaded = await this._selectLibraryRom(romName, null, {
        launchOwner: roomOwner.launchOwner,
        suppressPlayModePrompt: true,
        selectionIsCurrent,
      });
      if (!loaded || !selectionIsCurrent()) return;
      status.textContent = `✓ ${displayName}`;
      this.net.sendRomReady(this.romHash);
    };

    try {
      const res = await fetch(`/api/roms?system=${this.system.id}`);
      const roms = await res.json();
      if (!roms.length) {
        list.innerHTML = '<div style="color:var(--text-dim);font-size:11px">No ROMs available</div>';
        return;
      }

      const displayNames = this._resolveDisplayNames(roms);

      // Surface P1's ROM as the primary one-click option. Prefer hash
      // match (handles zipped library ROMs where the extracted name P1
      // shipped doesn't directly map to a library filename); fall back
      // to filename match if hash isn't available or unmatched.
      let p1Rom = null;
      if (p1RomHash) p1Rom = roms.find((r) => r.hash === p1RomHash);
      if (!p1Rom && p1RomName) p1Rom = roms.find((r) => r.name === p1RomName);
      if (p1Rom) {
        const p1DisplayName = displayNames.get(p1Rom.name) || p1Rom.name;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'lc-host-rom-btn';
        btn.style.cssText =
          'display:flex;flex-direction:column;align-items:center;gap:4px;width:100%;' +
          'padding:12px;font-family:inherit;font-size:13px;cursor:pointer;' +
          'background:var(--bg-secondary);color:var(--text);border:1px solid var(--accent);' +
          'border-radius:4px';
        btn.innerHTML =
          '<span style="font-size:9px;color:var(--accent);letter-spacing:2px">⚡ SAME GAME AS P1</span>' +
          `<span class="lc-host-rom-name">${p1DisplayName}</span>`;
        btn.addEventListener('click', () => selectRom(p1Rom.name, p1DisplayName, btn));
        hostRomDiv.appendChild(btn);
        hostRomDiv.classList.remove('hidden');
        listLabel.textContent = 'Or pick a different ROM';
      }

      for (const rom of roms) {
        if (rom.type === 'directory') continue;
        const entry = document.createElement('div');
        entry.className = 'rom-entry';
        const displayName = displayNames.get(rom.name) || rom.name;
        entry.innerHTML = `<span class="rom-entry-name" title="${displayName}">${displayName}</span>`;
        entry.style.cursor = 'pointer';
        entry.addEventListener('click', () => selectRom(rom.name, displayName, entry));
        list.appendChild(entry);
      }
      const filterInput = document.getElementById('link-cable-filter');
      filterInput?.addEventListener('input', () => {
        const q = filterInput.value.toLowerCase().trim();
        for (const entry of list.querySelectorAll('.rom-entry')) {
          const name = entry.textContent.toLowerCase();
          entry.classList.toggle('hidden', q && !name.includes(q));
        }
      });
    } catch {
      list.innerHTML = '<div style="color:var(--text-dim);font-size:11px">Failed to load ROM list</div>';
    }
  }

  _createLinkCableConnection(network, isInitiator, options, Connection) {
    if (arguments.length < 4) Connection = this._LinkCableConnectionClass;
    if (typeof Connection !== 'function') throw new Error('link-cable connection class not loaded');
    return new Connection(network, isInitiator, options);
  }

  async _loadLinkCableConnectionClass() {
    return (await import('./link-cable-connection.js')).LinkCableConnection;
  }

  async _loadLinkCableEngineClass(isGba) {
    this._LinkCableConnectionClass ||= await this._loadLinkCableConnectionClass();
    if (!isGba) return (await import('./link-cable-engine.js')).LinkCableEngine;
    return (await import('./link-cable-engine-gba.js')).GBALinkCableEngine;
  }

  async _startLinkCable(playerNum, assignmentOwner = null) {
    if (this._unsafeReplayBoundary) {
      this._flash('Reload the page to play again.');
      return false;
    }
    const assignmentIsCurrent = () => this._roomAssignmentIsCurrent(assignmentOwner);
    if (!assignmentIsCurrent()) return false;
    const expectedNet = assignmentOwner?.expectedNet || this.net;
    const lcDebug = new URLSearchParams(window.location.search).has('debug');
    const traceStart = performance.now();
    const trace = (event, data = undefined) => {
      if (!lcDebug) return;
      const dt = (performance.now() - traceStart).toFixed(1);
      const version = typeof BUILD_VERSION === 'string' ? BUILD_VERSION : 'dev';
      const sha = typeof BUILD_SHA === 'string' ? BUILD_SHA.slice(0, 7) : 'dev';
      if (data === undefined) {
        console.log(`[link-cable trace +${dt}ms main p${playerNum} v${version}/${sha}] ${event}`);
      } else {
        console.log(`[link-cable trace +${dt}ms main p${playerNum} v${version}/${sha}] ${event}`, data);
      }
    };

    trace('start-link-cable', {
      playerNum,
      system: this.system?.id,
      romBytes: this.romData?.byteLength || this.romData?.length || 0,
    });
    // A room-WS bounce funnels back here via the rejoin flow (assigned →
    // rom_ready → game_start) even though the P2P DataChannel never
    // dropped. Rebuilding would destroy a healthy connection and reboot
    // both players' games — resume in place instead. A genuinely dead
    // link never trips this: the connection's own recovery handles pc
    // failures, and exhausted/torn-down pairs report unhealthy.
    if (this._netMode === 'link-cable' && this._linkCableP2PHealthy()) {
      trace('skip-rebuild-p2p-healthy');
      this._resumeLinkCableAfterRoomBounce();
      return assignmentIsCurrent();
    }
    this.state = State.PLAYING;
    this._showScreen('game');
    const netModeKey = gameActionSessionKey(this);
    if (!gameActionSessionCurrent(this, netModeKey)) return false;
    this._netMode = 'link-cable';
    if (!gameActions.noteNetModeWrite(this, netModeKey)) return false;
    gameActions.refreshGameToolbarAvailability(this, 'net-mode');
    trace('screen-ready');

    // GB/GBC: regular self-driving core — each Game Boy runs independently
    // and we poll SIO registers (mirrors the _startSolo() path).
    // GBA: stepped core (LockstepLibretroCore) — the link engine drives
    // frames under a frame barrier so both emulated clocks advance
    // together; required by burst-validating link protocols (Mario Kart's
    // beacon arithmetic, F-Zero's commit groups).
    const isGba = this.system?.id === 'gba';
    const webrtcInitiator = playerNum === 1;
    const gbaIsMaster = playerNum === 1;
    trace('create-emulator-start');
    // Pin the system's multiplayer artifact explicitly. resolveCoreName also
    // ignores GB solo overrides when multiplayer=true, but this keeps the
    // link-cable launch safe even if another selector path changes later.
    // GBA has one core and therefore leaves coreName null.
    const sessionEmu = await this._createEmulator({
      multiplayer: true,
      lockstep: this._linkCableNeedsSteppedCore(),
      coreName: this.system?.coreMultiplayer || null,
    });
    const sessionInput = this.input, sessionSystem = this.system, sessionGeneration = this._emulatorGeneration;
    const emulatorIsCurrent = () => assignmentIsCurrent()
      && this.net === expectedNet
      && this.emu === sessionEmu
      && this.system === sessionSystem
      && this._emulatorGeneration === sessionGeneration
      && this.state === State.PLAYING;
    const emulatorCanContinue = () => emulatorIsCurrent() && !this._unsafeReplayBoundary;
    let ownedAutoSaveInterval = null;
    const retireOwnedEmulator = () => {
      if (ownedAutoSaveInterval) {
        clearInterval(ownedAutoSaveInterval);
        if (this._autoSaveInterval === ownedAutoSaveInterval) this._autoSaveInterval = null;
      }
      if (sessionEmu && this.emu === sessionEmu && this._emulatorGeneration === sessionGeneration) {
        this.running = false;
        if (this.input === sessionInput) sessionInput?.stop?.();
        if (this._unsafeReplayBoundary !== sessionEmu) {
          try { sessionEmu.destroy?.(); } catch { /* already retired */ }
          if (this.emu === sessionEmu) this.emu = null;
        }
      }
      return false;
    };
    if (!sessionEmu || !emulatorCanContinue()) return retireOwnedEmulator();
    // A failed prior connection may have paused the emulator that
    // _createEmulator just replaced. Never let that stale ownership suppress
    // pausing the fresh self-driving core on its next disconnect.
    if (this._linkCablePausedCore && this._linkCablePausedCore !== this.emu) {
      this._linkCablePausedCore = null;
    }
    trace('create-emulator-done', {
      core: this.emu?.constructor?.name,
      selfDriving: this.emu?.selfDriving,
      coreName: this.emu?.coreName,
    });

    if (this.emu instanceof LibretroCore) {
      trace('capture-key-handlers');
      this.emu.captureKeyHandlers();
    }

    if (isGba) {
      try {
        const mod = this.emu.module;
        trace('module-exports-preload', {
          hasEnable: typeof mod?._gba_link_cable_enable,
          hasSetConnected: typeof mod?._gba_link_cable_set_connected,
          playerNum,
          gbaIsMaster,
        });
        if (mod?._gba_link_cable_enable) {
          mod._gba_link_cable_enable(gbaIsMaster ? 1 : 0);
          mod._gba_link_cable_set_connected?.(1);
          trace('gba-driver-preinstalled');
        }
      } catch (e) {
        trace('gba-driver-preinstall-error', { message: e?.message });
        console.warn('[link-cable] failed to preinstall mGBA link driver:', e);
      }
    }

    trace('load-rom-start');
    this.emu.loadROM(this.romData, { injectInput: true });
    trace('load-rom-returned');
    let gbaDriverInstalled = false;
    if (isGba) {
      try {
        const mod = this.emu.module;
        if (mod?._gba_link_cable_enable) {
          mod._gba_link_cable_enable(gbaIsMaster ? 1 : 0);
          mod._gba_link_cable_set_connected?.(1);
          gbaDriverInstalled = true;
          trace('gba-driver-installed-postload');
        } else {
          trace('gba-driver-postload-missing');
        }
      } catch (e) {
        trace('gba-driver-postload-error', { message: e?.message });
        console.warn('[link-cable] failed to install mGBA link driver after ROM load:', e);
      }
    }

    // Activate link cable mode in the core. For GB/GBC (gambatte) this
    // prevents internal serial completion so our JS poller can intercept;
    // for GBA (mGBA) the driver registration above + the per-engine
    // `start()` handle it, so the gambatte-specific call is skipped.
    try {
      const mod = this.emu.module;
      if (isGba) {
        trace('module-exports', {
          hasEnable: typeof mod?._gba_link_cable_enable,
          hasSetRx: typeof mod?._gba_link_cable_set_rx,
          hasHasTx: typeof mod?._gba_link_cable_has_tx,
          hasSlaveExchange: typeof mod?._gba_link_cable_slave_exchange,
          hasConnected: typeof mod?._gba_link_cable_set_connected,
          hasNativeStats: typeof mod?._gba_link_cable_get_send_count,
        });
        if (lcDebug) console.log('[link-cable] mGBA module exists:', !!mod, 'has _gba_link_cable_enable:', typeof mod?._gba_link_cable_enable);
        // Driver registration happened immediately after loadROM if available.
      } else {
        trace('module-exports', {
          hasEnable: typeof mod?._link_cable_enable,
          hasSetRx: typeof mod?._link_cable_set_rx,
          hasHasTx: typeof mod?._link_cable_has_tx,
          hasHasRx: typeof mod?._link_cable_has_rx,
          hasConnected: typeof mod?._link_cable_set_connected,
          hasNativeStats: typeof mod?._link_cable_get_send_count,
        });
        if (lcDebug) console.log('[link-cable] module exists:', !!mod, 'has _link_cable_enable:', typeof mod?._link_cable_enable, 'has _link_cable_has_rx:', typeof mod?._link_cable_has_rx);
        if (mod?._link_cable_enable) {
          mod._link_cable_enable();
          trace('serial-intercept-enabled');
          if (lcDebug) console.log('[link-cable] C-level serial intercept enabled');
        } else {
          trace('serial-intercept-missing');
          if (lcDebug) console.warn('[link-cable] _link_cable_enable not found, serial transfers will complete internally');
        }
      }
    } catch (e) {
      trace('serial-intercept-error', { message: e?.message });
      console.error('[link-cable] failed to enable serial intercept:', e);
    }

    if (this.emu instanceof LibretroCore) {
      this._scheduleKeyHandlerCaptureRestore(sessionEmu, () => {
        trace('restore-key-handler-capture');
      });
    }

    this.canvas.tabIndex = 0;
    this.canvas.focus();
    if (!(await this._startInputForGameplay())) return false;
    this.running = true;
    gameActions.refreshGameToolbarAvailability(this, 'core-running');
    this.audio.init().catch(() => {});
    this._startAutoSaveTimer();
    ownedAutoSaveInterval = this._autoSaveInterval;
    if (!this.hostedNetplay) this._libretroInputLoop();
    trace('runtime-started', {
      inputStarted: true,
      running: this.running,
      hostedNetplay: !!this.hostedNetplay,
    });

    // Set up link cable SIO polling over WebRTC
    const payloadBytes = isGba ? 4 : 1;
    trace('connection-create', {
      payloadBytes,
      webrtcInitiator,
      gbaIsMaster,
      pendingLobbySwap: !!this._pendingLobbySwap,
    });
    // A rejoin can re-enter here while a previous engine/connection pair is
    // still live. Overwriting the references leaves the old engine's 4ms
    // barrier timer ticking against the SAME core — a zombie double-stepper
    // whose stale peer view trips its gate-breaker every 400ms and desyncs
    // the game within seconds. Tear the old pair down first.
    if (this.linkCableEngine) {
      this._stopGbaStateShadow();
      this._stopGbaPairedStateShadow();
      trace('destroy-stale-engine');
      this.linkCableEngine.destroy();
      this.linkCableEngine = null;
    }
    if (this._linkCableConnection) {
      trace('destroy-stale-connection');
      try { this._linkCableConnection.destroy(); } catch { /* already dead */ }
      this._linkCableConnection = null;
    }
    // Load both classes before constructing a signaling connection. If the
    // retained room is superseded during the dynamic import, there is no stale
    // connection callback to restore over a replacement session.
    let Engine, Connection;
    try {
      [Engine, Connection] = await Promise.all([this._loadLinkCableEngineClass(isGba), this._loadLinkCableConnectionClass()]);
    } catch (err) {
      if (!emulatorCanContinue()) return retireOwnedEmulator();
      retireOwnedEmulator();
      throw err;
    }
    if (!emulatorCanContinue()) return retireOwnedEmulator();
    const conn = this._createLinkCableConnection(expectedNet, webrtcInitiator, { payloadBytes }, Connection);
    const engine = new Engine(sessionEmu, conn, gbaIsMaster, {
      driverInstalled: gbaDriverInstalled,
      barrier: isGba,
      getLocalInput: () => this.input.poll(),
    });
    this.linkCableEngine = engine;
    // Link-distress telemetry: in-game COMMUNICATION ERRORs are screens,
    // not exceptions — the engine's stall watchdog + core counters are the
    // only way they reach error collection. Rate-limited engine-side;
    // deduped again by reportCrash's fingerprint window.
    if (isGba) {
      engine.onDistress = (report) => {
        if (!startupIsCurrent()) return;
        trace('link-distress', report);
        console.error('[gba-link-cable] distress:', report);
        reportCrash({
          error: { name: 'LinkCableDistress', message: `gba-link distress: ${report.kind} (sioMode=${report.sioMode}, role=${report.role})` },
          system: this.system?.id,
          romName: this.romName,
          romHash: this.romHash,
          appState: JSON.stringify({ netMode: this._netMode, ...report }).slice(0, 2000),
          frameCount: engine.core?.module?._gba_link_cable_get_diag?.(35) ?? null,
          uptime: this._serverSessionStartedAt ? Date.now() - this._serverSessionStartedAt : null,
          emu: this.emu,
          canvas: this.canvas,
        });
      };
    }
    this._linkCableConnection = conn;

    const startupIsCurrent = () => emulatorCanContinue()
      && this._linkCableConnection === conn
      && this.linkCableEngine === engine;
    const retireOwnedStartup = () => {
      if (this.linkCableEngine === engine) {
        engine.destroy();
        this.linkCableEngine = null;
      }
      if (this._linkCableConnection === conn) {
        try { conn.destroy(); } catch { /* already retired */ }
        this._linkCableConnection = null;
      }
      return retireOwnedEmulator();
    };

    this._wireLinkCableConnection(conn, trace, { engine, sessionIsCurrent: startupIsCurrent });

    trace('handshake-start');
    if (lcDebug) console.log('[link-cable] starting WebRTC handshake, player', playerNum);
    try {
      await conn.start();
    } catch (err) {
      if (!startupIsCurrent()) return retireOwnedStartup();
      throw err;
    }
    if (!startupIsCurrent()) return retireOwnedStartup();
    trace('handshake-start-returned');
    this._linkCableConnTimeout = setTimeout(() => {
      if (!startupIsCurrent()) return;
      if (conn.isUnusable?.()) return;
      if (!engine.running) {
        const state = conn.getDebugState?.();
        trace('connection-timeout', state);
        if (lcDebug) console.warn('[link-cable] connection timeout: DataChannel did not open', state);
        this._flash('Link cable connection failed. Try again');
      }
    }, 10000);
    return true;
  }

  // Connection lifecycle for link cable. First open starts the engine.
  // A mid-session drop is NOT terminal: the connection recovers itself
  // (ICE restart, then full pc rebuilds over room signaling). GBA freezes
  // because its stepped engine stops; GB/GBC must explicitly pause their
  // self-driving core before the native serial driver is marked disconnected,
  // or it advances on 0xFF bytes while the peer is absent. The engine resets
  // transient wire state, retains any destructively-read request for one
  // retransmit, and restarts once the channel re-opens.
  // onDisconnected fires only when recovery is exhausted.
  _wireLinkCableConnection(conn, trace, {
    engine = this.linkCableEngine,
    sessionIsCurrent = () => true,
  } = {}) {
    // The startup path supplies an identity-aware predicate. Keeping the
    // wiring helper's default predicate permissive preserves its use by the
    // isolated connection lifecycle tests and any diagnostics harness.
    const connectionIsCurrent = () => sessionIsCurrent();
    this._linkCableLatencyWarned = false;
    this._linkCableLatencyHigh = false;
    this._linkCableHighRttSamples = 0;
    this._linkCableLowRttSamples = 0;
    const isGba = this.system?.id === 'gba';
    let channelOpen = false;
    let localCompatible = !isGba;
    let remoteCompatible = !isGba;
    let compatibilityFailed = false;
    let engineStarted = false;
    let reconnecting = false;
    let gbInterruptionDeadlineTimer = null;
    let gbInterruptionFailure = null;

    if (isGba) conn.requireCompatibilityHandshake?.();

    const clearGbInterruptionDeadline = () => {
      if (gbInterruptionDeadlineTimer != null) {
        clearTimeout(gbInterruptionDeadlineTimer);
        gbInterruptionDeadlineTimer = null;
      }
    };

    const failExpiredGbInterruption = (safety) => {
      if (!connectionIsCurrent() || isGba || gbInterruptionFailure) return;
      gbInterruptionFailure = safety || { safe: false, reason: 'gb-native-wait-expired' };
      compatibilityFailed = true;
      engineStarted = false;
      reconnecting = false;
      channelOpen = false;
      clearGbInterruptionDeadline();
      clearTimeout(this._linkCableConnTimeout);
      clearInterval(this._linkCableHudInterval);
      engine.abortExpiredTransportInterruption?.();
      engine.stop();
      conn.markUnusable?.(gbInterruptionFailure.reason || 'gb-native-wait-expired');
      trace('gb-native-wait-expired', gbInterruptionFailure);
      this._flash('GB/GBC link recovery exceeded the serial deadline. Session stopped safely');
      this._showSysStatus('Link stopped safely', 'error');
    };

    const armGbInterruptionDeadline = (safety) => {
      clearGbInterruptionDeadline();
      if (isGba || !safety?.pendingNativeWait) return;
      const remainingMs = Number(safety.remainingMs);
      if (!safety.safe || !Number.isFinite(remainingMs) || remainingMs <= 0) {
        failExpiredGbInterruption(safety);
        return;
      }
      gbInterruptionDeadlineTimer = setTimeout(() => {
        gbInterruptionDeadlineTimer = null;
        if (!connectionIsCurrent()) return;
        const current = engine.getTransportInterruptionSafety?.();
        if (current?.safe === false) failExpiredGbInterruption(current);
      }, Math.ceil(remainingMs) + 1);
    };

    const failCompatibility = (source) => {
      if (!connectionIsCurrent() || compatibilityFailed) return;
      compatibilityFailed = true;
      engineStarted = false;
      reconnecting = false;
      clearGbInterruptionDeadline();
      clearTimeout(this._linkCableConnTimeout);
      clearInterval(this._linkCableHudInterval);
      engine.stop();
      conn.markUnusable?.(`${source}-core-incompatible`);
      trace('core-compatibility-failed', { source });
      this._flash(`${isGba ? 'GBA' : 'GB/GBC'} link core mismatch. Both players should clear browser cache and reload`);
      this._showSysStatus('Link unavailable', 'error');
    };

    const startEngine = () => {
      if (!connectionIsCurrent() || !channelOpen || !localCompatible || !remoteCompatible
          || compatibilityFailed || engineStarted) return;
      if (!isGba) {
        const interruptionSafety = engine.getTransportInterruptionSafety?.();
        if (interruptionSafety?.safe === false) {
          failExpiredGbInterruption(interruptionSafety);
          conn.sendCompatibility?.(false);
          return;
        }
      }
      const started = engine.start();
      if (started === false) {
        conn.sendCompatibility?.(false);
        const failedSafety = !isGba
          ? engine.getTransportInterruptionSafety?.()
          : null;
        if (failedSafety?.safe === false) failExpiredGbInterruption(failedSafety);
        else failCompatibility('local');
        return;
      }
      if (!isGba) {
        const resumedSafety = engine.getTransportInterruptionSafety?.();
        armGbInterruptionDeadline(resumedSafety);
        if (gbInterruptionFailure) {
          conn.sendCompatibility?.(false);
          return;
        }
      }
      conn.acceptCompatibility?.();
      engineStarted = true;
      clearTimeout(this._linkCableConnTimeout);
      clearInterval(this._linkCableHudInterval);
      if (reconnecting) {
        reconnecting = false;
        this._flash('Link cable reconnected!');
        this._resumeGbLinkCableAfterReconnect();
        trace('engine-restarted');
      } else {
        // Link-cable previously started no server play session at all, so its
        // otherwise valid end payload/heartbeat path never ran. Begin only
        // after the P2P channel and compatibility checks succeed, matching the
        // other multiplayer transports' definition of a real session.
        this._trackFunnelConnected();
        this._startPlaySession();
        this._armFunnelBootInference();
        this._flash('Link cable connected!');
        trace('engine-started');
      }
      this._showSysStatus('Link Cable', 'ok');
      this._linkCableHudInterval = setInterval(() => {
        if (!connectionIsCurrent()) return;
        const el = document.getElementById('stat-frame');
        if (el) el.textContent = `${engine.exchangeCount} xfer`;
      }, 500);
    };

    conn.onRemoteCompatibility = (compatible) => {
      if (!connectionIsCurrent()) return;
      trace('peer-core-compatibility', { compatible });
      if (!compatible) {
        failCompatibility('peer');
        return;
      }
      remoteCompatible = true;
      startEngine();
    };

    conn.onP2PRtt = (rtt) => {
      if (!connectionIsCurrent()) return;
      const ping = document.getElementById('stat-ping');
      if (ping && Number.isFinite(rtt)) ping.textContent = `${rtt}ms`;
      if (!isGba || !engineStarted || !Number.isFinite(rtt)) return;

      if (rtt >= GBA_LINK_RTT_WARN_MS) {
        this._linkCableHighRttSamples++;
        this._linkCableLowRttSamples = 0;
        if (this._linkCableHighRttSamples >= GBA_LINK_RTT_WARN_SAMPLES
            && !this._linkCableLatencyHigh) {
          this._linkCableLatencyHigh = true;
          if (!this._linkCableLatencyWarned) {
            this._linkCableLatencyWarned = true;
            this._flash(`High GBA link latency (${rtt}ms RTT). Gameplay may run below full speed`);
          }
          this._showSysStatus('Link Cable: high latency', 'warn');
        }
        return;
      }

      this._linkCableHighRttSamples = 0;
      if (!this._linkCableLatencyHigh) {
        this._linkCableLowRttSamples = 0;
        return;
      }
      this._linkCableLowRttSamples++;
      if (this._linkCableLowRttSamples >= GBA_LINK_RTT_RECOVER_SAMPLES) {
        this._linkCableLatencyHigh = false;
        this._linkCableLowRttSamples = 0;
        this._showSysStatus('Link Cable', 'ok');
      }
    };

    conn.onConnected = () => {
      if (!connectionIsCurrent()) return;
      trace('connection-open');
      channelOpen = true;
      if (!isGba) {
        startEngine();
        return;
      }
      localCompatible = (engine.missingBarrierExports?.().length ?? 1) === 0;
      conn.sendCompatibility?.(localCompatible);
      if (!localCompatible) {
        failCompatibility('local');
        return;
      }
      startEngine();
    };

    conn.onReconnecting = (reason) => {
      if (!connectionIsCurrent()) return;
      trace('connection-reconnecting', { reason });
      this._pauseGbLinkCableForReconnect();
      const interruptionSafety = engine.prepareForTransportInterruption?.();
      if (isGba && interruptionSafety?.safe === false) {
        // The paired candidate already advanced P1, while a dropped transport
        // cannot prove whether P2 completed the corresponding transfer/IRQ.
        // With no rollback state, restarting would risk silent divergence.
        compatibilityFailed = true;
        reconnecting = false;
        channelOpen = false;
        engineStarted = false;
        engine.stop();
        conn.markUnusable?.(interruptionSafety.reason);
        this._flash('Experimental GBA link acceleration interrupted. Session stopped safely');
        this._showSysStatus('Link stopped safely', 'error');
        trace('paired-shadow-interruption-failed-closed', interruptionSafety);
        return;
      }
      if (!isGba) {
        armGbInterruptionDeadline(interruptionSafety);
        if (gbInterruptionFailure) return;
      }
      reconnecting = true;
      channelOpen = false;
      engineStarted = false;
      // Re-gate both peers before any rebuilt/healed channel resumes. This
      // lets a GB side whose native deadline expired reject its peer before
      // that peer restarts gameplay.
      remoteCompatible = false;
      conn.requireCompatibilityHandshake?.();
      this._linkCableLatencyHigh = false;
      this._linkCableHighRttSamples = 0;
      this._linkCableLowRttSamples = 0;
      engine.stop();
      this._flash('Link cable dropped, reconnecting...');
      this._showSysStatus('Link reconnecting...', 'warn');
    };

    conn.onReconnected = ({ transportRebuilt = false } = {}) => {
      if (!connectionIsCurrent()) return;
      trace('connection-reconnected', { transportRebuilt });
      clearGbInterruptionDeadline();
      if (!isGba) {
        const interruptionSafety = engine.getTransportInterruptionSafety?.();
        if (!gbInterruptionFailure && interruptionSafety?.safe === false) {
          if (transportRebuilt) engine.resetTransport?.();
          failExpiredGbInterruption(interruptionSafety);
        } else if (!gbInterruptionFailure && transportRebuilt) {
          const resetSafety = engine.resetTransport?.();
          if (resetSafety?.safe === false) failExpiredGbInterruption(resetSafety);
        } else if (gbInterruptionFailure && transportRebuilt) {
          engine.resetTransport?.();
        }
        if (gbInterruptionFailure) {
          // The rebuilt channel is useful only to tell the peer to stop too;
          // no serial byte from the expired native wait may cross it.
          conn.sendCompatibility?.(false);
          trace('gb-native-wait-peer-rejected', gbInterruptionFailure);
          return;
        }
      }
      if (compatibilityFailed || conn.isUnusable?.()) {
        trace('connection-reconnected-ignored', { reason: 'connection-unusable' });
        return;
      }
      if (!isGba) conn.sendCompatibility?.(true);
      channelOpen = true;
      if (transportRebuilt && isGba) engine.resetTransport?.();
      if (isGba) {
        localCompatible = (engine.missingBarrierExports?.().length ?? 1) === 0;
        conn.sendCompatibility?.(localCompatible);
        if (!localCompatible) {
          failCompatibility('local');
          return;
        }
      }
      startEngine();
    };

    conn.onDisconnected = () => {
      if (!connectionIsCurrent()) return;
      trace('connection-disconnected');
      clearGbInterruptionDeadline();
      if (gbInterruptionFailure) return;
      channelOpen = false;
      engineStarted = false;
      reconnecting = false;
      this._pauseGbLinkCableForReconnect();
      engine.stop();
      this._showSysStatus('Link lost', 'error');
      this._flash('Link cable disconnected');
    };
  }

  _pauseGbLinkCableForReconnect() {
    const core = this.emu;
    if (this._linkCablePausedCore === core) return;
    if (!core?.selfDriving || !['gb', 'gbc'].includes(this.system?.id)) return;
    core.pause?.();
    this._linkCablePausedCore = core;
  }

  _resumeGbLinkCableAfterReconnect() {
    const core = this._linkCablePausedCore;
    if (!core) return;
    this._linkCablePausedCore = null;
    // A stale connection callback must never resume the replacement core.
    if (core === this.emu) core.resume?.();
  }

  // Research-only state-aware response generator. It is started explicitly
  // by the headed acceptance rig on GBA P2; production sessions never pay for
  // a second core. Its wire type is observational and cannot be consumed.
  async _startGbaStateShadow({ aheadFrames = 1 } = {}) {
    this._stopGbaStateShadow();
    this._stopGbaPairedStateShadow();
    if (this.system?.id !== 'gba' || !this.linkCableEngine?.running
        || this.linkCableEngine.isMaster || !this._linkCableConnection) {
      return { active: false, reason: 'running-gba-p2-required' };
    }
    const { GBAStateShadow } = await import('./gba-state-shadow.js');
    const shadow = new GBAStateShadow({
      liveCore: this.emu,
      liveEngine: this.linkCableEngine,
      liveConnection: this._linkCableConnection,
      romData: this.romData,
      aheadFrames,
    });
    this._gbaStateShadow = shadow;
    return shadow.start();
  }

  async _resynchronizeGbaStateShadow() {
    if (!this._gbaStateShadow) return { active: false, reason: 'not-created' };
    return this._gbaStateShadow.resynchronize();
  }

  _getGbaStateShadowStats() {
    return this._gbaStateShadow?.getStats?.() || null;
  }

  _stopGbaStateShadow() {
    if (!this._gbaStateShadow) return;
    try { this._gbaStateShadow.destroy(); } catch { /* shadow teardown must not affect play */ }
    this._gbaStateShadow = null;
  }

  async _holdGbaPairedShadowSeed({ timeoutMs = 3000 } = {}) {
    if (this.system?.id !== 'gba' || !this.linkCableEngine?.running) {
      return { ok: false, reason: 'running-gba-required' };
    }
    return this.linkCableEngine.requestPairedShadowSeedHold?.({ timeoutMs })
      || { ok: false, reason: 'paired-seed-hold-unavailable' };
  }

  _releaseGbaPairedShadowSeedHold() {
    return this.linkCableEngine?.releasePairedShadowSeedHold?.()
      || { ok: false, reason: 'paired-seed-hold-unavailable' };
  }

  _getGbaPairedShadowSeedHoldStats() {
    return this.linkCableEngine?.getPairedShadowSeedHoldStats?.() || null;
  }

  async _captureGbaPairedShadowSeed({ requireCoordinatedHold = false } = {}) {
    if (this.system?.id !== 'gba' || !this.linkCableEngine?.running) {
      return { ok: false, reason: 'running-gba-required' };
    }
    if (requireCoordinatedHold && !this.linkCableEngine.isPairedShadowSeedHeld?.()) {
      return { ok: false, reason: 'coordinated-paired-seed-hold-required' };
    }
    const { captureGbaPairedShadowSeed } = await import('./gba-paired-state-shadow.js');
    const seed = await captureGbaPairedShadowSeed({
      liveCore: this.emu,
      liveEngine: this.linkCableEngine,
      role: this.linkCableEngine.isMaster ? 'master' : 'slave',
    });
    if (!seed.ok) return seed;
    return {
      ...seed,
      snapshot: undefined,
      snapshotBase64: u8ToBase64(seed.snapshot),
      nativeSerialSlice: seed.nativeSerialSlice ? {
        ...seed.nativeSerialSlice,
        bytes: undefined,
        bytesBase64: u8ToBase64(seed.nativeSerialSlice.bytes),
      } : null,
    };
  }

  _decodeGbaPairedShadowSeed(seed) {
    if (!seed?.ok || typeof seed.snapshotBase64 !== 'string') return seed;
    const nativeSerialSlice = seed.nativeSerialSlice;
    return {
      ...seed,
      snapshot: base64ToU8(seed.snapshotBase64),
      snapshotBase64: undefined,
      nativeSerialSlice: nativeSerialSlice
        && typeof nativeSerialSlice.bytesBase64 === 'string' ? {
          ...nativeSerialSlice,
          bytes: base64ToU8(nativeSerialSlice.bytesBase64),
          bytesBase64: undefined,
        } : nativeSerialSlice,
    };
  }

  async _prepareGbaPairedStateShadow({
    aheadFrames = 2,
    identityMode = 'exact-frame',
    fingerprintInterval = 0,
    responderDiagnostics = false,
  } = {}) {
    this._stopGbaPairedStateShadow();
    this._stopGbaStateShadow();
    if (this.system?.id !== 'gba' || !this.linkCableEngine?.running
        || !this.linkCableEngine.isMaster) {
      return { active: false, prepared: false, reason: 'running-gba-p1-required' };
    }
    const { GBAPairedStateShadow } = await import('./gba-paired-state-shadow.js');
    const shadow = new GBAPairedStateShadow({
      liveCore: this.emu,
      liveEngine: this.linkCableEngine,
      romData: this.romData,
      aheadFrames,
      identityMode,
      fingerprintInterval,
      responderDiagnostics,
    });
    this._gbaPairedStateShadow = shadow;
    return shadow.prepare();
  }

  async _startGbaPairedStateShadow({
    masterSeed,
    slaveSeed,
    aheadFrames = 2,
    identityMode = 'exact-frame',
    fingerprintInterval = 0,
    responderDiagnostics = false,
  } = {}) {
    this._stopGbaStateShadow();
    if (this.system?.id !== 'gba' || !this.linkCableEngine?.running
        || !this.linkCableEngine.isMaster) {
      return { active: false, reason: 'running-gba-p1-required' };
    }
    let shadow = this._gbaPairedStateShadow;
    if (!shadow || shadow.aheadFrames !== (aheadFrames | 0)
        || shadow.identityMode !== identityMode
        || shadow.fingerprintInterval !== (fingerprintInterval | 0)
        || shadow.responderDiagnostics !== !!responderDiagnostics) {
      this._stopGbaPairedStateShadow();
      const { GBAPairedStateShadow } = await import('./gba-paired-state-shadow.js');
      shadow = new GBAPairedStateShadow({
        liveCore: this.emu,
        liveEngine: this.linkCableEngine,
        romData: this.romData,
        aheadFrames,
        identityMode,
        fingerprintInterval,
        responderDiagnostics,
      });
    }
    this._gbaPairedStateShadow = shadow;
    return shadow.start(
      this._decodeGbaPairedShadowSeed(masterSeed),
      this._decodeGbaPairedShadowSeed(slaveSeed),
    );
  }

  async _resynchronizeGbaPairedStateShadow({ masterSeed, slaveSeed } = {}) {
    if (!this._gbaPairedStateShadow) return { active: false, reason: 'not-created' };
    return this._gbaPairedStateShadow.resynchronize(
      this._decodeGbaPairedShadowSeed(masterSeed),
      this._decodeGbaPairedShadowSeed(slaveSeed),
    );
  }

  async _resumeGbaPairedStateShadowAfterReconnect() {
    if (!this._gbaPairedStateShadow) return { active: false, reason: 'not-created' };
    return this._gbaPairedStateShadow.resumeAfterTransportReset();
  }

  _armGbaPairedStateShadowTransportResume() {
    if (!this._gbaPairedStateShadow) return { armed: false, reason: 'not-created' };
    return this._gbaPairedStateShadow.armTransportResume();
  }

  _getGbaPairedStateShadowStats() {
    return this._gbaPairedStateShadow?.getStats?.() || null;
  }

  _stopGbaPairedStateShadow() {
    if (!this._gbaPairedStateShadow) return;
    try { this._gbaPairedStateShadow.destroy(); } catch { /* shadow teardown must not affect play */ }
    this._gbaPairedStateShadow = null;
  }

  _stopLinkCable() {
    clearTimeout(this._linkCableConnTimeout);
    clearInterval(this._linkCableHudInterval);
    this._stopGbaStateShadow();
    this._stopGbaPairedStateShadow();
    if (this.linkCableEngine) {
      this.linkCableEngine.destroy();
      this.linkCableEngine = null;
    }
    // The engine's destroy() only drops its reference — close the P2P pair
    // here, or a still-healthy leftover connection would make the next
    // _startLinkCable's resume-in-place guard skip a legitimate rebuild.
    if (this._linkCableConnection) {
      try { this._linkCableConnection.destroy(); } catch { /* already dead */ }
      this._linkCableConnection = null;
    }
    this._linkCablePausedCore = null;
    this._linkCableLatencyWarned = false;
    this._linkCableLatencyHigh = false;
    this._linkCableHighRttSamples = 0;
    this._linkCableLowRttSamples = 0;
  }

  // True while link-cable play has a live end-to-end P2P pair. While this
  // holds, the room websocket is only presence/signaling — its bounces are
  // survivable and must not tear the session down.
  _linkCableP2PHealthy() {
    return !!this._linkCableConnection?.isHealthy?.();
  }

  // A room-WS bounce paused page-level bookkeeping (rAF input pump, rejoin
  // flags, reconnect overlay) while the P2P link kept exchanging. Resume in
  // place — the engine and connection were never touched.
  _resumeLinkCableAfterRoomBounce() {
    if (this._unsafeReplayBoundary) {
      this._flash('Reload the page to play again.');
      return false;
    }
    this._selfReconnecting = false;
    this._waitingForRejoin = false;
    this._hideReconnectOverlay();
    this.state = State.PLAYING;
    this._showScreen('game');
    if (!this.running) {
      this.running = true;
      gameActions.refreshGameToolbarAvailability(this, 'core-running');
      if (!this.hostedNetplay) this._libretroInputLoop();
    }
  }

  // ── Rejoin ───────────────────────────────────────────────

  async _prepareRejoin(roomCode, assignmentOwner = null) {
    const assignmentIsCurrent = () => this._roomAssignmentIsCurrent(assignmentOwner);
    if (!assignmentIsCurrent()) return;
    try {
      // Live link-cable session whose P2P link survived a room bounce:
      // the ROM is already loaded — just re-register ready state with the
      // room server so it counts us as present again.
      if (this._netMode === 'link-cable' && this._linkCableP2PHealthy() && this.romData) {
        if (assignmentIsCurrent()) {
          (assignmentOwner?.expectedNet || this.net).sendRomReady(this.romHash);
        }
        return;
      }
      this._flash('Rejoining game...');

      const isHosted = !isLockstepEnabled(this.system?.id, this._roomCoreName, this.romData) &&
                       (this.system?.netplayMode === 'hosted' ||
                       (this.system?.rollbackCapable && this.system?.preferHosted));
      if (isHosted) {
        // A fresh P3/P4 joining an in-progress hosted room has no receiver yet.
        // Construct HostedNetplay (which installs/buffers the signal handler) and
        // create its RTCPeerConnection before rom_ready tells the host it is safe
        // to offer. The ensuing rejoin game_start is intentionally ignored because
        // _startHostedGuest has already transitioned this client to PLAYING.
        const started = await this._startHostedGuest(assignmentOwner);
        if (started === false || !assignmentIsCurrent()) return;
        (assignmentOwner?.expectedNet || this.net).sendRomReady('hosted-guest');
        return;
      }

      // Lockstep or rollback: download ROM and signal ready
      const rom = await this._downloadROM(roomCode);
      if (!assignmentIsCurrent()) return;
      if (!rom) {
        this._flash('No ROM shared in this room');
        return;
      }
      await this._installRoomRom(rom, { isCurrent: assignmentIsCurrent });
      if (!assignmentIsCurrent()) return;
      (assignmentOwner?.expectedNet || this.net).sendRomReady(this.romHash);
    } catch {
      if (!assignmentIsCurrent()) return;
      this._flash('Failed to rejoin');
    }
  }

  // ── Spectator Mode ───────────────────────────────────────

  /**
   * Join as a WebRTC spectator — receive video+audio stream from the host.
   * No ROM download or local emulator needed.
   */
  async _prepareAndStartSpectator(roomCode, assignmentOwner = null) {
    const expectedNet = assignmentOwner?.expectedNet || this.net;
    const spectatorId = expectedNet?.spectatorId;
    let ownedPeer = null;
    const assignmentIsCurrent = () => this._roomAssignmentIsCurrent(assignmentOwner);
    const spectatorIsCurrent = () => (
      assignmentIsCurrent()
      && this.net === expectedNet
      && expectedNet?.roomCode === roomCode
      && expectedNet?.spectatorId === spectatorId
      && ownedPeer !== null && this._spectatorPC === ownedPeer
      && this.state === State.SPECTATING
    );
    if (!assignmentIsCurrent()) return false;
    try {
      // A C64 watch invitation can arrive on a cold page before the optional
      // room chunk. Settle it before the session chooses its permanent layout.
      if (this.system?.id === 'c64') {
        await this._refreshFeatureFlags();
        if (!assignmentIsCurrent()) return false;
        if (moduleRegistry.getFeatures().game_room_v2 === true) await loadGameRoomChunk(this);
        if (!assignmentIsCurrent()) return false;
      }
      this._spectatorPC?.close();
      this._spectatorPC = null;
      this._flash('Joining as spectator...');

      this.state = State.SPECTATING;
      this._showScreen('game');
      this._updateResetButtonVisibility();
      this._updatePresenceList();

      // Hide the emulator canvas, show the video element for the remote stream
      if (this.canvas) this.canvas.style.display = 'none';
      const videoEl = document.getElementById('guest-video');
      if (videoEl) videoEl.classList.remove('hidden');

      // Create a lightweight RTCPeerConnection to receive the host's stream
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
        ],
      });
      this._spectatorPC = ownedPeer = pc;

      const bindPeer = (peer) => {
        // Receive video + audio tracks from the host.
        peer.ontrack = (e) => {
          if (!spectatorIsCurrent() || ownedPeer !== peer) return;
          this._setSpectatorStream(videoEl, e.streams[0]);
        };
        // Send ICE candidates to the host via the server (spectator → server → host).
        peer.onicecandidate = (e) => {
          if (e.candidate && spectatorIsCurrent() && ownedPeer === peer) {
            expectedNet.sendSignal({ type: 'ice', candidate: e.candidate });
          }
        };
        peer.onconnectionstatechange = () => {
          if (!spectatorIsCurrent() || ownedPeer !== peer) return;
          const s = peer.connectionState;
          if (s === 'connected') this._showSysStatus('Watching live', 'ok');
          else if (s === 'connecting') this._showSysStatus('Connecting...', 'warn');
          else if (s === 'failed' || s === 'disconnected') {
            this._flash('Stream disconnected');
          }
        };
      };
      bindPeer(pc);

      // Handle WebRTC signals from the host (offer, ICE).
      // If the host transitions (e.g. solo→hosted upgrade), the old PC
      // dies and the host sends a fresh offer. Recreate the PC on demand.
      this.net.onSignal = async (payload) => {
        if (!spectatorIsCurrent()) return;
        try {
          if (payload.type === 'offer') {
            let cur = this._spectatorPC;
            if (!cur || cur.connectionState === 'closed' || cur.connectionState === 'failed') {
              if (cur) cur.close();
              cur = new RTCPeerConnection({
                iceServers: [
                  { urls: 'stun:stun.l.google.com:19302' },
                  { urls: 'stun:stun1.l.google.com:19302' },
                ],
              });
              this._spectatorPC = ownedPeer = cur;
              bindPeer(cur);
              console.log('[spectator] Recreated PC for new host offer');
            }
            await cur.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: payload.sdp }));
            if (!spectatorIsCurrent() || ownedPeer !== cur) return;
            const answer = await cur.createAnswer();
            if (!spectatorIsCurrent() || ownedPeer !== cur) return;
            await cur.setLocalDescription(answer);
            if (!spectatorIsCurrent() || ownedPeer !== cur) return;
            expectedNet.sendSignal({ type: 'answer', sdp: answer.sdp });
            console.log('[spectator] Sent answer to host');
          } else if (payload.type === 'ice' && payload.candidate) {
            if (this._spectatorPC) {
              await this._spectatorPC.addIceCandidate(new RTCIceCandidate(payload.candidate));
            }
          }
        } catch (err) {
          if (spectatorIsCurrent()) console.error('[spectator] Signal error:', err);
        }
      };

      this.running = true;
      gameActions.refreshGameToolbarAvailability(this, 'core-running');
      if (!spectatorIsCurrent()) return false;
      expectedNet.sendSpectatorReady?.();
      console.log('[spectator] WebRTC spectator ready, waiting for host offer...');
      return true;
    } catch (err) {
      if (!assignmentIsCurrent()) return false;
      console.error('[spectator] Setup failed:', err);
      this._flash('Failed to join as spectator');
      return false;
    }
  }

  /** Attach a new spectator stream muted so video autoplay can begin. */
  _setSpectatorStream(videoEl, stream) {
    if (!videoEl || !stream) return false;
    if (videoEl.srcObject === stream) {
      this._syncSpectatorWebAudioTracks(stream);
      if (videoEl.muted && this._liveSpectatorAudioTracks(stream).length && !this._spectatorAudioCtx) {
        this._showSpectatorUnmute(videoEl);
      }
      return false;
    }
    this._teardownSpectatorAudio();
    if (this._specUnmuteOverlay) {
      this._specUnmuteOverlay.remove();
      this._specUnmuteOverlay = null;
    }
    videoEl.srcObject = stream;
    videoEl.muted = true;
    videoEl.play().catch(() => {});
    // RTCPeerConnection fires ontrack once per remote track. Video commonly
    // arrives first, so do not offer an audio action that is guaranteed to
    // fail before the matching audio-track event updates this same stream.
    if (this._liveSpectatorAudioTracks(stream).length) {
      this._showSpectatorUnmute(videoEl);
    }
    return true;
  }

  /**
   * Build an audible path for spectator game audio from a trusted click.
   * The Web Audio path handles browsers (notably iOS/Safari) that keep a
   * remote media element silent even after its muted property is cleared.
   */
  _enableSpectatorAudio(videoEl) {
    const stream = videoEl?.srcObject;
    if (this._spectatorAudioAttempt?.stream === stream) {
      return this._spectatorAudioAttempt.promise;
    }

    const attempt = { stream, promise: null };
    attempt.promise = (async () => {
      try {
        return await this._performSpectatorAudioEnable(videoEl, stream);
      } finally {
        if (this._spectatorAudioAttempt === attempt) this._spectatorAudioAttempt = null;
      }
    })();
    this._spectatorAudioAttempt = attempt;
    return attempt.promise;
  }

  async _performSpectatorAudioEnable(videoEl, stream) {
    const audioTracks = this._liveSpectatorAudioTracks(stream);
    if (!audioTracks.length) throw new Error('Spectator stream has no live audio track');

    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) {
      // Browsers without Web Audio fall back to the media element. It is the
      // only audible path in this mode.
      this._teardownSpectatorAudio();
      videoEl.muted = false;
      await videoEl.play();
      return 'media-element';
    }

    // Web Audio owns output in this mode. Keep the video element muted so the
    // same remote tracks cannot also play through the media-element path.
    this._teardownSpectatorAudio();
    videoEl.muted = true;
    const ctx = new Ctx();
    this._spectatorAudioCtx = ctx;

    const anchorPlayPromises = audioTracks
      .map(audioTrack => this._routeSpectatorWebAudioTrack(ctx, audioTrack));

    // Construct every anchor and request playback before awaiting so all calls
    // retain the click's transient user activation on strict mobile browsers.
    await Promise.all([ctx.resume(), ...anchorPlayPromises]);
    return 'web-audio';
  }

  _liveSpectatorAudioTracks(stream) {
    return (stream?.getAudioTracks?.() || [])
      .filter(track => track?.kind === 'audio' && track.readyState !== 'ended');
  }

  _routeSpectatorWebAudioTrack(ctx, audioTrack) {
    const audioOnly = new MediaStream([audioTrack]);
    const anchor = new Audio();
    anchor.srcObject = audioOnly;
    anchor.muted = true;
    this._spectatorAudioAnchors.push(anchor);

    const source = ctx.createMediaStreamSource(audioOnly);
    source.connect(ctx.destination);
    this._spectatorAudioSources.push(source);
    this._spectatorAudioTracks.push(audioTrack);
    return Promise.resolve(anchor.play());
  }

  /** Add tracks introduced by same-stream renegotiation to the running graph. */
  _syncSpectatorWebAudioTracks(stream) {
    const ctx = this._spectatorAudioCtx;
    if (!ctx || ctx.state === 'closed') return 0;
    const routedTracks = new Set(this._spectatorAudioTracks || []);
    const additions = this._liveSpectatorAudioTracks(stream)
      .filter(track => !routedTracks.has(track));
    for (const audioTrack of additions) {
      try {
        this._routeSpectatorWebAudioTrack(ctx, audioTrack).catch(err => {
          console.warn('[spectator] Late audio track anchor failed:', err);
        });
      } catch (err) {
        console.warn('[spectator] Late audio track setup failed:', err);
      }
    }
    return additions.length;
  }

  _teardownSpectatorAudio() {
    for (const source of this._spectatorAudioSources || []) {
      try { source.disconnect(); } catch { /* ignore */ }
    }
    this._spectatorAudioSources = [];
    const ctx = this._spectatorAudioCtx;
    this._spectatorAudioCtx = null;
    if (ctx) {
      try { ctx.close().catch(() => {}); } catch { /* ignore */ }
    }
    for (const anchor of this._spectatorAudioAnchors || []) {
      try { anchor.pause(); } catch { /* ignore */ }
      anchor.srcObject = null;
    }
    this._spectatorAudioAnchors = [];
    this._spectatorAudioTracks = [];
  }

  /** Show an unmute overlay on the spectator video (iOS audio policy). */
  _showSpectatorUnmute(videoEl) {
    if (this._specUnmuteOverlay?.isConnected) return;
    this._specUnmuteOverlay = null;
    const parent = videoEl?.parentElement;
    if (!parent) return;
    const overlay = document.createElement('div');
    overlay.id = 'spectator-unmute-overlay';
    overlay.style.cssText = `
      position: absolute; inset: 0; display: flex; align-items: center;
      justify-content: center; background: rgba(0,0,0,0.6);
      cursor: pointer; z-index: 10; font-family: var(--font-pixel);
      font-size: 14px; color: #fff;
    `;
    overlay.textContent = 'Tap to enable audio';
    overlay.addEventListener('click', async () => {
      if (overlay.getAttribute('aria-busy') === 'true') return;
      const stream = videoEl.srcObject;
      overlay.setAttribute('aria-busy', 'true');
      overlay.textContent = 'Enabling audio...';
      try {
        await this._enableSpectatorAudio(videoEl);
        if (this._specUnmuteOverlay !== overlay || videoEl.srcObject !== stream) return;
        overlay.remove();
        this._specUnmuteOverlay = null;
      } catch (err) {
        if (this._specUnmuteOverlay !== overlay || videoEl.srcObject !== stream) return;
        videoEl.muted = true;
        this._teardownSpectatorAudio();
        overlay.textContent = 'Tap to retry audio';
        console.warn('[spectator] Audio setup failed, retry available:', err);
      } finally {
        if (this._specUnmuteOverlay === overlay) overlay.setAttribute('aria-busy', 'false');
      }
    });
    if (!parent.style.position) parent.style.position = 'relative';
    parent.appendChild(overlay);
    this._specUnmuteOverlay = overlay;
  }

  // ── Side-by-Side (two-stream) ────────────────────────────
  //
  // Inbound sink plumbing + spectator runtime + two-panel stage for
  // side-by-side rooms. A participant subscribes to a racer "source"
  // identified by `srcSlot` (1|2): it receives that source's offer/ICE
  // via `net.onSignal(payload)` (with `payload.srcSlot`) and answers/ICEs
  // back via `net.sendSignalToSlot(srcSlot, …)`. A spectator sinks BOTH
  // slots; a racer (built in a later task) sinks only the opponent.
  // These helpers are shared by both roles; the racer-runtime task sets
  // `this._sbsMySlot`/`this._sbsOpponentSlot` before calling
  // `_buildSbsStage('racer')`.

  /** Get-or-create the RTCPeerConnection that receives racer `srcSlot`'s stream. */
  _sbsEnsureSink(srcSlot, isSessionCurrent = () => true) {
    if (this._sbsSinks.has(srcSlot)) return this._sbsSinks.get(srcSlot);
    const expectedNet = this.net;
    const pc = new RTCPeerConnection({ iceServers: [
      { urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' },
    ]});
    const videoEl = this._sbsVideoEls.get(srcSlot);
    pc.ontrack = (e) => {
      if (isSessionCurrent() && videoEl && e.streams[0]) {
        videoEl.srcObject = e.streams[0];
        videoEl.play().catch(() => {});
        if (this._sbsRole === 'spectator') this._sbsApplyAudio();
      }
    };
    pc.onicecandidate = (e) => {
      if (e.candidate && isSessionCurrent() && this.net === expectedNet) {
        expectedNet.sendSignalToSlot(srcSlot, { type: 'ice', candidate: e.candidate, srcSlot });
      }
    };
    pc.onconnectionstatechange = () => {
      // Stream died → the racer at `srcSlot` dropped. Hide their panel + notify.
      // This covers spectators (who get no peer_left) and backs up the racer's
      // peer_left. Act only on 'failed' (terminal): 'disconnected' is often a
      // transient ICE blip that recovers on its own, and hiding on it would strand
      // the panel since v1 has no reconnect. Bounded cleanup so the PC doesn't linger.
      if (pc.connectionState === 'failed' && isSessionCurrent()) {
        try { pc.close(); } catch {}
        this._sbsSinks.delete(srcSlot);
        this._sbsHidePanel(srcSlot);
      }
    };
    const entry = { pc };
    this._sbsSinks.set(srcSlot, entry);
    return entry;
  }

  /** Handle an inbound stream signal (offer/ICE) from racer source `srcSlot`. */
  async _sbsHandleInboundSignal(srcSlot, payload, isSessionCurrent = () => true) {
    if (!isSessionCurrent()) return false;
    const { pc } = this._sbsEnsureSink(srcSlot, isSessionCurrent);
    try {
      if (payload.type === 'offer') {
        await pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: payload.sdp }));
        if (!isSessionCurrent()) return false;
        const answer = await pc.createAnswer();
        if (!isSessionCurrent()) return false;
        await pc.setLocalDescription(answer);
        if (!isSessionCurrent()) return false;
        this.net.sendSignalToSlot(srcSlot, { type: 'answer', sdp: answer.sdp, srcSlot });
        this._sbsShowPanel(srcSlot); // a fresh offer = this racer's stream is (re)connecting
        // The opponent racer just offered → they're wired up and listening, so
        // offer our own stream back to them now (idempotent if we already have a
        // viewer for them). This drives the host→joiner direction race-free: the
        // joiner can't receive an offer until after its async ROM download wires
        // the inbound handler, so a peer_joined-triggered host offer arrives too
        // early and is dropped. Gated to racers (spectators never source a stream).
        if (this._sbsRole === 'racer' && srcSlot === this._sbsOpponentSlot) {
          this._soloStream?.addRacerViewer(srcSlot);
        }
      } else if (payload.type === 'ice' && payload.candidate) {
        await pc.addIceCandidate(new RTCIceCandidate(payload.candidate));
      }
      return true;
    } catch (err) {
      if (isSessionCurrent()) console.error('[sbs] inbound signal error', err);
      return false;
    }
  }

  /**
   * Join a side-by-side room as a spectator — sink BOTH racer streams and
   * show them in two labelled video panels. No local emulator.
   */
  async _startSideBySideSpectator(roomCode, participants = null, assignmentOwner = null) {
    // Close any lingering prior SBS session (e.g. after an abnormal exit) while
    // its maps still hold the PCs — must run BEFORE we replace _sbsSinks below,
    // since _teardownSbs iterates the live map. No-op on first entry (_sbsRole unset).
    this._teardownSbsSession();
    this._sbsRole = 'spectator';
    this._sbsRoomCode = roomCode;
    this._sbsSessionOwner = assignmentOwner;
    if (assignmentOwner?.launchOwner != null) {
      this._roomJoinLaunchOwner = assignmentOwner.launchOwner;
    }
    this._sbsSinks = new Map();
    this._sbsVideoEls = new Map();
    this._sbsAudio = createAudioSourceSelector({ activeSlot: 1, muted: false });
    this.state = State.SPECTATING;
    this._showScreen('game');
    // No local emulator for a spectator — hide the solo canvas so only the two stream panels show.
    const cc = document.getElementById('canvas-container');
    if (cc) cc.style.display = 'none';
    const spectatorIsCurrent = () => (
      this._roomAssignmentIsCurrent(assignmentOwner)
      && this._sbsRole === 'spectator'
      && this._sbsRoomCode === roomCode
    );
    this._buildSbsStage('spectator'); // panels for slot 1 (left) + slot 2 (right)
    this._sbsEnsureSink(1, spectatorIsCurrent);
    this._sbsEnsureSink(2, spectatorIsCurrent);
    this._sbsApplyAudio();
    this.net.onSignal = async (payload) => {
      if (!payload || payload.channel === 'voice') return;
      await this._sbsHandleInboundSignal(payload.srcSlot ?? 1, payload, spectatorIsCurrent);
    };
    this.running = true;
    gameActions.refreshGameToolbarAvailability(this, 'core-running');
    this.net.sendSpectatorReady?.(); // both racers offer on receiving this
    // Chat people list: label both racer slots + track other spectators live.
    this.net.onPeerJoined = (player, username, image) => this._sbsSetPlayerSlot(player, username, image);
    this.net.onSpectatorJoined = (spectatorId, username, image) => {
      if (!this._spectators) this._spectators = new Map();
      this._spectators.set(spectatorId, { username: username || null, image: image || null });
      this._updatePresenceList();
    };
    this.net.onSpectatorLeft = (spectatorId) => { this._spectators?.delete(spectatorId); this._updatePresenceList(); };
    this._sbsSeedPresence(participants);
  }

  /**
   * Join a side-by-side room as a racer — run the local emulator on the left
   * and the opponent's WebRTC stream on the right, while streaming our own
   * canvas/audio out to the opponent racer + every spectator.
   *
   * Assumes the WebSocket is ALREADY connected to the shared two-racer room
   * (the entry-point flow joins before calling this). `_startSolo()` honours
   * the `_sbsRole === 'racer'` guard in its room-creation block, so it reuses
   * the open SBS socket instead of spinning up a `solo:<username>` room.
   *
   * @param {1|2} mySlot — this racer's slot in the room
   * @param {string} roomCode — the side-by-side room code
   */
  async _startSideBySideRacer(
    mySlot,
    roomCode,
    participants = null,
    retainedRoomOwner = null,
  ) {
    const titleGuard = unsupportedTitleRomGuard(this);
    if (titleGuard && !await titleGuard) return false;
    const expectedNet = this.net;
    if (retainedRoomOwner && !this._roomAssignmentIsCurrent(retainedRoomOwner)) return false;
    // Close any lingering prior SBS session (e.g. after an abnormal exit) while
    // its maps still hold the PCs — must run BEFORE we replace _sbsSinks below,
    // since _teardownSbs iterates the live map. No-op on first entry (_sbsRole unset).
    this._teardownSbsSession();
    this._sbsRole = 'racer';
    this._sbsMySlot = mySlot;
    this._sbsOpponentSlot = mySlot === 1 ? 2 : 1;
    this._sbsSinks = new Map();
    this._sbsVideoEls = new Map();
    this._sbsRoomCode = roomCode;

    // Left panel: own #canvas-container. Right panel: opponent <video>.
    // Built BEFORE _startSolo so _createEmulator's replaceChild lands the
    // fresh canvas inside the relocated container (i.e. the left panel).
    this._buildSbsStage('racer');

    // Boot the local emulator + solo loop. With `_sbsRole === 'racer'` set,
    // _startSolo reuses the SBS socket and wires the (deferred) solo-stream
    // machinery; _sbsWireSource below replaces those handlers with the
    // srcSlot-tagged versions.
    // A joining racer claims its successor before downloading the ROM and
    // passes it here. A host starts synchronously from peer_joined and claims
    // the successor now. Teardown clears the old SBS owner, so republish the
    // retained owner before solo boot without advancing the generation again.
    let racerSessionOwner = retainedRoomOwner;
    if (racerSessionOwner) {
      this._roomJoinLaunchOwner = racerSessionOwner.launchOwner;
    } else {
      const racerLaunchOwner = this._beginRoomSessionLaunchIntent();
      racerSessionOwner = {
        launchOwner: racerLaunchOwner,
        expectedNet,
        expectedRoomCode: roomCode,
      };
    }
    if (!this._roomAssignmentIsCurrent(racerSessionOwner)) return false;
    this._sbsSessionOwner = racerSessionOwner;
    const soloStart = this._startSolo(racerSessionOwner.launchOwner, {
      retainedRoomOwner: racerSessionOwner,
    });
    const racerIsCurrent = () => (
      this._roomAssignmentIsCurrent(racerSessionOwner)
      && this._sbsRole === 'racer'
      && this._sbsRoomCode === roomCode
      && this.state === State.SOLO
    );
    await soloStart;
    if (!racerIsCurrent()) return false;

    this._sbsWireSource();   // tagged SoloStreamHost + spectator/opponent offer wiring
    if (!racerIsCurrent()) return false;
    this._sbsWireInbound();  // demux onSignal for inbound (opponent's) stream
    if (!racerIsCurrent()) return false;

    // Handshake (race-free): the JOINER (slot 2) initiates by offering its
    // stream as soon as it's wired up — the host has been ready since it created
    // the room. The host offers BACK on receiving that offer (see
    // _sbsHandleInboundSignal). A host offer triggered by `peer_joined` would
    // arrive before the joiner finishes its async ROM download + inbound wiring,
    // so it would be dropped — that was the one-directional-stream bug.
    if (mySlot === 2 && racerIsCurrent()) {
      this._sbsOfferOpponent();
    }
    // peer_joined no longer drives the offer (handshake-driven) — it just labels
    // the opponent racer in the chat people list.
    this.net.onPeerJoined = (player, username, image) => {
      if (!racerIsCurrent()) return;
      this._sbsShowPanel(player); // un-hide if this racer had dropped and returned
      this._sbsSetPlayerSlot(player, username, image);
    };
    this.net.onPeerLeft = () => {
      if (racerIsCurrent()) this._sbsHidePanel(this._sbsOpponentSlot);
    };

    // Populate the chat people list (both racer slots + any spectators).
    if (!racerIsCurrent()) return false;
    this._sbsSeedPresence(participants);
    return true;
  }

  /** Offer our stream to the opponent racer and prepare to receive theirs. */
  _sbsOfferOpponent() {
    this._soloStream?.addRacerViewer(this._sbsOpponentSlot);
    this._sbsEnsureSink(this._sbsOpponentSlot);
  }

  /**
   * Stand up the srcSlot-tagged SoloStreamHost and (re)wire the spectator +
   * opponent signaling callbacks. Called after _startSolo, so it overwrites
   * the untagged solo-stream handlers _startSoloStream installed. Any untagged
   * SoloStreamHost that raced into existence is destroyed so the tagged one
   * (which stamps outbound offers/ICE with `srcSlot` for demux) wins.
   */
  _sbsWireSource() {
    const net = this.net, slot = this._sbsMySlot, roomCode = this._sbsRoomCode;
    const socket = net?.ws, generation = net?._socketGeneration, key = roomLaunchSessionKey(this);
    const current = () => this.net === net && net?.connected && net.ws === socket
      && net._socketGeneration === generation && net.roomCode === roomCode
      && this._sbsRole === 'racer' && this._sbsMySlot === slot && this._sbsRoomCode === roomCode
      && this.state === State.SOLO && sameLaunchSessionKey(key, roomLaunchSessionKey(this));
    if (!current()) return;
    let host = this._soloStream;
    const opponentSlot = this._sbsOpponentSlot;
    const renewOpponent = host?._peers.has('r' + opponentSlot) || false;
    let renewed = false;
    if (!host || host._srcSlot !== slot || !host._current()) {
      if (host) {
        // Retire only this captured host; its borrowed tracks belong to the canvas owner.
        try { host.destroy(); } catch {}
        if (this._soloStream !== host) return;
        this._soloStream = null;
      }
      if (!current()) return;
      const videoStream = this._getCanvasStream(), capture = canvasStreamOwners.get(this);
      const sourceCurrent = () => current() && canvasStreamOwners.get(this) === capture
        && capture?.stream === videoStream && capture.current();
      if (!videoStream || !sourceCurrent()) return;
      host = new SoloStreamHost(
        net,
        videoStream,
        this._soloStreamAudioSource(),
        this.system?.fps || 60,
        slot,
        sourceCurrent,
      );
      if (!sourceCurrent() || this._soloStream) { host.destroy(); return; }
      this._soloStream = host;
      renewed = true;
    }
    const ownsStream = () => current() && this._soloStream === host && host._current();
    net.onSpectatorJoined = (spectatorId, username, image) => {
      if (!ownsStream()) return;
      this._requestCanvasStreamFrame?.(host._videoStream);
      if (!ownsStream()) return;
      host.addSpectator(spectatorId);
      if (!ownsStream()) return;
      if (!this._spectators) this._spectators = new Map();
      this._spectators.set(spectatorId, { username: username || null, image: image || null });
      this._updatePresenceList();
    };
    net.onSpectatorReady = (spectatorId) => {
      if (ownsStream()) host.addSpectator(spectatorId); // addViewer guards duplicates
    };
    net.onSpectatorLeft = (spectatorId) => {
      if (!ownsStream()) return;
      host.removeSpectator(spectatorId);
      if (!ownsStream()) return;
      this._spectators?.delete(spectatorId);
      this._updatePresenceList();
    };
    net.onSpectatorSignal = (spectatorId, payload) => {
      if (ownsStream()) return host.handleSignal(spectatorId, payload);
    };
    if (renewed && renewOpponent && ownsStream()) host.addRacerViewer(opponentSlot);
  }

  /**
   * Demux inbound WebRTC signals on the racer. Signals tagged with OUR slot
   * are the opponent answering the stream WE offered them → route to the
   * SoloStreamHost viewer 'r<opponentSlot>'. Signals tagged with another slot
   * are the opponent's own stream being offered to us → inbound sink. Voice
   * signals are owned by VoiceChat and ignored here.
   */
  _sbsWireInbound() {
    const sessionOwner = this._sbsSessionOwner;
    const sessionIsCurrent = () => this._roomAssignmentIsCurrent(sessionOwner);
    this.net.onSignal = async (payload) => {
      if (!payload || payload.channel === 'voice' || !sessionIsCurrent()) return; // voice owned by VoiceChat
      const src = payload.srcSlot;
      if (src == null) return;
      if (src === this._sbsMySlot) {
        this._soloStream?.handleSignal('r' + this._sbsOpponentSlot, payload); // opponent answering MY stream
      } else {
        await this._sbsHandleInboundSignal(src, payload, sessionIsCurrent); // opponent's stream offered to me
      }
    };
  }

  /** Build the two-panel stage and register each panel's <video> by slot. */
  _buildSbsStage(role) {
    this._teardownSbs(); // idempotent
    this._sbsHiddenSlots = new Set(); // fresh per room — no stale "dropped" slots
    const cc = document.getElementById('canvas-container');
    // Anchor the stage where the emulator canvas normally lives (inside
    // .game-main), so it inherits the proven game-screen layout. Appending it
    // directly to #screen-game dropped it into an off-screen phantom grid cell
    // whenever the chat sidebar's `display:grid` layout (.chat-open) was active.
    const home = cc?.parentElement || document.querySelector('.game-main') || document.getElementById('screen-game');
    const anchor = cc ? cc.nextSibling : null; // remember the canvas's slot before we move it
    this._sbsCanvasHome = cc?.parentElement || null;
    this._sbsCanvasNext = anchor; // L4: _teardownSbs puts the canvas back before this sibling
    const stage = document.createElement('div');
    stage.id = 'sbs-stage'; stage.className = 'sbs-stage';
    const panel = (slot, label) => {
      const p = document.createElement('div'); p.className = 'sbs-panel'; p.dataset.slot = String(slot);
      const lbl = document.createElement('div'); lbl.className = 'sbs-panel-label'; lbl.textContent = label;
      const media = document.createElement('div'); media.className = 'sbs-panel-media';
      p.append(lbl, media); return { p, media };
    };
    if (role === 'racer') {
      const mine = panel(this._sbsMySlot, 'You');
      mine.p.classList.add('sbs-panel-self');
      if (cc) { cc.style.display = ''; mine.media.appendChild(cc); }
      const opp = panel(this._sbsOpponentSlot, 'Opponent');
      opp.p.classList.add('sbs-panel-opponent');
      const v = document.createElement('video'); v.id = 'sbs-video-opp'; v.playsInline = true; v.autoplay = true; v.muted = true;
      opp.media.appendChild(v); this._sbsVideoEls.set(this._sbsOpponentSlot, v);
      this._sbsAttachUnmute(v, this._sbsOpponentSlot);
      stage.append(this._sbsMySlot === 1 ? mine.p : opp.p, this._sbsMySlot === 1 ? opp.p : mine.p);
    } else {
      for (const slot of [1, 2]) {
        const pn = panel(slot, slot === 1 ? 'Player 1' : 'Player 2');
        const v = document.createElement('video'); v.id = `sbs-video-${slot}`; v.playsInline = true; v.autoplay = true; v.muted = true;
        pn.media.appendChild(v); this._sbsVideoEls.set(slot, v);
        this._sbsAttachUnmute(v, slot);
        stage.appendChild(pn.p);
      }
    }
    if (home) home.insertBefore(stage, (anchor && anchor.parentNode === home) ? anchor : null);
    else document.getElementById('screen-game')?.appendChild(stage);
    // Layout control + saved layout. Built after the stage is in the DOM so
    // _sbsSetLayout can query it. PiP is racer-only; coerce it away otherwise.
    this._sbsBuildLayoutControl(stage, role);
    let mode = loadLayout();
    if (role !== 'racer' && mode === 'pip') mode = 'side';
    this._sbsSetLayout(mode);
    this._sbsUpdatePanelLabels();
  }

  /** Tear down all sinks + the stage, restoring the emulator canvas to its home. */
  _teardownSbs() {
    for (const entry of this._sbsSinks?.values?.() || []) { try { entry.pc.close(); } catch {} }
    this._sbsSinks?.clear?.();
    const cc = document.getElementById('canvas-container');
    if (cc) {
      if (this._sbsCanvasHome) {
        // L4: back to its recorded slot; appended only when that sibling has left the home.
        const next = this._sbsCanvasNext?.parentNode === this._sbsCanvasHome ? this._sbsCanvasNext : null;
        this._sbsCanvasHome.insertBefore(cc, next);
        this._sbsCanvasHome = null;
        this._sbsCanvasNext = null;
      }
      cc.style.display = '';
    }
    document.getElementById('sbs-stage')?.remove();
  }

  /**
   * A racer (slot) dropped: hide their panel so the survivor's screen takes the
   * freed space, drop them from the chat people list, and flash a one-time
   * notice. The other player keeps playing (the emulators are independent).
   * Idempotent per slot — the WS peer_left and the stream dying both call this,
   * and they collapse to a single notice until the slot returns.
   */
  _sbsHidePanel(slot) {
    if (!slot) return;
    if (!this._sbsHiddenSlots) this._sbsHiddenSlots = new Set();
    if (this._sbsHiddenSlots.has(slot)) return;
    this._sbsHiddenSlots.add(slot);
    const name = this._lobbySlotUsernames?.[slot - 1];
    document.getElementById('sbs-stage')?.classList.add('sbs-gone-' + slot);
    this._flash(`${name ? '@' + name : 'Player ' + slot} disconnected`);
    if (this._lobbySlotUsernames) this._lobbySlotUsernames[slot - 1] = null;
    if (this._lobbySlotImages) this._lobbySlotImages[slot - 1] = null;
    this._updatePresenceList?.();
  }

  /** A racer (slot) returned: un-hide their panel (re-stream + presence handle the rest). */
  _sbsShowPanel(slot) {
    if (!slot || !this._sbsHiddenSlots?.has(slot)) return;
    this._sbsHiddenSlots.delete(slot);
    document.getElementById('sbs-stage')?.classList.remove('sbs-gone-' + slot);
  }

  /** Build the 3-icon layout switcher pinned to the stage. PiP is racer-only. */
  _sbsBuildLayoutControl(stage, role) {
    const ctrl = document.createElement('div');
    ctrl.className = 'sbs-layout-control';
    const defs = [
      { mode: 'side', icon: '▥', title: 'Side by side' },
      { mode: 'topbottom', icon: '▤', title: 'Top and bottom' },
      { mode: 'pip', icon: '🖼️', title: 'Picture-in-picture' },
    ];
    for (const d of defs) {
      if (d.mode === 'pip' && role !== 'racer') continue; // PiP racer-only
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'sbs-layout-btn'; b.dataset.mode = d.mode;
      b.title = d.title; b.textContent = d.icon;
      b.addEventListener('click', () => this._sbsSetLayout(d.mode));
      ctrl.appendChild(b);
    }
    stage.appendChild(ctrl);
  }

  /** Switch the side-by-side layout (side|topbottom|pip), persist, reflect in the UI. */
  _sbsSetLayout(mode) {
    const stage = document.getElementById('sbs-stage');
    if (!stage) return;
    this._sbsLayout = mode;
    stage.classList.remove('sbs-layout-side', 'sbs-layout-topbottom', 'sbs-layout-pip');
    stage.classList.add('sbs-layout-' + mode);
    for (const b of stage.querySelectorAll('.sbs-layout-btn')) {
      b.classList.toggle('active', b.dataset.mode === mode);
    }
    const opp = stage.querySelector('.sbs-panel-opponent');
    if (mode === 'pip') {
      this._sbsApplyPipCorner(loadPipCorner());
      if (opp) this._sbsInitPipDrag(opp);
    } else if (opp) {
      // Drop any inline drag positioning so the non-PiP layouts lay out cleanly.
      opp.style.left = opp.style.top = opp.style.right = opp.style.bottom = '';
    }
    saveLayout(mode);
  }

  /** Place the PiP overlay in a corner via the stage's sbs-pip-* class. */
  _sbsApplyPipCorner(corner) {
    const stage = document.getElementById('sbs-stage');
    if (!stage) return;
    stage.classList.remove('sbs-pip-tl', 'sbs-pip-tr', 'sbs-pip-bl', 'sbs-pip-br');
    stage.classList.add('sbs-pip-' + corner);
    const opp = stage.querySelector('.sbs-panel-opponent');
    if (opp) opp.style.left = opp.style.top = opp.style.right = opp.style.bottom = ''; // let the class position it
  }

  /**
   * Make the PiP opponent overlay draggable (Pointer Events → mouse + touch).
   * A gesture that moves past a small threshold is a drag (suppress the click,
   * so it doesn't toggle mute); a stationary press stays a tap. On release the
   * overlay snaps to the nearest corner and the choice is persisted.
   */
  _sbsInitPipDrag(overlayEl) {
    if (overlayEl._sbsDragArmed) return; // arm once per element
    overlayEl._sbsDragArmed = true;
    const THRESHOLD = 6; // px before a press becomes a drag
    let dragging = false, moved = false, startX = 0, startY = 0;
    const getStage = () => document.getElementById('sbs-stage');

    overlayEl.addEventListener('pointerdown', (e) => {
      if (this._sbsLayout !== 'pip') return;
      dragging = true; moved = false; startX = e.clientX; startY = e.clientY;
      try { overlayEl.setPointerCapture(e.pointerId); } catch {}
    });

    overlayEl.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      if (!moved && Math.hypot(e.clientX - startX, e.clientY - startY) < THRESHOLD) return;
      moved = true;
      overlayEl.classList.add('dragging');
      const stage = getStage(); if (!stage) return;
      const sb = stage.getBoundingClientRect();
      const ob = overlayEl.getBoundingClientRect();
      let left = e.clientX - sb.left - ob.width / 2;
      let top = e.clientY - sb.top - ob.height / 2;
      left = Math.max(0, Math.min(left, sb.width - ob.width));
      top = Math.max(0, Math.min(top, sb.height - ob.height));
      // Position by top/left only. Use 'auto' (not '') so the active corner class
      // can't reassert its right/bottom — otherwise top+bottom (or left+right) are
      // both set and the overlay stretches to fill the stage (its shadow with it).
      overlayEl.style.right = overlayEl.style.bottom = 'auto';
      overlayEl.style.left = left + 'px';
      overlayEl.style.top = top + 'px';
    });

    const end = (e) => {
      if (!dragging) return;
      dragging = false;
      try { overlayEl.releasePointerCapture(e.pointerId); } catch {}
      if (!moved) return; // a tap → let the click (mute toggle) fire normally
      overlayEl.classList.remove('dragging');
      const stage = getStage(); if (!stage) return;
      const sb = stage.getBoundingClientRect();
      const ob = overlayEl.getBoundingClientRect();
      const corner = nearestCorner(
        { left: ob.left - sb.left, top: ob.top - sb.top, width: ob.width, height: ob.height },
        { width: sb.width, height: sb.height },
      );
      savePipCorner(corner);
      this._sbsApplyPipCorner(corner); // clears inline pos + sets class → CSS animates to corner
      // Swallow the click that fires right after a drag so it doesn't toggle mute.
      overlayEl.addEventListener('click', (ev) => { ev.stopPropagation(); ev.preventDefault(); }, { once: true, capture: true });
    };
    overlayEl.addEventListener('pointerup', end);
    overlayEl.addEventListener('pointercancel', end);
  }

  /** Label one racer slot (1|2) in the chat people list and re-render it. */
  _sbsSetPlayerSlot(player, username, image) {
    if (player !== 1 && player !== 2) return;
    if (!this._lobbySlotUsernames) this._lobbySlotUsernames = [null, null];
    if (!this._lobbySlotImages) this._lobbySlotImages = [null, null];
    this._lobbySlotUsernames[player - 1] = username || '';
    this._lobbySlotImages[player - 1] = image || null;
    this._updatePresenceList();
    this._sbsUpdatePanelLabels();
  }

  /**
   * Seed the chat people list for a side-by-side room from the server's
   * `assigned.participants` snapshot (both racer slots + spectators), then
   * render it. onAssigned skips its normal presence seeding for SBS (the host
   * bails on the State.SOLO guard, the joiner returns early), so we do it here.
   */
  _sbsSeedPresence(participants) {
    this._lobbySlotUsernames = [null, null];
    this._lobbySlotImages = [null, null];
    this._spectators = new Map();
    if (participants?.players) {
      for (const p of participants.players) {
        if ((p.slot === 1 || p.slot === 2) && (p.occupied || p.username)) {
          this._lobbySlotUsernames[p.slot - 1] = p.username ?? '';
          this._lobbySlotImages[p.slot - 1] = p.image || null;
        }
      }
    }
    if (participants?.spectators) {
      for (const s of participants.spectators) {
        this._spectators.set(s.spectatorId, { username: s.username || null, image: s.image || null });
      }
    }
    // A racer always knows its own identity even if the snapshot lacks it.
    if (this._sbsRole === 'racer' && this._sbsMySlot) {
      const user = authState.getCurrentUser();
      this._lobbySlotUsernames[this._sbsMySlot - 1] = user?.displayUsername || user?.username || '';
      this._lobbySlotImages[this._sbsMySlot - 1] = user?.image || null;
    }
    if (this._chatPanel && this._sbsRoomCode) {
      this._chatPanel.addRoomChannel(this._sbsRoomCode, this._sbsRole === 'spectator');
    }
    this._updatePresenceList();
    this._sbsUpdatePanelLabels();
  }

  /**
   * Set each SBS panel's caption to the player's username: the self panel shows
   * "You", a known opponent/player shows "@username", and an unidentified one
   * falls back to "Opponent" (racer) or "Player N" (spectator). No-op in PiP
   * (labels are hidden there via CSS).
   */
  _sbsUpdatePanelLabels() {
    const stage = document.getElementById('sbs-stage');
    if (!stage) return;
    for (const panel of stage.querySelectorAll('.sbs-panel')) {
      const slot = Number(panel.dataset.slot);
      const lbl = panel.querySelector('.sbs-panel-label');
      if (!lbl) continue;
      const name = this._lobbySlotUsernames?.[slot - 1];
      if (this._sbsRole === 'racer' && slot === this._sbsMySlot) {
        lbl.textContent = 'You';
      } else if (name) {
        lbl.textContent = '@' + name;
      } else {
        lbl.textContent = this._sbsRole === 'racer' ? 'Opponent' : 'Player ' + slot;
      }
    }
  }

  /** Wire a panel's tap behaviour: racer toggles opponent mute; spectator picks audio source. */
  _sbsAttachUnmute(videoEl, slot) {
    if (this._sbsRole === 'racer') {
      // single opponent panel: tap toggles its mute (racer hears their own local emulator)
      videoEl.addEventListener('click', () => {
        videoEl.muted = !videoEl.muted;
        videoEl.play().catch(() => {});
        gameActions.refreshGameToolbarAvailability(this, 'sbs-source');
      });
      return;
    }
    // spectator: tapping a panel makes it the active audio source (or toggles mute if already active)
    if (!this._sbsAudio) this._sbsAudio = createAudioSourceSelector({ activeSlot: 1, muted: false });
    videoEl.addEventListener('click', () => { this._sbsAudio.setActive(slot); this._sbsApplyAudio(); });
  }

  /** Apply the audio-source selector's state to the spectator's video elements. */
  _sbsApplyAudio() {
    if (!this._sbsAudio) return;
    for (const [slot, v] of this._sbsVideoEls) {
      v.muted = !this._sbsAudio.isAudible(slot);
      if (!v.muted) v.play().catch(() => {});
    }
    gameActions.refreshGameToolbarAvailability(this, 'sbs-source');
  }

  async _prepareSpectator(roomCode, assignmentOwner = null) {
    const assignmentIsCurrent = () => this._roomAssignmentIsCurrent(assignmentOwner);
    if (!assignmentIsCurrent()) return;
    try {
      this._flash('Downloading ROM...');
      const rom = await this._downloadROM(roomCode);
      if (!assignmentIsCurrent()) return;
      if (!rom) {
        this._flash('No ROM shared in this room');
        return;
      }
      await this._installRoomRom(rom, { isCurrent: assignmentIsCurrent });
      if (!assignmentIsCurrent()) return;
      this._flash('Waiting for game to start...');
    } catch {
      if (!assignmentIsCurrent()) return;
      this._flash('Failed to prepare spectator');
    }
  }

  async _startSpectating(assignmentOwner = null) {
    const assignmentIsCurrent = () => this._roomAssignmentIsCurrent(assignmentOwner);
    if (!assignmentIsCurrent()) return false;
    this.state = State.SPECTATING;
    this._showScreen('game');
    this._updateResetButtonVisibility();
    this._updatePresenceList();

    const sessionEmu = await this._createEmulator({ multiplayer: true, coreName: this._roomCoreName });
    if (!sessionEmu || this.emu !== sessionEmu || this.state !== State.SPECTATING
        || !assignmentIsCurrent()) return false;
    this.emu.loadROM(this.romData);

    this._specInputs = { 1: {}, 2: {} };
    this._specFrame = 0;

    this.emu.onAudioBufferReady = (samples) => {
      this.audio.writeSamples(samples);
    };

    this.audio.init().catch(() => {});
    this.running = true;
    gameActions.refreshGameToolbarAvailability(this, 'core-running');
    this.lastFrameTime = performance.now();
    this._spectatorLoop(performance.now());
    return true;
  }

  _spectatorInput(player, frame, buttons) {
    if (!this._specInputs) return;
    this._specInputs[player] = this._specInputs[player] || {};
    this._specInputs[player][frame] = buttons;
  }

  _spectatorLoop(now) {
    if (!this.running) return;
    this.animFrameId = requestAnimationFrame((t) => this._spectatorLoop(t));

    let advanced = 0;
    const soloMode = !this._specInputs[2] ||
      Object.keys(this._specInputs[2]).length === 0;

    if (soloMode) {
      // Solo room: only P1 input — advance when P1 input is available
      while (advanced < 4 &&
             this._specInputs[1][this._specFrame] !== undefined) {
        this.emu.setButtons(0, this._specInputs[1][this._specFrame]);
        this.emu.stepFrame();
        delete this._specInputs[1][this._specFrame];
        this._specFrame++;
        advanced++;
      }
    } else {
      // Multiplayer: need both P1 and P2 input
      while (advanced < 4 &&
             this._specInputs[1][this._specFrame] !== undefined &&
             this._specInputs[2][this._specFrame] !== undefined) {
        this.emu.setButtons(0, this._specInputs[1][this._specFrame]);
        this.emu.setButtons(1, this._specInputs[2][this._specFrame]);
        this.emu.stepFrame();
        delete this._specInputs[1][this._specFrame];
        delete this._specInputs[2][this._specFrame];
        this._specFrame++;
        advanced++;
      }
    }
  }

  // ── Hosted Swap (host migration) ─────────────────────────

  /** §6: the final-score flush, then teardown. A session-end teardown marks the
   *  recorder a detached source before its stop (Task 2), so the flush's frozen
   *  capture survives the session-start increment. */
  _endSoloHandoffRecording() {
    this._destroyReplayRecorder({ flushPendingPostRoll: true, sessionEnd: true });
  }

  _chargeHandoff(reservation, bytes) {
    if (!Number.isSafeInteger(bytes) || bytes < 0
        || replayBoundary.externalReplayBytes(this) + replayBoundary.recorderRetainedBytes(this.replayRecorder) + bytes
          > replayBoundary.REPLAY_LIVE_BYTES_LIMIT) throw new Error('handoff capacity exceeded');
    this._handoffReservations ||= new Set();
    this._handoffReservations.add(reservation);
    reservation.bytes += bytes;
    this._settleHandoff(reservation);
  }

  _settleHandoff(reservation) {
    if (!reservation.retained && reservation.operations === 0) this._handoffReservations?.delete(reservation);
    this._handoffChargeBytes = [...(this._handoffReservations || [])].reduce((sum, entry) => sum + entry.bytes, 0);
  }

  _releaseHandoffSave(save) {
    const reservation = save && this._handoffSaves?.get(save);
    if (!reservation) return;
    reservation.retained = false;
    for (const controller of reservation.controllers) controller.abort();
    this._settleHandoff(reservation);
  }

  _holdHandoffSave(save) {
    const reservation = save && this._handoffSaves?.get(save);
    if (!reservation) return () => {};
    reservation.operations++;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      reservation.operations--;
      this._settleHandoff(reservation);
    };
  }

  _replacePendingSave(save) {
    const previous = this._pendingSave;
    this._pendingSave = save;
    if (previous !== save) this._releaseHandoffSave(previous);
  }

  _discardPendingSave(save) {
    if (this._pendingSave === save) this._pendingSave = null;
    this._releaseHandoffSave(save);
  }

  _discardSoloHandoff() {
    const save = this._pendingSoloHandoffSave;
    this._pendingSoloHandoffSave = null;
    this._releaseHandoffSave(save);
    const token = this._soloHandoffCaptureToken;
    this._soloHandoffCaptureToken = null;
    if (token?.reservation) {
      token.reservation.retained = false;
      for (const controller of token.reservation.controllers) controller.abort();
      this._settleHandoff(token.reservation);
    }
  }

  async _prepareSoloMultiplayerHandoff() {
    if (this._pendingSoloHandoffSave) return true;
    if (this.state !== State.SOLO || !this.emu) return false;
    if (this._soloHandoffCaptureToken) return false;

    const emu = this.emu;
    const reservation = { bytes: 0, retained: false, operations: 1, controllers: new Set(),
      limit: baseStateByteLimitForBuild(emu.coreBuildId) };
    const captureToken = { reservation };
    this._soloHandoffCaptureToken = captureToken;

    try {
      const jsnes = emu.coreName === 'jsnes';
      let size;
      if (jsnes) size = JSNES_STATE_CHARGE_BYTES;
      else {
        if (!isValidCoreBuildId(emu.coreBuildId) || typeof emu.module?._retroplay_serialize_size !== 'function')
          throw new Error('handoff serialize size unavailable');
        size = emu.module._retroplay_serialize_size();
        if (!Number.isSafeInteger(size) || size <= 0 || size > reservation.limit)
          throw new Error('invalid handoff serialize size');
      }
      this._chargeHandoff(reservation, jsnes ? size : 2 * size);
      let emulatorState = await saveManager.captureEmulatorState(this);
      if (this._soloHandoffCaptureToken !== captureToken || this.emu !== emu) return false;
      if (!emulatorState) throw new Error('empty emulator state');
      if (jsnes) emulatorState = normalizeJsnesState(emulatorState, emu);
      if (jsnes ? !emulatorState || jsonUtf8SizeBounded(emulatorState, reservation.limit) == null
        : !(emulatorState instanceof Uint8Array) || emulatorState.length === 0 || emulatorState.length > size)
        throw new Error('handoff state exceeds preflight bound');
      if (!lockstepStart.validateSaveFrame(this._libretroSoloFrame || this.soloFrame || 0).ok)
        throw new Error('invalid handoff frame');
      this._pendingSoloHandoffSave = {
        emulatorState,
        frame: this._libretroSoloFrame || this.soloFrame || 0,
        coreName: this.emu.coreName || null,
      };
      reservation.retained = true;
      (this._handoffSaves ||= new WeakMap()).set(this._pendingSoloHandoffSave, reservation);
      this.running = false;
      if (this.animFrameId) {
        cancelAnimationFrame(this.animFrameId);
        this.animFrameId = null;
      }
      if (this.emu.selfDriving && typeof this.emu.module?.pauseMainLoop === 'function') {
        try {
          this.emu.module.pauseMainLoop();
          this._soloHandoffWasmPaused = true;
          noteC64PauseChange(this);
        } catch (err) {
          console.warn('[invite] could not pause the old solo main loop:', err);
        }
      }
      this._soloHandoffCaptureToken = null;
      return true;
    } catch (err) {
      if (this._soloHandoffCaptureToken !== captureToken) return false;
      this._soloHandoffError = err;
      console.warn('[invite] failed to preflight solo state:', err);
      this._soloHandoffCaptureToken = null;
      const discarded = this._pendingSoloHandoffSave;
      this._pendingSoloHandoffSave = null;
      this._releaseHandoffSave(discarded);
      reservation.retained = false;
      this._flash('Could not preserve this session for multiplayer');
      return false;
    } finally {
      reservation.operations--;
      this._settleHandoff(reservation);
      if (this._soloHandoffCaptureToken === captureToken) this._soloHandoffCaptureToken = null;
    }
  }

  _cancelSoloMultiplayerHandoff() {
    const shouldResume = !!(this._pendingSoloHandoffSave || this._soloHandoffWasmPaused);
    this._discardSoloHandoff();
    if (!shouldResume || this.state !== State.SOLO || !this.emu
        || this._unsafeReplayBoundary === this.emu) return;

    if (this._soloHandoffWasmPaused
        && typeof this.emu.module?.resumeMainLoop === 'function') {
      this.emu.module.resumeMainLoop();
    }
    this._soloHandoffWasmPaused = false;
    noteC64PauseChange(this);
    this.running = true;
    gameActions.refreshGameToolbarAvailability(this, 'core-running');
    if (!this.emu.selfDriving) {
      this.lastFrameTime = performance.now();
      this.frameAccumulator = 0;
      this._soloLoop(this.lastFrameTime);
    }
  }

  _clearHostedSwapTimeout() {
    if (this._hostedSwapTimeout) clearTimeout(this._hostedSwapTimeout);
    this._hostedSwapTimeout = null;
  }

  _discardHostedSwapHandoff({ resumeWasm = false } = {}) {
    this._clearHostedSwapTimeout();
    this._hostedSwapPreparationToken = null;
    this._hostedSwapAcceptToken = null;
    if (resumeWasm && this._unsafeReplayBoundary !== this.emu && this._hostedSwapWasmPaused
        && typeof this.emu?.module?.resumeMainLoop === 'function') {
      this.emu.module.resumeMainLoop();
    }
    this._hostedSwapPrepared = false;
    this._hostedSwapFrozen = false;
    noteC64PauseChange(this);
    this._hostedSwapWasmPaused = false;
    this._hostedSwapWasRunning = false;
  }

  _freezeHostedSwapHandoff() {
    if (this._hostedSwapFrozen) return true;
    const wasRunning = this.running === true;
    let wasmPaused = false;

    if (this.emu?.selfDriving) {
      if (typeof this.emu.module?.pauseMainLoop !== 'function'
          || typeof this.emu.module?.resumeMainLoop !== 'function') return false;
      try {
        this.emu.module.pauseMainLoop();
        wasmPaused = true;
      } catch (err) {
        console.warn('[swap] could not freeze the hosted core:', err);
        return false;
      }
    }

    this._hostedSwapWasRunning = wasRunning;
    this._hostedSwapFrozen = true;
    noteC64PauseChange(this);
    this._hostedSwapWasmPaused = wasmPaused;
    this.running = false;
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    return true;
  }

  _resumeHostedSwapHandoff() {
    this._clearHostedSwapTimeout();
    const shouldResume = this._hostedSwapFrozen
      && this._hostedSwapWasRunning
      && this.state === State.PLAYING
      && !!this.emu && this._unsafeReplayBoundary !== this.emu;
    const resumeWasm = shouldResume && this._hostedSwapWasmPaused
      && typeof this.emu.module?.resumeMainLoop === 'function';
    const selfDriving = !!this.emu?.selfDriving;
    this._hostedSwapPreparationToken = null;
    this._hostedSwapPrepared = false;
    this._hostedSwapFrozen = false;
    noteC64PauseChange(this);
    this._hostedSwapWasmPaused = false;
    this._hostedSwapWasRunning = false;
    if (!shouldResume) return;

    this.running = true;
    gameActions.refreshGameToolbarAvailability(this, 'core-running');
    if (resumeWasm) this.emu.module.resumeMainLoop();
    if (selfDriving) {
      if (this.emu instanceof LibretroCore) this._hostedP1InputLoop();
    } else {
      this.lastFrameTime = performance.now();
      this.frameAccumulator = 0;
      this._hostedHostLoop(this.lastFrameTime);
    }
  }

  _hostedSwapSessionToken() {
    return {
      launchOwner: this._roomJoinLaunchOwner,
      net: this.net,
      roomCode: this.net?.roomCode || null,
      emulator: this.emu,
      playerNumber: this.net?.playerNumber || null,
    };
  }

  _isHostedSwapSessionCurrent(token) {
    return !!token
      && this._roomAssignmentIsCurrent({
        launchOwner: token.launchOwner,
        expectedNet: token.net,
        expectedRoomCode: token.roomCode,
      })
      && this.state === State.PLAYING
      && this.net === token.net
      && this.net?.roomCode === token.roomCode
      && this.net?.playerNumber === token.playerNumber
      && this.emu === token.emulator;
  }

  _beginHostedRoleTransition(newPlayerNum, predecessorOwner) {
    const launchOwner = this._beginRoomSessionLaunchIntent(
      { clearPendingSave: true },
      predecessorOwner,
    );
    if (launchOwner == null) return null;
    return {
      launchOwner,
      expectedNet: this.net,
      expectedRoomCode: this.net?.roomCode || null,
      expectedPlayerNumber: newPlayerNum,
    };
  }

  _isHostedRoleTransitionCurrent(owner) {
    return !!owner
      && this._roomAssignmentIsCurrent(owner)
      && this.state === State.PLAYING
      && owner?.expectedNet?.playerNumber === owner?.expectedPlayerNumber;
  }

  _isHostedSwapPreparationCurrent(token) {
    return this._hostedSwapPreparationToken === token
      && this._isHostedSwapSessionCurrent(token);
  }

  _cancelStaleHostedSwapPreparation(token) {
    if (!token || this._hostedSwapPreparationToken !== token) return;
    if (this.state === State.PLAYING && this.emu === token.emulator) {
      this._resumeHostedSwapHandoff();
    } else {
      this._discardHostedSwapHandoff();
    }
  }

  _armHostedSwapTimeout(token) {
    this._clearHostedSwapTimeout();
    this._hostedSwapTimeout = setTimeout(() => {
      if (!this._hostedSwapPrepared || !this._isHostedSwapPreparationCurrent(token)) return;
      token.net?.sendSwapDecline?.();
      this._resumeHostedSwapHandoff();
      const button = document.getElementById('btn-swap');
      if (button) button.disabled = false;
      this._flash('Swap timed out');
    }, HOSTED_SWAP_TIMEOUT_MS);
  }

  _prepareHostedSwapHandoff() {
    // The current P1 is the only peer that owns authoritative hosted state.
    // Lockstep still participates in the server preflight, but has no state
    // transfer because both peers already run the same simulation.
    if (this.net?.playerNumber !== 1) return Promise.resolve(true);
    if (this._hostedSwapPrepared) {
      if (this._isHostedSwapPreparationCurrent(this._hostedSwapPreparationToken)) {
        return Promise.resolve(true);
      }
      this._cancelStaleHostedSwapPreparation(this._hostedSwapPreparationToken);
    }
    if (this._hostedSwapPreparationPromise) return this._hostedSwapPreparationPromise;

    const preparationToken = this._hostedSwapSessionToken();
    this._hostedSwapPreparationToken = preparationToken;
    const preparationPromise = this._performHostedSwapPreparation(preparationToken);
    this._hostedSwapPreparationPromise = preparationPromise;
    const clearPreparation = () => {
      if (this._hostedSwapPreparationPromise === preparationPromise) {
        this._hostedSwapPreparationPromise = null;
      }
    };
    preparationPromise.then(clearPreparation, clearPreparation);
    return preparationPromise;
  }

  async _performHostedSwapPreparation(preparationToken) {
    if (!this.lockstep) {
      try {
        const state = await saveManager.captureEmulatorState(this);
        if (!this._isHostedSwapPreparationCurrent(preparationToken)) {
          this._cancelStaleHostedSwapPreparation(preparationToken);
          return false;
        }
        if (!state) throw new Error('empty emulator state');
        const currentFrame = this.lockstep?.currentFrame
          ?? this.rollback?.currentFrame
          ?? this.hostedHostFrame
          ?? this.soloFrame;
        if (!this._freezeHostedSwapHandoff()) {
          throw new Error('could not freeze hosted emulator');
        }
        await this._uploadSaveState(preparationToken.roomCode, {
          emulatorState: state,
          frame: currentFrame,
          coreName: preparationToken.emulator?.coreName || null,
        });
        if (!this._isHostedSwapPreparationCurrent(preparationToken)) {
          this._cancelStaleHostedSwapPreparation(preparationToken);
          return false;
        }
      } catch (err) {
        if (!this._isHostedSwapPreparationCurrent(preparationToken)) return false;
        this._resumeHostedSwapHandoff();
        console.warn('[swap] host preflight failed:', err);
        this._flash('Could not capture game state. Swap canceled');
        return false;
      }
    }

    if (!this._isHostedSwapPreparationCurrent(preparationToken)) {
      this._cancelStaleHostedSwapPreparation(preparationToken);
      return false;
    }
    preparationToken.net.sendSwapPrepared();
    this._hostedSwapPrepared = true;
    this._armHostedSwapTimeout(preparationToken);
    return true;
  }

  _bindSwapPromptActions() {
    document.getElementById('btn-swap-accept')?.addEventListener('click', () => {
      this._acceptPlayerSwap();
    });
    document.getElementById('btn-swap-decline')?.addEventListener('click', () => {
      this._declinePlayerSwap();
    });
  }

  _setSwapPromptAcceptPending(pending) {
    const accept = document.getElementById('btn-swap-accept');
    const decline = document.getElementById('btn-swap-decline');
    if (accept) accept.disabled = pending;
    // Decline is the cancellation path for a slow/hung capture or upload and
    // must remain a real, clickable control while Accept is pending.
    if (decline) decline.disabled = false;
  }

  async _requestPlayerSwap() {
    if (this.state !== State.PLAYING) return false;
    if (this._netMode === 'link-cable') {
      this._flash('Player swap is unavailable during link cable play');
      return false;
    }
    const sessionToken = this._hostedSwapSessionToken();
    const button = document.getElementById('btn-swap');
    if (button) button.disabled = true;
    if (!(await this._prepareHostedSwapHandoff())
        || !this._isHostedSwapSessionCurrent(sessionToken)) {
      if (button) button.disabled = false;
      return false;
    }
    sessionToken.net.sendSwapRequest();
    this._flash('Swap requested...');
    return true;
  }

  _acceptPlayerSwap() {
    if (this._hostedSwapAcceptPromise) return this._hostedSwapAcceptPromise;

    const sessionToken = this._hostedSwapSessionToken();
    const acceptToken = {};
    this._hostedSwapAcceptToken = acceptToken;
    this._setSwapPromptAcceptPending(true);

    const acceptPromise = (async () => {
      const prepared = await this._prepareHostedSwapHandoff();
      if (this._hostedSwapAcceptToken !== acceptToken) return false;
      if (!prepared || !this._isHostedSwapSessionCurrent(sessionToken)) {
        if (this._isHostedSwapSessionCurrent(sessionToken)) sessionToken.net.sendSwapDecline();
        document.getElementById('swap-prompt')?.classList.add('hidden');
        return false;
      }
      sessionToken.net.sendSwapAccept();
      document.getElementById('swap-prompt')?.classList.add('hidden');
      return true;
    })();
    this._hostedSwapAcceptPromise = acceptPromise;
    const clearAccept = () => {
      if (this._hostedSwapAcceptPromise === acceptPromise) {
        this._hostedSwapAcceptPromise = null;
      }
      if (this._hostedSwapAcceptToken === acceptToken) {
        this._hostedSwapAcceptToken = null;
      }
    };
    acceptPromise.then(clearAccept, clearAccept);
    return acceptPromise;
  }

  _declinePlayerSwap() {
    const net = this.net;
    this._hostedSwapAcceptToken = null;
    this._resumeHostedSwapHandoff();
    this._setSwapPromptAcceptPending(false);
    net?.sendSwapDecline?.();
    document.getElementById('swap-prompt')?.classList.add('hidden');
  }

  _sendUpgradeInvite(spectatorId) {
    if (this.state !== State.SOLO || this._isBasicMode) return;
    this._pendingUpgrade = spectatorId;
    this.net.sendUpgradeInvite(spectatorId);
    this._flash('Invite sent...');
    this._updatePresenceList();
  }

  _showUpgradePrompt(hostUsername) {
    const prompt = document.getElementById('upgrade-prompt');
    if (!prompt) return;
    const text = document.getElementById('upgrade-prompt-text');
    if (text) text.textContent = `${hostUsername || 'The host'} invited you to play!`;
    prompt.classList.remove('hidden');
  }

  async _hostedSwap(newPlayerNum, predecessorOwner) {
    const transitionOwner = this._beginHostedRoleTransition(newPlayerNum, predecessorOwner);
    const transitionIsCurrent = () => this._isHostedRoleTransitionCurrent(transitionOwner);
    if (!transitionIsCurrent()) return false;
    const wasHost = newPlayerNum === 2; // was P1 (host), now P2 (guest)
    this._flash('Swapping players...');

    if (wasHost) {
      // Old host → guest: capture/upload already completed before swap_accept,
      // and the server refused to mutate slot ownership without that preflight.
      if (!this._hostedSwapPrepared) {
        console.error('[swap] server committed without local host preflight');
        this._flash('Swap could not be completed safely');
        return;
      }
      this._discardHostedSwapHandoff();
      if (!transitionIsCurrent()) return false;
      transitionOwner.expectedNet.sendSwapHostReady();
      this._stopGame();
      if (!transitionIsCurrent()) return false;
      const started = await this._startHostedGuest(transitionOwner);
      if (started === false || !transitionIsCurrent()) return false;
      this._flash(`Swapped! You are now Player ${newPlayerNum}`);
      return true;
    } else {
      // Old guest → host: tear down guest, wait for old host to upload state
      this._stopGame();
      if (!transitionIsCurrent()) return false;
      this._hostedRoleTransitionOwner = transitionOwner;
      // _becomeHost() will be called when swap_host_ready arrives
      return true;
    }
  }

  async _becomeHost(transitionOwner) {
    const transitionIsCurrent = () => this._isHostedRoleTransitionCurrent(transitionOwner);
    if (!transitionIsCurrent()) return false;
    try {
      const rom = await this._downloadROM(transitionOwner.expectedRoomCode);
      if (!transitionIsCurrent()) return false;
      if (!rom) {
        this._flash('Failed to download ROM');
        return false;
      }
      await this._installRoomRom(rom, { isCurrent: transitionIsCurrent });
      if (!transitionIsCurrent()) return false;

      const save = await this._downloadSaveState(transitionOwner.expectedRoomCode, transitionIsCurrent);
      if (!transitionIsCurrent()) return false;
      if (save) this._replacePendingSave(save);

      if (!transitionIsCurrent()) return false;
      const started = await this._startHostedHost(null, transitionOwner);
      if (started === false || !transitionIsCurrent()) return false;
      this._flash(`Swapped! You are now Player ${transitionOwner.expectedPlayerNumber}`);
      return true;
    } catch (err) {
      if (!transitionIsCurrent()) return false;
      console.warn('[swap] failed to become host:', err);
      this._flash('Failed to become host');
      return false;
    }
  }

  // ── Async Turn-Based: Live Baton Pass ────────────────────
  //
  // These flip the local UI role in-place when the opponent ends their
  // turn while we're already connected to the `async:<gameId>` room. The
  // WebSocket stays open across the swap; the server slides slots and the
  // SoloStream + spectator WebRTC paths take over with a fresh offer.

  /**
   * The opponent ended their turn while we were spectating live → we are
   * now the active player. Tear down the spectator video, claim the player
   * slot on the server, then run the standard async-turn boot path so the
   * end-turn button shows and SoloStream starts streaming to (the new)
   * spectator.
   */
  async _batonBecomePlayer(payload, predecessorOwner) {
    const gameId = payload?.game_id;
    if (!gameId) return;
    const launchOwner = this._beginRoomSessionLaunchIntent(
      { clearPendingSave: true },
      predecessorOwner,
    );
    if (launchOwner == null) return false;
    const expectedNet = this.net;
    const expectedRoomCode = this.net?.roomCode;
    const batonOwner = { launchOwner, expectedNet, expectedRoomCode };
    const batonIsCurrent = () => this._roomAssignmentIsCurrent(batonOwner);
    this._flash('Your turn, passing the baton...', 2500);

    // Tear down the spectator WebRTC receiver and hide the remote video.
    if (this._spectatorPC) {
      try { this._spectatorPC.close(); } catch {}
      this._spectatorPC = null;
    }
    this._teardownSpectatorAudio();
    const videoEl = document.getElementById('guest-video');
    if (videoEl) {
      videoEl.srcObject = null;
      videoEl.classList.add('hidden');
    }
    if (this._specUnmuteOverlay) {
      this._specUnmuteOverlay.remove?.();
      this._specUnmuteOverlay = null;
    }
    if (this.canvas) this.canvas.style.display = '';
    this._stopSoloStream();
    this._releaseCanvasStream();

    // Register the host-side spectator callbacks BEFORE we send
    // swap_to_player. The server's `spectator_joined` for the demoted
    // opponent rides back on the same socket within milliseconds — if
    // onSpectatorJoined isn't set yet the message is dropped and the
    // WebRTC offer never goes out. Defer the actual offer until after
    // _startSolo() replaces the canvas and boots the emulator.
    const me = authState.getCurrentUser?.();
    const myUsername = payload.player_username || me?.displayUsername || me?.username || null;
    this._lobbySlotUsernames = [myUsername ?? '', null];
    this._lobbySlotImages = [payload.player_image || me?.image || null, null];
    this._soloRoomCode = 'async:' + gameId;
    this._spectators = this._spectators instanceof Map ? this._spectators : new Map();
    this._deferSoloSpectatorOffers = true;
    this._deferredSoloSpectatorIds = new Set();
    this._startSoloStream();

    // Promote ourselves to player on the server (in-place; no rekey, no
    // reconnect). The server validates against the DB swap that the
    // end-turn handler already committed.
    this.net.sendSwapToPlayer();

    // Load the freshly-saved state and run the standard async-turn boot.
    // _startAsyncTurn handles ROM resolution, system switching, and sets
    // _pendingSave + _pendingAsyncGame so the post-boot path lights up
    // the end-turn button.
    try {
      const ok = await this.asyncGame.loadAndResume(gameId, { isLaunchCurrent: batonIsCurrent });
      if (!ok || !batonIsCurrent()) {
        if (!batonIsCurrent()) return;
        this._flash('Could not load opponent\'s state');
        return;
      }
      const pending = this.asyncGame.consumePendingResume();
      if (!batonIsCurrent()) return;
      if (!pending) {
        this._flash('Failed to prepare game');
        return;
      }
      // Mark that we already own the live room so _ensureLiveRoom skips
      // the POST /api/rooms call and just reuses the open WebSocket.
      this._soloRoomCode = 'async:' + gameId;
      await this._startAsyncTurn(pending, { launchOwner, preserveLiveRoom: true });
      if (!batonIsCurrent()) return;
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (!batonIsCurrent()) return;
      this._deferSoloSpectatorOffers = false;
      const deferred = [...(this._deferredSoloSpectatorIds || [])];
      this._deferredSoloSpectatorIds?.clear();
      for (const spectatorId of deferred) {
        this._queueSoloSpectatorOffer?.(spectatorId, { retry: true });
      }
    } catch (err) {
      if (!batonIsCurrent()) return;
      this._deferSoloSpectatorOffers = false;
      console.error('[baton] become-player failed:', err);
      this._flash('Baton pass failed. Try the notification');
    }
  }

  /**
   * We just ended our turn and the opponent was spectating live → we
   * become the new spectator in-place. Stop the emulator + outgoing
   * stream, set up the spectator WebRTC receiver, and wait for the new
   * host's offer.
   */
  async _batonBecomeSpectator(payload) {
    const gameId = payload?.game_id;
    if (!gameId) return;
    const roomCode = payload?.room_code || `async:${gameId}`;
    const cancelPostTurnExit = () => {
      if (!this.asyncGame) return;
      this.asyncGame._skipPostEndTurnExit = true;
      if (this.asyncGame._liveBatonFallbackTimer) {
        clearTimeout(this.asyncGame._liveBatonFallbackTimer);
        this.asyncGame._liveBatonFallbackTimer = null;
      }
      if (this.asyncGame._postEndTurnExitTimer) {
        clearTimeout(this.asyncGame._postEndTurnExitTimer);
        this.asyncGame._postEndTurnExitTimer = null;
      }
    };
    if (this.state === State.SPECTATING && this.net?.roomCode === roomCode) {
      cancelPostTurnExit();
      return;
    }
    if (this._batonSpectatorTransitioning === gameId) return;
    this._batonSpectatorTransitioning = gameId;

    // Cancel the pending menu-exit that endTurn() scheduled — we're
    // staying in the (now spectator) view instead.
    cancelPostTurnExit();

    this._lobbySlotUsernames = [payload.player_username ?? '', null];
    this._lobbySlotImages = [payload.player_image || null, null];
    this._spectators = new Map();
    this._flash('Watching live: opponent has the baton', 4000);

    // Stop streaming + emulator. Keep the WebSocket open; the server has
    // already moved us into the spectator list and the new host will
    // open a fresh WebRTC offer to us.
    this._stopSoloStream();
    this._stopGame();

    // Transition to spectator view.
    this.state = State.SPECTATING;
    this._showScreen('game');
    this._updateResetButtonVisibility?.();
    this._updatePresenceList();
    if (this.canvas) this.canvas.style.display = 'none';
    const videoEl = document.getElementById('guest-video');
    if (videoEl) videoEl.classList.remove('hidden');

    // Set up the receiver-side WebRTC plumbing. We mirror the relevant
    // pieces of _prepareAndStartSpectator — but skip its room-connect
    // setup since our WebSocket is already in this room.
    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
      ],
    });
    this._spectatorPC = pc;
    pc.ontrack = (e) => {
      this._setSpectatorStream(videoEl, e.streams[0]);
    };
    pc.onicecandidate = (e) => {
      if (e.candidate) this.net.sendSignal({ type: 'ice', candidate: e.candidate });
    };
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === 'connected') this._showSysStatus?.('Watching live', 'ok');
      else if (s === 'failed' || s === 'disconnected') this._flash('Stream disconnected');
    };

    this.net.onSignal = async (sigPayload) => {
      try {
        if (sigPayload.type === 'offer') {
          let cur = this._spectatorPC;
          if (!cur || cur.connectionState === 'closed' || cur.connectionState === 'failed') {
            if (cur) cur.close();
            cur = new RTCPeerConnection({
              iceServers: [
                { urls: 'stun:stun.l.google.com:19302' },
                { urls: 'stun:stun1.l.google.com:19302' },
              ],
            });
            this._spectatorPC = cur;
            cur.ontrack = pc.ontrack;
            cur.onicecandidate = pc.onicecandidate;
            cur.onconnectionstatechange = pc.onconnectionstatechange;
          }
          await cur.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: sigPayload.sdp }));
          const answer = await cur.createAnswer();
          await cur.setLocalDescription(answer);
          this.net.sendSignal({ type: 'answer', sdp: answer.sdp });
        } else if (sigPayload.type === 'ice' && sigPayload.candidate && this._spectatorPC) {
          await this._spectatorPC.addIceCandidate(new RTCIceCandidate(sigPayload.candidate));
        }
      } catch (err) {
        console.error('[baton spectator] signal error:', err);
      }
    };
    this.net.sendSpectatorReady?.();
    this._batonSpectatorTransitioning = null;
  }

  _handleCrash(error) {
    if (!this.running || !this.emu) return false;
    if (this._crashRecoveryInProgress) return true;
    this._crashRecoveryInProgress = true;
    if (this.system?.id === 'c64') diskPicker.setC64DiskStatus(this, 'stopped', error?.message || 'Emulator crashed');
    console.error('[crash-recovery]', error);

    reportCrash({
      error: error instanceof Error ? error : new Error(String(error)),
      system: this.system?.id,
      romName: this.romName,
      romHash: this.romHash,
      appState: this.state,
      frameCount: this._libretroSoloFrame ?? this.soloFrame,
      uptime: this._serverSessionStartedAt ? Date.now() - this._serverSessionStartedAt : null,
      emu: this.emu,
      canvas: this.canvas,
      memorySnapshot: this.memoryMonitor?.getSnapshot?.() ?? null,
    });

    // Field/debug overrides are sticky by design, but they must not make the
    // crash-recovery loop deterministic. The lean Gambatte build cannot safely
    // resume an Asyncify sleep; `gb-sleepless=off` re-enables that path, while
    // an out-of-band `gb-displayhz` value can prevent the sleepless config from
    // engaging. Clear both after this exact trap so the automatic relaunch gets
    // the production-safe measured/default pacing. This runs after reportCrash
    // so telemetry still records the configuration that actually failed.
    this._healLeanGbTrapConfig(error);

    // Best-effort save BEFORE teardown (_exitToMenu → _stopGame frees the emu).
    // For a raw WASM trap (e.g. the Asyncify "null function" crash) the module's
    // Asyncify state may be poisoned, so saveState() can return null —
    // _autoSaveInner then keeps the prior save instead of clobbering it. We
    // await the result so the flash reports honestly whether anything was saved
    // and so the emu isn't freed mid-serialize. Runs async; _handleCrash still
    // returns true synchronously so window.onerror treats the error as handled.
    const canSave = !this.emu?._aborted && !!this.emu?.module?.HEAPU8;
    (async () => {
      let saved = false;
      if (canSave) {
        // N64 serializes on its run loop; at crash time the loop may be dead, so
        // use a short deadline (saveStateN64 honours opts.crash) to avoid hanging
        // teardown. Other cores ignore the flag.
        try { saved = await this._autoSave({ crash: true }); } catch {}
      }
      // WASM-trap auto-relaunch: the lean GB core's known occasional iOS traps
      // (IGNORE_INDIRECT holes — "call_indirect to a null table entry" / OOB
      // memory access) are timing-luck, not a deterministic code path, so an
      // in-place relaunch restoring the save captured a moment ago (or the
      // latest prior autosave) almost always succeeds — the crash becomes a
      // few-second hiccup instead of an ejection to the menu. Plain JS errors
      // keep the menu path: they'd recur on relaunch. _exitToMenu still runs
      // first so the session-end beacon (scores/playtime) posts for the
      // crashed session and every subsystem tears down cleanly.
      if (this._shouldAutoRelaunch(error)) {
        try {
          await this._exitToMenu({ flushPendingPostRoll: false });
          let save = null;
          try { save = await this._findLatestLocalSave(this.romHash); } catch { /* boot fresh */ }
          if (save?.emulatorState) {
            this._replacePendingSave({
              emulatorState: save.emulatorState,
              frame: save.frame || 0,
              replayLog: save.replayLog || null,
              coreName: saveManager.coreNameForSave(save, save.system || this.system?.id),
            });
            this._soloModeOverride = saveManager.soloModeForSave(save, save.system || this.system?.id);
          }
          this._flash('💥 Emulator crashed, recovering…');
          await this._startSolo();
          this._flash(save?.emulatorState
            ? '💥 Recovered from a crash, resumed from your last save'
            : '💥 Recovered from a crash, restarted from the beginning');
          this._crashRecoveryInProgress = false;
          return;
        } catch (relaunchErr) {
          console.warn('[crash-recovery] relaunch failed, falling back to menu:', relaunchErr);
        }
      }
      // Falls through here on non-trap errors, relaunch cap, or a failed
      // relaunch (a second _exitToMenu is safe — its teardowns are guarded).
      try { this._exitToMenu({ flushPendingPostRoll: false }); } catch {}
      this._flash(saved ? 'Game crashed, progress saved' : 'Game crashed');
      this._crashRecoveryInProgress = false;
    })();
    return true;
  }

  /**
   * Whether a crash should auto-relaunch the game in place instead of exiting
   * to the menu. Only SOLO sessions on a WASM core, only for wasm-trap-shaped
   * errors, and at most twice per 10 minutes — a save state that crashes the
   * core on load must not relaunch-loop the session.
   */
  _shouldAutoRelaunch(error) {
    if (this.state !== State.SOLO || !this.romData) return false;
    if (!(this.emu instanceof LibretroCore)) return false;
    const msg = String(error?.message || error || '');
    const isTrap = (typeof WebAssembly !== 'undefined' && error instanceof WebAssembly.RuntimeError)
      || /call_indirect|null function|out of bounds memory|memory access out of bounds|unreachable/i.test(msg);
    if (!isTrap) return false;
    const now = Date.now();
    this._crashRelaunches = (this._crashRelaunches || []).filter((t) => now - t < 600_000);
    if (this._crashRelaunches.length >= 2) return false;
    this._crashRelaunches.push(now);
    return true;
  }

  /** Remove sticky lean-GB pacing debug overrides after an Asyncify-shaped
   *  trap so crash recovery does not relaunch the same broken configuration. */
  _healLeanGbTrapConfig(error) {
    if (this.system?.id !== 'gb'
        || !['gambatte', 'gambatte_v2'].includes(this.emu?.coreName)) return false;
    const msg = String(error?.message || error || '');
    const isTrap = (typeof WebAssembly !== 'undefined' && error instanceof WebAssembly.RuntimeError)
      || /call_indirect|null function|out of bounds memory|memory access out of bounds|unreachable/i.test(msg);
    if (!isTrap) return false;
    try {
      localStorage.removeItem('retroplay:gb-sleepless');
      localStorage.removeItem('retroplay:gb-displayhz');
    } catch { /* storage unavailable — relaunch still proceeds */ }
    return true;
  }

  /** Latest local save (auto or manual) for a ROM — the crash-relaunch restore
   *  source. Instance seam so tests can stub it (module imports aren't mocked
   *  in main.test.js). */
  async _findLatestLocalSave(romHash) {
    await initSaveDB();
    return findMostRecentSave(romHash);
  }

  _onVisibilityChange() {
    // A hidden tab stops rAF, so the C64 solo loop gets no paused callback:
    // restart the evaluation clock's measurement on every visibility change.
    noteC64PauseChange(this);
    if (!this.running || !this.emu?.selfDriving || this._unsafeReplayBoundary === this.emu) return;
    if (!(this.emu instanceof LibretroCore)) return;

    if (document.hidden) {
      if (typeof this.emu.module?.pauseMainLoop === 'function') {
        this.emu.module.pauseMainLoop();
        this._wasmPausedByVisibility = true;
        noteC64PauseChange(this);
      }
      // N64 deliberately does NOT save here: the in-loop serialize pump only
      // fires while emscripten_mainloop iterates, and the loop stops the moment
      // the tab is hidden (rAF throttles to ~0 Hz). N64 auto-saves on the
      // periodic during-play timer instead. See docs/n64-save-handoff.md.
    } else if (this._wasmPausedByVisibility) {
      this._wasmPausedByVisibility = false;
      noteC64PauseChange(this);
      if (typeof this.emu.module?.resumeMainLoop === 'function') {
        this.emu.module.resumeMainLoop();
      }
      resumeAllAudioContexts();
    }
  }

  // ── Solo idle guard ("are you still there?") ─────────────
  // Bound once at construction. The watcher is a no-op until _startIdleGuard()
  // (solo session start) and is torn down by _stopIdleGuard() in _exitToMenu.
  _bindIdleActivity() {
    // "Soft" activity: passive movement — resets the idle timer but never
    // dismisses the prompt (noteActivity is a no-op while prompting).
    const soft = () => this.idleWatcher.noteActivity();
    document.addEventListener('wheel', soft, { passive: true });
    document.addEventListener('pointermove', () => {
      const now = Date.now();
      if (now - this._idleLastMove < 5000) return; // throttle ~1/5s
      this._idleLastMove = now;
      this.idleWatcher.noteActivity();
    }, { passive: true });
    // "Strong" activity: deliberate input — affirms if the prompt is showing,
    // otherwise just resets the timer.
    const strong = () => this._idleStrongActivity();
    document.addEventListener('keydown', strong);
    document.addEventListener('pointerdown', strong, { passive: true });
    document.addEventListener('touchstart', strong, { passive: true });
    document.getElementById('idle-prompt-stay')?.addEventListener('click', () => this._idleStrongActivity());
  }

  _idleStrongActivity() {
    if (this.idleWatcher.isPrompting()) this._idleAffirm();
    else this.idleWatcher.noteActivity();
  }

  _startIdleGuard() {
    this.idleWatcher.start();
    if (this._idleGamepadPoll) clearInterval(this._idleGamepadPoll);
    this._idleGamepadPoll = setInterval(() => this._pollGamepadActivity(), 1000);
  }

  _stopIdleGuard() {
    this.idleWatcher.stop();
    if (this._idleGamepadPoll) { clearInterval(this._idleGamepadPoll); this._idleGamepadPoll = null; }
    this._hideIdlePrompt();
  }

  _pollGamepadActivity() {
    const pads = navigator.getGamepads ? navigator.getGamepads() : [];
    let active = false;
    for (const p of pads) {
      if (!p) continue;
      if (p.buttons.some(b => b.pressed)) { active = true; break; }
      if (p.axes.some(a => Math.abs(a) > 0.5)) { active = true; break; }
    }
    if (active) this._idleStrongActivity();
  }

  _showIdlePrompt() {
    const modal = document.getElementById('idle-prompt');
    const label = document.getElementById('idle-prompt-countdown');
    if (!modal) return;
    let remaining = 120;
    if (label) label.textContent = String(remaining);
    modal.classList.remove('hidden');
    if (this._idleCountdownTimer) clearInterval(this._idleCountdownTimer);
    this._idleCountdownTimer = setInterval(() => {
      remaining -= 1;
      if (label) label.textContent = String(Math.max(0, remaining));
      if (remaining <= 0 && this._idleCountdownTimer) {
        clearInterval(this._idleCountdownTimer);
        this._idleCountdownTimer = null;
      }
    }, 1000);
  }

  _hideIdlePrompt() {
    document.getElementById('idle-prompt')?.classList.add('hidden');
    if (this._idleCountdownTimer) { clearInterval(this._idleCountdownTimer); this._idleCountdownTimer = null; }
  }

  _idleAffirm() {
    this._hideIdlePrompt();
    this.idleWatcher.affirm();
  }

  _idleExpire() {
    this._hideIdlePrompt();
    this._flash('Solo session ended: no activity for an hour');
    this._exitToMenu();
  }

  _requestExitToMenu() {
    if (this.state === State.SPECTATING) {
      this._exitToMenu();
      return;
    }
    showExitConfirm(this);
  }

  async _exitToMenu({ flushPendingPostRoll = true, land = true } = {}) {
    if (this._unsafeReplayBoundary) {
      const failures = [];
      let key = null, input = null, room = null, module = null, boundary = null, retained = false;
      try {
        key = roomSessionKey(this); input = this._gameplayInputLeases;
        room = this._gameRoom; module = this._gameRoomModule; boundary = this._unsafeReplayBoundary; retained = true;
      } catch (cause) { failures.push({ item: 'boundary-binding', cause }); }
      const current = () => retained && isCurrentSessionKey(this, key) && this._gameplayInputLeases === input
        && this._gameRoom === room && this._gameRoomModule === module && this._unsafeReplayBoundary === boundary;
      try {
        const result = this._leavePageAfterUncertainBoundary();
        failures.push({ item: 'boundary-reload', cause: Object.assign(
          new Error('The unsafe replay boundary reload was not verified'), { result }) });
      } catch (cause) { failures.push({ item: 'boundary-reload', cause }); }
      // A returned value proves neither navigation nor recovery. Keep the
      // unsafe core halted and offer the provider's independently owned Reload.
      try {
        const proof = ensureRoomRecovery(this, { destination: null, isCurrent: current, nativeOnly: true, doc: document });
        if (verifyRoomRecoveryOwnership(this, proof, { isCurrent: current, nativeOnly: true, doc: document }) !== true)
          throw new Error('the owned native Reload action was not verified');
        for (const entry of Object.getOwnPropertyDescriptors(proof).failures.value)
          failures.push({ item: `boundary-recovery:${entry.item}`, cause: entry.cause });
      } catch (cause) {
        let entries = null;
        try { entries = cause?.failures; }
        catch (inspectionCause) { failures.push({ item: 'boundary-recovery:inspection', cause: inspectionCause }); }
        if (Array.isArray(entries)) for (let index = 0; index < entries.length; index += 1) {
          try {
            const entry = entries[index];
            if (Object.hasOwn(entries, index) && entry && typeof entry.item === 'string' && Object.hasOwn(entry, 'cause'))
              failures.push({ item: `boundary-recovery:${entry.item}`, cause: entry.cause });
          } catch (inspectionCause) { failures.push({ item: 'boundary-recovery:inspection', cause: inspectionCause }); }
        }
        failures.push({ item: 'boundary-recovery', cause });
      }
      const cause = failures.length === 1 ? failures[0].cause : Object.assign(
        new AggregateError(failures.map((entry) => entry.cause), 'The unsafe replay boundary could not leave',
          { cause: failures[0].cause }), { name: 'GameRoomBoundaryExitError', failures });
      const ending = Promise.resolve({ replayFlush: 'failed', sessionEnd: 'failed', cause });
      // Verified pending recovery is still a failed teardown, never reloading.
      return land ? undefined : { ending };
    }
    // A library-origin exit can pause at the durability barrier below. Treat
    // repeat clicks/navigation attempts during that wait as the same exit;
    // otherwise a second call would see cleared session metadata, reveal home,
    // and let the first call redirect unexpectedly when its POST later lands.
    if (this._libraryExitInFlight) return this._libraryExitInFlight;

    // Leaving gameplay is itself a competing launch intent. Invalidate room,
    // save, ROM, and game-change continuations before any teardown awaits can
    // let them resume against the menu or a later session.
    if (!this._multitapSession?.involuntary) this.net.clearMultitapReconnect?.(this.net.roomCode);
    const exitingMultitap = retireMultitapSession(this);
    this._beginGameLaunchIntent({ clearPendingSave: true });
    this._escExitPrimed = 0;
    hideExitConfirm(this);

    const prevState = this.state;
    this.state = State.MENU;
    if (prevState === State.PLAYING || prevState === State.SPECTATING
        || prevState === State.LOBBY && exitingMultitap?.completedNative) {
      this.net.disconnect();
    }
    if (prevState === State.SOLO && this.net?.connected) {
      this.net.disconnect(4000);
    }
    this._stopRoomScreenshots();
    this._stopSoloStream();
    this._stopIdleGuard();
    this._teardownSbsSession();
    this._destroyClipRecorder();
    this._destroyReplayRecorder({ flushPendingPostRoll, sessionEnd: true });
    this._releaseCanvasStream();
    this._soloRoomCode = null;
    this._roomPeerUserId = null;
    try { sessionStorage.removeItem('retroplay:soloSession'); } catch {}
    const path = window.location.pathname;
    if (path !== '/') {
      window.history.replaceState({}, '', '/');
    }
    // Session accounting no longer needs the emulator once the request has
    // been assembled. Delivery is durable (keepalive + the session-end
    // outbox), so it must not hold a same-document homepage return on a
    // stopped game screen while a replay flush or the network is slow. Once
    // the write commits, _onPlaySessionRecorded refreshes visible home panels.
    const endingPlaySession = this._endPlaySession()
      .catch((cause) => ({ replayFlush: 'none', sessionEnd: 'failed', cause }));
    const stopped = exitingMultitap ? this._stopGame({ multitapAudioOwned: true }) : this._stopGame();
    if (stopped?.departed === true) return {
      ending: endingPlaySession, departed: true, reason: stopped.reason, cause: stopped.cause,
    };
    // A terminal title stop can finish after its socket began reconnecting.
    // disconnect() cancels that retry; retire its overlay only after the stop
    // confirms that a replacement session has not taken ownership.
    this._hideReconnectOverlay();
    this._resetRoomSessionUi();
    // G6: the room's leave flow runs its own destination after this teardown,
    // so it skips the menu landing, the home page view and the library redirect.
    if (!land) return { ending: endingPlaySession };

    // If the user came in via a library deep-link (?system=X&rom=Y),
    // send them back to /library instead of dumping them on the main
    // app menu. This is a full-document navigation: wait until the final
    // replay evidence and session-end POST have completed (or the failed POST
    // has entered the durable outbox) before unloading the page. Same-document
    // homepage returns below deliberately remain non-blocking.
    if (this._arrivedFromLibrary) {
      this._arrivedFromLibrary = false;
      const returnToLibrary = endingPlaySession.then(() => window.location.assign('/library'));
      let trackedExit;
      trackedExit = returnToLibrary.finally(() => {
        if (this._libraryExitInFlight === trackedExit) this._libraryExitInFlight = null;
      });
      this._libraryExitInFlight = trackedExit;
      return trackedExit;
    }

    this._showScreen('menu');
    this._updateResetButtonVisibility();
    this._loadSavesList();
    this._loadMenuData();
  }

  _micLevelLoop(now) {
    return roomChat.micLevelLoop(this, now);
  }

  _stopMicLevelLoop() {
    return roomChat.stopMicLevelLoop(this);
  }

  /**
   * SM5: the room cleanup runs even when a teardown step throws, and the
   * exception still propagates.
   */
  _stopGame({ multitapAudioOwned = false } = {}) {
    this._multitapReplayStartup?.abort();
    const multitap = retireMultitapSession(this);
    const stopped = gameActions.endRoomSession(this);
    // A confirmed replacement owns the shared app; neither teardown nor finally may run.
    if (stopped.departed === true) return stopped;
    try {
      if (multitapAudioOwned || multitap) this._stopGameTeardown({ multitapAudioOwned: true });
      else this._stopGameTeardown();
    } finally {
      callRoom(this, 'onSessionEnd', 'stop');
      finishRoomSessionCleanup(this);
    }
    return stopped;
  }

  _stopGameTeardown({ multitapAudioOwned = false } = {}) {
    // PR 2 (spec 8.8, README decision 6): the author session and any open draft score run end first,
    // while the launch generation, the studio and the draft clock still answer for this launch.
    try { authoringHost.endAuthorSessionForTeardown(this); } catch (error) { console.warn('[authoring] teardown hook failed', error); }
    multitapAudioOwned ||= !!(this._superunoCompletedLease || this._superunoCompletedRetirement);
    clearSuperunoLobbyStartup(this);
    clearSuperunoReady(this);
    retireSuperunoMatchEnd(this);
    void this._retireSuperunoStoppedRoom?.();
    const replayBoot = this._multitapReplayPendingBoot;
    const pendingReplayBoot = this.system?.id === 'snes' && this.emu?.coreName === 'snes9x2010_multitap'
      && replayBoot?.core === this.emu && replayBoot.settled ? replayBoot : null;
    if (pendingReplayBoot) {
      pendingReplayBoot.retire();
      // Hold the actual session initialization/cleanup settlement, including
      // its quarantine result. The native builder must not await its own hold.
      const retirement = pendingReplayBoot.settled.then(() => ({ ok: !this._unsafeReplayBoundary }));
      this._multitapRetirement = retirement;
      void retirement.then(() => { if (this._multitapRetirement === retirement) this._multitapRetirement = null; });
      multitapAudioOwned = true;
    }
    this._closeShare?.();
    // §6: a session torn down during startup resolves as superseded.
    this._lockstepSession?.resources?.restoreHandlers?.();
    this._lockstepSession?.settle({ start: 'superseded', recording: 'off', stages: [], cleanupErrors: [] });
    this._lockstepSession = null;
    this._roomProfile = null; this._multitapOwnerRun = null; this._multitapOwnerWork = null;
    this._dismissSaveOverwritePicker?.('cancel');
    this.releaseGameplayInput('game-change');
    if (isSavesPanelOpen()) closeSavesPanel();
    this._replayBoundaryOwner = null;
    this._stateCapturePending = false;
    this._replaySessionGen = (this._replaySessionGen || 0) + 1;
    // Invalidate solo launches that are still in auth/save/flag/room/audio
    // setup and have not reached _createEmulator yet.
    this._gameLaunchGeneration = (this._gameLaunchGeneration || 0) + 1;
    // Cancel any WASM factory still awaiting initialization. Its continuation
    // sees the generation mismatch, destroys the late module, and returns null.
    this._emulatorGeneration = (this._emulatorGeneration || 0) + 1;
    this._beginFinalScoreReplayFlush();
    this._rewindRing = [];
    this._rewindConfirmed = false;
    this._achievementsDisabledThisSession = false;
    this._achievementsDisabledReason = null;
    this._c64SpeedLatched = false;
    clearTimeout(this._rewindIndicatorTimer);
    document.getElementById('rewind-indicator')?.classList.add('hidden');
    hideRewindConfirm(this);
    // Every exit, relaunch and room departure resolves a pending speed confirm as a
    // cancel, without moving focus (and the select's input lease) into the closing screen.
    cancelC64Speed(this, { focus: false });
    this._stopMicLevelLoop();
    this._destroyClipRecorder();
    this._destroyReplayRecorder({ sessionEnd: true });
    dispatchReplayRecording(this, { recording: false, reason: 'session_end' });
    this._clearPendingBaseGeneration();
    this._teardownGhostRace();
    if (multitapAudioOwned) this._endPlaySession({ multitap: true });
    else {
      this._endAchievementSession();
      this._endPlaySession(); // no-op if already ended by _endAchievementSession
    }
    clearBreadcrumb();
    this._stopMemoryMonitor();
    diskPicker.resetC64RuntimeControls(this);
    resetC64SpecialKeys(this);
    this.running = false;
    this._discardSoloHandoff();
    this._discardPendingSave(this._pendingSave);
    this._soloHandoffWasmPaused = false;
    noteC64PauseChange(this);
    // The core is released below, so a visibility pause ends with the session.
    // Once `running` is false the visibility handler returns before clearing it.
    this._wasmPausedByVisibility = false;
    noteC64PauseChange(this);
    this._hostedRoleTransitionOwner = null;
    this._discardHostedSwapHandoff();
    clearInterval(this._autoSaveInterval);
    this._autoSaveInterval = null;
    try { this._libretroCanvasObs?.disconnect(); } catch {}
    this._libretroCanvasObs = null;
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    // Clean up audio (AudioProcessor + any Emscripten-created contexts)
    if (this._clipAudioCapture) {
      this._clipAudioCapture.destroy();
      this._clipAudioCapture = null;
    }
    this._teardownSpectatorAudio();
    this.audio.destroy();
    // The retiring profile core owns its contexts until real native/bulk drain.
    // Closing every context here could interrupt its snapshot acknowledgement.
    if (!multitapAudioOwned) closeAllAudioContexts();
    // Snapshot C64 save disk data before destroying the VFS, then upload
    if (this.system?.id === 'c64' && this.emu?.saveDiskCount
        && authState.getCurrentUser() && !this._c64DiskSyncDisabled) {
      const snapshots = [];
      for (let s = 0; s < this.emu.maxSaveDiskSlots; s++) {
        if (!this.emu._saveDiskActivated[s]) continue;
        const data = this.emu.readDiskByIndex(this.emu.gameDiskCount + s);
        if (data) snapshots.push({ slot: `disk${s}`, data, label: this._userDiskNames?.[s] || `Save Disk ${s + 1}` });
      }
      if (snapshots.length) {
        const sys = this.system.id;
        const hash = this._isBasicMode ? 'basic' : this.romHash;
        const romName = this._isBasicMode ? null : this.romName || null;
        Promise.all(snapshots.map(s => {
          const meta = this.saveSync?.getServerMeta(sys, hash, 'disk', s.slot);
          return this.saveSync?.upload(sys, hash, 'disk', s.slot, s.data, meta?.sha256 || '', s.label, romName);
        })).catch(() => {});
      }
    }
    if (this._isBasicMode && this.system?.id === 'c64' && this.emu?.loaded
        && authState.getCurrentUser() && !this._c64DiskSyncDisabled) {
      const scratchData = this.emu.readDiskFromVFS('/game/scratch.d64');
      if (scratchData) {
        const meta = this.saveSync?.getServerMeta('c64', 'basic', 'disk', 'scratch');
        this.saveSync?.upload('c64', 'basic', 'disk', 'scratch', scratchData, meta?.sha256 || '', this._scratchDiskName || 'Scratch')
          .catch(() => {});
      }
    }
    this._userDiskNames = null;
    this._scratchDiskName = 'Scratch';
    this._c64DiskSyncDisabled = false;
    // Clean up emulator
    // Virtual pads wrap navigator.getGamepads above the libretro native gate.
    // Unwrap them first so core.destroy can restore the original reader.
    if (this.virtualPads) {
      this.virtualPads.destroy();
      this.virtualPads = null;
      this.input.useRealGamepads(null);
    }
    if (this.emu) {
      if (!pendingReplayBoot || this.emu !== pendingReplayBoot.core) this.emu.destroy();
      this.emu = null;
    }
    this.input.stop();
    this.rollback = null;
    this._specInputs = null;
    this._isMultiDisk = false;
    this._isBasicMode = false;
    this.canvas.classList.remove('show-cursor');
    this._p2PrevBitmask = 0;
    this._lastSentBitmask = undefined;
    this._lastSentAnalogX = undefined;
    this._lastSentAnalogY = undefined;

    // Clean up voice moderation + report
    if (this._voiceModeration) {
      this._voiceModeration.destroy();
      this._voiceModeration = null;
    }
    document.getElementById('btn-report')?.classList.add('hidden');
    // Clean up lockstep netplay
    this._stopLockstep();
    // Clean up link cable netplay
    this._stopLinkCable();
    // Clean up hosted netplay
    if (this.hostedNetplay) {
      this.hostedNetplay.destroy();
      this.hostedNetplay = null;
    }
    // Clean up multi-peer hosted netplay (N64 4-player)
    if (this.hostedMultiPeer) {
      this.hostedMultiPeer.destroy();
      this.hostedMultiPeer = null;
    }
    this._isMultiPeerHost = false;
    this.inputParity = null;
    this._stopParityHudTimer();
    this._renderParityHud();
    this._hostFrameEstimate = 0;
    this._hostDelayFrames = 0;
    this._hostFrameKnownAt = 0;
    if (this.net) this.net.onPingCheckRequest = null;
    // Clean up spectator WebRTC connection
    if (this._spectatorPC) {
      try { this._spectatorPC.close(); } catch { /* ignore */ }
      this._spectatorPC = null;
    }
    if (this._specUnmuteOverlay) {
      this._specUnmuteOverlay.remove();
      this._specUnmuteOverlay = null;
    }
    this._setRoomMicControlsVisible(false);
    // Restore video/canvas visibility
    const guestVideo = document.getElementById('guest-video');
    if (guestVideo) {
      guestVideo.classList.add('hidden');
      guestVideo.srcObject = null;
    }
    this.canvas.style.display = '';
    // T3: the core is destroyed and #guest-video hidden, so no capture source
    // remains. The explicit hides below (#btn-change-game and the rest) run
    // after this refresh and keep their effect.
    gameActions.refreshGameToolbarAvailability(this, 'session-stop');

    this._hideDiskPicker();
    this._closeCheatsheet();
    this._closeCheevoPanel();
    this._hideSysStatus();
    this._closeChat();
    this._closeChatDrawer();
    document.getElementById('hud')?.classList.add('hidden');
    document.getElementById('stat-cheevos')?.classList.add('hidden');
    document.getElementById('swap-prompt')?.classList.add('hidden');
    document.getElementById('game-change-prompt')?.classList.add('hidden');
    this._hideGameChangeOverlay();
    document.getElementById('btn-change-game')?.classList.add('hidden');
    document.getElementById('touch-gamepad')?.classList.add('hidden');
  }

  // ── Game Change ────────────────────────────────────────

  // The overlay itself lives in modules/game-change.js; these delegates keep
  // the names tests and other modules call.
  _showGameChangeOverlay() { return showGameChangeOverlay(this); }
  _hideGameChangeOverlay() { return hideGameChangeOverlay(this); }
  _loadGameChangeRomList(systemId) { return loadGameChangeRomList(this, systemId); }
  _loadGameChangeTop() { return loadGameChangeTop(this); }
  _gcMakeTopEntry(system, romName, title, hash) { return gcMakeTopEntry(this, system, romName, title, hash); }

  // Route a Change Game selection to the right executor. In a netplay room the
  // host requests the change and waits for the opponent; in solo there's no
  // opponent, so we boot the new game locally.
  _requestGameChange(system, romName) {
    if (this._netMode === 'link-cable') {
      this._flash('Start a new link cable room to change cartridges');
      return;
    }
    const launchOwner = this.state === State.SOLO
      ? this._beginGameLaunchIntent({ clearPendingSave: true })
      : this._beginRoomSessionLaunchIntent({ clearPendingSave: true });
    const roomSessionOwner = this.state === State.SOLO
      ? null
      : this._captureRoomSessionOwner();
    this._hideGameChangeOverlay();
    this._pendingGameChange = { system, romName, launchOwner, roomSessionOwner };
    if (this.state === State.SOLO) {
      this._executeSoloGameChange();
      return;
    }
    this.net.sendGameChangeRequest(system, romName);
    document.getElementById('btn-change-game').disabled = true;
    this._flash('Waiting for opponent...');
  }

  // Fetch a ROM from the library, unzip if needed, and stash it as the current
  // ROM via _setRom. Returns false on any failure so callers can bail. Shared
  // by the netplay (_executeGameChange) and solo (_executeSoloGameChange) flows.
  async _fetchAndSetRom(system, romName, launchOwner, roomSessionOwner = null,
    { suppressPlayModePrompt = false } = {}) {
    const launchNamespace = currentSaveDBNamespace();
    const ownsLaunch = () => launchOwner === undefined
      || (roomSessionOwner
        ? this._roomAssignmentIsCurrent(roomSessionOwner)
        : this._isGameLaunchOwnerCurrent(launchOwner));
    try {
      let multiDisk = this._multiDiskRoms?.[system]?.has(romName) === true;
      const url = `/api/roms/${system}/${encodeURIComponent(romName)}`;
      let res = await fetch(multiDisk ? `${url}/disks` : url);
      if (!ownsLaunch()) return false;
      // Queue/favorite selections may precede loading the system's catalog.
      // A C64 directory cannot be downloaded through the single-file route.
      if (!multiDisk && system === 'c64' && res.status === 404) {
        multiDisk = true;
        res = await fetch(`${url}/disks`);
      }
      if (!ownsLaunch()) return false;
      if (!res.ok) throw new Error('Download failed');
      if (multiDisk) {
        const bundle = await res.json();
        if (!ownsLaunch()) return false;
        if (!Array.isArray(bundle.disks) || !bundle.disks.length) return false;
        // The verified catalog identity (spec 4.3); an invalid bundle throws
        // into the catch below and the launch fails.
        const identity = await resolveC64LaunchIdentity(this, bundle, {
          headerHash: res.headers?.get?.(CATALOG_HASH_HEADER) ?? null, isCurrent: ownsLaunch });
        if (!identity || !ownsLaunch()) return false;
        this.romData = bundle;
        this._romDataB64 = btoa(JSON.stringify(bundle));
        this.romName = bundle.name;
        this._activeRomFilename = romName;
        this._isMultiDisk = true;
        commitC64Identity(this, identity);
        this._resetCanonicalTitle();
        const label = document.getElementById('rom-name');
        label.textContent = `${romName} (${bundle.disks.length} disks)`;
        label.classList.remove('hidden');
        this._updateLobbyGameTitle();
        await finishC64LaunchIdentity(this, bundle, identity, { expectedNamespace: launchNamespace });
        if (!ownsLaunch()) return false;
        return true;
      }
      const buf = await res.arrayBuffer();
      if (!ownsLaunch()) return false;
      let data = new Uint8Array(buf);
      let name = romName;

      // Extract from zip if needed
      if (romName.toLowerCase().endsWith('.zip')) {
        const extracted = this._extractRomFromZip(data);
        if (extracted) {
          data = extracted.data;
          name = extracted.name;
        }
      }

      let romStr = '';
      for (let i = 0; i < data.length; i++) romStr += String.fromCharCode(data[i]);
      if (!ownsLaunch()) return false;
      this._activeRomFilename = romName;
      this._setRom(romStr, name, { preserveLaunchIntent: true, suppressPlayModePrompt });
      return true;
    } catch {
      return false;
    }
  }

  async _executeGameChange() {
    if (!this._pendingGameChange) return;
    const { system, romName, launchOwner, roomSessionOwner = null } = this._pendingGameChange;
    const ownsLaunch = () => launchOwner === undefined
      || (roomSessionOwner
        ? this._roomAssignmentIsCurrent(roomSessionOwner)
        : this._isGameLaunchOwnerCurrent(launchOwner));
    if (!ownsLaunch()) return;
    this._pendingGameChange = null;

    // Stop current game (emulator, WebRTC, etc.) but keep WebSocket alive
    this._stopGame();

    // Switch system if needed
    if (system !== this.system.id) {
      this._selectSystem(system);
    }

    const romLoaded = roomSessionOwner
      ? await this._fetchAndSetRom(system, romName, launchOwner, roomSessionOwner)
      : await this._fetchAndSetRom(system, romName, launchOwner);
    if (!romLoaded) {
      if (!ownsLaunch()) return;
      this._flash('Failed to load ROM');
      return;
    }
    if (!ownsLaunch()) return;

    // Upload the new ROM, then update our lobby capacity before the server can
    // notify peers and admit the newly available N64 seats.
    await this._uploadROM(this.net.roomCode);
    if (!ownsLaunch()) return;

    // Show lobby while waiting for P2
    this.state = State.LOBBY;
    this._showScreen('lobby');
    this._configureLobbySlots(this.system?.maxPlayers || 2);
    this._refreshLobbyInviteButton();
    document.getElementById('lobby-status').textContent = 'Waiting for opponent to load...';
    document.getElementById('btn-change-game').disabled = false;

    this._roomCoreName = resolveCoreName(this.system, true, this.romData);
    this.net.sendGameChange(this.system.id, this._roomCoreName);
  }

  // Solo change-game: no opponent to coordinate with, so tear down the current
  // game and boot the new ROM locally. Re-entering _startSolo reuses the full
  // solo boot path — including re-uploading the new ROM to the current owned
  // room so any invited spectators follow along, even after leaving BASIC.
  async _executeSoloGameChange() {
    if (!this._pendingGameChange) return;
    const { system, romName, launchOwner } = this._pendingGameChange;
    const ownsLaunch = () => launchOwner === undefined
      || this._isGameLaunchOwnerCurrent(launchOwner);
    if (!ownsLaunch()) return;
    this._pendingGameChange = null;

    const retainedRoomCode = this._soloRoomCode || null;
    if (retainedRoomCode) {
      this._stopRoomScreenshots();
      this._stopSoloStream();
      this._stopIdleGuard();
      this._releaseCanvasStream();
    }
    this._stopGame();

    // Switch system if needed
    if (system !== this.system.id) {
      this._selectSystem(system);
    }

    if (!(await this._fetchAndSetRom(system, romName, launchOwner))) {
      if (!ownsLaunch()) return;
      this._flash('Failed to load ROM');
      return;
    }
    if (!ownsLaunch()) return;

    this._restartSoloRoomCode = retainedRoomCode;
    try {
      await this._startSolo();
    } finally {
      this._restartSoloRoomCode = null;
    }
  }

  // ── Disk Picker ────────────────────────────────────────

  _toggleCheatsheet() {
    const result = diskPicker.toggleCheatsheet(this);
    // X-ESC: where the controls overlay opened in the order Esc compares with
    // the game room's top layer (game-keys.js).
    if (this._cheatsheetOpen === true) noteSurfaceOpened(document.getElementById('cheatsheet'));
    return result;
  }

  _closeCheatsheet() {
    return diskPicker.closeCheatsheet(this);
  }

  _autoShowCheatsheet() {
    return diskPicker.autoShowCheatsheet(this);
  }

  _isCheatsheetDismissed(systemId) {
    return diskPicker.isCheatsheetDismissed(this, systemId);
  }

  _setCheatsheetDismissed(systemId, dismissed) {
    return diskPicker.setCheatsheetDismissed(this, systemId, dismissed);
  }

  _updateCheatsheetFooter() {
    return diskPicker.updateCheatsheetFooter(this);
  }

  /** Populate the cheatsheet overlay with controls for the active system. */
  _updateCheatsheet() {
    return diskPicker.updateCheatsheet(this);
  }

  _showDiskPicker() {
    return diskPicker.showDiskPicker(this);
  }

  _hideDiskPicker() {
    return diskPicker.hideDiskPicker(this);
  }

  _toggleDiskPicker(drive = 8) {
    return diskPicker.toggleDiskPicker(this, drive);
  }

  _diskPickerSelect(index) {
    return diskPicker.diskPickerSelect(this, index);
  }

  _diskPickerKeyHandler(e) {
    return diskPicker.diskPickerKeyHandler(this, e);
  }

  _createSaveDisk() {
    return diskPicker.createSaveDisk(this);
  }

  _copyMountedDisk() {
    return diskPicker.copyMountedDisk(this);
  }

  async _saveDisksToAccount(opts) {
    return diskPicker.saveDisksToAccount(this, opts);
  }

  async _loadUserDisksFromServer() {
    return diskPicker.loadUserDisksFromServer(this);
  }

  _downloadScratchDisk() {
    return diskPicker.downloadScratchDisk(this);
  }

  _downloadDisk(index, name) {
    return diskPicker.downloadDisk(this, index, name);
  }

  async _saveScratchDiskToAccount() {
    return diskPicker.saveScratchDiskToAccount(this);
  }

  /** Turn a supported single-file C64 disk into the one-entry playlist shape.
   *  The playlist preallocates save-disk slots before VICE boots, which makes
   *  actual runtime swapping possible for library and BYO `.d64` launches. */
  _singleC64DiskForCurrentRom() {
    if (this.system?.id !== 'c64' || this._isMultiDisk) return null;
    const name = String(this.romName || this._activeRomFilename || '').split(/[\\/]/).pop();
    const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')).toLowerCase() : '';
    if (!this.system.diskExtensions?.includes(ext)) return null;
    const source = this.romData;
    let data;
    if (source instanceof Uint8Array) data = new Uint8Array(source);
    else if (ArrayBuffer.isView(source)) {
      data = new Uint8Array(source.buffer.slice(source.byteOffset, source.byteOffset + source.byteLength));
    } else if (source instanceof ArrayBuffer) data = new Uint8Array(source.slice(0));
    else if (typeof source === 'string') data = Uint8Array.from(source, char => char.charCodeAt(0) & 0xff);
    else return null;
    return { name, data };
  }

  _setC64EmulationSpeed(multiplier) {
    return requestC64Speed(this, multiplier);
  }

  /** Esc's close for the classic C64 speed confirm (game-keys.js CLASSIC_SURFACES). */
  _cancelC64Speed() {
    cancelC64Speed(this);
  }

  _renameScratchDisk(labelEl) {
    return diskPicker.renameScratchDisk(this, labelEl);
  }

  _renameSaveDisk(labelEl, slotIndex) {
    return diskPicker.renameSaveDisk(this, labelEl, slotIndex);
  }

  _hashString(str) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) {
      hash ^= str.charCodeAt(i);
      hash = (hash * 0x01000193) >>> 0;
    }
    return hash.toString(16).padStart(8, '0');
  }

  // ── System Status Indicator ───────────────────────────

  _showSysStatus(text, level = 'ok') {
    const el = document.getElementById('sys-status');
    const dot = document.getElementById('sys-status-dot');
    const txt = document.getElementById('sys-status-text');
    if (!el) return;
    el.classList.remove('hidden');
    dot.className = `sys-dot ${level}`;
    txt.textContent = text;
  }

  _hideSysStatus() {
    document.getElementById('sys-status')?.classList.add('hidden');
    if (this._sysStatusTimer) {
      clearInterval(this._sysStatusTimer);
      this._sysStatusTimer = null;
    }
  }

  /**
   * Start the periodic system-status update: real measured FPS for JS-driven
   * cores, the steady native rate for self-driving cores.
   */
  _startFpsMonitor() {
    if (this.system?.id === 'c64') {
      diskPicker.updateC64RuntimeControls(this);
      updateC64SpecialKeys(this);
    }
    this._fpsFrames = 0;
    this._fpsLastTime = performance.now();

    this._sysStatusTimer = setInterval(() => {
      const now = performance.now();
      const elapsed = (now - this._fpsLastTime) / 1000;
      this._fpsLastTime = now;

      if (!this.running || !this.emu) {
        this._showSysStatus('Stopped', 'error');
        return;
      }

      if (this.emu.selfDriving) {
        // Self-driving cores run at native FPS and JS never steps their
        // frames, so there is nothing real to measure. Don't probe canvas
        // pixels for "activity" either: readbacks race the core's GL ops
        // (why N64 always skipped it), and a non-preserved WebGL buffer
        // legitimately reads black mid-play, which surfaced as a bogus
        // persistent "Waiting..." pill on iOS.
        const targetFps = this.system?.fps || 60;
        this._lastFps = Math.round(targetFps);
        this._showSysStatus(`${this._lastFps} FPS · 100%`, 'ok');
      } else {
        // JS-driven cores — measure actual frames
        const fps = this._fpsFrames / elapsed;
        this._fpsFrames = 0;
        this._lastFps = Math.round(fps);
        const targetFps = this.system?.fps || 60;
        const pct = Math.round((fps / targetFps) * 100);
        const level = pct >= 95 ? 'ok' : pct >= 70 ? 'warn' : 'error';
        this._showSysStatus(`${this._lastFps} FPS · ${pct}%`, level);
      }
    }, 1000);
  }

  /** Call from the game loop to count frames for FPS measurement */
  _tickFps() {
    this._fpsFrames = (this._fpsFrames || 0) + 1;
  }

  // ── Stats Screen Routing ────────────────────────────────

  /**
   * Push `/u` (or `/u/:username`) and open the stats screen.
   * Used by the menu nav button and any other entry point.
   */
  _navigateToStats(username = null) {
    return screenRouter.navigateToStats(this, username);
  }

  _navigateToActivity(username = null, tab = null) {
    return screenRouter.navigateToActivity(this, username, tab);
  }

  async _showActivityScreen(username = null, tab = null) {
    if (!authState.isLoaded()) {
      try { await authState.refreshSession(); } catch { /* non-fatal */ }
    }
    const user = authState.getCurrentUser();
    const resolved = username || user?.username || null;
    if (!resolved) return;
    const profile = !!username;
    const viewingSelf = user?.username?.toLowerCase() === resolved.toLowerCase();
    this.state = State.MENU;
    this._showScreen('activity');
    // Highlight "Friends" nav when on the friends activity tab
    if (tab === 'friends') {
      document.querySelectorAll('.site-nav-link[data-page]').forEach(el => {
        el.classList.toggle('active', el.dataset.page === 'friends');
      });
    }
    const { renderActivityScreen } = await import('./activity.js');
    renderActivityScreen(document.getElementById('screen-activity'), {
      username: resolved,
      displayUsername: viewingSelf ? (user?.displayUsername || resolved) : resolved,
      userImage: viewingSelf ? (user?.image || null) : null,
      tab: profile ? 'mine' : (tab || 'all'),
      profile,
      onTabChange: profile ? null : (newTab) => this._navigateToActivity(null, newTab),
    });
  }

  _navigateToGallery(username) {
    return screenRouter.navigateToGallery(this, username);
  }

  async _showGalleryScreen(username, opts = {}) {
    if (!username) return;
    this.state = State.MENU;
    this._showScreen('gallery');
    const { renderGalleryScreen } = await import('./gallery.js');
    renderGalleryScreen(document.getElementById('screen-gallery'), {
      username,
      app: this,
      defaultFilter: opts.defaultFilter || null,
    });
  }

  _navigateToReplays() {
    return screenRouter.navigateToReplays(this);
  }

  async _showReplaysScreen() {
    this.state = State.MENU;
    // Replay detail routes render the browse screen as their backdrop. Count
    // the actual detail impression, not both detail and browse.
    const pageViewName = /^\/replay\/[0-9a-f-]+\/?$/i.test(window.location.pathname)
      ? 'replay-detail'
      : 'replays';
    this._showScreen('replays', pageViewName);
    const { renderReplaysBrowse } = await import('./modules/replays-browse.js');
    renderReplaysBrowse(document.getElementById('screen-replays'), { app: this });
  }

  _loadMenuData() {
    void this._serviceWorkerFreshness?.check();
    const gen = this._menuDataGen = (this._menuDataGen || 0) + 1;
    const useHomeV2 = this._homeV2Enabled();
    document.documentElement.dataset.homepageRender = useHomeV2 ? 'v2' : 'legacy';

    // Homepage v2: the panel host owns all
    // menu data loading — none of the v1 card loaders below may run, or they
    // would fetch into the hidden v1 DOM on every system switch.
    if (useHomeV2) {
      return this._mountHomeV2(gen).catch((error) =>
        this._activateLegacyHomeFallback(gen, error));
    }

    this._homeFallbackActive = false;
    return this._loadLegacyMenuData(gen);
  }

  _loadLegacyMenuData(gen) {
    const romListReady = this._loadRomLibrary(gen);
    this._loadMyRomsList();
    this._loadSavesList();
    this._loadAchievementsCard();

    const cheevosReady = this.cheevosEngine.loadSystem(this.system.id);

    Promise.all([romListReady, cheevosReady])
      .then(() => {
        if (gen !== this._menuDataGen) return;
        this._loadAchievementsCard();
        this._stampRomBadges();
      })
      .catch(() => {});

    // Discover-replays teaser (below the library): a row of recent public
    // replays, gated on the replay_browse flag. The renderer hides the section
    // on empty/error; the generation guard skips a stale render after a rapid
    // system switch. Rendered after the library load so it appears below it.
    if (moduleRegistry.isEnabled('replay_browse')) {
      romListReady.then(() => {
        if (gen !== this._menuDataGen) return;
        const el = document.getElementById('discover-replays');
        if (el) {
          import('./modules/replays-browse.js')
            .then(({ renderDiscoverRow }) =>
              renderDiscoverRow(el, this, { limit: 8, isCurrent: () => gen === this._menuDataGen }))
            .catch(() => {});
        }
      });
    }

    this._loadActivityCard();
    this._loadActiveGamesCard();
    this._loadTrendingCard();
    this._loadTopPicksCard();
    this._loadTopGamesCard();
    this._loadLeaderboardsTeaserCard();
    this._loadUpNextCard();
    this._loadListsCard();
    this._loadFriendsColumn();
    this._loadSuggestedCard();
    return romListReady;
  }

  _activateLegacyHomeFallback(gen = this._menuDataGen, error = null) {
    if (this.state !== State.MENU) return;
    if (this._activeScreenName != null && this._activeScreenName !== 'menu') return;
    if (!Number.isInteger(gen)) {
      gen = this._menuDataGen = (this._menuDataGen || 0) + 1;
    }
    if (gen !== this._menuDataGen || this._homeFallbackGen === gen) return;
    this._homeFallbackGen = gen;
    this._homeFallbackActive = true;

    if (error) console.warn('[home] v2 mount failed; loading the legacy fallback', error);
    try { this._homeHost?.unmount?.(); } catch { /* best-effort partial mount cleanup */ }
    this._homeHost = null;
    this._homeCtx = null;
    document.documentElement.setAttribute('data-home-fallback', '');
    document.documentElement.classList.remove('app-loading');
    document.getElementById('home-panels')?.classList.add('hidden');
    document.querySelector('#screen-menu .menu-container')?.classList.remove('hidden');
    document.getElementById('discover-replays')?.classList.remove('hidden');
    this._homeRevealReady?.();
    this._homeRevealReady = null;
    return this._loadLegacyMenuData(gen);
  }

  /**
   * Reconcile homepage save views after SaveSync has committed an upload.
   * During gameplay the mounted v2 host is only invalidated; _loadMenuData()
   * consumes the fresh resource on return. If the async upload lands after the
   * user already returned home, reload both save-aware homepage surfaces.
   */
  _onSaveSyncUpload() {
    this._homeHost?.invalidateResource?.('saves');
    const homeVisible = this.state === State.MENU
      && (this._activeScreenName == null || this._activeScreenName === 'menu');
    if (!homeVisible) return;
    if (this._homeV2Enabled() && !this._homeFallbackActive) {
      this._homeHost?.reloadPanel?.('saves');
      this._homeHost?.reloadPanel?.('hero');
    } else {
      this._loadSavesList();
    }
  }

  /**
   * Reconcile the homepage once POST /api/sessions/end has
   * committed (user_game_stats.last_played_at: the recents order, and the
   * has_history that picks Start Here vs Jump Back In). The exit path returns
   * home without waiting on this network write, so a commit that lands after
   * the home is visible reloads the hero and activity itself.
   */
  _onPlaySessionRecorded() {
    this._homeHost?.invalidateResource?.('recent-games');
    this._homeHost?.invalidateResource?.('featured');
    this._homeHost?.invalidateResource?.('activity');
    this._homeHost?.invalidateResource?.('compete');
    const homeVisible = this.state === State.MENU
      && (this._activeScreenName == null || this._activeScreenName === 'menu');
    if (!homeVisible) return;
    if (this._homeV2Enabled() && !this._homeFallbackActive) {
      this._homeHost?.reloadPanel?.('hero');
      this._homeHost?.reloadPanel?.('activity');
      this._homeHost?.reloadPanel?.('compete', { refresh: true });
    } else {
      this._loadRecentlyPlayedList();
      this._loadActivityCard();
      this._loadLeaderboardsTeaserCard({ refresh: true, preserveTab: true });
    }
  }

  /**
   * A session-end whose POST failed is queued and replayed later by the outbox
   * (boot / back online / tab visible). Its delivery stamps user_game_stats
   * exactly like the direct send, so it reconciles the homepage the same way —
   * otherwise a hero already re-fetched on the return home would stay stale
   * until the next navigation. Returns the unsubscribe (tests).
   */
  _wireSessionEndOutbox() {
    const onDelivered = () => this._onPlaySessionRecorded();
    document.addEventListener(SESSION_END_DELIVERED_EVENT, onDelivered);
    // Spec 7.9: queued session ends only for their own account and store.
    sessionEndOutbox.setScoreOwner(() => {
      const user = authState.getCurrentUser();
      return user ? { userId: user.id, store: this.cheevosStore } : null;
    });
    return () => {
      document.removeEventListener(SESSION_END_DELIVERED_EVENT, onDelivered);
      sessionEndOutbox.setScoreOwner(null);
    };
  }

  /**
   * Gate for the homepage v2 panel host. V2 is the launched default, including
   * before the feature payload arrives or when it is temporarily unavailable.
   * A loaded, explicit homepage_v2=false remains the operator kill switch;
   * the user's server-mirrored `legacy` preference always wins.
   */
  /**
   * Re-pick the homepage layout in place after the mirror changed (the
   * boot reconcile or the Settings row). Off the menu this is a no-op —
   * the next menu render reads the fresh mirror through the gate.
   */
  _applyHomepageLayoutChange() {
    if (this.state !== State.MENU) return;
    // null means the implicit boot menu — no _showScreen has run yet
    // (same convention as the host's refresh-pause seeding, PR #206).
    if (this._activeScreenName != null && this._activeScreenName !== 'menu') return;
    if (!this._homeV2Enabled() && this._homeHost) {
      // v2 → legacy: tear the host down and restore the v1 chrome before
      // the v1 loaders repopulate it.
      this._homeHost.unmount();
      this._homeHost = null;
      this._homeCtx = null;
      document.getElementById('home-panels')?.classList.add('hidden');
      document.querySelector('#screen-menu .menu-container')?.classList.remove('hidden');
      document.getElementById('discover-replays')?.classList.remove('hidden');
    } else if (this._homeV2Enabled()) {
      // legacy → default (or a v2 refresh): drop any stale boot give-up marker
      // so the CSS guard hides the legacy shell while the async v2 host mounts,
      // rather than leaving v1 visible until the mount lands.
      document.documentElement.removeAttribute('data-home-fallback');
    }
    this._loadMenuData();
  }

  _homeV2Enabled() {
    try {
      if (localStorage.getItem('retroplay:homepage-layout') === 'legacy') return false;
    } catch { /* storage blocked → default layout */ }
    return moduleRegistry.isEnabled('homepage_v2');
  }

  _syncHomeCreatorLink() {
    this._homeHost?.setAccess?.({
      serverFeatures: moduleRegistry.getServerFeatures(),
      authenticated: authState.isAuthenticated(),
    });
  }

  /**
   * Final boot barrier used by auth-ui before it reveals the document. The
   * identity/settings probe settles first, then flags, then the selected home
   * host. This prevents the static legacy markup from becoming visible while
   * v2's dynamic modules are still loading.
   */
  async _prepareHomepageForReveal() {
    await moduleRegistry.whenLoaded();
    if (this.state !== State.MENU) return;
    if (!this._homeV2Enabled()) {
      this._loadMenuData();
      return;
    }

    // Reveal as soon as the v2 shell + skeletons exist; panel data continues
    // loading behind them. Waiting for every homepage API would trade the v1
    // flash for an unnecessarily long blank screen on a slow connection.
    let markShellReady;
    const shellReady = new Promise((resolve) => { markShellReady = resolve; });
    this._homeRevealReady = markShellReady;
    await Promise.race([shellReady, this._loadMenuData()]);
    if (this._homeRevealReady === markShellReady) this._homeRevealReady = null;
  }

  /**
   * Mount/refresh the homepage v2 panel host. The home modules load via
   * dynamic import so the eager bundle is untouched while the flag is dark.
   * The host is recreated when the signed-in identity changes, because
   * authOnly panels resolve at mount time.
   *
   * Single-flight with a trailing rerun: boot fires _loadMenuData twice in
   * quick succession (flags-loaded, then the auth subscriber), and letting
   * both interleave through the import/mount/load pipeline could strand an
   * aborted first fetch without its replacement (PR #203 journey flake).
   * Same dirty-chain shape as queue-state.js.
   */
  async _mountHomeV2() {
    // The chain rechecks _homeV2Enabled() on EVERY iteration: the layout
    // preference can flip to legacy while a mount is in flight (Settings
    // 0102 PATCH), and the queued rerun re-reads the CURRENT _menuDataGen —
    // so Inner's gen guards pass and, ungated, the rerun would recreate v2
    // over the legacy chrome (P1: home-your-shelves legacy round-trip).
    this._homeMountChain ??= createSingleFlightChain({
      gate: () => this._homeV2Enabled(),
      run: () => this._mountHomeV2Inner(this._menuDataGen),
    });
    return this._homeMountChain.request();
  }

  async _mountHomeV2Inner(gen) {
    // bindAuth's subscription fires once immediately with the unresolved
    // `null` identity. Mounting from that callback builds an anonymous host
    // which is torn down again as soon as the session probe publishes the
    // real user. Besides doing every panel fetch twice, that replacement can
    // detach a section while a fast user is scrolling or tabbing through it.
    // The auth subscriber calls _loadMenuData again when the initial probe
    // settles (for both signed-in and anonymous visitors), so leave the
    // app-loading shell untouched until the identity is authoritative.
    if (!authState.isLoaded()) return;
    const uid = authState.getCurrentUser()?.id || null;
    if (this._homeHost && this._homeHostUser !== uid) {
      this._homeHost.unmount();
      this._homeHost = null;
      this._homeCtx = null;
    }
    if (!this._homeHost) {
      this._homeHostUser = uid;
      const [{ createHomeHost }, { createHomeContext }, { HOME_PANELS }, hiddenSections] = await Promise.all([
        import('./home/host.js'),
        import('./home/context.js'),
        import('./home/registry.js'),
        import('./home/hidden-sections.js'),
      ]);
      if (gen !== this._menuDataGen) return;
      this._homeCtx = createHomeContext({
        systemId: this.system.id,
        systemLabel: this.system.name || this.system.id,
        user: authState.getCurrentUser(),
        features: moduleRegistry.getFeatures(),
        serverFeatures: moduleRegistry.getServerFeatures(),
        authenticated: authState.isAuthenticated(),
        app: this,
        bootstrap: true,
      });
      this._homeHost = createHomeHost({ panels: HOME_PANELS, ctx: this._homeCtx });
      // Hidden sections (spec Hd1, Hd9): the host calls this factory at every
      // mount() for that visit's controller and destroys it at unmount(). Only a
      // user id the X-RetroPlay-User header and the device mirror can carry gets one.
      const homeCtx = this._homeCtx;
      this._homeHiddenSections = uid && hiddenSections.USER_ID_RE.test(uid)
        ? (events) => hiddenSections.createHiddenSectionsController({
          ...events, ctx: homeCtx, userId: uid, refreshSession: () => authState.refreshSession(),
        })
        : null;
      // Late-bind the host onto ctx now that both exist — panels reach
      // per-panel pause/reload through ctx.host (Task 12/15).
      this._homeCtx.host = this._homeHost;
      // A remount can happen BEHIND another screen (the auth-identity
      // subscriber fires while game-state is MENU on Rooms/Friends/etc.),
      // and no _showScreen follows to pause the fresh host — it must
      // inherit the current surface's pause state (PR #206 review).
      this._homeHost.setRefreshPaused(
        this._replayViewerHomeRefreshPaused
          || (this._activeScreenName != null && this._activeScreenName !== 'menu'));
    }
    if (gen !== this._menuDataGen) return;
    const mountEl = document.getElementById('home-panels');
    if (!mountEl) return;
    if (!mountEl.childElementCount) {
      this._homeHost.mount(mountEl, { hiddenSections: this._homeHiddenSections });
    }
    document.querySelector('#screen-menu .menu-container')?.classList.add('hidden');
    document.getElementById('discover-replays')?.classList.add('hidden');
    mountEl.classList.remove('hidden');
    // v2 is up: cancel the boot give-up failsafe (index.html __homeFallbackTimer)
    // so it can never drop to the legacy shell behind a working homepage, and
    // clear any fallback it already set (a slow mount that beat the timer).
    document.documentElement.dataset.homeReady = '1';
    document.documentElement.removeAttribute('data-home-fallback');
    this._homeFallbackActive = false;
    this._homeFallbackGen = null;
    clearTimeout(window.__homeFallbackTimer);
    this._homeRevealReady?.();
    this._homeRevealReady = null;
    if (this._homeCtx.systemId !== this.system.id) {
      await this._homeHost.setSystem(this.system.id, this.system.name || this.system.id);
    } else {
      await this._homeHost.load();
    }
    // The first panel wave has settled (data, empty or error). What's New
    // (news-modal.js) waits for this latch so it opens over painted panels,
    // not over the boot skeletons. It stays set for the page's lifetime.
    document.documentElement.dataset.homePanelsRevealed = '1';
  }

  /** Pop back to the menu from the stats or settings screen. */
  _navigateToMenu() {
    return screenRouter.navigateToMenu(this);
  }

  /** Handle popstate — browser back/forward between /, /u, /u/:username, /settings. */
  _routeFromLocation() {
    return screenRouter.routeFromLocation(this);
  }

  // ── Top 100 Screen ───────────────────────────────────────

  _navigateToTop100(systemKey = null) {
    return screenRouter.navigateToTop100(this, systemKey);
  }

  async _showTop100Screen(systemKey = null) {
    this.state = State.MENU;
    const { showTop100Screen } = await import('./modules/top100.js');
    showTop100Screen(this, systemKey);
  }

  // ── Leaderboards Screen ──────────────────────────────────

  _navigateToLeaderboards(gameId = null, tab = null, highlight = null) {
    return screenRouter.navigateToLeaderboards(this, gameId, tab, highlight);
  }

  async _showLeaderboardsScreen(gameId = null, tab = null, highlight = null) {
    this.state = State.MENU;
    const { showLeaderboardsScreen } = await import('./modules/leaderboards.js');
    showLeaderboardsScreen(this, gameId, tab, highlight);
  }

  /**
   * Render the stats screen.
   * @param {string|null} username — if set, show that user's public profile;
   *   otherwise show own profile (API when signed in, localStorage fallback).
   */
  async _showStatsScreen(username = null) {
    this.state = State.MENU;
    const { showStatsScreen } = await import('./modules/stats.js');
    showStatsScreen(this, username);
  }

  // ── Settings ─────────────────────────────────────────────────────────

  _navigateToSettings() {
    return screenRouter.navigateToSettings(this);
  }

  _navigateToContact() {
    return screenRouter.navigateToContact(this);
  }

  _navigateToPrivacy() {
    return screenRouter.navigateToPrivacy(this);
  }

  async _showPrivacyScreenAsync() {
    this.state = State.MENU;
    const { showPrivacyScreen } = await import('./modules/privacy.js');
    showPrivacyScreen(this);
  }

  _showPrivacyScreen() {
    this._showPrivacyScreenAsync();
  }

  _navigateToTos() {
    return screenRouter.navigateToTos(this);
  }

  async _showTosScreenAsync() {
    this.state = State.MENU;
    const { showTosScreen } = await import('./modules/tos.js');
    showTosScreen(this);
  }

  _showTosScreen() {
    this._showTosScreenAsync();
  }

  _navigateToCommunityStandards() {
    return screenRouter.navigateToCommunityStandards(this);
  }

  async _showCommunityStandardsScreenAsync() {
    this.state = State.MENU;
    const { showCommunityStandardsScreen } = await import('./modules/community-standards.js');
    showCommunityStandardsScreen(this);
  }

  _showCommunityStandardsScreen() {
    this._showCommunityStandardsScreenAsync();
  }

  _navigateToAcknowledgements() {
    return screenRouter.navigateToAcknowledgements(this);
  }

  async _showAcknowledgementsScreenAsync() {
    this.state = State.MENU;
    const { showAcknowledgementsScreen } = await import('./modules/acknowledgements.js');
    showAcknowledgementsScreen(this);
  }

  _showAcknowledgementsScreen() {
    this._showAcknowledgementsScreenAsync();
  }

  async _showContactScreen() {
    const { initFeedbackPanel, openFeedbackPanel } = await import('./modules/feedback-panel.js');
    initFeedbackPanel();
    openFeedbackPanel();
  }

  // ── Rooms screen ──────────────────────────────────────────────────

  _navigateToRooms() {
    return screenRouter.navigateToRooms(this);
  }

  async _showRoomsScreen() {
    this.state = State.MENU;
    const { showRoomsScreen } = await import('./modules/rooms.js');
    showRoomsScreen(this);
  }

  // ── Controllers screen ────────────────────────────────────────────

  _navigateToControllers() {
    return screenRouter.navigateToControllers(this);
  }

  async _showControllersScreen() {
    this.state = State.MENU;
    // All-user screen; the module itself renders "coming soon" when the
    // `controller_profiles` flag is OFF (route resolves regardless of flag).
    const { showControllersScreen } = await import('./modules/controllers.js');
    showControllersScreen(this);
  }

  // ── Friends screen ────────────────────────────────────────────────
  async _showFriendsScreen() {
    if (new URLSearchParams(window.location.search).has('invite')) cancelPlayIntent('canceled');
    // Nav-token guard: capture the URL at ENTRY — before ANY await in this
    // function, including the refreshSession() await immediately below. A
    // user can navigate elsewhere (e.g. to /rooms) while that request is
    // still in flight; re-reading the URL fresh only AFTER it resolves
    // would silently miss this — the invite param is gone from the NEW
    // url, so inviteId reads null and every later guard (which only runs
    // `if (inviteId)`) gets skipped entirely, falling through to show
    // Friends unconditionally under whatever screen the user actually
    // navigated to. Re-checked against this SAME token after every await
    // below.
    const entryUrl = window.location.pathname + window.location.search;

    // Race guard: direct navigation to /friends (page load, browser
    // back/forward, share-link, bookmark) fires _checkJoinLink() before
    // the boot-time session probe resolves. Reading getCurrentUser()
    // synchronously here returns null even for signed-in users whose
    // cookie is perfectly valid, then bounces them to sign-in — very
    // confusing. Mirror the _startSolo pattern: await isLoaded() on
    // cold navigation, then decide.
    if (!authState.isLoaded()) {
      try { await authState.refreshSession(); } catch { /* non-fatal */ }
      if (window.location.pathname + window.location.search !== entryUrl) return;
    }
    // isAuthenticated(), not a bare getCurrentUser() truthiness check: a
    // half-authenticated { user, session: null } (verification-pending
    // sign-up) must not see the authenticated friends screen either.
    if (!authState.isAuthenticated()) {
      const inviteParam = new URLSearchParams(window.location.search).get('invite');
      if (inviteParam) {
        // Task 18: signed-out (or half-authenticated) deep link into a
        // specific invite. Retain the URL — do NOT _navigateToMenu(), which
        // pushes '/' — so the auth callback (or the in-tab
        // _routeFromLocation() re-run once signed in) lands back on
        // /friends?invite=<id>. Show the menu behind the modal so the app
        // isn't blank while the user signs in.
        this._showScreen('menu');
        openAuthModal('signin', { returnTo: window.location.pathname + window.location.search });
        return;
      }
      this._flash('Sign in to see friends');
      openAuthModal('signin');
      this._navigateToMenu();
      return;
    }

    // Scheduled Invite to Play (Task 17): /friends?invite=<id> deep-links to
    // a specific pending invite. moduleRegistry.isEnabled() is fail-OPEN
    // (true before flags load) and boot fires moduleRegistry.load()
    // unawaited, so reading it here directly could run the invite flow (or
    // show its stale-link banner) even when play_invites_scheduled is truly
    // off, purely because flags hadn't arrived yet on a cold navigation.
    // whenLoaded() + this strict `=== true` read (never isEnabled) make the
    // gate fail-CLOSED: only once flags are actually known AND the flag
    // reads true does the param survive; otherwise it's silently dropped
    // and this renders as a normal /friends visit.
    const inviteId = new URLSearchParams(window.location.search).get('invite');
    let highlightInvite = null;
    if (inviteId) {
      // Stale-continuation guard: this fires fire-and-forget from
      // _routeFromLocation, and moduleRegistry.whenLoaded() can take a
      // while on a cold load. If the user navigates away from
      // /friends?invite=… before flags resolve (or during the EARLIER
      // refreshSession await above), this continuation must not resume and
      // show Friends (or run the invite lookup) out from under whatever
      // screen they navigated to instead. Checked against the SAME entry
      // token captured at the top of this function.
      await moduleRegistry.whenLoaded();
      if (window.location.pathname + window.location.search !== entryUrl) return;
      if (moduleRegistry.getFeatures().play_invites_scheduled === true) {
        highlightInvite = inviteId;
      }
    }

    this.state = State.MENU;
    this._showScreen('friends');
    renderFriendsScreen(
      document.getElementById('screen-friends'),
      highlightInvite ? { initialTab: 'pending', highlightInvite } : {},
    );
    document.querySelector('#screen-friends .card-view-all[href="/friends/activity"]')?.addEventListener('click', (e) => {
      e.preventDefault();
      this._navigateToActivity(null, 'friends');
    });
  }

  _navigateToFriends() {
    return screenRouter.navigateToFriends(this);
  }

  // ── Invite-accept landing page ─────────────────────────────────────
  // Called on /invite/:token. Previews the invite, then Accept → POSTs
  // to the accept endpoint and routes the user to their new friend's
  // profile.
  async _showInviteAcceptScreen(token) {
    cancelPlayIntent('canceled');
    this.state = State.MENU;
    this._showScreen('invite-accept');
    const body = document.getElementById('invite-accept-body');
    if (!body) return;
    body.innerHTML = '<p class="invite-accept-message">Loading invite…</p>';
    try {
      const res = await fetch(`/api/friend-invites/${encodeURIComponent(token)}`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('invalid');
      const data = await res.json();
      if (!data.valid) {
        body.innerHTML = '<p class="invite-accept-message">This invite is no longer valid (expired, revoked, or fully used).</p>' +
          '<button id="invite-accept-home" class="friend-action friend-action-secondary">Back to home</button>';
        document.getElementById('invite-accept-home').onclick = () => this._navigateToMenu();
        return;
      }
      const owner = data.owner || {};
      body.innerHTML = '';
      const avatar = document.createElement('img');
      avatar.src = owner.image || '/favicon.svg';
      avatar.alt = '';
      body.appendChild(avatar);
      const p = document.createElement('p');
      p.className = 'invite-accept-message';
      p.textContent = `@${owner.displayUsername || owner.username || '???'} invited you to be friends on RetroPlay.`;
      body.appendChild(p);

      const user = authState.getCurrentUser();
      if (!user) {
        const info = document.createElement('p');
        info.className = 'invite-accept-message';
        info.textContent = 'Sign in to accept.';
        body.appendChild(info);
        const signIn = document.createElement('button');
        signIn.className = 'friend-action friend-action-primary';
        signIn.textContent = 'Sign In';
        signIn.onclick = () => openAuthModal('signin');
        body.appendChild(signIn);
        return;
      }

      const accept = document.createElement('button');
      accept.className = 'friend-action friend-action-primary';
      accept.textContent = '+ Accept';
      accept.onclick = async () => {
        accept.disabled = true;
        try {
          const r = await fetch(`/api/friend-invites/${encodeURIComponent(token)}/accept`, {
            method: 'POST',
            credentials: 'same-origin',
          });
          if (!r.ok) throw Object.assign(new Error('fail'), { status: r.status });
          this._flash(`You're now friends with @${owner.displayUsername || owner.username}`);
          const roomParam = new URLSearchParams(window.location.search).get('room');
          if (roomParam) {
            try {
              const roomRes = await fetch(`/api/rooms/${encodeURIComponent(roomParam)}`);
              if (roomRes.ok) {
                this._pushRoomHistory(roomParam);
                this._joinRoom(roomParam);
                return;
              }
            } catch { /* room gone — fall through */ }
            this._flash('Room is no longer available');
            this._navigateToMenu();
          } else if (owner.username) {
            this._navigateToStats(owner.username);
          } else {
            this._navigateToFriends();
          }
        } catch (err) {
          accept.disabled = false;
          this._flash(err?.status === 410 ? 'Invite no longer valid' : 'Could not accept invite');
        }
      };
      body.appendChild(accept);

      const decline = document.createElement('button');
      decline.className = 'friend-action friend-action-secondary';
      decline.textContent = 'No thanks';
      decline.onclick = () => this._navigateToMenu();
      body.appendChild(decline);
    } catch {
      body.innerHTML = '<p class="invite-accept-message">This invite could not be loaded.</p>';
    }
  }

  async _showSettingsScreen() {
    this.state = State.MENU;
    const { showSettingsScreen } = await import('./modules/settings.js');
    showSettingsScreen(this);
  }

  // Both tour starters hold until the ToS gate (if any) is accepted — the
  // gate paints below Shepherd's overlay, so a tour firing alongside it
  // covers the terms the user is required to act on. The state re-check
  // inside the callback matters: acceptance can land minutes later.
  _schedulePendingHomepageTour(delay = 1200) {
    let pending = false;
    try { pending = !!sessionStorage.getItem('retroplay:signup-pending-tour'); } catch { /* ignore unavailable storage */ }
    if (!pending) return;
    if (this._pendingHomepageTourTimer) return;
    this._pendingHomepageTourTimer = setTimeout(() => {
      this._pendingHomepageTourTimer = null;
      let stillPending = false;
      try { stillPending = !!sessionStorage.getItem('retroplay:signup-pending-tour'); } catch { /* ignore unavailable storage */ }
      const menu = document.getElementById('screen-menu');
      const homepageVisible = this.state === State.MENU && menu && !menu.classList.contains('hidden');
      if (stillPending && authState.isAuthenticated() && homepageVisible) {
        window.dispatchEvent(new CustomEvent('retroplay:start-tour'));
      }
    }, delay);
  }

  async _startTour() {
    const releasePendingTour = () => {
      try { sessionStorage.removeItem('retroplay:signup-pending-tour'); } catch { /* ignore unavailable storage */ }
      document.body.classList.remove('homepage-tour-active');
      window.dispatchEvent(new CustomEvent('retroplay:homepage-tour-ended'));
    };
    if (this._homepageTourStartPending || document.body.classList.contains('homepage-tour-active')) return;
    const menu = document.getElementById('screen-menu');
    if (this.state !== State.MENU || !menu || menu.classList.contains('hidden')) return;
    // Claim startup before the legal-gate wait or dynamic import. Both can
    // yield long enough for navigation and signup timers to request the tour
    // again; only one deferred callback may ever construct Shepherd.
    this._homepageTourStartPending = true;
    whenTosClear(async () => {
      const currentMenu = document.getElementById('screen-menu');
      if (this.state !== State.MENU || !currentMenu || currentMenu.classList.contains('hidden')) {
        this._homepageTourStartPending = false;
        return;
      }
      try {
        const { startHomepageTour } = await import('./modules/tour.js');
        await startHomepageTour(this);
      } catch (err) {
        let signupTourPending = false;
        try { signupTourPending = !!sessionStorage.getItem('retroplay:signup-pending-tour'); } catch { /* ignore unavailable storage */ }
        // startHomepageTour owns cleanup once it has claimed priority. This
        // fallback covers import/setup failures that happen before it can.
        if (signupTourPending || document.body.classList.contains('homepage-tour-active')) {
          releasePendingTour();
        }
        console.error('Homepage tour failed to start', err);
      } finally {
        this._homepageTourStartPending = false;
      }
    });
  }

  async _startGameplayTour() {
    if (this.state !== State.SOLO) return;
    whenTosClear(async () => {
      if (this.state !== State.SOLO) return;
      const { startGameplayTour } = await import('./modules/gameplay-tour.js');
      startGameplayTour(this);
    });
  }

  // ── Watch (spectate) a user's public active room ────────────────────

  async _joinWatchRoom(username, existingLaunchOwner = null) {
    cancelPlayIntent('canceled');
    const launchOwner = existingLaunchOwner
      ?? this._beginGameLaunchIntent({ clearPendingSave: true });
    const launchIsCurrent = () => this._isGameLaunchOwnerCurrent(launchOwner);
    if (!launchIsCurrent()) return;
    try {
      const res = await fetch(`/api/rooms/by-user/${encodeURIComponent(username)}`);
      if (!launchIsCurrent()) return;
      if (!res.ok) {
        this._flash(`${username} is not playing right now`);
        this._navigateToMenu();
        return;
      }
      const data = await res.json();
      if (!launchIsCurrent()) return;
      if (!data.roomPublic) {
        this._flash('This room is private');
        this._navigateToMenu();
        return;
      }
      // Join as spectator via the existing room join flow
      return await this._joinRoom(data.code, launchOwner, { spectator: true });
    } catch {
      if (!launchIsCurrent()) return;
      this._flash('Could not find room');
      this._navigateToMenu();
    }
  }

  // ── Activity card ───────────────────────────────────────────────

  async _loadActivityCard() {
    return menuCards.loadActivityCard(this);
  }

  _formatRelativeShort(ts) {
    return menuCards.formatRelativeShort(this, ts);
  }

  // ── Friends column (homepage sidebar) ──────────────────────────
  _loadFriendsColumn() {
    return menuCards.loadFriendsColumn(this);
  }

  _loadSuggestedCard() {
    return menuCards.loadSuggestedCard(this);
  }

  // ── Rooms card (homepage widget) ────────────────────────────────

  async _loadActiveGamesCard() {
    return menuCards.loadActiveGamesCard(this);
  }

  _systemEmoji(sys) {
    return menuCards.systemEmoji(this, sys);
  }

  // ── TRENDING GAMES card ────────────────────────────────────────────

  _systemTabList() {
    return menuCards.systemTabList(this);
  }

  async _loadTrendingCard() {
    return menuCards.loadTrendingCard(this);
  }

  async _renderTrendingGames(system, list) {
    return menuCards.renderTrendingGames(this, system, list);
  }

  _renderTrendingList(games, list, system) {
    return menuCards.renderTrendingList(this, games, list, system);
  }

  // ── TOP 10 PICKS card ─────────────────────────────────────────────

  _loadTopPicksCard() {
    return menuCards.loadTopPicksCard(this);
  }

  _renderTopPicksList(key, list) {
    return menuCards.renderTopPicksList(this, key, list);
  }

  // ── TOP GAMES card ────────────────────────────────────────────────

  _topGamesCache = {};

  async _loadTopGamesCard() {
    return menuCards.loadTopGamesCard(this);
  }

  async _renderTopGames(system, list) {
    return menuCards.renderTopGames(this, system, list);
  }

  _renderTopGamesList(games, list, system) {
    return menuCards.renderTopGamesList(this, games, list, system);
  }

  async _launchTopGame(system, game) {
    return menuCards.launchTopGame(this, system, game);
  }

  // ── TOP LEADERBOARDS teaser card ──────────────────────────────────

  async _loadLeaderboardsTeaserCard(options) {
    const m = await import('./modules/leaderboards-teaser.js');
    return m.loadLeaderboardsTeaserCard(this, options);
  }

  async _renderLeaderboardTeaser(mode, list, empty, options) {
    const m = await import('./modules/leaderboards-teaser.js');
    return m.renderLeaderboardTeaser(this, mode, list, empty, options);
  }

  // ── UP NEXT card (head of My Queue) ─────────────────────────────────
  // Shows the top 3 games of the signed-in user's queue with one-click
  // Play. The GET lazily creates the queue server-side on first visit.

  async _loadUpNextCard() {
    return menuCards.loadUpNextCard(this);
  }

  _buildUpNextEntry(item) {
    return menuCards.buildUpNextEntry(this, item);
  }

  async _launchQueueGame(item) {
    return menuCards.launchQueueGame(this, item);
  }

  // ── LISTS card (My Lists / Top Lists tabs) ─────────────────────────

  async _loadListsCard() {
    return menuCards.loadListsCard(this);
  }

  async _renderMyLists(list, card, prefetched) {
    return menuCards.renderMyLists(this, list, card, prefetched);
  }

  async _renderTopLists(list, card) {
    return menuCards.renderTopLists(this, list, card);
  }

  /**
   * Format a duration in SECONDS as "Xh Ym" or "Ym". Matches the API
   * convention post-0016 migration (play_time_seconds, duration_seconds).
   * Callers with a millisecond-native source (e.g. the local
   * achievement store's playMs) must divide by 1000 before calling.
   */
  _formatDuration(seconds) {
    return menuCards.formatDuration(this, seconds);
  }

  // ── Room listing thumbnail capture ────────────────────────────────
  // The host periodically ships a small JPEG of its game canvas; the server keeps
  // it as room.screenshot for the live preview on the home / rooms pages. Runs for
  // EVERY play mode (solo, hosted, lockstep, link-cable, side-by-side, async) —
  // host only, so a guest/spectator never overwrites the room's preview.

  /** Whether this client is the room's host (the sole screenshot sender). Solo /
   *  async have no guest (playerNumber unset → treated as host); in 2-player modes
   *  P1 is the host and spectators never qualify. The server also enforces host via
   *  room.hostUserId, so this is the bandwidth-saver, not the security boundary. */
  _amRoomHost() {
    if (this.net?.spectator) return false;
    const pn = this.net?.playerNumber;
    return pn == null || pn === 1;
  }

  _startRoomScreenshots() {
    this._stopRoomScreenshots();
    const tmp = document.createElement('canvas');
    const scale = Math.max(1, Math.ceil(320 / (this.system?.width || 256)));
    tmp.width = (this.system?.width || 256) * scale;
    tmp.height = (this.system?.height || 240) * scale;
    const ctx = tmp.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    this._soloScreenshotCanvas = tmp;
    const owner = this._roomPreviewOwner = {};
    let pending = false;
    this._soloScreenshotTimer = setInterval(() => {
      if (pending || this._roomPreviewOwner !== owner || !this.net?.connected || !this._amRoomHost()) return;
      try {
        const capture = resolvePresentationCapture(this, 'room-preview');
        if (capture.kind === 'blocked') return;
        const source = capture.kind === 'public' ? capture.lease.canvas : capture.canvas;
        if (!source) return;
        const net = this.net, connection = { net, socket: net.ws, generation: net._socketGeneration, roomCode: net.roomCode };
        const key = roomLaunchSessionKey(this);
        const current = () => this._roomPreviewOwner === owner && net.connected
          && publicCaptureCurrent(this, capture, 'room-preview', connection, key);
        if (!current()) return;
        ctx.drawImage(source, 0, 0, tmp.width, tmp.height);
        if (!current()) return;
        pending = true;
        tmp.toBlob((blob) => {
          if (!blob || !current()) { pending = false; return; }
          const reader = new FileReader();
          if (!current()) { pending = false; return; }
          reader.onload = () => {
            try {
              if (!current()) return;
              const base64 = reader.result.split(',')[1];
              if (base64 && current()) net.sendRaw(JSON.stringify({ type: 'screenshot_frame', data: base64 }));
            } finally { pending = false; }
          };
          reader.onerror = reader.onabort = () => { pending = false; };
          reader.readAsDataURL(blob);
        }, 'image/jpeg', 0.6);
      } catch { pending = false; }
    }, 2000);
  }

  _stopRoomScreenshots() {
    this._roomPreviewOwner = null;
    if (this._soloScreenshotTimer) {
      clearInterval(this._soloScreenshotTimer);
      this._soloScreenshotTimer = null;
    }
    this._soloScreenshotCanvas = null;
  }

  _getCanvasStream(purpose = 'spectator') {
    const capture = resolvePresentationCapture(this, purpose);
    const previous = canvasStreamOwners.get(this);
    if (capture.kind === 'blocked') { if (previous) this._releaseCanvasStream(previous); return null; }
    if (previous) {
      const usable = previous.current();
      if (canvasStreamOwners.get(this) !== previous) return null;
      if (usable) return previous.stream;
      this._releaseCanvasStream(previous);
      if (canvasStreamOwners.has(this)) return null;
    }
    const net = this.net, connection = { net, socket: net?.ws, generation: net?._socketGeneration, roomCode: net?.roomCode };
    const key = roomLaunchSessionKey(this), before = canvasStreamOwners.get(this);
    const current = () => publicCaptureCurrent(this, capture, purpose, connection, key);
    let handle, stream, entry, released = false;
    let videos = [];
    const release = () => {
      if (released) return; released = true;
      if (handle) handle.release();
      else for (const track of videos) try { track.stop(); } catch { /* continue owned video release */ }
    };
    try {
      if (!current() || canvasStreamOwners.get(this) !== before) return null;
      if (capture.kind === 'public') { handle = capture.lease.openStream(this.system?.fps || 60); stream = handle.stream; }
      else { if (!this.canvas) return null; stream = this.canvas.captureStream(this.system?.fps || 60); videos = stream.getVideoTracks(); }
      if (!current() || canvasStreamOwners.get(this) !== before) { release(); return null; }
      entry = { stream, current, release };
      canvasStreamReturns.set(stream, entry); canvasStreamPrivateOwners.set(entry, this);
      canvasStreamOwners.set(this, entry); this._canvasStream = stream;
      this._requestCanvasStreamFrame();
      const usable = current();
      if (usable && canvasStreamOwners.get(this) === entry) return stream;
      this._releaseCanvasStream(entry); return null;
    } catch { if (entry) this._releaseCanvasStream(entry); else release(); return null; }
  }

  _requestCanvasStreamFrame(expected = canvasStreamOwners.get(this)) {
    try {
      const entry = canvasStreamOwners.get(this);
      const target = expected && (canvasStreamPrivateOwners.get(expected) === this ? expected : canvasStreamReturns.get(expected));
      if (expected && (target !== entry || canvasStreamPrivateOwners.get(target) !== this)) return;
      const stream = entry?.stream ?? this._canvasStream;
      const current = () => this._canvasStream === stream && (!entry || canvasStreamOwners.get(this) === entry
        && entry.current() && canvasStreamOwners.get(this) === entry && this._canvasStream === stream);
      if (!current()) { if (entry) this._releaseCanvasStream(entry); return; }
      const tracks = stream?.getVideoTracks?.() || [];
      for (const track of tracks) {
        if (!current()) { if (entry) this._releaseCanvasStream(entry); return; }
        const request = track.requestFrame;
        if (!current()) { if (entry) this._releaseCanvasStream(entry); return; }
        if (typeof request === 'function') request.call(track);
        if (!current()) { if (entry) this._releaseCanvasStream(entry); return; }
      }
    } catch { /* requestFrame is best-effort */ }
  }

  _releaseCanvasStream(expected = canvasStreamOwners.get(this)) {
    const entry = canvasStreamOwners.get(this);
    const target = expected && (canvasStreamPrivateOwners.get(expected) === this ? expected : canvasStreamReturns.get(expected));
    if (expected && (target !== entry || canvasStreamPrivateOwners.get(target) !== this)) {
      if (canvasStreamPrivateOwners.get(target) === this) target.release();
      return;
    }
    const stream = this._canvasStream;
    if (entry) {
      canvasStreamOwners.delete(this);
      if (this._canvasStream === entry.stream) this._canvasStream = null;
      entry.release();
    } else if (stream) {
      this._canvasStream = null;
      for (const track of stream.getVideoTracks?.() || []) try { track.stop(); } catch { /* borrowed audio stays live */ }
    }
  }

  /** Set up 1-to-many WebRTC streaming for solo-play spectators. */
  _startSoloStream() {
    // Async-turn live rooms set _soloRoomCode just before this (CH1).
    roomChat.syncRoomChatAvailability(this);
    if (!this.canvas || !this.net?.connected || !this._soloRoomCode
        || this.net.roomCode !== this._soloRoomCode) return;
    const owner = this._soloStreamOwner = { net: this.net, socketGeneration: this.net._socketGeneration };
    const ownsStream = () => this._soloStreamOwner === owner && this.net === owner.net
      && owner.net.connected && owner.net._socketGeneration === owner.socketGeneration
      && this._soloRoomCode === owner.net.roomCode && (this.state === State.SOLO || this._deferSoloSpectatorOffers);
    // The socket remains authoritative while the emulator/stream is stopped.
    // No new assignment arrives on a retained connection after a game reboot.
    if (owner.net.roomParticipants?.spectators instanceof Map) {
      this._spectators = new Map([...owner.net.roomParticipants.spectators].map(([id, row]) => [
        id, { username: row.username || null, image: row.image || null },
      ]));
      for (const ids of [this._pendingSoloSpectatorOfferIds, this._readySoloSpectatorIds, this._deferredSoloSpectatorIds]) {
        for (const id of ids || []) if (!this._spectators.has(id)) ids.delete(id);
      }
      this._updatePresenceList();
    }

    // Defer SoloStreamHost + captureStream() until a spectator actually
    // joins. This avoids engaging the GPU video-encoder pipeline when
    // nobody is watching (matches the lockstep pattern at _startLockstep).
    const initStream = () => {
      if (!ownsStream() || this._soloStream) return;
      this._soloStream = new SoloStreamHost(
        this.net,
        this._getCanvasStream(),
        this._soloStreamAudioSource(),
        this.system?.fps || 60,
      );
    };
    if (!this._pendingSoloSpectatorOfferIds) this._pendingSoloSpectatorOfferIds = new Set();
    if (!this._readySoloSpectatorIds) this._readySoloSpectatorIds = new Set();
    const scheduleOfferFallback = (spectatorId, opts = {}) => {
      setTimeout(() => {
        if (!ownsStream()) return;
        if (!this._pendingSoloSpectatorOfferIds?.has(spectatorId)) return;
        if (this._deferSoloSpectatorOffers) return;
        if (this.state !== State.SOLO || !this._spectators.has(spectatorId)) return;
        console.warn(`[solo] Spectator ${spectatorId} did not send ready; offering anyway`);
        this._offerSoloSpectator?.(spectatorId, opts);
      }, 3000);
    };
    this._offerSoloSpectator = (spectatorId, opts = {}) => {
      if (!ownsStream() || !this._spectators.has(spectatorId)) return;
      this._pendingSoloSpectatorOfferIds?.delete(spectatorId);
      initStream();
      this._requestCanvasStreamFrame();
      this._soloStream?.addSpectator(spectatorId);
      if (opts.retry) {
        setTimeout(() => {
          if (!ownsStream()) return;
          const state = this._soloStream?.getSpectatorState?.(spectatorId);
          if (this.state !== State.SOLO || !this._spectators.has(spectatorId)) return;
          if (state === 'connected' || state === 'completed') return;
          console.warn(`[solo] Retrying spectator ${spectatorId} offer after baton handoff (state=${state || 'none'})`);
          this._soloStream?.removeSpectator(spectatorId);
          this._requestCanvasStreamFrame();
          this._soloStream?.addSpectator(spectatorId);
        }, 2000);
      }
    };
    this._queueSoloSpectatorOffer = (spectatorId, opts = {}) => {
      if (!ownsStream() || !this._spectators.has(spectatorId)) return;
      this._pendingSoloSpectatorOfferIds.add(spectatorId);
      if (this._deferSoloSpectatorOffers) {
        if (!this._deferredSoloSpectatorIds) this._deferredSoloSpectatorIds = new Set();
        this._deferredSoloSpectatorIds.add(spectatorId);
        return;
      }
      if (this._readySoloSpectatorIds.has(spectatorId)) {
        this._offerSoloSpectator(spectatorId, opts);
        return;
      }
      scheduleOfferFallback(spectatorId, opts);
    };

    this.net.onSpectatorJoined = (spectatorId, username, image) => {
      if (!ownsStream()) return;
      this._playJoinPing();
      console.log(`[solo] Spectator ${spectatorId} joined, creating WebRTC stream`);
      this._spectators.set(spectatorId, { username: username || null, image: image || null });
      this._queueSoloSpectatorOffer(spectatorId);
      const count = (this._soloStream?.spectatorCount || 0) + (this._deferredSoloSpectatorIds?.size || 0) || 1;
      this._flash(`Spectator joined (${count} watching)`);
      const label = username ? `@${username}` : `Spectator ${spectatorId}`;
      this._showChatSystemMessage(`${label} has entered the chat`);
      this._updatePresenceList();
      gameActions.refreshGameToolbarAvailability(this, 'spectators');
    };

    this.net.onSpectatorLeft = (spectatorId, username) => {
      if (!ownsStream()) return;
      console.log(`[solo] Spectator ${spectatorId} left`);
      this._spectators.delete(spectatorId);
      this._deferredSoloSpectatorIds?.delete(spectatorId);
      this._pendingSoloSpectatorOfferIds?.delete(spectatorId);
      this._readySoloSpectatorIds?.delete(spectatorId);
      this._soloStream?.removeSpectator(spectatorId);
      if (this._soloStream?.spectatorCount === 0) {
        this._stopSoloStream();
        if (!this.clipRecorder) this._releaseCanvasStream();
        this._startSoloStream();
      } else {
        const count = this._soloStream?.spectatorCount || 0;
        if (count > 0) this._flash(`Spectator left (${count} watching)`);
      }
      const label = username ? `@${username}` : `Spectator ${spectatorId}`;
      this._showChatSystemMessage(`${label} has left the chat`);
      this._updatePresenceList();
      gameActions.refreshGameToolbarAvailability(this, 'spectators');
    };

    this.net.onSpectatorSignal = (spectatorId, payload) => {
      if (!ownsStream()) return;
      this._soloStream?.handleSignal(spectatorId, payload);
    };

    this.net.onSpectatorReady = (spectatorId) => {
      if (!ownsStream() || !this._spectators.has(spectatorId)) return;
      this._readySoloSpectatorIds.add(spectatorId);
      if (this._pendingSoloSpectatorOfferIds.has(spectatorId) && !this._deferSoloSpectatorOffers) {
        this._offerSoloSpectator(spectatorId, { retry: true });
      }
    };
    // A retained room's viewers stay on their sockets while the host reboots
    // into a game or resumes BASIC. Offer the replacement canvas to them too.
    if (!this._soloStream) {
      for (const spectatorId of this._spectators?.keys() || []) {
        this._queueSoloSpectatorOffer(spectatorId);
      }
    }
  }

  _stopSoloStream() {
    this._soloStreamOwner = null;
    this._pendingSoloSpectatorOfferIds?.clear();
    this._readySoloSpectatorIds?.clear();
    if (this._soloStream) {
      this._soloStream.destroy();
      this._soloStream = null;
    }
    if (this.net) {
      this.net.onSpectatorJoined = null;
      this.net.onSpectatorLeft = null;
      this.net.onSpectatorSignal = null;
      this.net.onSpectatorReady = null;
    }
    this._offerSoloSpectator = null;
    this._queueSoloSpectatorOffer = null;
  }

  /**
   * Tear down side-by-side runtime state when leaving a room/game. Mirrors
   * _stopSoloStream() for the SBS path: closes the inbound sink PCs + removes
   * the #sbs-stage (via _teardownSbs), destroys the racer's outbound
   * SoloStreamHost (handled by the _stopSoloStream() call that precedes this
   * on exit), drops the overridden net handlers, and clears _sbs* state so a
   * subsequent solo/netplay/spectate session starts clean.
   *
   * No-op when _sbsRole is unset — legacy (non-SBS) sessions are unaffected.
   */
  _teardownSbsSession() {
    this._sbsRoom = false; // clear the room flag even if we never raced (host left the lobby)
    this._sbsJoining = false;
    this._roomJoinLaunchOwner = null;
    this._sbsSessionOwner = null;
    if (!this._sbsRole) return;
    this._teardownSbs();
    // Racer's outbound stream host (set up via _startSolo). _stopSoloStream()
    // is called just before this on the exit path and already destroys/nulls
    // it; guard here so this method is also safe if called standalone.
    if (this._soloStream) {
      try { this._soloStream.destroy(); } catch {}
      this._soloStream = null;
    }
    // Drop the srcSlot-demux/inbound onSignal override installed by
    // _sbsWireInbound (racer) / _startSideBySideSpectator (spectator).
    if (this.net) {
      this.net.onSignal = null;
      this.net.onSpectatorJoined = null;
      this.net.onSpectatorLeft = null;
      this.net.onSpectatorSignal = null;
      this.net.onSpectatorReady = null;
    }
    this._sbsRole = null;
    this._sbsMySlot = null;
    this._sbsOpponentSlot = null;
  }


  _formatShortDate(ts) {
    return formatShortDate(ts);
  }

  _setText(id, value) {
    const el = document.getElementById(id);
    if (el) el.textContent = value;
  }

  // ── UI Helpers ──────────────────────────────────────────

  // ── Page view tracking ─────────────────────────────────
  // Fire-and-forget POST /api/views. Only user-facing screens are tracked
  // (menu/game/lobby/loading are excluded — they're transient or already
  // covered by play_sessions). Sub-ID lets us distinguish e.g. a stats
  // page for user foo vs bar, or per-game leaderboard views.
  //
  // The shared browser helper suppresses immediate router double-fires.
  _trackPageView(screenName, explicitSubId = null) {
    // Map internal screen names → canonical page IDs (must match the
    // allowlist in src/server/routes/views.js).
    const PAGE_MAP = {
      stats: 'stats',
      leaderboards: 'leaderboards',
      settings: 'settings',
      rooms: 'rooms',
      friends: 'friends',
      top100: 'top100',
      contact: 'contact',
      activity: 'feed',
      menu: 'home',
      replays: 'replays',
      'replay-detail': 'replay-detail',
      'clip-detail': 'clip-detail',
      // Explicitly NOT tracked: game, lobby, loading
      // No admin/analytics entries — those screens live in the standalone
      // admin app now, which never reaches this code path.
    };
    const page = PAGE_MAP[screenName];
    if (!page) return;

    // Sub-ID enrichment for pages where context matters.
    let subId = explicitSubId;
    if (subId == null && page === 'stats') {
      // Track whose profile was viewed — use the URL since _viewedUsername
      // isn't a stable field.
      const m = window.location.pathname.match(/^\/u\/([^/]+)\/?$/);
      if (m) subId = decodeURIComponent(m[1]);
    } else if (subId == null && page === 'leaderboards') {
      const m = window.location.pathname.match(/^\/leaderboards\/(?:achievements\/)?(\d+)\/?$/);
      if (m) subId = m[1];
    } else if (subId == null && page === 'top100') {
      const m = window.location.pathname.match(/^\/top100\/([a-z0-9]+)\/?$/);
      if (m) subId = m[1];
    } else if (subId == null && page === 'replay-detail') {
      const m = window.location.pathname.match(/^\/replay\/([0-9a-f-]+)\/?$/i);
      if (m) subId = m[1];
    } else if (subId == null && page === 'clip-detail') {
      const m = window.location.pathname.match(/^\/clip\/([0-9a-f-]+)\/?$/i);
      if (m) subId = m[1];
    }

    trackPageView(page, { subId });
  }

  // ── Funnel instrumentation (RP-E15) ────────────────────
  //
  // The client-observable half of the two funnels. See src/client/funnel.js for
  // the privacy and fail-closed contract, and src/shared/funnel-events.js for
  // what each step means.

  /** Activation step 1 — the first screen this visit rendered, whatever it was. */
  _trackFunnelLanding(screenName) {
    // Dedupe lives in the funnel module (once per page lifetime), so this fires
    // on whichever screen paints first and is a no-op afterwards. A visitor
    // landing straight on a deep link counts too — the funnel measures arrivals,
    // not homepage arrivals.
    const SURFACE_BY_SCREEN = {
      menu: 'home', rooms: 'rooms', leaderboards: 'other', friends: 'friends',
      replays: 'other', top100: 'other', stats: 'other',
    };
    trackFunnelStep('landing', { surface: SURFACE_BY_SCREEN[screenName] || 'deep-link' });
  }

  /**
   * Context shared by the per-launch steps.
   *
   * Deliberately carries NO game reference. The catalog id is resolved lazily
   * (`_resolveCurrentGameId`, only on queue/list adds), so identifying the game
   * at launch would mean an extra request on the critical launch path — and for
   * a private/BYO upload there is no catalog match to resolve anyway. The
   * activation funnel's question is "did this visitor get to play", which
   * `system_id` segments well enough; per-game popularity already lives in
   * mv_popular_games. Skipping it also means this path cannot leak a private
   * ROM's identity even by accident.
   */
  _funnelGameContext() {
    return {
      system_id: this.system?.id || null,
      net_mode: this._netMode || null,
      room_code: this._netplayRoomCode() || null,
    };
  }

  /** The room code for a NETPLAY session, or null. A solo room is not a
   *  multiplayer session and must not correlate into the multiplayer funnel. */
  _netplayRoomCode() {
    const code = this.net?.roomCode;
    if (!code || typeof code !== 'string') return null;
    if (code === this._soloRoomCode) return null;
    return /^[A-Z0-9]{6}$/.test(code) ? code : null;
  }

  /** Consume the library marker only for the ROM and launch that loaded it. */
  _takeLibraryLaunchAttribution(existingLaunchOwner) {
    const pending = this._pendingLibraryLaunch;
    this._pendingLibraryLaunch = null;
    return !!pending && pending.romName != null
      && pending.owner === (existingLaunchOwner ?? this._gameLaunchIntentGeneration)
      && pending.system === this.system?.id
      && pending.romName === this.romName;
  }

  /** Read the screen being left before _showScreen('game') overwrites it. */
  _funnelSurfaceForLaunch(libraryLaunch = false) {
    // A CTA on another surface can pre-declare where a subsequent launch
    // belongs — the rooms empty-state "Browse games" button (RP-E06) sends the
    // visitor to the catalog to pick a game, but the resulting launch should be
    // credited to `rooms`, not the menu they passed through.
    //
    // The override is SCOPED, not permanent: it only applies to a launch made
    // from the menu it landed the visitor on, and _invalidatePendingLaunchSurface
    // drops it the moment they navigate away without launching. Without that a
    // Browse click followed by "wander off, launch something else later" would
    // stamp `rooms` on an unrelated launch and corrupt the very RP-E15 metric
    // this feeds. Read-and-clear one-shot either way.
    const pending = this._pendingFunnelSurface;
    this._pendingFunnelSurface = null;
    if (pending && this._activeScreenName === 'menu') return pending;

    if (libraryLaunch && (!this._activeScreenName || this._activeScreenName === 'menu')) {
      return 'library';
    }

    const from = this._activeScreenName;
    if (!from) return 'deep-link';
    const SURFACE_BY_SCREEN = {
      menu: 'home', rooms: 'rooms', friends: 'friends', lobby: 'rooms',
      leaderboards: 'other', replays: 'other', top100: 'other', stats: 'other',
    };
    return SURFACE_BY_SCREEN[from] || 'other';
  }

  /**
   * Invalidate a pending cross-surface launch-attribution override (set by the
   * rooms empty-state "Browse games" CTA — see _funnelSurfaceForLaunch) when
   * the visitor navigates somewhere other than the menu it targets or the game
   * runtime. Leaving the menu for Friends, Leaderboards, Rooms, etc. abandons
   * the browse-to-launch path, so a stale `rooms` must not survive to a later
   * launch from an unrelated surface. Called from _showScreen on every route
   * change; `menu` (the target) and `game` (the launch itself) are exempt.
   */
  _invalidatePendingLaunchSurface(name) {
    if (name !== 'menu' && name !== 'game') this._pendingFunnelSurface = null;
    if (name !== 'menu') this._pendingLibraryLaunch = null;
  }

  /** Activation step 3 — the visitor asked for a game to start. */
  _trackFunnelLaunch(surface = 'other') {
    // A new launch is genuinely a new attempt, so clear the per-launch dedupe
    // from any previous session in this tab first.
    resetFunnelSession();
    trackFunnelStep('launch_requested', { ...this._funnelGameContext(), surface });
    this._funnelFirstFrameSent = false;
    this._cancelFunnelBootInference();
    this._armFunnelFiveMinuteTimer();
  }

  /**
   * Activation step 4 (+ multiplayer step 4 in a netplay room) — the session is
   * live and producing frames. Idempotent per launch.
   *
   * Call this from a path that has PROVEN a frame ran (a JS-stepped frame loop, or
   * a video element that reported `playing`). For self-driving cores, where JS
   * never sees a frame boundary, use _armFunnelBootInference() instead — it
   * records the same step with `detail: 'boot_inferred'` so the weaker evidence
   * stays visible in the data rather than being laundered into a hard number.
   *
   * @param {'frame_observed'|'boot_inferred'} evidence
   */
  _trackFunnelFirstFrame(evidence = 'frame_observed') {
    if (this._funnelFirstFrameSent) return;
    this._funnelFirstFrameSent = true;
    this._cancelFunnelBootInference();
    const ctx = { ...this._funnelGameContext(), detail: evidence };
    trackFunnelStep('first_frame', ctx);
    // A netplay first frame is also the multiplayer funnel's step 4. Both are
    // recorded: they correlate on different keys (funnel_id vs room_code) and
    // measure different questions, so one cannot substitute for the other.
    if (ctx.room_code) trackFunnelStep('net_first_frame', ctx);
  }

  /**
   * Self-driving cores (EmulatorJS/RetroArch WASM) drive their own game loop, so
   * JS has no frame boundary to observe and no honest way to prove a frame
   * rendered: pixel readback races the core's GL ops and reads legitimately black
   * mid-play on iOS (see _startFpsMonitor), and the input-log ring that would give
   * a core-side frame stamp exists only on the patched GB builds and only while the
   * input override is armed.
   *
   * So instead of emitting on the first input-loop tick — which fires whether or
   * not the core ever booted, and was therefore a false positive on a failed
   * boot — wait out a grace period and require the session to still be alive with
   * the same emulator instance. That filters instant boot failures and crashes,
   * and the event is tagged `boot_inferred` so nobody mistakes it for a measured
   * frame.
   */
  _armFunnelBootInference() {
    this._cancelFunnelBootInference();
    if (this._funnelFirstFrameSent) return;
    const emuAtArm = this.emu;
    const BOOT_GRACE_MS = 3000;
    this._funnelBootInferTimer = setTimeout(() => {
      this._funnelBootInferTimer = null;
      // Same session, same core instance, still running, and the tab was not
      // backgrounded through the whole grace window (a hidden self-driving core is
      // paused, so it has not booted into gameplay).
      if (!this.running || this.emu !== emuAtArm) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      this._trackFunnelFirstFrame('boot_inferred');
    }, BOOT_GRACE_MS);
  }

  _cancelFunnelBootInference() {
    clearTimeout(this._funnelBootInferTimer);
    this._funnelBootInferTimer = null;
  }

  /** Multiplayer step 3 — the netplay transport came up for this player. */
  _trackFunnelConnected() {
    const room = this._netplayRoomCode();
    if (!room) return;
    trackFunnelStep('connected', { ...this._funnelGameContext(), room_code: room });
  }

  /**
   * The emulated-frame counter for whichever session type is live. Used to prove
   * the core is actually advancing, rather than trusting `running`.
   *
   * Every path here is driven either by JS stepping frames or by a rAF loop, so
   * all of them stall when the tab is backgrounded — which is exactly the
   * property the active-play accounting below depends on.
   */
  _funnelFrameCounter() {
    if (this.hostedNetplay && this._netMode === 'guest') {
      // A hosted guest runs no core; the host's video is the session. currentTime
      // advances only while that video actually plays.
      const v = document.getElementById('guest-video');
      return v ? Math.floor((v.currentTime || 0) * 60) : 0;
    }
    // Both engines expose `currentFrame` (LockstepEngine and RollbackEngine); the
    // solo/hosted counters live on `this`. Summing is safe because only one
    // session type is live at a time, and a stale counter from a previous session
    // is a constant — it cannot make `advanced` look true on its own.
    return (this.lockstep?.currentFrame ?? 0)
      + (this.rollback?.currentFrame ?? 0)
      + (this.hostedHostFrame ?? 0)
      + (this._libretroSoloFrame ?? 0)
      + (this.soloFrame ?? 0);
  }

  /**
   * Activation step 5 — five minutes of ACTIVE play reached.
   *
   * Wall-clock is the wrong measure: a `setTimeout` that only checks `running`
   * counts a backgrounded tab as play. Self-driving cores are paused while hidden
   * (with `running` still true), and rAF-driven cores stop advancing when the
   * browser throttles background frames — so in both cases the session is idle
   * while the clock keeps ticking.
   *
   * Instead this samples every 10s and only banks that slice when the document is
   * visible AND the frame counter moved since the last sample. Time spent in a
   * background tab, on a paused core, or on a stalled session is never credited.
   */
  _armFunnelFiveMinuteTimer() {
    this._cancelFunnelFiveMinuteTimer();
    const SAMPLE_MS = 10_000;
    const TARGET_MS = 5 * 60 * 1000;
    this._funnelActivePlayMs = 0;
    this._funnelLastFrameSample = this._funnelFrameCounter();
    this._funnelFiveMinuteTimer = setInterval(() => {
      const frames = this._funnelFrameCounter();
      const advanced = frames > this._funnelLastFrameSample;
      this._funnelLastFrameSample = frames;
      const visible = typeof document === 'undefined'
        || document.visibilityState === undefined
        || document.visibilityState === 'visible';
      if (!this.running || !visible || !advanced) return;

      this._funnelActivePlayMs += SAMPLE_MS;
      if (this._funnelActivePlayMs < TARGET_MS) return;
      this._cancelFunnelFiveMinuteTimer();
      trackFunnelStep('play_5min', this._funnelGameContext());
    }, SAMPLE_MS);
  }

  _cancelFunnelFiveMinuteTimer() {
    clearInterval(this._funnelFiveMinuteTimer);
    this._funnelFiveMinuteTimer = null;
  }


  _showScreen(name, pageViewName = name) {
    if (name === 'game') gameActions.beginRoomSession(this);
    // CR3: open this launch's capture decisions right after its session
    // generation, before anything (including a mounted room) reads them.
    if (name === 'game') beginCaptureDecisions(this, { gen: this._roomSessionGen, mode: gameActions.sessionMode(this) });
    // A2: decide this launch's layout (mounting or unmounting the room) before the screen,
    // header, toolbar and chat decisions below read it.
    if (name === 'game') prepareGameScreenLayout(this);
    // A5: an unverified restore is reloading the page into classic mode; show neither layout.
    if (this._roomRecoveryReloading) return;
    // S6, G8: stop before hiding unless fields and layers have a synchronous exact proof.
    if (!prepareRoomScreenHide(this, name)) return;
    this._invalidatePendingLaunchSurface(name);
    const previousScreen = this._activeScreenName || null;
    document.querySelectorAll('.screen').forEach(el => el.classList.add('hidden'));
    document.getElementById(`screen-${name}`)?.classList.remove('hidden');
    this._activeScreenName = name;
    newsScreenChanged(name);
    if (name === 'menu') this._schedulePendingHomepageTour();
    // Homepage v2: the host stays mounted behind most screens — pause its
    // refreshMs polling whenever the menu is not the visible surface. An
    // in-place replay is also a foreground surface even though menu remains
    // the active SPA screen underneath it.
    this._homeHost?.setRefreshPaused?.(
      name !== 'menu' || this._replayViewerHomeRefreshPaused === true);

    const gameTitle = (name === 'game' || name === 'lobby')
      ? (this._activeGameTitle?.() || this._bestDisplayTitle?.() || '')
      : '';
    const baseTitle = routeTitleForScreen(name);
    const routeTitle = gameTitle ? `${baseTitle}: ${gameTitle}` : baseTitle;
    if (name === 'game') {
      const heading = document.getElementById('game-route-title');
      if (heading) heading.textContent = gameTitle ? `Playing ${gameTitle}` : 'Playing a game';
    }
    activateScreenAccessibility(name, {
      title: routeTitle,
      focus: previousScreen !== null && previousScreen !== name,
    });
    this._applyLandscapeFs?.();

    const hideChrome = name === 'game' || name === 'lobby';
    // H1 and A7: a room-mode game keeps the site header and marks <body>.
    const roomGame = name === 'game' && isRoomMode(this);
    document.getElementById('site-header')?.classList.toggle('hidden', hideChrome && !roomGame);
    document.body.classList.toggle('game-room-active', roomGame);
    document.getElementById('global-search-rail')?.classList.toggle('is-chrome-hidden', hideChrome);
    document.getElementById('site-footer')?.classList.toggle('hidden', hideChrome);
    document.getElementById('theme-showcase')?.classList.toggle('hidden', hideChrome);
    document.getElementById('btn-feedback')?.classList.toggle('hidden', hideChrome);
    if (hideChrome) {
      // A pending launch can complete while Feedback is opening. Hiding its
      // DOM must also release the keyboard lease before gameplay attaches.
      this.releaseGameplayInput('feedback-panel');
      const fp = document.getElementById('feedback-panel');
      if (fp) { fp.classList.remove('open'); fp.classList.add('hidden'); }
    }

    const tosGate = document.getElementById('tos-gate-modal');
    if (tosGate) {
      const onLegalPage = name === 'tos'
        || name === 'privacy'
        || name === 'community-standards'
        || name === 'acknowledgements'
        || name === 'third-party-notices';
      tosGate.classList.toggle('hidden', onLegalPage);
    }

    // Highlight active nav link
    const pageMap = { menu: 'menu', stats: 'menu', settings: 'menu', admin: 'menu', analytics: 'menu', contact: 'menu', privacy: 'menu', tos: 'menu', 'community-standards': 'menu', acknowledgements: 'menu', 'admin-contact': 'menu', 'admin-contact-detail': 'menu', 'admin-moderation': 'menu', 'admin-moderation-detail': 'menu', friends: 'friends', rooms: 'rooms', leaderboards: 'leaderboards', top100: 'top100', replays: 'replays', controllers: 'controllers' };
    const activePage = pageMap[name] || '';
    const currentPage = ['menu', 'friends', 'rooms', 'leaderboards', 'top100', 'replays', 'controllers'].includes(name)
      ? name
      : '';
    document.querySelectorAll('.site-nav-link[data-page]').forEach(el => {
      const active = el.dataset.page === activePage;
      el.classList.toggle('active', active);
      if (el.dataset.page === currentPage) el.setAttribute('aria-current', 'page');
      else el.removeAttribute('aria-current');
    });

    // Record a page view for user-facing screens. A modal-backed detail route
    // can override the visible backdrop's name so one navigation produces one
    // canonical impression.
    this._trackPageView(pageViewName);
    this._trackFunnelLanding(name);

    const gameControls = document.getElementById('game-controls');
    const touchGamepad = document.getElementById('touch-gamepad');
    const controlsHint = document.getElementById('game-controls-hint');
    const chat = document.getElementById('chat');
    const chatSidebar = document.getElementById('chat-sidebar');
    const screenGame = document.getElementById('screen-game');
    document.getElementById('back-to-menu')?.classList.toggle('hidden', name !== 'game');
    hideExitConfirm(this);

    if (name === 'game') {
      this._teardownMenuDOM();
      this._homeHost?.unmount();
      this._releaseMenuCaches();
      gameControls?.classList.remove('hidden');
      diskPicker.updateC64RuntimeControls(this);
      updateC64SpecialKeys(this);
      renderMute(this);
      // Desktop: show briefly as a peek, then auto-hide (classic only: the game
      // room docks the toolbar, H5).
      if (gameControls && !isRoomMode(this) && !('ontouchstart' in window) && window.innerWidth > 800) {
        clearTimeout(this._dockPeekTimer);
        gameControls.classList.remove('dock-hidden');
        this._dockPeekTimer = setTimeout(() => {
          this._dockPeekTimer = null;
          // Don't retract if the ⋯ menu was opened during the peek window.
          if (!document.getElementById('screen-game').classList.contains('hidden')
              && document.getElementById('game-more-menu')?.classList.contains('hidden')) {
            gameControls.classList.add('dock-hidden');
          }
        }, 1500);
      }
      this._autoShowCheatsheet();
      if (this.state === State.SPECTATING) this._setRoomMicControlsVisible(false);
      const showPad = 'ontouchstart' in window && !this._gamepadConnected;
      if (showPad) touchGamepad?.classList.remove('hidden');
      controlsHint?.classList.toggle('hidden', this._gamepadConnected);
      const isNetplay = this.state === State.PLAYING || this.state === State.SPECTATING;
      const hasSoloRoom = this.state === State.SOLO && !!this._soloRoomCode;
      const showChat = (isNetplay || hasSoloRoom) && this._chatEnabled !== false;
      document.getElementById('btn-chat')?.classList.toggle('hidden', !showChat);
      // Save, Load, Rewind, Reset, Screenshot, Swap and Change game (T3, SM3).
      gameActions.refreshGameToolbarAvailability(this, 'show-game');
      // CH3: in the game room the room owns the overlay and the embedded
      // sidebar; reconcile only hands availability to the room.
      if (showChat && !isRoomMode(this)) {
        chat?.classList.remove('hidden');
        if (this._chatSidebarOpen && !this._isMobileChat()) {
          chatSidebar?.classList.remove('hidden');
          screenGame?.classList.add('chat-open');
        }
      }
      this._reconcileChatViewport();
    } else {
      clearTimeout(this._dockPeekTimer);
      this._dockPeekTimer = null;
      gameControls?.classList.remove('dock-hidden', 'tap-hidden');
      gameControls?.classList.add('hidden');
      document.getElementById('change-game-link')?.classList.add('hidden');
      touchGamepad?.classList.add('hidden');
      controlsHint?.classList.add('hidden');
      // CH3: the game room owns the overlay and the embedded sidebar.
      if (!isRoomMode(this)) {
        chat?.classList.add('hidden');
        chatSidebar?.classList.add('hidden');
        screenGame?.classList.remove('chat-open');
        roomChat.syncChatSidebarAccessibility(this, false);
      }
      // Don't leave the menu stuck in faux-fullscreen (overflow:hidden).
      document.body.classList.remove('mobile-fullscreen');
    }
    // Centralize report-action refresh for every game/spectator/role screen
    // transition. Solo-room assignment also refreshes directly because its
    // socket can arrive after the game screen is already visible.
    this._updateReportButtonVisibility();
    // L3: every _showScreen('game') ends with a room layout pass (no room, no call).
    if (name === 'game') callRoom(this, 'relayout', 'show-screen');
  }

  _teardownMenuDOM() {
    const ids = [
      'rom-library', 'favorites-list', 'recent-list',
      'saves-list', 'saves-tabs',
      'achievements-list',
      'active-games-list',
      'trending-list', 'trending-tabs',
      'top-picks-list', 'top-picks-tabs',
      'top-games-list', 'top-games-tabs',
      'lb-teaser-list', 'lb-teaser-tabs',
      'my-lists-list', 'lists-tabs',
      'my-roms-list',
      'menu-friends-list', 'menu-suggested-list',
      'friends-activity-list',
    ];
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) el.innerHTML = '';
    }
  }

  _releaseMenuCaches() {
    this._topGamesCache = {};
    this._trendingCache = {};
    this._allFavorites = null;

    const sys = this.system?.id;
    if (sys && this._romTitleCache) {
      const keep = this._romTitleCache[sys];
      this._romTitleCache = keep ? { [sys]: keep } : {};
    }
    if (sys && this._romHashToTitle) {
      const keep = this._romHashToTitle[sys];
      this._romHashToTitle = keep ? { [sys]: keep } : {};
    }
    if (sys && this._romFilenameToHash) {
      const keep = this._romFilenameToHash[sys];
      this._romFilenameToHash = keep ? { [sys]: keep } : {};
    }
    if (sys && this._multiDiskRoms) {
      const keep = this._multiDiskRoms[sys];
      this._multiDiskRoms = keep ? { [sys]: keep } : {};
    }
  }

  _updatePresenceList() {
    this._hideUserCard();
    // CH1 and CH2: presence changes refresh the Chat tab and its watching count.
    roomChat.syncRoomChatAvailability(this);
    const section = document.getElementById('presence-section');
    if (!section) return;

    const isActive = this.state === State.PLAYING ||
                     this.state === State.SPECTATING ||
                     this.state === State.LOBBY ||
                     (this.state === State.SOLO && this._soloRoomCode);
    section.classList.toggle('hidden', !isActive);
    if (!isActive) return;

    const slots = this._lobbySlotUsernames || [null, null];
    const images = this._lobbySlotImages || [null, null];
    const myPlayer = this.net?.playerNumber || 0;

    const renderPresenceName = (el, username) => {
      if (username) {
        const safe = this._escapeHtml(username);
        el.innerHTML = `<retro-username username="${safe}" size="compact" display-name="${safe}"></retro-username>`;
      } else {
        el.textContent = 'Anonymous';
      }
    };

    for (let i = 0; i < 4; i++) {
      const playerNum = i + 1;
      const item = document.getElementById(`presence-p${playerNum}`);
      const nameEl = document.getElementById(`presence-p${playerNum}-name`);
      if (!item || !nameEl) continue;
      const hasPlayer = slots[i] !== null && slots[i] !== undefined;
      if (hasPlayer) {
        item.classList.remove('hidden');
        renderPresenceName(nameEl, slots[i]);
        item.classList.toggle('presence-self', myPlayer === playerNum);
        this._setPresenceAvatar(`p${playerNum}`, slots[i], images[i]);
        item.style.cursor = slots[i] ? 'pointer' : '';
        item.onclick = slots[i] ? () => this._showUserCard(slots[i], images[i], item) : null;
      } else {
        item.classList.add('hidden');
        item.onclick = null;
      }
    }

    const container = document.getElementById('presence-spectators');
    const tpl = document.getElementById('presence-spectator-tpl');
    container.innerHTML = '';

    for (const [spectatorId, info] of this._spectators) {
      const clone = tpl.content.cloneNode(true);
      const item = clone.querySelector('.presence-item');
      if (this.net?.spectator && this.net?.spectatorId === spectatorId) {
        item.classList.add('presence-self');
      }
      clone.querySelector('.presence-role').textContent = 'SPEC';
      const nameEl = clone.querySelector('.presence-name');
      if (info.username) {
        const safe = this._escapeHtml(info.username);
        nameEl.innerHTML = `<retro-username username="${safe}" size="compact" display-name="${safe}"></retro-username>`;
      } else {
        nameEl.textContent = `Spectator ${spectatorId}`;
      }
      const sImg = clone.querySelector('.presence-avatar');
      const sPh = clone.querySelector('.presence-avatar-placeholder');
      const u = info.username;
      sImg.hidden = !u; sPh.hidden = !!u;
      if (u) {
        sImg.src = info.image || `/api/users/${encodeURIComponent(u)}/avatar`;
        sImg.onerror = () => { sImg.hidden = true; sPh.textContent = u[0].toUpperCase(); sPh.hidden = false; };
      } else { sPh.textContent = '?'; }
      if (u) {
        item.style.cursor = 'pointer';
        item.addEventListener('click', (e) => {
          if (e.target.closest('.spectator-invite-btn')) return;
          this._showUserCard(u, info.image, item);
        });
      }
      if (this.state === State.SOLO && !this._isBasicMode && this._sbsRole == null && !this._pendingUpgrade && u) {
        // Solo-only "promote spectator to P2" upgrade. Side-by-side racers also sit
        // in State.SOLO but their room is full at two racers (the server rejects the
        // upgrade with not_solo), so don't offer it.
        const btn = document.createElement('button');
        btn.className = 'spectator-invite-btn';
        btn.title = 'Invite to play';
        btn.textContent = '+ Invite';
        btn.onclick = (e) => { e.stopPropagation(); this._sendUpgradeInvite(spectatorId); };
        item.appendChild(btn);
      } else if (this.state === State.SOLO && this._pendingUpgrade === spectatorId) {
        const tag = document.createElement('span');
        tag.className = 'spectator-invite-pending';
        tag.textContent = 'Invited';
        item.appendChild(tag);
      }
      container.appendChild(clone);
    }

    // The server's roster snapshot intentionally omits "you" (see the spectator
    // `assigned` path in server.js), so render the local user's own spectator row
    // here. Covers every entry path: lobby, solo, side-by-side, and cross-replica.
    let selfSpectatorShown = false;
    if (this.net?.spectator && this.net?.spectatorId != null && !this._spectators.has(this.net.spectatorId)) {
      selfSpectatorShown = true;
      const me = authState.getCurrentUser();
      const myName = me?.displayUsername || me?.username || null;
      const myImage = me?.image || null;
      const clone = tpl.content.cloneNode(true);
      const item = clone.querySelector('.presence-item');
      item.classList.add('presence-self');
      clone.querySelector('.presence-role').textContent = 'SPEC';
      const nameEl = clone.querySelector('.presence-name');
      if (myName) {
        const safe = this._escapeHtml(myName);
        nameEl.innerHTML = `<retro-username username="${safe}" size="compact" display-name="${safe}"></retro-username>`;
      } else {
        nameEl.textContent = 'You';
      }
      const sImg = clone.querySelector('.presence-avatar');
      const sPh = clone.querySelector('.presence-avatar-placeholder');
      sImg.hidden = !myName; sPh.hidden = !!myName;
      if (myName) {
        sImg.src = myImage || `/api/users/${encodeURIComponent(myName)}/avatar`;
        sImg.onerror = () => { sImg.hidden = true; sPh.textContent = myName[0].toUpperCase(); sPh.hidden = false; };
      } else { sPh.textContent = '?'; }
      container.appendChild(clone);
    }

    const playerCount = slots.filter(s => s !== null && s !== undefined).length;
    const total = playerCount + this._spectators.size + (selfSpectatorShown ? 1 : 0);
    document.getElementById('presence-count').textContent = total;

    const badge = document.getElementById('chat-presence-badge');
    if (badge) {
      badge.textContent = total;
      badge.classList.toggle('hidden', total < 2);
    }
  }

  _setPresenceAvatar(slot, username, imageUrl) {
    const img = document.getElementById(`presence-${slot}-avatar`);
    const ph = document.getElementById(`presence-${slot}-avatar-ph`);
    if (!img) return;
    img.hidden = !username;
    ph.hidden = !!username;
    if (username) {
      img.src = imageUrl || `/api/users/${encodeURIComponent(username)}/avatar`;
      img.onerror = () => { img.hidden = true; ph.textContent = username[0].toUpperCase(); ph.hidden = false; };
    } else { ph.textContent = '?'; }
  }

  _showUserCard(username, imageUrl, anchorEl) {
    if (!username) return;
    const card = document.getElementById('user-info-card');
    if (!card) return;

    if (this._userCardUsername === username && !card.classList.contains('hidden')) {
      this._hideUserCard();
      return;
    }
    this._userCardUsername = username;

    card.innerHTML = '<div class="user-card-loading">Loading…</div>';
    card.classList.remove('hidden');

    const rect = anchorEl.getBoundingClientRect();
    const sidebar = document.getElementById('chat-sidebar');
    const sidebarRect = sidebar?.getBoundingClientRect();
    if (sidebarRect) {
      const left = Math.max(4, rect.left - sidebarRect.left);
      const top = rect.bottom - sidebarRect.top + 4;
      card.style.left = `${left}px`;
      card.style.top = `${top}px`;
      card.style.maxWidth = `${sidebarRect.width - left - 4}px`;
    }

    this._userCardDismiss = (e) => {
      if (!card.contains(e.target) && !anchorEl.contains(e.target)) this._hideUserCard();
    };
    this._userCardEsc = (e) => { if (e.key === 'Escape') this._hideUserCard(); };
    setTimeout(() => {
      document.addEventListener('click', this._userCardDismiss, true);
      document.addEventListener('keydown', this._userCardEsc);
    }, 0);

    fetch(`/api/users/${encodeURIComponent(username)}`, { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        if (this._userCardUsername !== username) return;
        this._renderUserCard(card, data, username, imageUrl);
      })
      .catch(() => {
        if (this._userCardUsername !== username) return;
        card.innerHTML = '<div class="user-card-loading">Could not load profile</div>';
      });
  }

  _renderUserCard(card, data, username, imageUrl) {
    card.innerHTML = '';

    // Banner (z:0) as the very first child — sits behind everything
    // else in the popover. Renders nothing if no banner equipped.
    // The popover container is already position:relative.
    const banner = document.createElement('retro-banner');
    banner.setAttribute('username', username);
    card.appendChild(banner);

    // Tint overlay (z:0.5) for readability over banner artwork.
    // Pure CSS — element only needs to exist so the rule can match.
    const overlay = document.createElement('div');
    overlay.className = 'user-card-banner-overlay';
    card.appendChild(overlay);

    const header = document.createElement('div');
    header.className = 'user-card-header';

    const avatarWrap = document.createElement('div');
    avatarWrap.className = 'user-card-avatar-wrap';
    const displayName = data.user?.displayUsername || data.user?.username || username;
    const avatar = document.createElement('retro-avatar');
    avatar.className = 'user-card-avatar';
    avatar.setAttribute('username', username);
    avatar.setAttribute('size', 'hero');
    avatar.setAttribute('fallback-avatar', '');
    avatarWrap.append(avatar);

    const identity = document.createElement('div');
    identity.className = 'user-card-identity';
    const nameEl = document.createElement('retro-username');
    nameEl.className = 'user-card-name';
    nameEl.setAttribute('username', username);
    nameEl.setAttribute('size', 'hero');
    nameEl.setAttribute('display-name', displayName);
    identity.appendChild(nameEl);
    if (data.custom_status) {
      const status = document.createElement('div');
      status.className = 'user-card-status';
      status.textContent = data.custom_status;
      identity.appendChild(status);
    }
    header.append(avatarWrap, identity);
    card.appendChild(header);

    const stats = document.createElement('div');
    stats.className = 'user-card-stats';
    const addStat = (label, value) => {
      const row = document.createElement('div');
      row.className = 'user-card-stat';
      const valSpan = document.createElement('span');
      valSpan.className = 'user-card-stat-value';
      valSpan.textContent = value;
      row.append(valSpan, ` ${label}`);
      stats.appendChild(row);
    };
    if (data.stats) {
      if (data.stats.points != null) addStat('points', Number(data.stats.points).toLocaleString());
      if (data.stats.games != null) addStat('games played', Number(data.stats.games).toLocaleString());
    }
    if (data.friend_count != null) addStat('friends', Number(data.friend_count).toLocaleString());
    if (data.mutual_friends_count != null) {
      addStat('mutual friends', Number(data.mutual_friends_count).toLocaleString());
    }
    if (stats.children.length) card.appendChild(stats);

    const actions = document.createElement('div');
    actions.className = 'user-card-actions';

    if (data.friend_status && data.friend_status !== 'self') {
      const actionSlot = document.createElement('div');
      mountProfileActionButton(actionSlot, username, { currentStatus: data.friend_status });
      requestAnimationFrame(() => {
        actionSlot.querySelectorAll('.friend-action-danger, .friend-action-blocked').forEach(b => b.remove());
      });
      actions.appendChild(actionSlot);
    }

    if (this.net?.spectator || this.state === State.SPECTATING) {
      const link = document.createElement('a');
      link.className = 'user-card-profile-link';
      link.href = `/u/${encodeURIComponent(username)}`;
      link.textContent = 'VIEW PROFILE';
      actions.appendChild(link);
    }
    card.appendChild(actions);
  }

  _configureLobbySlots(maxPlayers = 2) {
    const count = Math.max(2, Math.min(validatedMultitapProfile(this._roomProfile)?.capacity || 4, Number(maxPlayers) || 2));
    this._lobbyMaxPlayers = count;
    const resizeRoster = (roster, emptyValue) => {
      const resized = Array.isArray(roster) ? roster.slice(0, count) : [];
      while (resized.length < count) resized.push(emptyValue);
      return resized;
    };
    this._lobbySlotUsernames = resizeRoster(this._lobbySlotUsernames, null);
    this._lobbySlotImages = resizeRoster(this._lobbySlotImages, null);
    this._lobbySlotOccupied = resizeRoster(this._lobbySlotOccupied, false);
    const container = document.getElementById('lobby-player-slots');
    container?.classList.toggle('multi-player', count > 2);
    container?.querySelectorAll('[data-player-slot]').forEach((slot) => {
      slot.classList.toggle('hidden', Number(slot.dataset.playerSlot) > count);
    });
  }

  _firstOpenLobbySlot() {
    const count = this._lobbyMaxPlayers || this.system?.maxPlayers || 2;
    for (let i = 0; i < count; i++) {
      if (!this._lobbySlotOccupied?.[i]) return i + 1;
    }
    return null;
  }

  _hideUserCard() {
    const card = document.getElementById('user-info-card');
    if (card) { card.classList.add('hidden'); card.innerHTML = ''; }
    this._userCardUsername = null;
    if (this._userCardDismiss) {
      document.removeEventListener('click', this._userCardDismiss, true);
      this._userCardDismiss = null;
    }
    if (this._userCardEsc) {
      document.removeEventListener('keydown', this._userCardEsc);
      this._userCardEsc = null;
    }
  }

  _setPlayerStatus(playerNum, status) {
    const statusEl = document.getElementById(`lobby-p${playerNum}-status`);
    const userEl = document.getElementById(`lobby-p${playerNum}-username`);
    if (!statusEl) return;
    if (status !== 'connected' && this._pendingInvite?.slot === playerNum) {
      if (userEl) this._renderLobbyPlayerCard(userEl, this._pendingInvite.username, playerNum);
      statusEl.textContent = '○ Invited';
      statusEl.className = 'player-status pending';
      return;
    }
    const username = this._lobbySlotUsernames?.[playerNum - 1];
    if (userEl) {
      if (username) {
        this._renderLobbyPlayerCard(userEl, username, playerNum);
      } else {
        userEl.innerHTML = '';
      }
    }
    statusEl.textContent = status === 'connected' ? '● Connected' : '○ Waiting...';
    statusEl.className = `player-status ${status}`;
  }

  _renderLobbyPlayerCard(container, username, slotNum) {
    if (!this._lobbyCardCache) this._lobbyCardCache = {};
    if (this._lobbyCardCache[slotNum] === username) return;
    this._lobbyCardCache[slotNum] = username;

    container.innerHTML = '';
    const card = document.createElement('div');
    card.className = 'lobby-player-card';

    const avatarWrap = document.createElement('div');
    avatarWrap.className = 'lobby-player-avatar-wrap';
    const avatar = document.createElement('retro-avatar');
    avatar.className = 'lobby-player-avatar';
    avatar.setAttribute('username', username);
    avatar.setAttribute('size', 'list');
    avatar.setAttribute('fallback-avatar', '');
    avatarWrap.append(avatar);

    const identity = document.createElement('div');
    identity.className = 'lobby-player-identity';
    const nameEl = document.createElement('retro-username');
    nameEl.className = 'lobby-player-name';
    nameEl.setAttribute('username', username);
    nameEl.setAttribute('size', 'list');
    nameEl.setAttribute('display-name', username);
    identity.appendChild(nameEl);

    const statsRow = document.createElement('div');
    statsRow.className = 'lobby-player-stats';
    identity.appendChild(statsRow);

    card.append(avatarWrap, identity);
    container.appendChild(card);

    fetch(`/api/users/${encodeURIComponent(username)}`, { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        if (this._lobbyCardCache[slotNum] !== username) return;
        const displayName = data.user?.displayUsername || data.user?.username || username;
        nameEl.setAttribute('display-name', displayName);
        const parts = [];
        if (data.stats?.points != null) parts.push(`${Number(data.stats.points).toLocaleString()} pts`);
        if (data.stats?.games != null) parts.push(`${Number(data.stats.games).toLocaleString()} games`);
        if (data.friend_count != null) parts.push(`${Number(data.friend_count).toLocaleString()} friends`);
        statsRow.textContent = parts.join(' · ');
        if (!parts.length) statsRow.remove();
      })
      .catch(() => {});
  }

  _setupLobbySlotSelection() {
    const slots = document.querySelectorAll('.player-slot');
    if (slots.length < 2) return;
    slots.forEach(s => s.classList.add('selectable'));
    slots[0].dataset.swapHint = 'Click to become Player 1';
    slots[1].dataset.swapHint = 'Click to become Player 2';
    slots[0].classList.add('selected');
    this._pendingLobbySwap = false;

    const pickSlot = (mySlot) => {
      if (this.state !== State.LOBBY) return;
      const wantSwap = mySlot === 2;
      // No-op if we're already in the requested orientation.
      if (this._pendingLobbySwap === wantSwap) return;
      // Swap the username + pending-invite tracking so the visual slot
      // labels follow the host's choice. After the host picks P2, the
      // joiner / pending invitee belongs in P1.
      if (this._lobbySlotUsernames) {
        [this._lobbySlotUsernames[0], this._lobbySlotUsernames[1]] = [this._lobbySlotUsernames[1], this._lobbySlotUsernames[0]];
      }
      if (this._pendingInvite) {
        this._pendingInvite.slot = this._pendingInvite.slot === 1 ? 2 : 1;
      }
      this._pendingLobbySwap = wantSwap;
      slots[mySlot - 1].classList.add('selected');
      slots[(mySlot - 1) ^ 1].classList.remove('selected');
      this._setPlayerStatus(mySlot, 'connected');
      // The other slot keeps its pending-invite visual via _setPlayerStatus.
      this._setPlayerStatus(mySlot === 1 ? 2 : 1, 'waiting');
      document.getElementById('lobby-player').textContent = `You are Player ${mySlot}`;
    };

    slots[0].onclick = () => pickSlot(1);
    slots[1].onclick = () => pickSlot(2);
  }

  /**
   * Existing hook name for the launch, spectator and exit paths. Reset now
   * follows T3 (solo, a netplay host or C64 BASIC), so this recomputes the
   * whole toolbar through the shared availability refresh (SM3).
   */
  _updateResetButtonVisibility() {
    gameActions.refreshGameToolbarAvailability(this, 'reset-visibility');
  }

  _updateHUD() {
    if (!this.rollback) return;
    const stats = this.rollback.getStats();
    const ping = document.getElementById('stat-ping');
    const frame = document.getElementById('stat-frame');
    const rb = document.getElementById('stat-rollbacks');
    const adv = document.getElementById('stat-advantage');
    const delay = document.getElementById('stat-delay');
    const sync = document.getElementById('stat-sync');

    if (ping) ping.textContent = `${this.net.latency}ms`;
    if (frame) frame.textContent = stats.frame;
    if (rb) rb.textContent = stats.rollbackCount;
    if (adv) adv.textContent = stats.frameAdvantage;
    if (delay) delay.textContent = stats.delay;
    if (sync) {
      sync.textContent = stats.desync ? 'DESYNC' : 'OK';
      sync.classList.toggle('desync', stats.desync);
    }
  }

  _toggleHUDVisibility() {
    const hud = document.getElementById('hud');
    const parity = document.getElementById('hud-parity');
    if (hud) hud.classList.toggle('hidden');
    if (parity && this.state === State.PLAYING) parity.classList.toggle('hidden');
  }

  _flash(message, { actionLabel, onAction } = {}) {
    const el = document.getElementById('flash');
    if (!el) return;
    // Unhide BEFORE setting the text: #flash is a role=status live region,
    // and mutations made while it's display:none are not announced.
    el.classList.remove('hidden');
    el.classList.add('show');
    el.textContent = message;
    // Optional inline action (e.g. "Share" on an achievement unlock). The
    // toast is never the only path to its action — persistent entry points
    // exist elsewhere — so the timeout doesn't gate the capability.
    let timeoutMs = 7000;
    if (actionLabel && typeof onAction === 'function') {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'flash-action';
      btn.textContent = actionLabel;
      btn.addEventListener('click', () => {
        clearTimeout(this._flashTimer);
        el.classList.remove('show');
        el.classList.add('hidden');
        onAction();
      });
      el.appendChild(btn);
      timeoutMs = 12000;
    }
    // Track the hide timer so a follow-up flash can't hide this one early.
    clearTimeout(this._flashTimer);
    this._flashTimer = setTimeout(() => {
      el.classList.remove('show');
      setTimeout(() => el.classList.add('hidden'), 300);
    }, timeoutMs);
  }

  // ── Reconnect overlay ───────────────────────────────────

  _showReconnectOverlay(attempt, max) {
    let overlay = document.getElementById('reconnect-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'reconnect-overlay';
      overlay.style.cssText =
        'position:fixed;inset:0;display:flex;align-items:center;justify-content:center;' +
        'background:rgba(0,0,0,0.75);z-index:9999;color:#fff;font-family:inherit;font-size:18px;';
      document.body.appendChild(overlay);
    }
    overlay.textContent = `Reconnecting\u2026 (${attempt}/${max})`;
  }

  _hideReconnectOverlay() {
    document.getElementById('reconnect-overlay')?.remove();
  }

  // ── Achievements ────────────────────────────────────────

  /**
   * Start a play session (time tracking + server heartbeats) without
   * achievement ticking. Used for P2 guests in hosted netplay who
   * don't run the emulator locally.
   */
  _startPlaySession() {
    if (!this.romHash) return;

    this._endPlaySession(); // defensive: flush any prior session

    this._cheevosSessionSys = this.system.id;
    this._cheevosSessionHash = this.romHash;
    this.cheevosStore.startSession(this.system.id, this.romHash);
    this._cheevosSessionStart = Date.now();

    // Flush play-time every 10s so a browser crash doesn't lose much.
    this._cheevosPlayTimeInterval = setInterval(() => {
      this._flushAchievementPlayTime();
    }, 10000);

    // Server-side session lifecycle (no-op when not signed in).
    const gameInfo = this.cheevosEngine.getCurrentGame?.();
    const slug = gameInfo?.id || null;
    const title = this._canonicalTitle
      || gameInfo?.title
      || this._romDisplayTitle(this._activeRomFilename || this.romName, this.system.id)
      || this.romName
      || null;
    this._serverSessionStart(this.system.id, this.romHash, slug, title);
  }

  /** End play session. Flushes time and server session. Safe to call multiple times.
   *  Returns a promise that resolves when the server session end POST completes. */
  _endPlaySession({ multitap = false } = {}) {
    if (multitap || this._serverSessionMeta?.roomProfile === 'snes-multitap-v1' || validatedMultitapProfile(this._roomProfile)) {
      return this._serverSessionEnd(this._beginFinalScoreReplayFlush());
    }
    // A session that ends before the five-minute mark never reports it, and one
    // that dies inside the boot grace window never claims a first frame.
    this._cancelFunnelFiveMinuteTimer();
    this._cancelFunnelBootInference();
    if (this._cheevosPlayTimeInterval) {
      clearInterval(this._cheevosPlayTimeInterval);
      this._cheevosPlayTimeInterval = null;
    }
    if (this._cheevosTickInterval) {
      clearInterval(this._cheevosTickInterval);
      this._cheevosTickInterval = null;
    }
    this._flushAchievementPlayTime();
    if (this._cheevosSessionSys && this._cheevosSessionHash) {
      this.cheevosStore.endSession(this._cheevosSessionSys, this._cheevosSessionHash);
    }
    // An author session wrote no Auto Save, so it uploads none; its teardown may have ended the profile already.
    if (this._authorProfile || this._authorPlayEnded) this._authorPlayEnded = false;
    else this._uploadAutoSaveToServer();
    // Snapshot/extend the replay while its recorder is still live. The server
    // session-end payload is built synchronously below, then its score POST
    // waits for this best-effort upload so a new PB is not published ahead of
    // its verifier evidence. Idempotent with teardown paths that began it first.
    const finalReplayFlush = this._beginFinalScoreReplayFlush();
    const p = this._serverSessionEnd(finalReplayFlush);
    this._cheevosSessionSys = null;
    this._cheevosSessionHash = null;
    this._cheevosSessionStart = 0;
    return p;
  }

  /** AC1: the achievement session, marked loading for the room's list until it settles. */
  _startAchievementSession() {
    return cheevoPanel.startAchievementSession(this);
  }

  /**
   * Begin tracking the current ROM for achievements + play time.
   * Runs in solo mode and for P1 (host) in hosted netplay.
   * Safe to call repeatedly; second call will flush the first session first.
   */
  async _runAchievementSession() {
    const captureKey = roomSessionKey(this);
    const sessionStillCurrent = () => isCurrentSessionKey(this, captureKey);
    if (!sessionStillCurrent()) return;
    const isHost = this.state === State.SOLO ||
      (this.state === State.PLAYING && this.net?.playerNumber === 1);
    if (!isHost) return;
    if (!this.romHash) return;

    this._endAchievementSession(); // defensive: flush any prior session

    // Start the underlying play session (time + server heartbeats) — this
    // still runs for blocked systems below, since play-time tracking is
    // unrelated to the RAM-read correctness problem.
    this._startPlaySession();

    // Correctness gate, not a feature flag: some systems' RAM reads don't
    // land on the byte an achievement/score definition expects (see
    // achievement-support.js for why). Bail before fetching definitions or
    // touching engine state so we never report a garbage unlock or score.
    // Static id check only — a system that additionally needs the ACTUAL
    // core instance to prove itself (gba, via gbaTwoRegionCapable) can't be
    // decided here: _createEmulator's emu.init() resolves before this runs,
    // but the core's own memory-region pointers can still be a few frames
    // from ready (RetroArch's task queue drains content load after
    // callMain() returns), so a one-shot probe here would fail closed on a
    // perfectly good rebuilt core. That decision lives in the achievement
    // engine's tick warmup path instead (engine.js tick()), which already
    // tolerates exactly this kind of late-resolving core state and can
    // retry across several frames before giving up.
    if (!achievementsSupported(this.system.id)) return;
    // C64 arms only with a verified identity for the current hash (spec I3).
    // _endAchievementSession above already unloaded any earlier game.
    if (!c64TrackingAllowed(this)) return;

    // Load system definitions (cheevos JSON). Idempotent and cached —
    // first call fetches, subsequent calls resolve instantly.
    // X-KEY: everything below runs after awaits. The clip settles carry this
    // session's key, and each continuation checks it first (K), so a load
    // that finishes after another session started does nothing.
    try {
      await this.cheevosEngine.loadSystem(this.system.id);
    } catch {
      settleClipFromPaths(this, { key: captureKey });  // CR3: no WebM recorder starts this session
      return;
    }

    // Re-check state — might have changed during the await.
    const stillHost = this.state === State.SOLO ||
      (this.state === State.PLAYING && this.net?.playerNumber === 1);
    if (!stillHost || !this.emu || !sessionStillCurrent()) return;

    // Authored overlays (amended spec 9.3): the delivery starts now, beside the
    // seed fetches below, and loadGame waits for its published part at most
    // AUTHORED_PUBLISHED_WAIT_MS from here, so static tracking is never held
    // longer. The loader installs whatever arrives later (the delivery, an
    // admin's drafts) onto the running game, fenced by its load generation.
    // A loader chunk that arrives only after loadGame still loads the delivery,
    // within the deadline fixed here, and attaches it to the game already running.
    let gameLoadStarted = false;
    const authoredPublished = withTimeout(
      this._loadAuthoredForGame(sessionStillCurrent, () => gameLoadStarted), AUTHORED_PUBLISHED_WAIT_MS);

    // Seed unlock dedup from server (signed-in only, non-fatal).
    if (authState.getCurrentUser()) {
      this.cheevosStore.enableServerMode();
      try {
        const res = await fetch(
          `/api/achievements/game/${encodeURIComponent(this.system.id)}/${encodeURIComponent(this.romHash)}`,
          { credentials: 'include' },
        );
        if (!sessionStillCurrent()) return;
        if (res.ok) {
          const data = await res.json();
          if (!sessionStillCurrent()) return;  // K: a stale answer seeds nothing
          this.cheevosStore.seedUnlocks(this.system.id, this.romHash, data.achievements?.list || []);
        }
      } catch { if (!sessionStillCurrent()) return; /* non-fatal — loadGame still runs */ }
    }

    // BYO-ROM mode: if this ROM isn't in the static /cheevos/v2 file, fetch its
    // DB-backed trigger definitions (ingested from RetroAchievements) and merge
    // them so the live engine evaluates them like a bundled game. Gated on the
    // master switch being EXPLICITLY on (the flag system is fail-open, so never
    // auto-fetch when the flag is absent/unreachable). Best-effort.
    const byoHash = this.romHash;
    if (moduleRegistry.getFeatures().byo_rom_mode === true && typeof byoHash === 'string') {
      const sysCache = this.cheevosEngine._systemCache.get(this.system.id);
      if (!sysCache?.byHash.has(byoHash)) {
        try {
          const res = await fetch(
            `/api/achievements/defs/${encodeURIComponent(this.system.id)}/${encodeURIComponent(byoHash)}`,
          );
          if (!sessionStillCurrent()) return;
          if (res.ok) {
            const data = await res.json();
            if (!sessionStillCurrent()) return;  // K: a stale answer adds nothing
            if (data.achievements?.length) {
              this.cheevosEngine.addRuntimeGameDef(this.system.id, byoHash, {
                ...data.game,
                achievements: data.achievements,
              });
            }
          }
        } catch { if (!sessionStillCurrent()) return; /* non-fatal — loadGame still runs with no achievements */ }
      }
    }

    // The published delivery, if it settled within its cap (withTimeout never rejects).
    await authoredPublished;

    // K: nothing below awaits, so this one check covers loadGame and every
    // capture start below.
    if (!sessionStillCurrent()) return;
    gameLoadStarted = true;
    this.cheevosEngine.loadGame(this.emu, this.system.id, this.romHash);
    const loadedGame = this.cheevosEngine.getCurrentGame();
    if (loadedGame?.systemId === captureKey.system
        && primaryHash(loadedGame.romHash) === captureKey.romHash) {
      this._cheevoLoadedKey = { ...captureKey };
    }
    // The engine's disable survives loadGame but not unload() or a new
    // engine: restart the C64 cadence and re-apply this session's speed
    // latch or rewind disable either way (spec 4.8).
    this._c64EvalClock?.reset();
    applySessionTrackingLatch(this);
    this._updateAchievementsHUD();
    this._startAuthoredRefresh();

    this._scoreLeaderboard = null;
    this._scoreLeaderboardRank = null;
    this._scoreBaseline = null;
    this._podiumPBClipped = false;
    if (this.cheevosEngine.scoreSpec && authState.getCurrentUser()) {
      this._loadScoreLeaderboard();
    }

    // An NES replay saves only with a v2 claim, which reads the score through
    // this game's score spec (captureFrozenClaim in modules/replay-save.js).
    // Without a readable one, the first save would end recording anyway. End it
    // now through the one fallback, so touch hides 📹 instead of offering a clip
    // that cannot be saved, and desktop records WebM before the first milestone.
    // _startSolo does not await this session, so the recorder may not exist yet
    // (its module import pending or not started): _isRecordingReplay() covers
    // those, and _replayRecordingOff stops _initReplayRecorder from arming later.
    if (this.system.id === 'nes' && !scoreSpecSupported(this.cheevosEngine.scoreSpec)
        && (this.replayRecorder || this._isRecordingReplay())) {
      this._replayRecordingUnavailable('nes_v2_capability_unavailable');
    }

    if (this.state === State.SOLO
        && (this.cheevosEngine.getCurrentGame?.() || this.cheevosEngine.scoreSpec)) {
      this._initClipRecorder();
    } else {
      settleClipFromPaths(this, { key: captureKey });  // CR3: no WebM recorder starts this session
    }
    if (this.state === State.SOLO) {
      const recorderEmu = this.emu;
      const onRecorderArmError = (err) => {
        console.warn('[replay] achievement-session recorder arm failed:', err);
        if (this.state === State.SOLO && this.emu === recorderEmu && sessionStillCurrent()) {
          this._replayRecordingUnavailable('achievement_recorder_arm');
        }
      };
      try {
        const arm = this._initReplayRecorder();
        arm?.catch(onRecorderArmError);
      } catch (err) { onRecorderArmError(err); }
    }
  }

  async _loadScoreLeaderboard() {
    const sys = this.system?.id;
    const hash = Array.isArray(this.romHash) ? this.romHash[0] : this.romHash;
    if (!sys || !hash) return;
    try {
      const lookup = await fetch(`/api/games/lookup?system=${encodeURIComponent(sys)}&hash=${encodeURIComponent(hash)}`);
      if (!lookup.ok) return;
      const { game_id } = await lookup.json();
      if (!game_id) return;
      const res = await fetch(`/api/leaderboards/game/${game_id}/scores?limit=10`);
      if (!res.ok) return;
      const data = await res.json();
      if (this.romHash !== hash && (!Array.isArray(this.romHash) || this.romHash[0] !== hash)) return;
      const rows = data.leaderboard || [];
      this._scoreLeaderboard = rows.map(e => e.high_score);
      // Seed the player's current standing so we only clip genuinely new
      // milestones (mirroring the server's leaderboard-tracker dedup) — not every
      // rank she re-climbs to from zero at the start of each new session.
      const me = authState.getCurrentUser?.();
      const myRow = me ? rows.find(e => e.user_id === me.id) : null;
      if (myRow) {
        this._scoreLeaderboardRank = myRow.rank;
        this._scoreBaseline = Number(myRow.high_score);
      }
    } catch { /* non-fatal */ }
  }

  /**
   * Spec 9.3 (amended): start this load's authored overlays (achievements/authored-loader.js, lazy); settles with
   * its published part. Never rejects. The load's identity (this session's check and target) and its one deadline
   * are fixed here, before the chunk is awaited. `gameLoaded()` true once loadGame ran: a loader chunk that arrives
   * that late still fetches within what is left of that deadline and attaches the load to the game already
   * running (late installation), fenced by this session and the engine's load generation.
   */
  _loadAuthoredForGame(sessionStillCurrent, gameLoaded = () => false) {
    const target = {
      systemId: this.system.id, romHash: primaryHash(this.romHash), coreName: this.emu?.coreName || null,
      sessionStillCurrent, deadlineAt: performance.now() + AUTHORED_LOAD_DEADLINE_MS, attachRunning: gameLoaded,
    };
    return loadAuthoredLoaderModule().then((loader) => (loader ? loader.startAuthoredForGame(this, target).published : 'skipped'));
  }

  _startAuthoredRefresh() { authoredLoaderModule?.startAuthoredRefresh(this); }

  // ── Achievement authoring: one-line delegates (modules/authoring-host.js) ──
  // _startSolo calls the first four through `this`, so tests that run its source stub them.

  /** Spec 8.1, 12: take the pending author launch for this launch owner (one shot). */
  _takeAuthorLaunch(launchOwner) { return authoringHost.takeAuthorLaunch(this, launchOwner); }

  /** PR 2 README decision 5: this solo launch's draft test session. */
  _startDraftTestSession(options) { authoringHost.startDraftTestSession(this, options); }

  /** Spec 8.1: the author session profile for a taken author launch, when still available. */
  _beginAuthorLaunch(request) { return authoringHost.beginAuthorLaunch(this, request); }

  /** Spec 8.1: an author session's studio, once its core runs. */
  _startAuthorStudio() { return authoringHost.startAuthorStudio(this); }

  /** The loader installed or cleared drafts (PR 2 Task 1); the authoring host forwards it. */
  _onAuthoredDraftsChanged(selection) { authoringHost.onAuthoredDraftsChanged(this, selection); }

  /** The loader is about to replace the draft overlay (PR 2 Task 1): an open draft score run ends first. */
  _onAuthoredDraftsReplacing(info) { authoringHost.onAuthoredDraftsReplacing(this, info); }

  /** A discontinuity ends the author session's segment (spec 8.3); replay-boundary.js calls it at a reset. */
  _noteAuthorSegment(reason) { return authoringHost.noteAuthorSegment(this, reason); }

  _stopAuthoredRefresh() { authoredLoaderModule?.stopAuthoredRefresh(this); }

  /** The loader changed the running game's authored items (a refresh, an expiry or a late delivery). */
  _onAuthoredOverlayChanged() {
    // A late delivery for an authored-only game (amended spec 9.3): the engine holds this launch's
    // game only now, so record it as loaded (as after loadGame) and the signed-out list shows it.
    const key = roomSessionKey(this);
    const loadedGame = this.cheevosEngine?.getCurrentGame?.();
    if (isCurrentSessionKey(this, key) && loadedGame?.systemId === key.system
        && primaryHash(loadedGame.romHash) === key.romHash) {
      this._cheevoLoadedKey = { ...key };
    }
    this._updateAchievementsHUD();
    cheevoPanel.renderVisibleCheevoList(this);
  }

  /** Flush play-time, unload achievements engine, and end the play session. */
  _endAchievementSession() {
    const p = this._endPlaySession();
    this._stopAuthoredRefresh();
    this.cheevosEngine.unload();
    this._scoreLeaderboard = null;
    this._scoreLeaderboardRank = null;
    this._scoreBaseline = null;
    this._podiumPBClipped = false;
    this._loadAchievementsCard();
    return p;
  }

  // ── Live rewind ─────────────────────────────────────────

  /**
   * True when live rewind applies to the current session: solo play with nobody
   * watching (a rewind would desync input-relay spectators), not replay playback
   * or a ghost race, the flag on, and a JS-stepped core that can snapshot live.
   * That means the recordable solo cores — NES/SNES/GBA — but NOT self-driving
   * cores (GB in solo, N64, C64) nor the pump cores whose live serialize is
   * unsafe (atari2600/genesis report `canSnapshot === false`).
   */
  _rewindEnabled() {
    return this.state === State.SOLO
      && (this._spectators?.size || 0) === 0
      && !this._replayPlaybackActive
      && !this._ghostRaceActive
      && !!this.emu
      && !this.emu.selfDriving
      && this.emu.canSnapshot !== false
      && moduleRegistry.getFeatures().live_rewind === true
      // The author session profile turns live rewind off (spec 8.1, 8.2 SNES amendment).
      && !authoringHost.authorSessionActive(this);
  }

  _updateRewindButtonVisibility() {
    gameActions.refreshGameToolbarAvailability(this, 'rewind-visibility');
  }

  /** True if the loaded game tracks achievements or a high score. */
  _gameHasTrackedProgress() {
    const hasAch = (this.cheevosEngine?.getCurrentGame?.()?.total || 0) > 0;
    const hasScore = scoreSpecSupported(this.cheevosEngine?.scoreSpec);
    return hasAch || hasScore;
  }

  /** Capture a save-state at the snapshot interval. Cheap; safe every frame. */
  _maybeCaptureRewindSnapshot() {
    if (!this._rewindEnabled()) return;
    if (this.soloFrame % REWIND_SNAPSHOT_INTERVAL !== 0) return;
    let coreState;
    try { coreState = this.emu.saveState(); } catch { return; }
    if (!coreState) return;
    this._rewindRing.push({ frame: this.soloFrame, coreState });
    if (this._rewindRing.length > REWIND_RING_MAX) this._rewindRing.shift();
  }

  /**
   * Rewind tap handler. On a game that tracks progress, the first rewind shows a
   * confirm (it disables achievements/high-score for the session); on an
   * untracked game it rewinds silently. Reuses the same disable flag the HUD
   * "OFF" badge reads.
   */
  _rewindLive() {
    if (!this._rewindEnabled() || !this.emu?.loaded) return;
    if (!this._rewindRing.length) { this._flash('Nothing to rewind yet'); return; }
    if (!this._achievementsDisabledThisSession
        && !this._rewindConfirmed
        && this._gameHasTrackedProgress()) {
      showRewindConfirm(this);
      return;
    }
    this._doRewind();
  }

  _confirmRewind() {
    this._rewindConfirmed = true;
    hideRewindConfirm(this);
    if (!this._achievementsDisabledThisSession && this._gameHasTrackedProgress()) {
      this._achievementsDisabledThisSession = true;
      this._achievementsDisabledReason = 'rewind';
      this.cheevosEngine.disabled = true;
      this._updateAchievementsHUD();
      // AC5: a visible Achievements tab picks up its rewind notice now.
      cheevoPanel.renderVisibleRoomList(this);
      this._flash('Achievements disabled for this session');
    }
    this._doRewind();
  }

  async _doRewind() {
    const ring = this._rewindRing;
    if (!ring.length) { this._flash('Nothing to rewind yet'); return; }
    const target = this.soloFrame - REWIND_DEFAULT_SECONDS * REWIND_SNAPSHOT_INTERVAL;
    // Newest snapshot at or before target; else the oldest available (rewind as
    // far back as history allows when there's less than the full window).
    let snap = null;
    for (let i = ring.length - 1; i >= 0; i--) {
      if (ring[i].frame <= target) { snap = ring[i]; break; }
    }
    if (!snap) snap = ring[0];
    const boundary = this._beginSteppedReplayBoundary();
    if (!boundary) { this._flash('Rewind is busy. Try again.'); return; }
    if (!await this._awaitSteppedReplayBoundary(boundary)) {
      this._flash('Rewind could not reach a frame boundary');
      return;
    }
    let ok;
    try { ok = this.emu.loadState(snap.coreState); } catch { ok = false; }
    if (ok === false) {
      if (boundary) this._abortSteppedReplayBoundary(boundary, 'rewind_failed');
      this._flash('Rewind failed'); return;
    }
    this.soloFrame = snap.frame;
    this.frameAccumulator = 0;
    this.lastFrameTime = performance.now();
    // Drop now-future snapshots so a second rewind can't jump forward.
    this._rewindRing = ring.filter((s) => s.frame <= snap.frame);
    // A new base binds the post-rewind core state; the old source cannot be
    // extended across a discontinuity, even when its prefix was deterministic.
    if (boundary) {
      this._onEmulatorStateLoaded();
      void this._finishSteppedReplayBoundary(boundary, 'rewind');
    }
    this._flash('Rewound');
    const ind = document.getElementById('rewind-indicator');
    if (ind) {
      ind.classList.remove('hidden');
      clearTimeout(this._rewindIndicatorTimer);
      this._rewindIndicatorTimer = setTimeout(() => ind.classList.add('hidden'), 700);
    }
  }

  _flushAchievementPlayTime() {
    if (!this._cheevosSessionStart) return;
    const sys = this._cheevosSessionSys;
    const hash = this._cheevosSessionHash;
    if (!sys || !hash) return;
    const now = Date.now();
    const delta = now - this._cheevosSessionStart;
    this._cheevosSessionStart = now;
    if (delta > 0) {
      this.cheevosStore.addPlayTime(sys, hash, delta);
    }
  }

  /** Callback from AchievementEngine when a new achievement unlocks. */
  async _onAchievementUnlocked(ach) {
    // Snapshot the trigger frame BEFORE the async sync call — the game loop
    // keeps stepping frames during the await, so replayRecorder.endFrame would
    // drift past the actual milestone by the time _autoReplayCapture runs.
    const triggerFrame = this.replayRecorder?.endFrame;
    const captureGeneration = this._replaySessionGen;
    const captureRecorder = this.replayRecorder;
    const syncResult = await this._syncSingleUnlock(ach);
    // A validated answer that inserted nothing (already unlocked, not published, out of scope)
    // skips the celebration, also when its reconciliation then stopped ('partial', its failure
    // kept); a skipped or failed sync keeps the local unlock's.
    const validated = syncResult?.status === 'synced' || syncResult?.status === 'partial';
    if (validated && !syncResult.inserted) {
      if (syncResult.status === 'partial') {
        console.warn('[achievements] live unlock not celebrated; reconciliation incomplete:', syncResult.code, syncResult.cause ?? null);
      }
      this._serverAchCache = null;
      this._serverAchCacheTime = 0;
      return;
    }

    this._playAchievementChime();
    this.input.vibrate('achievement');
    this._updateAchievementsHUD();
    // The cached list and any fetch in flight predate this unlock.
    cheevoPanel.noteAchievementUnlocked(this);
    const captureCurrent = this._replaySessionGen === captureGeneration && this.replayRecorder === captureRecorder;
    if (ach.points > 1 && captureCurrent) {
      this._autoClipCapture('achievement', ach.name,
        this._autoReplayCapture('achievement', ach.name, triggerFrame));
    }
    let msg = `🏆 Achievement Unlocked: ${ach.name}`;
    if (syncResult?.rarity) msg += ` (${syncResult.rarity.rarity_percent}%, ${syncResult.rarity.rarity_tier})`;
    const shareMeta = syncResult?.achievement;
    const shareUser = authState.getCurrentUser();
    if (shareMeta?.game_id && shareMeta.slug && shareUser?.username && await socialSharingEnabled()) {
      this._flash(msg, {
        actionLabel: 'Share',
        onAction: () => openShareMenu({
          kind: 'achievement',
          username: shareUser.username,
          gameId: shareMeta.game_id,
          achSlug: shareMeta.slug,
          name: ach.name,
          points: ach.points || 0,
          gameTitle: shareMeta.game_title || this._activeGameTitle?.() || 'this game',
          rarityPercent: syncResult?.rarity?.rarity_percent ?? null,
          rarityTier: syncResult?.rarity?.rarity_tier ?? null,
        }),
      });
    } else {
      this._flash(msg);
    }
    this._serverAchCache = null;
    this._serverAchCacheTime = 0;
    this._loadAchievementsCard();
  }

  _onScoreUpdate(score) {
    // Zero is the initialised score floor, not a leaderboard result. On an
    // empty board it would otherwise compare as rank #1 and create a bogus
    // milestone as soon as score RAM became readable, before gameplay began.
    // Keep this aligned with AchievementStore.updateHighScore(), which only
    // persists positive scores.
    if (!Number.isFinite(score) || score <= 0) return;
    publishGameScore(this, score);

    const triggerFrame = this.replayRecorder?.endFrame;
    if (Number.isInteger(triggerFrame)
        && triggerFrame >= 0
        && (!this._sessionScorePeak || score > this._sessionScorePeak.score)) {
      this._sessionScorePeak = { score, triggerFrame };
    }

    const board = this._scoreLeaderboard;
    if (!board) return;
    let rank = board.findIndex(s => score > s) + 1;
    if (rank === 0 && board.length < 10) rank = board.length + 1;
    if (rank === 0) return;

    // Clip the same milestones the server records in its leaderboard tracker
    // (see _leaderboard-tracker.js maybeInsert): a genuine rank improvement, or —
    // only on the podium — a new personal best at an unchanged rank, once per
    // session (matching the single Discord post made at session end). Without the
    // seeded rank/baseline a returning podium player re-clips every rank she
    // climbs back up to from zero.
    const held = this._scoreLeaderboardRank;
    const PODIUM_RANK = 3;
    let triggerRef;
    if (!held || rank < held) {
      this._scoreLeaderboardRank = rank;
      triggerRef = `Reached #${rank}`;
    } else if (rank === held && rank <= PODIUM_RANK && !this._podiumPBClipped
               && this._scoreBaseline != null && score > this._scoreBaseline) {
      this._podiumPBClipped = true;
      triggerRef = `New best #${rank}`;
    } else {
      return; // a rank she already holds — nothing newsworthy
    }

    this._autoClipCapture(
      'high_score', triggerRef, this._autoReplayCapture('high_score', triggerRef, triggerFrame, score),
    );
  }

  // ── Clip Recorder ───────────────────────────────────────────────

  /**
   * Whether this solo session captures clips with the local WebM ClipRecorder (the
   * FALLBACK) rather than server-rendering them from the replay (Phase E). WebM is
   * the source UNLESS clips are fully server-rendered this session — which is exactly
   * what _milestoneClipFromReplay decides (render-capable system + a replay actually
   * recording + the server_rendered_clips cutover, or touch). So the fallback covers
   * the again-WebM contexts: GB/N64/C64 (no render core), Atari/Genesis Fast saves
   * and anon/offline (no replay recording), and any desktop session while the cutover
   * is still off — so flipping nothing on (the default) preserves today's WebM
   * behaviour exactly. Desktop-only: touch never runs the rolling-buffer recorder
   * (_clipsEnabled false).
   */
  _webmClipFallback() {
    if (!this._clipsEnabled) return false;
    return !this._milestoneClipFromReplay();
  }

  async _loadGameAudioCaptureClass() {
    const { GameAudioCapture } = await import('./audio-capture.js');
    return GameAudioCapture;
  }

  /** Prepare the shared audio tap before the emulator creates its context. */
  async _prepareClipAudioCapture(isLaunchCurrent = null) {
    const launchIsCurrent = () => !isLaunchCurrent || isLaunchCurrent();
    const captureNeeded = () => this._webmClipFallback() || !!this._soloRoomCode;
    if (!launchIsCurrent() || !captureNeeded() || this._clipAudioCapture) return;
    let GameAudioCapture;
    try {
      GameAudioCapture = await this._loadGameAudioCaptureClass();
    } catch {
      return;
    }
    if (!launchIsCurrent() || !captureNeeded() || this._clipAudioCapture) return;
    const capture = new GameAudioCapture();
    capture.prepare();
    if (!launchIsCurrent()) {
      capture.destroy();
      return;
    }
    this._clipAudioCapture = capture;
  }

  async _waitForClipAudioTrack(timeoutMs = 750) {
    const started = performance.now();
    while (performance.now() - started < timeoutMs) {
      if (this._clipAudioCapture?.getAudioTracks?.().length || this.audio?._gainNode) return;
      if (this.state !== State.SOLO || !this.running) return;
      await new Promise(r => setTimeout(r, 50));
    }
  }

  /**
   * CR3: the clip decision settles once the WebM recorder start ends, so every
   * exit of _startClipRecorder (and a throw) reaches the finally below. X-KEY:
   * it settles only the session (and game) this attempt started in. Attempts
   * run one at a time: an earlier one may still hold the module import and
   * may belong to an older session, whose recorder start the key check in
   * _startClipRecorder skips, so this attempt starts its own only after that
   * one ends (and the earlier attempt's own settle is dropped).
   */
  async _initClipRecorder() {
    const captureKey = roomSessionKey(this);
    const earlier = this._clipRecorderAttempt;
    const attempt = (async () => {
      if (earlier) await Promise.resolve(earlier).catch(() => {});
      if (!isCurrentSessionKey(this, captureKey)) return;
      await this._startClipRecorder(captureKey);
    })();
    this._clipRecorderAttempt = attempt;
    try {
      await attempt;
    } finally {
      if (this._clipRecorderAttempt === attempt) this._clipRecorderAttempt = null;
      settleClipFromPaths(this, { key: captureKey });
    }
  }

  async _startClipRecorder(key = roomSessionKey(this)) {
    if (resolvePresentationCapture(this, 'screenshot').kind !== 'ordinary') return;
    if (!isCurrentSessionKey(this, key)) return; // before any recorder teardown
    if (!this._webmClipFallback() || !this.canvas) return;
    if (this.clipRecorder) {
      // A recorder bound to a replaced canvas records only black frames —
      // rebuild it against the live canvas instead of keeping the stale one.
      if (this.clipRecorder.canvas === this.canvas) return;
      this._destroyClipRecorder();
    }
    if (this._clipRecorderLoading) return;
    this._clipRecorderLoading = true;

    let ClipRecorder;
    try {
      ({ ClipRecorder } = await import('./clip-recorder.js'));
    } catch {
      this._clipRecorderLoading = false;
      return;
    }
    this._clipRecorderLoading = false;
    // K: the import above and the audio wait below are awaits, so the recorder
    // starts only while this attempt's session key is still current.
    if (!isCurrentSessionKey(this, key)) return;
    if (resolvePresentationCapture(this, 'screenshot').kind !== 'ordinary') return;
    if (!this._webmClipFallback() || !this.canvas || this.clipRecorder || this.state !== State.SOLO) return;

    await this._waitForClipAudioTrack();
    if (!isCurrentSessionKey(this, key)) return;
    if (resolvePresentationCapture(this, 'screenshot').kind !== 'ordinary') return;
    if (!this._webmClipFallback() || !this.canvas || this.clipRecorder || this.state !== State.SOLO) return;

    const recorder = new ClipRecorder(this.canvas, {
      onPerfPause: () => { if (isCurrentSessionKey(this, key)) this._flash('Clip recording paused'); },
      getAudioTracks: () => isCurrentSessionKey(this, key) ? (this._clipAudioCapture?.getAudioTracks() || []) : [],
    });
    if (!isCurrentSessionKey(this, key) || resolvePresentationCapture(this, 'screenshot').kind !== 'ordinary') { recorder.stop(); return; }
    if (!recorder.start()) return;
    if (!isCurrentSessionKey(this, key) || resolvePresentationCapture(this, 'screenshot').kind !== 'ordinary') { recorder.stop(); return; }
    this.clipRecorder = recorder;
    this._clipAutoWarm = Date.now() + 10_000;
    this._clipAutoCool = 0;

    const btnClip = document.getElementById('btn-clip');
    if (btnClip) {
      btnClip.classList.remove('hidden');
      const dot = btnClip.querySelector('.clip-rec-dot');
      if (dot) dot.classList.remove('hidden');
    }
  }

  _destroyClipRecorder() {
    if (this.clipRecorder) {
      this.clipRecorder.stop();
      this.clipRecorder = null;
    }
    const btnClip = document.getElementById('btn-clip');
    if (btnClip) btnClip.classList.add('hidden');
    this._dismissClipToast();
  }

  /**
   * Genesis solo save-mode default. Local-only, read
   * synchronously at launch. 'replay' = lockstep recording (RPL1 saves, replays);
   * 'fast' = self-driving (instant state-blob saves, no replay). Defaults to
   * 'replay'. See docs/superpowers/specs/2026-06-17-solo-save-mode-toggle-design.md.
   */
  _savedSaveMode(systemId) {
    try {
      return localStorage.getItem('retroplay:saveMode:' + systemId) === 'fast' ? 'fast' : 'replay';
    } catch { return 'replay'; }
  }

  /** Persist the per-system solo save-mode default (local-only; see _savedSaveMode). */
  _setSavedSaveMode(systemId, mode) {
    try { localStorage.setItem('retroplay:saveMode:' + systemId, mode === 'fast' ? 'fast' : 'replay'); }
    catch { /* ignore */ }
  }

  /**
   * Whether the user has replay recording enabled. Read from localStorage
   * (mirrored from the server `replays_enabled` setting by the settings screen)
   * so the gate is synchronous at game start. Defaults to true when unset —
   * matches the server default and means a never-visited-settings user records.
   */
  _replaysRecordingEnabled() {
    try { return localStorage.getItem('retroplay:replaysEnabled') !== '0'; }
    catch { return true; }
  }

  /**
   * Whether new replays default to public. Mirrored from the server
   * `default_replay_public` setting into localStorage by the settings screen.
   * Defaults to true (matches the server default) when unset.
   */
  _defaultReplayPublic() {
    return replayViewer.defaultReplayPublic(this);
  }

  /**
   * Re-fetch feature flags from the server. moduleRegistry loads flags once at
   * boot and never refreshes on its own, so a flag an admin toggles mid-session
   * (e.g. atari_genesis_replay_saves) would never reach an already-open tab.
   * Called at the start of each solo session so a freshly-flipped recording flag
   * arms on the next game launch with no full page reload. Timeout-guarded and
   * error-swallowing: a slow or failed refresh must never block a game launch,
   * so on timeout/error we simply keep the flags already in the cache.
   */
  async _refreshFeatureFlags(timeoutMs = 1500) {
    try {
      await Promise.race([
        moduleRegistry.load(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('feature-flag refresh timed out')), timeoutMs)),
      ]);
    } catch {
      /* keep the cached flags — never block a game launch on a flag refresh */
    }
  }

  /**
   * Whether replay mode (the JS-stepped lockstep recording path) is even an
   * OPTION for this solo session: recording-gated, signed-in, allowlisted
   * system, atari/genesis flag on, joypad-only title, not replay/ghost playback.
   * The mode CHOICE (replay vs fast) is layered on top in _shouldRecordSolo.
   */
  _replayModeAvailable(coreName = resolveCoreName(this.system)) {
    if (coreName === 'picodrive' || (this.system?.id === 'genesis' && isGenesis32xRom(this.romData))) return false;
    if (!this._replaysEnabled) return false;
    if (!this._replaysRecordingEnabled()) return false;
    if (!authState.getCurrentUser()?.id) return false;
    if (!LOCKSTEP_SOLO_RECORD_SYSTEMS.has(this.system?.id)
        && !SELF_DRIVING_RECORD_SYSTEMS.has(this.system?.id)) return false;
    // Atari and legacy Genesis Plus GX replay-derived saves are flag-gated.
    // ClownMDEmu serializes directly while recording, so it does not depend on
    // that workaround flag and can provide replay + instant saves together.
    if ((this.system?.id === 'atari2600'
          || (this.system?.id === 'genesis' && coreName !== 'clownmdemu'))
        && moduleRegistry.getFeatures().atari_genesis_replay_saves !== true) {
      return false;
    }
    // GB recording is fail-CLOSED behind gb_replay_recording: GB records
    // via the SELF-DRIVING input-log ring (no JS-stepping — stepping gambatte
    // traps in-browser; parity proven by verify-gb-selfdrive-record-spike.mjs),
    // and the flag is that ring path's deliberate kill switch. Explicit
    // === true, like the atari/genesis gate above — an absent flag must
    // stay OFF.
    if (this.system?.id === 'gb'
        && moduleRegistry.getFeatures().gb_replay_recording !== true) {
      return false;
    }
    if (this.system?.id === 'n64'
        && moduleRegistry.getFeatures().n64_replays !== true) {
      return false;
    }
    if (this._pendingReplayId || this._pendingGhostId) return false;
    const hash = Array.isArray(this.romHash) ? this.romHash[0] : this.romHash;
    if (!hash) return false;
    // Non-joypad Atari (paddle/driving/light-gun/keypad) and Genesis light-gun
    // titles read analog/pointer input the uint16 lockstep log can't capture —
    // they can't record, so replay mode isn't available; they self-drive.
    if (this.system?.id === 'atari2600' && ATARI_NON_JOYPAD_ROM_HASHES.has(hash)) return false;
    if (this.system?.id === 'genesis' && GENESIS_NON_JOYPAD_ROM_HASHES.has(hash)) return false;
    return true;
  }

  /**
   * Resolve the solo save mode for this launch: 'fast' when replay mode isn't
   * available; otherwise the one-shot override (set by a save load) if present,
   * else the user's saved per-system default.
   */
  _resolveSoloMode(coreName = resolveCoreName(this.system)) {
    if (!this._replayModeAvailable(coreName)) return 'fast';
    return this._soloModeOverride ?? this._savedSaveMode(this.system?.id);
  }

  /**
   * Whether this solo session records. Genesis still follows its save-mode
   * choice; Atari records on its combined self-driving path.
   */
  _shouldRecordSolo(coreName = resolveCoreName(this.system)) {
    // The author session profile records no solo replay (spec 8.1).
    if (authoringHost.authorSessionActive(this)) return false;
    if (!this._replayModeAvailable(coreName)) return false;
    if (this.system?.id === 'genesis' && coreName === 'clownmdemu') {
      return true;
    }
    if (this.system?.id === 'atari2600') return true;
    if (this.system?.id === 'genesis') {
      return this._resolveSoloMode(coreName) === 'replay';
    }
    return true;
  }

  /** A loaded self-driving save lacks a native post-pump frame acknowledgment,
   * so this launch cannot start a faithful replay recording. */
  _selfDrivingRecordArmBlocked({ saveApplied, asyncGameTurnPending }) {
    return !!saveApplied || !!asyncGameTurnPending;
  }

  /**
   * A blocked self-driving arm at launch. With a save applied (GB + flag off,
   * N64 + save, unsupported core) recording is over for the session and MUST
   * end through the one fallback (spec §1): WebM clip path + 📹 — never the
   * "no recorder, _replayRecordingOff still false" limbo. Without a save (async
   * turn, replay prefix) today's behaviour stands: just leave record mode.
   */
  _applySelfDrivingArmBlock({ saveApplied }) {
    if (saveApplied) this._replayRecordingUnavailable('selfdriving_launch_blocked');
    else this._soloRecordMode = false;
  }

  /** A replay is live-playable in-browser when we have a frame-stepped core for
   *  its system — jsnes (NES) or a lockstep libretro core (the solo-record set) —
   *  or a self-driving playback core (GB: the core-side playback buffer), the
   *  latter fail-closed behind gb_replay_live_playback. */
  _isLivePlayable(systemId) {
    if (systemId === 'gbc') systemId = 'gb';
    if (systemId === 'nes' || LOCKSTEP_SOLO_RECORD_SYSTEMS.has(systemId)) return true;
    if (!SELF_DRIVING_PLAYBACK_SYSTEMS.has(systemId)) return false;
    const features = moduleRegistry.getFeatures();
    if (systemId === 'gb') return features.gb_replay_live_playback === true;
    if (systemId === 'n64') return features.n64_replays === true;
    return false;
  }

  /**
   * Whether the LINK-CABLE multiplayer path must drive the libretro core via the
   * stepped LockstepLibretroCore (frame barrier) instead of letting it self-drive.
   * Today only GBA: mGBA's burst-validating link protocols (Mario Kart beacon
   * arithmetic, F-Zero commit groups) require both emulated clocks to advance in
   * step; GB/GBC self-drive and poll SIO registers.
   *
   * Deliberately keyed on the SYSTEM ID, NOT on LOCKSTEP_SOLO_RECORD_SYSTEMS. Solo
   * replay recording (that set) and link-cable lockstep are INDEPENDENT mechanisms
   * that merely both use the stepped core for GBA. Changing the solo-record set
   * must never alter link-cable behaviour — e.g. SNES is in the record set but is
   * not a link-cable system, and if GBA were ever dropped from the record set,
   * link-cable must still drive it stepped. Used by _startLinkCable's
   * _createEmulator({ lockstep }) call.
   */
  _linkCableNeedsSteppedCore(systemId = this.system?.id) {
    return systemId === 'gba';
  }

  /**
   * Whether this core cannot self-drive at all, so solo play (recording or
   * not) and the 2-player hosted host must drive it through the stepped
   * LockstepLibretroCore instead.
   *
   * snes9x2010 ships as an ASYNCIFY_IGNORE_INDIRECT build (docker/build-core.sh
   * applies the flag to every core it does not exclude), which leaves its
   * dynCall_v export uninstrumented. A self-driving RetroArch main loop enters
   * the wasm through dynCall_v(fn), and with the stock audio_sync = true the
   * OpenAL driver's al_write sleeps (emscripten_sleep) whenever its buffer
   * queue is full: on nearly every frame above 60Hz, at boot while the
   * AudioContext is still suspended (it cannot drain), and in a burst during
   * some 60Hz boots. On wake, Asyncify.doRewind calls
   * dynCall_v() again with no argument, which calls table slot 0 and traps
   * "null function", so the FIRST sleep ends the session. The stepped core
   * writes audio_sync = "false" and never sleeps. (The lean gambatte builds
   * share the defect but cannot be stepped; they rely on the sleepless config
   * in LibretroCore._writeConfig instead.)
   *
   * Keyed on the CORE, not the system or LOCKSTEP_SOLO_RECORD_SYSTEMS: this is
   * a property of one binary. A full-ASYNCIFY rebuild of snes9x2010 (with
   * replay-checkpoint requalification) would let SNES self-drive again.
   */
  _coreCannotSelfDrive(coreName) {
    return coreName === 'snes9x2010';
  }

  /** Which viewer slot to show for a replay in the detail modal:
   *  'live' (boot a core), 'video' (mp4), or 'none' (coming soon). */
  _replayViewerMode(replay) {
    if (replay.superunoPublicOnly === true) return replay.has_video ? 'video' : 'none';
    if (this._unsafeReplayBoundary) return replay.has_video ? 'video' : 'none';
    const fromSave = !!(replay.from_save || replay.base_save_sha256);
    if (this._isLivePlayable(replay.system_id) && (!fromSave || this._isBaseStatePlayable(replay.system_id, replay.core_name))) return 'live';
    if (replay.has_video) return 'video';
    return 'none';
  }

  /** Build + initialise an ISOLATED core bound to `canvas` for inline replay
   *  playback — frame-stepped (NES/lockstep set) or self-driving (GB, played
   *  via the core-side playback buffer). Not wired into this.emu / the solo
   *  lifecycle. */
  async _buildReplayCore(systemId, canvas, coreName = null, romBytes = null, lifecycle = undefined) {
    const check = () => { if (this._unsafeReplayBoundary) throw new Error('Reload the page to play again.'); };
    check();
    const { buildReplayCore } = await import('./cores/replay-core.js');
    check();
    const multitap = systemId === 'snes' && coreName === 'snes9x2010_multitap';
    if (multitap && this._multitapRetirement) {
      const retired = await this._multitapRetirement;
      check();
      if (retired?.ok === false) throw new Error('Reload the page to play again.');
    }
    const initialize = async () => {
      const core = lifecycle === undefined
        ? await buildReplayCore(systemId, canvas, coreName, resolveCoreName, romBytes)
        : await buildReplayCore(systemId, canvas, coreName, resolveCoreName, romBytes, lifecycle);
      try { check(); }
      catch (error) {
        try { await core.destroy?.(); } catch (cleanupError) { error.cleanupErrors = [cleanupError]; }
        throw error;
      }
      return core;
    };
    const work = initialize();
    const retirement = multitap ? work.then(() => ({ ok: true }), error => {
      const ok = !error.cleanupErrors?.length;
      if (!ok) this._unsafeReplayBoundary ||= true;
      return { ok };
    }) : null;
    if (retirement) this._multitapRetirement = retirement;
    let core;
    try { core = await work; }
    finally { if (retirement && this._multitapRetirement === retirement) this._multitapRetirement = null; }
    // The pinned viewer awaits audio and native setup after construction. Fence
    // its actual native entry points while leaving owned teardown available.
    const guarded = new Set(['loadROM', 'reset', 'loadState', 'stepFrame', 'frame', 'setButtons',
      'armReplayPlayback', 'armN64ReplayPlayback', 'beginReplayBoundary', 'stateFingerprint', 'restoreReplayCheckpoint']);
    const module = core.module;
    const guardedModule = module && new Proxy(module, {
      get(target, property) {
        const value = Reflect.get(target, property, target);
        if (typeof value !== 'function') return value;
        return property === 'resumeMainLoop' ? (...args) => { check(); return value.apply(target, args); } : value.bind(target);
      },
      set: (target, property, value) => Reflect.set(target, property, value, target),
    });
    return new Proxy(core, {
      get(target, property) {
        if (property === 'selfDriving' || property === 'stateFingerprint') check();
        if (property === 'module') return guardedModule;
        const value = Reflect.get(target, property, target);
        if (typeof value !== 'function') return value;
        return guarded.has(property) ? (...args) => { check(); return value.apply(target, args); } : value.bind(target);
      },
      set: (target, property, value) => Reflect.set(target, property, value, target),
    });
  }

  /** Fetch ROM bytes for a replay (zip-aware), for inline playback. Throws on
   *  unavailability so the caller can fall back to the mp4. */
  async _fetchReplayRomBytes(systemId, romFilename) {
    if (!romFilename) throw new Error('no rom filename');
    // GBC is a catalog alias; the download route serves both formats under gb.
    const playbackSystem = SYSTEMS[systemId]?.id || systemId;
    const res = await fetch(`/api/roms/${playbackSystem}/${encodeURIComponent(romFilename)}`);
    if (!res.ok) throw new Error(`rom fetch ${res.status}`);
    let data = new Uint8Array(await res.arrayBuffer());
    if (romFilename.toLowerCase().endsWith('.zip')) {
      const extracted = this._extractRomFromZip(data, SYSTEMS[playbackSystem] || this.system);
      if (!extracted) throw new Error('no rom in zip');
      data = extracted.data;
    }
    return data;
  }

  /** Fail-closed client gate for snapshotting a base (spec §6). */
  _baseStatesEnabled() {
    return moduleRegistry.getFeatures().replay_base_states === true;
  }

  _gameplayHeld() { return replayBoundary.gameplayHeld(this); }
  _logBoundaryFailure(fields) { return replayBoundary.logBoundaryFailure(this, fields); }
  _beginResetHold(origin, opts = {}) { return replayBoundary.beginResetHold(this, { origin, ...opts }); }
  _acknowledgeResetFrame(owner) { return replayBoundary.acknowledgeFrame(this, owner); }
  _commitReset(owner) { return replayBoundary.commitReset(this, owner); }
  _armAfterResetCommit(owner, opts = {}) { return replayBoundary.armAfterCommit(this, owner, opts); }
  _releaseResetHold(owner) { return replayBoundary.releaseHold(this, owner); }
  _resetStopMessage() { return replayBoundary.resetStopMessage(this); }

  _clearPendingBaseGeneration() {
    this._pendingBaseGeneration?.releasePending?.();
    this._pendingBaseGeneration = null;
  }

  /**
   * A save state was applied to the running core (launch-with-save or the
   * Saves panel). Order matters (spec §1): drop any parked generation, set the
   * hint, drop live-rewind snapshots (they predate the base), tear down the
   * recorder so the next _initReplayRecorder starts a fresh session.
   */
  _onEmulatorStateLoaded() {
    this._clearPendingBaseGeneration();
    this._replayBaseStateLoaded = true;
    this._rewindRing = [];
    // A new base was loaded → this starts a fresh recording generation. Advance
    // the session token BEFORE tearing the old recorder down: any
    // in-flight finalizer captured against the PRIOR session (its opts._gen)
    // must fail the isCurrent gen-match and stay stale in the null-recorder
    // window between here and the next _initReplayRecorder, rather than briefly
    // regaining authority via allowAfterTeardown (app.replayRecorder === null &&
    // gen still matching). The legitimate teardown flush captures its own gen at
    // _saveReplay call time and never routes through here, so it is unaffected.
    this._replaySessionGen = (this._replaySessionGen || 0) + 1;
    this._destroyReplayRecorder();
  }

  /** Stop JS stepping before a reset, in-place load, or rewind changes state. */
  _beginSteppedReplayBoundary() {
    if (!this.emu || this.emu.selfDriving || this._gameplayHeld()) return null;
    // Keep three seconds of the 15-second boundary budget for failure cleanup.
    const boundary = { emu: this.emu, deadline: replayBoundary.monoNow() + replayBoundary.STAGE_MS };
    this._replayBoundaryOwner = boundary;
    this._stateCapturePending = true;
    this.frameAccumulator = 0;
    this._replaySessionGen = (this._replaySessionGen || 0) + 1;
    this._destroyReplayRecorder();
    return boundary;
  }

  /** Apply a self-driving reset/load and re-arm from an acknowledged native
   * post-pump boundary. Legacy cores lack that acknowledgment and stay on the
   * existing playback-only fallback. The pump snapshot is atomic with Atari
   * and N64 load/reset; GB can snapshot synchronously while the loop is held. */
  async _runSelfDrivingReplayBoundary(operation, state = null, restoredFrame = 0,
    deadline = Date.now() + 12000, signal = null, authority = {}) {
    const emu = this.emu;
    if (this._gameplayHeld() || operation === 'reset' && this._statePumpQueue) {
      // The pinned load consumer catches a rejected acquisition; a returned
      // unsupported result would authorize its raw legacy load fallback.
      if (operation === 'load') throw new Error('Save is busy. Try again.');
      return { applied: false, recorded: false, safeToMutate: false, unsafeToMutate: false, failure: { stage: 'preflight', cause: 'busy' } };
    }
    if (!emu?.selfDriving || typeof emu.beginReplayBoundary !== 'function') return { applied: false, recorded: false,
      safeToMutate: !!emu?.selfDriving && !this._gameplayHeld()
        && emu.loaded !== false && emu._replayBoundaryUnsafe !== true
        && typeof emu.beginReplayBoundary !== 'function', unsafeToMutate: false,
      failure: { stage: 'preflight', cause: 'unavailable' } };
    // The real pinned implementation returns before submission when these
    // exports are absent. Establish that capability proof before calling it;
    // no arbitrary callable or fulfilled null grants direct-reset permission.
    if (operation === 'reset' && emu.beginReplayBoundary === LibretroCore.prototype.beginReplayBoundary
        && emu.loaded === true && emu._replayBoundaryUnsafe !== true && emu.module
        && ['_retroplay_replay_boundary_request', '_retroplay_replay_boundary_reset_request',
          '_retroplay_replay_boundary_status', '_retroplay_replay_boundary_cancel', 'resumeMainLoop']
          .some(name => typeof emu.module[name] !== 'function')) {
      return { applied: false, recorded: false, safeToMutate: true, unsafeToMutate: false,
        failure: { stage: 'preflight', cause: 'unavailable' } };
    }
    // §2 Clock: convert the pinned caller's wall-clock deadline before the first
    // await, and give the pinned native wait a derived wall-clock deadline plus
    // a signal that the monotonic watchdog aborts.
    const deadlineLease = replayBoundary.retainMonotonicDeadline(this, deadline);
    const monoDeadline = deadlineLease.deadline;
    const watchdog = replayBoundary.deadlineSignal(monoDeadline, signal);
    const boundary = { emu, deadline: monoDeadline };
    this._replayBoundaryOwner = boundary;
    this._stateCapturePending = true;
    this._replaySessionGen = (this._replaySessionGen || 0) + 1;
    authority.generation = this._replaySessionGen;
    const launch = this._gameLaunchIntentGeneration, session = this._lockstepSession;
    const current = () => this.emu === emu && this._replayBoundaryOwner === boundary
      && this._replaySessionGen === authority.generation
      && this._gameLaunchIntentGeneration === launch && this._lockstepSession === session;
    let applied = false;
    let unsafeToMutate = false;
    let boundaryCallStarted = false;
    let boundarySubmitted = false;
    let stage = 'native';
    try {
      this._destroyReplayRecorder();
      if (signal?.aborted || replayBoundary.monoNow() >= monoDeadline) return { applied: false, recorded: false,
        failure: { stage: 'queue', cause: 'deadline' } };
      const pump = this.system?.id === 'atari2600' || this.system?.id === 'n64';
      boundaryCallStarted = true;
      const native = await replayBoundary.raceDeadline(emu.beginReplayBoundary({ operation,
        state: pump && operation === 'load' ? state : null,
        snapshot: pump, deadline: replayBoundary.wallDeadlineFor(monoDeadline), signal: watchdog.signal }), monoDeadline);
      if (native.timedOut) {
        watchdog.abort();
        unsafeToMutate = true;   // the submitted native pump may still be active
        throw new Error('boundary deadline');
      }
      if (native.error) throw native.error;
      const stopped = native.value;
      const loadedDescriptor = stopped && Object.getOwnPropertyDescriptor(stopped, 'loaded');
      const loaded = loadedDescriptor && Object.hasOwn(loadedDescriptor, 'value') ? loadedDescriptor.value : undefined;
      if (loaded !== true && loaded !== false) {
        unsafeToMutate = true;
        return { applied: false, recorded: false, unsafeToMutate,
          failure: { stage: 'native', cause: 'unverified' } };
      }
      boundarySubmitted = stopped.submitted !== false;
      unsafeToMutate = loaded === false && boundarySubmitted;
      if (signal?.aborted || !current()) {
        unsafeToMutate = boundarySubmitted;
        return { applied: false, recorded: false, unsafeToMutate,
          failure: { stage: 'session', cause: signal?.aborted ? 'aborted' : 'stale' } };
      }
      if (loaded === false) {
        // A request which reached native code may still be inside an Asyncify
        // pump after a timeout. Even an acknowledged core refusal does not
        // authorize a second mutation on this instance.
        return { applied: false, recorded: false, unsafeToMutate, safeToMutate: !boundarySubmitted,
          failure: stopped.failure || { stage: 'load', cause: 'core_refused' } };
      }
      if (!pump && operation === 'load') {
        unsafeToMutate = true;
        if (emu.loadState(state) === false) {
          return { applied: false, recorded: false, unsafeToMutate,
            failure: { stage: 'load', cause: 'core_refused' } };
        }
        unsafeToMutate = false;
      }
      applied = true;
      stage = 'snapshot';
      this._clearPendingBaseGeneration();
      this._rewindRing = [];
      // A reset or load invalidates N64's clean-boot clip prefix even if its
      // regular RPA1 input log can continue from a new base.
      if (this.system?.id === 'n64') {
        emu.freezeN64ClipRecording?.();
        if (emu._n64ClipRecording) emu._n64ClipRecording.valid = false;
      }
      const baseBytes = pump ? stopped.stateBytes : this._captureReplayBaseStateBytes();
      const frame = operation === 'reset' ? 0 : Math.max(0, restoredFrame | 0);
      this.soloFrame = frame;
      this._libretroSoloFrame = frame;
      this._recordFrameOffset = frame;
      this._recordDrainCursor = 0;
      this._recordResumePrefix = null;
      // N64 has no headless base-state verifier or render consumer yet.
      // Keep gameplay after reset/load, but never publish a base-backed source.
      if (this.system?.id === 'n64') {
        this._replayRecordingUnavailable('n64_base_consumer_unqualified');
        return { applied: true, recorded: false,
          failure: { stage: 'consumer', cause: 'n64_base_unqualified' } };
      }
      if (!baseBytes || !this._baseStatesEnabled() || !isValidCoreBuildId(emu.coreBuildId)
          || replayBoundary.monoNow() >= boundary.deadline) {
        this._replayRecordingUnavailable('selfdriving_base_unavailable');
        return { applied: true, recorded: false,
          failure: stopped.failure || { stage: 'snapshot', cause: 'unavailable' } };
      }
      stage = 'ring';
      const ringArmed = this.system?.id === 'n64'
        ? !!emu.enableN64RecordingRing?.() : !!emu.enableRecordingRing?.();
      if (!ringArmed) {
        this._replayRecordingUnavailable('selfdriving_ring_arm');
        return { applied: true, recorded: false,
          failure: { stage: 'ring', cause: 'arm_failed' } };
      }
      this._pendingBaseGeneration = this._newBaseGeneration(baseBytes, emu.coreBuildId);
      this._replayBaseStateLoaded = false;
      stage = 'recorder';
      const remaining = boundary.deadline - replayBoundary.monoNow();
      if (remaining <= 0) throw new Error('boundary deadline');
      let timer;
      try {
        await Promise.race([this._initReplayRecorder(), new Promise((_, reject) => {
          timer = setTimeout(() => reject(new Error('boundary deadline')), remaining);
        })]);
      } finally { clearTimeout(timer); }
      if (!current()) return { applied, recorded: false, failure: { stage: 'session', cause: 'stale' } };
      if (this._replayRecordingOff
          || replayBoundary.monoNow() >= boundary.deadline
          || !this.replayRecorder?.isRecording) throw new Error('recorder did not arm');
      return { applied: true, recorded: true };
    } catch (error) {
      if (boundaryCallStarted && !applied) unsafeToMutate = true;
      if (current()) {
        this._replaySessionGen = (this._replaySessionGen || 0) + 1;
        authority.generation = this._replaySessionGen;
        this._replayRecordingUnavailable('selfdriving_boundary_failed');
      }
      return { applied, recorded: false, ...(unsafeToMutate ? { unsafeToMutate } : {}), failure: { stage,
        cause: error?.message === 'boundary deadline' ? 'deadline' : 'exception',
        message: String(error?.message || error) } };
    } finally {
      try {
        if (!current() || unsafeToMutate) emu.quarantineReplayBoundary?.();
        else emu.endReplayBoundary?.();
      }
      catch (error) {
        unsafeToMutate = true;
        try { emu.quarantineReplayBoundary?.(); } catch { /* reload still required */ }
        if (current()) {
          this._replaySessionGen = (this._replaySessionGen || 0) + 1;
          authority.generation = this._replaySessionGen;
          this._replayRecordingUnavailable('selfdriving_release_failed');
        }
        return { applied, recorded: false, unsafeToMutate, failure: { stage: 'release', cause: 'exception',
          message: String(error?.message || error) } };
      } finally {
        watchdog.dispose();
        deadlineLease.release();
        if (current()) {
          this._replayBoundaryOwner = null;
          this._stateCapturePending = false;
        }
      }
    }
  }

  /** Keep an unacknowledged native state pump isolated until a fresh document
   * replaces its WASM instance. A same-page teardown may call back into WASM. */
  _haltUncertainReplayBoundary(emu, reason) {
    if (this.emu !== emu) return;
    this._replayRecordingUnavailable(reason);
    this._unsafeReplayBoundary = emu;
    emu.quarantineReplayBoundary?.();
    this.running = false;
    this._flash('Emulator state could not be confirmed. Reloading to recover…');
    try { this._reloadAfterUncertainBoundary(); } catch { /* stay halted if navigation is blocked */ }
  }

  _reloadAfterUncertainBoundary() { window.location.reload(); }

  _abortSteppedReplayBoundary(boundary, reason) {
    if (this._replayBoundaryOwner !== boundary || this.emu !== boundary.emu) return;
    this._replaySessionGen = (this._replaySessionGen || 0) + 1;
    this._replayBoundaryOwner = null;
    this._stateCapturePending = false;
    this._replayRecordingUnavailable(reason);
  }

  async _awaitSteppedReplayBoundary(boundary) {
    try {
      while (this._replayBoundaryOwner === boundary && this.emu === boundary.emu) {
        if (replayBoundary.monoNow() >= boundary.deadline) break;
        if (typeof boundary.emu.stepReady !== 'function' || boundary.emu.stepReady()) return true;
        await new Promise(resolve => setTimeout(resolve, Math.min(16,
          Math.max(1, boundary.deadline - replayBoundary.monoNow()))));
      }
    } catch { /* An uncertain frame completion cannot authorize a new source. */ }
    this._abortSteppedReplayBoundary(boundary, 'frame_boundary_unavailable');
    return false;
  }

  async _finishSteppedReplayBoundary(boundary, operation) {
    if (this._replayBoundaryOwner !== boundary) return false;
    if (this.emu !== boundary.emu) {
      this._replaySessionGen = (this._replaySessionGen || 0) + 1;
      this._replayBoundaryOwner = null;
      this._stateCapturePending = false;
      return false;
    }
    const remaining = Math.max(0, boundary.deadline - replayBoundary.monoNow());
    let timer;
    try {
      if (!remaining) throw new Error('boundary deadline');
      await Promise.race([
        this._initReplayRecorder(),
        new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('boundary deadline')), remaining); }),
      ]);
      if (this._replayBoundaryOwner !== boundary || this.emu !== boundary.emu) return false;
      if (replayBoundary.monoNow() >= boundary.deadline) throw new Error('boundary deadline');
      if (this._replaysEnabled && this._replaysRecordingEnabled()
          && !this._replayRecordingOff && !this.replayRecorder?.isRecording) {
        throw new Error('recorder did not arm');
      }
      return true;
    } catch (error) {
      this._abortSteppedReplayBoundary(boundary, `${operation}_boundary_failed`);
      return false;
    } finally {
      clearTimeout(timer);
      if (this._replayBoundaryOwner === boundary) {
        this._replayBoundaryOwner = null;
        this._stateCapturePending = false;
      }
      this.frameAccumulator = 0;
      this.lastFrameTime = performance.now();
    }
  }

  _captureReplayBaseStateBytes(opts = {}) { return captureBaseStateBytes(this.emu, opts); }
  _captureReplayBaseStateResult(opts = {}) { return captureBaseStateResult(this.emu, opts); }

  _newBaseGeneration(bytes, originBuild, recorder = null) {
    return newBaseGeneration({ bytes, originBuild, emu: this.emu, app: this, recorder });
  }

  /**
   * The ONE routine through which a session's recording ends for good (spec §1
   * Fallback): null snapshot, flag off after a load, GB ring-arm failure,
   * Saves-panel re-arm failure, digest failure. Leaves the session exactly where
   * a replays-disabled session is: WebM clip path + 📹 on desktop.
   */
  _replayRecordingUnavailable(reason) {
    // CR3: settle first, so the stop event below carries the final reason.
    settleReplayDecision(this, 'off', { code: reason });
    this._replayRecordingOff = true;
    this._soloRecordMode = false;
    this._clearPendingBaseGeneration();
    this._destroyReplayRecorder();
    dispatchReplayRecording(this, { recording: false });
    console.info('[replay] recording unavailable this session:', reason);
    this._initClipRecorder();
  }

  /** CR5: `{ decided, recording, reason }` for the room's Clips & Replays panel. */
  _replayRecordingState() {
    return replayRecordingState(this);
  }

  /**
   * GB only: snapshot + enableRecordingRing() in ONE synchronous turn, so the
   * base is the state at ring frame 0. Parks the generation for
   * _initReplayRecorder (which runs later, async). Returns the arm result.
   */
  _armRecordRingWithBaseState() {
    const emu = this.emu;
    if (!this._replayBaseStateLoaded || !this._baseStatesEnabled()) return !!emu?.enableRecordingRing?.();
    const bytes = this._captureReplayBaseStateBytes();
    if (!bytes) return false;
    // A base state must carry a valid core-build provenance (spec §3). Without a
    // real generated coreBuildId we cannot attribute the state, so fail the arm
    // (→ the no-recording fallback) rather than parking a generation stamped
    // 'unknown'. Checked before arming the ring so nothing is left half-armed.
    if (!isValidCoreBuildId(emu?.coreBuildId)) return false;
    if (!emu.enableRecordingRing?.()) return false;
    this._clearPendingBaseGeneration();
    this._pendingBaseGeneration = this._newBaseGeneration(bytes, emu.coreBuildId);
    this._replayBaseStateLoaded = false;
    return true;
  }

  /**
   * A boot-start self-driving record ring failed to arm (GB/Atari
   * `enableRecordingRing`, N64 `enableN64RecordingRing`). This is a PERMANENT
   * recorder-arm failure, so — like every other permanent exit — it ends
   * recording through the ONE fallback (spec §1 Fallback): `_replayRecordingOff`,
   * teardown, WebM clip path. The prior code only reached the fallback when a
   * base state had already been loaded (`_replayBaseStateLoaded`); a plain
   * boot-start failure fell through with just `this._soloRecordMode = false`,
   * leaving `_replayRecordingOff` false and WebM down — an inconsistent
   * predictive-replay + manual-clip-ownership limbo.
   */
  _onSelfDrivingRingArmFailed() {
    this._replayRecordingUnavailable(`${this.system?.id || 'selfdriving'}_ring_arm`);
  }

  /** Base-state replays play live on stepped cores or qualified native playback. */
  _isBaseStatePlayable(systemId, coreName = null) {
    if (['gb', 'gbc'].includes(systemId) && coreName === 'gambatte_mp') return true;
    if (systemId === 'n64') return !coreName || coreName === 'mupen64plus_next';
    return systemId === 'nes' || LOCKSTEP_SOLO_RECORD_SYSTEMS.has(systemId);
  }

  /**
   * Initialise the replay recorder for the current session. Lazy-imports the
   * module on first use. The recorder runs continuously from this point until
   * _destroyReplayRecorder() — observe() calls in the solo loop and the
   * lockstep onApplyInputs hook feed it. Nothing is uploaded until the user
   * triggers _saveReplay() or an auto-capture milestone fires.
   *
   * @param {object} [opts]
   * @param {boolean} [opts.isNetplay]
   * @param {string} [opts.opponentUserId]
   */
  _loadReplayRecorderModule() { return import('./replay-recorder.js'); }

  async _initReplayRecorder(opts = {}) {
    const key = roomSessionKey(this), emu = this.emu, replayGen = this._replaySessionGen;
    const boundary = this._replayBoundaryOwner;
    const current = () => isCurrentSessionKey(this, key) && this.emu === emu
      && this._replaySessionGen === replayGen && this._replayBoundaryOwner === boundary;
    while (this._replayArmAttempt) {
      const prior = this._replayArmAttempt;
      if (current() && isCurrentSessionKey(this, prior.key) && prior.emu === emu
          && prior.replayGen === replayGen && prior.boundary === boundary)
        return prior.promise ?? Promise.resolve().then(() => prior.promise);
      // A new launch waits for the old import and then starts its own arm.
      // The old promise's caller retains any rejection; a stale failure does
      // not prevent the current launch from making its own capture decision.
      try { await prior.promise; } catch (cause) { prior.cause = cause; }
      if (!current()) return replayBoundary.stageResult('arm', 'skipped_stale', 'stale');
    }
    if (!current()) return replayBoundary.stageResult('arm', 'skipped_stale', 'stale');
    const attempt = { key, emu, replayGen, boundary, promise: null };
    this._replayArmAttempt = attempt;
    this._replayRecorderLoading = true;
    try {
      attempt.promise = this._startReplayRecorder(opts);
      attempt.modulePromise = this._replayRecorderModulePromise;
      return await attempt.promise;
    } finally {
      if (this._replayArmAttempt === attempt) {
        // An old launch's settled import is no authority for its replacement.
        // Keep same-launch boundary arms on the shared promise, and retry a
        // new admitted launch after the old work has actually settled.
        if ((!isCurrentSessionKey(this, key) || this.emu !== emu)
            && this._replayRecorderModulePromise === attempt.modulePromise) {
          this._replayRecorderModulePromise = null;
        }
        this._replayArmAttempt = null;
        this._replayRecorderLoading = false;
      }
    }
  }

  async _startReplayRecorder(opts = {}) {
    // CR3: every exit that ends this session's chance to record settles the
    // replay decision. The in-flight, already-recording and stale exits do not.
    // X-KEY: the recorder start below runs after the module import, so it
    // starts (sessionIsCurrent checks the key), settles and reports only under
    // the key this call started with.
    const captureKey = roomSessionKey(this);
    const result = (status, cause = null, error = null) => {
      if (status === 'unsupported') settleReplayDecision(this, 'off', { key: captureKey });
      return replayBoundary.stageResult('arm', status, cause, error);
    };
    const fail = (cause, error = null) => {
      this._replayRecordingUnavailable(cause);
      return result('failed', cause, error);
    };
    // §2 Typed results: an arm that does not apply here is 'unsupported'.
    if (!this._replaysEnabled) return result('unsupported', 'replays_disabled');
    if (!this._replaysRecordingEnabled()) return result('unsupported', 'recording_opt_out');   // user opt-out
    if (this._replayRecordingOff) return result('unsupported', 'recording_off');
    if (this._replayBaseStateLoaded && !this._pendingBaseGeneration && !this._baseStatesEnabled()) {
      return fail('flag_off');   // today's behaviour: no recorder after a load
    }
    // Self-driving cores record only through the GB/Atari/N64 ring when this
    // session armed record mode (unchanged rule).
    if (this.emu?.selfDriving
        && !(SELF_DRIVING_RECORD_SYSTEMS.has(this.system?.id) && this._soloRecordMode)) return result('unsupported', 'selfdriving');
    if (this._replayPlaybackActive) return result('unsupported', 'playback');  // never record a replay of a replay
    if (this.replayRecorder) return this._replayBoundaryOwner
      ? result('failed', 'recorder_init', new Error('boundary arm found an existing recorder'))
      : result('ok', 'already_recording');
    const sys = this.system?.id;
    const hash = Array.isArray(this.romHash) ? this.romHash[0] : this.romHash;
    if (!sys || !hash) return result('unsupported', 'no_rom');

    // §7: an explicit base frame (netplay, the reset and start boundaries) is
    // validated before any snapshot; solo callers keep soloFrame.
    const explicitBaseFrame = opts.baseFrame !== undefined;
    const baseFrame = explicitBaseFrame ? opts.baseFrame
      : (this._recordResumePrefix ? 0 : this.soloFrame);
    if (!Number.isInteger(baseFrame) || baseFrame < 0 || baseFrame > replayBoundary.MAX_BASE_FRAME) {
      return fail('invalid_base_frame');
    }

    const sessionEmu = this.emu;
    const sessionGen = this._replaySessionGen;
    const boundaryOwner = this._replayBoundaryOwner;
    // §2: a boundary is held by its owner object, never by _stateCapturePending,
    // which a pinned capture may set and clear while the boundary is open.
    // K: the module import below is an await; whatever follows it (the
    // recorder start, or the fallback after a failed import) acts only while
    // this call's full session key is current, as well as its core and replay
    // generation.
    const sessionIsCurrent = () => this.emu === sessionEmu
      && isCurrentSessionKey(this, captureKey)
      && this._replaySessionGen === sessionGen
      && this._replayBoundaryOwner === boundaryOwner
      && ((boundaryOwner?.kind !== 'reset' && boundaryOwner?.kind !== 'start')
        || !replayBoundary.isStale(this, boundaryOwner));
    const pastDeadline = () => !!boundaryOwner && replayBoundary.monoNow() >= boundaryOwner.deadline;
    if (pastDeadline()) return result('timeout', 'late_arm');

    // §7: a stale import never turns an arm into a no-op; every arm awaits the
    // one shared module promise.
    if (!this._replayRecorderModulePromise) {
      this._replayRecorderModulePromise = this._loadReplayRecorderModule();
    }
    let ReplayRecorder;
    try {
      ({ ReplayRecorder } = await this._replayRecorderModulePromise);
    } catch (err) {
      if (!sessionIsCurrent()) return result('skipped_stale');
      this._replayRecorderModulePromise = null;
      if (pastDeadline()) return result('timeout', 'late_arm', err);
      if (!this._replaysEnabled || !this._replaysRecordingEnabled() || this._replayRecordingOff)
        return result('unsupported', 'recording_off');
      console.warn('[replay] recorder module unavailable:', err);
      return fail('module_import', err);
    }
    if (!sessionIsCurrent()) return result('skipped_stale');
    if (pastDeadline()) return result('timeout', 'late_arm');
    if (!this._replaysEnabled || !this._replaysRecordingEnabled() || this._replayRecordingOff)
      return result('unsupported', 'recording_off');
    if (this.replayRecorder) return boundaryOwner
      ? result('failed', 'recorder_init', new Error('boundary arm found an existing recorder'))
      : result('ok', 'already_recording');

    // Base state (spec §1): the snapshot and start() share ONE synchronous turn.
    let generation = null;
    try {
      let discardedPending = false;
      if (this._pendingBaseGeneration) {
        if (this._pendingBaseGeneration.emu === this.emu) {
          generation = this._pendingBaseGeneration;
          this._pendingBaseGeneration = null;
        } else { discardedPending = true; this._clearPendingBaseGeneration(); }
      }
      let baseBytes = null;
      const needsFreshBase = this._replayBaseStateLoaded || discardedPending;
      // §1: a browser without CompressionStream cannot upload a base (gzipBytes()
      // would send the raw bytes), so a recording that needs one ends here.
      if ((needsFreshBase || generation) && typeof globalThis.CompressionStream !== 'function') {
        return fail('base_upload_unsupported');
      }
      if (needsFreshBase && !generation) {
        if (!this._baseStatesEnabled()) return fail('flag_off');
        // §7: charge the snapshot against the 80 MiB aggregate before taking it,
        // on top of the new recorder's own fixed reservation.
        let pending = 0;
        const reserve = (bytes) => {
          if (replayBoundary.externalReplayBytes(this) + replayBoundary.RECORDER_FIXED_LIVE_BYTES + pending + bytes
              > replayBoundary.REPLAY_LIVE_BYTES_LIMIT) {
            return false;
          }
          pending += bytes;
          return true;
        };
        const snapshot = this._captureReplayBaseStateResult({ reserve });
        if (!sessionIsCurrent()) return result('skipped_stale');
        if (pastDeadline()) return result('timeout', 'late_arm', snapshot.error);
        if (snapshot.status !== 'ok') return fail(snapshot.cause, snapshot.error);
        baseBytes = snapshot.bytes;
        if (!isValidCoreBuildId(this.emu?.coreBuildId)) return fail('build_id_unavailable');
      }
      // §7: a recording with a non-zero base frame always carries a base.
      if (explicitBaseFrame && baseFrame !== 0 && !(generation || baseBytes)) return fail('base_required');
      if (pastDeadline()) return result('timeout', 'late_arm');   // §2 Arm: checked when the snapshot returns
      const originBuild = this.emu?.coreBuildId;
      const clipEmu = this.emu;
      const n64ClipProvider = sys === 'n64' && clipEmu?._n64ClipRecording?.valid
        ? clipEmu.createN64ClipProvider() : null;
      const retire = (recorder) => {
        // §2 Arm: an unpublished recorder stays charged until its stop returns.
        const bytes = replayBoundary.recorderRetainedBytes(recorder);
        let stopFailed = false;
        try { recorder.stop(); } catch (stopError) { stopFailed = true; opts.cleanupErrors?.push(stopError); }
        replayBoundary.drainRecorder(this, recorder, { stopFailed, bytes });
      };
      let recorder = null;
      try {
        recorder = new ReplayRecorder({ format: sys === 'n64' ? 'rpa1' : 'rpl1' });
        if (!sessionIsCurrent()) { retire(recorder); return result('skipped_stale'); }
        recorder._externalCaptureBytes = () => replayBoundary.externalReplayBytes(this);
        recorder.start({
          systemId: sys,
          n64ClipProvider,
          coreName: this.emu?.coreName || (sys === 'nes' ? 'jsnes' : resolveCoreName(this.system)),
          romHash: hash,
          gameTitle: this._activeGameTitle?.() || null,
          baseFrame,
          isNetplay: !!opts.isNetplay,
          opponentUserId: opts.opponentUserId || null,
          hasBaseSave: !!(generation || baseBytes),
        });
        if (!sessionIsCurrent()) { retire(recorder); return result('skipped_stale'); }
        if (baseBytes) generation = this._newBaseGeneration(baseBytes, originBuild, recorder);
        if (generation) {
          generation.attach?.(recorder);
          recorder.base = generation;
          if (!recorder.setBaseRetainedBytes(generation.bytes.byteLength)) {
            throw new Error('replay capacity exceeded');
          }
          if (!sessionIsCurrent()) { retire(recorder); return result('skipped_stale'); }
          this._replayBaseStateLoaded = false;
        }
        if (this._recordResumePrefix) {
          const prefixFrames = recorder.seedFromLog(base64ToU8(this._recordResumePrefix));
          if (!sessionIsCurrent()) { retire(recorder); return result('skipped_stale'); }
          this._recordFrameOffset = prefixFrames;
          this.soloFrame = prefixFrames;
          this._libretroSoloFrame = prefixFrames;
          this._recordResumePrefix = null;
        }
      } catch (err) {
        const cause = recorder?._capacityFailure || (err?.code === 'replay_capacity_exceeded' ? err.code : 'recorder_init');
        if (recorder) retire(recorder);
        if (!sessionIsCurrent()) return result('skipped_stale');
        if (pastDeadline()) return result('timeout', 'late_arm', err);
        console.warn('[replay] recorder initialization failed:', err);
        this._recordResumePrefix = null;
        return fail(cause, err);
      }
      if (!sessionIsCurrent()) { retire(recorder); return result('skipped_stale'); }
      if (pastDeadline()) {                 // §2 Arm: a late arm is not published
        retire(recorder);
        return result('timeout', 'late_arm');
      }
      this.replayRecorder = recorder;
      // A start owns publication before any awaiting caller can be superseded.
      // Reset owners retain their prior recorder as the committed old source.
      if (boundaryOwner?.kind === 'start') boundaryOwner.recorder = recorder;
      // Pre-warm the replay-save module so _saveReplay's cache (this._replaySaveMod)
      // is populated well before teardown. This is the ONE point every recording
      // session passes through to become live: NES and lockstep cores land here
      // directly, and self-driving GB/Atari arrive via _armSelfDrivingRecordLoop ->
      // _initReplayRecorder. Because the final-score flush is a no-op unless
      // app.replayRecorder is a live recorder, warming here guarantees that any
      // session which can reach a flush has the module cached by the time teardown
      // runs — so _saveReplay's snapshotNow freeze fires synchronously, before
      // _destroyReplayRecorder() nulls the recorder. Fire-and-forget; never blocks
      // recording and swallows its own errors (the async fallback in _saveReplay
      // still applies if it somehow hasn't resolved).
      import('./modules/replay-save.js').then((m) => { this._replaySaveMod = m; }).catch(() => {});
      this._replayAutoCool = 0;
      // New recording session → forget the prior session's auto-replay so the next
      // milestone CREATES a fresh replay; later milestones in this session EXTEND
      // that one in place (see _autoReplayCapture) instead of duplicating it.
      this._sessionReplayId = null;
      this._sessionScorePeak = null;
      this._sessionReplayClaimedScore = null;
      this._finalScoreReplayFlushPromise = null;

      // Surface the manual "save replay" button now that we're recording.
      const btnReplay = document.getElementById('btn-replay');
      if (btnReplay) {
        btnReplay.classList.remove('hidden');
        const dot = btnReplay.querySelector('.clip-rec-dot');
        if (dot) dot.classList.remove('hidden');
      }

      // Phase D/E: when the replay path owns clips the WebM recorder never runs,
      // so nothing else would ever show 📹 — show it here (spec §5).
      if (this._manualClipFromReplay()) {
        const btnClip = document.getElementById('btn-clip');
        if (btnClip) {
          btnClip.classList.remove('hidden');
          btnClip.querySelector('.clip-rec-dot')?.classList.remove('hidden');
        }
      }
      settleReplayDecision(this, 'recording', { key: captureKey });
      dispatchReplayRecording(this, { recording: true, key: captureKey });
      return result('ok');
    } finally { generation?.releasePending?.(); }
  }

  _destroyReplayRecorder({ flushPendingPostRoll = false, sessionEnd = false } = {}) {
    // Only a clean session exit may preserve an incomplete post-roll. Recorder
    // replacement, save loads, generation changes, and failure teardown make
    // the live emulator state ineligible to finalize the old replay log.
    const replayFlush = flushPendingPostRoll
      ? replayViewer.beginCleanExitReplayFlush(this)
      : replayViewer.cancelPendingReplayPostRoll(this);
    const recorder = this.replayRecorder;
    if (recorder) {
      const bytes = replayBoundary.recorderRetainedBytes(recorder);
      this.replayRecorder = null;
      if (sessionEnd) recorder.detachedSource = true;
      let stopFailed = false;
      try { recorder.stop(); } catch (error) {
        stopFailed = true;
        (this._replayTeardownErrors || (this._replayTeardownErrors = [])).push(error);
      }
      replayBoundary.drainRecorder(this, recorder, { stopFailed, bytes });
      dispatchReplayRecording(this, { recording: false });
    }
    // Session ended → drop the session-replay handle so a new game can't extend
    // the previous session's replay.
    this._sessionReplayId = null;
    this._recordResumePrefix = null;
    const btnReplay = document.getElementById('btn-replay');
    if (btnReplay) {
      const dot = btnReplay.querySelector('.clip-rec-dot');
      if (dot) dot.classList.add('hidden');
      btnReplay.classList.add('hidden');
    }
    // 📹 belongs to the replay path unless a WebM recorder is live.
    if (!this.clipRecorder) {
      const btnClip = document.getElementById('btn-clip');
      if (btnClip) {
        btnClip.classList.add('hidden');
        btnClip.querySelector('.clip-rec-dot')?.classList.add('hidden');
      }
    }
    return replayFlush;
  }

  /**
   * Manual "save replay" HUD button. Saves the buffered run so far as a
   * category='manual' replay (private by default). Lets players keep a great
   * run that didn't trip an achievement/high-score auto-capture. CR7: settles
   * after the capture's terminal report: resolves with it (null when a
   * teardown or a session change ended the capture), or rejects with the error
   * after reporting failed/exception; `origin` is 'dock' (the default),
   * 'strip' or 'rail'.
   */
  _onReplayButton(options = {}) {
    return captureReplay(this, options);
  }

  /**
   * Begin replay playback. Called from _startSolo when _pendingReplayId is
   * set. The emulator + ROM are already loaded and `this.running` is true;
   * we just instantiate a ReplayPlayer, attach it to this.emu, and let it
   * drive setButtons + stepFrame in its own RAF loop. The normal solo loop
   * is NOT started — ReplayPlayer owns the cadence.
   *
   * Pre-v1 limitation: replays with a base_save_sha256 are refused. Auto-
   * captured replays start at frame 0 anyway; manual mid-game saves are rare.
   */
  async _startReplayPlayback(replayId, prepared = null) {
    return prepared ? replayViewer.startReplayPlayback(this, replayId, prepared) : replayViewer.startReplayPlayback(this, replayId);
  }

  _showReplayHud(replay) {
    return replayViewer.showReplayHud(this, replay);
  }

  _toggleReplayPause(btn) {
    return replayViewer.toggleReplayPause(this, btn);
  }

  _restartReplay(btn) {
    return replayViewer.restartReplay(this, btn);
  }

  /**
   * Skip the fullscreen replay back `seconds` (default 10), mirroring the ⏪
   * control on the full replay viewer. Seeks via the nearest
   * snapshot in the ring; if there isn't enough history (or the core can't load
   * a snapshot), falls back to a full restart-from-boot.
   */
  _rewindReplay(seconds = 10) {
    return replayViewer.rewindReplay(this, seconds);
  }

  /**
   * Enable/disable ⏮ Restart + ⏪ Rewind based on whether there's anything to
   * rewind to (matches the inline surfaces, which disable both at the start of
   * a run). No-op when the HUD isn't mounted.
   */
  _syncReplayTransport() {
    return replayViewer.syncReplayTransport(this);
  }

  _cycleReplaySpeed(btn) {
    return replayViewer.cycleReplaySpeed(this, btn);
  }

  _updateReplayHud(frame, total) {
    return replayViewer.updateReplayHud(this, frame, total);
  }

  _hideReplayHud() {
    return replayViewer.hideReplayHud(this);
  }

  _onReplayEnd() {
    return replayViewer.onReplayEnd(this);
  }

  _exitReplayPlayback() {
    return replayViewer.exitReplayPlayback(this);
  }

  // ── Ghost race ──────────────────────────────────────────────────

  /**
   * Start a ghost race: the player runs a normal solo session while a second
   * emulator replays a ranked ghost run beside them, frame-synced. The player
   * IS recorded (they could set a new record), so we do NOT set the
   * playback-active guard — only the ghost emu is driven by a ReplayPlayer.
   *
   * NES-only for v1 (matches the verifier's coverage and keeps two
   * simultaneous emulators cheap).
   */
  async _startGhostRace(ghostId) {
    return replayViewer.startGhostRace(this, ghostId);
  }

  _ghostRaceLoop(now) {
    return replayViewer.ghostRaceLoop(this, now);
  }

  _showGhostRaceHud(ghost) {
    return replayViewer.showGhostRaceHud(this, ghost);
  }

  _updateGhostRaceHud() {
    return replayViewer.updateGhostRaceHud(this);
  }

  _hideGhostRaceHud() {
    return replayViewer.hideGhostRaceHud(this);
  }

  /**
   * Tear down the ghost emulator, player, canvas, and HUD without navigating.
   * Safe to call unconditionally — used both by the explicit Exit button and
   * by the standard game-stop cleanup path (so the second emulator never
   * leaks regardless of how the user leaves).
   */
  _teardownGhostRace() {
    return replayViewer.teardownGhostRace(this);
  }

  _exitGhostRace() {
    return replayViewer.exitGhostRace(this);
  }

  /**
   * Build the current replay buffer into an RPL1 blob and upload it to
   * /api/replays. Returns the new replay id on success, or null on failure.
   *
   * Public for use by the dev console and future UI bindings (HUD button,
   * achievement-unlock auto-save, end-of-session prompt). Not yet exposed
   * through the user-facing UI in v1.
   *
   * @param {object} [opts]
   * @param {'manual'|'achievement'|'high_score'|'speedrun'|'session'} [opts.category='manual']
   * @param {string|null} [opts.categoryRef]
   * @param {string|null} [opts.caption]
   */
  _saveReplay(opts = {}) {
    // NOT `async`. The final-score flush (beginFinalScoreReplayFlush) calls this
    // and then, in the SAME synchronous turn, runs _destroyReplayRecorder() which
    // stops the recorder and sets app.replayRecorder = null. saveReplay's
    // recorder.snapshot() (the snapshotNow freeze) must therefore run BEFORE that
    // teardown, in the caller's turn. An `await import(...)` here would defer the
    // whole saveReplay body — snapshot included — to a later microtask, by which
    // point the recorder is already null and saveReplay bails at its
    // `!recorder?.isRecording` guard: the flush becomes a silent no-op. So once
    // the module is cached we call straight through, synchronously. The cache is
    // pre-warmed the moment a recorder is armed (_initReplayRecorder), so every
    // session that can reach a final-score flush hits the synchronous branch.
    if (this._replaySaveMod) return this._replaySaveMod.saveReplay(this, opts);
    return import('./modules/replay-save.js').then((m) => { this._replaySaveMod = m; return m.saveReplay(this, opts); });
  }

  /**
   * Grab the current game canvas as a JPEG and upload it as the replay's
   * thumbnail, so the gallery, activity feed, and viewer show a representative
   * frame of the run instead of the generic 📼 placeholder. Fire-and-forget;
   * any failure (e.g. a WebGL canvas without a readable backbuffer) just
   * leaves the placeholder in place.
   */
  async _captureReplayThumbnail(replayId) {
    const { captureReplayThumbnail } = await import('./modules/replay-save.js');
    return captureReplayThumbnail(this, replayId);
  }

  /** Open the full replay viewer, optionally with browse-page navigation. */
  async _openReplayViewer(replayId, options = {}) {
    newsReplayChanged(true);
    const { skipPageView = false, ...viewerOptions } = options || {};
    // Direct /replay/:id routes already count the detail when their backdrop
    // renders. In-app modal opens count here. An explicit signal is more robust
    // than a time-window dedupe when a slow dynamic import delays the viewer.
    if (!skipPageView) this._trackPageView('replay-detail', replayId);
    try {
      return await replayViewer.openReplayViewer(this, replayId, viewerOptions);
    } finally {
      if (!this._replayViewerHomeRefreshPaused && !document.getElementById('replay-viewer')) {
        newsReplayChanged(false);
      }
    }
  }

  /**
   * Flip a replay between public and private (owner-only) via the is_public
   * PATCH. Public replays appear in the gallery to others, in the activity
   * feed, on speedrun boards, and get an og:video/mp4 render — so this is the
   * gate for the entire share surface.
   */
  async _toggleReplayPublic(replay, btn) {
    return replayViewer.toggleReplayPublic(this, replay, btn);
  }

  /**
   * Submit (or withdraw) a replay to the verified-speedrun leaderboard by
   * PATCHing its category. Server enforces that only user-settable categories
   * can change, so achievement/high_score auto-captures can't be relabeled.
   */
  async _toggleReplaySpeedrun(replay, btn, overlay) {
    return replayViewer.toggleReplaySpeedrun(this, replay, btn, overlay);
  }

  /**
   * Fetch and render the verified speedrun leaderboard for the replay's ROM.
   * Highlights the current replay's runner if they rank.
   */
  async _loadReplaySpeedruns(replay, overlay) {
    return replayViewer.loadReplaySpeedruns(this, replay, overlay);
  }

  // The modal opens in-place (gallery/activity/leaderboard/notifications) and
  // does NOT push /replay/:id, so there's no URL to restore — the {fromRouter}
  // arg the router passes is accepted and ignored.
  _closeReplayViewer(_opts = {}) {
    const result = replayViewer.closeReplayViewer(this, _opts);
    if (!_opts.replacingViewer) newsReplayChanged(!!this._replayViewerHomeRefreshPaused);
    return result;
  }

  /**
   * CR7: settles after the clip's terminal report, like _onReplayButton (see
   * captureClip); `origin` is 'dock' (the default), 'strip' or 'rail'.
   */
  _onClipButton(options = {}) {
    return captureClip(this, options);
  }

  /**
   * Whether `sysId` has a server-side headless render core ENABLED right now: the
   * always-on baseline (NES/Atari/Genesis) plus flag-gated SNES (replay_render_snes)
   * and GBA (replay_render_gba). Mirror of the server registry
   * (cores/headless-core.js) — the client can't import server code, so keep the two
   * in lockstep as render cores land. Drives both clip-from-replay decisions below.
   */
  _serverRenderableSystem(sysId = this.system?.id) {
    return SERVER_RENDER_SYSTEMS.includes(sysId)
      || (sysId === 'snes' && moduleRegistry.isEnabled('replay_render_snes'))
      || (sysId === 'gb' && moduleRegistry.isEnabled('replay_render_gb'))
      || (sysId === 'gba' && moduleRegistry.isEnabled('replay_render_gba'));
  }

  /**
   * Whether this solo session is recording (or is about to record) a replay that
   * can be server-rendered into a clip. Mirrors _initReplayRecorder's gate: replays
   * on + opt-in + a record-capable core, not replay playback — so NES, lockstep
   * record cores, and the self-driving ring systems qualify; unrelated self-driving
   * cores (Genesis Fast saves, C64) and anonymous sessions do NOT. Once the recorder
   * exists it is the live truth; before that (e.g. at _initClipRecorder time, which
   * runs just before the replay recorder inits) it reads emu.selfDriving predictively.
   * NOTE: this is deliberately NOT _shouldRecordSolo — that gates only the lockstep
   * CORE choice and is false for jsnes NES even though NES records a replay.
   */
  _isRecordingReplay() {
    if (this.replayRecorder) return !!this.replayRecorder.isRecording;
    if (this._replayRecordingOff) return false;
    if (!this._replaysEnabled || !this._replaysRecordingEnabled()) return false;
    if (this._replayPlaybackActive) return false;
    if (!this.emu) return false;
    if (this.emu.selfDriving) {
      return SELF_DRIVING_RECORD_SYSTEMS.has(this.system?.id) && this._soloRecordMode === true;
    }
    return true;
  }

  /**
   * Whether a milestone (achievement / high-score) clip will be SERVER-RENDERED
   * from the replay log rather than captured as a local WebM clip. True on touch
   * always (it never captures WebM), and on DESKTOP only when the
   * server_rendered_clips cutover flag is on — in both cases only for a
   * render-capable system with clips enabled. When true, _autoClipCapture skips
   * its WebM capture (so a milestone yields exactly ONE clip) and _autoReplayCapture
   * sets clip_source so the server renders the highlight from the replay.
   */
  _milestoneClipFromReplay() {
    return moduleRegistry.isEnabled('video_clips')
      && this._serverRenderableSystem()
      && this._isRecordingReplay()
      && (!!navigator.maxTouchPoints || moduleRegistry.isEnabled('server_rendered_clips'));
  }

  /**
   * Whether the manual 📹 clip button should SERVER-RENDER from the live replay
   * (Phase D) instead of a local WebM capture. Same gate as milestone clips: on
   * touch always, because touch never runs the WebM recorder and this is its only
   * manual clip path; on desktop only under the server_rendered_clips cutover.
   * Either way it needs a replay recording on a render-capable system with clips
   * enabled.
   */
  _manualClipFromReplay() {
    return this._milestoneClipFromReplay();
  }

  _autoClipCapture(triggerType, triggerRef, replayIdPromise) {
    // The replay-render path owns the clip for this milestone (touch always;
    // desktop under server_rendered_clips) — don't ALSO capture a local WebM clip.
    if (this._milestoneClipFromReplay()) return;
    if (!this.clipRecorder || !this.clipRecorder.isRecording) return;
    const now = Date.now();
    if (triggerType !== 'achievement' && now < (this._clipAutoWarm || 0)) return;
    if (now < (this._clipAutoCool || 0)) return;
    const recorder = this.clipRecorder;
    const systemId = this.system?.id;
    const romHash = Array.isArray(this.romHash) ? this.romHash[0] : this.romHash;
    this._clipAutoCool = now + 15_000;
    recorder.captureAroundTrigger(triggerType, triggerRef, this._activeGameTitle())
      .then(data => {
        if (!data) return;
        // Telemetry only — a healthy 15s capture is ≥~20 KB even for static
        // content; under ~1 KB/s means the recorder captured a dead canvas
        // (black frames, silent audio). Surface it in admin client-errors so
        // a recurrence of the stale-recorder bug is visible, but still upload.
        const secs = (data.durationMs || 0) / 1000;
        if (secs >= 3 && data.blob?.size < secs * 1000) {
          reportCrash({
            error: new Error(`[clips] suspiciously small ${triggerType} clip: ${data.blob.size}B for ${Math.round(secs)}s (quality L${recorder.getQualityLevel?.() ?? '?'})`),
            system: systemId,
            romName: this.romName,
            romHash,
            appState: this.state,
            emu: this.emu,
            canvas: this.canvas,
          });
        }
        recorder.upload(data, {
          systemId,
          romHash,
          caption: triggerRef || null,
          replayId: replayIdPromise,
        }).catch(() => {});
      });
  }

  /**
   * Save a replay of the current session triggered by a milestone (achievement
   * unlock, high-score). Fires alongside _autoClipCapture but holds its own
   * cooldown so a single milestone never burns both budgets. Saved replays
   * default to private (is_public=false); owner can flip via PATCH later.
   *
   * The replay covers the entire session up to "now" (the recorder buffers
   * the whole run, since input logs are bytes-per-second). Players watching
   * an auto-saved replay get the full run that led to the milestone, which
   * is more valuable for speedrun analysis than a 15-second clip.
   *
   * @param {'achievement'|'high_score'} category
   * @param {string|null} categoryRef
   * @param {number|undefined} triggerFrame - absolute frame where the milestone
   *   occurred, captured before any async delay. Passed to the server so the
   *   highlight render centers on this frame (10s before + 5s after).
   * @param {number|null} [claimedScore] - the score observed at the trigger
   *   frame (high-score milestones only); achievement unlocks pass nothing.
   */
  _autoReplayCapture(category, categoryRef, triggerFrame, claimedScore) {
    return replayViewer.autoReplayCapture(this, category, categoryRef, triggerFrame, claimedScore);
  }

  _cancelPendingReplayPostRoll() {
    return replayViewer.cancelPendingReplayPostRoll(this);
  }

  /** §4: true while a lockstep session is starting or running on this page. */
  _inLockstepSession() {
    const session = this._lockstepSession;
    return !!session && this.state === State.PLAYING && (!session.emu || session.emu === this.emu);
  }

  /** The reset button routes by session (spec, Units). */
  async _onResetButton() {
    const done = (reset, recording = 'off', stage = null) => ({ reset, recording,
      stages: stage ? [stage] : [], cleanupErrors: [] });
    if (this._unsafeReplayBoundary === this.emu && this.emu) return done('halted');
    // §4: the netplay path owns the reset during lockstep startup and play, even
    // before the engine exists.
    if (this._inLockstepSession() || (this.lockstep && this.lockstepConn)) {
      return this._handleLockstepReset('local');
    }
    if (!this.romData || !this.emu) return done('refused', 'off', replayBoundary.stageResult('preflight', 'failed', 'unavailable'));
    const soloReset = this.state === State.SOLO;
    if (soloReset && !this.emu.selfDriving) return this._resetSoloSession();
    const emu = this.emu, state = this.state, launch = this._gameLaunchIntentGeneration;
    const authority = { generation: this._replaySessionGen };
    const current = () => this.emu === emu && this.state === state
      && this._gameLaunchIntentGeneration === launch && this._replaySessionGen === authority.generation;
    let nativeReset = null;
    if (soloReset && this.emu.selfDriving) {
      nativeReset = await this._runSelfDrivingReplayBoundary('reset', null, 0, Date.now() + 12000, null, authority);
      if (!current()) return done('skipped_stale');
      if (nativeReset?.failure?.cause === 'busy') {
        this._flash('Reset is busy. Try again.');
        return done('refused', 'off', replayBoundary.stageResult('preflight', 'failed', 'busy'));
      }
      if (nativeReset?.unsafeToMutate) {
        const failure = nativeReset.failure;
        this._haltUncertainReplayBoundary(emu, failure
          ? `selfdriving_reset_${failure.stage}_${failure.cause}`
          : 'selfdriving_reset_boundary_unavailable');
        return done('halted', 'ended', replayBoundary.stageResult('native', 'failed', failure?.cause || 'unverified'));
      }
      if (nativeReset?.applied !== true) {
        if (this._statePumpQueue || this._gameplayHeld()) {
          this._flash('Reset is busy. Try again.');
          return done('refused', 'off', replayBoundary.stageResult('preflight', 'failed', 'busy'));
        }
        if (nativeReset?.applied !== false || nativeReset.safeToMutate !== true || nativeReset.unsafeToMutate !== false) {
          this._haltUncertainReplayBoundary(emu, 'native_reset_unverified');
          return done('halted', 'ended', replayBoundary.stageResult('native', 'failed', 'unverified'));
        }
        const failure = nativeReset?.failure;
        this._replayRecordingUnavailable(failure
          ? `selfdriving_reset_${failure.stage}_${failure.cause}`
          : 'selfdriving_reset_boundary_unavailable');
      }
    }
    try {
      if (nativeReset?.applied === true) {
        // The native request reset in its acknowledged pump turn.
      } else if (this.emu.reset) {
        this.emu.reset();
      } else {
        this.emu.loadROM(this.romData);
      }
    } catch (error) {
      this._flash('Reset failed');
      return done('halted', 'ended', replayBoundary.stageResult('commit', 'failed', 'reset_commit_failed', error));
    }
    if (soloReset) {
      this.soloFrame = 0;
      this._libretroSoloFrame = 0;
      this._recordDrainCursor = 0;
      this._recordFrameOffset = 0;
    }
    // The reset replays power-on RAM: re-arm the achievement boot-window guards.
    this.cheevosEngine.notifyCoreReset();
    // A self-driving reset ends the author session's segment (spec 8.3); the stepped reset's
    // segment starts in replay-boundary.js commitReset.
    this._noteAuthorSegment('reset');
    // Drop rewind history: a snapshot from before the reset would undo it.
    this._rewindRing = [];
    return done('applied', nativeReset?.recorded === true ? 'continued' : 'off');
  }

  /**
   * §8: a committed reset whose recording cannot continue. `armCause` is the
   * arm's own reason when it has one; if the arm did not end recording itself,
   * propagate that reason instead of replacing it with the generic fallback.
   */
  _endResetRecording(owner, reason, origin, armCause = null) {
    const unavailableReason = reason === 'reset_arm_failed' || reason === 'netplay_reset_arm_failed'
      ? (armCause || reason) : reason;
    if (!this._replayRecordingOff) this._replayRecordingUnavailable(unavailableReason);
    this._flash(this._resetStopMessage());
    this._logBoundaryFailure({ operation: origin === 'solo' ? 'reset' : 'netplay_reset', origin,
      stage: 'arm', status: 'failed', cause: armCause || reason });
    return { reset: 'applied', recording: 'ended', stages: owner.stages, cleanupErrors: owner.cleanupErrors };
  }

  /** §3: the solo reset of a stepped core. Resolves, never rejects. */
  async _resetSoloSession() {
    const done = (reset, recording, owner = null, extraStages = []) => ({ reset, recording,
      stages: [...(owner?.stages || []), ...extraStages], cleanupErrors: owner?.cleanupErrors || [] });
    const recordingNow = () => (this.replayRecorder?.isRecording ? 'continued' : 'off');
    const hold = this._beginResetHold('solo');
    if (hold.busy) {
      this._flash('Reset is busy. Try again.');
      this._logBoundaryFailure({ operation: 'reset', origin: 'solo', stage: 'hold', status: 'failed', cause: 'busy' });
      return done('refused', recordingNow(), null, [replayBoundary.stageResult('hold', 'failed', 'busy')]);
    }
    const owner = hold.owner;
    try {
      const ack = await this._acknowledgeResetFrame(owner);
      owner.stages.push(ack);
      if (replayBoundary.isStale(this, owner)) return done('skipped_stale', 'off', owner);
      if (ack.status === 'skipped_stale') return done('skipped_stale', 'off', owner);
      // The clock is read just before the commit (A1); past the deadline, refuse.
      if (ack.status !== 'ok' || replayBoundary.monoNow() >= owner.deadline) {
        if (ack.status === 'ok') owner.stages.push(replayBoundary.stageResult('commit', 'timeout', 'deadline'));
        this._flash('Reset could not reach a frame boundary');
        this._logBoundaryFailure({ operation: 'reset', origin: 'solo', stage: ack.status === 'ok' ? 'commit' : 'acknowledge',
          status: ack.status === 'ok' ? 'timeout' : ack.status, cause: ack.cause || 'deadline' });
        return done('refused', recordingNow(), owner);
      }
      try {
        this._commitReset(owner);
      } catch (error) {
        owner.stages.push(replayBoundary.stageResult('commit', 'failed', 'reset_commit_failed', error));
        this._logBoundaryFailure({ operation: 'reset', origin: 'solo', stage: 'commit', status: 'failed', cause: 'reset_commit_failed' });
        this._haltUncertainReplayBoundary(owner.emu, 'reset_commit_failed');
        return done('halted', 'ended', owner);
      }
      owner.stages.push(replayBoundary.stageResult('commit', 'ok'));
      // §2: with recording off, the recorder steps are skipped and nothing is announced.
      if (!replayBoundary.recordingWanted(this)) return done('applied', 'off', owner);
      // A late commit stands because it is complete; only the arm is skipped.
      if (replayBoundary.monoNow() >= owner.deadline) return this._endResetRecording(owner, 'reset_deadline', 'solo');
      const arm = await this._armAfterResetCommit(owner, { isNetplay: false });
      if (replayBoundary.isStale(this, owner)) return done('skipped_stale', 'off', owner);
      owner.stages.push(arm);
      if (arm.status === 'ok') return done('applied', 'continued', owner);
      if (arm.status === 'unsupported') return done('applied', 'off', owner);
      if (arm.status === 'skipped_stale') return done('skipped_stale', 'off', owner);
      return this._endResetRecording(owner, arm.status === 'timeout' ? 'reset_deadline' : 'reset_arm_failed', 'solo', arm.cause);
    } finally {
      this._releaseResetHold(owner);
    }
  }

  /**
   * Best-effort final extension of this session's existing replay. Bypasses the
   * milestone cooldown and creates no duplicate clip; the sole purpose is to
   * make the replay's verified score cover the high score written at teardown.
   */
  _beginFinalScoreReplayFlush() {
    return replayViewer.beginFinalScoreReplayFlush(this);
  }

  /**
   * Manual 📹 clip via server render (Phase D). Marks the current replay frame and
   * renders the ~15s highlight from the live replay log server-side, instead of a
   * local WebM capture — the user gets a brief "rendering…" callout and the clip
   * surfaces (gallery / Discord) when the render lands. Reuses the session replay
   * (replaceId) so a manual clip and milestone clips share ONE replay row; each
   * manual tap still renders its own clip (see the server PUT /log manual block).
   */
  _manualReplayClip(triggerFrame) {
    return replayViewer.manualReplayClip(this, triggerFrame);
  }

  /**
   * Show a brief, self-dismissing "📼 Replay saved" callout with a "View" link
   * that opens the replay viewer. Displayed only when an auto-capture actually
   * succeeds (id is truthy). Stacks below the #flash element (top:60px) so it
   * does not collide with the achievement toast that triggered the save.
   */
  async _showReplaySavedCallout(replayId) {
    const { showReplaySavedCallout } = await import('./modules/replay-save.js');
    return showReplaySavedCallout(this, replayId);
  }

  _activeGameTitle() {
    return this._canonicalTitle || this._displayTitle || this.romName || '';
  }

  _bestDisplayTitle() {
    return this._canonicalTitle
      || this._romDisplayTitle?.(this.romName, this.system?.id)
      || this.romName || null;
  }

  /**
   * The clip toast is non-modal and play continues under it, so its caption
   * holds a gameplay-input lease only while focused (like the desktop room
   * chat input). _dismissClipToast releases it on Save, Discard, the
   * auto-dismiss timer and game exit.
   */
  _bindClipToastCaption() {
    return bindClipPreview(this);
  }

  _showClipToast(clipData, review, key) {
    return showClipPreview(this, clipData, review, key);
  }

  _dismissClipToast(reason) {
    return dismissClipPreview(this, reason);
  }

  _saveClip(caption) {
    return saveClipPreview(this, caption);
  }

  // ── Clip Viewer ─────────────────────────────────────────────────

  _openClipViewer(clipId, shareRoute) {
    // A cold /clip/:id boot hides every .screen before routing. Give the
    // modal a real page underneath it so dismissing the shared clip cannot
    // expose the otherwise-empty SPA shell.
    if (shareRoute) {
      this._showScreen('menu', 'clip-detail');
    } else {
      this._trackPageView('clip-detail', clipId);
    }
    // The clip is a modal over whatever screen is behind it. Capture the
    // backdrop's real document title (after any share-route _showScreen above)
    // so close restores it verbatim — screens like the gallery/leaderboards set
    // data-specific titles that routeTitleForScreen() would flatten. Title the
    // tab for the clip meanwhile; _loadClipViewer refines it to the game name.
    if (this._preClipTitle == null) this._preClipTitle = document.title;
    setPageTitle('Clip');
    const viewer = document.getElementById('clip-viewer');
    if (!viewer) return;

    viewer.classList.remove('hidden');
    this._loadClipViewer(clipId);

    document.getElementById('clip-viewer-backdrop')?.addEventListener('click', () => {
      this._closeClipViewer();
    }, { once: true });
    document.getElementById('clip-viewer-close')?.addEventListener('click', () => {
      this._closeClipViewer();
    }, { once: true });
  }

  _closeClipViewer() {
    const viewer = document.getElementById('clip-viewer');
    const video = document.getElementById('clip-viewer-video');
    const confirmEl = document.getElementById('clip-viewer-confirm');
    const delBtn = document.getElementById('clip-viewer-delete');
    if (viewer) viewer.classList.add('hidden');
    if (video) { video.pause(); video.onerror = null; video.removeAttribute('src'); video.removeAttribute('poster'); video.classList.remove('hidden'); try { video.load(); } catch { /* ignore */ } }
    const unavailable = document.getElementById('clip-viewer-unavailable');
    if (unavailable) { unavailable.classList.add('hidden'); unavailable.classList.remove('has-poster'); }
    if (confirmEl) confirmEl.classList.add('hidden');
    if (delBtn) delBtn.setAttribute('aria-expanded', 'false');
    this._activeClipId = null;

    // /clip/:id is a modal-only legacy share route. Once its modal is gone,
    // replace it with the homepage URL (rather than leaving a refresh/back
    // trap that immediately reopens the clip).
    if (location.pathname.startsWith('/clip/')) {
      history.replaceState(0, '', '/');
    }

    // Restore the exact title the modal was covering (a share link left the
    // backdrop on the menu; an in-app modal keeps the gallery/leaderboard/etc.
    // data-specific title it captured on open).
    if (this._preClipTitle != null) {
      document.title = this._preClipTitle;
      this._preClipTitle = null;
    }
  }

  async _loadClipViewer(clipId) {
    this._activeClipId = clipId;
    const loadToken = this._clipViewerLoadToken = (this._clipViewerLoadToken || 0) + 1;
    for (const button of document.querySelectorAll('.clip-viewer-actions button')) {
      button.onclick = null; button.disabled = true;
    }
    const pin = document.getElementById('clip-viewer-pin');
    pin?.classList.add('hidden');
    try {
      const res = await fetch(`/api/clips/${clipId}`, { credentials: 'include' });
      if (!res.ok) return;
      const clip = await res.json();
      // Bail if the viewer was closed or switched to another clip while this
      // request was in flight — a stale response must not retitle the tab or
      // repopulate the (now hidden or different) modal.
      if (this._activeClipId !== clipId || this._clipViewerLoadToken !== loadToken) return;

      const video = document.getElementById('clip-viewer-video');
      const unavailable = document.getElementById('clip-viewer-unavailable');
      const thumbUrl = clip.has_thumbnail || clip.thumbnail_path ? `/api/clips/${clipId}/thumbnail` : null;
      // Show a graceful "still processing" state instead of a broken/404 player
      // when the mp4 isn't ready (server render queued/in-flight, or evicted).
      const showUnavailable = () => {
        if (video) { video.classList.add('hidden'); video.onerror = null; video.removeAttribute('src'); try { video.load(); } catch { /* ignore */ } }
        if (unavailable) {
          unavailable.classList.remove('hidden');
          const posterEl = document.getElementById('clip-viewer-unavailable-poster');
          if (thumbUrl && posterEl) { posterEl.style.backgroundImage = `url("${thumbUrl}")`; unavailable.classList.add('has-poster'); }
          else { unavailable.classList.remove('has-poster'); if (posterEl) posterEl.style.backgroundImage = ''; }
        }
      };
      if (video) {
        const videoVersion = encodeURIComponent(`${clip.created_at || ''}:${clip.file_size || ''}:${clip.duration_ms || ''}`);
        if (clip.has_mp4) {
          if (unavailable) unavailable.classList.add('hidden');
          video.classList.remove('hidden');
          if (thumbUrl) video.setAttribute('poster', thumbUrl); else video.removeAttribute('poster');
          video.onerror = () => showUnavailable(); // evicted between metadata and load
          // The rendered mp4 is the canonical playback artifact whenever it is
          // available, regardless of whether the clip began as a WebM upload or
          // was rendered from a replay. This keeps browser compatibility from
          // depending on support for the original capture codec.
          video.src = `/api/clips/${clipId}/mp4?v=${videoVersion}`;
          try { video.load(); } catch { /* ignore */ }
        } else {
          showUnavailable();
        }
      }
      document.getElementById('clip-viewer-title').textContent = clip.game_title || '';
      setPageTitle(clip.game_title ? `${clip.game_title} clip` : 'Clip');
      document.getElementById('clip-viewer-system').textContent = systemTag(clip.system_id);
      document.getElementById('clip-viewer-caption').textContent = clip.caption || '';

      const triggerEl = document.getElementById('clip-viewer-trigger');
      if (clip.trigger_type === 'achievement') {
        triggerEl.textContent = `Achievement: ${clip.trigger_ref || ''}`;
      } else if (clip.trigger_type === 'high_score') {
        triggerEl.textContent = `High Score: ${clip.trigger_ref || ''}`;
      } else {
        triggerEl.textContent = '';
      }

      const actionsEl = document.querySelector('.clip-viewer-actions');
      if (actionsEl) {
        const shareBtn = document.getElementById('clip-viewer-share');
        const dlBtn = document.getElementById('clip-viewer-download');
        const pinBtn = document.getElementById('clip-viewer-pin');
        const delBtn = document.getElementById('clip-viewer-delete');
        if (clip.is_owner) {
          if (shareBtn) shareBtn.classList.remove('hidden');
          if (dlBtn) dlBtn.classList.remove('hidden');
          if (pinBtn) pinBtn.classList.remove('hidden');
          if (delBtn) delBtn.classList.remove('hidden');
        } else {
          if (shareBtn) { shareBtn.title = 'Copy link'; shareBtn.classList.remove('hidden'); }
          if (dlBtn) dlBtn.classList.add('hidden');
          if (pinBtn) pinBtn.classList.add('hidden');
          if (delBtn) delBtn.classList.add('hidden');
        }
      }

      this._renderClipReactions(clip.reactions || []);
      await this._wireClipViewerActions(clip);
    } catch { /* ignore */ }
  }

  _renderClipReactions(reactions) {
    const bar = document.getElementById('clip-viewer-reactions');
    if (!bar) return;
    for (const btn of bar.querySelectorAll('.reaction-btn')) {
      const emoji = btn.dataset.emoji;
      const r = reactions.find(x => x.emoji === emoji);
      btn.querySelector('.reaction-count').textContent = r ? r.count : '0';
      btn.classList.toggle('mine', !!r?.mine);
      btn.onclick = () => this._toggleReaction('clip', this._activeClipId, emoji, btn);
    }
  }

  async _toggleReaction(targetType, targetId, emoji, btn) {
    try {
      const res = await fetch('/api/reactions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ target_type: targetType, target_id: targetId, emoji }),
      });
      if (!res.ok) return;
      const { action } = await res.json();
      const count = btn.querySelector('.reaction-count');
      const cur = parseInt(count.textContent, 10) || 0;
      count.textContent = action === 'added' ? cur + 1 : Math.max(0, cur - 1);
      btn.classList.toggle('mine', action === 'added');
    } catch { /* ignore */ }
  }

  /**
   * Share a URL via the platform's native share sheet when available
   * (mobile iOS/Android), falling back to clipboard. Use anywhere we
   * want a "share this" button. Returns 'shared' | 'copied' | 'cancelled' | 'error'.
   *
   * @param {{ url: string, title?: string, text?: string }} payload
   */
  async _shareUrl({ url, title, text }) {
    if (!url) return 'error';
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ url, title, text });
        return 'shared';
      } catch (err) {
        // AbortError = user dismissed; anything else falls through to clipboard.
        if (err && err.name === 'AbortError') return 'cancelled';
      }
    }
    try {
      await navigator.clipboard?.writeText(url);
      this._flash('Link copied!');
      return 'copied';
    } catch {
      this._flash('Could not share');
      return 'error';
    }
  }

  async _wireClipViewerActions(clip) {
    const token = this._clipViewerLoadToken;
    const isCurrent = () => this._activeClipId === clip.id && this._clipViewerLoadToken === token;
    try {
      const { wireClipViewerActions } = await import('./modules/clip-viewer-actions.js');
      if (isCurrent()) wireClipViewerActions(this, clip);
    } catch { if (isCurrent()) this._flash('Clip controls unavailable. Please reopen the clip.'); }
  }

  // ── Clip Forward Modal ──────────────────────────────────────────

  async _openClipForwardModal(clip) {
    const modal = document.getElementById('clip-forward-modal');
    if (!modal) return;
    this._forwardClip = clip;
    modal.classList.remove('hidden');

    const searchInput = document.getElementById('clip-forward-search');
    const friendsEl = document.getElementById('clip-forward-friends');
    const emailInput = document.getElementById('clip-forward-email');
    const emailSendBtn = document.getElementById('clip-forward-email-send');

    if (searchInput) searchInput.value = '';
    if (emailInput) emailInput.value = '';

    modal.querySelector('.clip-forward-backdrop')?.addEventListener('click', () => this._closeClipForwardModal(), { once: true });
    document.getElementById('clip-forward-close')?.addEventListener('click', () => this._closeClipForwardModal(), { once: true });

    try {
      const res = await fetch('/api/friends', { credentials: 'include' });
      if (!res.ok) throw new Error();
      const { friends } = await res.json();
      this._forwardFriends = friends || [];
    } catch {
      this._forwardFriends = [];
    }

    this._renderForwardFriendRows();

    if (searchInput) {
      searchInput.oninput = () => this._renderForwardFriendRows(searchInput.value.trim().toLowerCase());
    }

    if (emailSendBtn) {
      emailSendBtn.onclick = () => this._forwardClipByEmail();
    }
  }

  _renderForwardFriendRows(filter = '') {
    const container = document.getElementById('clip-forward-friends');
    if (!container) return;
    const friends = (this._forwardFriends || []).filter((f) => {
      if (!filter) return true;
      const name = (f.displayUsername || f.username || '').toLowerCase();
      return name.includes(filter);
    });

    if (friends.length === 0) {
      container.innerHTML = `<div class="clip-forward-empty">${filter ? 'No matches' : 'No friends yet'}</div>`;
      return;
    }

    container.innerHTML = '';
    for (const friend of friends) {
      const row = document.createElement('div');
      row.className = 'clip-forward-friend-row';
      const displayName = friend.displayUsername || friend.username;
      const username = friend.username || '';
      const avatar = username
        ? `<retro-avatar class="clip-forward-friend-avatar" username="${this._escapeHtml(username)}" size="list" fallback-avatar></retro-avatar>`
        : `<span class="clip-forward-friend-avatar-placeholder">👤</span>`;
      const online = friend.presence?.online ? `<span class="clip-forward-friend-status">online</span>` : '';
      row.innerHTML = `${avatar}<span class="clip-forward-friend-name">${this._escapeHtml(displayName)}</span>${online}`;
      row.addEventListener('click', () => this._forwardClipToFriend(friend, row));
      container.appendChild(row);
    }
  }

  async _forwardClipToFriend(friend, row) {
    if (!this._forwardClip) return;
    if (row) row.classList.add('sent');
    try {
      const res = await fetch(`/api/clips/${this._forwardClip.id}/forward`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ to_username: friend.username }),
      });
      if (res.ok) {
        this._flash(`Clip sent to @${friend.displayUsername || friend.username}`);
        this._closeClipForwardModal();
      } else {
        const err = await res.json().catch(() => ({}));
        if (row) row.classList.remove('sent');
        if (err.error === 'not_friends') this._flash('Not friends with this user');
        else this._flash('Could not forward clip');
      }
    } catch {
      if (row) row.classList.remove('sent');
      this._flash('Could not forward clip');
    }
  }

  async _forwardClipByEmail() {
    if (!this._forwardClip) return;
    const input = document.getElementById('clip-forward-email');
    const email = input?.value?.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this._flash('Enter a valid email address');
      return;
    }
    try {
      const res = await fetch(`/api/clips/${this._forwardClip.id}/forward`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ to_email: email }),
      });
      if (res.ok) {
        this._flash('Clip emailed!');
        this._closeClipForwardModal();
      } else {
        const err = await res.json().catch(() => ({}));
        if (err.error === 'daily_limit_reached') this._flash('Daily email limit reached');
        else if (err.error === 'email_throttled') this._flash('Already sent to this email');
        else this._flash('Could not send email');
      }
    } catch {
      this._flash('Could not send email');
    }
  }

  _closeClipForwardModal() {
    const modal = document.getElementById('clip-forward-modal');
    if (modal) modal.classList.add('hidden');
    this._forwardClip = null;
    this._forwardFriends = null;
  }

  // ── Clip Gallery (Profile Tab) ──────────────────────────────────

  async _renderClipsTab(username, container) {
    container.innerHTML = '<p style="color: var(--text-dim); font-size: 11px;">Loading clips...</p>';
    try {
      const res = await fetch(`/api/users/${encodeURIComponent(username)}/clips?limit=50`);
      if (!res.ok) { container.innerHTML = ''; return; }
      const { clips, total } = await res.json();

      if (!clips.length) {
        container.innerHTML = '<p style="color: var(--text-dim); font-size: 11px;">No clips yet</p>';
        return;
      }

      const grid = document.createElement('div');
      grid.className = 'clips-grid';

      for (const clip of clips) {
        const card = document.createElement('div');
        card.className = 'clip-card';
        card.addEventListener('click', () => this._openClipViewer(clip.id));

        const durationSec = Math.round((clip.duration_ms || 0) / 1000);
        const triggerIcon = clip.trigger_type === 'achievement' ? '🏆'
          : clip.trigger_type === 'high_score' ? '🏅' : '📹';
        // Replay-rendered clips may not have a thumbnail yet — show a CSS
        // placeholder instead of a broken image (and swap on a late 404).
        const thumbMarkup = clip.has_thumbnail || clip.thumbnail_path
          ? `<img class="clip-card-thumb" src="/api/clips/${clip.id}/thumbnail" alt="${escapeHtml(clip.game_title || 'Clip')}" loading="lazy">`
          : `<div class="clip-card-placeholder">${triggerIcon}</div>`;

        card.innerHTML = `
          ${thumbMarkup}
          <div class="clip-card-overlay">
            <span class="clip-card-title">${escapeHtml(clip.game_title || '')}</span>
            <span class="clip-card-duration">${durationSec}s</span>
          </div>
          ${clip.pinned ? '<span class="clip-card-pin">📌</span>' : ''}
          <span class="clip-card-trigger">${triggerIcon}</span>
          ${clip.visibility === 'private' ? '' : '<button class="clip-card-forward" title="Forward clip" aria-label="Forward">➤</button>'}
        `;
        const thumbImg = card.querySelector('.clip-card-thumb');
        if (thumbImg) thumbImg.addEventListener('error', () => {
          const ph = document.createElement('div');
          ph.className = 'clip-card-placeholder';
          ph.textContent = triggerIcon;
          thumbImg.replaceWith(ph);
        });
        card.querySelector('.clip-card-forward')?.addEventListener('click', (e) => {
          e.stopPropagation();
          this._openClipForwardModal(clip);
        });
        grid.appendChild(card);
      }

      container.innerHTML = '';
      container.appendChild(grid);
    } catch {
      container.innerHTML = '';
    }
  }

  // ── Activity Reactions ──────────────────────────────────────────

  _renderActivityReactionRow(targetType, targetId) {
    const row = document.createElement('div');
    row.className = 'reaction-row';
    row.dataset.targetType = targetType;
    row.dataset.targetId = targetId;
    return row;
  }

  async _loadActivityReactions(rows) {
    if (!rows.length) return;

    const grouped = {};
    for (const row of rows) {
      const key = row.dataset.targetType;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(row.dataset.targetId);
    }

    for (const [type, ids] of Object.entries(grouped)) {
      try {
        const res = await fetch(`/api/reactions?target_type=${type}&target_ids=${ids.join(',')}`, {
          credentials: 'include',
        });
        if (!res.ok) continue;
        const { reactions } = await res.json();

        for (const row of rows) {
          if (row.dataset.targetType !== type) continue;
          const data = reactions[row.dataset.targetId] || [];
          this._fillReactionRow(row, type, row.dataset.targetId, data);
        }
      } catch { /* ignore */ }
    }
  }

  _fillReactionRow(row, targetType, targetId, reactions) {
    row.innerHTML = '';
    const emojis = [
      ['fire', '🔥'], ['heart', '❤️'], ['clap', '👏'],
      ['laugh', '😂'], ['skull', '💀'], ['gamepad', '🎮'],
    ];

    for (const [key, symbol] of emojis) {
      const r = reactions.find(x => x.emoji === key);
      const count = r?.count || 0;
      if (!count && !r) continue;

      const btn = document.createElement('button');
      btn.className = `reaction-btn${r?.mine ? ' mine' : ''}`;
      btn.dataset.emoji = key;
      btn.innerHTML = `${symbol} <span class="reaction-count">${count}</span>`;
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._toggleReaction(targetType, targetId, key, btn);
      });
      row.appendChild(btn);
    }
  }

  // ── Friends badge + play-invite toast ───────────────────────────
  //
  // Badge shows on the "Friends" entry inside the account popover.
  // Hidden when count is 0; capped at "9+" visually.
  _setFriendsBadge(count) {
    const badge = document.getElementById('account-popover-friends-badge');
    if (!badge) return;
    if (!count || count <= 0) {
      badge.classList.add('hidden');
      return;
    }
    badge.classList.remove('hidden');
    badge.textContent = count > 9 ? '9+' : String(count);
  }

  // Toast for an incoming play-invite. Self-dismissing after 30s; clicks
  // "Join" → routes to /r/:code (the server already created the room).
  _showPlayInviteToast(invite) {
    if (!invite || !invite.room_code) return;
    // Dedup: never show two toasts for the same invite id simultaneously.
    this._activeInviteToasts = this._activeInviteToasts || new Set();
    if (this._activeInviteToasts.has(invite.id)) return;
    this._activeInviteToasts.add(invite.id);

    const toast = document.createElement('div');
    toast.className = 'play-invite-toast';
    const from = invite.from || {};
    const fromName = from.displayUsername || from.username || 'A friend';
    toast.innerHTML = `
      <div class="play-invite-toast-header">▶ Play invite</div>
      <div class="play-invite-toast-body">
        <strong>@${this._escapeHtml(fromName)}</strong> wants to play.${invite.message ? '<br><em>"' + this._escapeHtml(invite.message) + '"</em>' : ''}
      </div>
      <div class="play-invite-toast-actions">
        <button class="friend-action friend-action-secondary" data-action="decline">Decline</button>
        <button class="friend-action friend-action-primary" data-action="accept">Join</button>
      </div>
    `;
    const cleanup = () => {
      toast.remove();
      this._activeInviteToasts.delete(invite.id);
    };
    toast.querySelector('[data-action="accept"]').onclick = async () => {
      const attempt = notificationsUi.captureNotificationLaunch(this);
      const replaced = (cause = new Error('A newer launch replaced this invite')) => ({ ok: false, superseded: true, cause });
      try {
        if (!attempt.current()) return replaced();
        const res = await fetch(`/api/play-invites/${encodeURIComponent(invite.id)}/accept`, { method: 'POST', credentials: 'same-origin' });
        if (!attempt.current()) return replaced();
        if (!res.ok) throw new Error(`The invite accept answered ${res.status}`);
        const body = await res.json();
        if (!attempt.current()) return replaced();
        if (!body.room_code) throw new Error('The accepted invite has no room code');
        cleanup();
        if (!attempt.current()) return replaced();
        this._pushRoomHistory(body.room_code);
        if (!attempt.current()) return replaced();
        const joined = await this._joinRoom(body.room_code, attempt.owner, { onLaunchCommitted: attempt.commitLaunch });
        if (!attempt.owned()) return replaced();
        return attempt.result(joined, 'The room join did not complete');
      } catch (cause) {
        if (!attempt.current()) return replaced(cause);
        this._flash('Could not join. The invite may have expired.');
        if (attempt.current()) cleanup();
        return { ok: false, cause, notified: true };
      }
    };
    toast.querySelector('[data-action="decline"]').onclick = async () => {
      try {
        await fetch(`/api/play-invites/${encodeURIComponent(invite.id)}/decline`, {
          method: 'POST',
          credentials: 'same-origin',
        });
      } catch { /* no-op */ }
      cleanup();
    };
    document.body.appendChild(toast);
    setTimeout(cleanup, 30_000);
  }

  // ── Notifications bell + slide-in panel ─────────────────────────
  //
  // The chip lives in the account-chrome cluster and shows the total
  // unread_count from the poller. Clicking opens a right-docked panel
  // that fetches /api/notifications/list, bulk-marks the rows read on
  // open, and renders per-kind action buttons (Accept/Ignore for
  // friend_request, Join/Decline for play_invite, Delete-only for
  // friend_accepted). The panel is a plain overlay — not a .screen —
  // so it doesn't tear down gameplay while open.

  _setNotificationsBellBadge(count) {
    return notificationsUi.setNotificationsBellBadge(this, count);
  }

  _bindNotificationsPanel() {
    return notificationsUi.bindNotificationsPanel(this);
  }

  async _openNotificationsPanel() {
    return notificationsUi.openNotificationsPanel(this);
  }

  _closeNotificationsPanel() {
    return notificationsUi.closeNotificationsPanel(this);
  }

  _closeAccountPopover(opts) {
    return closeAccountPopover(opts);
  }

  _closeSavesPanel() {
    return closeSavesPanel();
  }

  async _renderNotificationsPanel() {
    return notificationsUi.renderNotificationsPanel(this);
  }

  _groupExpiredNotifs(items) {
    return notificationsUi.groupExpiredNotifs(this, items);
  }

  _renderLiveAsyncRow(game) {
    return notificationsUi.renderLiveAsyncRow(this, game);
  }

  async _watchAsyncLive(gameId) {
    return notificationsUi.watchAsyncLive(this, gameId);
  }

  _renderNotificationRow(n) {
    return notificationsUi.renderNotificationRow(this, n);
  }

  async _deleteNotification(id) {
    return notificationsUi.deleteNotification(this, id);
  }

  async _acceptFriendFromNotification(n, username) {
    return notificationsUi.acceptFriendFromNotification(this, n, username);
  }

  async _joinFromInviteNotification(n) {
    return notificationsUi.joinFromInviteNotification(this, n);
  }

  async _declineInviteFromNotification(n) {
    return notificationsUi.declineInviteFromNotification(this, n);
  }

  async _acceptPlayRequestFromNotification(n) {
    return notificationsUi.acceptPlayRequestFromNotification(this, n);
  }

  async _acceptAsyncChallenge(n) {
    return notificationsUi.acceptAsyncChallenge(this, n);
  }

  async _declineAsyncChallenge(n) {
    return notificationsUi.declineAsyncChallenge(this, n);
  }

  async _playAsyncTurn(n) {
    return notificationsUi.playAsyncTurn(this, n);
  }

  async _resignAsyncGame(n) {
    return notificationsUi.resignAsyncGame(this, n);
  }

  /**
   * Launch a solo session from a game-suggestion notification. Mirrors the
   * library deep-link boot (system switch → library load → ROM select →
   * solo) but pre-checks rom_available so a suggestion whose file left the
   * library fails with a flash instead of dying silently at the menu.
   */
  async _playSuggestedGame(n) {
    return notificationsUi.playSuggestedGame(this, n);
  }

  /**
   * Launch a game solo from a `game_suggestion_played` notification (the
   * sender clicking "Play" on the news that their friend played it). Unlike
   * _playSuggestedGame, this carries NO `suggestion` param — the sender isn't
   * the recipient, so there's nothing to mark played. Routes through the
   * universal solo deep-link boot, which owns system switch / zip / auth.
   */
  _playGameFromNotification(n) {
    return notificationsUi.playGameFromNotification(this, n);
  }

  async _dismissSuggestion(n) {
    return notificationsUi.dismissSuggestion(this, n);
  }

  /**
   * Fire-and-forget "played" mark for a pending suggestion launch. Set by
   * the /?suggestion= deep link and _playSuggestedGame; consumed by
   * _startSolo so every launch path (auto-start, sign-in-then-play) counts.
   * The server enforces recipient-only, so a sender clicking their own card
   * is a harmless 404.
   */
  _markSuggestionPlayed() {
    const id = this._pendingSuggestionId;
    if (!id) return;
    this._pendingSuggestionId = null;
    fetch(`/api/game-suggestions/${encodeURIComponent(id)}/played`, {
      method: 'POST', credentials: 'same-origin',
    }).catch(() => {});
  }

  async _startAsyncTurn(pending, {
    launchOwner: existingLaunchOwner = null,
    onLaunchCommitted = null,
    preserveLiveRoom = false,
  } = {}) {
    // G4: a fresh async turn during a room session asks first. A baton handoff
    // inside the live room (preserveLiveRoom) continues the session itself.
    // The leave target's run answers the turn's own result (c2).
    if (!preserveLiveRoom && guardRoomNavigation(this, {
      kind: 'launch',
      label: 'another game',
      run: async () => {
        const attempt = notificationsUi.captureNotificationLaunch(this);
        const started = await this._startAsyncTurn(pending, { launchOwner: attempt.owner, onLaunchCommitted: attempt.commitLaunch });
        return attempt.result(started, 'The async turn did not start');
      },
    })) return false;
    let launchOwner = existingLaunchOwner
      ?? this._beginGameLaunchCandidate({ clearPendingSave: true });
    const launchBeforeKey = roomLaunchSessionKey(this);
    let launchExpectedKey = launchBeforeKey;
    const expectedNet = preserveLiveRoom ? this.net : null;
    const expectedRoomCode = preserveLiveRoom ? expectedNet?.roomCode : null;
    const retainedRoomOwner = preserveLiveRoom
      ? { launchOwner, expectedNet, expectedRoomCode }
      : null;
    const launchIsCurrent = () => (
      retainedRoomOwner
        ? this._roomAssignmentIsCurrent(retainedRoomOwner)
        : this._isGameLaunchOwnerCurrent(launchOwner)
    ) && !roomRecoveryBlocked(this) && sameLaunchSessionKey(launchExpectedKey, roomLaunchSessionKey(this));
    if (!launchIsCurrent()) return false;

    try {
      const { game, state, system_id } = pending;
      const system = SYSTEMS[system_id];
      if (!system) { this._flash('Unsupported system'); return false; }
      const romResp = await fetch(`/api/roms?system=${system_id}`, { credentials: 'include' });
      if (!launchIsCurrent()) return false;
      const roms = romResp.ok ? await romResp.json().catch(() => []) : [];
      if (!launchIsCurrent()) return false;
      const romEntry = Array.isArray(roms) ? roms.find(r => r.hash === game.rom_hash) : null;
      if (!romEntry) { this._flash('ROM not available'); return false; }

      this._flash('Loading ROM...');
      const romRes = await fetch(`/api/roms/${system_id}/${encodeURIComponent(romEntry.filename || romEntry.name)}`);
      if (!launchIsCurrent()) return false;
      if (!romRes.ok) { this._flash('Failed to download ROM'); return false; }
      const buf = await romRes.arrayBuffer();
      if (!launchIsCurrent()) return false;
      let data = new Uint8Array(buf);
      let romName = romEntry.filename || romEntry.name;

      if (romName.toLowerCase().endsWith('.zip')) {
        const extracted = this._extractRomFromZip(data);
        if (!extracted) { this._flash('No ROM found in zip'); return; }
        data = extracted.data;
        romName = extracted.name;
      }

      let romStr = '';
      for (let i = 0; i < data.length; i++) romStr += String.fromCharCode(data[i]);
      if (!launchIsCurrent()) return false;
      launchOwner = retainedRoomOwner
        ? (this._roomAssignmentIsCurrent(retainedRoomOwner) ? launchOwner : null)
        : this._commitGameLaunchCandidate(
          launchOwner,
          { clearPendingSave: true },
        );
      if (launchOwner == null) return false;
      if (!preserveLiveRoom) this._prepareFreshSaveLaunch();
      if (!launchIsCurrent()) return false;
      if (this.system?.id !== system_id) {
        this._selectSystem(system_id);
      }
      if (roomRecoveryBlocked(this) || !(retainedRoomOwner ? this._roomAssignmentIsCurrent(retainedRoomOwner)
        : this._isGameLaunchOwnerCurrent(launchOwner)) || this.system?.id !== system_id) return false;
      const ownSystemKey = Object.freeze({ ...launchBeforeKey, system: system_id,
        romHash: launchBeforeKey.system === system_id ? launchBeforeKey.romHash : null });
      if (!sameLaunchSessionKey(ownSystemKey, roomLaunchSessionKey(this))) return false;
      this._activeRomFilename = romEntry.filename || romEntry.name;
      this._setRom(romStr, romName, { preserveLaunchIntent: true });
      launchExpectedKey = Object.freeze({ ...launchBeforeKey, system: system_id, romHash: roomLaunchSessionKey(this).romHash });
      const selectedSource = this.romData;
      const selectedFilename = this._activeRomFilename;
      if (!launchIsCurrent() || selectedFilename !== (romEntry.filename || romEntry.name)) return false;

      this._replacePendingSave({
        emulatorState: state,
        coreName: resolveArtifactCoreName(system_id, game.core_name || null),
      });
      this._pendingAsyncGame = game;
      let completed = null;
      const selectedKey = launchExpectedKey;
      const started = await this._startSolo(launchOwner, { retainedRoomOwner,
        onLaunchCommitted: (before, after, producerCurrent) => {
          if (!sameLaunchSessionKey(before, selectedKey) || producerCurrent() !== true
              || this.romData !== selectedSource || this._activeRomFilename !== selectedFilename
              || !sameLaunchSessionKey(after, roomLaunchSessionKey(this))) return false;
          completed = { key: after, current: producerCurrent };
          launchExpectedKey = after;
          return launchIsCurrent();
        },
      });
      if (started !== true || !completed || completed.current() !== true || !launchIsCurrent()) return false;
      const accepted = typeof onLaunchCommitted !== 'function'
        || onLaunchCommitted(launchBeforeKey, completed.key,
          () => launchIsCurrent() && completed.current() === true
            && this.romData === selectedSource && this._activeRomFilename === selectedFilename) === true;
      return accepted && launchIsCurrent() && completed.current() === true;
    } catch (err) {
      if (typeof onLaunchCommitted === 'function') throw err;
      if (!launchIsCurrent()) return false;
      try { console.error('[async] start turn failed:', err); } catch { /* keep the failed result */ }
      this._flash('Failed to load game');
      return false;
    } finally {
      this._finishGameLaunchCandidate(launchOwner);
    }
  }

  _resolvedLabel(n) {
    return notificationsUi.resolvedLabel(this, n);
  }

  _timeAgo(iso) {
    return notificationsUi.timeAgo(this, iso);
  }

  // ── Avatar upload (Phase 7c) ─────────────────────────

  async _uploadAvatar(file) {
    if (!authState.getCurrentUser()) return;
    if (file.size > 2 * 1024 * 1024) {
      this._flash('Image must be under 2 MB');
      return;
    }
    try {
      const buf = await file.arrayBuffer();
      const res = await fetch('/api/me/avatar', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': file.type },
        body: buf,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        this._flash(body?.notice || 'Failed to upload avatar');
        return;
      }
      const { image_url } = await res.json();
      // Update the in-memory user so the chrome re-renders with the new avatar.
      await authState.refreshSession();
      this._updateSettingsAvatar(authState.getCurrentUser());
      this._flash('Avatar updated');
    } catch (err) {
      console.warn('[avatar] upload failed:', err);
      this._flash('Failed to upload avatar');
    }
  }

  _updateSettingsAvatar(user) {
    const el = document.getElementById('settings-avatar-img');
    const removeBtn = document.getElementById('settings-avatar-remove');
    if (!el) return;
    if (user?.image) {
      el.innerHTML = `<img src="${user.image}" alt="" class="settings-avatar-img">`;
      if (removeBtn) removeBtn.disabled = false;
    } else {
      el.textContent = '👤';
      if (removeBtn) removeBtn.disabled = true;
    }
  }

  async _removeAvatar() {
    if (!authState.getCurrentUser()) return;
    try {
      const res = await fetch('/api/me/avatar', {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Delete failed');
      await authState.refreshSession();
      this._updateSettingsAvatar(authState.getCurrentUser());
      this._flash('Avatar removed');
    } catch (err) {
      console.warn('[avatar] remove failed:', err);
      this._flash('Failed to remove avatar');
    }
  }

  // ── Server sync helpers (Phase 5) ──────────────────────
  // Bodies live in src/client/achievements/server-sync.js (spec 6.4, 7.7, 7.9).

  /** One-shot upload of every local unlock on first sign-in. */
  async _syncLocalAchievementsToServer() { return syncLocalAchievementsToServer(this); }

  /** Sync one live unlock; reconciles a refused authored unlock. */
  async _syncSingleUnlock(ach) { return syncSingleUnlock(this, ach); }

  /** One-shot upload of every cached best on first sign-in. */
  async _syncLocalScoresToServer() { return syncLocalScoresToServer(this); }

  // ── Server play sessions (Phase 5) ─────────────────────
  //
  // Pairs with _startAchievementSession / _endAchievementSession. When the
  // user is signed in, we POST /api/sessions/start at solo-start, send a
  // heartbeat every ~60s, and POST /api/sessions/end on tear-down.
  // No-op when anonymous.

  async _serverSessionStart(systemId, romHash, gameSlug, gameTitle) {
    if (!authState.getCurrentUser()) return;
    const sessionMeta = Object.freeze({ system: systemId, romHash, gameSlug, gameTitle,
      ...(validatedMultitapProfile(this._roomProfile) && this._multitapSession?.admitted
        ? { roomProfile: 'snes-multitap-v1' } : {}) });
    this._serverSessionMeta = sessionMeta;
    this._serverSessionStartedAt = Date.now();
    // Generate the idempotency/correlation key client-side so the first
    // heartbeat can still identify this session if the /start response is
    // lost. The server validates and echoes it, and remains the fallback for
    // browsers without randomUUID().
    this._serverSessionId = globalThis.crypto?.randomUUID?.() || null;

    // Persist a trail point immediately and arm the cadence before /start can
    // block on a slow network. The UUID is already local, so neither recovery
    // coverage nor later heartbeats depend on that response arriving.
    if (this._serverHeartbeatInterval) clearInterval(this._serverHeartbeatInterval);
    this._serverSessionHeartbeat();
    this._serverHeartbeatInterval = setInterval(() => this._serverSessionHeartbeat(), 60_000);

    try {
      const res = await fetch('/api/sessions/start', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_uuid: this._serverSessionId }),
      });
      if (res.ok) {
        const body = await res.json();
        // A newer session may have replaced this one while auth/network was
        // slow. Never attach the old response or timer to the new session.
        if (this._serverSessionMeta !== sessionMeta) return;
        this._serverSessionId = body.session_id || this._serverSessionId;
      }
    } catch (err) {
      console.warn('[sync] session start failed:', err);
    }

    if (this._serverSessionMeta !== sessionMeta) return;
  }

  async _serverSessionHeartbeat() {
    if (!authState.getCurrentUser() || !this._serverSessionMeta) return;
    const m = this._serverSessionMeta;
    // Include room_code when the client is currently in a netplay room.
    // Drives the "friends currently playing" Join deep-link on /friends.
    // Null when playing solo — server column is nullable.
    const roomCode = this.net?.roomCode || null;
    const transportMode = roomCode
      ? (this._netMode === 'link-cable'
          ? 'link-cable'
          : this.lockstep || this.multitapEngine
            ? 'lockstep'
            : this.rollback
              ? 'rollback'
              : (this.hostedNetplay || this._netMode === 'host' || this._netMode === 'guest')
                ? 'hosted'
                : 'unknown')
      : null;
    const metrics = roomCode ? {
      session_uuid: this._serverSessionId,
      session_started_at: this._serverSessionStartedAt
        ? new Date(this._serverSessionStartedAt).toISOString() : null,
      transport_mode: transportMode,
      rtt_ms: this.hostedNetplay?.p2pLatency
        ?? this.lockstepConn?.p2pLatency
        ?? this._linkCableConnection?.p2pLatency
        ?? null,
      fps: this._lastFps ?? null,
      // These are cumulative per-session counters. The analytics view groups
      // by session and takes MAX, so longer sessions are not overweighted by
      // their greater number of heartbeat rows. A lockstep wait is a stall,
      // never a rollback.
      rollbacks: this.rollback?.rollbackCount ?? 0,
      stalls: this.lockstep?.stallCount ?? 0,
      desyncs: (this.lockstep?.desyncDetected || this.rollback?.desyncDetected) ? 1 : 0,
    } : {
      session_uuid: this._serverSessionId,
      session_started_at: this._serverSessionStartedAt
        ? new Date(this._serverSessionStartedAt).toISOString() : null,
    };
    // Memory telemetry (Phase 1) — fields are all nullable on the server
    // side, so this is safe to send even when the monitor isn't running.
    const snap = this.memoryMonitor?.getSnapshot?.() || null;
    const memory = snap ? {
      device_class: snap.deviceClass,
      js_heap_used_bytes: snap.jsHeapUsed,
      js_heap_total_bytes: snap.jsHeapTotal,
      wasm_heap_bytes: snap.wasmHeap,
      dom_nodes: snap.domNodes,
      growth_30s: snap.growth_30s,
      growth_60s: snap.growth_60s,
      growth_5m: snap.growth_5m,
      clips_enabled: this._clipsEnabled,
      clips_state: snap.clipsState,
      visibility: snap.visibility,
    } : undefined;
    try {
      await fetch('/api/sessions/heartbeat', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          system: m.system, rom_hash: m.romHash,
          game_slug: m.gameSlug, game_title: m.gameTitle,
          room_code: roomCode,
          ...metrics,
          ...memory,
        }),
      });
    } catch (err) {
      console.warn('[sync] heartbeat failed:', err);
    }
  }

  /**
   * G6: end the server play session durably. Never rejects; resolves to
   * `{ replayFlush, sessionEnd, cause }` (see endServerSession).
   */
  _serverSessionEnd(finalReplayFlush = null) {
    return endServerSession(this, finalReplayFlush);
  }

  // ── Save sync helpers (Phase 6) ─────────────────────

  async _syncLocalSavesToServer() {
    return saveManager.syncLocalSavesToServer(this);
  }

  _serializeSaveForSync(save) {
    return saveManager.serializeSaveForSync(this, save);
  }

  async _uploadAutoSaveToServer() {
    return saveManager.uploadAutoSaveToServer(this);
  }

  async _uploadManualSaveToServer(save, slot, options) {
    const manualSave = await manualSaveModulePromise;
    if (!manualSave) return;
    return manualSave.uploadManualSaveToServer(this, save, slot, options);
  }

  async _maybeResolveSaveConflictOnLoad(isLaunchCurrent = null) {
    const { resolveTrackedSaveConflict } = await import('./modules/save-overwrite.js');
    return resolveTrackedSaveConflict(
      this, isLaunchCurrent, saveManager.maybeResolveSaveConflictOnLoad,
    );
  }

  // ── Favorites sync helpers (Phase 6) ────────────────

  /**
   * One-shot upload of local favorites to the server on first sign-in.
   * Resolves filenames to rom_hash using the ROM index, skips unresolvable.
   */
  async _syncLocalFavoritesToServer() {
    const user = authState.getCurrentUser();
    if (!user) return;
    const markerKey = `retroplay-favorites-synced:${user.id}`;
    if (localStorage.getItem(markerKey)) return;

    const favs = this._getFavorites();
    if (!favs.length) {
      localStorage.setItem(markerKey, '1');
      return;
    }

    // Group by system so we can batch the index lookups.
    const bySystem = new Map();
    for (const f of favs) {
      if (!bySystem.has(f.system)) bySystem.set(f.system, []);
      bySystem.get(f.system).push(f);
    }

    let synced = 0;
    for (const [system, systemFavs] of bySystem) {
      // Ensure the ROM title cache is loaded for this system.
      await ensureRomTitleCache(this, system);
      const hashMap = this._romFilenameToHash?.[system] || {};

      for (const fav of systemFavs) {
        const hash = fav.hash || hashMap[fav.name];
        if (!hash) continue; // unresolvable — stays local-only
        try {
          await fetch('/api/favorites', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ system_id: system, rom_hash: hash, favorited: true }),
          });
          synced++;
        } catch { /* skip */ }
      }
    }

    localStorage.setItem(markerKey, '1');
    // Also pull the server's canonical list and merge into local.
    await this._pullServerFavorites();
  }

  /** Fetch server favorites and merge into local localStorage list. */
  async _pullServerFavorites() {
    if (!authState.getCurrentUser()) return;
    try {
      const res = await fetch('/api/favorites', { credentials: 'include' });
      if (!res.ok) return;
      const { favorites } = await res.json();
      // Merge: for each server favorite, ensure it's in the local list.
      const local = this._getFavorites();
      const localSet = new Set(local.map(f => `${f.system}:${f.name}`));

      // Build a hash→filename reverse map from the title cache.
      const hashToFilename = {};
      for (const [sys, fileMap] of Object.entries(this._romFilenameToHash || {})) {
        hashToFilename[sys] = hashToFilename[sys] || {};
        for (const [fn, h] of Object.entries(fileMap)) hashToFilename[sys][h] = fn;
      }

      for (const sf of favorites) {
        if (sf.removed_at) continue;
        const filename = hashToFilename[sf.system_id]?.[sf.rom_hash];
        if (!filename) continue;
        const key = `${sf.system_id}:${filename}`;
        if (!localSet.has(key)) {
          local.push({ name: filename, system: sf.system_id, hash: sf.rom_hash });
        }
      }
      this._saveFavorites(local);
      this._loadFavoritesList();
    } catch (err) {
      console.warn('[sync] favorites pull failed:', err);
    }
  }

  /** Fire-and-forget: POST a favorite toggle to the server when signed in. */
  _syncFavoriteToServer(system, romHash, favorited) {
    if (!authState.getCurrentUser() || !romHash) return;
    fetch('/api/favorites', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ system_id: system, rom_hash: romHash, favorited }),
    }).catch(() => {});
  }

  // ── Conflict picker (Phase 6) ─────────────────────────
  //
  // Returns a Promise resolving to 'local' | 'remote' | 'both'.
  // Caller acts on the result (upload local, download server, or keep both).

  async _showSaveConflictPicker({ system, romHash, kind, slot, localMeta, serverMeta }) {
    const controller = new AbortController();
    this._saveConflictPickerAbort = controller;
    this._saveConflictPickerOwner = this._gameLaunchIntentGeneration;
    const onAbort = () => {
      document.getElementById('save-conflict-close')?.click();
    };
    try {
      const { showTrackedSaveConflictPicker } = await import('./modules/save-overwrite.js');
      if (controller.signal.aborted) return null;
      controller.signal.addEventListener('abort', onAbort, { once: true });
      return await showTrackedSaveConflictPicker(
        this,
        { system, romHash, kind, slot, localMeta, serverMeta },
        saveManager.showSaveConflictPicker,
      );
    } finally {
      controller.signal.removeEventListener('abort', onAbort);
      if (this._saveConflictPickerAbort === controller) {
        this._saveConflictPickerAbort = null;
        this._saveConflictPickerOwner = null;
      }
    }
  }

  _playJoinPing() {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const gameplayActive = this.state === State.PLAYING
        || this.state === State.SOLO
        || this.state === State.SPECTATING;
      const reusableCtx = getReusableAudioContext();
      // Never introduce a second audio context during active gameplay. On
      // affected mobile/libretro combinations that can suspend the core while
      // it is blocked in al_write, leaving both game and audio paused forever.
      // The visual spectator notice remains available if the core has not yet
      // exposed a context that can carry this optional ping.
      if (gameplayActive && !reusableCtx) return;
      const ctx = reusableCtx || new AC();
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const now = ctx.currentTime;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.15, now);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
      gain.connect(ctx.destination);
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = 880;
      osc.connect(gain);
      osc.start(now);
      osc.stop(now + 0.3);
      // A borrowed game context owns the emulator's lifetime and must never be
      // closed by a cosmetic notification.
      if (!reusableCtx) setTimeout(() => ctx.close().catch(() => {}), 400);
    } catch {}
  }

  /** Simple three-note chime via Web Audio — no external asset. */
  _playAchievementChime() {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const gameplayActive = this.state === State.PLAYING
        || this.state === State.SOLO
        || this.state === State.SPECTATING;
      const reusableCtx = getReusableAudioContext();
      if (gameplayActive && !reusableCtx) return;
      const ctx = reusableCtx || new AC();
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const now = ctx.currentTime;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.2, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.6);
      gain.connect(ctx.destination);

      const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        osc.type = 'square';
        osc.frequency.value = freq;
        osc.connect(gain);
        osc.start(now + i * 0.08);
        osc.stop(now + i * 0.08 + 0.18);
      });
      if (!reusableCtx) setTimeout(() => ctx.close().catch(() => {}), 800);
    } catch {
      // Chime is cosmetic; ignore failures.
    }
  }

  /** Update the in-game HUD counter (solo only). */
  /**
   * Fetch the signed-in user's achievement counts from the server.
   * Returns a Map<romHash, { unlocked, total }> for the given system,
   * or an empty Map if not signed in / fetch fails. Cached for 60s.
   */
  async _fetchServerAchievementCounts() {
    const user = authState.getCurrentUser();
    if (!user?.username) return new Map();

    const now = Date.now();
    if (this._serverAchCache && now - this._serverAchCacheTime < 60_000) {
      return this._serverAchCache;
    }

    try {
      const res = await fetch(`/api/users/${encodeURIComponent(user.username)}/games`, {
        credentials: 'include',
      });
      if (!res.ok) return this._serverAchCache || new Map();
      const data = await res.json();
      const map = new Map();
      for (const g of data.games || []) {
        if (g.rom_hash) {
          map.set(g.rom_hash, {
            unlocked: g.achievements_unlocked ?? 0,
            total: g.achievements_total ?? 0,
            title: g.title || null,
            systemId: g.system_id || null,
          });
        }
      }
      this._serverAchCache = map;
      this._serverAchCacheTime = now;
      return map;
    } catch {
      return this._serverAchCache || new Map();
    }
  }

  _updateAchievementsHUD() {
    const el = document.getElementById('stat-cheevos');
    // AC6: the game room hides the floating trophy counter.
    if (!el || isRoomMode(this)) return;
    if (this._achievementsDisabledThisSession) {
      el.classList.remove('hidden');
      el.querySelector('.hud-value').textContent = 'OFF';
      el.style.opacity = '0.5';
      el.title = trackingOffNotice(this);
      return;
    }
    el.style.opacity = '';
    el.title = 'View achievements';
    const g = this.cheevosEngine.getCurrentGame();
    if (!g || !g.total) {
      el.classList.add('hidden');
      return;
    }
    el.classList.remove('hidden');
    el.querySelector('.hud-value').textContent = `${g.unlocked}/${g.total}`;
  }

  _toggleCheevoPanel() {
    if (!c64SessionHasGame(this)) return;
    // AC6: no classic panel in the game room; its Achievements tab shows the list.
    if (isRoomMode(this)) {
      callRoom(this, 'showTab', 'achievements');
      return;
    }
    const panel = document.getElementById('cheevo-panel');
    if (!panel) return;
    if (panel.classList.contains('hidden')) {
      this._renderCheevoPanel();
      panel.classList.remove('hidden');
    } else {
      panel.classList.add('hidden');
    }
  }

  _closeCheevoPanel() {
    document.getElementById('cheevo-panel')?.classList.add('hidden');
  }

  _renderCheevoPanel() {
    return cheevoPanel.renderCheevoPanel(this);
  }

  _renderCheevoPanelItems(listEl, items) {
    listEl.innerHTML = '';
    const unlocked = items.filter(i => i.unlocked);
    const locked = items.filter(i => !i.unlocked);

    for (const item of [...unlocked, ...locked]) {
      const row = document.createElement('div');
      row.className = `cheevo-row${item.unlocked ? '' : ' locked'}`;

      // S2: icon and points come from the server (or the local definitions).
      // The icon is escaped like the name and description below, and points
      // are read as a number.
      const icon = this._escapeHtml(String(item.icon || '🏆'));
      const pts = Number(item.points) || 0;
      let meta = `<span class="cheevo-points">${pts} pt${pts !== 1 ? 's' : ''}</span>`;
      if (item.unlocked && item.unlockedAt) {
        const d = new Date(item.unlockedAt);
        meta += `<span class="cheevo-unlocked-at">${d.toLocaleDateString()}</span>`;
      }

      row.innerHTML =
        `<span class="cheevo-icon">${item.unlocked ? icon : '🔒'}</span>` +
        `<div class="cheevo-info">` +
          `<div class="cheevo-name">${this._escapeHtml(item.name)}</div>` +
          `<div class="cheevo-desc">${this._escapeHtml(item.description)}</div>` +
          `<div class="cheevo-meta">${meta}</div>` +
        `</div>`;
      // Share button on unlocked, server-backed rows (own unlocks only —
      // the panel always shows the signed-in player's game).
      const shareUser = authState.getCurrentUser();
      if (item.unlocked && item.slug && item.gameId && shareUser?.username) {
        row.appendChild(buildShareButton({
          kind: 'achievement',
          username: shareUser.username,
          gameId: item.gameId,
          achSlug: item.slug,
          name: item.name,
          points: item.points,
          gameTitle: item.gameTitle || 'this game',
          rarityPercent: item.rarityPercent,
          rarityTier: item.rarityTier,
        }, { className: 'cheevo-share', label: `Share achievement: ${item.name}` }));
      }
      listEl.appendChild(row);
    }
  }

  _fetchCheevoPanelServer(systemId, romHash, listEl) {
    return cheevoPanel.fetchCheevoPanelServer(this, systemId, romHash, listEl);
  }

  /** Render the menu-screen "Achievements" card summary (all systems). */
  async _loadAchievementsCard() {
    const card = document.getElementById('achievements-card');
    const list = document.getElementById('achievements-list');
    const empty = document.getElementById('achievements-empty');
    if (!card || !list) return;

    if (!authState.isLoaded()) {
      try { await authState.refreshSession(); } catch { /* non-fatal */ }
    }
    const user = authState.getCurrentUser();

    // Signed in: server is the source of truth — never fall back to localStorage.
    if (user?.username) {
      try {
        const counts = await this._fetchServerAchievementCounts();
        const games = [];
        for (const [romHash, c] of counts) {
          if (!c.unlocked) continue;
          games.push({
            romHash,
            systemId: c.systemId,
            title: c.title || romHash,
            unlocked: c.unlocked,
            total: c.total,
          });
        }
        games.sort((a, b) => b.unlocked - a.unlocked);
        list.innerHTML = '';
        if (!games.length) {
          empty?.classList.remove('hidden');
          card.classList.remove('hidden');
          return;
        }
        empty?.classList.add('hidden');
        this._renderAchievementsCardEntries(card, list, games);
      } catch {
        card.classList.add('hidden');
      }
      return;
    }

    // Anonymous only: current system from localStorage.
    const authored = this.cheevosEngine.listGamesForSystem(this.system.id);
    const games = authored.filter(g => {
      if (!g.romHash) return false;
      const stats = this.cheevosStore.getStats(g.systemId, g.romHash);
      return stats.sessions > 0 || stats.playMs > 0 || g.unlocked > 0;
    }).filter(g => g.unlocked > 0);
    list.innerHTML = '';
    if (!games.length) { card.classList.add('hidden'); return; }
    empty?.classList.add('hidden');
    this._renderAchievementsCardEntries(card, list, games);
  }

  _renderAchievementsCardEntries(card, list, games) {
    list.innerHTML = '';
    for (const g of games) {
      const pct = g.total ? Math.round((g.unlocked / g.total) * 100) : 0;
      const entry = document.createElement('div');
      entry.className = 'cheevo-entry';
      const sysName = systemName(g.systemId);
      entry.innerHTML =
        `<span class="cheevo-title" title="${this._escapeHtml(g.title)}">${this._escapeHtml(g.title)}</span>` +
        `<span class="cheevo-sys">${this._escapeHtml(sysName)}</span>` +
        `<span class="cheevo-count">${g.unlocked}/${g.total}</span>` +
        `<div class="cheevo-bar"><div class="cheevo-bar-fill" style="width:${pct}%"></div></div>`;
      entry.addEventListener('click', () => {
        this.cheevosEngine.loadSystem(g.systemId).then(() => {
          this._showAchievementDetail(g.systemId, g.romHash);
        }).catch(() => this._flash('Could not load achievements'));
      });
      list.appendChild(entry);
    }
    if (games.length) card.classList.remove('hidden');
    else card.classList.add('hidden');
  }

  /** Open the per-game detail modal listing each achievement. */
  async _showAchievementDetail(systemId, romHash) {
    const modal = document.getElementById('achievements-modal');
    const body = document.getElementById('achievements-modal-body');
    const titleEl = document.getElementById('achievements-modal-title');
    if (!modal || !body || !titleEl) return;

    const user = authState.getCurrentUser();
    let items = null;    // [{ name, description, icon, points, unlocked, unlockedAt }]
    let title = null;
    let playTimeStr = '<1 min';
    let sessions = 0;
    let isServerData = false;

    const showServerError = () => {
      titleEl.textContent = 'Achievements unavailable';
      body.innerHTML =
        `<div class="cheevo-modal-error" role="alert">` +
          `<div class="cheevo-modal-error-title">Couldn’t load achievements</div>` +
          `<p>Your signed-in progress is stored on the server. Try again to load the authoritative data.</p>` +
          `<button id="btn-cheevo-retry" type="button" class="btn btn-mode btn-primary">Retry</button>` +
        `</div>`;
      document.getElementById('btn-cheevo-retry')?.addEventListener('click', () => {
        body.innerHTML = '<div class="cheevo-modal-error" role="status">Loading achievements…</div>';
        void this._showAchievementDetail(systemId, romHash);
      });
      modal.classList.remove('hidden');
    };

    // Signed in: server is the source of truth for unlock state.
    if (user) {
      try {
        const res = await fetch(
          `/api/achievements/game/${encodeURIComponent(systemId)}/${encodeURIComponent(romHash)}`,
          { credentials: 'include' },
        );
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        if (!Array.isArray(data.achievements?.list)) throw new Error('invalid_response');
        title = data.game?.title || 'Achievements';
        const s = data.user_stats;
        const mins = Math.round((s?.play_time_seconds || 0) / 60);
        playTimeStr = mins < 1 ? '<1 min' : `${mins} min`;
        sessions = s?.session_count || 0;
        items = data.achievements.list.map(a => ({
          name: a.name, description: a.description || '', icon: a.icon,
          points: a.points || 0, unlocked: a.unlocked, unlockedAt: a.unlocked_at,
        }));
        isServerData = true;
      } catch (err) {
        console.warn('[achievements] server fetch failed:', err);
        showServerError();
        return;
      }
    }

    // Anonymous only: use localStorage via the engine.
    if (!items) {
      const entries = this.cheevosEngine.listGameAchievements(systemId, romHash);
      if (!entries.length) {
        this._flash('No achievements for this game yet');
        return;
      }
      const gameSummary = this.cheevosEngine.listGamesForSystem(systemId)
        .find(g => g.roms?.some(r => r.hash === romHash));
      title = gameSummary?.title || 'Achievements';
      const stats = this.cheevosStore.getStats(systemId, romHash);
      const mins = Math.round(stats.playMs / 60000);
      playTimeStr = mins < 1 ? '<1 min' : `${mins} min`;
      sessions = stats.sessions;
      items = entries.map(e => ({
        name: e.achievement.name, description: e.achievement.description,
        icon: e.achievement.icon, points: e.achievement.points || 0,
        unlocked: e.unlocked, unlockedAt: e.unlockedAt,
      }));
    }

    titleEl.textContent = title;

    const gameSummary = this.cheevosEngine.listGamesForSystem(systemId)
      .find(g => g.roms?.some(r => r.hash === romHash));
    const romFilename = gameSummary?.roms?.find(r => r.hash === romHash)?.filename || '';

    const unlocked = items.filter(a => a.unlocked);
    const locked = items.filter(a => !a.unlocked);
    const sorted = [...unlocked, ...locked];

    body.innerHTML =
      (romFilename ? `<div class="cheevo-rom-name" title="${this._escapeHtml(romFilename)}">${this._escapeHtml(romFilename)}</div>` : '') +
      `<div class="cheevo-stats-row">` +
        `<span>⏱ ${playTimeStr}</span>` +
        `<span>🎯 ${sessions} session${sessions === 1 ? '' : 's'}</span>` +
      `</div>` +
      (sorted.length ? sorted.map(a => {
        const icon = a.unlocked ? (a.icon || '🏆') : '🔒';
        let badge = '';
        if (a.unlocked && a.unlockedAt) {
          const d = new Date(a.unlockedAt);
          badge = `<span class="cheevo-unlock-date">${d.toLocaleDateString()}</span>`;
        } else if (a.unlocked) {
          badge = `<span class="cheevo-unlock-date">Unlocked</span>`;
        }
        return `<div class="cheevo-item ${a.unlocked ? 'unlocked' : 'locked'}">` +
          `<div class="cheevo-icon">${icon}</div>` +
          `<div class="cheevo-main">` +
            `<div class="cheevo-name">${this._escapeHtml(a.name)}${badge}</div>` +
            `<div class="cheevo-desc">${this._escapeHtml(a.description || '')}</div>` +
          `</div>` +
          `<div class="cheevo-points">${a.points}pt</div>` +
        `</div>`;
      }).join('') : '<div class="cheevo-modal-empty">No achievements are available for this game yet.</div>') +
      (isServerData ? '' :
        `<div class="cheevo-modal-actions">` +
          `<button id="btn-cheevo-reset-game" class="btn btn-mode btn-danger">Reset this game</button>` +
        `</div>`);

    if (!isServerData) {
      document.getElementById('btn-cheevo-reset-game')?.addEventListener('click', async () => {
        if (confirm(`Reset achievements and stats for ${title}? This cannot be undone.`)) {
          // Cleared only against readable saved progress (another tab's other games stay).
          if (await this.cheevosStore.resetGame(systemId, romHash) !== true) {
            this._flash('Achievements could not be reset. Try again.');
            return;
          }
          this._loadAchievementsCard();
          this._closeAchievementsModal();
          this._flash('Achievements reset');
        }
      });
    }

    modal.classList.remove('hidden');
  }

  _closeAchievementsModal() {
    document.getElementById('achievements-modal')?.classList.add('hidden');
  }

}

// ── Boot ─────────────────────────────────────────────────
// Skipped during unit tests — happy-dom fires DOMContentLoaded before
// the test file runs its imports, but we also don't want the full
// constructor side effects when importing the NESPlay class in tests.
if (typeof process === 'undefined' || !process.env?.VITEST) {
  window.addEventListener('DOMContentLoaded', () => {
    window.app = new NESPlay();
    registerServiceWorker(navigator.serviceWorker, BUILD_SHA);
    window.app._serviceWorkerFreshness = monitorServiceWorkerFreshness(navigator.serviceWorker, {
      buildSha: BUILD_SHA,
      buildVersion: BUILD_VERSION,
      // Never throw away live gameplay or a replay running in-place over `/`.
      // The bare homepage is the surface whose suspended documents need
      // deployment reconciliation.
      shouldReload: () => isSafeHomepageReload(window.app),
    });
    const versionEl = document.getElementById('site-footer-version');
    if (versionEl) versionEl.textContent = `v${BUILD_VERSION} · ${BUILD_SHA.slice(0, 7)}`;
  });
}
